import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Output, Input, NgModule, Pipe, Injectable, InjectionToken, Directive, ViewChild, HostListener, ViewChildren, ViewContainerRef, ElementRef, ViewEncapsulation } from '@angular/core';
import * as i1$1 from '@mhe/ngx-shared';
import { ButtonModule, ModalModule, AlertModule, RadioButtonModule, NgxSharedModule, TranslateModule } from '@mhe/ngx-shared';
import * as i4 from '@mhe/tinymce';
import { RichTextEditorModule } from '@mhe/tinymce';
import * as i1 from '@ngrx/store';
import { createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i5 from 'angular2-draggable';
import { AngularDraggableModule } from 'angular2-draggable';
import * as i4$1 from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i2$1 from '@mhe/air-rendering';
import { AirModalModule, AirRenderingModule } from '@mhe/air-rendering';
import * as shared from '@mhe/air-shared';
import * as he from 'he';
import { fraction, divide, subtract, multiply, add, smallerEq, largerEq, abs, floor, number } from 'mathjs';
import * as uuid from 'uuid';
import { v5, v4 } from 'uuid';
import { take, takeUntil } from 'rxjs/operators';
import * as i2 from '@angular/common/http';
import * as i3$1 from '@mhe/lighthouse';
import { OptionBehavior, LhDropMenuModule } from '@mhe/lighthouse';
import * as i6 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject, Subject, BehaviorSubject, empty, take as take$1, map, takeUntil as takeUntil$1 } from 'rxjs';
import * as d3 from 'd3';
import { AlertType } from '@mhe/air-ui-common';
import * as i2$2 from 'ngx-file-drop';
import { NgxFileDropModule } from 'ngx-file-drop';
import * as d3Drag from 'd3-drag';
import { EventEmitter as EventEmitter$1 } from 'events';

class MathEditorComponent {
    constructor() {
        this.updateAnswer = new EventEmitter();
        this.editorBlur = new EventEmitter();
        this.mathml = '';
        this.mathEditorType = 'basic';
        this.answers = [];
        this.keyDownTimeout = null;
        this.errorMsg = false;
        this.isWirisLoaded = false;
    }
    ngOnChanges(simpleChanges) {
        if (simpleChanges.mathEditorType && simpleChanges.mathEditorType.previousValue) {
            this.initializeMathEditor();
        }
        if (this.editor && simpleChanges.mathml && !simpleChanges.mathml.previousValue) {
            this.setMathmlInEditor();
        }
        if (simpleChanges.wirisMathUrl && !simpleChanges.wirisMathUrl.previousValue) {
            if (window['com'] && window['com'].wiris.jsEditor) {
                return;
            }
            if (this.wirisMathUrl) {
                this.loadScript(this.wirisMathUrl);
            }
            else {
                // FIXME: Keep this if condition until Angular 16 comes into play,
                // where we can enforce the input to be required
                console.warn(`Wiris Math URL is unset`);
            }
        }
    }
    async loadScript(mathUrl) {
        const wirisMathEditorScript = document.createElement('script');
        wirisMathEditorScript.setAttribute('src', `${mathUrl}/editor`);
        document.body.appendChild(wirisMathEditorScript);
        wirisMathEditorScript.onload = (e) => {
            this.isWirisLoaded = true;
            this.initializeMathEditor();
        };
    }
    ngAfterViewInit() {
        this.initializeMathEditor();
    }
    setMathmlInEditor() {
        const sanitizedML = this.mathml.replace(/<mo><</g, '<mo>&lt;<').replace(/<mo>></g, '<mo>&gt;<');
        if (sanitizedML !== '') {
            this.editor.setMathML(sanitizedML);
            return;
        }
        this.editor.setMathML(this.mathml);
        return;
    }
    initializeMathEditor() {
        const wirisObject = window['com'];
        if (!wirisObject?.wiris?.jsEditor) {
            return;
        }
        if (this.preset) {
            if (this.preset === 'math-4-5') {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('basic', 'math-4-5'),
                });
            }
            else if (this.preset === 'math-6-8') {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('basic', 'math-6-8'),
                });
            }
            else if (this.preset === 'geometry') {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('basic', 'geometry'),
                });
            }
            else {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('basic', 'math-k-3'),
                });
            }
        }
        else {
            if (this.mathEditorType === 'basic') {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('basic'),
                });
            }
            else {
                this.editor = wirisObject.wiris.jsEditor.JsEditor.newInstance({
                    language: 'en',
                    toolbar: this.buildToolbar('full'),
                });
            }
        }
        this.editor.getEditorModel().addEditorListener({
            caretPositionChanged: () => { },
            contentChanged: () => {
                this.updateAnswer.emit(this.editor.getMathML());
            },
        });
        const editorRef = document.getElementById(this.editorId);
        if (!editorRef) {
            return;
        }
        this.editor.insertInto(editorRef);
        this.setMathmlInEditor();
        const inputElem = editorRef.getElementsByTagName('input');
        // This is to prevent authors from copy/pasting math contents into editor
        inputElem[0].addEventListener('paste', (event) => {
            event.preventDefault();
        });
        // This is to prevent authors from drag and drop math contents into editor
        inputElem[0].addEventListener('drop', (event) => {
            event.preventDefault();
        });
        inputElem[0].addEventListener('blur', this.onEditorBlur.bind(this), false);
    }
    onEditorBlur() {
        this.editorBlur.emit();
    }
    buildToolbar(editorType, preset = null) {
        if (preset) {
            switch (preset) {
                case 'math-4-5':
                    return shared.wiris_toolbar.math45;
                case 'math-6-8':
                    return shared.wiris_toolbar.math68;
                case 'geometry':
                    return shared.wiris_toolbar.geometry;
                case 'math-k-3':
                default:
                    return shared.wiris_toolbar.mathk3;
            }
        }
        switch (editorType) {
            case 'basic':
                return shared.wiris_toolbar.basic;
            case 'full':
                return shared.wiris_toolbar.full;
            default:
                return shared.wiris_toolbar.default;
        }
    }
}
MathEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathEditorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
MathEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: MathEditorComponent, selector: "aa-math-editor", inputs: { editorId: "editorId", mathml: "mathml", mathEditorType: "mathEditorType", answers: "answers", wirisMathUrl: "wirisMathUrl", preset: "preset" }, outputs: { updateAnswer: "updateAnswer", editorBlur: "editorBlur" }, usesOnChanges: true, ngImport: i0, template: "\n<div [id]=\"editorId\"\n     class=\"mathEditor\"></div>\n", styles: [".mathEditor{height:250px}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-math-editor', changeDetection: ChangeDetectionStrategy.OnPush, template: "\n<div [id]=\"editorId\"\n     class=\"mathEditor\"></div>\n", styles: [".mathEditor{height:250px}\n"] }]
        }], propDecorators: { updateAnswer: [{
                type: Output
            }], editorBlur: [{
                type: Output
            }], editorId: [{
                type: Input
            }], mathml: [{
                type: Input
            }], mathEditorType: [{
                type: Input
            }], answers: [{
                type: Input
            }], wirisMathUrl: [{
                type: Input
            }], preset: [{
                type: Input
            }] } });

class MathEditorModule {
}
MathEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MathEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: MathEditorModule, declarations: [MathEditorComponent], imports: [CommonModule], exports: [MathEditorComponent] });
MathEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathEditorModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MathEditorComponent],
                    imports: [CommonModule],
                    providers: [],
                    exports: [MathEditorComponent],
                }]
        }] });

class HtmlToPlaintextPipe {
    transform(value) {
        const element = document.createElement('div');
        element.innerHTML = value && value.replace(/<[^>]*>/g, '');
        /* Hack for IE: Stop innerText to truncate the new line */
        element.innerHTML = element.innerHTML.replace(/\n/g, '##########');
        return element.innerText.replace(/##########/g, '\n');
    }
}
HtmlToPlaintextPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HtmlToPlaintextPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextPipe, name: "htmlToPlainText" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'htmlToPlainText',
                }]
        }] });

class HtmlToPlaintextWithImagePipe {
    transform(value) {
        const element = document.createElement('div');
        const spanWithImage = `<span class="span-with-image ahe-icon-image"></span>`;
        value = value.replace(/<img[^>]*>/g, '####');
        element.innerHTML = value;
        let innerText = element.innerText;
        innerText = innerText.replace(/####/g, spanWithImage);
        return innerText;
    }
}
HtmlToPlaintextWithImagePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextWithImagePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
HtmlToPlaintextWithImagePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextWithImagePipe, name: "htmlToPlainTextWithImage" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HtmlToPlaintextWithImagePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'htmlToPlainTextWithImage',
                }]
        }] });

class ResponseToUnderscorePipe {
    transform(value) {
        return value.replace(/\$\{response:[\w-]*\}/g, '__');
    }
}
ResponseToUnderscorePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ResponseToUnderscorePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ResponseToUnderscorePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ResponseToUnderscorePipe, name: "responseToUnderscore" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ResponseToUnderscorePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'responseToUnderscore',
                }]
        }] });

class CapitalizePipe {
    transform(value) {
        if (value) {
            return value.charAt(0).toUpperCase() + value.slice(1);
        }
        return value;
    }
}
CapitalizePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CapitalizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
CapitalizePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: CapitalizePipe, name: "capitalize" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CapitalizePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'capitalize' }]
        }] });

class PipesModule {
}
PipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: PipesModule, declarations: [HtmlToPlaintextPipe, HtmlToPlaintextWithImagePipe, ResponseToUnderscorePipe, CapitalizePipe], imports: [CommonModule], exports: [HtmlToPlaintextPipe, HtmlToPlaintextWithImagePipe, ResponseToUnderscorePipe] });
PipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PipesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [HtmlToPlaintextPipe, HtmlToPlaintextWithImagePipe, ResponseToUnderscorePipe, CapitalizePipe],
                    exports: [HtmlToPlaintextPipe, HtmlToPlaintextWithImagePipe, ResponseToUnderscorePipe],
                }]
        }] });

class ImageLabelDndService {
    getElementCoordinate(element) {
        return {
            left: element?.getBoundingClientRect().left,
            top: element?.getBoundingClientRect().top,
            width: element?.getBoundingClientRect().width,
            height: element?.getBoundingClientRect().height,
        };
    }
    getImageCoordinate() {
        const element = document.getElementById('image-upload');
        const imageElement = element?.getElementsByClassName('image')[0];
        if (!imageElement) {
            return {
                top: 0,
                left: 0,
                height: 0,
                width: 0,
            };
        }
        return this.getElementCoordinate(imageElement);
    }
    getHiddenLabelElement(frontLabelElement) {
        const idString = frontLabelElement.getAttribute('id');
        const splittedArray = idString?.split('-');
        const backLabelId = (splittedArray && splittedArray[0]) + '-' + (splittedArray && splittedArray[1]) + '-back';
        return document.getElementById(backLabelId);
    }
    isDropingZone(imageCoordinate, labelCoordinate, relativeCoordinate, labelElement) {
        const labelCoordinateWRTImage = this.calculateLabelCoordinateWRTImage(imageCoordinate, labelCoordinate, relativeCoordinate);
        const draggedElementCoordinate = this.getElementCoordinate(labelElement);
        const labelBottomY = labelCoordinateWRTImage.y + (draggedElementCoordinate.height || 0);
        const labelBottomX = labelCoordinateWRTImage.x + (draggedElementCoordinate.width || 0);
        const isLabelInLeftOrTop = labelCoordinateWRTImage.x < 0 || labelCoordinateWRTImage.y < 0;
        const isLabelInRight = labelBottomX > imageCoordinate.width;
        const isLabelInBottom = labelBottomY > imageCoordinate.height;
        return !isLabelInLeftOrTop && !isLabelInRight && !isLabelInBottom;
    }
    calculateLabelCoordinateWRTImage(imageCoordinate, labelCoordinate, relativeCoordinate) {
        const label_X = labelCoordinate.left + relativeCoordinate.x;
        const label_Y = labelCoordinate.top + relativeCoordinate.y;
        const image_X0 = imageCoordinate.left;
        const image_Y0 = imageCoordinate.top;
        const labelWRTImage_X = label_X - image_X0;
        const labelWRTImage_Y = label_Y - image_Y0;
        return {
            x: labelWRTImage_X,
            y: labelWRTImage_Y,
        };
    }
    getIndexOfDraggingElement(element) {
        const idString = element.getAttribute('id');
        return Number(idString?.split('-')[1]);
    }
    isOverlapping(target, destination) {
        const targetBottomX = target?.x + (target.width || 0);
        const targetBottomY = target.y + (target.height || 0);
        const destinationBottomX = destination.x + (destination.width || 0);
        const destinationBottomY = destination.y + (destination.height || 0);
        if (target.x > destinationBottomX || destination.x > targetBottomX) {
            return false;
        }
        if (target.y > destinationBottomY || destination.y > targetBottomY) {
            return false;
        }
        return true;
    }
}
ImageLabelDndService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelDndService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ImageLabelDndService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelDndService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelDndService, decorators: [{
            type: Injectable
        }] });

const ZERO_KEY = 48;
const NINE_KEY = 57;
const DECIMAL_POINT_KEY = 110;
const PERIOD_KEY = 190;
const BACKSPACE_KEY = 8;
const DELETE_KEY = 46;
const NUMPAD_ZERO_KEY = 96;
const NUMPAD_NINE_KEY = 105;
const LEFT_ARROW_KEY = 37;
const RIGHT_ARROW_KEY = 38;
const UP_ARROW_KEY = 39;
const DOWN_ARROW_KEY = 40;
const TAB_KEY = 9;
const MINUS_KEY = 189;
const NUMBAD_MINUS_KEY = 109;
const ESC_KEY = 27;
const SLASH_KEY = 191;

class SanitizeInputService {
    sanitizeInput(event, allowMinus = false, allowDecimal = true, allowSlash = false) {
        const eventKeyCode = event.keyCode || event.which;
        const eventTargetValue = event.target.value;
        if (eventTargetValue) {
            const DECIMAL_PATTERN = /^\d*\.\d*$/;
            const hasDecimal = DECIMAL_PATTERN.test(eventTargetValue);
            if (hasDecimal) {
                allowDecimal = false;
            }
        }
        if ((eventKeyCode >= ZERO_KEY && eventKeyCode <= NINE_KEY && !event.shiftKey) ||
            (allowDecimal &&
                (eventKeyCode === DECIMAL_POINT_KEY || (eventKeyCode === PERIOD_KEY && !event.shiftKey))) ||
            eventKeyCode === BACKSPACE_KEY ||
            eventKeyCode === DELETE_KEY ||
            (eventKeyCode >= NUMPAD_ZERO_KEY && eventKeyCode <= NUMPAD_NINE_KEY) ||
            (eventKeyCode >= LEFT_ARROW_KEY && eventKeyCode <= DOWN_ARROW_KEY) ||
            (eventKeyCode === TAB_KEY && event.shiftKey) ||
            eventKeyCode === TAB_KEY ||
            (event.ctrlKey && event.key.toLocaleLowerCase() === 'a') ||
            (event.metaKey && event.key.toLocaleLowerCase() === 'a') ||
            (event.ctrlKey && event.key.toLocaleLowerCase() === 'c') ||
            (event.metaKey && event.key.toLocaleLowerCase() === 'c') ||
            (event.ctrlKey && event.key.toLocaleLowerCase() === 'v') ||
            (event.metaKey && event.key.toLocaleLowerCase() === 'v') ||
            (event.ctrlKey && event.key.toLocaleLowerCase() === 'x') ||
            (event.metaKey && event.key.toLocaleLowerCase() === 'x') ||
            (((eventKeyCode === MINUS_KEY && !event.shiftKey) || eventKeyCode === NUMBAD_MINUS_KEY) &&
                allowMinus) ||
            (eventKeyCode === SLASH_KEY && allowSlash)) {
            return true;
        }
        return false;
    }
}
SanitizeInputService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SanitizeInputService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SanitizeInputService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SanitizeInputService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SanitizeInputService, decorators: [{
            type: Injectable
        }] });

class TextUtilityService {
    convertMathmlToSingleChar(content) {
        return content ? content.replace(/<math(.*?)<\/math>/g, ' ') : '';
    }
    isOnlyNumeric(text) {
        const NUMERIC_PATTERN = /[^0-9\\.-]/g;
        const hasNonNumeric = NUMERIC_PATTERN.test(text);
        if (hasNonNumeric) {
            return false;
        }
        return true;
    }
}
TextUtilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TextUtilityService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TextUtilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TextUtilityService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TextUtilityService, decorators: [{
            type: Injectable
        }] });

const prefix$o = '[Air Multiple Choice]';
const SETUP_EMPTY_ITEM$l = `${prefix$o} Setup Empty Item`;
class SetupEmptyItemAction$l {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$l;
    }
}
const UPDATE_SELECT_MULTIPLE = `${prefix$o} Update Select Multiple`;
class UpdateSelectMultipleAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SELECT_MULTIPLE;
    }
}
const ADD_CHOICE$4 = `${prefix$o} Add Choice`;
class AddChoiceAction$4 {
    constructor() {
        this.type = ADD_CHOICE$4;
    }
}
const UPDATE_CHOICE_CONTENT$3 = `${prefix$o} Update Choice Content`;
class UpdateChoiceContentAction$3 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE_CONTENT$3;
    }
}
const DELETE_CHOICE$4 = `${prefix$o} Delete Choice`;
class DeleteChoiceAction$4 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CHOICE$4;
    }
}
const ADD_ANSWER$2 = `${prefix$o} Add Answer`;
class AddAnswerAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_ANSWER$2;
    }
}
const DELETE_ANSWER$2 = `${prefix$o} Delete Answer`;
class DeleteAnswerAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ANSWER$2;
    }
}
const UPDATE_RATIONALE_CONTENT$1 = `${prefix$o} Update Rationale Content`;
class UpdateRationaleContentAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_RATIONALE_CONTENT$1;
    }
}
const TOGGLE_RATIONALE_VISIBILITY$2 = `${prefix$o} Toggle Rationale Visibility`;
class ToggleRationaleVisibilityAction$2 {
    constructor() {
        this.type = TOGGLE_RATIONALE_VISIBILITY$2;
    }
}
const TOGGLE_CHOICES_SHUFFLING$1 = `${prefix$o} Toggle Choices Shuffling`;
class ToggleChoicesShufflingAction$1 {
    constructor() {
        this.type = TOGGLE_CHOICES_SHUFFLING$1;
    }
}
const LOAD_AIR_ITEM$n = `${prefix$o} Load Air Item`;
class LoadAirItemAction$n {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$n;
    }
}
const MOVE_CHOICE_UP = `${prefix$o} Move Choice Up`;
class MoveChoiceUpAction {
    constructor(payload) {
        this.payload = payload;
        this.type = MOVE_CHOICE_UP;
    }
}
const MOVE_CHOICE_DOWN = `${prefix$o} Move Choice Down`;
class MoveChoiceDownAction {
    constructor(payload) {
        this.payload = payload;
        this.type = MOVE_CHOICE_DOWN;
    }
}
const RESET_STORE$o = `${prefix$o} Reset Store`;
class ResetStoreAction$o {
    constructor() {
        this.type = RESET_STORE$o;
    }
}

var airMultipleChoice_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$l,
    SetupEmptyItemAction: SetupEmptyItemAction$l,
    UPDATE_SELECT_MULTIPLE: UPDATE_SELECT_MULTIPLE,
    UpdateSelectMultipleAction: UpdateSelectMultipleAction,
    ADD_CHOICE: ADD_CHOICE$4,
    AddChoiceAction: AddChoiceAction$4,
    UPDATE_CHOICE_CONTENT: UPDATE_CHOICE_CONTENT$3,
    UpdateChoiceContentAction: UpdateChoiceContentAction$3,
    DELETE_CHOICE: DELETE_CHOICE$4,
    DeleteChoiceAction: DeleteChoiceAction$4,
    ADD_ANSWER: ADD_ANSWER$2,
    AddAnswerAction: AddAnswerAction$2,
    DELETE_ANSWER: DELETE_ANSWER$2,
    DeleteAnswerAction: DeleteAnswerAction$2,
    UPDATE_RATIONALE_CONTENT: UPDATE_RATIONALE_CONTENT$1,
    UpdateRationaleContentAction: UpdateRationaleContentAction$1,
    TOGGLE_RATIONALE_VISIBILITY: TOGGLE_RATIONALE_VISIBILITY$2,
    ToggleRationaleVisibilityAction: ToggleRationaleVisibilityAction$2,
    TOGGLE_CHOICES_SHUFFLING: TOGGLE_CHOICES_SHUFFLING$1,
    ToggleChoicesShufflingAction: ToggleChoicesShufflingAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$n,
    LoadAirItemAction: LoadAirItemAction$n,
    MOVE_CHOICE_UP: MOVE_CHOICE_UP,
    MoveChoiceUpAction: MoveChoiceUpAction,
    MOVE_CHOICE_DOWN: MOVE_CHOICE_DOWN,
    MoveChoiceDownAction: MoveChoiceDownAction,
    RESET_STORE: RESET_STORE$o,
    ResetStoreAction: ResetStoreAction$o
});

const prefix$n = '[Item Builder]';
const UPDATE_SCORING_TYPE = `${prefix$n} Update Scoring Type`;
class UpdateScoringTypeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SCORING_TYPE;
    }
}
const UPDATE_POINTS = `${prefix$n} Update Points`;
class UpdatePointsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_POINTS;
    }
}
const UPDATE_SCORING_VISIBILITY = `${prefix$n} Update Scoring Visibility`;
class UpdateScoreOptionsVisibility {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SCORING_VISIBILITY;
    }
}
const LOAD_AUTHORING_ITEM = `${prefix$n} Load Authoring Item`;
class LoadAuthoringItemAction {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AUTHORING_ITEM;
    }
}
const RESET_STORE$n = `${prefix$n} Reset Store`;
class ResetStoreAction$n {
    constructor() {
        this.type = RESET_STORE$n;
    }
}
const UPDATE_EXPLANATION = `${prefix$n} Update Explanation`;
class UpdateExplanationAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EXPLANATION;
    }
}
const UPDATE_CAL_TYPE = `${prefix$n} Update CALC type`;
class UpdateCAlcAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CAL_TYPE;
    }
}
const UPDATE_HINT = `${prefix$n} Update Hint`;
class UpdateHintAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_HINT;
    }
}
const UPDATE_EXAMPLE_RESPONSE = `${prefix$n} Update Example Response`;
class UpdateExampleResponseAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EXAMPLE_RESPONSE;
    }
}
const UPDATE_EXAMPLE = `${prefix$n} Update Example`;
class UpdateExampleAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EXAMPLE;
    }
}
const UPDATE_PROMPT = `${prefix$n} Update Prompt`;
class UpdatePromptAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_PROMPT;
    }
}

var ContentType;
(function (ContentType) {
    ContentType["File"] = "file";
    ContentType["Link"] = "link";
    ContentType["TextEntry"] = "text";
    ContentType["GoogleDrive"] = "googleDrive";
})(ContentType || (ContentType = {}));
var ContentState;
(function (ContentState) {
    ContentState[ContentState["Create"] = 0] = "Create";
    ContentState[ContentState["Edit"] = 1] = "Edit";
    ContentState[ContentState["Uploading"] = 2] = "Uploading";
    ContentState[ContentState["Saved"] = 3] = "Saved";
})(ContentState || (ContentState = {}));

// TODO: create a cleaner interface for state
const setResource = (state, payload, resourceType) => {
    const updatedResource = {
        content: payload.content,
        content_type: payload.contentType,
    };
    if (payload.contentType === ContentType.File) {
        updatedResource.file_url = payload.url;
    }
    return {
        ...state,
        resources: {
            ...state.resources,
            [resourceType]: updatedResource,
        },
    };
};

var ItemType;
(function (ItemType) {
    ItemType["ALGORITHMIC"] = "algorithmic";
    ItemType["AUDIO_RECORDING"] = "audio_recording";
    ItemType["BUCKETING"] = "bucketing";
    ItemType["CHOICE_MATRIX"] = "choice_matrix";
    ItemType["CLOZE"] = "cloze";
    ItemType["EQUATION_ENTRY"] = "equation_entry";
    ItemType["ESSAY"] = "essay";
    ItemType["EXTERNAL"] = "external";
    ItemType["FILL_IN_THE_BLANK"] = "fill_in_the_blank";
    ItemType["GRID_IN"] = "grid_in";
    ItemType["IMAGE_LABELING"] = "image_labels";
    ItemType["MATCHING"] = "matching";
    ItemType["MULTIPLE_CHOICE"] = "multiple_choice";
    ItemType["MULTIPLE_CHOICE_MULTI_SELECT"] = "multiple_select";
    ItemType["MULTIPLE_CHOICE_AUDIO"] = "multiple_choice_audio";
    ItemType["NUMBER_LINE"] = "number_line";
    ItemType["ORDERING"] = "ordering";
    ItemType["SELECT_TEXT"] = "select_text";
    ItemType["SHORT_ANSWER"] = "short_answer";
    ItemType["TRUE_FALSE"] = "true_false";
    ItemType["INSTRUCTION"] = "instruction";
    ItemType["FILE_UPLOAD"] = "file_upload";
    ItemType["GRAPHING"] = "graphing";
    ItemType["DRAWING"] = "drawing";
    ItemType["COORDINATE_GRAPH"] = "coordinate_grapher";
    ItemType["TABLE_ENTRY"] = "table_entry";
    ItemType["HOTSPOT"] = "hotspot";
})(ItemType || (ItemType = {}));

const initialState$p = {
    prompt: '',
    choices: [],
    answers: [],
    answer: undefined,
    selectMultiple: false,
    nextKey: 0,
    showRationales: false,
    random: false,
};
function airMultipleChoice(state = initialState$p, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.MULTIPLE_CHOICE) {
        return state;
    }
    switch (action.type) {
        case SETUP_EMPTY_ITEM$l: {
            return {
                ...state,
                choices: [
                    { key: '1', content: '', rationale: '' },
                    { key: '2', content: '', rationale: '' },
                    { key: '3', content: '', rationale: '' },
                    { key: '4', content: '', rationale: '' },
                ],
                nextKey: 5,
            };
        }
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case ADD_CHOICE$4: {
            return {
                ...state,
                choices: [
                    ...state.choices,
                    {
                        key: state.nextKey.toString(),
                        content: '',
                        rationale: '',
                    },
                ],
                nextKey: state.nextKey + 1,
            };
        }
        case UPDATE_CHOICE_CONTENT$3: {
            const payloadKey = action.payload.choice.key;
            const payloadContent = action.payload.content.trim();
            const choices = state.choices.map((choice) => {
                if (choice === action.payload.choice) {
                    return { ...choice, content: action.payload.content };
                }
                else {
                    return choice;
                }
            });
            if (state.selectMultiple) {
                return {
                    ...state,
                    choices: choices,
                    answers: state?.answers?.filter((answer) => {
                        return (payloadKey === answer && payloadContent) || payloadKey !== answer;
                    }),
                };
            }
            else {
                return {
                    ...state,
                    choices: choices,
                    answer: payloadKey === state.answer && !payloadContent ? '' : state.answer,
                };
            }
        }
        case DELETE_CHOICE$4: {
            const selectedChoice = action.payload.choice;
            return {
                ...state,
                choices: state.choices.filter((choice) => choice !== selectedChoice),
                answers: state.answers?.filter((answer) => answer !== selectedChoice.key),
                answer: state.answer === selectedChoice.key ? undefined : state.answer,
            };
        }
        case ADD_ANSWER$2: {
            const payloadContent = action.payload.content.trim();
            const payloadKey = action.payload.key;
            if (state.selectMultiple) {
                return { ...state, answers: !payloadContent ? state.answers : [...(state.answers || []), payloadKey] };
            }
            else {
                return { ...state, answer: !payloadContent ? state.answer : payloadKey };
            }
        }
        case DELETE_ANSWER$2: {
            return {
                ...state,
                answers: state.answers?.filter((answer) => answer !== action.payload.key),
                answer: state.answer === action.payload.key ? undefined : state.answer,
            };
        }
        case UPDATE_RATIONALE_CONTENT$1: {
            return {
                ...state,
                choices: state.choices.map((choice) => {
                    if (choice === action.payload.choice) {
                        return { ...choice, rationale: action.payload.content };
                    }
                    else {
                        return choice;
                    }
                }),
            };
        }
        case TOGGLE_RATIONALE_VISIBILITY$2: {
            return { ...state, showRationales: !state.showRationales };
        }
        case UPDATE_SELECT_MULTIPLE: {
            return { ...state, selectMultiple: action.payload };
        }
        case TOGGLE_CHOICES_SHUFFLING$1: {
            return { ...state, random: !state.random };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case MOVE_CHOICE_UP: {
            const currentIndex = state.choices.indexOf(action.payload);
            return {
                ...state,
                choices: state.choices.map((choice, index) => {
                    if (index === currentIndex) {
                        return state.choices[index - 1];
                    }
                    else if (index === currentIndex - 1) {
                        return state.choices[index + 1];
                    }
                    return choice;
                }),
            };
        }
        case MOVE_CHOICE_DOWN: {
            const currentIndex = state.choices.indexOf(action.payload);
            return {
                ...state,
                choices: state.choices.map((choice, index) => {
                    if (index === currentIndex) {
                        return state.choices[index + 1];
                    }
                    else if (index === currentIndex + 1) {
                        return state.choices[index - 1];
                    }
                    return choice;
                }),
            };
        }
        case RESET_STORE$o: {
            return initialState$p;
        }
        case LOAD_AIR_ITEM$n: {
            const fields = getAirItemFieldsData(action.payload);
            return {
                ...state,
                ...fields,
            };
        }
        default: {
            return state;
        }
    }
}
function getAirItemFieldsData(payload) {
    let data = {};
    if ('prompt' in payload && payload.choices) {
        data = {
            prompt: payload.prompt,
            choices: payload.choices.map((choice) => {
                return { ...choice, rationale: choice.rationale || '' };
            }),
            answers: payload.answers || [],
            answer: payload.answer,
            nextKey: getNextKey$4(payload),
            selectMultiple: !payload.answer,
            showRationales: payload.choices.length > 0 && payload.choices[0].rationale !== undefined,
            resources: payload.resources,
        };
    }
    if ('random' in payload) {
        data.random = payload.random;
    }
    return data;
}
function getNextKey$4(air) {
    const maxKey = air.choices
        .filter((choice) => choice.key.match(/^\d+$/))
        .map((choice) => parseInt(choice.key, 10))
        .reduce((a, b) => Math.max(a, b), 0);
    return maxKey + 1;
}
function getPureAirMultipleChoice$1(state) {
    const choices = state.choices
        .filter((choice) => choice.content.trim())
        .map((choice) => {
        return state.showRationales ? choice : { key: choice.key, content: choice.content };
    });
    const air = {
        prompt: state.prompt,
        random: state.random,
        choices,
        resources: state.resources,
    };
    if (state.selectMultiple) {
        air.answers = state.answers;
    }
    else {
        air.answer = state.answer;
    }
    return air;
}

var airMultipleChoice_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$p,
    airMultipleChoice: airMultipleChoice,
    getPureAirMultipleChoice: getPureAirMultipleChoice$1,
    'default': airMultipleChoice
});

const prefix$m = '[Air Ordering]';
const SETUP_EMPTY_ITEM$k = `${prefix$m} Setup Empty Item`;
class SetupEmptyItemAction$k {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$k;
    }
}
const ADD_CHOICE$3 = `${prefix$m} Add Choice`;
class AddChoiceAction$3 {
    constructor() {
        this.type = ADD_CHOICE$3;
    }
}
const UPDATE_CHOICE_CONTENT$2 = `${prefix$m} Update Choice Content`;
class UpdateChoiceContentAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE_CONTENT$2;
    }
}
const TOGGLE_DISTRACTOR_VISIBILITY = `${prefix$m} Toggle Distractor Visibility`;
class ToggleDistractorVisibilityAction {
    constructor() {
        this.type = TOGGLE_DISTRACTOR_VISIBILITY;
    }
}
const DELETE_CHOICE$3 = `${prefix$m} Delete Choice`;
class DeleteChoiceAction$3 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CHOICE$3;
    }
}
const ADD_DISTRACTOR$1 = `${prefix$m} Add Distractor`;
class AddDistractorAction$1 {
    constructor() {
        this.type = ADD_DISTRACTOR$1;
    }
}
const UPDATE_DISTRACTOR_CONTENT$1 = `${prefix$m} Update Distractor Content`;
class UpdateDistractorContentAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_DISTRACTOR_CONTENT$1;
    }
}
const DELETE_DISTRACTOR$1 = `${prefix$m} Delete Distractor`;
class DeleteDistractorAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_DISTRACTOR$1;
    }
}
const LOAD_AIR_ITEM$m = `${prefix$m} Load Air Item`;
class LoadAirItemAction$m {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$m;
    }
}
const RESET_STORE$m = `${prefix$m} Reset Store`;
class ResetStoreAction$m {
    constructor() {
        this.type = RESET_STORE$m;
    }
}

var airOrdering_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$k,
    SetupEmptyItemAction: SetupEmptyItemAction$k,
    ADD_CHOICE: ADD_CHOICE$3,
    AddChoiceAction: AddChoiceAction$3,
    UPDATE_CHOICE_CONTENT: UPDATE_CHOICE_CONTENT$2,
    UpdateChoiceContentAction: UpdateChoiceContentAction$2,
    TOGGLE_DISTRACTOR_VISIBILITY: TOGGLE_DISTRACTOR_VISIBILITY,
    ToggleDistractorVisibilityAction: ToggleDistractorVisibilityAction,
    DELETE_CHOICE: DELETE_CHOICE$3,
    DeleteChoiceAction: DeleteChoiceAction$3,
    ADD_DISTRACTOR: ADD_DISTRACTOR$1,
    AddDistractorAction: AddDistractorAction$1,
    UPDATE_DISTRACTOR_CONTENT: UPDATE_DISTRACTOR_CONTENT$1,
    UpdateDistractorContentAction: UpdateDistractorContentAction$1,
    DELETE_DISTRACTOR: DELETE_DISTRACTOR$1,
    DeleteDistractorAction: DeleteDistractorAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$m,
    LoadAirItemAction: LoadAirItemAction$m,
    RESET_STORE: RESET_STORE$m,
    ResetStoreAction: ResetStoreAction$m
});

const initialState$o = {
    prompt: '',
    choices: [],
    distractors: [],
    answers: [],
    nextKey: 0,
    showDistractors: false,
};
function getNextKey$3(choices) {
    const maxResponseNumber = choices
        .map((choice) => choice.key.match(/^response(\d+)$/))
        .filter((match) => match)
        .map((match) => {
        if (!match || !match[1]) {
            return 0;
        }
        return parseInt(match[1], 10);
    })
        .reduce((a, b) => Math.max(a, b), 0);
    return maxResponseNumber + 1;
}
// TODO: refactor action to have a proper type
function airOrdering(state = initialState$o, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.ORDERING) {
        return state;
    }
    switch (action.type) {
        case SETUP_EMPTY_ITEM$k: {
            return {
                ...state,
                choices: [
                    { key: 'response0', text: '' },
                    { key: 'response1', text: '' },
                    { key: 'response2', text: '' },
                ],
                answers: ['response0', 'response1', 'response2'],
                nextKey: 3,
            };
        }
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case ADD_CHOICE$3: {
            return {
                ...state,
                answers: [...state.answers, 'response' + state.nextKey],
                choices: [
                    ...state.choices,
                    {
                        key: 'response' + state.nextKey,
                        text: '',
                    },
                ],
                nextKey: state.nextKey + 1,
            };
        }
        case UPDATE_CHOICE_CONTENT$2: {
            return {
                ...state,
                choices: state.choices.map((choice) => {
                    if (choice === action.payload.choice) {
                        return { key: choice.key, text: action.payload.content };
                    }
                    else {
                        return choice;
                    }
                }),
            };
        }
        case DELETE_CHOICE$3: {
            return {
                ...state,
                choices: state.choices.filter((choice) => choice !== action.payload),
                answers: state.answers.filter((answer) => answer !== action.payload.key),
            };
        }
        case ADD_DISTRACTOR$1: {
            return {
                ...state,
                distractors: [
                    ...state.distractors,
                    {
                        key: 'response' + state.nextKey,
                        text: '',
                    },
                ],
                nextKey: state.nextKey + 1,
            };
        }
        case UPDATE_DISTRACTOR_CONTENT$1: {
            return {
                ...state,
                distractors: state.distractors.map((distractor) => {
                    if (distractor === action.payload.distractor) {
                        return {
                            ...distractor,
                            text: action.payload.content,
                        };
                    }
                    else {
                        return distractor;
                    }
                }),
            };
        }
        case DELETE_DISTRACTOR$1: {
            return {
                ...state,
                distractors: state.distractors.filter((distractor) => distractor !== action.payload),
                showDistractors: state.distractors.length < 2 ? false : true,
            };
        }
        case TOGGLE_DISTRACTOR_VISIBILITY: {
            if (state.distractors.length === 0) {
                return {
                    ...state,
                    distractors: [
                        ...state.distractors,
                        {
                            key: 'response' + state.nextKey,
                            text: '',
                        },
                    ],
                    nextKey: state.nextKey + 1,
                    showDistractors: !state.showDistractors,
                };
            }
            else {
                return {
                    ...state,
                    distractors: [],
                    showDistractors: !state.showDistractors,
                };
            }
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$m: {
            return initialState$o;
        }
        case LOAD_AIR_ITEM$m: {
            const distractors = action.payload.choices.filter((choice) => {
                return !action.payload.answers.includes(choice.key);
            });
            return {
                ...state,
                prompt: action.payload.prompt,
                answers: action.payload.answers,
                choices: action.payload.answers.map((answer) => {
                    return action.payload.choices.find((choice) => choice.key === answer);
                }),
                distractors,
                showDistractors: distractors.length > 0,
                nextKey: getNextKey$3(action.payload.choices),
                resources: action.payload.resources,
            };
        }
        default: {
            return state;
        }
    }
}
function getPureAirOrdering$1(state) {
    const choices = state.choices.filter((choice) => choice.text.trim());
    const distractors = state.distractors.filter((distractor) => distractor.text.trim());
    const allChoices = state.showDistractors ? choices.concat(distractors) : choices;
    return {
        prompt: state.prompt,
        choices: allChoices,
        answers: choices.map((choice) => choice.key),
        startFromKey: state.showDistractors,
        resources: state.resources,
    };
}

var airOrdering_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$o,
    airOrdering: airOrdering,
    getPureAirOrdering: getPureAirOrdering$1,
    'default': airOrdering
});

const prefix$l = '[Air True False]';
const SETUP_EMPTY_ITEM$j = `${prefix$l} Setup Empty Item`;
class SetupEmptyItemAction$j {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$j;
    }
}
const UPDATE_CORRECT_ANSWER$2 = `${prefix$l} Add Answer`;
class UpdateCorrectAnswerAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CORRECT_ANSWER$2;
    }
}
const UPDATE_TRUE_RATIONALE_CONTENT = `${prefix$l} Update True Rationale Content`;
class UpdateTrueRationaleContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TRUE_RATIONALE_CONTENT;
    }
}
const UPDATE_FALSE_RATIONALE_CONTENT = `${prefix$l} Update False Rationale Content`;
class UpdateFalseRationaleContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_FALSE_RATIONALE_CONTENT;
    }
}
const TOGGLE_RATIONALE_VISIBILITY$1 = `${prefix$l} Toggle Rationale Visibility`;
class ToggleRationaleVisibilityAction$1 {
    constructor() {
        this.type = TOGGLE_RATIONALE_VISIBILITY$1;
    }
}
const LOAD_AIR_ITEM$l = `${prefix$l} Load Air Item`;
class LoadAirItemAction$l {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$l;
    }
}
const RESET_STORE$l = `${prefix$l} Reset Store`;
class ResetStoreAction$l {
    constructor() {
        this.type = RESET_STORE$l;
    }
}

var airTrueFalse_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$j,
    SetupEmptyItemAction: SetupEmptyItemAction$j,
    UPDATE_CORRECT_ANSWER: UPDATE_CORRECT_ANSWER$2,
    UpdateCorrectAnswerAction: UpdateCorrectAnswerAction$2,
    UPDATE_TRUE_RATIONALE_CONTENT: UPDATE_TRUE_RATIONALE_CONTENT,
    UpdateTrueRationaleContentAction: UpdateTrueRationaleContentAction,
    UPDATE_FALSE_RATIONALE_CONTENT: UPDATE_FALSE_RATIONALE_CONTENT,
    UpdateFalseRationaleContentAction: UpdateFalseRationaleContentAction,
    TOGGLE_RATIONALE_VISIBILITY: TOGGLE_RATIONALE_VISIBILITY$1,
    ToggleRationaleVisibilityAction: ToggleRationaleVisibilityAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$l,
    LoadAirItemAction: LoadAirItemAction$l,
    RESET_STORE: RESET_STORE$l,
    ResetStoreAction: ResetStoreAction$l
});

const initialState$n = {
    prompt: '',
    answer: '',
    trueRationale: '',
    falseRationale: '',
    showRationales: false,
};
function airTrueFalse(state = initialState$n, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.TRUE_FALSE) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case UPDATE_CORRECT_ANSWER$2: {
            return { ...state, answer: action.payload };
        }
        case UPDATE_TRUE_RATIONALE_CONTENT: {
            return { ...state, trueRationale: action.payload };
        }
        case UPDATE_FALSE_RATIONALE_CONTENT: {
            return { ...state, falseRationale: action.payload };
        }
        case TOGGLE_RATIONALE_VISIBILITY$1: {
            return { ...state, showRationales: !state.showRationales };
        }
        case LOAD_AIR_ITEM$l: {
            return {
                prompt: action.payload.prompt,
                answer: action.payload.answer,
                trueRationale: action.payload.trueRationale || '',
                falseRationale: action.payload.falseRationale || '',
                showRationales: !!(action.payload.trueRationale || action.payload.falseRationale),
                resources: action.payload.resources,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$l: {
            return initialState$n;
        }
        default: {
            return state;
        }
    }
}
function getPureAirTrueFalse$1(state) {
    const air = {
        prompt: state.prompt,
        answer: state.answer,
        resources: state.resources,
    };
    if (state.showRationales) {
        air.trueRationale = state.trueRationale;
        air.falseRationale = state.falseRationale;
    }
    return air;
}

var airTrueFalse_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$n,
    airTrueFalse: airTrueFalse,
    getPureAirTrueFalse: getPureAirTrueFalse$1,
    'default': airTrueFalse
});

const prefix$k = '[Air Essay]';
const SETUP_EMPTY_ITEM$i = `${prefix$k} Setup Empty Item`;
class SetupEmptyItemAction$i {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$i;
    }
}
const RESET_STORE$k = `${prefix$k} Reset Store`;
class ResetStoreAction$k {
    constructor() {
        this.type = RESET_STORE$k;
    }
}
const UPDATE_EDITOR_TYPE$1 = `${prefix$k} Update Editor Type`;
class UpdateEditorTypeAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EDITOR_TYPE$1;
    }
}
const UPDATE_CHARACTERS_RESPONSE_LIMIT$1 = `${prefix$k} Update Character Response Limit`;
class UpdateCharactersResponseLimitAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHARACTERS_RESPONSE_LIMIT$1;
    }
}
const UPDATE_WORDS_RESPONSE_LIMIT$1 = `${prefix$k} Update Words Response Limit`;
class UpdateWordsResponseLimitAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_WORDS_RESPONSE_LIMIT$1;
    }
}
const SELECT_WORDS_RESPONSE_LIMIT$1 = `${prefix$k} Select Words Response Limit`;
class SelectWordsResponseLimitAction$1 {
    constructor() {
        this.type = SELECT_WORDS_RESPONSE_LIMIT$1;
    }
}
const SELECT_CHARACTERS_RESPONSE_LIMIT$1 = `${prefix$k} Select Characters Response Limit`;
class SelectCharactersResponseLimitAction$1 {
    constructor() {
        this.type = SELECT_CHARACTERS_RESPONSE_LIMIT$1;
    }
}
const LOAD_AIR_ITEM$k = `${prefix$k} Load Air Item`;
class LoadAirItemAction$k {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$k;
    }
}

var airEssay_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$i,
    SetupEmptyItemAction: SetupEmptyItemAction$i,
    RESET_STORE: RESET_STORE$k,
    ResetStoreAction: ResetStoreAction$k,
    UPDATE_EDITOR_TYPE: UPDATE_EDITOR_TYPE$1,
    UpdateEditorTypeAction: UpdateEditorTypeAction$1,
    UPDATE_CHARACTERS_RESPONSE_LIMIT: UPDATE_CHARACTERS_RESPONSE_LIMIT$1,
    UpdateCharactersResponseLimitAction: UpdateCharactersResponseLimitAction$1,
    UPDATE_WORDS_RESPONSE_LIMIT: UPDATE_WORDS_RESPONSE_LIMIT$1,
    UpdateWordsResponseLimitAction: UpdateWordsResponseLimitAction$1,
    SELECT_WORDS_RESPONSE_LIMIT: SELECT_WORDS_RESPONSE_LIMIT$1,
    SelectWordsResponseLimitAction: SelectWordsResponseLimitAction$1,
    SELECT_CHARACTERS_RESPONSE_LIMIT: SELECT_CHARACTERS_RESPONSE_LIMIT$1,
    SelectCharactersResponseLimitAction: SelectCharactersResponseLimitAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$k,
    LoadAirItemAction: LoadAirItemAction$k
});

const initialState$m = {
    prompt: '',
    editorType: 'Standard',
    maxCharacters: null,
    maxWords: 1000,
    responseLimitInCharacters: false,
    responseLimitInWords: true,
    isAnswerLimitExceeded: false,
};
function airEssay(state = initialState$m, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.ESSAY) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            const content = action.payload.content;
            const answerCount = content.split('__').length - 1;
            return {
                ...state,
                prompt: content,
                isAnswerLimitExceeded: answerCount > 20,
            };
        }
        case LOAD_AIR_ITEM$k: {
            return {
                ...state,
                prompt: action.payload.prompt,
                editorType: action.payload.editorType,
                maxCharacters: action.payload.maxCharacters || null,
                maxWords: action.payload.maxWords || null,
                responseLimitInCharacters: action.payload.maxCharacters ? true : false,
                responseLimitInWords: action.payload.maxWords ? true : false,
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$k: {
            return initialState$m;
        }
        case UPDATE_EDITOR_TYPE$1: {
            return { ...state, editorType: action.payload };
        }
        case SELECT_CHARACTERS_RESPONSE_LIMIT$1: {
            return {
                ...state,
                maxWords: null,
                maxCharacters: 1000,
                responseLimitInCharacters: true,
                responseLimitInWords: false,
            };
        }
        case SELECT_WORDS_RESPONSE_LIMIT$1: {
            return {
                ...state,
                maxCharacters: null,
                maxWords: 1000,
                responseLimitInCharacters: false,
                responseLimitInWords: true,
            };
        }
        case UPDATE_WORDS_RESPONSE_LIMIT$1: {
            return {
                ...state,
                maxWords: action.payload ? Number(action.payload) : null,
            };
        }
        case UPDATE_CHARACTERS_RESPONSE_LIMIT$1: {
            return {
                ...state,
                maxCharacters: action.payload ? Number(action.payload) : null,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        default: {
            return state;
        }
    }
}
function getPureAirEssay$1(state) {
    const air = {
        prompt: state.prompt,
        editorType: state.editorType,
        resources: state.resources,
    };
    if (state.maxCharacters) {
        air.maxCharacters = filterFloat$1(state.maxCharacters);
    }
    if (state.maxWords) {
        air.maxWords = filterFloat$1(state.maxWords);
    }
    return air;
}
function filterFloat$1(value) {
    const LIMIT_PATTERN = /^\s*(?=.*[1-9])\d*\s*$/;
    if (LIMIT_PATTERN.test(value.toString())) {
        return Number(value);
    }
    return null;
}

var airEssay_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$m,
    airEssay: airEssay,
    getPureAirEssay: getPureAirEssay$1,
    'default': airEssay
});

const prefix$j = '[Air Short Answer]';
const SETUP_EMPTY_ITEM$h = `${prefix$j} Setup Empty Item`;
class SetupEmptyItemAction$h {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$h;
    }
}
const RESET_STORE$j = `${prefix$j} Reset Store`;
class ResetStoreAction$j {
    constructor() {
        this.type = RESET_STORE$j;
    }
}
const UPDATE_CHARACTERS_RESPONSE_LIMIT = `${prefix$j} Update Character Response Limit`;
class UpdateCharactersResponseLimitAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHARACTERS_RESPONSE_LIMIT;
    }
}
const UPDATE_WORDS_RESPONSE_LIMIT = `${prefix$j} Update Words Response Limit`;
class UpdateWordsResponseLimitAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_WORDS_RESPONSE_LIMIT;
    }
}
const SELECT_WORDS_RESPONSE_LIMIT = `${prefix$j} Select Words Response Limit`;
class SelectWordsResponseLimitAction {
    constructor() {
        this.type = SELECT_WORDS_RESPONSE_LIMIT;
    }
}
const SELECT_CHARACTERS_RESPONSE_LIMIT = `${prefix$j} Select Characters Response Limit`;
class SelectCharactersResponseLimitAction {
    constructor() {
        this.type = SELECT_CHARACTERS_RESPONSE_LIMIT;
    }
}
const LOAD_AIR_ITEM$j = `${prefix$j} Load Air Item`;
class LoadAirItemAction$j {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$j;
    }
}

var airShortAnswer_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$h,
    SetupEmptyItemAction: SetupEmptyItemAction$h,
    RESET_STORE: RESET_STORE$j,
    ResetStoreAction: ResetStoreAction$j,
    UPDATE_CHARACTERS_RESPONSE_LIMIT: UPDATE_CHARACTERS_RESPONSE_LIMIT,
    UpdateCharactersResponseLimitAction: UpdateCharactersResponseLimitAction,
    UPDATE_WORDS_RESPONSE_LIMIT: UPDATE_WORDS_RESPONSE_LIMIT,
    UpdateWordsResponseLimitAction: UpdateWordsResponseLimitAction,
    SELECT_WORDS_RESPONSE_LIMIT: SELECT_WORDS_RESPONSE_LIMIT,
    SelectWordsResponseLimitAction: SelectWordsResponseLimitAction,
    SELECT_CHARACTERS_RESPONSE_LIMIT: SELECT_CHARACTERS_RESPONSE_LIMIT,
    SelectCharactersResponseLimitAction: SelectCharactersResponseLimitAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$j,
    LoadAirItemAction: LoadAirItemAction$j
});

const initialState$l = {
    prompt: '',
    maxCharacters: 1000,
    maxWords: null,
    responseLimitInCharacters: true,
    responseLimitInWords: false,
};
function airShortAnswer(state = initialState$l, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.SHORT_ANSWER) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case SELECT_CHARACTERS_RESPONSE_LIMIT: {
            return {
                ...state,
                maxWords: null,
                maxCharacters: 1000,
                responseLimitInCharacters: true,
                responseLimitInWords: false,
            };
        }
        case SELECT_WORDS_RESPONSE_LIMIT: {
            return {
                ...state,
                maxCharacters: null,
                maxWords: 1000,
                responseLimitInCharacters: false,
                responseLimitInWords: true,
            };
        }
        case UPDATE_WORDS_RESPONSE_LIMIT: {
            return {
                ...state,
                maxWords: action.payload ? Number(action.payload) : null,
            };
        }
        case UPDATE_CHARACTERS_RESPONSE_LIMIT: {
            return {
                ...state,
                maxCharacters: action.payload ? Number(action.payload) : null,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        case LOAD_AIR_ITEM$j: {
            return {
                ...state,
                prompt: action.payload.prompt,
                maxCharacters: action.payload.maxCharacters || null,
                maxWords: action.payload.maxWords || null,
                responseLimitInCharacters: action.payload.maxCharacters ? true : false,
                responseLimitInWords: action.payload.maxWords ? true : false,
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$j: {
            return initialState$l;
        }
        default: {
            return state;
        }
    }
}
function getPureAirShortAnswer$1(state) {
    const air = {
        prompt: state.prompt,
        resources: state.resources,
    };
    if (state.maxCharacters) {
        air.maxCharacters = filterFloat(state.maxCharacters);
    }
    if (state.maxWords) {
        air.maxWords = filterFloat(state.maxWords);
    }
    return air;
}
function filterFloat(value) {
    const LIMIT_PATTERN = /^\s*(?=.*[1-9])\d*\s*$/;
    if (LIMIT_PATTERN.test(value.toString())) {
        return Number(value);
    }
    return null;
}

var airShortAnswer_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$l,
    airShortAnswer: airShortAnswer,
    getPureAirShortAnswer: getPureAirShortAnswer$1,
    'default': airShortAnswer
});

const prefix$i = '[Air Fib]';
const SETUP_EMPTY_ITEM$g = `${prefix$i} Setup Empty Item`;
class SetupEmptyItemAction$g {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$g;
    }
}
const ADD_ANSWER_VALUE = `${prefix$i} Add Answer Value`;
class AddAnswerValueAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_ANSWER_VALUE;
    }
}
const UPDATE_ANSWER_VALUE = `${prefix$i} Update Answer Value`;
class UpdateAnswerValueAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER_VALUE;
    }
}
const DELETE_ANSWER_VALUE = `${prefix$i} Delete Answer Value`;
class DeleteAnswerValueAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ANSWER_VALUE;
    }
}
const TOGGLE_INPUT_FORMAT = `${prefix$i} Toggle Input Format`;
class ToggleInputFormatAction {
    constructor() {
        this.type = TOGGLE_INPUT_FORMAT;
    }
}
const TOGGLE_CASE_SENSITIVE = `${prefix$i} Toggle Case Sensitive`;
class ToggleCaseSensitive {
    constructor() {
        this.type = TOGGLE_CASE_SENSITIVE;
    }
}
const TOGGLE_IGNORE_ACCENTS = `${prefix$i} Toggle Ignore Accents`;
class ToggleIgnoreAccents {
    constructor() {
        this.type = TOGGLE_IGNORE_ACCENTS;
    }
}
const TOGGLE_IGNORE_PUNCTUATION = `${prefix$i} Toggle Ignore Punctuation`;
class ToggleIgnorePunction {
    constructor() {
        this.type = TOGGLE_IGNORE_PUNCTUATION;
    }
}
const TOGGLE_IGNORE_ANSWER_ORDER = `${prefix$i} Toggle Ignore Answer Order`;
class ToggleIgnoreAnswerOrder {
    constructor() {
        this.type = TOGGLE_IGNORE_ANSWER_ORDER;
    }
}
const TOGGLE_IGNORE_SPACING = `${prefix$i} Toggle Ignore Spacing`;
class ToggleIgnoreSpacing {
    constructor() {
        this.type = TOGGLE_IGNORE_SPACING;
    }
}
const SELECT_TOLERANCE_TYPE = `${prefix$i} Select Tolerance Type`;
class SelectToleranceType {
    constructor(payload) {
        this.payload = payload;
        this.type = SELECT_TOLERANCE_TYPE;
    }
}
const UPDATE_TOLERANCE_VALUE = `${prefix$i} Update Tolerance Value`;
class UpdateToleranceValue {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TOLERANCE_VALUE;
    }
}
const UPDATE_PALETTE_LANG = `${prefix$i} Update Palette Language`;
class UpdatePaletteLanguage {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_PALETTE_LANG;
    }
}
const LOAD_AIR_ITEM$i = `${prefix$i} Load Air Item`;
class LoadAirItemAction$i {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$i;
    }
}
const RESET_STORE$i = `${prefix$i} Reset Store`;
class ResetStoreAction$i {
    constructor() {
        this.type = RESET_STORE$i;
    }
}

var airFib_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$g,
    SetupEmptyItemAction: SetupEmptyItemAction$g,
    ADD_ANSWER_VALUE: ADD_ANSWER_VALUE,
    AddAnswerValueAction: AddAnswerValueAction,
    UPDATE_ANSWER_VALUE: UPDATE_ANSWER_VALUE,
    UpdateAnswerValueAction: UpdateAnswerValueAction,
    DELETE_ANSWER_VALUE: DELETE_ANSWER_VALUE,
    DeleteAnswerValueAction: DeleteAnswerValueAction,
    TOGGLE_INPUT_FORMAT: TOGGLE_INPUT_FORMAT,
    ToggleInputFormatAction: ToggleInputFormatAction,
    TOGGLE_CASE_SENSITIVE: TOGGLE_CASE_SENSITIVE,
    ToggleCaseSensitive: ToggleCaseSensitive,
    TOGGLE_IGNORE_ACCENTS: TOGGLE_IGNORE_ACCENTS,
    ToggleIgnoreAccents: ToggleIgnoreAccents,
    TOGGLE_IGNORE_PUNCTUATION: TOGGLE_IGNORE_PUNCTUATION,
    ToggleIgnorePunction: ToggleIgnorePunction,
    TOGGLE_IGNORE_ANSWER_ORDER: TOGGLE_IGNORE_ANSWER_ORDER,
    ToggleIgnoreAnswerOrder: ToggleIgnoreAnswerOrder,
    TOGGLE_IGNORE_SPACING: TOGGLE_IGNORE_SPACING,
    ToggleIgnoreSpacing: ToggleIgnoreSpacing,
    SELECT_TOLERANCE_TYPE: SELECT_TOLERANCE_TYPE,
    SelectToleranceType: SelectToleranceType,
    UPDATE_TOLERANCE_VALUE: UPDATE_TOLERANCE_VALUE,
    UpdateToleranceValue: UpdateToleranceValue,
    UPDATE_PALETTE_LANG: UPDATE_PALETTE_LANG,
    UpdatePaletteLanguage: UpdatePaletteLanguage,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$i,
    LoadAirItemAction: LoadAirItemAction$i,
    RESET_STORE: RESET_STORE$i,
    ResetStoreAction: ResetStoreAction$i
});

const initialState$k = {
    prompt: '',
    answers: [],
    inputFormat: 'any',
    caseSensitive: false,
    ignoreAccents: false,
    ignoreSpacing: false,
    ignorePunctuation: false,
    allowAnyOrder: false,
    enableFormatMasking: false,
    tolerance: {
        type: 'none',
        value: null,
    },
    tooManyAnswers: false,
};
function airFib(state = initialState$k, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.FILL_IN_THE_BLANK) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            const content = action.payload.content;
            const blankCount = content.split('__').length - 1;
            if (blankCount > 20) {
                return {
                    ...state,
                    tooManyAnswers: true,
                };
            }
            else {
                return {
                    ...state,
                    prompt: content,
                    answers: Array(blankCount)
                        .fill(true)
                        .map((_, index) => {
                        if (state.answers[index]) {
                            return state.answers[index];
                        }
                        else {
                            return {
                                response: 'response' + index,
                                values: [''],
                            };
                        }
                    }),
                    tooManyAnswers: false,
                };
            }
        }
        case ADD_ANSWER_VALUE: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        values: answer === action.payload ? [...answer.values, ''] : answer.values,
                    };
                }),
            };
        }
        case UPDATE_ANSWER_VALUE: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer === action.payload.answer) {
                        const values = [...answer.values];
                        values[action.payload.index] = action.payload.content;
                        return { ...answer, values };
                    }
                    else {
                        return { ...answer, values: answer.values };
                    }
                }),
            };
        }
        case UPDATE_PALETTE_LANG: {
            return {
                ...state,
                paletteLanguage: action.payload,
            };
        }
        case DELETE_ANSWER_VALUE: {
            const answerValues = [...action.payload.answer.values];
            answerValues.splice(action.payload.answerIndex, 1);
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        values: action.payload.answer === answer ? answerValues : answer.values,
                    };
                }),
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case TOGGLE_CASE_SENSITIVE: {
            return {
                ...state,
                caseSensitive: !state.caseSensitive,
            };
        }
        case TOGGLE_IGNORE_ACCENTS: {
            return {
                ...state,
                ignoreAccents: !state.ignoreAccents,
            };
        }
        case TOGGLE_IGNORE_PUNCTUATION: {
            return {
                ...state,
                ignorePunctuation: !state.ignorePunctuation,
            };
        }
        case TOGGLE_IGNORE_SPACING: {
            return {
                ...state,
                ignoreSpacing: !state.ignoreSpacing,
            };
        }
        case TOGGLE_IGNORE_ANSWER_ORDER: {
            return {
                ...state,
                allowAnyOrder: !state.allowAnyOrder,
            };
        }
        case TOGGLE_INPUT_FORMAT: {
            return {
                ...state,
                inputFormat: state.inputFormat === 'numeric' ? 'any' : 'numeric',
                caseSensitive: false,
                ignoreAccents: false,
                ignoreSpacing: false,
                ignorePunctuation: false,
                allowAnyOrder: false,
                answers: state.answers.map((answer) => {
                    if (state.inputFormat === 'any') {
                        return {
                            ...answer,
                            values: answer.values.map((value) => (/^[-\d]+$/.test(value) ? value : '')),
                        };
                    }
                    else {
                        return answer;
                    }
                }),
            };
        }
        case SELECT_TOLERANCE_TYPE: {
            return {
                ...state,
                tolerance: {
                    ...state.tolerance,
                    type: action.payload.toLowerCase(),
                    value: null,
                },
            };
        }
        case UPDATE_TOLERANCE_VALUE: {
            return {
                ...state,
                tolerance: {
                    ...state.tolerance,
                    value: action.payload,
                },
            };
        }
        case LOAD_AIR_ITEM$i: {
            const payload = action.payload;
            const prompt = new ResponseToUnderscorePipe().transform(payload.prompt);
            return {
                ...state,
                prompt,
                answers: payload.answers,
                inputFormat: payload.inputFormat,
                caseSensitive: payload.caseSensitive,
                ignoreAccents: payload.ignoreAccents,
                ignoreSpacing: payload.ignoreSpacing,
                ignorePunctuation: payload.ignorePunctuation,
                allowAnyOrder: payload.allowAnyOrder,
                enableFormatMasking: payload.enableFormatMasking,
                tolerance: payload.tolerance,
                resources: action.payload.resources,
                paletteLanguage: action.payload.paletteLanguage || 'none',
            };
        }
        case RESET_STORE$i: {
            return initialState$k;
        }
        default: {
            return state;
        }
    }
}
function getPureAirFib$1(state) {
    const airFib = {
        ...state,
        answers: state.answers.map((answer) => {
            return {
                ...answer,
                values: answer.values.filter((value, index) => {
                    return index === 0 || value.trim() !== '';
                }),
            };
        }),
        tolerance: getTolerance(state.tolerance),
        prompt: generateStem$2(state),
        resources: state.resources,
        paletteLanguage: state.paletteLanguage,
    };
    if (state.tolerance && !state.tolerance.value) {
        delete airFib.tolerance;
    }
    if (state.paletteLanguage === 'none') {
        delete airFib.paletteLanguage;
    }
    return airFib;
}
function getTolerance(tolerance) {
    if (!tolerance) {
        return undefined;
    }
    const { type, value } = tolerance;
    return {
        type,
        value: value && !value.toString().endsWith('.') ? Number(value) : null,
    };
}
function generateStem$2(state) {
    return state.prompt.split('__').reduce((stem, value, index) => {
        if (state.answers[index]) {
            return stem + value + '${response:' + state.answers[index].response + '}';
        }
        else {
            return stem + value;
        }
    }, '');
}

var airFib_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$k,
    airFib: airFib,
    getPureAirFib: getPureAirFib$1,
    'default': airFib
});

const prefix$h = '[Air File Upload]';
const SETUP_EMPTY_ITEM$f = `${prefix$h} Setup Empty Item`;
class SetupEmptyItemAction$f {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$f;
    }
}
const RESET_STORE$h = `${prefix$h} Reset Store`;
class ResetStoreAction$h {
    constructor() {
        this.type = RESET_STORE$h;
    }
}
const LOAD_AIR_ITEM$h = `${prefix$h} Load Air Item`;
class LoadAirItemAction$h {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$h;
    }
}

var airFileUpload_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$f,
    SetupEmptyItemAction: SetupEmptyItemAction$f,
    RESET_STORE: RESET_STORE$h,
    ResetStoreAction: ResetStoreAction$h,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$h,
    LoadAirItemAction: LoadAirItemAction$h
});

const initialState$j = {
    prompt: '',
};
function airFileUpload(state = initialState$j, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.FILE_UPLOAD) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case SETUP_EMPTY_ITEM$f: {
            return initialState$j;
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        case LOAD_AIR_ITEM$h: {
            const payload = action.payload;
            return {
                ...state,
                prompt: payload.prompt,
                resources: payload.resources,
            };
        }
        case RESET_STORE$h: {
            return initialState$j;
        }
        default: {
            return state;
        }
    }
}
function getPureAirFileUpload$1(state) {
    return {
        prompt: state.prompt,
        resources: state.resources,
    };
}

var airFileUpload_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$j,
    airFileUpload: airFileUpload,
    getPureAirFileUpload: getPureAirFileUpload$1,
    'default': airFileUpload
});

const prefix$g = '[Air Matching]';
const SETUP_EMPTY_ITEM$e = `${prefix$g} Setup Empty Item`;
class SetupEmptyItemAction$e {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$e;
    }
}
const ADD_PROMPT = `${prefix$g} Add Prompt`;
class AddPromptAction {
    constructor() {
        this.type = ADD_PROMPT;
    }
}
const ADD_CHOICE$2 = `${prefix$g} Add Choice`;
class AddChoiceAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_CHOICE$2;
    }
}
const UPDATE_PROMPT_CONTENT = `${prefix$g} Update Prompt Content`;
class UpdatePromptContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_PROMPT_CONTENT;
    }
}
const UPDATE_CHOICE_CONTENT$1 = `${prefix$g} Update Choice Content`;
class UpdateChoiceContentAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE_CONTENT$1;
    }
}
const DELETE_PROMPT = `${prefix$g} Delete Prompt`;
class DeletePromptAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_PROMPT;
    }
}
const DELETE_CHOICE$2 = `${prefix$g} Delete Choice`;
class DeleteChoiceAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CHOICE$2;
    }
}
const ADD_DISTRACTOR = `${prefix$g} Add Distractor`;
class AddDistractorAction {
    constructor() {
        this.type = ADD_DISTRACTOR;
    }
}
const UPDATE_DISTRACTOR_CONTENT = `${prefix$g} Update Distractor Content`;
class UpdateDistractorContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_DISTRACTOR_CONTENT;
    }
}
const DELETE_DISTRACTOR = `${prefix$g} Delete Distractor`;
class DeleteDistractorAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_DISTRACTOR;
    }
}
const LOAD_AIR_ITEM$g = `${prefix$g} Load Air Item`;
class LoadAirItemAction$g {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$g;
    }
}
const RESET_STORE$g = `${prefix$g} Reset Store`;
class ResetStoreAction$g {
    constructor() {
        this.type = RESET_STORE$g;
    }
}

var airMatching_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$e,
    SetupEmptyItemAction: SetupEmptyItemAction$e,
    ADD_PROMPT: ADD_PROMPT,
    AddPromptAction: AddPromptAction,
    ADD_CHOICE: ADD_CHOICE$2,
    AddChoiceAction: AddChoiceAction$2,
    UPDATE_PROMPT_CONTENT: UPDATE_PROMPT_CONTENT,
    UpdatePromptContentAction: UpdatePromptContentAction,
    UPDATE_CHOICE_CONTENT: UPDATE_CHOICE_CONTENT$1,
    UpdateChoiceContentAction: UpdateChoiceContentAction$1,
    DELETE_PROMPT: DELETE_PROMPT,
    DeletePromptAction: DeletePromptAction,
    DELETE_CHOICE: DELETE_CHOICE$2,
    DeleteChoiceAction: DeleteChoiceAction$2,
    ADD_DISTRACTOR: ADD_DISTRACTOR,
    AddDistractorAction: AddDistractorAction,
    UPDATE_DISTRACTOR_CONTENT: UPDATE_DISTRACTOR_CONTENT,
    UpdateDistractorContentAction: UpdateDistractorContentAction,
    DELETE_DISTRACTOR: DELETE_DISTRACTOR,
    DeleteDistractorAction: DeleteDistractorAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$g,
    LoadAirItemAction: LoadAirItemAction$g,
    RESET_STORE: RESET_STORE$g,
    ResetStoreAction: ResetStoreAction$g
});

const initialState$i = {
    prompt: '',
    prompts: [],
    choices: [],
    answers: [],
    nextPromptKey: 0,
    nextChoiceKey: 0,
    distractors: [],
};
function airMatching(state = initialState$i, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.MATCHING) {
        return state;
    }
    switch (action.type) {
        case SETUP_EMPTY_ITEM$e: {
            return {
                ...state,
                prompts: [
                    { key: 'prompt-1', content: '', matches: 1 },
                    { key: 'prompt-2', content: '', matches: 1 },
                    { key: 'prompt-3', content: '', matches: 1 },
                    { key: 'prompt-4', content: '', matches: 1 },
                ],
                choices: [
                    { key: 'choice-1', content: '' },
                    { key: 'choice-2', content: '' },
                    { key: 'choice-3', content: '' },
                    { key: 'choice-4', content: '' },
                ],
                answers: [
                    { prompt: 'prompt-1', choices: ['choice-1'] },
                    { prompt: 'prompt-2', choices: ['choice-2'] },
                    { prompt: 'prompt-3', choices: ['choice-3'] },
                    { prompt: 'prompt-4', choices: ['choice-4'] },
                ],
                nextPromptKey: 5,
                nextChoiceKey: 6,
                distractors: [{ key: 'choice-5', content: '' }],
            };
        }
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case ADD_PROMPT: {
            return {
                ...state,
                prompts: [
                    ...state.prompts,
                    {
                        key: 'prompt-' + state.nextPromptKey,
                        content: '',
                        matches: 1,
                    },
                ],
                choices: [
                    ...state.choices,
                    {
                        key: 'choice-' + state.nextChoiceKey,
                        content: '',
                    },
                ],
                answers: [
                    ...state.answers,
                    {
                        prompt: 'prompt-' + state.nextPromptKey,
                        choices: ['choice-' + state.nextChoiceKey],
                    },
                ],
                nextPromptKey: state.nextPromptKey + 1,
                nextChoiceKey: state.nextChoiceKey + 1,
            };
        }
        case ADD_CHOICE$2: {
            return {
                ...state,
                choices: [
                    ...state.choices,
                    {
                        key: 'choice-' + state.nextChoiceKey,
                        content: '',
                    },
                ],
                prompts: state.prompts.map((prompt) => {
                    if (prompt.key === action.payload.prompt) {
                        return { ...prompt, matches: prompt.matches + 1 };
                    }
                    return prompt;
                }),
                answers: state.answers.map((answer) => {
                    if (answer === action.payload) {
                        return {
                            ...answer,
                            choices: [...answer.choices, 'choice-' + state.nextChoiceKey],
                        };
                    }
                    return answer;
                }),
                nextChoiceKey: state.nextChoiceKey + 1,
            };
        }
        case UPDATE_PROMPT_CONTENT: {
            const content = action.payload.content;
            return {
                ...state,
                prompts: state.prompts.map((prompt) => {
                    if (prompt.key === action.payload.promptKey) {
                        return {
                            ...prompt,
                            content: content,
                        };
                    }
                    return prompt;
                }),
            };
        }
        case UPDATE_CHOICE_CONTENT$1: {
            const content = action.payload.content;
            return {
                ...state,
                choices: state.choices.map((choice) => {
                    if (choice.key === action.payload.choiceKey) {
                        return {
                            ...choice,
                            content: content,
                        };
                    }
                    return choice;
                }),
            };
        }
        case DELETE_PROMPT: {
            const answers = state.answers;
            const deleteChoiceKeys = answers.find((answer) => {
                return answer.prompt === action.payload.prompt;
            })?.choices;
            return {
                ...state,
                prompts: state.prompts.filter((prompt) => prompt.key !== action.payload.prompt),
                choices: state.choices.filter((choice) => !deleteChoiceKeys?.includes(choice.key)),
                answers: state.answers.filter((answer) => answer !== action.payload),
            };
        }
        case DELETE_CHOICE$2: {
            return {
                ...state,
                choices: state.choices.filter((choice) => choice.key !== action.payload.choiceKey),
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        choices: answer.choices.filter((choice) => {
                            return answer.prompt === action.payload.promptKey ? choice !== action.payload.choiceKey : true;
                        }),
                    };
                }),
                prompts: state.prompts.map((prompt) => {
                    if (prompt.key === action.payload.promptKey) {
                        return {
                            ...prompt,
                            matches: prompt.matches - 1,
                        };
                    }
                    return prompt;
                }),
            };
        }
        case ADD_DISTRACTOR: {
            return {
                ...state,
                distractors: [
                    ...state.distractors,
                    {
                        key: 'choice-' + state.nextChoiceKey,
                        content: '',
                    },
                ],
                nextChoiceKey: state.nextChoiceKey + 1,
            };
        }
        case UPDATE_DISTRACTOR_CONTENT: {
            const content = action.payload.content;
            return {
                ...state,
                distractors: state.distractors.map((distractor) => {
                    if (distractor === action.payload.distractor) {
                        return {
                            ...distractor,
                            content: content,
                        };
                    }
                    return distractor;
                }),
            };
        }
        case DELETE_DISTRACTOR: {
            return {
                ...state,
                distractors: state.distractors.filter((distractor) => distractor !== action.payload),
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$g: {
            return initialState$i;
        }
        case LOAD_AIR_ITEM$g: {
            const { payload } = action;
            const choiceKeys = payload.choices.map((choice) => {
                let keyIndex = choice.key.split('-')[1];
                if (!keyIndex) {
                    keyIndex = getMaxKeyNumber(payload.choices).toString();
                }
                return parseInt(keyIndex, 10);
            });
            const maxChoiceKey = choiceKeys.length > 0 ? choiceKeys.reduce((a, b) => Math.max(a, b)) : 0;
            return {
                ...state,
                prompt: payload.prompt,
                prompts: payload.prompts,
                choices: payload.choices.filter((choice) => {
                    return payload.answers.some((answer) => answer.choices.includes(choice.key));
                }),
                answers: payload.answers,
                nextPromptKey: getNextPromptKey(payload),
                nextChoiceKey: maxChoiceKey + 1,
                distractors: payload.choices.filter((choice) => {
                    return !payload.answers.some((answer) => answer.choices.includes(choice.key));
                }),
                resources: action.payload.resources,
            };
        }
        default: {
            return state;
        }
    }
}
function getNextPromptKey(payload) {
    const prompts = payload.prompts;
    if (prompts.length === 0) {
        return 0;
    }
    const lastPrompt = prompts[prompts.length - 1];
    let lastPromptKeyIndex = lastPrompt.key.split('-')[1];
    if (!lastPromptKeyIndex) {
        lastPromptKeyIndex = getMaxKeyNumber(prompts);
    }
    return parseInt(lastPromptKeyIndex, 10) + 1;
}
function getMaxKeyNumber(items) {
    const keys = [];
    items.forEach((item) => {
        const keyText = item.key.split('-')[0];
        const keyIndex = item.key.split('-')[1];
        if (keyText === 'prompt' || keyText === 'choice') {
            keys.push(parseInt(keyIndex, 10));
        }
    });
    return keys.length ? Math.max(...keys) : items.length;
}
function getPureAirMatching$1(state) {
    const trimmedDistractors = state.distractors.filter((distractor) => distractor.content.trim() !== '');
    const trimmedPrompts = state.prompts.filter((prompt) => prompt.content.trim() !== '');
    const trimmedChoices = state.choices.filter((choice) => choice.content.trim() !== '');
    const answers = state.answers
        .filter((answer) => {
        return trimmedPrompts.some((prompt) => prompt.key === answer.prompt);
    })
        .map((answer) => {
        return {
            ...answer,
            choices: answer.choices.filter((choiceKey) => {
                return trimmedChoices.some((choice) => choice.key === choiceKey);
            }),
        };
    });
    const prompts = trimmedPrompts.map((prompt) => {
        const promptAnswer = answers.find((answer) => answer.prompt === prompt.key);
        return {
            ...prompt,
            matches: promptAnswer?.choices.length,
        };
    });
    const choices = trimmedChoices
        .filter((choice) => {
        return answers.some((answer) => answer.choices.includes(choice.key));
    })
        .concat(trimmedDistractors);
    return {
        prompt: state.prompt,
        prompts: prompts,
        choices: choices,
        answers: answers,
        resources: state.resources,
    };
}

var airMatching_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$i,
    airMatching: airMatching,
    getNextPromptKey: getNextPromptKey,
    getMaxKeyNumber: getMaxKeyNumber,
    getPureAirMatching: getPureAirMatching$1,
    'default': airMatching
});

const prefix$f = '[Air Cloze]';
const SETUP_EMPTY_ITEM$d = `${prefix$f} Setup Empty Item`;
class SetupEmptyItemAction$d {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$d;
    }
}
const TOGGLE_CHOICES_SHUFFLING = `${prefix$f} Toggle Choices Shuffling`;
class ToggleChoicesShufflingAction {
    constructor() {
        this.type = TOGGLE_CHOICES_SHUFFLING;
    }
}
const ADD_CHOICE$1 = `${prefix$f} Add Choice`;
class AddChoiceAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_CHOICE$1;
    }
}
const UPDATE_CHOICE_CONTENT = `${prefix$f} Update Choice Content`;
class UpdateChoiceContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE_CONTENT;
    }
}
const DELETE_CHOICE$1 = `${prefix$f} Delete Choice`;
class DeleteChoiceAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CHOICE$1;
    }
}
const UPDATE_CORRECT_ANSWER$1 = `${prefix$f} Update Correct Answer`;
class UpdateCorrectAnswerAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CORRECT_ANSWER$1;
    }
}
const LOAD_AIR_ITEM$f = `${prefix$f} Load Air Item`;
class LoadAirItemAction$f {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$f;
    }
}
const RESET_STORE$f = `${prefix$f} Reset Store`;
class ResetStoreAction$f {
    constructor() {
        this.type = RESET_STORE$f;
    }
}

var airCloze_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$d,
    SetupEmptyItemAction: SetupEmptyItemAction$d,
    TOGGLE_CHOICES_SHUFFLING: TOGGLE_CHOICES_SHUFFLING,
    ToggleChoicesShufflingAction: ToggleChoicesShufflingAction,
    ADD_CHOICE: ADD_CHOICE$1,
    AddChoiceAction: AddChoiceAction$1,
    UPDATE_CHOICE_CONTENT: UPDATE_CHOICE_CONTENT,
    UpdateChoiceContentAction: UpdateChoiceContentAction,
    DELETE_CHOICE: DELETE_CHOICE$1,
    DeleteChoiceAction: DeleteChoiceAction$1,
    UPDATE_CORRECT_ANSWER: UPDATE_CORRECT_ANSWER$1,
    UpdateCorrectAnswerAction: UpdateCorrectAnswerAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$f,
    LoadAirItemAction: LoadAirItemAction$f,
    RESET_STORE: RESET_STORE$f,
    ResetStoreAction: ResetStoreAction$f
});

const initialState$h = {
    prompt: '',
    responses: [],
    answers: [],
    nextKey: 0,
    randomize: true,
    tooManyAnswers: false,
};
function airCloze(state = initialState$h, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.CLOZE) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            const content = action.payload.content;
            const blankCount = content.split('__').length - 1;
            let isNewBlankAdded = false;
            let updatedState;
            if (blankCount > 20) {
                updatedState = {
                    ...state,
                    tooManyAnswers: true,
                };
            }
            else {
                updatedState = {
                    ...state,
                    prompt: content,
                    responses: Array(blankCount)
                        .fill(true)
                        .map((_, index) => {
                        if (state.responses[index]) {
                            return state.responses[index];
                        }
                        else {
                            isNewBlankAdded = true;
                            return {
                                key: 'response' + index,
                                choices: [
                                    {
                                        key: 'choice' + state.nextKey,
                                        value: '',
                                    },
                                    {
                                        key: 'choice' + (state.nextKey + 1),
                                        value: '',
                                    },
                                ],
                            };
                        }
                    }),
                    nextKey: isNewBlankAdded ? state.nextKey + 2 : state.nextKey,
                    tooManyAnswers: false,
                };
            }
            return {
                ...updatedState,
                answers: !updatedState.responses.length
                    ? []
                    : state.answers.filter((answer) => {
                        return updatedState.responses.some((response) => response.key === answer.response);
                    }),
            };
        }
        case ADD_CHOICE$1: {
            return {
                ...state,
                nextKey: state.nextKey + 1,
                responses: state.responses.map((response) => {
                    const choiceObj = {
                        key: 'choice' + state.nextKey,
                        value: '',
                    };
                    return {
                        ...response,
                        choices: action.payload === response ? [...response.choices, choiceObj] : response.choices,
                    };
                }),
            };
        }
        case UPDATE_CHOICE_CONTENT: {
            return {
                ...state,
                responses: state.responses.map((response) => {
                    return {
                        ...response,
                        choices: response.choices.map((choice) => {
                            if (response === action.payload.response && choice === action.payload.choice) {
                                return {
                                    ...choice,
                                    value: action.payload.content,
                                };
                            }
                            return choice;
                        }),
                    };
                }),
            };
        }
        case DELETE_CHOICE$1: {
            return {
                ...state,
                responses: state.responses.map((response) => {
                    return {
                        ...response,
                        choices: response.choices.filter((choice) => {
                            if (response === action.payload.response) {
                                if (choice === action.payload.choice) {
                                    return null;
                                }
                                else {
                                    return choice;
                                }
                            }
                            else {
                                return choice;
                            }
                        }),
                    };
                }),
                answers: state.answers.filter((answer) => {
                    return !(answer.response === action.payload.response.key && answer.choice === action.payload.choice.key);
                }),
            };
        }
        case UPDATE_CORRECT_ANSWER$1: {
            if (!action.payload.value.trim()) {
                return state;
            }
            return {
                ...state,
                answers: state.answers
                    .filter((answer) => {
                    return answer.response !== action.payload.responseKey;
                })
                    .concat({
                    choice: action.payload.choiceKey,
                    response: action.payload.responseKey,
                }),
            };
        }
        case TOGGLE_CHOICES_SHUFFLING: {
            return { ...state, randomize: !state.randomize };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case LOAD_AIR_ITEM$f: {
            return {
                ...state,
                prompt: new ResponseToUnderscorePipe().transform(action.payload.prompt),
                answers: action.payload.answers,
                responses: action.payload.responses,
                randomize: action.payload.randomize,
                nextKey: getNextKey$2(action.payload),
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$f: {
            return initialState$h;
        }
        default: {
            return state;
        }
    }
}
function getNextKey$2(air) {
    const responseKeyNumbers = air.responses
        .map((response) => response.key.match(/^response(\d+)$/))
        .filter((match) => match)
        .map((match) => {
        if (!match || !match[1]) {
            return 0; // TODO review this properly
        }
        return parseInt(match[1], 10);
    });
    const choiceKeyNumbers = [];
    air.responses.forEach((response) => {
        response.choices
            .map((choice) => choice.key.match(/^choice(\d+)$/))
            .filter((match) => match)
            .map((match) => {
            if (!match || !match[1]) {
                return 0; // TODO review this properly
            }
            return parseInt(match[1], 10);
        })
            .forEach((choiceNumber) => choiceKeyNumbers.push(choiceNumber));
    });
    const allNumbers = [...responseKeyNumbers, ...choiceKeyNumbers];
    const maxKeyNumber = allNumbers.reduce((a, b) => Math.max(a, b), 0);
    return maxKeyNumber + 1;
}
function getPureAirCloze$1(state) {
    return {
        prompt: generateStem$1(state),
        answers: state.answers,
        randomize: state.randomize,
        responses: state.responses.map((response) => {
            return {
                ...response,
                choices: response.choices.filter((choice, index) => {
                    return index <= 1 || choice.value.trim() !== '';
                }),
            };
        }),
        resources: state.resources,
    };
}
function generateStem$1(state) {
    return state.prompt.split('__').reduce((stem, value, index) => {
        if (state.responses[index]) {
            return stem + value + '${response:' + state.responses[index].key + '}';
        }
        else {
            return stem + value;
        }
    }, '');
}

var airCloze_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$h,
    airCloze: airCloze,
    getPureAirCloze: getPureAirCloze$1,
    'default': airCloze
});

const prefix$e = '[Air Select Text]';
const SETUP_EMPTY_ITEM$c = `${prefix$e} SETUP_EMPTY_ITEM`;
class SetupEmptyItemAction$c {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$c;
    }
}
const RESET_STORE$e = `${prefix$e} RESET_STORE`;
class ResetStoreAction$e {
    constructor() {
        this.type = RESET_STORE$e;
    }
}
const UPDATE_UNIT_TYPE = `${prefix$e} UPDATE_UNIT_TYPE`;
class UpdateUnitTypeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_UNIT_TYPE;
    }
}
const UPDATE_SELECTION_TEXT_HTML = `${prefix$e} UPDATE_SELECTION_TEXT_HTML`;
class UpdateSelectionTextHtmlAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SELECTION_TEXT_HTML;
    }
}
const IS_CHOICE_CORRECT = `${prefix$e} IS_CHOICE_CORRECT`;
class IsChoiceCorrectAction {
    constructor(payload) {
        this.payload = payload;
        this.type = IS_CHOICE_CORRECT;
    }
}
const ALLOW_EDIT_SELECTED_TEXT = `${prefix$e} ALLOW_EDIT_SELECTED_TEXT`;
class AllowEditTextAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ALLOW_EDIT_SELECTED_TEXT;
    }
}
const TOGGLE_CHOICE = `${prefix$e} TOGGLE_CHOICE`;
class ToggleChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = TOGGLE_CHOICE;
    }
}
const TOGGLE_ANSWER = `${prefix$e} TOGGLE_ANSWER`;
class ToggleAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = TOGGLE_ANSWER;
    }
}
const SELECTED_CUSTOM_RANGE = `${prefix$e} SELECTED_CUSTOM_RANGE`;
class SelectedCustomRangeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SELECTED_CUSTOM_RANGE;
    }
}
const LOAD_AIR_ITEM$e = `${prefix$e} Load Air Item`;
class LoadAirItemAction$e {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$e;
    }
}
const UPDATE_RATIONALE_CONTENT = `${prefix$e} Update Rationale Content`;
class UpdateRationaleContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_RATIONALE_CONTENT;
    }
}
const TOGGLE_RATIONALE_VISIBILITY = `${prefix$e} Toggle Rationale Visibility`;
class ToggleRationaleVisibilityAction {
    constructor() {
        this.type = TOGGLE_RATIONALE_VISIBILITY;
    }
}
const ADD_CORRECT_ANSWER = `${prefix$e} Add Correct Answer`;
class AddCorrectAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_CORRECT_ANSWER;
    }
}
const DELETE_CORRECT_ANSWER = `${prefix$e} Delete Correct Answer`;
class DeleteCorrectAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CORRECT_ANSWER;
    }
}
const UPDATE_SELECT_EDIT_CORRECT_ANSWER = `${prefix$e} Update Select Edit Correct Answer`;
class UpdateSelectEditCorrectAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SELECT_EDIT_CORRECT_ANSWER;
    }
}
const SAVE_SCHEMA = `${prefix$e} Enable Select and Edit`;
class SaveSchemaVersionAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SAVE_SCHEMA;
    }
}

var airSelectText_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$c,
    SetupEmptyItemAction: SetupEmptyItemAction$c,
    RESET_STORE: RESET_STORE$e,
    ResetStoreAction: ResetStoreAction$e,
    UPDATE_UNIT_TYPE: UPDATE_UNIT_TYPE,
    UpdateUnitTypeAction: UpdateUnitTypeAction,
    UPDATE_SELECTION_TEXT_HTML: UPDATE_SELECTION_TEXT_HTML,
    UpdateSelectionTextHtmlAction: UpdateSelectionTextHtmlAction,
    IS_CHOICE_CORRECT: IS_CHOICE_CORRECT,
    IsChoiceCorrectAction: IsChoiceCorrectAction,
    ALLOW_EDIT_SELECTED_TEXT: ALLOW_EDIT_SELECTED_TEXT,
    AllowEditTextAction: AllowEditTextAction,
    TOGGLE_CHOICE: TOGGLE_CHOICE,
    ToggleChoiceAction: ToggleChoiceAction,
    TOGGLE_ANSWER: TOGGLE_ANSWER,
    ToggleAnswerAction: ToggleAnswerAction,
    SELECTED_CUSTOM_RANGE: SELECTED_CUSTOM_RANGE,
    SelectedCustomRangeAction: SelectedCustomRangeAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$e,
    LoadAirItemAction: LoadAirItemAction$e,
    UPDATE_RATIONALE_CONTENT: UPDATE_RATIONALE_CONTENT,
    UpdateRationaleContentAction: UpdateRationaleContentAction,
    TOGGLE_RATIONALE_VISIBILITY: TOGGLE_RATIONALE_VISIBILITY,
    ToggleRationaleVisibilityAction: ToggleRationaleVisibilityAction,
    ADD_CORRECT_ANSWER: ADD_CORRECT_ANSWER,
    AddCorrectAnswerAction: AddCorrectAnswerAction,
    DELETE_CORRECT_ANSWER: DELETE_CORRECT_ANSWER,
    DeleteCorrectAnswerAction: DeleteCorrectAnswerAction,
    UPDATE_SELECT_EDIT_CORRECT_ANSWER: UPDATE_SELECT_EDIT_CORRECT_ANSWER,
    UpdateSelectEditCorrectAnswerAction: UpdateSelectEditCorrectAnswerAction,
    SAVE_SCHEMA: SAVE_SCHEMA,
    SaveSchemaVersionAction: SaveSchemaVersionAction
});

// See https://confluence.mheducation.com/x/fb_yC
const SENTENCE_SEPARATOR_REGEX = /[\.\?!…]+(\s+|$)/;
const WORD_SEPARATOR_REGEX = /[ ;:"_\+=><&~#\^\*\(\)\[\]\{\}`\|]+/;
function extractSelectionUnitStrings(selectionText, unitType) {
    if (unitType === 'paragraph') {
        return extractParagraphUnits(selectionText);
    }
    else if (unitType === 'sentence') {
        return extractSentenceUnits(selectionText);
    }
    else {
        return extractWordUnits(selectionText);
    }
}
function extractParagraphUnits(selectionText) {
    return split([selectionText], /\n/, true);
}
function extractSentenceUnits(selectionText) {
    const sentences = split(extractParagraphUnits(selectionText), SENTENCE_SEPARATOR_REGEX, true);
    return separateSentenceTrailingWhitespace(sentences);
}
function extractWordUnits(selectionText) {
    const sentencesWithSeparateFinalPunctuation = split(extractParagraphUnits(selectionText), SENTENCE_SEPARATOR_REGEX, false);
    return split(sentencesWithSeparateFinalPunctuation, WORD_SEPARATOR_REGEX, false);
}
function split(sources, separatorRegex, includeSeparatorInUnit) {
    const units = [];
    sources.forEach((source) => {
        let unmatched = source;
        while (unmatched.length) {
            const match = unmatched.match(separatorRegex);
            if (match !== null) {
                const beforeMatch = unmatched.substring(0, match.index);
                if (includeSeparatorInUnit) {
                    units.push(beforeMatch + match[0]);
                }
                else if (beforeMatch.length) {
                    units.push(beforeMatch, match[0]);
                }
                else {
                    units.push(match[0]);
                }
                if (match.index !== undefined) {
                    unmatched = unmatched.substring(match.index + match[0].length);
                }
            }
            else {
                units.push(unmatched);
                break;
            }
        }
    });
    return units;
}
function separateSentenceTrailingWhitespace(sentences) {
    const unitsWithFillerWhitespace = [];
    sentences.forEach((sentence) => {
        const match = sentence.match(/^(.*?)(\s*)$/);
        if (match && match[1].length) {
            unitsWithFillerWhitespace.push(match[1]);
        }
        if (match && match[2].length) {
            unitsWithFillerWhitespace.push(match[2]);
        }
    });
    return unitsWithFillerWhitespace;
}

const initialState$g = {
    prompt: '',
    selectionTextHtml: '',
    unitType: 'word',
    selectionUnits: [],
    answers: [],
    nextKey: 0,
    editSelectedText: false,
    showRationales: false,
};
function airSelectText(state = initialState$g, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.SELECT_TEXT) {
        return state;
    }
    switch (action.type) {
        case RESET_STORE$e:
        case SETUP_EMPTY_ITEM$c: {
            return initialState$g;
        }
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case UPDATE_UNIT_TYPE: {
            const selectionUnits = extractSelectionUnits(state.selectionTextHtml, action.payload, state.nextKey);
            return {
                ...state,
                selectionUnits,
                unitType: action.payload,
                nextKey: getMaxKey(selectionUnits) + 1,
                answers: [],
            };
        }
        case UPDATE_SELECTION_TEXT_HTML: {
            const selectionUnits = extractSelectionUnits(action.payload, state.unitType, 0);
            return {
                ...state,
                selectionTextHtml: action.payload,
                selectionUnits,
                answers: [],
                nextKey: getMaxKey(selectionUnits) + 1,
            };
        }
        case ALLOW_EDIT_SELECTED_TEXT: {
            const choices = state.selectionUnits.filter((unit) => unit.type === 'choice');
            return {
                ...state,
                editSelectedText: action.payload,
                answers: action.payload && state.selectionUnits.length
                    ? choices.map((choice) => {
                        return {
                            key: choice.key,
                            values: [choice.text],
                            isAnswerChoiceCorrect: false,
                        };
                    })
                    : [],
            };
        }
        case TOGGLE_CHOICE: {
            let selectionUnits = state.selectionUnits.map((unit) => {
                return unit.key === action.payload ? toggleChoice(unit, state.unitType) : unit;
            });
            let selectEditAnswers = [];
            if (state.unitType === 'custom') {
                selectionUnits = mergeAdjacentFillerUnits(selectionUnits);
            }
            if (state.editSelectedText) {
                const selectUnits = state.selectionUnits.filter((unit) => unit.key === action.payload);
                const answerObject = { key: action.payload, values: [selectUnits[0].text] };
                const answers = state.answers.filter((answer) => answer.key !== action.payload);
                selectEditAnswers = answers.length < state.answers.length ? answers : [...state.answers, answerObject];
            }
            else {
                selectEditAnswers = state.answers.filter((answer) => (answer.key ? answer.key : answer) !== action.payload);
            }
            return {
                ...state,
                selectionUnits,
                answers: selectEditAnswers,
            };
        }
        case TOGGLE_ANSWER: {
            return {
                ...state,
                answers: state.answers.some((answer) => answer.key === action.payload.key)
                    ? state.answers.filter((answer) => answer.key !== action.payload.key)
                    : [...state.answers, { key: action.payload.key, values: [action.payload.text] }],
            };
        }
        case SELECTED_CUSTOM_RANGE: {
            const units = getSelectionUnitsAfterCustomSelection(state.selectionUnits, action.payload, state.nextKey);
            let selectEditAnswers = [];
            if (state.editSelectedText) {
                const selectUnits = units.filter((unit) => unit.key && unit.type === 'choice');
                const answerKeys = state.answers.map((answer) => answer.key);
                const customSelect = selectUnits.filter((unit) => unit.key && !answerKeys.includes(unit.key));
                const answerObject = { key: customSelect[0].key, values: [customSelect[0].text] };
                selectEditAnswers = customSelect.length ? [...state.answers, answerObject] : [...state.answers];
            }
            else {
                selectEditAnswers = state.answers.filter((answer) => {
                    return units.map((unit) => unit.key).includes(answer.key);
                });
            }
            return {
                ...state,
                selectionUnits: units,
                nextKey: getMaxKey(units) + 1,
                answers: selectEditAnswers,
            };
        }
        case UPDATE_RATIONALE_CONTENT: {
            return {
                ...state,
                selectionUnits: state.selectionUnits.map((choice) => {
                    if (choice === action.payload.choice) {
                        return { ...choice, rationale: action.payload.content };
                    }
                    else {
                        return choice;
                    }
                }),
            };
        }
        case ADD_CORRECT_ANSWER: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        values: answer.key === action.payload ? [...answer.values, ''] : answer.values,
                    };
                }),
            };
        }
        case DELETE_CORRECT_ANSWER: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    const answerValues = answer.key === action.payload.answerKey
                        ? answer.values.filter((ans, index) => index !== action.payload.valueIndex)
                        : answer.values;
                    return {
                        ...answer,
                        values: answerValues,
                    };
                }),
            };
        }
        case IS_CHOICE_CORRECT: {
            const choices = state.selectionUnits.filter((unit) => unit.type === 'choice');
            const choiceText = choices.find((choice) => choice.key === action.payload.answerKey)?.text;
            const isChoiceCorrect = action.payload.isChoiceCorrect;
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer.key === action.payload.answerKey) {
                        return {
                            ...answer,
                            isAnswerChoiceCorrect: isChoiceCorrect,
                            values: isChoiceCorrect && choiceText ? [choiceText] : [''],
                        };
                    }
                    return answer;
                }),
            };
        }
        case UPDATE_SELECT_EDIT_CORRECT_ANSWER: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer.key === action.payload.answerKey) {
                        const values = [...answer.values];
                        values[action.payload.valueIndex] = action.payload.content;
                        return { ...answer, values };
                    }
                    else {
                        return { ...answer, values: answer.values };
                    }
                }),
            };
        }
        case TOGGLE_RATIONALE_VISIBILITY: {
            return { ...state, showRationales: !state.showRationales };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case SAVE_SCHEMA: {
            const updatedState = {
                ...state,
                schema: action.payload,
            };
            if (!action.payload) {
                delete updatedState.schema;
            }
            return updatedState;
        }
        case LOAD_AIR_ITEM$e: {
            const selectionText = action.payload.selectionText;
            const unitType = action.payload.textSelectionUnit;
            const selectionUnits = loadSelectionUnits(selectionText, unitType, 0);
            const showRationales = selectionUnits.some((unit) => unit.rationale);
            const editSelectedText = action.payload.editSelectedText;
            const answers = loadAnswers(action.payload.answers, selectionUnits, editSelectedText);
            return {
                ...state,
                editSelectedText: action.payload.editSelectedText,
                prompt: action.payload.prompt,
                selectionTextHtml: getSelectionHtml(selectionText),
                unitType,
                selectionUnits,
                answers: answers,
                nextKey: getMaxKey(selectionUnits) + 1,
                resources: action.payload.resources,
                showRationales,
            };
        }
        default: {
            return state;
        }
    }
}
function loadSelectionUnits(selectionText, unitType, nextKey) {
    let selectionUnits = [];
    let keyNumber = getMaxKey(selectionText);
    selectionText.forEach((unit) => {
        if (unit.type === 'filler') {
            const fillerUnits = extractSelectionUnits(unit.text, unitType, nextKey).map((selectionUnit) => {
                keyNumber++;
                return {
                    ...selectionUnit,
                    key: getKey(keyNumber),
                };
            });
            selectionUnits = selectionUnits.concat(fillerUnits);
        }
        else {
            selectionUnits.push(unit);
        }
    });
    return selectionUnits;
}
function loadAnswers(answers, selectionUnits, editSelectedText) {
    if (editSelectedText) {
        answers = selectionUnits
            .filter((unit) => unit.type === 'choice')
            .map((unit) => {
            const answerIndex = answers.findIndex((answer) => answer.key === unit.key);
            if (answerIndex > -1) {
                return answers[answerIndex];
            }
            else {
                return {
                    key: unit.key,
                    values: [unit.text],
                };
            }
        });
    }
    const loadAnswers = answers.map((answer) => {
        if (typeof answer === 'object') {
            const choices = selectionUnits.filter((unit) => unit.key === answer.key && answer.values.length === 1 && answer.values[0] === unit.text);
            const values = answer.values.filter((val) => val);
            return {
                ...answer,
                values: values,
                isAnswerChoiceCorrect: !!choices.length,
            };
        }
        else {
            const matchedChoiceText = selectionUnits.find((unit) => unit.key === answer)?.text;
            return {
                key: answer,
                values: [matchedChoiceText],
                isAnswerChoiceCorrect: true,
            };
        }
    });
    return loadAnswers;
}
function extractSelectionUnits(selectionTextHtml, unitType, nextKey) {
    const selectionPlainText = new HtmlToPlaintextPipe().transform(selectionTextHtml);
    if (unitType === 'custom') {
        return [
            {
                key: getKey(nextKey),
                text: selectionPlainText,
                type: 'filler',
            },
        ];
    }
    return extractSelectionUnitStrings(selectionPlainText, unitType).map((text, i) => {
        return {
            key: getKey(nextKey + i),
            text,
            type: 'filler',
        };
    });
}
function getSelectionUnitsAfterCustomSelection(selectionUnits, payload, nextKey) {
    const startUnit = selectionUnits.find((unit) => unit.key === payload.startUnitKey);
    const endUnit = selectionUnits.find((unit) => unit.key === payload.endUnitKey);
    const startUnitIndex = startUnit ? selectionUnits.indexOf(startUnit) : -1;
    const endUnitIndex = endUnit ? selectionUnits.indexOf(endUnit) : -1;
    const units = [];
    selectionUnits.forEach((unit, index) => {
        if (index < startUnitIndex || index > endUnitIndex) {
            units.push(unit);
            return;
        }
        else if (index > startUnitIndex && index < endUnitIndex) {
            const lastUnit = units.pop();
            if (lastUnit) {
                units.push({
                    ...lastUnit,
                    text: lastUnit.text + unit.text,
                    type: 'merge',
                });
            }
            return;
        }
        if (index === startUnitIndex && payload.startOffset > 0) {
            units.push({
                ...unit,
                key: getKey(nextKey++),
                text: unit.text.slice(0, payload.startOffset),
            });
        }
        const lastUnit = units[units.length - 1];
        let text = '';
        let key = '';
        if (lastUnit && lastUnit.type === 'merge') {
            units.pop();
            text = lastUnit.text;
            key = lastUnit.key;
        }
        units.push({
            key: key || getKey(nextKey++),
            text: text +
                unit.text.slice(index === startUnitIndex ? payload.startOffset : 0, index === endUnitIndex ? payload.endOffset : unit.text.length),
            type: index === endUnitIndex ? 'choice' : 'merge',
        });
        if (index === endUnitIndex && endUnit && payload.endOffset < endUnit.text.length) {
            units.push({
                ...unit,
                key: getKey(nextKey++),
                text: unit.text.slice(payload.endOffset),
            });
        }
    });
    return units;
}
function getPureAirSelectText$1(state) {
    const onlySelectText = isOnlySelectText(state.schema);
    return {
        schema: onlySelectText ? undefined : { version: 2, revision: 0, type: 'select_text' },
        prompt: state.prompt,
        textSelectionUnit: state.unitType,
        editSelectedText: onlySelectText ? undefined : state.editSelectedText,
        selectionText: getPureSelectionText(state.selectionUnits, Boolean(state.showRationales)),
        answers: getPureAnswers(state.answers, onlySelectText, Boolean(state.editSelectedText)),
        resources: state.resources,
    };
}
function isOnlySelectText(payload) {
    return !!(payload && payload.version === 1 && payload.revision === 0);
}
function getPureSelectionText(selectionUnits, showRationales) {
    const selectionText = [];
    let currentFillerText = '';
    const pushCurrentFiller = () => {
        if (!currentFillerText) {
            return;
        }
        selectionText.push({
            type: 'filler',
            text: currentFillerText,
        });
        currentFillerText = '';
    };
    const pushChoice = (selectionUnit) => {
        const choice = {
            key: selectionUnit.key,
            type: 'choice',
            text: selectionUnit.text,
            rationale: selectionUnit.rationale || '',
        };
        if (!showRationales) {
            delete choice.rationale;
        }
        selectionText.push(choice);
    };
    selectionUnits.forEach((selectionUnit) => {
        if (selectionUnit.type === 'choice') {
            pushCurrentFiller();
            pushChoice(selectionUnit);
        }
        else {
            currentFillerText += selectionUnit.text;
        }
    });
    pushCurrentFiller();
    return selectionText;
}
function getPureAnswers(answers, onlySelectText, editSelectedText) {
    const filteredAnswers = editSelectedText ? answers.filter((answer) => !answer.isAnswerChoiceCorrect) : answers;
    return filteredAnswers.map((answer) => {
        if (onlySelectText) {
            return answer.key;
        }
        else {
            return {
                key: answer.key,
                values: answer.values.filter((value) => value),
            };
        }
    });
}
function getSelectionHtml(selectionUnits) {
    return selectionUnits
        .map((unit) => {
        return new HtmlToPlaintextPipe().transform(unit.text);
    })
        .join('');
}
function toggleChoice(selectionUnit, unitType) {
    const unitDetails = { ...selectionUnit };
    if (selectionUnit.type === 'choice') {
        if (selectionUnit.rationale) {
            delete unitDetails.rationale;
        }
        return {
            ...unitDetails,
            type: 'filler',
        };
    }
    return {
        ...unitDetails,
        type: 'choice',
    };
}
function mergeAdjacentFillerUnits(selectionUnits) {
    return selectionUnits.reduce((mergedUnits, unit) => {
        if (mergedUnits.length) {
            const lastMergedUnit = mergedUnits[mergedUnits.length - 1];
            if (unit.type === 'filler' && lastMergedUnit.type === 'filler') {
                return mergedUnits.slice(0, mergedUnits.length - 1).concat({
                    ...lastMergedUnit,
                    text: lastMergedUnit.text + unit.text,
                });
            }
        }
        return mergedUnits.concat(unit);
    }, []);
}
function getKey(keyNumber) {
    return `select-text-${keyNumber}`;
}
function getMaxKey(selectionUnits) {
    return selectionUnits
        .filter((unit) => unit.key)
        .map((unit) => +unit.key.replace('select-text-', ''))
        .reduce((max, curr) => (max < curr ? curr : max), -1);
}

var airSelectText_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$g,
    'default': airSelectText,
    getPureAirSelectText: getPureAirSelectText$1
});

const prefix$d = '[Air Bucketing]';
const SETUP_EMPTY_ITEM$b = `${prefix$d} Setup Empty Item`;
class SetupEmptyItemAction$b {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$b;
    }
}
const ADD_BUCKET = `${prefix$d} Add Bucket`;
class AddBucketAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_BUCKET;
    }
}
const DELETE_BUCKET = `${prefix$d} Delete Bucket`;
class DeleteBucketAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_BUCKET;
    }
}
const UPDATE_BUCKET = `${prefix$d} Update Bucket`;
class UpdateBucketAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_BUCKET;
    }
}
const ADD_CHOICE = `${prefix$d} Add Choice`;
class AddChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_CHOICE;
    }
}
const DELETE_CHOICE = `${prefix$d} Delete Choice`;
class DeleteChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_CHOICE;
    }
}
const INCREMENT_CHOICE = `${prefix$d} Increment Choice`;
class IncrementChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = INCREMENT_CHOICE;
    }
}
const DECREMENT_CHOICE = `${prefix$d} Decrement Choice`;
class DecrementChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DECREMENT_CHOICE;
    }
}
const UPDATE_CHOICE = `${prefix$d} Update Choice`;
class UpdateChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE;
    }
}
const LOAD_AIR_ITEM$d = `${prefix$d} Load Air Item`;
class LoadAirItemAction$d {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$d;
    }
}
const RESET_STORE$d = `${prefix$d} Reset Store`;
class ResetStoreAction$d {
    constructor() {
        this.type = RESET_STORE$d;
    }
}

var airBucketing_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$b,
    SetupEmptyItemAction: SetupEmptyItemAction$b,
    ADD_BUCKET: ADD_BUCKET,
    AddBucketAction: AddBucketAction,
    DELETE_BUCKET: DELETE_BUCKET,
    DeleteBucketAction: DeleteBucketAction,
    UPDATE_BUCKET: UPDATE_BUCKET,
    UpdateBucketAction: UpdateBucketAction,
    ADD_CHOICE: ADD_CHOICE,
    AddChoiceAction: AddChoiceAction,
    DELETE_CHOICE: DELETE_CHOICE,
    DeleteChoiceAction: DeleteChoiceAction,
    INCREMENT_CHOICE: INCREMENT_CHOICE,
    IncrementChoiceAction: IncrementChoiceAction,
    DECREMENT_CHOICE: DECREMENT_CHOICE,
    DecrementChoiceAction: DecrementChoiceAction,
    UPDATE_CHOICE: UPDATE_CHOICE,
    UpdateChoiceAction: UpdateChoiceAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$d,
    LoadAirItemAction: LoadAirItemAction$d,
    RESET_STORE: RESET_STORE$d,
    ResetStoreAction: ResetStoreAction$d
});

const initialState$f = {
    prompt: '',
    buckets: [],
    choices: [],
    answers: [],
    nextBucketKey: 0,
    nextChoiceKey: 0,
};
function airBucketing(state = initialState$f, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.BUCKETING) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case ADD_BUCKET: {
            return {
                ...state,
                buckets: [
                    ...state.buckets,
                    {
                        key: 'bucket-' + state.nextBucketKey,
                        text: action.payload,
                    },
                ],
                answers: [
                    ...state.answers,
                    {
                        prompt: 'bucket-' + state.nextBucketKey,
                        choices: state.choices.map((choice) => {
                            return {
                                key: choice.key,
                                count: 0,
                            };
                        }),
                    },
                ],
                nextBucketKey: state.nextBucketKey + 1,
            };
        }
        case UPDATE_BUCKET: {
            return {
                ...state,
                buckets: state.buckets.map((bucket) => {
                    if (bucket === action.payload.bucket) {
                        return { ...bucket, text: action.payload.content };
                    }
                    return bucket;
                }),
            };
        }
        case DELETE_BUCKET: {
            return {
                ...state,
                buckets: state.buckets.filter((bucket) => bucket !== action.payload),
                answers: state.answers.filter((answer) => answer.prompt !== action.payload.key),
            };
        }
        case ADD_CHOICE: {
            return {
                ...state,
                choices: [
                    ...state.choices,
                    {
                        key: 'choice-' + state.nextChoiceKey,
                        text: action.payload,
                    },
                ],
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        choices: [
                            ...answer.choices,
                            {
                                key: 'choice-' + state.nextChoiceKey,
                                count: 0,
                            },
                        ],
                    };
                }),
                nextChoiceKey: state.nextChoiceKey + 1,
            };
        }
        case INCREMENT_CHOICE: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer.prompt === action.payload.bucket.key) {
                        return {
                            ...answer,
                            choices: getModifiedChoiceCount(true, action.payload, answer),
                        };
                    }
                    return answer;
                }),
            };
        }
        case DECREMENT_CHOICE: {
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer.prompt === action.payload.bucket.key) {
                        return {
                            ...answer,
                            choices: getModifiedChoiceCount(false, action.payload, answer),
                        };
                    }
                    return answer;
                }),
            };
        }
        case UPDATE_CHOICE: {
            return {
                ...state,
                choices: state.choices.map((choice) => {
                    if (choice.key === action.payload.choice.key) {
                        return { ...choice, text: action.payload.content };
                    }
                    return choice;
                }),
            };
        }
        case DELETE_CHOICE: {
            return {
                ...state,
                choices: state.choices.filter((choice) => choice.key !== action.payload.key),
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        choices: answer.choices.filter((choice) => choice.key !== action.payload.key),
                    };
                }),
            };
        }
        case LOAD_AIR_ITEM$d: {
            return {
                ...state,
                prompt: action.payload.prompt,
                buckets: action.payload.prompts.map((prompt) => {
                    return {
                        key: prompt.key,
                        text: prompt.mediaFile || prompt.text,
                    };
                }),
                answers: action.payload.answers.map((answer) => {
                    const sortedChoices = action.payload.choices.map((choice) => {
                        return answer.choices.find((answerChoice) => answerChoice.key === choice.key);
                    });
                    return {
                        prompt: answer.prompt,
                        choices: sortedChoices,
                    };
                }),
                choices: action.payload.choices.map((choice) => {
                    return {
                        key: choice.key,
                        text: choice.mediaFile || choice.text,
                    };
                }),
                nextBucketKey: getNextKey$1(action.payload.prompts, 'bucket'),
                nextChoiceKey: getNextKey$1(action.payload.choices, 'choice'),
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$d: {
            return initialState$f;
        }
        default: {
            return state;
        }
    }
}
function getModifiedChoiceCount(increaseCounter, payload, answer) {
    return answer.choices.map((choice) => {
        if (choice.key === payload.choice.key) {
            return {
                ...choice,
                count: increaseCounter ? choice.count + 1 : choice.count - 1,
            };
        }
        return choice;
    });
}
function getNextKey$1(bucketOrChoiceArray, selector) {
    const maxKey = bucketOrChoiceArray
        .map((prompt) => parseInt(prompt.key.split(selector + '-')[1], 10))
        .reduce((a, b) => Math.max(a, b), 0);
    return maxKey + 1;
}
function getPureAirBucketing$1(state) {
    return {
        prompt: state.prompt,
        prompts: state.buckets,
        answers: state.answers,
        choices: state.choices,
        choicesReusable: true,
        resources: state.resources,
    };
}

var airBucketing_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$f,
    airBucketing: airBucketing,
    getPureAirBucketing: getPureAirBucketing$1,
    'default': airBucketing
});

const prefix$c = '[Air ChoiceMatrix]';
const SETUP_EMPTY_ITEM$a = `${prefix$c} Setup Empty Item`;
class SetupEmptyItemAction$a {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$a;
    }
}
const TOGGLE_MULTIPLE_RESPONSES = `${prefix$c} Toggle multiple responses`;
class ToggleMultipleResponsesAction {
    constructor(payload) {
        this.payload = payload;
        this.type = TOGGLE_MULTIPLE_RESPONSES;
    }
}
const ADD_ROW = `${prefix$c} Add Row`;
class AddRowAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_ROW;
    }
}
const ADD_COLUMN = `${prefix$c} Add Column`;
class AddColumnAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_COLUMN;
    }
}
const DELETE_ROW = `${prefix$c} Delete Row`;
class DeleteRowAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ROW;
    }
}
const DELETE_COLUMN = `${prefix$c} Delete Column`;
class DeleteColumnAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_COLUMN;
    }
}
const UPDATE_ROW_LABEL = `${prefix$c} Update row label`;
class UpateRowLabelAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ROW_LABEL;
    }
}
const UPDATE_COLUMN_LABEL = `${prefix$c} Update column label`;
class UpateColumnLabelAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_COLUMN_LABEL;
    }
}
const SELECT_ANSWER = `${prefix$c} Select Answer`;
class SelectAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SELECT_ANSWER;
    }
}
const LOAD_AIR_ITEM$c = `${prefix$c} Load Air Item`;
class LoadAirItemAction$c {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$c;
    }
}
const RESET_STORE$c = `${prefix$c} Reset Store`;
class ResetStoreAction$c {
    constructor() {
        this.type = RESET_STORE$c;
    }
}

var airChoiceMatrix_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$a,
    SetupEmptyItemAction: SetupEmptyItemAction$a,
    TOGGLE_MULTIPLE_RESPONSES: TOGGLE_MULTIPLE_RESPONSES,
    ToggleMultipleResponsesAction: ToggleMultipleResponsesAction,
    ADD_ROW: ADD_ROW,
    AddRowAction: AddRowAction,
    ADD_COLUMN: ADD_COLUMN,
    AddColumnAction: AddColumnAction,
    DELETE_ROW: DELETE_ROW,
    DeleteRowAction: DeleteRowAction,
    DELETE_COLUMN: DELETE_COLUMN,
    DeleteColumnAction: DeleteColumnAction,
    UPDATE_ROW_LABEL: UPDATE_ROW_LABEL,
    UpateRowLabelAction: UpateRowLabelAction,
    UPDATE_COLUMN_LABEL: UPDATE_COLUMN_LABEL,
    UpateColumnLabelAction: UpateColumnLabelAction,
    SELECT_ANSWER: SELECT_ANSWER,
    SelectAnswerAction: SelectAnswerAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$c,
    LoadAirItemAction: LoadAirItemAction$c,
    RESET_STORE: RESET_STORE$c,
    ResetStoreAction: ResetStoreAction$c
});

const initialState$e = {
    prompt: '',
    rowLabels: [''],
    columnLabels: [''],
    selectMultiplePerRow: false,
    answers: [[]],
};
function airChoiceMatrix(state = initialState$e, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.CHOICE_MATRIX) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case LOAD_AIR_ITEM$c: {
            return {
                ...state,
                prompt: action.payload.prompt,
                rowLabels: action.payload.rowLabels,
                columnLabels: action.payload.columnLabels,
                selectMultiplePerRow: action.payload.selectMultiplePerRow,
                answers: action.payload.answers,
                resources: action.payload.resources,
            };
        }
        case ADD_ROW: {
            const rows = action.payload.noOfRows;
            return {
                ...state,
                rowLabels: [...state.rowLabels].concat(Array(rows).fill('')),
                answers: [...state.answers].concat(Array(rows).fill([])),
            };
        }
        case ADD_COLUMN: {
            const columns = action.payload.noOfColumns;
            return {
                ...state,
                columnLabels: [...state.columnLabels].concat(Array(columns).fill('')),
            };
        }
        case SELECT_ANSWER: {
            return {
                ...state,
                answers: state.answers.map((answer, index) => {
                    if (index === action.payload.rowIndex) {
                        if (!state.selectMultiplePerRow) {
                            return [action.payload.columnIndex];
                        }
                        else {
                            const answerObject = answer.find((ans) => ans === action.payload.columnIndex);
                            if (typeof answerObject === 'number') {
                                const answerIndex = answer.indexOf(answerObject);
                                return answer.filter((ans, index) => index !== answerIndex);
                            }
                            else {
                                return [...answer, action.payload.columnIndex];
                            }
                        }
                    }
                    return answer;
                }),
            };
        }
        case DELETE_ROW: {
            return {
                ...state,
                rowLabels: state.rowLabels.filter((rowLabel, rowIndex) => {
                    return rowIndex !== action.payload;
                }),
                answers: state.answers.filter((answer, answerIndex) => {
                    return answerIndex !== action.payload;
                }),
            };
        }
        case DELETE_COLUMN: {
            const columnIndex = action.payload;
            const modifiedState = {
                ...state,
                columnLabels: state.columnLabels.filter((columnLabel, index) => {
                    return index !== columnIndex;
                }),
                answers: state.answers.map((answer) => {
                    return answer.map((ans) => {
                        if (ans > columnIndex) {
                            return ans - 1;
                        }
                        else if (ans === columnIndex) {
                            return -1;
                        }
                        return ans;
                    });
                }),
            };
            return {
                ...modifiedState,
                answers: modifiedState.answers.map((answer) => {
                    return answer.filter((ans) => ans !== -1);
                }),
            };
        }
        case UPDATE_COLUMN_LABEL: {
            return {
                ...state,
                columnLabels: state.columnLabels.map((columnLabel, colIndex) => {
                    if (colIndex === action.payload.index) {
                        return action.payload.value;
                    }
                    return columnLabel;
                }),
            };
        }
        case UPDATE_ROW_LABEL: {
            return {
                ...state,
                rowLabels: state.rowLabels.map((rowLabel, rowIndex) => {
                    if (rowIndex === action.payload.index) {
                        return action.payload.value;
                    }
                    return rowLabel;
                }),
            };
        }
        case TOGGLE_MULTIPLE_RESPONSES: {
            return {
                ...state,
                selectMultiplePerRow: action.payload,
                answers: state.answers.map((answer) => []),
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$c: {
            return initialState$e;
        }
        default: {
            return state;
        }
    }
}
function getPureAirChoiceMatrix$1(state) {
    return {
        prompt: state.prompt,
        rowLabels: state.rowLabels,
        columnLabels: state.columnLabels,
        answers: state.answers,
        selectMultiplePerRow: state.selectMultiplePerRow,
        resources: state.resources,
    };
}

var airChoiceMatrix_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$e,
    airChoiceMatrix: airChoiceMatrix,
    getPureAirChoiceMatrix: getPureAirChoiceMatrix$1,
    'default': airChoiceMatrix
});

const prefix$b = '[Air Equation Entry]';
const SETUP_EMPTY_ITEM$9 = `${prefix$b} Setup Empty Item`;
class SetupEmptyItemAction$9 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$9;
    }
}
const ADD_ANSWER$1 = `${prefix$b} Add Answer`;
class AddAnswerAction$1 {
    constructor() {
        this.type = ADD_ANSWER$1;
    }
}
const DELETE_ANSWER$1 = `${prefix$b} Delete Answer`;
class DeleteAnswerAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ANSWER$1;
    }
}
const UPDATE_EDITOR_TYPE = `${prefix$b} Update Editor Type`;
class UpdateEditorTypeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EDITOR_TYPE;
    }
}
const UPDATE_ANSWER$1 = `${prefix$b} Update Answer`;
class UpdateAnswerAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER$1;
    }
}
const UPDATE_ALLOW_EQUIVALENCY = `${prefix$b} Allow Equivalency`;
class UpdateAllowEquivalencyAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ALLOW_EQUIVALENCY;
    }
}
const RESET_STORE$b = `${prefix$b} Reset Store`;
class ResetStoreAction$b {
    constructor() {
        this.type = RESET_STORE$b;
    }
}
const LOAD_AIR_ITEM$b = `${prefix$b} Load Air Item`;
class LoadAirItemAction$b {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$b;
    }
}

var airEquationEntry_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$9,
    SetupEmptyItemAction: SetupEmptyItemAction$9,
    ADD_ANSWER: ADD_ANSWER$1,
    AddAnswerAction: AddAnswerAction$1,
    DELETE_ANSWER: DELETE_ANSWER$1,
    DeleteAnswerAction: DeleteAnswerAction$1,
    UPDATE_EDITOR_TYPE: UPDATE_EDITOR_TYPE,
    UpdateEditorTypeAction: UpdateEditorTypeAction,
    UPDATE_ANSWER: UPDATE_ANSWER$1,
    UpdateAnswerAction: UpdateAnswerAction$1,
    UPDATE_ALLOW_EQUIVALENCY: UPDATE_ALLOW_EQUIVALENCY,
    UpdateAllowEquivalencyAction: UpdateAllowEquivalencyAction,
    RESET_STORE: RESET_STORE$b,
    ResetStoreAction: ResetStoreAction$b,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$b,
    LoadAirItemAction: LoadAirItemAction$b
});

const MathML = '<math xmlns="http://www.w3.org/1998/Math/MathML"/>';
const initialState$d = {
    prompt: '',
    editor: 'basic',
    allowEquivalency: false,
    correctness: null,
    answers: [''],
    preset: undefined,
};
function airEquationEntry(state = initialState$d, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.EQUATION_ENTRY) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case ADD_ANSWER$1: {
            return {
                ...state,
                answers: [...state.answers, MathML],
            };
        }
        case DELETE_ANSWER$1: {
            return {
                ...state,
                answers: state.answers.filter((answer, index) => action.payload !== index),
            };
        }
        case UPDATE_EDITOR_TYPE: {
            return {
                ...state,
                editor: action.payload,
            };
        }
        case UPDATE_ALLOW_EQUIVALENCY: {
            return {
                ...state,
                allowEquivalency: action.payload,
            };
        }
        case UPDATE_ANSWER$1: {
            return {
                ...state,
                answers: state.answers.map((answer, index) => {
                    if (index === action.payload.index) {
                        return he.decode(action.payload.answer);
                    }
                    return answer;
                }),
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case LOAD_AIR_ITEM$b: {
            // in case we get very old items that don't know about presets, only use default for allow equialency if it does not exist
            const allowEquivalency = action.payload.preset ? undefined : action.payload.allowEquivalency || false;
            const correctness = action.payload.correctness;
            return {
                ...state,
                prompt: action.payload.prompt,
                editor: action.payload.editor,
                allowEquivalency: allowEquivalency,
                correctness: correctness === null || correctness === undefined ? null : correctness,
                answers: action.payload.answers ? action.payload.answers : [MathML],
                preset: action.payload.preset,
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$b: {
            return initialState$d;
        }
        default: {
            return state;
        }
    }
}
function getPureAirEquationEntry$1(state) {
    return {
        prompt: state.prompt,
        editor: state.editor,
        answers: state.answers.filter((answer) => {
            return answer !== MathML;
        }),
        allowEquivalency: state.allowEquivalency,
        correctness: state.correctness,
        preset: state.preset,
        resources: state.resources,
    };
}

var airEquationEntry_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$d,
    airEquationEntry: airEquationEntry,
    getPureAirEquationEntry: getPureAirEquationEntry$1,
    'default': airEquationEntry
});

const prefix$a = '[Air Image Labeling]';
const SETUP_EMPTY_ITEM$8 = `${prefix$a} Setup Empty Item`;
class SetupEmptyItemAction$8 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$8;
    }
}
const SET_IMAGE_LOADING_INDICATOR = `${prefix$a} Set Image Loading Indicator`;
class SetImageLoadingIndicator {
    constructor(payload) {
        this.payload = payload;
        this.type = SET_IMAGE_LOADING_INDICATOR;
    }
}
const ADD_DISTRACTOR_DROPZONE = `${prefix$a} Add Distractor Dropzone`;
class AddOrUpdateDistractorDropzoneAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_DISTRACTOR_DROPZONE;
    }
}
const REMOVE_DISTRACTOR_DROPZONE = `${prefix$a} Remove Distractor Dropzone`;
class RemoveDistractorDropzoneAction {
    constructor(payload) {
        this.payload = payload;
        this.type = REMOVE_DISTRACTOR_DROPZONE;
    }
}
const REMOVE_LABEL = `${prefix$a} Remove Label`;
class RemoveLabelAction {
    constructor(payload) {
        this.payload = payload;
        this.type = REMOVE_LABEL;
    }
}
const LOAD_AIR_ITEM$a = `${prefix$a} Load Air Item`;
class LoadAirItemAction$a {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$a;
    }
}
const RESET_STORE$a = `${prefix$a} Reset Store`;
class ResetStoreAction$a {
    constructor() {
        this.type = RESET_STORE$a;
    }
}
const ADD_LABEL_DISTRACTOR = `${prefix$a} Add Label Distractor`;
class AddLabelOrDistractorAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_LABEL_DISTRACTOR;
    }
}
const DELETE_LABEL_DISTRACTOR = `${prefix$a} Delete Label Distractor`;
class DeleteLabelOrDistractorAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_LABEL_DISTRACTOR;
    }
}
const MOVE_LABEL_UP = `${prefix$a} Move Label Up`;
class MoveLabelUpAction {
    constructor(payload) {
        this.payload = payload;
        this.type = MOVE_LABEL_UP;
    }
}
const MOVE_LABEL_DOWN = `${prefix$a} Move Label Down`;
class MoveLabelDownAction {
    constructor(payload) {
        this.payload = payload;
        this.type = MOVE_LABEL_DOWN;
    }
}
const UPDATE_LABEL_DISTRACTOR = `${prefix$a} Update Label Distractor`;
class UpdateLabelOrDistractorAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_LABEL_DISTRACTOR;
    }
}
const UPDATE_BACKGROUND_IMAGE$1 = `${prefix$a} Update Background Image`;
class UpdateBackgroundImageAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_BACKGROUND_IMAGE$1;
    }
}
const DELETE_BACKGROUND_IMAGE$1 = `${prefix$a} Delete Background Image`;
class DeleteBackgroundImageAction$1 {
    constructor() {
        this.type = DELETE_BACKGROUND_IMAGE$1;
    }
}
const DELETE_LABEL_DROPZONE = `${prefix$a} Delete Label Drop Zone`;
class DeleteLabelDropZoneAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_LABEL_DROPZONE;
    }
}
const SET_RELATIVE_COORDINATE = `${prefix$a} Set Relative Coordinate`;
class SetRelativeCoordinateAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SET_RELATIVE_COORDINATE;
    }
}
const DELETE_DISTRACTOR_DROPZONE = `${prefix$a} Delete Distractor Label Drop Zone`;
class DeleteDistractorDropZoneAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_DISTRACTOR_DROPZONE;
    }
}
const ADD_CHOICE_IN_LABEL = `${prefix$a} Add Choice In Label`;
class AddChoiceInLabel {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_CHOICE_IN_LABEL;
    }
}
const UPDATE_CHOICE_IN_LABEL = `${prefix$a} Update Choice In Label`;
class UpdateChoiceInLabel {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CHOICE_IN_LABEL;
    }
}

var airImageLabeling_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$8,
    SetupEmptyItemAction: SetupEmptyItemAction$8,
    SET_IMAGE_LOADING_INDICATOR: SET_IMAGE_LOADING_INDICATOR,
    SetImageLoadingIndicator: SetImageLoadingIndicator,
    ADD_DISTRACTOR_DROPZONE: ADD_DISTRACTOR_DROPZONE,
    AddOrUpdateDistractorDropzoneAction: AddOrUpdateDistractorDropzoneAction,
    REMOVE_DISTRACTOR_DROPZONE: REMOVE_DISTRACTOR_DROPZONE,
    RemoveDistractorDropzoneAction: RemoveDistractorDropzoneAction,
    REMOVE_LABEL: REMOVE_LABEL,
    RemoveLabelAction: RemoveLabelAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$a,
    LoadAirItemAction: LoadAirItemAction$a,
    RESET_STORE: RESET_STORE$a,
    ResetStoreAction: ResetStoreAction$a,
    ADD_LABEL_DISTRACTOR: ADD_LABEL_DISTRACTOR,
    AddLabelOrDistractorAction: AddLabelOrDistractorAction,
    DELETE_LABEL_DISTRACTOR: DELETE_LABEL_DISTRACTOR,
    DeleteLabelOrDistractorAction: DeleteLabelOrDistractorAction,
    MOVE_LABEL_UP: MOVE_LABEL_UP,
    MoveLabelUpAction: MoveLabelUpAction,
    MOVE_LABEL_DOWN: MOVE_LABEL_DOWN,
    MoveLabelDownAction: MoveLabelDownAction,
    UPDATE_LABEL_DISTRACTOR: UPDATE_LABEL_DISTRACTOR,
    UpdateLabelOrDistractorAction: UpdateLabelOrDistractorAction,
    UPDATE_BACKGROUND_IMAGE: UPDATE_BACKGROUND_IMAGE$1,
    UpdateBackgroundImageAction: UpdateBackgroundImageAction$1,
    DELETE_BACKGROUND_IMAGE: DELETE_BACKGROUND_IMAGE$1,
    DeleteBackgroundImageAction: DeleteBackgroundImageAction$1,
    DELETE_LABEL_DROPZONE: DELETE_LABEL_DROPZONE,
    DeleteLabelDropZoneAction: DeleteLabelDropZoneAction,
    SET_RELATIVE_COORDINATE: SET_RELATIVE_COORDINATE,
    SetRelativeCoordinateAction: SetRelativeCoordinateAction,
    DELETE_DISTRACTOR_DROPZONE: DELETE_DISTRACTOR_DROPZONE,
    DeleteDistractorDropZoneAction: DeleteDistractorDropZoneAction,
    ADD_CHOICE_IN_LABEL: ADD_CHOICE_IN_LABEL,
    AddChoiceInLabel: AddChoiceInLabel,
    UPDATE_CHOICE_IN_LABEL: UPDATE_CHOICE_IN_LABEL,
    UpdateChoiceInLabel: UpdateChoiceInLabel
});

const initialState$c = {
    prompt: '',
    choices: [],
    labels: [],
    answers: [],
    randomize: false,
    nextChoicekey: 0,
    nextLabelkey: 0,
    backgroundImage: '',
    isImageLoaded: false,
    distractorDropzones: [],
    isEditMode: false,
    initialDropzones: [],
};
function airImageLabeling(state = initialState$c, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.IMAGE_LABELING) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case UPDATE_BACKGROUND_IMAGE$1: {
            return {
                ...state,
                backgroundImage: action.payload.url,
            };
        }
        case DELETE_BACKGROUND_IMAGE$1: {
            return {
                ...state,
                backgroundImage: '',
                labels: [],
                answers: [],
                distractorDropzones: [],
                initialDropzones: [],
                isImageLoaded: false,
            };
        }
        case LOAD_AIR_ITEM$a: {
            const answers = action.payload.answers;
            const labels = action.payload.labels;
            const choices = action.payload.choices;
            const distractors = labels.filter((label) => {
                return answers.find((answer) => answer.label === label.key) == null;
            });
            return {
                prompt: action.payload.prompt,
                choices: choices,
                labels: labels.map((label) => {
                    return {
                        ...label,
                        height: label.height || getLabelDimensions(answers, choices, label).height,
                        width: label.width || getLabelDimensions(answers, choices, label).width,
                    };
                }),
                answers: answers,
                randomize: action.payload.randomize,
                backgroundImage: action.payload.backgroundImage,
                distractorDropzones: distractors.map((distractor, index) => {
                    return {
                        dropzoneId: 'distractor-' + index + '-dropzone-back',
                        labelKey: distractor.key,
                    };
                }),
                initialDropzones: distractors.map((distractor, index) => {
                    return {
                        dropzoneId: 'distractor-' + index + '-dropzone-back',
                        labelKey: distractor.key,
                    };
                }),
                isEditMode: true,
                nextChoicekey: getNextKey(action.payload.choices, 'choice'),
                nextLabelkey: getNextKey(action.payload.labels, 'label'),
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$a: {
            return initialState$c;
        }
        case ADD_LABEL_DISTRACTOR: {
            return {
                ...state,
                choices: [
                    ...state.choices,
                    {
                        key: 'choice-' + state.nextChoicekey,
                        content: action.payload.content,
                    },
                ],
                nextChoicekey: (state.nextChoicekey || 0) + 1,
            };
        }
        case DELETE_LABEL_DISTRACTOR: {
            const answerToDelete = state.answers && state.answers.find((answer) => answer.choice === action.payload.key);
            if (!answerToDelete) {
                return {
                    ...state,
                    choices: state.choices.filter((choice) => choice !== action.payload),
                };
            }
            const choiceIndex = state.choices.indexOf(action.payload);
            const modifiedChoiceKeys = state.choices
                .filter((choice, index) => index > choiceIndex)
                .map((choice) => choice.key);
            const labelsToMove = state.answers
                .filter((answer) => modifiedChoiceKeys.includes(answer.choice))
                .map((answer) => answer.label);
            const modifiedLabels = state.labels.filter((label) => label.key !== answerToDelete.label);
            const deletedLabel = state.labels.find((label) => label.key === answerToDelete.label);
            return {
                ...state,
                choices: state.choices.filter((choice) => choice !== action.payload),
                labels: modifiedLabels.map((label, index) => {
                    if (labelsToMove.includes(label.key || '')) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) + (deletedLabel?.height || 0) + 20,
                            },
                        };
                    }
                    return label;
                }),
                answers: state.answers.filter((answer) => answer !== answerToDelete),
            };
        }
        case REMOVE_LABEL: {
            const answer = state.answers.length && state.answers.find((answer) => answer.choice === action.payload.choiceKey);
            if (!answer) {
                return { ...state };
            }
            return {
                ...state,
                labels: state.labels.filter((label) => label.key !== answer.label),
                answers: state.answers.filter((answer) => answer.choice !== action.payload.choiceKey),
            };
        }
        case UPDATE_LABEL_DISTRACTOR: {
            const editedChoice = action.payload.editedObject;
            const mappedChoice = state.answers.find((answer) => answer.choice === editedChoice.key);
            const hasImage = action.payload.content.split('<img').length > 1;
            return {
                ...state,
                choices: state.choices.map((choice) => {
                    if (choice === editedChoice) {
                        return {
                            ...choice,
                            content: action.payload.content,
                        };
                    }
                    return choice;
                }),
                labels: state.labels.map((label) => {
                    if (mappedChoice && label.key === mappedChoice.label) {
                        return {
                            ...label,
                            height: hasImage ? 86 : 44,
                            width: hasImage ? 140 : 160,
                        };
                    }
                    return label;
                }),
            };
        }
        case MOVE_LABEL_UP: {
            const currentIndex = state.choices.findIndex((choice) => choice.key === action.payload.key);
            const previousIndex = currentIndex - 1;
            const previousChoice = state.choices[previousIndex];
            const currentAnswer = state.answers.find((answer) => answer.choice === action.payload.key);
            const previousAnswer = state.answers.find((answer) => answer.choice === previousChoice.key);
            const prevLabel = state.labels.find((label) => previousAnswer && label.key === previousAnswer.label);
            const hasImage = state.choices[currentIndex].content.split('<img').length > 1;
            const hasPrevImage = state.choices[previousIndex].content.split('<img').length > 1;
            return {
                ...state,
                choices: state.choices.map((choice, index) => {
                    if (index === currentIndex) {
                        return state.choices[index - 1];
                    }
                    else if (index === currentIndex - 1) {
                        return state.choices[index + 1];
                    }
                    return choice;
                }),
                labels: state.labels.map((label) => {
                    if (currentAnswer && label.key === currentAnswer.label && prevLabel) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) + (prevLabel?.height || 0) + 20,
                            },
                        };
                    }
                    else if (currentAnswer && label.key === currentAnswer.label && !prevLabel) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) + (hasPrevImage ? 86 : 44) + 20,
                            },
                        };
                    }
                    else if (previousAnswer && label.key === previousAnswer.label) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) - (hasImage ? 86 : 44) - 20,
                            },
                        };
                    }
                    return label;
                }),
            };
        }
        case MOVE_LABEL_DOWN: {
            const currentIndex = state.choices.findIndex((choice) => choice.key === action.payload.key);
            const nextIndex = currentIndex + 1;
            const nextChoice = state.choices[nextIndex];
            const currentAnswer = state.answers.find((answer) => answer.choice === action.payload.key);
            const nextAnswer = state.answers.find((answer) => answer.choice === nextChoice.key);
            const nextLabel = state.labels.find((label) => nextAnswer && label.key === nextAnswer.label);
            const hasImage = state.choices[currentIndex].content.split('<img').length > 1;
            const hasNextImage = state.choices[nextIndex].content.split('<img').length > 1;
            return {
                ...state,
                choices: state.choices.map((choice, index) => {
                    if (index === currentIndex) {
                        return state.choices[index + 1];
                    }
                    else if (index === currentIndex + 1) {
                        return state.choices[index - 1];
                    }
                    return choice;
                }),
                labels: state.labels.map((label) => {
                    if (currentAnswer && label.key === currentAnswer.label && nextLabel) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) - (nextLabel?.height || 0) - 20,
                            },
                        };
                    }
                    else if (currentAnswer && label.key === currentAnswer.label && !nextLabel) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) - (hasNextImage ? 86 : 44) - 20,
                            },
                        };
                    }
                    else if (nextAnswer && label.key === nextAnswer.label) {
                        return {
                            ...label,
                            relativeCoordinate: {
                                ...label.relativeCoordinate,
                                y: (label.relativeCoordinate?.y || 0) + (hasImage ? 86 : 44) + 20,
                            },
                        };
                    }
                    return label;
                }),
            };
        }
        case DELETE_LABEL_DROPZONE: {
            const choiceKey = action.payload.key;
            const draggedLabel = state.answers.find((answer) => answer.choice === choiceKey);
            if (draggedLabel) {
                return {
                    ...state,
                    labels: state.labels.filter((label) => {
                        if (label.key !== draggedLabel.label) {
                            return {
                                ...label,
                            };
                        }
                    }),
                    answers: state.answers.filter((answer) => {
                        if (answer.label !== draggedLabel.label && answer.choice !== choiceKey) {
                            return {
                                ...answer,
                            };
                        }
                    }),
                };
            }
            else {
                return {
                    ...state,
                };
            }
        }
        case DELETE_DISTRACTOR_DROPZONE: {
            const dropzoneId = action.payload;
            if (dropzoneId) {
                const selectedDropzone = state.distractorDropzones.find((dropzone) => dropzone.dropzoneId === dropzoneId);
                return {
                    ...state,
                    distractorDropzones: state.distractorDropzones.filter((dist) => dist.dropzoneId !== dropzoneId),
                    labels: state.labels.filter((label) => label.key !== selectedDropzone?.labelKey),
                };
            }
            return {
                ...state,
            };
        }
        case SET_IMAGE_LOADING_INDICATOR: {
            return {
                ...state,
                isImageLoaded: action.payload,
            };
        }
        case ADD_DISTRACTOR_DROPZONE: {
            const draggedDistractor = state.distractorDropzones.find((dropzone) => dropzone.dropzoneId === action.payload.key);
            if (draggedDistractor) {
                return {
                    ...state,
                    labels: state.labels.map((label) => {
                        if (label.key === draggedDistractor.labelKey) {
                            return {
                                ...label,
                                x: action.payload.x,
                                y: action.payload.y,
                                relativeCoordinate: action.payload.relativeCoordinate,
                                height: parseInt(action.payload.height, 10),
                                width: parseInt(action.payload.width, 10),
                            };
                        }
                        return label;
                    }),
                };
            }
            else {
                return {
                    ...state,
                    labels: [
                        ...state.labels,
                        {
                            key: 'label-' + state.nextLabelkey,
                            x: action.payload.x,
                            y: action.payload.y,
                            relativeCoordinate: action.payload.relativeCoordinate,
                            height: parseInt(action.payload.height, 10),
                            width: parseInt(action.payload.width, 10),
                        },
                    ],
                    distractorDropzones: [
                        ...state.distractorDropzones,
                        {
                            dropzoneId: action.payload.key,
                            labelKey: 'label-' + state.nextLabelkey,
                        },
                    ],
                    nextLabelkey: (state.nextLabelkey || 0) + 1,
                };
            }
        }
        case REMOVE_DISTRACTOR_DROPZONE: {
            const distractor = state.distractorDropzones.length &&
                state.distractorDropzones.find((dropzone) => {
                    return dropzone.dropzoneId === action.payload.dropzoneKey;
                });
            if (!distractor) {
                return state;
            }
            return {
                ...state,
                distractorDropzones: state.distractorDropzones.filter((distractorDropZone) => {
                    return distractorDropZone.dropzoneId !== action.payload.dropzoneKey;
                }),
                labels: state.labels.filter((label) => label.key !== distractor.labelKey),
            };
        }
        case SET_RELATIVE_COORDINATE: {
            return {
                ...state,
                labels: state.labels.map((label) => {
                    if (label.key === action.payload.key) {
                        return {
                            ...label,
                            relativeCoordinate: action.payload.relativeCoordinate,
                        };
                    }
                    return label;
                }),
            };
        }
        case ADD_CHOICE_IN_LABEL: {
            return {
                ...state,
                labels: [
                    ...state.labels,
                    {
                        key: 'label-' + state.nextLabelkey,
                        x: action.payload.x,
                        y: action.payload.y,
                        relativeCoordinate: action.payload.relativeCoordinate,
                        height: parseInt(action.payload.height, 10),
                        width: parseInt(action.payload.width, 10),
                    },
                ],
                answers: [
                    ...state.answers,
                    {
                        choice: action.payload.key,
                        label: 'label-' + state.nextLabelkey,
                    },
                ],
                nextLabelkey: (state.nextLabelkey || 0) + 1,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_CHOICE_IN_LABEL: {
            return {
                ...state,
                labels: state.labels.map((label) => {
                    if (label.key === action.payload.key) {
                        return {
                            ...label,
                            x: action.payload.x,
                            y: action.payload.y,
                            relativeCoordinate: action.payload.relativeCoordinate,
                            height: parseInt(action.payload.height, 10),
                            width: parseInt(action.payload.width, 10),
                        };
                    }
                    return label;
                }),
            };
        }
        default: {
            return state;
        }
    }
}
function getLabelDimensions(answers, choices, label) {
    const labelInAnswer = answers.find((answer) => answer.label === label.key);
    let hasImage = false;
    if (labelInAnswer) {
        const mappedChoice = choices.find((choice) => labelInAnswer.choice === choice.key);
        if (mappedChoice) {
            hasImage = mappedChoice.content.split('<img').length > 1;
        }
    }
    return hasImage ? { height: 86, width: 140 } : { height: 44, width: 160 };
}
function getNextKey(array, selector) {
    const maxKey = array
        .map((elem, index) => parseInt(elem.key.split(selector + '-')[1], 10) || index)
        .reduce((a, b) => Math.max(a, b), 0);
    return maxKey + 1;
}
function getPureAirImageLabeling$1(state) {
    return {
        prompt: state.prompt,
        choices: state.choices,
        labels: state.labels,
        answers: state.answers,
        randomize: state.randomize,
        backgroundImage: state.backgroundImage,
        isImageLoaded: state.isImageLoaded,
        distractorDropzones: state.distractorDropzones,
        resources: state.resources,
    };
}

var airImageLabeling_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$c,
    airImageLabeling: airImageLabeling,
    getLabelDimensions: getLabelDimensions,
    getPureAirImageLabeling: getPureAirImageLabeling$1,
    'default': airImageLabeling
});

const prefix$9 = '[Air Instruction Item]';
const SETUP_EMPTY_ITEM$7 = `${prefix$9} Setup Empty Item`;
class SetupEmptyItemAction$7 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$7;
    }
}
const RESET_STORE$9 = `${prefix$9} Reset Store`;
class ResetStoreAction$9 {
    constructor() {
        this.type = RESET_STORE$9;
    }
}
const LOAD_AIR_ITEM$9 = `${prefix$9} Load Air Item`;
class LoadAirItemAction$9 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$9;
    }
}

var airInstructionItem_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$7,
    SetupEmptyItemAction: SetupEmptyItemAction$7,
    RESET_STORE: RESET_STORE$9,
    ResetStoreAction: ResetStoreAction$9,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$9,
    LoadAirItemAction: LoadAirItemAction$9
});

const initialState$b = {
    prompt: '',
};
function airInstructionItem(state = initialState$b, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.INSTRUCTION) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$9: {
            return initialState$b;
        }
        case LOAD_AIR_ITEM$9: {
            const { payload } = action;
            return {
                ...state,
                prompt: payload.prompt,
                resources: payload.resources,
            };
        }
        default: {
            return state;
        }
    }
}
function getPureAirInstructionItem$1(state) {
    const air = {
        prompt: state.prompt,
        resources: state.resources,
    };
    return air;
}

var airInstructionItem_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$b,
    airInstructionItem: airInstructionItem,
    getPureAirInstructionItem: getPureAirInstructionItem$1,
    'default': airInstructionItem
});

const prefix$8 = '[Air Grid In]';
const SETUP_EMPTY_ITEM$6 = `${prefix$8} Setup Empty Item`;
class SetupEmptyItemAction$6 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$6;
    }
}
const RESET_STORE$8 = `${prefix$8} Reset Store`;
class ResetStoreAction$8 {
    constructor() {
        this.type = RESET_STORE$8;
    }
}
const LOAD_AIR_ITEM$8 = `${prefix$8} Load Air Item`;
class LoadAirItemAction$8 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$8;
    }
}
const UPDATE_OPTION_TYPE = `${prefix$8} Update Option Type`;
class UpdateOptionTypeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_OPTION_TYPE;
    }
}
const ADD_ANSWER = `${prefix$8} Add Answer`;
class AddAnswerAction {
    constructor() {
        this.type = ADD_ANSWER;
    }
}
const UPDATE_ANSWER = `${prefix$8} Update Answer`;
class UpdateAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER;
    }
}
const DELETE_ANSWER = `${prefix$8} Delete Answer`;
class DeleteAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ANSWER;
    }
}

var airGridIn_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$6,
    SetupEmptyItemAction: SetupEmptyItemAction$6,
    RESET_STORE: RESET_STORE$8,
    ResetStoreAction: ResetStoreAction$8,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$8,
    LoadAirItemAction: LoadAirItemAction$8,
    UPDATE_OPTION_TYPE: UPDATE_OPTION_TYPE,
    UpdateOptionTypeAction: UpdateOptionTypeAction,
    ADD_ANSWER: ADD_ANSWER,
    AddAnswerAction: AddAnswerAction,
    UPDATE_ANSWER: UPDATE_ANSWER,
    UpdateAnswerAction: UpdateAnswerAction,
    DELETE_ANSWER: DELETE_ANSWER,
    DeleteAnswerAction: DeleteAnswerAction
});

const initialState$a = {
    prompt: '',
    optionType: 'numeric',
    answers: [],
    tooManyAnswers: false,
};
function airGridIn(state = initialState$a, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.GRID_IN) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            const content = action.payload.content;
            const blankCount = content.split('__').length - 1;
            if (blankCount > 20) {
                return {
                    ...state,
                    tooManyAnswers: true,
                };
            }
            else {
                const value = state.optionType === 'numeric' ? '0' : state.optionType === 'decimal' ? '.' : 'a';
                return {
                    ...state,
                    prompt: content,
                    answers: Array(blankCount)
                        .fill(null)
                        .map((blank, index) => {
                        if (state.answers[index]) {
                            return state.answers[index];
                        }
                        else {
                            return {
                                key: `response${index}`,
                                value,
                            };
                        }
                    }),
                    tooManyAnswers: false,
                };
            }
        }
        case SETUP_EMPTY_ITEM$6: {
            return initialState$a;
        }
        case LOAD_AIR_ITEM$8: {
            const payload = action.payload;
            const prompt = new ResponseToUnderscorePipe().transform(payload.prompt);
            return {
                ...state,
                prompt,
                optionType: payload.optionType,
                answers: payload.answers,
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$8: {
            return initialState$a;
        }
        case UPDATE_OPTION_TYPE: {
            const value = action.payload === 'numeric' ? '0' : action.payload === 'decimal' ? '.' : 'a';
            return {
                ...state,
                optionType: action.payload,
                answers: state.answers.map((answer) => {
                    return {
                        ...answer,
                        value,
                    };
                }),
            };
        }
        case ADD_ANSWER: {
            const answers = [...state.answers];
            const value = state.optionType === 'numeric' ? '0' : state.optionType === 'decimal' ? '.' : 'a';
            answers.push({ key: `response${answers.length}`, value });
            return {
                ...state,
                answers,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_ANSWER: {
            const payload = action.payload;
            return {
                ...state,
                answers: state.answers.map((answer) => {
                    if (answer.key === payload.key) {
                        return {
                            ...answer,
                            value: payload.updatedValue,
                        };
                    }
                    else {
                        return { ...answer };
                    }
                }),
            };
        }
        case DELETE_ANSWER: {
            return {
                ...state,
                answers: state.answers.filter((answer) => answer.key !== action.payload),
            };
        }
        default: {
            return state;
        }
    }
}
function getPureAirGridIn$1(state) {
    return {
        prompt: generateStem(state),
        optionType: state.optionType,
        answers: state.answers,
        tooManyAnswers: state.tooManyAnswers,
        resources: state.resources,
    };
}
function generateStem(state) {
    return state.prompt.split('__').reduce((stem, value, index) => {
        if (state.answers[index]) {
            return stem + value + '${response:' + state.answers[index].key + '}';
        }
        else {
            return stem + value;
        }
    }, '');
}

var airGridIn_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$a,
    airGridIn: airGridIn,
    getPureAirGridIn: getPureAirGridIn$1,
    'default': airGridIn
});

const prefix$7 = '[Air Audio Recording]';
const SETUP_EMPTY_ITEM$5 = `${prefix$7} Setup Empty Item`;
class SetupEmptyItemAction$5 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$5;
    }
}
const RESET_STORE$7 = `${prefix$7} Reset Store`;
class ResetStoreAction$7 {
    constructor() {
        this.type = RESET_STORE$7;
    }
}
const LOAD_AIR_ITEM$7 = `${prefix$7} Load Air Item`;
class LoadAirItemAction$7 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$7;
    }
}
const UPDATE_AUDIO_SPAN = `${prefix$7} Update Audio Span`;
class UpdateAudioSpanAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_AUDIO_SPAN;
    }
}

var airAudioRecording_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$5,
    SetupEmptyItemAction: SetupEmptyItemAction$5,
    RESET_STORE: RESET_STORE$7,
    ResetStoreAction: ResetStoreAction$7,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$7,
    LoadAirItemAction: LoadAirItemAction$7,
    UPDATE_AUDIO_SPAN: UPDATE_AUDIO_SPAN,
    UpdateAudioSpanAction: UpdateAudioSpanAction
});

const initialState$9 = {
    prompt: '',
    maximumLength: 10,
};
function airAudioRecording(state = initialState$9, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.AUDIO_RECORDING) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case SETUP_EMPTY_ITEM$5: {
            return initialState$9;
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case LOAD_AIR_ITEM$7: {
            const payload = action.payload;
            return {
                ...state,
                prompt: payload.prompt,
                maximumLength: payload.maximumLength,
                resources: payload.resources,
            };
        }
        case RESET_STORE$7: {
            return initialState$9;
        }
        case UPDATE_AUDIO_SPAN: {
            const payload = action.payload;
            return {
                ...state,
                maximumLength: payload === '' ? null : Number(payload.trim()),
            };
        }
        default: {
            return state;
        }
    }
}
function getPureAirAudioRecording$1(state) {
    return {
        prompt: state.prompt,
        maximumLength: state.maximumLength,
        resources: state.resources,
    };
}

var airAudioRecording_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$9,
    airAudioRecording: airAudioRecording,
    getPureAirAudioRecording: getPureAirAudioRecording$1,
    'default': airAudioRecording
});

var ChoiceMatrixCellType;
(function (ChoiceMatrixCellType) {
    ChoiceMatrixCellType["TEXT"] = "text";
    ChoiceMatrixCellType["EQUATION_ENTRY"] = "equationEntry";
})(ChoiceMatrixCellType || (ChoiceMatrixCellType = {}));
var ChoiceMatrixTableProps;
(function (ChoiceMatrixTableProps) {
    ChoiceMatrixTableProps["ROW"] = "row";
    ChoiceMatrixTableProps["COLUMN"] = "column";
})(ChoiceMatrixTableProps || (ChoiceMatrixTableProps = {}));

var nlPointType;
(function (nlPointType) {
    nlPointType["CLOSED"] = "closed";
    nlPointType["OPEN"] = "open";
})(nlPointType || (nlPointType = {}));
var graphType;
(function (graphType) {
    graphType["DECIMALS"] = "decimals";
    graphType["FRACTION"] = "fraction";
})(graphType || (graphType = {}));
var responseType;
(function (responseType) {
    responseType["POINT"] = "point";
    responseType["CONNECTION"] = "connection";
})(responseType || (responseType = {}));

var ShapeTypeEnum;
(function (ShapeTypeEnum) {
    ShapeTypeEnum["POLYGON"] = "polygon";
    ShapeTypeEnum["RECTANGLE"] = "rectangle";
    ShapeTypeEnum["ELLIPSE"] = "ellipse";
})(ShapeTypeEnum || (ShapeTypeEnum = {}));
var SVGShapeType;
(function (SVGShapeType) {
    SVGShapeType["POLYGON"] = "polygon";
    SVGShapeType["RECT"] = "rect";
    SVGShapeType["ELLIPSE"] = "ellipse";
})(SVGShapeType || (SVGShapeType = {}));
function isOfShapeType(obj, propertyChecker) {
    return obj.shape === propertyChecker;
}

var modeEnum;
(function (modeEnum) {
    modeEnum["AUTHOR"] = "author";
    modeEnum["STUDENT"] = "student";
})(modeEnum || (modeEnum = {}));

var TableEntryCellType;
(function (TableEntryCellType) {
    TableEntryCellType["BLANK"] = "blank";
    TableEntryCellType["HEADER"] = "header";
    TableEntryCellType["TEXT"] = "text";
    TableEntryCellType["DROPDOWN"] = "dropdown";
    TableEntryCellType["TEXT_ENTRY"] = "textEntry";
    TableEntryCellType["EQUATION_ENTRY"] = "equationEntry";
})(TableEntryCellType || (TableEntryCellType = {}));

const prefix$6 = '[Air Number Line]';
const SETUP_EMPTY_ITEM$4 = `${prefix$6} Setup Empty Item`;
class SetupEmptyItemAction$4 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$4;
    }
}
const RESET_STORE$6 = `${prefix$6} Reset Store`;
class ResetStoreAction$6 {
    constructor() {
        this.type = RESET_STORE$6;
    }
}
const LOAD_AIR_ITEM$6 = `${prefix$6} Load Air Item`;
class LoadAirItemAction$6 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$6;
    }
}
const UPDATE_MIN_VALUE = `${prefix$6} Update Min Value`;
class UpdateMinValueAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_MIN_VALUE;
    }
}
const UPDATE_MAX_VALUE = `${prefix$6} Update Max Value`;
class UpdateMaxValueAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_MAX_VALUE;
    }
}
const UPDATE_TICK_INTERVAL = `${prefix$6} Update Tick Interval`;
class UpdateTickIntervalAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TICK_INTERVAL;
    }
}
const UPDATE_LABEL_INTERVAL = `${prefix$6} Update Label interval`;
class UpdateLabelIntervalAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_LABEL_INTERVAL;
    }
}
const UPDATE_CORRECT_ANSWER = `${prefix$6} Update Correct Answer`;
class UpdateCorrectAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CORRECT_ANSWER;
    }
}
const UPDATE_ANSWER_FORMAT = `${prefix$6} Update Answer Format`;
class UpdateAnswerFormatAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER_FORMAT;
    }
}
const CLEAR_CORRECT_ANSWER = `${prefix$6} Clear Correct Answer`;
class ClearCorrectAnswerAction {
    constructor() {
        this.type = CLEAR_CORRECT_ANSWER;
    }
}
const ADVANCED_NUMBERLINE = `${prefix$6} Is Advanced number line`;
class AdvancedNumberLineAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADVANCED_NUMBERLINE;
    }
}
const SHOW_GRAPH_AS = `${prefix$6} Show Graph As`;
class ShowGraphAsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SHOW_GRAPH_AS;
    }
}
const SHOW_RESPONSE_MODE_AS = `${prefix$6} Show Response Mode As`;
class ShowResponseModeAsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SHOW_RESPONSE_MODE_AS;
    }
}
const ALLOW_SNAP = `${prefix$6} Allow snap to tick`;
class AllowSnapAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ALLOW_SNAP;
    }
}
const UPDATE_ANSWER_LABEL = `${prefix$6} Update Answer Label`;
class UpdateAnswerLabelAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER_LABEL;
    }
}
const UPDATE_ANSWER_TOLERANCE = `${prefix$6} Update Answer Tolerance`;
class UpdateAnswerToleranceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER_TOLERANCE;
    }
}
const DELETE_TOLERANCE = `${prefix$6} Delete Tolerance`;
class DeleteToleranceAction {
    constructor() {
        this.type = DELETE_TOLERANCE;
    }
}
const UPDATE_INTERNAL_TICK = `${prefix$6} Update Internal Tick Interval`;
class UpdateInternalTickAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_INTERNAL_TICK;
    }
}
const UPDATE_PLOT = `${prefix$6} Update Plot`;
class UpdatePlotAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_PLOT;
    }
}
const UPDATE_ANSWER_SEGMENT = `${prefix$6} Update AnswerSegment`;
class UpdateAnswerSegmentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWER_SEGMENT;
    }
}

var airNumberLine_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$4,
    SetupEmptyItemAction: SetupEmptyItemAction$4,
    RESET_STORE: RESET_STORE$6,
    ResetStoreAction: ResetStoreAction$6,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$6,
    LoadAirItemAction: LoadAirItemAction$6,
    UPDATE_MIN_VALUE: UPDATE_MIN_VALUE,
    UpdateMinValueAction: UpdateMinValueAction,
    UPDATE_MAX_VALUE: UPDATE_MAX_VALUE,
    UpdateMaxValueAction: UpdateMaxValueAction,
    UPDATE_TICK_INTERVAL: UPDATE_TICK_INTERVAL,
    UpdateTickIntervalAction: UpdateTickIntervalAction,
    UPDATE_LABEL_INTERVAL: UPDATE_LABEL_INTERVAL,
    UpdateLabelIntervalAction: UpdateLabelIntervalAction,
    UPDATE_CORRECT_ANSWER: UPDATE_CORRECT_ANSWER,
    UpdateCorrectAnswerAction: UpdateCorrectAnswerAction,
    UPDATE_ANSWER_FORMAT: UPDATE_ANSWER_FORMAT,
    UpdateAnswerFormatAction: UpdateAnswerFormatAction,
    CLEAR_CORRECT_ANSWER: CLEAR_CORRECT_ANSWER,
    ClearCorrectAnswerAction: ClearCorrectAnswerAction,
    ADVANCED_NUMBERLINE: ADVANCED_NUMBERLINE,
    AdvancedNumberLineAction: AdvancedNumberLineAction,
    SHOW_GRAPH_AS: SHOW_GRAPH_AS,
    ShowGraphAsAction: ShowGraphAsAction,
    SHOW_RESPONSE_MODE_AS: SHOW_RESPONSE_MODE_AS,
    ShowResponseModeAsAction: ShowResponseModeAsAction,
    ALLOW_SNAP: ALLOW_SNAP,
    AllowSnapAction: AllowSnapAction,
    UPDATE_ANSWER_LABEL: UPDATE_ANSWER_LABEL,
    UpdateAnswerLabelAction: UpdateAnswerLabelAction,
    UPDATE_ANSWER_TOLERANCE: UPDATE_ANSWER_TOLERANCE,
    UpdateAnswerToleranceAction: UpdateAnswerToleranceAction,
    DELETE_TOLERANCE: DELETE_TOLERANCE,
    DeleteToleranceAction: DeleteToleranceAction,
    UPDATE_INTERNAL_TICK: UPDATE_INTERNAL_TICK,
    UpdateInternalTickAction: UpdateInternalTickAction,
    UPDATE_PLOT: UPDATE_PLOT,
    UpdatePlotAction: UpdatePlotAction,
    UPDATE_ANSWER_SEGMENT: UPDATE_ANSWER_SEGMENT,
    UpdateAnswerSegmentAction: UpdateAnswerSegmentAction
});

const AUTHORING_NAMESPACE = '03899b50-147c-40c4-bde9-e91e9abf524a';
function generateUuid(state) {
    return v5(JSON.stringify({
        ...state,
    }), AUTHORING_NAMESPACE).replace(/-/g, '');
}

const initialState$8 = {
    prompt: '',
    min: -5,
    max: 5,
    tickInterval: 1,
    labelInterval: 1,
    answers: [],
    oldAnswers: [],
    limitStudentEntries: false,
    isAdvancedNumberLine: false,
    allowSnapToTick: true,
    divisionsBetweenTicks: 0,
    showGraphAs: graphType.DECIMALS,
    showResponseModeAs: responseType.POINT,
};
function airNumberLine(state = initialState$8, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.NUMBER_LINE) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case UPDATE_MIN_VALUE: {
            if (state.min === action.payload) {
                return state;
            }
            return {
                ...state,
                min: action.payload,
                answers: [],
            };
        }
        case UPDATE_MAX_VALUE: {
            if (state.max === action.payload) {
                return state;
            }
            return {
                ...state,
                max: action.payload,
                answers: [],
            };
        }
        case UPDATE_TICK_INTERVAL: {
            if (state.tickInterval === action.payload) {
                return state;
            }
            return {
                ...state,
                tickInterval: action.payload,
                answers: [],
            };
        }
        case UPDATE_LABEL_INTERVAL: {
            if (state.labelInterval === action.payload) {
                return state;
            }
            return {
                ...state,
                labelInterval: action.payload,
                answers: [],
            };
        }
        case SHOW_GRAPH_AS: {
            return {
                ...state,
                showGraphAs: action.payload,
            };
        }
        case SHOW_RESPONSE_MODE_AS: {
            return {
                ...state,
                showResponseModeAs: action.payload,
            };
        }
        case ALLOW_SNAP: {
            return {
                ...state,
                allowSnapToTick: action.payload,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_CORRECT_ANSWER: {
            const answers = [...state.answers];
            const payload = action.payload.toString();
            const answerObject = answers.find((answer) => answer.value === payload);
            const answerIndex = answers.indexOf(answerObject);
            if (answers.length === 10 && answerIndex === -1) {
                return { ...state };
            }
            else if (answerIndex > -1) {
                answers.splice(answerIndex, 1);
                const matchedConnection = answers.find((answer) => answer['type'] === responseType.CONNECTION &&
                    (answer['from'] === answerObject['value'] || answer['to'] === answerObject['value']));
                if (matchedConnection) {
                    answers.splice(answers.indexOf(matchedConnection), 1);
                }
                return {
                    ...state,
                    answers,
                };
            }
            const updatedAnswer = {
                type: 'point',
                pointType: nlPointType.CLOSED,
                value: payload,
                labels: [''],
                uuid: generateUuid(payload),
            };
            return {
                ...state,
                answers: [...answers, updatedAnswer],
            };
        }
        case UPDATE_ANSWER_FORMAT: {
            const answers = [...state.answers];
            const payload = action.payload.value?.toString();
            const updatedAnswers = answers.map((answer) => {
                if (answer.uuid === action.payload.uuid && action.payload.type === responseType.POINT) {
                    return {
                        ...answer,
                        value: payload,
                    };
                }
                else {
                    return answer;
                }
            });
            const modifiedAnswers = updatedAnswers.map((answer) => {
                if (action.payload.type === responseType.CONNECTION && action.payload.uuid === answer.uuid) {
                    return {
                        ...answer,
                        from: fetchPoint(action.payload.from, updatedAnswers),
                        to: fetchPoint(action.payload.to, updatedAnswers),
                    };
                }
                else {
                    return answer;
                }
            });
            return {
                ...state,
                answers: [...modifiedAnswers],
            };
        }
        case UPDATE_ANSWER_SEGMENT: {
            const answers = [...state.answers];
            const sameResponse = answers.find((answer) => {
                return (answer.type === responseType.CONNECTION &&
                    answer.from === action.payload.from &&
                    answer.to === action.payload.to);
            });
            const foundIndex = answers.indexOf(sameResponse);
            if (foundIndex > -1) {
                answers.splice(foundIndex, 1);
                return {
                    ...state,
                    answers,
                };
            }
            const updatedAnswer = {
                type: 'connection',
                from: action.payload.from,
                to: action.payload.to,
                uuid: generateUuid(action.payload),
            };
            return {
                ...state,
                answers: [...answers, updatedAnswer],
            };
        }
        case ADVANCED_NUMBERLINE: {
            return {
                ...state,
                isAdvancedNumberLine: action.payload,
            };
        }
        case CLEAR_CORRECT_ANSWER: {
            const answers = [...state.answers];
            return {
                ...state,
                answers: [],
                oldAnswers: answers,
            };
        }
        case UPDATE_INTERNAL_TICK: {
            return {
                ...state,
                divisionsBetweenTicks: action.payload - 1,
            };
        }
        case UPDATE_ANSWER_LABEL: {
            const answers = state.answers;
            const answer = action.payload.labelledAnswer;
            const labelledAnswers = answers.map((ans) => {
                if (ans.value === answer.value && action.payload.event) {
                    return {
                        ...ans,
                        labels: [action.payload.event],
                    };
                }
                return ans;
            });
            return {
                ...state,
                answers: labelledAnswers,
            };
        }
        case UPDATE_ANSWER_TOLERANCE: {
            const minValue = fraction(state.min);
            const maxValue = fraction(state.max);
            const onePointToValue = fraction(divide(fraction(state.tickInterval), fraction((state.divisionsBetweenTicks ? state.divisionsBetweenTicks : 0) + 1)));
            const stateAnswers = state.answers;
            const answers = stateAnswers.map((answer) => {
                if (answer.type === 'point') {
                    const answerValue = fraction(answer.value);
                    let fromValue = subtract(answerValue, multiply(onePointToValue, action.payload.numberOfPointsBelow));
                    let toValue = add(answerValue, multiply(onePointToValue, action.payload.numberOfPointsAbove));
                    fromValue = smallerEq(fromValue, minValue) ? minValue : fromValue;
                    toValue = largerEq(toValue, maxValue) ? maxValue : toValue;
                    const from = calculateValueFromFraction(fromValue);
                    const to = calculateValueFromFraction(toValue);
                    const toleranceRange = { from, to };
                    return {
                        ...answer,
                        toleranceRange,
                    };
                }
                else {
                    return answer;
                }
            });
            return {
                ...state,
                answers,
                tolerance: action.payload,
            };
        }
        case DELETE_TOLERANCE: {
            const stateAnswers = state.answers;
            const answers = stateAnswers.map(({ toleranceRange, ...answer }) => answer);
            const { tolerance, ...restState } = state;
            return {
                ...restState,
                answers,
            };
        }
        case UPDATE_PLOT: {
            const stateAnswers = state.answers;
            const answer = action.payload.labelledAnswer;
            const selectedAnswer = stateAnswers.map((ans) => {
                if (ans.uuid === answer.uuid) {
                    return {
                        ...ans,
                        pointType: action.payload.pointType,
                    };
                }
                return ans;
            });
            return {
                ...state,
                answers: selectedAnswer,
            };
        }
        case SETUP_EMPTY_ITEM$4: {
            return initialState$8;
        }
        case LOAD_AIR_ITEM$6: {
            const payload = action.payload;
            return {
                ...state,
                prompt: payload.prompt,
                min: payload.min.toString(),
                max: payload.max.toString(),
                tickInterval: payload.tickInterval.toString(),
                labelInterval: payload.labelInterval.toString(),
                answers: calculateAnswers(payload.answers, state),
                oldAnswers: calculateAnswers(payload.answers, state),
                resources: action.payload.resources,
                isAdvancedNumberLine: payload.isAdvancedNumberLine === undefined ? true : payload.isAdvancedNumberLine,
                allowSnapToTick: payload.allowSnapToTick === undefined || typeof payload.min === 'number' ? true : payload.allowSnapToTick,
                divisionsBetweenTicks: payload.divisionsBetweenTicks || 0,
                showGraphAs: payload.showGraphAs || graphType.DECIMALS,
                tolerance: payload.tolerance,
                showResponseModeAs: payload.showResponseModeAs || responseType.POINT,
            };
        }
        case RESET_STORE$6: {
            return initialState$8;
        }
        default: {
            return state;
        }
    }
}
function calculateValueFromFraction(fractionValue) {
    const { s, n, d } = fractionValue;
    const sign = s < 0 ? '-' : '';
    const numerator = n;
    const denominator = d === 1 ? '' : `/${d}`;
    return sign + numerator + denominator;
}
function calculateAnswers(answers, state) {
    return answers.map((answer, index) => {
        if (typeof answer !== 'object') {
            return convertOldToNew(answer, state, index);
        }
        if (answer.type === responseType.CONNECTION) {
            return {
                ...answer,
                from: answer.from ? fetchPoint(answer.from, answers) : '-infinity',
                to: answer.to ? fetchPoint(answer.to, answers) : '+infinity',
            };
        }
        return answer;
    });
}
function fetchPoint(uuid, answers) {
    if (uuid === '-infinity' || uuid === '+infinity') {
        return uuid;
    }
    return answers.find((answer) => answer.uuid === uuid).value;
}
function fetchUUID(elem, state) {
    const answers = state.answers;
    const selectedElem = answers.find((answer) => answer.value === elem);
    return selectedElem ? selectedElem['uuid'] : elem;
}
function convertOldToNew(answer, payload, index) {
    return {
        value: answer.toString(),
        type: 'point',
        pointType: nlPointType.CLOSED,
        labels: [''],
        uuid: generateUuid(payload + '' + index),
    };
}
function getPureAirNumberLine$1(state) {
    const isLegacyNumberLine = !state.isAdvancedNumberLine;
    const answers = state.answers;
    return {
        isAdvancedNumberLine: isLegacyNumberLine ? undefined : true,
        prompt: state.prompt,
        min: isLegacyNumberLine ? Number(state.min) : state.min,
        max: isLegacyNumberLine ? Number(state.max) : state.max,
        tickInterval: isLegacyNumberLine ? Number(state.tickInterval) : state.tickInterval,
        labelInterval: isLegacyNumberLine ? Number(state.labelInterval) : state.labelInterval,
        answers: isLegacyNumberLine
            ? answers.map((answer) => Number(answer.value || answer))
            : answers.map((answer) => {
                if (answer.type === responseType.CONNECTION) {
                    return {
                        ...answer,
                        from: fetchUUID(answer['from'] + '', state),
                        to: fetchUUID(answer['to'] + '', state),
                    };
                }
                return answer;
            }),
        limitStudentEntries: isLegacyNumberLine ? false : true,
        resources: state.resources,
        showGraphAs: isLegacyNumberLine ? undefined : state.showGraphAs,
        allowSnapToTick: isLegacyNumberLine ? undefined : state.allowSnapToTick,
        divisionsBetweenTicks: isLegacyNumberLine ? undefined : state.divisionsBetweenTicks,
        tolerance: isLegacyNumberLine ? undefined : state.tolerance,
        allowLabelInResponses: answers.some((answer) => {
            return answer['labels'] && answer['labels'].length && answer['labels'][0] !== '';
        }),
        allowConnectionInResponses: answers.some((answer) => {
            return answer.type === responseType.CONNECTION || answer.pointType === nlPointType.OPEN;
        }),
    };
}

var airNumberLine_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$8,
    airNumberLine: airNumberLine,
    calculateAnswers: calculateAnswers,
    fetchPoint: fetchPoint,
    fetchUUID: fetchUUID,
    convertOldToNew: convertOldToNew,
    getPureAirNumberLine: getPureAirNumberLine$1,
    'default': airNumberLine
});

const prefix$5 = '[Air Algorithmic]';
const LOAD_AIR_ITEM$5 = `${prefix$5} Load Air Item`;
class LoadAirItemAction$5 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$5;
    }
}
const RESET_STORE$5 = `${prefix$5} Reset Store`;
class ResetStoreAction$5 {
    constructor() {
        this.type = RESET_STORE$5;
    }
}

var airAlgorithmic_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$5,
    LoadAirItemAction: LoadAirItemAction$5,
    RESET_STORE: RESET_STORE$5,
    ResetStoreAction: ResetStoreAction$5
});

const initialState$7 = {
    externalId: '',
    externalSubtype: 'aheAlgo',
    externalType: 'algorithmic',
    externalUrn: '',
    title: '',
};
function airAlgorithmic(state = initialState$7, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.ALGORITHMIC) {
        return state;
    }
    switch (action.type) {
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case LOAD_AIR_ITEM$5: {
            return {
                ...state,
                externalId: action.payload.externalId,
                externalSubtype: action.payload.externalSubtype,
                externalType: action.payload.externalType,
                externalUrn: action.payload.externalUrn,
                title: action.payload.title,
                resources: action.payload.resources,
            };
        }
        case RESET_STORE$5: {
            return initialState$7;
        }
        default: {
            return state;
        }
    }
}
function getPureAirAlgorithmic$1(state) {
    return {
        ...state,
        resources: state.resources,
    };
}

var airAlgorithmic_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$7,
    airAlgorithmic: airAlgorithmic,
    getPureAirAlgorithmic: getPureAirAlgorithmic$1,
    'default': airAlgorithmic
});

const prefix$4 = '[Air Drawing]';
const SETUP_EMPTY_ITEM$3 = `${prefix$4} Setup Empty Item`;
class SetupEmptyItemAction$3 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$3;
    }
}
const UPDATE_TOOL_CONFIG = `${prefix$4} Update Tool Config`;
class UpdateToolConfigAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TOOL_CONFIG;
    }
}
const UPDATE_MATH_CONFIG = `${prefix$4} Update Math Config`;
class UpdateMathConfigAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_MATH_CONFIG;
    }
}
const UPDATE_BACKGROUND_IMAGE = `${prefix$4} Update Background Image`;
class UpdateBackgroundImageAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_BACKGROUND_IMAGE;
    }
}
const DELETE_BACKGROUND_IMAGE = `${prefix$4} Delete Background Image`;
class DeleteBackgroundImageAction {
    constructor() {
        this.type = DELETE_BACKGROUND_IMAGE;
    }
}
const RESET_STORE$4 = `${prefix$4} Reset Store`;
class ResetStoreAction$4 {
    constructor() {
        this.type = RESET_STORE$4;
    }
}
const LOAD_AIR_ITEM$4 = `${prefix$4} Load Air Item`;
class LoadAirItemAction$4 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$4;
    }
}

var airDrawing_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$3,
    SetupEmptyItemAction: SetupEmptyItemAction$3,
    UPDATE_TOOL_CONFIG: UPDATE_TOOL_CONFIG,
    UpdateToolConfigAction: UpdateToolConfigAction,
    UPDATE_MATH_CONFIG: UPDATE_MATH_CONFIG,
    UpdateMathConfigAction: UpdateMathConfigAction,
    UPDATE_BACKGROUND_IMAGE: UPDATE_BACKGROUND_IMAGE,
    UpdateBackgroundImageAction: UpdateBackgroundImageAction,
    DELETE_BACKGROUND_IMAGE: DELETE_BACKGROUND_IMAGE,
    DeleteBackgroundImageAction: DeleteBackgroundImageAction,
    RESET_STORE: RESET_STORE$4,
    ResetStoreAction: ResetStoreAction$4,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$4,
    LoadAirItemAction: LoadAirItemAction$4
});

const initialState$6 = {
    prompt: '',
    toolConfiguration: 'basic',
    mathConfiguration: 'basic',
    backgroundImage: '',
};
function airDrawing(state = initialState$6, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.DRAWING) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case UPDATE_TOOL_CONFIG: {
            return {
                ...state,
                toolConfiguration: action.payload,
            };
        }
        case UPDATE_MATH_CONFIG: {
            const payload = action.payload;
            return {
                ...state,
                mathConfiguration: payload === 'all' ? 'full' : payload,
            };
        }
        case UPDATE_BACKGROUND_IMAGE: {
            return {
                ...state,
                backgroundImage: action.payload.url,
            };
        }
        case DELETE_BACKGROUND_IMAGE: {
            return {
                ...state,
                backgroundImage: '',
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        case LOAD_AIR_ITEM$4: {
            const payload = action.payload;
            return {
                ...state,
                prompt: payload.prompt,
                toolConfiguration: payload.toolConfiguration,
                mathConfiguration: payload.mathConfiguration,
                backgroundImage: payload.backgroundImage,
                resources: payload.resources,
            };
        }
        case RESET_STORE$4: {
            return initialState$6;
        }
        default: {
            return state;
        }
    }
}
function getPureAirDrawing$1(state) {
    return {
        ...state,
        resources: state.resources,
    };
}

var airDrawing_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$6,
    airDrawing: airDrawing,
    getPureAirDrawing: getPureAirDrawing$1,
    'default': airDrawing
});

const prefix$3 = '[Coordinate Graph]';
const SETUP_EMPTY_ITEM$2 = `${prefix$3} Setup Empty Item`;
class SetupEmptyItemAction$2 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$2;
    }
}
const RESET_STORE$3 = `${prefix$3} Reset Store`;
class ResetStoreAction$3 {
    constructor() {
        this.type = RESET_STORE$3;
    }
}
const LOAD_AIR_ITEM$3 = `${prefix$3} Load Air Item`;
class LoadAirItemAction$3 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$3;
    }
}
const SAVE_ITEM = `${prefix$3} Save Item`;
class SaveItemAction {
    constructor(payload) {
        this.payload = payload;
        this.type = SAVE_ITEM;
    }
}

var airCoordinateGraph_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$2,
    SetupEmptyItemAction: SetupEmptyItemAction$2,
    RESET_STORE: RESET_STORE$3,
    ResetStoreAction: ResetStoreAction$3,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$3,
    LoadAirItemAction: LoadAirItemAction$3,
    SAVE_ITEM: SAVE_ITEM,
    SaveItemAction: SaveItemAction
});

const initialState$5 = {
    prompt: '',
    data: {},
    answers: {},
};
function coordinateGrapher(state = initialState$5, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.COORDINATE_GRAPH) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case SETUP_EMPTY_ITEM$2: {
            return initialState$5;
        }
        case LOAD_AIR_ITEM$3: {
            const payload = action.payload;
            return {
                ...payload,
            };
        }
        case SAVE_ITEM: {
            const payload = action.payload;
            return {
                ...state,
                answers: payload.gridDocument,
                data: {
                    ...payload,
                    gridDocument: {
                        ...payload.gridDocument,
                        models: [],
                    },
                    checkDocument: {},
                },
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case RESET_STORE$3: {
            return initialState$5;
        }
        default: {
            return state;
        }
    }
}
function getPureCoordinateGrapher$1(state) {
    let graphState = { ...state };
    if (state.answers && state.answers.models) {
        graphState = {
            ...state,
            answers: {
                ...state.answers,
                models: deduplicateOpenPoints(state.answers?.models),
            },
        };
    }
    return graphState;
}
function deduplicateOpenPoints(models) {
    const uniquePoints = [];
    const deduplicatedData = [];
    for (const model of models) {
        if (model.type === 'openPoint') {
            // Generate a unique key based on x and y coordinates
            const pointKey = `${model.pts[0].x}_${model.pts[0].y}`;
            // Check if the point is unique, if not, skip it
            if (!uniquePoints.includes(pointKey)) {
                deduplicatedData.push(model);
                uniquePoints.push(pointKey);
            }
        }
        else {
            deduplicatedData.push(model);
        }
    }
    return deduplicatedData;
}

var airCoordinateGraph_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$5,
    coordinateGrapher: coordinateGrapher,
    getPureCoordinateGrapher: getPureCoordinateGrapher$1,
    deduplicateOpenPoints: deduplicateOpenPoints,
    'default': coordinateGrapher
});

const prefix$2 = '[Air Table Entry]';
const SETUP_EMPTY_ITEM$1 = `${prefix$2} Setup Empty Item`;
class SetupEmptyItemAction$1 {
    constructor() {
        this.type = SETUP_EMPTY_ITEM$1;
    }
}
const RESET_STORE$2 = `${prefix$2} Reset Store`;
class ResetStoreAction$2 {
    constructor() {
        this.type = RESET_STORE$2;
    }
}
const LOAD_AIR_ITEM$2 = `${prefix$2} Load Air Item`;
class LoadAirItemAction$2 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$2;
    }
}
const UPDATE_TABLE = `${prefix$2} Update Table`;
class UpdateTableAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TABLE;
    }
}
const UPDATE_ROWS = `${prefix$2} Update Rows`;
class UpdateRowsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ROWS;
    }
}
const DELETE_ROWS = `${prefix$2} Delete Rows`;
class DeleteRowsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ROWS;
    }
}
const DELETE_COLUMNS = `${prefix$2} Delete Columns`;
class DeleteColumnsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_COLUMNS;
    }
}
const DELETE_ROWS_AND_COLUMNS = `${prefix$2} Delete Rows and Columns`;
class DeleteRowsAndColumnsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_ROWS_AND_COLUMNS;
    }
}
const UPDATE_COLUMNS = `${prefix$2} Update Columns`;
class UpdateColumnsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_COLUMNS;
    }
}
const UPDATE_ROWS_AND_COLUMNS = `${prefix$2} Update Rows and Columns`;
class UpdateRowsAndColumnsAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ROWS_AND_COLUMNS;
    }
}
const UPDATE_CELL_TYPE = `${prefix$2} Update Cell Type`;
class UpdateCellTypeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CELL_TYPE;
    }
}
const UPDATE_TEXT_ENTRY_ANSWER = `${prefix$2} Update Text Entry Answer`;
class UpdateTextEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TEXT_ENTRY_ANSWER;
    }
}
const UPDATE_EQUATION_ENTRY_ANSWER = `${prefix$2} Update Equation Entry Answer`;
class UpdateEquationEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_EQUATION_ENTRY_ANSWER;
    }
}
const ADD_TEXT_ENTRY_ANSWER = `${prefix$2} Add Text Entry Answer`;
class AddTextEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_TEXT_ENTRY_ANSWER;
    }
}
const ADD_EQUATION_ENTRY_ANSWER = `${prefix$2} Add Equation Entry Answer`;
class AddEquationEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_EQUATION_ENTRY_ANSWER;
    }
}
const ADD_DROP_DOWN_CHOICE = `${prefix$2} Add Drop Down Choice`;
class AddDropDownChoiceAction {
    constructor(payload) {
        this.payload = payload;
        this.type = ADD_DROP_DOWN_CHOICE;
    }
}
const UPDATE_DROP_DOWN_ANSWER = `${prefix$2} Update Drop Down Answer`;
class UpdateDropDownAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_DROP_DOWN_ANSWER;
    }
}
const UPDATE_DROP_DOWN_ANSWER_CONTENT = `${prefix$2} Update Drop Down Answer Content`;
class UpdateDropDownAnswerContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_DROP_DOWN_ANSWER_CONTENT;
    }
}
const UPDATE_TEXT_INPUT_CONTENT = `${prefix$2} Update Text Input Content`;
class UpdateTextInputContentAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_TEXT_INPUT_CONTENT;
    }
}
const UPDATE_CELL = `${prefix$2} Update Cell`;
class UpdateCellAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_CELL;
    }
}
const DELETE_TEXT_ENTRY_ANSWER = `${prefix$2} Delete Text Entry Answer`;
class DeleteTextEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_TEXT_ENTRY_ANSWER;
    }
}
const DELETE_EQUATION_ENTRY_ANSWER = `${prefix$2} Delete Equation Entry Answer`;
class DeleteEquationEntryAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_EQUATION_ENTRY_ANSWER;
    }
}
const DELETE_DROP_DOWN_ANSWER = `${prefix$2} Delete Drop Down Answer`;
class DeleteDropDownAnswerAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_DROP_DOWN_ANSWER;
    }
}

var airTableEntry_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM$1,
    SetupEmptyItemAction: SetupEmptyItemAction$1,
    RESET_STORE: RESET_STORE$2,
    ResetStoreAction: ResetStoreAction$2,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$2,
    LoadAirItemAction: LoadAirItemAction$2,
    UPDATE_TABLE: UPDATE_TABLE,
    UpdateTableAction: UpdateTableAction,
    UPDATE_ROWS: UPDATE_ROWS,
    UpdateRowsAction: UpdateRowsAction,
    DELETE_ROWS: DELETE_ROWS,
    DeleteRowsAction: DeleteRowsAction,
    DELETE_COLUMNS: DELETE_COLUMNS,
    DeleteColumnsAction: DeleteColumnsAction,
    DELETE_ROWS_AND_COLUMNS: DELETE_ROWS_AND_COLUMNS,
    DeleteRowsAndColumnsAction: DeleteRowsAndColumnsAction,
    UPDATE_COLUMNS: UPDATE_COLUMNS,
    UpdateColumnsAction: UpdateColumnsAction,
    UPDATE_ROWS_AND_COLUMNS: UPDATE_ROWS_AND_COLUMNS,
    UpdateRowsAndColumnsAction: UpdateRowsAndColumnsAction,
    UPDATE_CELL_TYPE: UPDATE_CELL_TYPE,
    UpdateCellTypeAction: UpdateCellTypeAction,
    UPDATE_TEXT_ENTRY_ANSWER: UPDATE_TEXT_ENTRY_ANSWER,
    UpdateTextEntryAnswerAction: UpdateTextEntryAnswerAction,
    UPDATE_EQUATION_ENTRY_ANSWER: UPDATE_EQUATION_ENTRY_ANSWER,
    UpdateEquationEntryAnswerAction: UpdateEquationEntryAnswerAction,
    ADD_TEXT_ENTRY_ANSWER: ADD_TEXT_ENTRY_ANSWER,
    AddTextEntryAnswerAction: AddTextEntryAnswerAction,
    ADD_EQUATION_ENTRY_ANSWER: ADD_EQUATION_ENTRY_ANSWER,
    AddEquationEntryAnswerAction: AddEquationEntryAnswerAction,
    ADD_DROP_DOWN_CHOICE: ADD_DROP_DOWN_CHOICE,
    AddDropDownChoiceAction: AddDropDownChoiceAction,
    UPDATE_DROP_DOWN_ANSWER: UPDATE_DROP_DOWN_ANSWER,
    UpdateDropDownAnswerAction: UpdateDropDownAnswerAction,
    UPDATE_DROP_DOWN_ANSWER_CONTENT: UPDATE_DROP_DOWN_ANSWER_CONTENT,
    UpdateDropDownAnswerContentAction: UpdateDropDownAnswerContentAction,
    UPDATE_TEXT_INPUT_CONTENT: UPDATE_TEXT_INPUT_CONTENT,
    UpdateTextInputContentAction: UpdateTextInputContentAction,
    UPDATE_CELL: UPDATE_CELL,
    UpdateCellAction: UpdateCellAction,
    DELETE_TEXT_ENTRY_ANSWER: DELETE_TEXT_ENTRY_ANSWER,
    DeleteTextEntryAnswerAction: DeleteTextEntryAnswerAction,
    DELETE_EQUATION_ENTRY_ANSWER: DELETE_EQUATION_ENTRY_ANSWER,
    DeleteEquationEntryAnswerAction: DeleteEquationEntryAnswerAction,
    DELETE_DROP_DOWN_ANSWER: DELETE_DROP_DOWN_ANSWER,
    DeleteDropDownAnswerAction: DeleteDropDownAnswerAction
});

const initialStateOfText = {
    type: TableEntryCellType.TEXT,
    text: '',
};
const initialStateOfTextEntry = {
    type: TableEntryCellType.TEXT_ENTRY,
    answers: [{ values: '' }],
    allowEquivalency: true, // this is hardcoded until we have UI mocks
};
const initialStateOfEquationEntry = {
    type: TableEntryCellType.EQUATION_ENTRY,
    answers: [{ values: '' }],
    allowEquivalency: true, // this is hardcoded until we have UI mocks
};
const initialStateOfTextEntryAnswer = {
    values: '',
};
const initialStateOfEquationEntryAnswer = {
    values: '',
};
const updateRows = (rows, numberOfRows) => {
    const rowArray = [];
    const colArray = [];
    // get the count of existing columns from any row
    const numberOfColumns = rows[0].cells.length;
    for (let j = 0; j < numberOfColumns; j++) {
        colArray.push({ ...initialStateOfText, key: uuid.v4() });
    }
    for (let i = 0; i < numberOfRows; i++) {
        rowArray.push({ key: uuid.v4(), cells: colArray });
    }
    return [...rows, ...rowArray];
};
const deleteRows = (rows, numberOfRows) => {
    // Business requirement is to delete from the last row
    return rows.slice(0, -Math.abs(numberOfRows));
};
const deleteColumns = (rows, numberOfColumns) => {
    return [...rows].map((item) => {
        return {
            ...item,
            cells: [...item.cells].slice(0, -Math.abs(numberOfColumns)),
        };
    });
};
const deleteRowsAndColumns = (rows, numberOfRows, numberOfColumns) => {
    // start by chopping columns first
    const choppedData = [...rows].map((item) => {
        return {
            ...item,
            cells: [...item.cells].slice(0, -Math.abs(numberOfColumns)),
        };
    });
    // now chop rows
    return choppedData.slice(0, -Math.abs(numberOfRows));
};
const updateColumns = (rows, numberOfColumns) => {
    const colArray = [];
    for (let j = 0; j < numberOfColumns; j++) {
        colArray.push({ ...initialStateOfText, key: uuid.v4() });
    }
    return [...rows].map((item) => {
        return {
            ...item,
            cells: [...item.cells, ...colArray],
        };
    });
};
const updateRowsAndColumns = (rows, numberOfRows, numberOfColumns) => {
    const colArray = [];
    const rowArray = [];
    // Build columns needed to build rows
    for (let j = 0; j < rows[0].cells.length; j++) {
        colArray.push({ ...initialStateOfText, key: uuid.v4() });
    }
    // Let's start by adding rows first with existing columns
    for (let i = 0; i < numberOfRows; i++) {
        rowArray.push({ key: uuid.v4(), cells: colArray });
    }
    const newRowArray = [...rows, ...rowArray];
    // Just add columns to the updated rows now
    const newColArray = [];
    for (let j = 0; j < numberOfColumns; j++) {
        newColArray.push({ ...initialStateOfText, key: uuid.v4() });
    }
    return [...newRowArray].map((item) => {
        return {
            ...item,
            cells: [...item.cells, ...newColArray],
        };
    });
};
const updateCellType = (rows, cell, rowIndex, columnIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (cell.type === TableEntryCellType.TEXT_ENTRY) {
                            return { ...initialStateOfTextEntry, key: uuid.v4() };
                        }
                        else if (cell.type === TableEntryCellType.EQUATION_ENTRY) {
                            return { ...initialStateOfEquationEntry, key: uuid.v4() };
                        }
                        else if (cell.type === TableEntryCellType.DROPDOWN) {
                            return {
                                type: TableEntryCellType.DROPDOWN,
                                choices: [{ key: uuid.v4(), value: '' }],
                                answer: '',
                                key: uuid.v4(),
                            };
                        }
                        else if (cell.type === TableEntryCellType.BLANK) {
                            return {
                                type: TableEntryCellType.BLANK,
                            };
                        }
                        else if (cell.type === TableEntryCellType.HEADER) {
                            return {
                                type: TableEntryCellType.HEADER,
                                text: '',
                            };
                        }
                        else {
                            return initialStateOfText;
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const addTextEntryAnswer = (rows, rowIndex, columnIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.TEXT_ENTRY) {
                            return {
                                ...column,
                                key: uuid.v4(),
                                answers: [...column['answers'], initialStateOfTextEntryAnswer],
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const addEquationEntryAnswer = (rows, rowIndex, columnIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.EQUATION_ENTRY) {
                            return {
                                ...column,
                                key: uuid.v4(),
                                answers: [...column['answers'], initialStateOfEquationEntryAnswer],
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateTextEntryAnswer = (rows, rowIndex, columnIndex, updatedAnswer, answerIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.TEXT_ENTRY) {
                            return {
                                ...column,
                                answers: [...column['answers']].map((answer, index) => {
                                    if (index === answerIndex) {
                                        return {
                                            ...answer,
                                            values: updatedAnswer,
                                        };
                                    }
                                    return {
                                        ...answer,
                                    };
                                }),
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateEquationEntryAnswer = (rows, rowIndex, columnIndex, updatedAnswer, answerIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.EQUATION_ENTRY) {
                            return {
                                ...column,
                                answers: [...column['answers']].map((answer, index) => {
                                    if (index === answerIndex) {
                                        return {
                                            ...answer,
                                            values: updatedAnswer,
                                        };
                                    }
                                    return {
                                        ...answer,
                                    };
                                }),
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateDropDownAnswer = (rows, rowIndex, columnIndex, selectedAnswer) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.DROPDOWN) {
                            return {
                                ...column,
                                answer: selectedAnswer,
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const addDropDownChoice = (rows, rowIndex, columnIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.DROPDOWN) {
                            return {
                                ...column,
                                key: uuid.v4(),
                                choices: [...column['choices'], { key: uuid.v4(), value: '' }],
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateDropDownAnswerContent = (rows, rowIndex, columnIndex, answerIndex, updatedText) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        return {
                            ...column,
                            choices: [...column['choices']].map((val, index) => {
                                if (answerIndex === index) {
                                    return {
                                        ...val,
                                        value: updatedText,
                                    };
                                }
                                return {
                                    ...val,
                                };
                            }),
                        };
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateTextInputContent = (rows, rowIndex, columnIndex, updatedText) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        if (column.type === TableEntryCellType.TEXT ||
                            column.type === TableEntryCellType.HEADER) {
                            return {
                                ...column,
                                text: updatedText,
                            };
                        }
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const updateCell = (rows, updatedCell, rowIndex, columnIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        return {
                            ...column,
                            ...updatedCell,
                        };
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const deleteTextEntryAnswer = (rows, rowIndex, columnIndex, answerIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        return {
                            ...column,
                            answers: [...column['answers']].filter((_, index, arr) => {
                                return arr[index] !== arr[answerIndex];
                            }),
                        };
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const deleteEquationEntryAnswer = (rows, rowIndex, columnIndex, answerIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        return {
                            ...column,
                            answers: [...column['answers']].filter((_, index, arr) => {
                                return arr[index] !== arr[answerIndex];
                            }),
                        };
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};
const deleteDropDownAnswer = (rows, rowIndex, columnIndex, answerIndex) => {
    return [...rows].map((item, index) => {
        if (index === rowIndex) {
            return {
                ...item,
                cells: [...item.cells].map((column, index) => {
                    if (index === columnIndex) {
                        return {
                            ...column,
                            choices: [...column['choices']].filter((_, index, arr) => {
                                return arr[index] !== arr[answerIndex];
                            }),
                        };
                    }
                    return {
                        ...column,
                    };
                }),
            };
        }
        return {
            ...item,
            cells: [...item.cells],
        };
    });
};

const initialState$4 = {
    prompt: 'Fill in the table below',
    rows: [
        {
            key: uuid.v4(),
            cells: [
                {
                    key: uuid.v4(),
                    type: TableEntryCellType.HEADER,
                    text: 'Header',
                },
                {
                    key: uuid.v4(),
                    type: TableEntryCellType.HEADER,
                    text: 'Header',
                },
            ],
        },
        {
            key: uuid.v4(),
            cells: [
                {
                    key: uuid.v4(),
                    type: TableEntryCellType.TEXT,
                    text: 'Placeholder Text',
                },
                {
                    key: uuid.v4(),
                    type: TableEntryCellType.TEXT,
                    text: 'Placeholder Text',
                },
            ],
        },
    ],
};
function airTableEntry(state = initialState$4, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.TABLE_ENTRY) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return {
                ...state,
                prompt: action.payload.content,
            };
        }
        case LOAD_AIR_ITEM$2: {
            return {
                ...state,
                prompt: action.payload.prompt,
                rows: action.payload.rows,
                resources: action.payload.resources,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        case RESET_STORE$2: {
            return initialState$4;
        }
        case UPDATE_ROWS: {
            return {
                ...state,
                rows: updateRows(state.rows, action.payload.numberOfRows),
            };
        }
        case DELETE_ROWS: {
            return {
                ...state,
                rows: deleteRows(state.rows, action.payload.numberOfRows),
            };
        }
        case DELETE_COLUMNS: {
            return {
                ...state,
                rows: deleteColumns(state.rows, action.payload.numberOfColumns),
            };
        }
        case DELETE_ROWS_AND_COLUMNS: {
            return {
                ...state,
                rows: deleteRowsAndColumns(state.rows, action.payload.numberOfRows, action.payload.numberOfColumns),
            };
        }
        case UPDATE_COLUMNS: {
            return {
                ...state,
                rows: updateColumns(state.rows, action.payload.numberOfColumns),
            };
        }
        case UPDATE_ROWS_AND_COLUMNS: {
            return {
                ...state,
                rows: updateRowsAndColumns(state.rows, action.payload.numberOfRows, action.payload.numberOfColumns),
            };
        }
        case UPDATE_CELL_TYPE: {
            return {
                ...state,
                rows: updateCellType(state.rows, action.payload.cell, action.payload.rowIndex, action.payload.columnIndex),
            };
        }
        case ADD_TEXT_ENTRY_ANSWER: {
            return {
                ...state,
                rows: addTextEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex),
            };
        }
        case ADD_EQUATION_ENTRY_ANSWER: {
            return {
                ...state,
                rows: addEquationEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex),
            };
        }
        case UPDATE_TEXT_ENTRY_ANSWER: {
            return {
                ...state,
                rows: updateTextEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answer, action.payload.answerIndex),
            };
        }
        case UPDATE_EQUATION_ENTRY_ANSWER: {
            return {
                ...state,
                rows: updateEquationEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answer, action.payload.answerIndex),
            };
        }
        case UPDATE_DROP_DOWN_ANSWER: {
            return {
                ...state,
                rows: updateDropDownAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answer),
            };
        }
        case ADD_DROP_DOWN_CHOICE: {
            return {
                ...state,
                rows: addDropDownChoice(state.rows, action.payload.rowIndex, action.payload.columnIndex),
            };
        }
        case UPDATE_DROP_DOWN_ANSWER_CONTENT: {
            return {
                ...state,
                rows: updateDropDownAnswerContent(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answerIndex, action.payload.updatedText),
            };
        }
        case UPDATE_TEXT_INPUT_CONTENT: {
            return {
                ...state,
                rows: updateTextInputContent(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.text),
            };
        }
        case UPDATE_CELL: {
            return {
                ...state,
                rows: updateCell(state.rows, action.payload.cell, action.payload.rowIndex, action.payload.columnIndex),
            };
        }
        case DELETE_TEXT_ENTRY_ANSWER: {
            return {
                ...state,
                rows: deleteTextEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answerIndex),
            };
        }
        case DELETE_EQUATION_ENTRY_ANSWER: {
            return {
                ...state,
                rows: deleteEquationEntryAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answerIndex),
            };
        }
        case DELETE_DROP_DOWN_ANSWER: {
            return {
                ...state,
                rows: deleteDropDownAnswer(state.rows, action.payload.rowIndex, action.payload.columnIndex, action.payload.answerIndex),
            };
        }
        default: {
            return state;
        }
    }
}
const getPureAirTableEntry$1 = (state) => {
    return {
        prompt: state.prompt,
        rows: state.rows,
        resources: state.resources,
    };
};

var airTableEntry_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$4,
    airTableEntry: airTableEntry,
    getPureAirTableEntry: getPureAirTableEntry$1,
    'default': airTableEntry
});

const prefix$1 = '[Air Hotspot]';
const SETUP_EMPTY_ITEM = `${prefix$1} Setup Empty Item`;
class SetupEmptyItemAction {
    constructor() {
        this.type = SETUP_EMPTY_ITEM;
    }
}
const UPDATE_IMAGE = `${prefix$1} Update Image`;
class UpdateImageAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_IMAGE;
    }
}
const UPDATE_SHAPES = `${prefix$1} Update Shapes`;
class UpdateShapesAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_SHAPES;
    }
}
const DELETE_SHAPE = `${prefix$1} Delete Polygon`;
class DeleteShapeAction {
    constructor(payload) {
        this.payload = payload;
        this.type = DELETE_SHAPE;
    }
}
const UPDATE_ANSWERS = `${prefix$1} Update ANSWERS`;
class UpdateAnswersAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_ANSWERS;
    }
}
const MAX_SELECTION = `${prefix$1} Update max selection`;
class UpdateMaxSelectionAction {
    constructor(payload) {
        this.payload = payload;
        this.type = MAX_SELECTION;
    }
}
const UPDATE_DISABLE_MAX_SELECTION = `${prefix$1} Update and disable max selection`;
class UpdateAndDisableMaxSelectionAction {
    constructor(payload) {
        this.payload = payload;
        this.type = UPDATE_DISABLE_MAX_SELECTION;
    }
}
const RESET_STORE$1 = `${prefix$1} Reset Store`;
class ResetStoreAction$1 {
    constructor() {
        this.type = RESET_STORE$1;
    }
}
const LOAD_AIR_ITEM$1 = `${prefix$1} Load Air Item`;
class LoadAirItemAction$1 {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM$1;
    }
}

var airHotspot_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SETUP_EMPTY_ITEM: SETUP_EMPTY_ITEM,
    SetupEmptyItemAction: SetupEmptyItemAction,
    UPDATE_IMAGE: UPDATE_IMAGE,
    UpdateImageAction: UpdateImageAction,
    UPDATE_SHAPES: UPDATE_SHAPES,
    UpdateShapesAction: UpdateShapesAction,
    DELETE_SHAPE: DELETE_SHAPE,
    DeleteShapeAction: DeleteShapeAction,
    UPDATE_ANSWERS: UPDATE_ANSWERS,
    UpdateAnswersAction: UpdateAnswersAction,
    MAX_SELECTION: MAX_SELECTION,
    UpdateMaxSelectionAction: UpdateMaxSelectionAction,
    UPDATE_DISABLE_MAX_SELECTION: UPDATE_DISABLE_MAX_SELECTION,
    UpdateAndDisableMaxSelectionAction: UpdateAndDisableMaxSelectionAction,
    RESET_STORE: RESET_STORE$1,
    ResetStoreAction: ResetStoreAction$1,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM$1,
    LoadAirItemAction: LoadAirItemAction$1
});

const initialState$3 = {
    prompt: '',
    backgroundImage: {
        url: '',
        altText: '',
    },
    maxSelections: 0,
    answers: [],
    shapes: [],
    disableMaxSelection: false,
};
function airHotspot(state = initialState$3, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.HOTSPOT) {
        return state;
    }
    switch (action.type) {
        case UPDATE_PROMPT: {
            return { ...state, prompt: action.payload.content };
        }
        case UPDATE_SHAPES: {
            const isExistingShape = state.shapes.find((shape) => shape.uuid === action.payload.uuid);
            if (isExistingShape) {
                const newShapes = [
                    ...state.shapes.map((shape) => {
                        if (shape.uuid === action.payload.uuid) {
                            return {
                                ...shape,
                                shapeData: action.payload.shapeData || shape.shapeData,
                                strokeColor: action.payload.strokeColor || shape.strokeColor,
                                fillColor: action.payload.fillColor || shape.fillColor,
                                altText: action.payload.altText || shape.altText,
                            };
                        }
                        return shape;
                    }),
                ];
                return {
                    ...state,
                    shapes: newShapes,
                };
            }
            else {
                const newShape = {
                    uuid: action.payload.uuid,
                    strokeColor: action.payload.strokeColor || 'blue',
                    fillColor: action.payload.fillColor || 'transparent',
                    shapeData: action.payload.shapeData,
                    shape: action.payload.shape || ShapeTypeEnum.POLYGON,
                    altText: action.payload.altText || '',
                };
                const newShapes = [...state.shapes, newShape];
                return {
                    ...state,
                    shapes: [...state.shapes, newShape],
                    maxSelections: newShapes.length,
                };
            }
        }
        case UPDATE_IMAGE: {
            const newShpes = action.payload.url ? [...state.shapes] : [];
            return {
                ...state,
                backgroundImage: {
                    url: action.payload.url,
                    altText: action.payload.content,
                },
                shapes: newShpes,
                answers: action.payload.url ? state.answers : [],
                maxSelections: newShpes.length,
            };
        }
        case DELETE_SHAPE: {
            const uuid = action.payload.uuid;
            const newShapes = state.shapes.filter((shape) => shape.uuid !== uuid);
            return {
                ...state,
                answers: state.answers?.filter((answer) => answer !== uuid),
                shapes: newShapes,
                maxSelections: newShapes.length,
            };
        }
        case MAX_SELECTION: {
            return {
                ...state,
                maxSelections: state.disableMaxSelection ? state.answers?.length : Number(action.payload),
            };
        }
        case UPDATE_DISABLE_MAX_SELECTION: {
            return {
                ...state,
                maxSelections: state.answers?.length,
                disableMaxSelection: action.payload,
            };
        }
        case UPDATE_ANSWERS: {
            return {
                ...state,
                answers: [...action.payload.answers],
            };
        }
        case LOAD_AIR_ITEM$1: {
            return {
                prompt: action.payload.prompt,
                answers: action.payload.answers || [],
                backgroundImage: action.payload.backgroundImage,
                maxSelections: action.payload.maxSelections || action.payload.shapes.length,
                shapes: action.payload.shapes || [],
                resources: action.payload.resources,
                disableMaxSelection: false,
            };
        }
        case UPDATE_EXPLANATION: {
            return setResource(state, action.payload, 'explanation');
        }
        case UPDATE_HINT: {
            return setResource(state, action.payload, 'hint');
        }
        case UPDATE_EXAMPLE: {
            return setResource(state, action.payload, 'example');
        }
        case UPDATE_EXAMPLE_RESPONSE: {
            return setResource(state, action.payload, 'example_response');
        }
        case RESET_STORE$1: {
            return initialState$3;
        }
        default: {
            return state;
        }
    }
}
function getPureAirHotspot$1(state) {
    const air = {
        prompt: state.prompt,
        answers: state.answers,
        shapes: state.shapes,
        backgroundImage: state.backgroundImage,
        maxSelections: state.maxSelections,
        resources: state.resources,
    };
    return air;
}

var airHotspot_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$3,
    airHotspot: airHotspot,
    getPureAirHotspot: getPureAirHotspot$1,
    'default': airHotspot
});

const prefix = '[Air Multiple Choice Audio]';
const RESET_STORE = `${prefix} Reset Store`;
class ResetStoreAction {
    constructor() {
        this.type = RESET_STORE;
    }
}
const LOAD_AIR_ITEM = `${prefix} Load Air Item`;
class LoadAirItemAction {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_ITEM;
    }
}

var airMultipleChoiceAudio_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    RESET_STORE: RESET_STORE,
    ResetStoreAction: ResetStoreAction,
    LOAD_AIR_ITEM: LOAD_AIR_ITEM,
    LoadAirItemAction: LoadAirItemAction
});

const initialState$2 = {
    prompt: {},
    choices: [
        {
            key: '',
            content: {},
        },
    ],
    answer: '',
};
function airMultipleChoiceAudio(state = initialState$2, action) {
    const itemType = (action.payload || {}).itemType;
    if (itemType && itemType !== ItemType.MULTIPLE_CHOICE_AUDIO) {
        return state;
    }
    switch (action.type) {
        case LOAD_AIR_ITEM: {
            return {
                prompt: action.payload.prompt,
                choices: action.payload.choices || [],
                answer: action.payload.answer || '',
                audioItemType: action.payload.audioItemType || 'default',
            };
        }
        case RESET_STORE: {
            return initialState$2;
        }
        default: {
            return state;
        }
    }
}
function getPureAirMultipleChoiceAudio$1(state) {
    const air = {
        prompt: state.prompt,
        answer: state.answer,
        choices: state.choices,
        audioItemType: state.audioItemType,
    };
    return air;
}

var airMultipleChoiceAudio_reducer = /*#__PURE__*/Object.freeze({
    __proto__: null,
    initialState: initialState$2,
    airMultipleChoiceAudio: airMultipleChoiceAudio,
    getPureAirMultipleChoiceAudio: getPureAirMultipleChoiceAudio$1,
    'default': airMultipleChoiceAudio
});

const reducers = {
    airCloze,
    airEssay,
    airFib,
    airMatching,
    airMultipleChoice,
    airOrdering,
    airSelectText,
    airShortAnswer,
    airTrueFalse,
    airBucketing,
    airChoiceMatrix,
    airEquationEntry,
    airImageLabeling,
    airInstructionItem,
    airGridIn,
    airAudioRecording,
    airNumberLine,
    airAlgorithmic,
    airDrawing,
    coordinateGrapher,
    airFileUpload,
    airTableEntry,
    airHotspot,
    airMultipleChoiceAudio,
};
function getAirAuthoringReducers() {
    return reducers;
}
const airAuthoringReducersToken = new InjectionToken('AIR Authoring Module reducers');
Object.assign(airAuthoringReducersToken, reducers);
const airAuthoringReducersProvider = {
    provide: airAuthoringReducersToken,
    useFactory: getAirAuthoringReducers,
};
/* tslint:enable */

const LoadAirConfig = '[AIR Config] Load AIR Config';
class LoadAIRConfigAction {
    constructor(payload) {
        this.payload = payload;
        this.type = LoadAirConfig;
    }
}

var airConfig_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    LoadAirConfig: LoadAirConfig,
    LoadAIRConfigAction: LoadAIRConfigAction
});

const initialState$1 = {
    mmsUrl: null,
    allowInlineValidation: false,
    apiConfig: null,
};
function AIRConfigReducer(state = initialState$1, action) {
    switch (action.type) {
        case LoadAirConfig:
            return { ...action.payload };
        default:
            return state;
    }
}
function getAirConfigReducers() {
    return AIRConfigReducer;
}
const airConfigReducersToken = new InjectionToken('AIR Config Module reducers');
Object.assign(airConfigReducersToken, AIRConfigReducer);
const airConfigReducersProvider = {
    provide: airConfigReducersToken,
    useFactory: getAirConfigReducers,
};

const getAirState = createFeatureSelector('airAuthoring');
const getAuthoringItem = createSelector(getAirState, (state) => state);
const getConfigState = createFeatureSelector('airConfig');
const getAIRConfig = createSelector(getConfigState, (state) => state);

var selectors = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getAirState: getAirState,
    getAuthoringItem: getAuthoringItem,
    getConfigState: getConfigState,
    getAIRConfig: getAIRConfig
});

class ConfigService {
    constructor(store) {
        this.store = store;
        this.validatorMap = [];
    }
    getConfig() {
        let airConfig;
        this.store
            .select(getConfigState)
            .pipe(take(1))
            .subscribe((config) => {
            airConfig = config;
        });
        return airConfig;
    }
}
ConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ConfigService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class FileUploadService {
    constructor(configService, http) {
        this.configService = configService;
        this.http = http;
        this.mmsUrl = '';
        const config = this.configService.getConfig();
        if (config) {
            this.setBaseUrl(config['mmsUrl']);
        }
    }
    setBaseUrl(url) {
        this.mmsUrl = url;
    }
    uploadFile(body, url) {
        const uploadUrl = `${this.mmsUrl}/api/v1/${url}/en-us`;
        return this.http.request('POST', uploadUrl, { body });
    }
}
FileUploadService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadService, deps: [{ token: ConfigService }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
FileUploadService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: i2.HttpClient }]; } });

const QUESTION_TEXT_LIMIT = 10000;
const ANSWER_CHOICE_LIMIT = 1000;
const ANSWER_CHOICE_LIMIT_CLOZE = 200;
const RATIONALE_LIMIT = 1000;
const RATIONALE_LIMIT_SELECT_TEXT = 250;
const CORRECT_ANSWER_LIMIT_FIB = 50;
const PROMPT_LIMIT_MATCHING = 200;
const CHOICE_LIMIT_MATCHING = 200;
const DISTRACTOR_LIMIT_MATCHING = 200;
const CHOICE_LIMIT_ORDERING = 200;
const DISTRACTOR_LIMIT_ORDERING = 200;
const HINT_LIMIT = 2000;
const EXPLAINATION_LIMIT = 2000;
const RESPONSE_TEXT_LIMIT = 10000;
const ENTITY_NAME_LIMIT = 60;
const ENTITY_NAME_ALERT_LIMIT = 10;
const SELECT_TEXT_SELECTION_TEXT_LIMIT = 4000;
const BUCKET_OR_CHOICE_TEXT_LIMIT = 200;
const PASSAGE_NAME_LIMIT = 100;
const LABEL_CONTENT_LIMIT = 90;
const EXAMPLE_LIMIT = 10000;
const SELECT_TEXT_CORRECT_ANSWER_LIMIT = 2000;

class AddUpdateBucketAndChoiceComponent {
    constructor() {
        this.closeBucketOrChoiceModal = new EventEmitter();
        this.cancelAction = new EventEmitter();
        this.confirmAction = new EventEmitter();
        this.bucketOrChoiceTextLimit = BUCKET_OR_CHOICE_TEXT_LIMIT;
    }
    ngOnChanges(changes) {
        this.content = this.editedBucketOrChoice ? this.editedBucketOrChoice.text : '';
        if (changes.openBucketOrChoiceModal) {
            this.showEditor = this.openBucketOrChoiceModal;
        }
    }
    onCancelAction() {
        this.showEditor = false;
        this.cancelAction.emit();
        this.closeBucketOrChoiceModal.emit();
    }
    onConfirmAction() {
        this.showEditor = false;
        this.confirmAction.emit(this.content);
        this.closeBucketOrChoiceModal.emit();
    }
    onContentChange(value) {
        this.content = value;
    }
}
AddUpdateBucketAndChoiceComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AddUpdateBucketAndChoiceComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AddUpdateBucketAndChoiceComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AddUpdateBucketAndChoiceComponent, selector: "aa-add-update-bucket-and-choice", inputs: { openBucketOrChoiceModal: "openBucketOrChoiceModal", editedBucketOrChoice: "editedBucketOrChoice", isBucketModal: "isBucketModal", modalTitle: "modalTitle", buttonText: "buttonText", focusElementOnClose: "focusElementOnClose" }, outputs: { closeBucketOrChoiceModal: "closeBucketOrChoiceModal", cancelAction: "cancelAction", confirmAction: "confirmAction" }, usesOnChanges: true, ngImport: i0, template: "<air-modal  [isOpen]=\"openBucketOrChoiceModal\"\n           [titleText]=\"modalTitle\"\n           [confirmButtonText]=\"buttonText\"\n           [disableConfirm]=\"!content\"\n           [focusElementOnClose]=\"focusElementOnClose\"\n           (cancel)=\"onCancelAction()\"\n           (close)=\"closeBucketOrChoiceModal.emit()\"\n           (confirm)=\"onConfirmAction()\">\n  <mhe-editor *ngIf=\"showEditor\"\n              [toggleRichTextMenuBar]=\"false\"\n              [content]=\"content\"\n              [maxCharacter]=\"200\"\n              [noMedia]=\"true\"\n              [imageOnly]=\"true\"\n              [showResize]=\"false\"\n              (contentChange)=\"onContentChange($event)\"></mhe-editor>\n  <div *ngIf=\"!isBucketModal\"\n        class=\"modal-description\">\n    <span translate\n          *ngIf=\"editedBucketOrChoice\">Note: Updating a choice updates it in all buckets.</span>\n    <span translate\n          *ngIf=\"!editedBucketOrChoice\">Note: Adding a choice adds it to all buckets.</span>\n  </div>\n</air-modal>\n", styles: [".modal-description{margin-top:1em}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AddUpdateBucketAndChoiceComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-add-update-bucket-and-choice', template: "<air-modal  [isOpen]=\"openBucketOrChoiceModal\"\n           [titleText]=\"modalTitle\"\n           [confirmButtonText]=\"buttonText\"\n           [disableConfirm]=\"!content\"\n           [focusElementOnClose]=\"focusElementOnClose\"\n           (cancel)=\"onCancelAction()\"\n           (close)=\"closeBucketOrChoiceModal.emit()\"\n           (confirm)=\"onConfirmAction()\">\n  <mhe-editor *ngIf=\"showEditor\"\n              [toggleRichTextMenuBar]=\"false\"\n              [content]=\"content\"\n              [maxCharacter]=\"200\"\n              [noMedia]=\"true\"\n              [imageOnly]=\"true\"\n              [showResize]=\"false\"\n              (contentChange)=\"onContentChange($event)\"></mhe-editor>\n  <div *ngIf=\"!isBucketModal\"\n        class=\"modal-description\">\n    <span translate\n          *ngIf=\"editedBucketOrChoice\">Note: Updating a choice updates it in all buckets.</span>\n    <span translate\n          *ngIf=\"!editedBucketOrChoice\">Note: Adding a choice adds it to all buckets.</span>\n  </div>\n</air-modal>\n", styles: [".modal-description{margin-top:1em}\n"] }]
        }], propDecorators: { openBucketOrChoiceModal: [{
                type: Input
            }], editedBucketOrChoice: [{
                type: Input
            }], isBucketModal: [{
                type: Input
            }], modalTitle: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], focusElementOnClose: [{
                type: Input
            }], closeBucketOrChoiceModal: [{
                type: Output
            }], cancelAction: [{
                type: Output
            }], confirmAction: [{
                type: Output
            }] } });

const getAirAuthoringState = createFeatureSelector('airAuthoring');
const getAirMultipleChoice = createSelector(getAirAuthoringState, (state) => state.airMultipleChoice);
const getPureAirMultipleChoice = createSelector(getAirMultipleChoice, getPureAirMultipleChoice$1);
const getAirOrdering = createSelector(getAirAuthoringState, (state) => state.airOrdering);
const getPureAirOrdering = createSelector(getAirOrdering, getPureAirOrdering$1);
const getAirTrueFalse = createSelector(getAirAuthoringState, (state) => state.airTrueFalse);
const getPureAirTrueFalse = createSelector(getAirTrueFalse, getPureAirTrueFalse$1);
const getAirEssay = createSelector(getAirAuthoringState, (state) => state.airEssay);
const getPureAirEssay = createSelector(getAirEssay, getPureAirEssay$1);
const getAirShortAnswer = createSelector(getAirAuthoringState, (state) => state.airShortAnswer);
const getPureAirShortAnswer = createSelector(getAirShortAnswer, getPureAirShortAnswer$1);
const getAirFib = createSelector(getAirAuthoringState, (state) => state.airFib);
const getPureAirFib = createSelector(getAirFib, getPureAirFib$1);
const getAirFileUpload = createSelector(getAirAuthoringState, (state) => state.airFileUpload);
const getPureAirFileUpload = createSelector(getAirFileUpload, getPureAirFileUpload$1);
const getAirMatching = createSelector(getAirAuthoringState, (state) => state.airMatching);
const getPureAirMatching = createSelector(getAirMatching, getPureAirMatching$1);
const getAirCloze = createSelector(getAirAuthoringState, (state) => state.airCloze);
const getPureAirCloze = createSelector(getAirCloze, getPureAirCloze$1);
const getAirSelectText = createSelector(getAirAuthoringState, (state) => state.airSelectText);
const getPureAirSelectText = createSelector(getAirSelectText, getPureAirSelectText$1);
const getAirBucketing = createSelector(getAirAuthoringState, (state) => state.airBucketing);
const getPureAirBucketing = createSelector(getAirBucketing, getPureAirBucketing$1);
const getAirChoiceMatrix = createSelector(getAirAuthoringState, (state) => state.airChoiceMatrix);
const getPureAirChoiceMatrix = createSelector(getAirChoiceMatrix, getPureAirChoiceMatrix$1);
const getAirEquationEntry = createSelector(getAirAuthoringState, (state) => state.airEquationEntry);
const getPureAirEquationEntry = createSelector(getAirEquationEntry, getPureAirEquationEntry$1);
const getAirImageLabeling = createSelector(getAirAuthoringState, (state) => state.airImageLabeling);
const getPureAirImageLabeling = createSelector(getAirImageLabeling, getPureAirImageLabeling$1);
const getAirInstructionItem = createSelector(getAirAuthoringState, (state) => state.airInstructionItem);
const getPureAirInstructionItem = createSelector(getAirInstructionItem, getPureAirInstructionItem$1);
const getAirGridIn = createSelector(getAirAuthoringState, (state) => state.airGridIn);
const getPureAirGridIn = createSelector(getAirGridIn, getPureAirGridIn$1);
const getAirAudioRecording = createSelector(getAirAuthoringState, (state) => state.airAudioRecording);
const getPureAirAudioRecording = createSelector(getAirAudioRecording, getPureAirAudioRecording$1);
const getAirNumberLine = createSelector(getAirAuthoringState, (state) => state.airNumberLine);
const getPureAirNumberLine = createSelector(getAirNumberLine, getPureAirNumberLine$1);
const getAirAlgorithmic = createSelector(getAirAuthoringState, (state) => state.airAlgorithmic);
const getPureAirAlgorithmic = createSelector(getAirAlgorithmic, getPureAirAlgorithmic$1);
const getAirDrawing = createSelector(getAirAuthoringState, (state) => state.airDrawing);
const getPureAirDrawing = createSelector(getAirDrawing, getPureAirDrawing$1);
const getCoordinateGrapher = createSelector(getAirAuthoringState, (state) => state.coordinateGrapher);
const getPureCoordinateGrapher = createSelector(getCoordinateGrapher, getPureCoordinateGrapher$1);
const getAirTableEntry = createSelector(getAirAuthoringState, (state) => state.airTableEntry);
const getPureAirTableEntry = createSelector(getAirTableEntry, getPureAirTableEntry$1);
const getAirHotspot = createSelector(getAirAuthoringState, (state) => state.airHotspot);
const getPureAirHotspot = createSelector(getAirHotspot, getPureAirHotspot$1);
const getAirMultipleChoiceAudio = createSelector(getAirAuthoringState, (state) => state.airMultipleChoiceAudio);
const getPureAirMultipleChoiceAudio = createSelector(getAirMultipleChoiceAudio, getPureAirMultipleChoiceAudio$1);
/* tslint:enable */

var fromAirState = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getAirAuthoringState: getAirAuthoringState,
    getAirMultipleChoice: getAirMultipleChoice,
    getPureAirMultipleChoice: getPureAirMultipleChoice,
    getAirOrdering: getAirOrdering,
    getPureAirOrdering: getPureAirOrdering,
    getAirTrueFalse: getAirTrueFalse,
    getPureAirTrueFalse: getPureAirTrueFalse,
    getAirEssay: getAirEssay,
    getPureAirEssay: getPureAirEssay,
    getAirShortAnswer: getAirShortAnswer,
    getPureAirShortAnswer: getPureAirShortAnswer,
    getAirFib: getAirFib,
    getPureAirFib: getPureAirFib,
    getAirFileUpload: getAirFileUpload,
    getPureAirFileUpload: getPureAirFileUpload,
    getAirMatching: getAirMatching,
    getPureAirMatching: getPureAirMatching,
    getAirCloze: getAirCloze,
    getPureAirCloze: getPureAirCloze,
    getAirSelectText: getAirSelectText,
    getPureAirSelectText: getPureAirSelectText,
    getAirBucketing: getAirBucketing,
    getPureAirBucketing: getPureAirBucketing,
    getAirChoiceMatrix: getAirChoiceMatrix,
    getPureAirChoiceMatrix: getPureAirChoiceMatrix,
    getAirEquationEntry: getAirEquationEntry,
    getPureAirEquationEntry: getPureAirEquationEntry,
    getAirImageLabeling: getAirImageLabeling,
    getPureAirImageLabeling: getPureAirImageLabeling,
    getAirInstructionItem: getAirInstructionItem,
    getPureAirInstructionItem: getPureAirInstructionItem,
    getAirGridIn: getAirGridIn,
    getPureAirGridIn: getPureAirGridIn,
    getAirAudioRecording: getAirAudioRecording,
    getPureAirAudioRecording: getPureAirAudioRecording,
    getAirNumberLine: getAirNumberLine,
    getPureAirNumberLine: getPureAirNumberLine,
    getAirAlgorithmic: getAirAlgorithmic,
    getPureAirAlgorithmic: getPureAirAlgorithmic,
    getAirDrawing: getAirDrawing,
    getPureAirDrawing: getPureAirDrawing,
    getCoordinateGrapher: getCoordinateGrapher,
    getPureCoordinateGrapher: getPureCoordinateGrapher,
    getAirTableEntry: getAirTableEntry,
    getPureAirTableEntry: getPureAirTableEntry,
    getAirHotspot: getAirHotspot,
    getPureAirHotspot: getPureAirHotspot,
    getAirMultipleChoiceAudio: getAirMultipleChoiceAudio,
    getPureAirMultipleChoiceAudio: getPureAirMultipleChoiceAudio
});

// tslint:disable: directive-class-suffix
class AirBaseComponent {
    constructor(_store, airSelector$, loadAirItemActionClass, resetStoreActionClass) {
        this._store = _store;
        this.loadAirItemActionClass = loadAirItemActionClass;
        this.resetStoreActionClass = resetStoreActionClass;
        this.updateAir = new EventEmitter();
        this.airSubscription = this._store.select(airSelector$).subscribe((air) => {
            this.updateAir.emit(air);
        });
    }
    ngOnInit() {
        if (this.initialAir) {
            this._store.dispatch(new this.loadAirItemActionClass(this.initialAir));
        }
    }
    ngOnDestroy() {
        this.airSubscription.unsubscribe();
        this._store.dispatch(new this.resetStoreActionClass());
    }
}
AirBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirBaseComponent, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive });
AirBaseComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: AirBaseComponent, inputs: { initialAir: "initialAir" }, outputs: { updateAir: "updateAir" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirBaseComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: undefined }, { type: undefined }, { type: undefined }]; }, propDecorators: { initialAir: [{
                type: Input
            }], updateAir: [{
                type: Output
            }] } });

const E_TOO_FEW_CHOICES = 'Include at least two non-empty answer choices, and then save your work.';
const E_TOO_FEW_CHOICES_ANSWERED = 'Include at least three non-empty ordering choices, and then save your work.';
const E_NO_ANSWER = 'Select a correct answer, and then save your work.';
const E_NO_ANSWER_SELECTED = 'Select at least one correct answer, and then save your work.';
const E_TOO_MANY_ANSWERS = 'A maximum of 20 blanks are supported.';
const E_TOO_FEW_ANSWERS = 'Select a correct answer for each blank.';
const E_EMPTY_CHOICES = ' Include at least two non-empty answer choices for each blank.';
const E_NO_CORRECT_ANSWER = 'Include at least one correct answer for each blank, and then save your work.';
const E_INVALID_NUMERIC_ANSWER = 'Enter correct answers using valid numeric formats, and then save your work';
const E_EMPTY_PROMPT = 'Enter content in the Question text field, and then save your work.';
const E_EMPTY_INSTRUCTIONS_PROMPT = 'Enter content in the Instructions field, and then save your work.';
const E_INVALID_POINTS = 'Enter a positive value with up to two decimal places in the Points field,\
  and then save your work.';
const E_NO_CHOICE_FOR_PROMPT = 'Include at least one non-empty matching choice for every prompt,\
  and then save your work.';
const E_NO_MATCHES = 'Include one non-empty prompt for every matching choice, and then save your work.';
const E_ONE_ANSWER_CHOICE = 'If including only one prompt in this item, include at least one non-empty \
  matching choice and non-empty distractor. Then save your work.';
const E_NO_RESPONSE_LIMIT = 'Enter a positive integer value up to 10,000 in the Character/Words limit field,\
  and then save your work.';
const E_TOO_FEW_CLOZE_ANSWERS = 'Select a correct answer for each blank.';
const E_TOO_FEW_BLANKS = 'Include at least one blank in the Question text field by inserting two \
  adjacent underscores, like "__".';
const E_PASTE_ERROR = 'The pasted content exceeds the maximum character limit allowed.';
const E_TOLERANCE_ERROR = 'Enter a positive value with up to ten decimal places in the Tolerance \
  input field, and then save your work';
const E_MAX_RESPONSE_FIELD = 'A maximum of twenty response fields are supported.';
const E_SCORING_ERROR = 'Complete all of the required fields before using the Advanced Scoring feature.';
const E_FULL_SCORE_ERROR = "The question's full value must be greater than or equal to each scoring \
  condition's value. Update to proceed.";
const E_SCORING_CONDITION_ERROR = 'At least one advanced scoring condition is incomplete. \
  Update the scoring conditions to proceed.';
const E_SCORING_CONDITION_DUPLICATE = 'Scoring conditions cannot be identical.';
const E_SCORING_CONDITION_RANGE_ERROR = 'The value for the selections dropdown menu must be \
  greater than or equal to the correct answers dropdown menu.';
const E_SCORING_CONDITION_SELECTION_ERROR = "A scoring condition created is the question's \
  correct answer. Update the scoring condition to proceed.";
const E_NO_BUCKET_ERROR = 'Include at least one non-empty bucket, and then save your work.';
const E_NO_CHOICE_ERROR = 'Include at least one non-empty choice, and then save your work.';
const E_CHOICE_MAPPING_ERROR = 'Map at least one choice to one bucket, and then save your work.';
const E_EMPTY_SELECTION_TEXT = 'Enter content in the Selection area text field, and then save your work.';
const E_TOO_FEW_SELECTION_CHOICES = 'Select at least two choices in the Selection choices field, and then \
  save your work.';
const E_NO_SELECT_TEXT_ANSWER = 'Select at least one correct answer in the Correct answers field, and then \
  save your work.';
const E_TOO_MANY_SELECTION_ANSWERS = 'The choices cannot all be correct answers. Update the choices or \
  correct answers selected, and then save your work.';
const E_NO_CORRECT_ANSWER_ENTERED = 'Include at least one correct answer, and then save your work.';
const E_INSUFFICIENT_CHOICE_ERROR = 'Add at least two labels, one label and one distractor label, or one \
  label and one distractor drop zone, and then save your work';
const E_BACKGROUND_IMAGE_ERROR = 'Upload a background image, and then save your work.';
const E_MAX_LABEL_ERROR = 'There can be maximim 20 labels, distractor labels, and distractor drop zones';
const E_MATRIX_LABEL_ERROR = 'Enter text in each row and column header, and then save your work.';
const E_CELL_EMPTY = 'Select one correct answer per row, and then save your work.';
const E_NO_ANSWERS_SELECTED = 'Select at least one correct answer in the matrix, and then save your work.';
const E_DUPLICATE_SINGLE_ITEM_SELECTED = 'A question cannot be added more than once.';
const E_DUPLICATE_LINKED_ITEM_SELECTED = 'A linked pair cannot be added more than once.';
const E_INVALID_AUDIO_SPAN = 'Provide a valid Maximum length, and then save your work.';
// New errors used in AIR item validations, in alpha order
const E_AUDIO_VALID_LENGTH = 'Enter a valid maximum length.';
const E_CLOZE_MIN_CHOICES = 'Enter at least two answer choices for each blank.';
const E_DECIMAL_NOT_ALLOWED = 'Decimal values are not supported.';
const E_EE_CORRECT_ANSWER = 'Enter at least one correct answer.';
const E_ESSAY_SA_LIMIT = 'Enter a positive integer up to 10,000.';
const E_IMAGE_CHOICE_ERROR = 'Add labels and/or distractor drop zones.';
const E_IMAGE_ERROR = 'Upload a background image.';
const E_INTERVAL_BLANK = 'Tick interval and label interval cannot be blank.';
const E_INVALID_ANSWER_CHOICES = 'Enter at least three answer choices.';
const E_INVALID_CHOICES = 'Enter at least two answer choices.';
const E_INVALID_PROMPT = 'Enter question text.';
const E_MISSING_AUDIO = 'Please add audio to the question';
const E_INVALID_SELECTION_TEXT = 'Enter selection text.';
const E_LABEL_INT_BLANK = 'Enter a label interval.';
const E_LABEL_INT_VALUE = 'The label interval must be greater than 0.';
const E_MATCHING_LESS_CHOICES = 'Enter at least two matching choices and/or distractors.';
const E_MATCHING_UNMATCHED_CHOICE = 'Enter at least one choice for every prompt.';
const E_MATCHING_UNMATCHED_PROMPT = 'Enter a prompt for every matching choice.';
const E_MAX_VALUE_BLANK = 'Enter a maximum value.';
const E_MAX_VALUE_INVALID = 'The maximum must be greater than the minimum.';
const E_MAX_COLUMNS = 'Max: 5 columns.';
const E_MAX_ROWS = 'Max: 10 rows.';
const E_MIN_BLANK = 'Enter at least one blank.';
const E_MIN_CORRECT_ANSWER = 'Enter at least one correct answer for each blank.';
const E_CORRECT_ANSWER_CONTAINS_PUNCTUATION = 'One or more correct answers contains punctuation and Ignore Punctuation is selected.';
const E_MIN_MAX_BLANK = 'The min or max cannot be blank.';
const E_MIN_MAX_NUMBER = 'The max must be greater than the min.';
const E_MIN_VALUE_BLANK = 'Enter a minimum value.';
const E_MIN_VALUE_INVALID = 'The minimum must be less than the maximum.';
const E_NO_ANSWER_CHOICE = 'Add at least one answer choice.';
const E_NO_IDENTICAL_CHOICES = 'Multiple answer choices cannot be identical.';
const E_NO_BUCKET = 'Add at least one bucket.';
const E_NO_CHOICE_FOR_BUCKET = 'Map at least one choice to a bucket.';
const E_NO_CORRECT_ANSWER_MULTIPLE_RESPONSE = 'Select at least one correct answer.';
const E_NO_CORRECT_ANSWER_CHOICE = 'For each answer choice, enter the correct answer — or select the checkbox if no correction is needed.';
const E_NO_CORRECT_ANSWER_PER_ROW = 'Select one correct answer per row.';
const E_NO_CORRECT_ANSWER_SELECTED = 'Select a correct answer.';
const E_BLANK_CORRECT_ANSWER = 'For each choice, enter at least one correct answer, or mark the answer choice as correct.';
const E_NO_POINT_PLOTTED = 'Plot at least one point.';
const E_TICK_INT_BLANK = 'Enter a tick interval.';
const E_TICK_INT_VALUE = 'The tick interval must be greater than 0.';
const E_NO_ROW_COLUMN_HEADER = 'Enter text in each row and column header.';
const E_TICK_RANGE_MAX = 'A maximum of 40 ticks are allowed. Please adjust the line settings accordingly.';
const E_TICK_RANGE_MIN = 'The tick and label interval must be greater than 0.';
const E_TOLERANCE_RANGE_ERROR = 'Enter a positive value with up to ten decimal places';
const E_TOO_FEW_CHOICES_SELECTED = 'Select at least two choices.';
const E_TOO_LARGE_AUDIO_SPAN = 'Value exceeds the maximum accepted length.';
const E_TOO_LOW_AUDIO_SPAN = 'Value does not meet minimum required length.';
const W_DROPDOWN_CHANGE = 'This change will clear your Selection Choices and Correct Answers. OK to clear?';
const E_SAME_ANSWER_AS_CHOICE = 'Mark the answer choice as correct or provide a different correct answer.';
const E_ALL_CHOICE_AS_CORRECT = 'Please provide at least one correct answer other than the original choice.';
const E_CHOICE_ANSWER_SAME = 'The number of choices and answers cannot be equal.';
const E_NL_TOLERANCE_ERROR = 'The tolerance range is not correct';
const E_CG_CORRECT_ANSWER = 'Plot at least one point.';
const E_TE_EMPTY_TABLE = 'Please fill in table.';
const EMPTY_FIELD_MESSAGE = 'Field should not be left empty.';
const E_MORE_THAN_ONE_SHAPE = 'Please draw more than one shape on your image.';
const E_SHAPE_INTERSECT = 'Two shapes are intersecting.';
const E_SHAPE_OVERLAP = 'Two shapes are overlapping.';
const E_MAX_SELECTION = 'The total number of selections cannot be more than the number of shapes';
const E_NO_BACKGROUND_IMAGE = 'Please add background image';

function hasDuplicate(choices, key) {
    const uniqueChoiceSet = new Set();
    for (const choice of choices) {
        const content = choice[key].trim();
        if (uniqueChoiceSet.has(content)) {
            return true;
        }
        uniqueChoiceSet.add(content);
    }
    return false;
}

class PromptValidationService {
    constructor() {
        this.promptError = '';
        this.minBlankCountError = '';
        this.maxBlankCountError = '';
    }
    validatePrompt(prompt) {
        this.promptError = '';
        if (typeof prompt === 'string') {
            if (!prompt) {
                this.promptError = E_INVALID_PROMPT;
            }
        }
        else if (typeof prompt === 'object') {
            if (!prompt.audio.mediaUrl) {
                this.promptError = E_MISSING_AUDIO;
            }
        }
        return this.promptError;
    }
    validateMinBlankCount(prompt) {
        this.minBlankCountError = '';
        prompt = prompt.replace(/__/g, '${response:');
        if (prompt.indexOf('${response:') < 0) {
            this.minBlankCountError = E_MIN_BLANK;
        }
        return this.minBlankCountError;
    }
    validateMaxBlankCount(tooManyAnswers) {
        this.maxBlankCountError = tooManyAnswers ? this.showMaxError() : '';
        return this.maxBlankCountError;
    }
    showMaxError() {
        this.resetErrors();
        return E_TOO_MANY_ANSWERS;
    }
    resetErrors() {
        this.promptError = '';
        this.minBlankCountError = '';
        this.maxBlankCountError = '';
    }
    getPromptError() {
        if (this.maxBlankCountError) {
            return [this.maxBlankCountError];
        }
        const error = this.promptError ? [this.promptError] : [];
        return this.minBlankCountError ? error.concat(this.minBlankCountError) : error;
    }
}
PromptValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromptValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PromptValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromptValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromptValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class BucketingValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirBucketing$ = this.store.select(getPureAirBucketing);
        this.bucketsError = '';
        this.choicesError = '';
        this.answersError = '';
    }
    validate() {
        let isValid;
        this.pureAirBucketing$.pipe(take(1)).subscribe((airBucketing) => {
            const hasPromptError = !!this.promptValidationService.validatePrompt(airBucketing.prompt);
            const hasBucketError = !!this.validateBuckets(airBucketing.prompts);
            const hasChoicesError = !!this.validateChoices(airBucketing.choices);
            const hasAnswersError = !!this.validateAnswers(airBucketing.answers);
            isValid = !(hasPromptError || hasBucketError || hasChoicesError || hasAnswersError);
        });
        return isValid;
    }
    validateBuckets(buckets) {
        const error = buckets.length ? '' : E_NO_BUCKET;
        this.bucketsError = error;
        return error;
    }
    validateChoices(choices) {
        const error = choices.length
            ? hasDuplicate(choices, 'text')
                ? E_NO_IDENTICAL_CHOICES
                : ''
            : E_NO_ANSWER_CHOICE;
        this.choicesError = error;
        return error;
    }
    validateAnswers(answers) {
        let hasAnswer = false;
        hasAnswer = answers.some((answer) => {
            return answer.choices.some((choice) => choice.count > 0);
        });
        const error = hasAnswer ? '' : E_NO_CHOICE_FOR_BUCKET;
        this.answersError = error;
        return error;
    }
    getBucketingErrors() {
        const errors = this.bucketsError ? [this.bucketsError] : [];
        if (this.choicesError) {
            errors.push(this.choicesError);
        }
        if (this.answersError) {
            errors.push(this.answersError);
        }
        return errors;
    }
    resetBucketsError() {
        this.bucketsError = '';
    }
    resetChoicesError() {
        this.choicesError = '';
    }
    resetAnswersError() {
        this.answersError = '';
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetBucketsError();
        this.resetChoicesError();
        this.resetAnswersError();
    }
}
BucketingValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BucketingValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
BucketingValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BucketingValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BucketingValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class InlineErrorComponent {
    constructor() {
        this.errors = [];
    }
}
InlineErrorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
InlineErrorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: InlineErrorComponent, selector: "aa-inline-error", inputs: { errors: "errors" }, ngImport: i0, template: "<div *ngIf=\"errors.length\" class=\"text-danger d-block mb-3\">\n  <span *ngFor=\"let error of errors\">{{ error }} <br /></span>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InlineErrorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-inline-error', template: "<div *ngIf=\"errors.length\" class=\"text-danger d-block mb-3\">\n  <span *ngFor=\"let error of errors\">{{ error }} <br /></span>\n</div>\n" }]
        }], propDecorators: { errors: [{
                type: Input
            }] } });

class BucketingChoicesComponent {
    constructor() {
        this.addBucket = new EventEmitter();
        this.updateBucket = new EventEmitter();
        this.updatePromptContent = new EventEmitter();
        this.deleteBucket = new EventEmitter();
        this.addChoice = new EventEmitter();
        this.deleteChoice = new EventEmitter();
        this.incrementChoice = new EventEmitter();
        this.decrementChoice = new EventEmitter();
        this.updateChoice = new EventEmitter();
        this.openBucketOrChoiceModal = false;
        this.deleteBucketOrChoiceModal = false;
        this.kebabMenuOptions = [
            {
                optionText: 'Edit',
                value: 'edit',
                behavior: OptionBehavior.OpensModal,
                optionAutomationIdPrefix: 'edit',
            },
            {
                optionText: 'Delete',
                value: 'delete',
                behavior: OptionBehavior.OpensModal,
                optionAutomationIdPrefix: 'delete',
            },
        ];
    }
    ngOnChanges() {
        if (this.buckets.length && !this.selectedBucket) {
            this.onBucketSelection(this.buckets[0]);
        }
    }
    getSelectedIndex() {
        return this.buckets.findIndex((bucket) => bucket.key === this.selectedBucket.key);
    }
    isOpeningBucketModal() {
        return this.modalSelector === 'Bucket';
    }
    stopAddingBucket() {
        return this.buckets.length === 10;
    }
    stopAddingChoice() {
        return this.getChoices().length === 10;
    }
    getModalTitle() {
        const addOrEditText = this.editedBucketOrChoice ? 'Edit ' : '';
        const modalTitleText = this.isOpeningBucketModal()
            ? this.modalSelector +
                ' ' +
                (this.buckets.indexOf(this.editedBucketOrChoice) + 1 || this.buckets.length + 1)
            : this.modalSelector;
        return addOrEditText + modalTitleText;
    }
    getButtonText() {
        return this.editedBucketOrChoice ? 'Save' : 'Add ' + this.modalSelector;
    }
    getFocusableElement() {
        return this.isOpeningBucketModal() ? this.addBucketRef : this.addChoiceRef;
    }
    getLhDropdownOptionsForBucket() {
        const options = [];
        options.push(this.kebabMenuOptions[0]);
        if (this.buckets.length > 1) {
            options.push(this.kebabMenuOptions[1]);
        }
        return options;
    }
    getChoices() {
        const selectedBucket = this.answers.find((answer) => answer.prompt === this.selectedBucket.key);
        return selectedBucket ? selectedBucket.choices : [];
    }
    getChoice(key) {
        return this.choices.find((choice) => choice.key === key);
    }
    disableDecrement(count) {
        return count < 1;
    }
    disableIncrement(count) {
        return count > 9;
    }
    onSelectedOption(selectedOptions, editedObj, selector) {
        this.editedBucketOrChoice = editedObj;
        this.modalSelector = selector;
        if (selectedOptions.value === 'edit') {
            this.openBucketOrChoiceModal = true;
        }
        else {
            this.deleteBucketOrChoiceModal = true;
        }
    }
    onClickingAddBucketOrChoiceLink(selector) {
        this.editedBucketOrChoice = null;
        this.modalSelector = selector;
        this.openBucketOrChoiceModal = true;
    }
    onConfirmAction(content) {
        if (this.isOpeningBucketModal()) {
            if (this.editedBucketOrChoice) {
                this.updateBucket.emit({ content, bucket: this.editedBucketOrChoice });
            }
            else {
                this.addBucket.emit(content);
                setTimeout(() => {
                    this.selectedBucket = this.buckets[this.buckets.length - 1];
                });
            }
        }
        else {
            if (this.editedBucketOrChoice) {
                this.updateChoice.emit({ content, choice: this.editedBucketOrChoice });
            }
            else {
                this.addChoice.emit(content);
            }
        }
        this.onCancelBucketOrChoiceModal();
    }
    onDeletingBucketOrChoice() {
        if (this.isOpeningBucketModal()) {
            if (this.editedBucketOrChoice) {
                this.deleteBucket.emit(this.editedBucketOrChoice);
            }
            setTimeout(() => {
                this.selectedBucket = this.buckets[0];
            });
        }
        else {
            if (this.editedBucketOrChoice) {
                this.deleteChoice.emit(this.editedBucketOrChoice);
            }
        }
        this.onCloseBucketOrChoiceModal();
    }
    onCancelBucketOrChoiceModal() {
        this.editedBucketOrChoice = null;
        this.deleteBucketOrChoiceModal = false;
    }
    onCloseBucketOrChoiceModal() {
        this.editedBucketOrChoice = null;
        this.openBucketOrChoiceModal = false;
        this.deleteBucketOrChoiceModal = false;
    }
    onBucketSelection(bucket) {
        this.selectedBucket = bucket;
    }
    onDecrementingChoices(choice) {
        this.decrementChoice.emit({ bucket: this.selectedBucket, choice });
    }
    onIncrementingChoices(choice) {
        this.incrementChoice.emit({ bucket: this.selectedBucket, choice });
    }
}
BucketingChoicesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BucketingChoicesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
BucketingChoicesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: BucketingChoicesComponent, selector: "aa-bucketing-choices", inputs: { buckets: "buckets", choices: "choices", answers: "answers", errors: "errors" }, outputs: { addBucket: "addBucket", updateBucket: "updateBucket", updatePromptContent: "updatePromptContent", deleteBucket: "deleteBucket", addChoice: "addChoice", deleteChoice: "deleteChoice", incrementChoice: "incrementChoice", decrementChoice: "decrementChoice", updateChoice: "updateChoice" }, viewQueries: [{ propertyName: "addBucketRef", first: true, predicate: ["addBucketRef"], descendants: true, static: true }, { propertyName: "addChoiceRef", first: true, predicate: ["addChoiceRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<h2 translate class=\"bucket-header\">Buckets *</h2>\n<button\n  #addBucketRef\n  type=\"button\"\n  class=\"add-bucket\"\n  [disabled]=\"stopAddingBucket()\"\n  [ngClass]=\"{ 'no-bucket': !buckets.length }\"\n  (click)=\"onClickingAddBucketOrChoiceLink('Bucket')\"\n>\n  <span translate>+ Add Bucket</span>\n</button>\n\n<div class=\"bucket-container\" [class.has-error]=\"errors.length\">\n  <div>\n    <aa-add-update-bucket-and-choice\n      [openBucketOrChoiceModal]=\"openBucketOrChoiceModal\"\n      [modalTitle]=\"getModalTitle()\"\n      [buttonText]=\"getButtonText()\"\n      [isBucketModal]=\"isOpeningBucketModal()\"\n      [focusElementOnClose]=\"getFocusableElement()\"\n      [editedBucketOrChoice]=\"editedBucketOrChoice\"\n      (confirmAction)=\"onConfirmAction($event)\"\n      (closeBucketOrChoiceModal)=\"onCloseBucketOrChoiceModal()\"\n      (cancelAction)=\"onCancelBucketOrChoiceModal()\"\n    ></aa-add-update-bucket-and-choice>\n  </div>\n\n  <air-modal\n    [isOpen]=\"deleteBucketOrChoiceModal\"\n    [titleText]=\"'Delete'\"\n    [confirmButtonText]=\"'Yes, delete'\"\n    (cancel)=\"onCloseBucketOrChoiceModal()\"\n    (close)=\"onCloseBucketOrChoiceModal()\"\n    (confirm)=\"onDeletingBucketOrChoice()\"\n  >\n    <p translate *ngIf=\"isOpeningBucketModal()\">\n      If you delete this bucket, all of the choices and their quantities within\n      this bucket will be deleted. Do you want to proceed?\n    </p>\n    <p translate *ngIf=\"!isOpeningBucketModal()\">\n      If you delete this choice, it will be deleted from all buckets. Do you\n      want to proceed?\n    </p>\n  </air-modal>\n\n  <div class=\"buckets-wrapper row row-cols-auto g-3\">\n    <div *ngFor=\"let bucket of buckets; let bucketIndex = index\" class=\"col\">\n      <div class=\"bucket-wrapper\">\n        <div\n          class=\"bucket-box\"\n          tabindex=\"0\"\n          (click)=\"onBucketSelection(bucket)\"\n        >\n          <div class=\"d-flex justify-content-between\">\n            <strong>Bucket {{ bucketIndex + 1 }}</strong>\n            <lh-drop-menu\n              [options]=\"getLhDropdownOptionsForBucket()\"\n              automationIdPrefix=\"bucket-{{bucketIndex}}\"\n              (optionSelected)=\"onSelectedOption($event, bucket, 'Bucket')\"\n            >\n              <i class=\"dpg-icon-system-kebab\"></i>\n            </lh-drop-menu>\n          </div>\n\n          <div class=\"text-truncate\">\n            <span [innerHTML]=\"bucket.text | htmlToPlainTextWithImage\"></span>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <div *ngIf=\"selectedBucket\" class=\"choices-wrapper\">\n    <div class=\"choices-heading\">\n      <span class=\"font-bold choice-index\"\n        >Choices for Bucket {{ getSelectedIndex() + 1 }}</span\n      >\n      <span translate *ngIf=\"choices.length\" class=\"margin-left\"\n        >How many times does each choice map to this bucket?</span\n      >\n    </div>\n    <div class=\"choice-wrapper\" *ngFor=\"let choice of getChoices(); let choiceIndex = index\">\n      <div class=\"choice-box\">\n        <span class=\"choice-text\">\n          <span\n            [innerHTML]=\"getChoice(choice.key)?.text | htmlToPlainTextWithImage\"\n          ></span>\n        </span>\n        <lh-drop-menu\n          [options]=\"kebabMenuOptions\"\n          automationIdPrefix=\"choice-{{choiceIndex}}\"\n          (optionSelected)=\"onSelectedOption($event, getChoice(choice.key), 'Choice')\"\n        >\n          <i class=\"dpg-icon-system-kebab\"></i>\n        </lh-drop-menu>\n      </div>\n      <div class=\"choice-count padding-left\">\n        <span class=\"font-bold quantity-text\" translate>QTY:</span>\n        <button\n          type=\"button\"\n          (click)=\"onDecrementingChoices(choice)\"\n          class=\"counter-button\"\n          [ngClass]=\"{ 'disable-counter': disableDecrement(choice.count) }\"\n          [disabled]=\"disableDecrement(choice.count)\"\n        >\n          <span translate>-</span>\n        </button>\n        <input\n          type=\"text\"\n          class=\"count-input\"\n          [disabled]=\"true\"\n          [value]=\"choice.count || 0\"\n          aria-label=\"count\"\n        />\n        <button\n          type=\"button\"\n          (click)=\"onIncrementingChoices(choice)\"\n          class=\"counter-button\"\n          [ngClass]=\"{ 'disable-counter': disableIncrement(choice.count) }\"\n          [disabled]=\"disableIncrement(choice.count)\"\n        >\n          <span translate>+</span>\n        </button>\n      </div>\n    </div>\n    <button\n      #addChoiceRef\n      type=\"button\"\n      class=\"add-choice\"\n      (click)=\"onClickingAddBucketOrChoiceLink('Choice')\"\n      [disabled]=\"stopAddingChoice()\"\n    >\n      <span translate>+ Add choice</span>\n    </button>\n  </div>\n</div>\n\n<aa-inline-error [errors]=\"errors\"></aa-inline-error>\n", styles: [".bucket-container{border:1px solid #a9a9a9;background:#fdfbfb;margin-bottom:20px;border-radius:4px;min-height:58px}.bucket-container.has-error{margin-bottom:0;border:1px solid #b0181e}.bucket-container .buckets-wrapper{padding:10px}.bucket-container .buckets-wrapper .bucket-wrapper{width:9em;background:#fff;border:1px solid #a9a9a9;display:inline-block;padding:.25rem .5rem}.bucket-container .buckets-wrapper .bucket-wrapper .bucket-box{background:transparent;border:none;text-align:left;cursor:pointer}.choices-wrapper{background:#fff;border-top:1px solid #a9a9a9}.choices-wrapper .choices-heading{padding:14px 20px}.choices-wrapper .choices-heading .choice-index{width:163px;display:inline-block}.choices-wrapper .choice-wrapper{display:flex;margin-left:12px;margin-right:40px;border-top:1px solid #dad7d7;padding:6px 0}.choices-wrapper .choice-wrapper .choice-box{display:flex;align-items:center;width:9em;background:#fff;border:1px solid #a9a9a9;text-align:left;margin:10px;padding:8px}.choices-wrapper .choice-wrapper .choice-text{width:70%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;float:left}.choices-wrapper .choice-wrapper .choice-count{margin-top:14px}.add-bucket,.add-choice{background:transparent;border:none}.add-bucket.no-bucket,.add-choice.no-bucket{position:relative;right:85px;top:50px}.add-bucket:disabled,.add-choice:disabled{opacity:0}.add-bucket:focus,.add-choice:focus{border:2px solid #006a7c}.add-choice{margin:0 20px 14px 14px}.bucket-header{display:inline-block;margin-top:24px;margin-right:16px}.count-input{width:40px;text-align:center;background:white;border:1px solid #a9a9a9;height:28px;border-radius:2px;position:relative;top:-1px;padding:5px}.font-bold{font-weight:700}.quantity-text{margin-right:10px}.margin-left{margin-left:40px}.padding-left{padding-left:96px}.counter-button{font-size:22px;line-height:1.2;font-weight:700;background:white;border:1px solid #a9a9a9;padding:0 10px;border-radius:2px;margin-right:5px;margin-left:5px}.counter-button.disable-counter{opacity:.4}:host ::ng-deep .span-with-image{height:20px;width:20px;display:inline-block;position:relative;top:4px}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: i3$1.LhDropMenuComponent, selector: "lh-drop-menu", inputs: ["options", "tooltipText", "btnClass", "uniqueName", "automationIdPrefix"], outputs: ["optionSelected"] }, { kind: "component", type: AddUpdateBucketAndChoiceComponent, selector: "aa-add-update-bucket-and-choice", inputs: ["openBucketOrChoiceModal", "editedBucketOrChoice", "isBucketModal", "modalTitle", "buttonText", "focusElementOnClose"], outputs: ["closeBucketOrChoiceModal", "cancelAction", "confirmAction"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "pipe", type: HtmlToPlaintextWithImagePipe, name: "htmlToPlainTextWithImage" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BucketingChoicesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-bucketing-choices', changeDetection: ChangeDetectionStrategy.OnPush, template: "<h2 translate class=\"bucket-header\">Buckets *</h2>\n<button\n  #addBucketRef\n  type=\"button\"\n  class=\"add-bucket\"\n  [disabled]=\"stopAddingBucket()\"\n  [ngClass]=\"{ 'no-bucket': !buckets.length }\"\n  (click)=\"onClickingAddBucketOrChoiceLink('Bucket')\"\n>\n  <span translate>+ Add Bucket</span>\n</button>\n\n<div class=\"bucket-container\" [class.has-error]=\"errors.length\">\n  <div>\n    <aa-add-update-bucket-and-choice\n      [openBucketOrChoiceModal]=\"openBucketOrChoiceModal\"\n      [modalTitle]=\"getModalTitle()\"\n      [buttonText]=\"getButtonText()\"\n      [isBucketModal]=\"isOpeningBucketModal()\"\n      [focusElementOnClose]=\"getFocusableElement()\"\n      [editedBucketOrChoice]=\"editedBucketOrChoice\"\n      (confirmAction)=\"onConfirmAction($event)\"\n      (closeBucketOrChoiceModal)=\"onCloseBucketOrChoiceModal()\"\n      (cancelAction)=\"onCancelBucketOrChoiceModal()\"\n    ></aa-add-update-bucket-and-choice>\n  </div>\n\n  <air-modal\n    [isOpen]=\"deleteBucketOrChoiceModal\"\n    [titleText]=\"'Delete'\"\n    [confirmButtonText]=\"'Yes, delete'\"\n    (cancel)=\"onCloseBucketOrChoiceModal()\"\n    (close)=\"onCloseBucketOrChoiceModal()\"\n    (confirm)=\"onDeletingBucketOrChoice()\"\n  >\n    <p translate *ngIf=\"isOpeningBucketModal()\">\n      If you delete this bucket, all of the choices and their quantities within\n      this bucket will be deleted. Do you want to proceed?\n    </p>\n    <p translate *ngIf=\"!isOpeningBucketModal()\">\n      If you delete this choice, it will be deleted from all buckets. Do you\n      want to proceed?\n    </p>\n  </air-modal>\n\n  <div class=\"buckets-wrapper row row-cols-auto g-3\">\n    <div *ngFor=\"let bucket of buckets; let bucketIndex = index\" class=\"col\">\n      <div class=\"bucket-wrapper\">\n        <div\n          class=\"bucket-box\"\n          tabindex=\"0\"\n          (click)=\"onBucketSelection(bucket)\"\n        >\n          <div class=\"d-flex justify-content-between\">\n            <strong>Bucket {{ bucketIndex + 1 }}</strong>\n            <lh-drop-menu\n              [options]=\"getLhDropdownOptionsForBucket()\"\n              automationIdPrefix=\"bucket-{{bucketIndex}}\"\n              (optionSelected)=\"onSelectedOption($event, bucket, 'Bucket')\"\n            >\n              <i class=\"dpg-icon-system-kebab\"></i>\n            </lh-drop-menu>\n          </div>\n\n          <div class=\"text-truncate\">\n            <span [innerHTML]=\"bucket.text | htmlToPlainTextWithImage\"></span>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <div *ngIf=\"selectedBucket\" class=\"choices-wrapper\">\n    <div class=\"choices-heading\">\n      <span class=\"font-bold choice-index\"\n        >Choices for Bucket {{ getSelectedIndex() + 1 }}</span\n      >\n      <span translate *ngIf=\"choices.length\" class=\"margin-left\"\n        >How many times does each choice map to this bucket?</span\n      >\n    </div>\n    <div class=\"choice-wrapper\" *ngFor=\"let choice of getChoices(); let choiceIndex = index\">\n      <div class=\"choice-box\">\n        <span class=\"choice-text\">\n          <span\n            [innerHTML]=\"getChoice(choice.key)?.text | htmlToPlainTextWithImage\"\n          ></span>\n        </span>\n        <lh-drop-menu\n          [options]=\"kebabMenuOptions\"\n          automationIdPrefix=\"choice-{{choiceIndex}}\"\n          (optionSelected)=\"onSelectedOption($event, getChoice(choice.key), 'Choice')\"\n        >\n          <i class=\"dpg-icon-system-kebab\"></i>\n        </lh-drop-menu>\n      </div>\n      <div class=\"choice-count padding-left\">\n        <span class=\"font-bold quantity-text\" translate>QTY:</span>\n        <button\n          type=\"button\"\n          (click)=\"onDecrementingChoices(choice)\"\n          class=\"counter-button\"\n          [ngClass]=\"{ 'disable-counter': disableDecrement(choice.count) }\"\n          [disabled]=\"disableDecrement(choice.count)\"\n        >\n          <span translate>-</span>\n        </button>\n        <input\n          type=\"text\"\n          class=\"count-input\"\n          [disabled]=\"true\"\n          [value]=\"choice.count || 0\"\n          aria-label=\"count\"\n        />\n        <button\n          type=\"button\"\n          (click)=\"onIncrementingChoices(choice)\"\n          class=\"counter-button\"\n          [ngClass]=\"{ 'disable-counter': disableIncrement(choice.count) }\"\n          [disabled]=\"disableIncrement(choice.count)\"\n        >\n          <span translate>+</span>\n        </button>\n      </div>\n    </div>\n    <button\n      #addChoiceRef\n      type=\"button\"\n      class=\"add-choice\"\n      (click)=\"onClickingAddBucketOrChoiceLink('Choice')\"\n      [disabled]=\"stopAddingChoice()\"\n    >\n      <span translate>+ Add choice</span>\n    </button>\n  </div>\n</div>\n\n<aa-inline-error [errors]=\"errors\"></aa-inline-error>\n", styles: [".bucket-container{border:1px solid #a9a9a9;background:#fdfbfb;margin-bottom:20px;border-radius:4px;min-height:58px}.bucket-container.has-error{margin-bottom:0;border:1px solid #b0181e}.bucket-container .buckets-wrapper{padding:10px}.bucket-container .buckets-wrapper .bucket-wrapper{width:9em;background:#fff;border:1px solid #a9a9a9;display:inline-block;padding:.25rem .5rem}.bucket-container .buckets-wrapper .bucket-wrapper .bucket-box{background:transparent;border:none;text-align:left;cursor:pointer}.choices-wrapper{background:#fff;border-top:1px solid #a9a9a9}.choices-wrapper .choices-heading{padding:14px 20px}.choices-wrapper .choices-heading .choice-index{width:163px;display:inline-block}.choices-wrapper .choice-wrapper{display:flex;margin-left:12px;margin-right:40px;border-top:1px solid #dad7d7;padding:6px 0}.choices-wrapper .choice-wrapper .choice-box{display:flex;align-items:center;width:9em;background:#fff;border:1px solid #a9a9a9;text-align:left;margin:10px;padding:8px}.choices-wrapper .choice-wrapper .choice-text{width:70%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;float:left}.choices-wrapper .choice-wrapper .choice-count{margin-top:14px}.add-bucket,.add-choice{background:transparent;border:none}.add-bucket.no-bucket,.add-choice.no-bucket{position:relative;right:85px;top:50px}.add-bucket:disabled,.add-choice:disabled{opacity:0}.add-bucket:focus,.add-choice:focus{border:2px solid #006a7c}.add-choice{margin:0 20px 14px 14px}.bucket-header{display:inline-block;margin-top:24px;margin-right:16px}.count-input{width:40px;text-align:center;background:white;border:1px solid #a9a9a9;height:28px;border-radius:2px;position:relative;top:-1px;padding:5px}.font-bold{font-weight:700}.quantity-text{margin-right:10px}.margin-left{margin-left:40px}.padding-left{padding-left:96px}.counter-button{font-size:22px;line-height:1.2;font-weight:700;background:white;border:1px solid #a9a9a9;padding:0 10px;border-radius:2px;margin-right:5px;margin-left:5px}.counter-button.disable-counter{opacity:.4}:host ::ng-deep .span-with-image{height:20px;width:20px;display:inline-block;position:relative;top:4px}\n"] }]
        }], propDecorators: { buckets: [{
                type: Input
            }], choices: [{
                type: Input
            }], answers: [{
                type: Input
            }], errors: [{
                type: Input
            }], addBucket: [{
                type: Output
            }], updateBucket: [{
                type: Output
            }], updatePromptContent: [{
                type: Output
            }], deleteBucket: [{
                type: Output
            }], addChoice: [{
                type: Output
            }], deleteChoice: [{
                type: Output
            }], incrementChoice: [{
                type: Output
            }], decrementChoice: [{
                type: Output
            }], updateChoice: [{
                type: Output
            }], addBucketRef: [{
                type: ViewChild,
                args: ['addBucketRef', { static: true }]
            }], addChoiceRef: [{
                type: ViewChild,
                args: ['addChoiceRef']
            }] } });

class AirBucketingComponent extends AirBaseComponent {
    constructor(store, validationService, configService) {
        super(store, getPureAirBucketing, LoadAirItemAction$d, ResetStoreAction$d);
        this.store = store;
        this.validationService = validationService;
        this.configService = configService;
        this.pureAirBucketing$ = this.store.select(getPureAirBucketing);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.allowInlineValidation = false;
        this.airBucketing$ = store.select(getAirBucketing);
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onAddBucket(content) {
        this.validationService.resetBucketsError();
        this.store.dispatch(new AddBucketAction(content));
    }
    onDeleteBucket(bucket) {
        this.store.dispatch(new DeleteBucketAction(bucket));
    }
    onUpdateBucket(payload) {
        this.store.dispatch(new UpdateBucketAction(payload));
    }
    onAddChoice(content) {
        this.validationService.resetChoicesError();
        this.store.dispatch(new AddChoiceAction(content));
    }
    onDeleteChoice(choice) {
        this.store.dispatch(new DeleteChoiceAction(choice));
    }
    onUpdateChoice(payload) {
        this.store.dispatch(new UpdateChoiceAction(payload));
    }
    onDecrementingChoice({ bucket, choice }) {
        this.store.dispatch(new DecrementChoiceAction({ bucket, choice }));
    }
    onIncrementingChoice({ bucket, choice }) {
        this.validationService.resetAnswersError();
        this.store.dispatch(new IncrementChoiceAction({ bucket, choice }));
    }
    getErrors() {
        return this.allowInlineValidation && this.validationService.getBucketingErrors();
    }
}
AirBucketingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirBucketingComponent, deps: [{ token: i1.Store }, { token: BucketingValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirBucketingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirBucketingComponent, selector: "aa-air-bucketing", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-bucketing-choices [buckets]=\"(airBucketing$ | async)?.buckets\"\n                      [choices]=\"(airBucketing$ | async)?.choices\"\n                      [answers]=\"(airBucketing$ | async)?.answers\"\n                      [errors]=\"getErrors()\"\n                      (addBucket)=\"onAddBucket($event)\"\n                      (deleteBucket)=\"onDeleteBucket($event)\"\n                      (updateBucket)=\"onUpdateBucket($event)\"\n                      (addChoice)=\"onAddChoice($event)\"\n                      (deleteChoice)=\"onDeleteChoice($event)\"\n                      (updateChoice)=\"onUpdateChoice($event)\"\n                      (incrementChoice)=\"onIncrementingChoice($event)\"\n                      (decrementChoice)=\"onDecrementingChoice($event)\"></aa-bucketing-choices>\n", dependencies: [{ kind: "component", type: BucketingChoicesComponent, selector: "aa-bucketing-choices", inputs: ["buckets", "choices", "answers", "errors"], outputs: ["addBucket", "updateBucket", "updatePromptContent", "deleteBucket", "addChoice", "deleteChoice", "incrementChoice", "decrementChoice", "updateChoice"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirBucketingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-bucketing', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-bucketing-choices [buckets]=\"(airBucketing$ | async)?.buckets\"\n                      [choices]=\"(airBucketing$ | async)?.choices\"\n                      [answers]=\"(airBucketing$ | async)?.answers\"\n                      [errors]=\"getErrors()\"\n                      (addBucket)=\"onAddBucket($event)\"\n                      (deleteBucket)=\"onDeleteBucket($event)\"\n                      (updateBucket)=\"onUpdateBucket($event)\"\n                      (addChoice)=\"onAddChoice($event)\"\n                      (deleteChoice)=\"onDeleteChoice($event)\"\n                      (updateChoice)=\"onUpdateChoice($event)\"\n                      (incrementChoice)=\"onIncrementingChoice($event)\"\n                      (decrementChoice)=\"onDecrementingChoice($event)\"></aa-bucketing-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: BucketingValidationService }, { type: ConfigService }]; } });

class ChoiceMatrixValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirChoiceMatrix$ = this.store.select(getPureAirChoiceMatrix);
        this.rowColLabelError = '';
        this.correctAnswerError = '';
    }
    validate() {
        let isValid;
        this.pureAirChoiceMatrix$.pipe(take(1)).subscribe((airChoiceMatrix) => {
            const promptError = !!this.promptValidationService.validatePrompt(airChoiceMatrix.prompt);
            const noHeaderError = !!this.validateRowColsLabels(airChoiceMatrix.columnLabels, airChoiceMatrix.rowLabels);
            const correctAnswerError = !!this.validateCorrectAnswerMultipleResponse(airChoiceMatrix.answers, airChoiceMatrix.selectMultiplePerRow);
            isValid = !(promptError || noHeaderError || correctAnswerError);
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetAnswerError();
    }
    validateRowColsLabels(columnLabels, rowLabels) {
        this.rowColLabelError = '';
        this.rowColLabelError =
            columnLabels.filter((label) => !label).length || rowLabels.filter((label) => !label).length
                ? E_NO_ROW_COLUMN_HEADER
                : '';
        return this.rowColLabelError;
    }
    validateCorrectAnswerMultipleResponse(answers, selectMultipleResponse) {
        this.correctAnswerError = '';
        if (selectMultipleResponse) {
            this.correctAnswerError = !answers.filter((answer) => answer.length > 0).length
                ? E_NO_CORRECT_ANSWER_MULTIPLE_RESPONSE
                : '';
        }
        else {
            this.correctAnswerError = answers.filter((answer) => answer.length < 1).length
                ? E_NO_CORRECT_ANSWER_PER_ROW
                : '';
        }
        return this.correctAnswerError;
    }
    getRowColError() {
        return this.rowColLabelError ? [this.rowColLabelError] : [];
    }
    resetAnswerError() {
        this.rowColLabelError = '';
        this.correctAnswerError = '';
    }
    getAnswerError() {
        const error = this.rowColLabelError ? [this.rowColLabelError] : [];
        return this.correctAnswerError ? error.concat(this.correctAnswerError) : error;
    }
    getCorrectAnswerError() {
        return this.correctAnswerError ? [this.correctAnswerError] : [];
    }
}
ChoiceMatrixValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
ChoiceMatrixValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class MathService {
    constructor() {
        this.mathJax = {
            source: 'https://cdn.assess.prod.mheducation.com/3rdparty/mathjax/2.7.1/MathJax.js?config=TeX-MML-AM_HTMLorMML',
        };
        this.signal = new ReplaySubject();
        void this.registerMathJaxAsync(this.mathJax)
            .then(() => this.signal.next(true))
            .catch((error) => {
            console.log(error);
        });
    }
    async registerMathJaxAsync(config) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = config.id || '';
            script.type = 'text/javascript';
            script.src = config.source;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = (error) => reject(error);
            document.head.appendChild(script);
        });
    }
    ready() {
        return this.signal;
    }
    render(element, math) {
        if (math) {
            element.innerHTML = math;
        }
        const mathjax = window.MathJax;
        if (mathjax && mathjax.Hub) {
            mathjax.Hub.Queue(['Typeset', mathjax.Hub, element]);
        }
    }
}
MathService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MathService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class MathDirective {
    constructor(mathService, elementRef) {
        this.mathService = mathService;
        this.elementRef = elementRef;
        this.alive$ = new Subject();
        this.el = elementRef.nativeElement;
    }
    ngOnInit() {
        this.render();
    }
    ngOnChanges(changes) {
        if (changes && changes['appMath'] && changes['appMath'].currentValue) {
            this.render();
        }
    }
    render() {
        this.mathService
            .ready()
            .pipe(take(1), takeUntil(this.alive$))
            .subscribe(() => this.mathService.render(this.el, this.appMath));
    }
    ngOnDestroy() {
        this.alive$.next(false);
    }
}
MathDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathDirective, deps: [{ token: MathService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
MathDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: MathDirective, selector: "[appMath]", inputs: { appMath: "appMath" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appMath]',
                }]
        }], ctorParameters: function () { return [{ type: MathService }, { type: i0.ElementRef }]; }, propDecorators: { appMath: [{
                type: Input
            }] } });

class TrashCanComponent {
    constructor() {
        this.delete = new EventEmitter();
    }
}
TrashCanComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrashCanComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
TrashCanComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: TrashCanComponent, selector: "aa-trash-can", outputs: { delete: "delete" }, ngImport: i0, template: "<button class=\"trash-can\"\n        type=\"button\"\n        aria-label=\"delete\"\n        (click)=\"delete.emit()\">\n  <i class=\"dpg-icon dpg-icon-trash\"></i>\n</button>\n", styles: [".trash-can{border:none;background:transparent}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrashCanComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-trash-can', template: "<button class=\"trash-can\"\n        type=\"button\"\n        aria-label=\"delete\"\n        (click)=\"delete.emit()\">\n  <i class=\"dpg-icon dpg-icon-trash\"></i>\n</button>\n", styles: [".trash-can{border:none;background:transparent}\n"] }]
        }], propDecorators: { delete: [{
                type: Output
            }] } });

const MHE_EDITOR_CHARACTER_TEXT = 'characters';

class ChoiceMatrixRowsColsCellComponent {
    constructor() {
        this.cellData = '';
        this.notifyIsModalOpen = new EventEmitter();
        this.updateRowLabel = new EventEmitter();
        this.updateColumnLabel = new EventEmitter();
        this.errors = [];
        this.closeImmediateOnConfirm = true;
        this.blankMML = '<math xmlns=\"http://www.w3.org/1998/Math/MathML\"/>';
        this.cellType = ChoiceMatrixCellType.TEXT;
        this.confirmButtonText = 'Save';
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        this.mathEditorType = 'basic';
        this.choiceMatrixCellType = ChoiceMatrixCellType;
        this.cellTypeOptions = [
            {
                value: ChoiceMatrixCellType.TEXT,
                viewValue: 'Text',
            },
            {
                value: ChoiceMatrixCellType.EQUATION_ENTRY,
                viewValue: 'Equation Entry',
            },
        ];
    }
    ngOnChanges(changes) {
        if (changes.displayModal && changes.displayModal.currentValue) {
            if (changes.cellData?.currentValue) {
                this.cellDataBeforeSave = changes.cellData?.currentValue;
            }
            this.cellType =
                this.cellDataBeforeSave.indexOf('<math') > -1 ? ChoiceMatrixCellType.EQUATION_ENTRY : ChoiceMatrixCellType.TEXT;
        }
    }
    populateSelectedDropdownOption() {
        const selectedCellTypeValue = this.cellTypeOptions?.find((option) => option?.value === this.cellType);
        //this.cellType = selectedCellTypeValue;
        return selectedCellTypeValue;
    }
    cancelModal() {
        this.notifyIsModalOpen.emit(false);
        this.errors = [];
    }
    closeModal() {
        this.notifyIsModalOpen.emit(false);
        this.errors = [];
    }
    confirmModal() {
        this.validateCellContents();
        if (this.errors.length < 1) {
            this.notifyIsModalOpen.emit(false);
            if (this.tableProperty === ChoiceMatrixTableProps.ROW) {
                this.updateRowLabel.emit({ value: this.cellDataBeforeSave, index: this.rowOrColIndex });
            }
            else {
                this.updateColumnLabel.emit({ value: this.cellDataBeforeSave, index: this.rowOrColIndex });
            }
            this.closeImmediateOnConfirm = false;
        }
        else {
            this.closeImmediateOnConfirm = true;
        }
    }
    validateCellContents() {
        this.errors = [];
        if (!this.cellDataBeforeSave || this.cellDataBeforeSave === this.blankMML) {
            this.errors.push(EMPTY_FIELD_MESSAGE);
        }
    }
    onUpdateCellData(updatedAnswer) {
        this.cellDataBeforeSave = updatedAnswer;
    }
    onCellTypeSelected(event) {
        this.cellType = event?.value;
        this.cellDataBeforeSave = '';
        this.errors = [];
    }
}
ChoiceMatrixRowsColsCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixRowsColsCellComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ChoiceMatrixRowsColsCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ChoiceMatrixRowsColsCellComponent, selector: "aa-choice-matrix-rows-cols-cell", inputs: { tableProperty: "tableProperty", cellData: "cellData", displayModal: "displayModal", wirisMathUrl: "wirisMathUrl", rowOrColIndex: "rowOrColIndex" }, outputs: { notifyIsModalOpen: "notifyIsModalOpen", updateRowLabel: "updateRowLabel", updateColumnLabel: "updateColumnLabel" }, usesOnChanges: true, ngImport: i0, template: "<air-modal\n  [isOpen]=\"displayModal\"\n  [modalSize]=\"'lg'\"\n  [confirmButtonText]=\"confirmButtonText\"\n  [focusCloseOnOpen]=\"false\"\n  [closeImmediateOnConfirm]=\"closeImmediateOnConfirm\"\n  (cancel)=\"cancelModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>\n    <h3 style=\"display: inline-block;padding-right: 5px\">Cell Type: </h3>\n   <select\n      ngbAutofocus\n      class=\"form-select w-auto border-top-left-radius-6 border-bottom-left-radius-6 display-inline\"\n      [ngModel]=\"populateSelectedDropdownOption()\"\n      (ngModelChange)=\"onCellTypeSelected($event)\">\n      <option\n        *ngFor=\"let cellTypeOption of cellTypeOptions\"\n        [ngValue]=\"cellTypeOption\">\n        {{cellTypeOption.viewValue}}\n      </option>\n    </select>\n  </div>\n  <div *ngIf=\"cellType === choiceMatrixCellType.TEXT\">\n    <br/>\n    <h3>Text <span class=\"is-required\">*</span></h3>\n    <mhe-editor\n      [content]=\"cellDataBeforeSave\"\n      [maxCharacter]=\"200\"\n      [showCharacterLimit]=\"true\"\n      [useRichText]=\"false\"\n      [showResize]=\"false\"\n      (contentChange)=\"onUpdateCellData($event)\"\n    ></mhe-editor>\n  </div>\n  <div *ngIf=\"cellType === choiceMatrixCellType.EQUATION_ENTRY\">\n    <br/>\n    <h3>Equation Entry <span class=\"is-required\">*</span></h3>\n    <div class=\"container\">\n      <br/>\n      <div class=\"row align-items-start\">\n        <aa-math-editor\n          [editorId]=\"'equation'\"\n          (updateAnswer)=\"onUpdateCellData($event)\"\n          [mathml]=\"cellDataBeforeSave\"\n          [mathEditorType]=\"mathEditorType\"\n          [wirisMathUrl]=\"wirisMathUrl\"\n        ></aa-math-editor>\n      </div>\n    </div>\n  </div>\n  <span *ngIf=\"errors.length > 0\" class=\"help-block has-error\">\n    <span *ngFor=\"let error of errors\">\n      <ul>\n        <li>{{error}}</li>\n      </ul>\n    </span>\n  </span>\n  <br/>\n  <span>Note: Changing the dropdown would clear the editor.</span>\n</air-modal>\n", styles: [".has-error,.is-required{color:#e21f26}.display-inline{display:inline-block}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: MathEditorComponent, selector: "aa-math-editor", inputs: ["editorId", "mathml", "mathEditorType", "answers", "wirisMathUrl", "preset"], outputs: ["updateAnswer", "editorBlur"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixRowsColsCellComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-choice-matrix-rows-cols-cell', template: "<air-modal\n  [isOpen]=\"displayModal\"\n  [modalSize]=\"'lg'\"\n  [confirmButtonText]=\"confirmButtonText\"\n  [focusCloseOnOpen]=\"false\"\n  [closeImmediateOnConfirm]=\"closeImmediateOnConfirm\"\n  (cancel)=\"cancelModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>\n    <h3 style=\"display: inline-block;padding-right: 5px\">Cell Type: </h3>\n   <select\n      ngbAutofocus\n      class=\"form-select w-auto border-top-left-radius-6 border-bottom-left-radius-6 display-inline\"\n      [ngModel]=\"populateSelectedDropdownOption()\"\n      (ngModelChange)=\"onCellTypeSelected($event)\">\n      <option\n        *ngFor=\"let cellTypeOption of cellTypeOptions\"\n        [ngValue]=\"cellTypeOption\">\n        {{cellTypeOption.viewValue}}\n      </option>\n    </select>\n  </div>\n  <div *ngIf=\"cellType === choiceMatrixCellType.TEXT\">\n    <br/>\n    <h3>Text <span class=\"is-required\">*</span></h3>\n    <mhe-editor\n      [content]=\"cellDataBeforeSave\"\n      [maxCharacter]=\"200\"\n      [showCharacterLimit]=\"true\"\n      [useRichText]=\"false\"\n      [showResize]=\"false\"\n      (contentChange)=\"onUpdateCellData($event)\"\n    ></mhe-editor>\n  </div>\n  <div *ngIf=\"cellType === choiceMatrixCellType.EQUATION_ENTRY\">\n    <br/>\n    <h3>Equation Entry <span class=\"is-required\">*</span></h3>\n    <div class=\"container\">\n      <br/>\n      <div class=\"row align-items-start\">\n        <aa-math-editor\n          [editorId]=\"'equation'\"\n          (updateAnswer)=\"onUpdateCellData($event)\"\n          [mathml]=\"cellDataBeforeSave\"\n          [mathEditorType]=\"mathEditorType\"\n          [wirisMathUrl]=\"wirisMathUrl\"\n        ></aa-math-editor>\n      </div>\n    </div>\n  </div>\n  <span *ngIf=\"errors.length > 0\" class=\"help-block has-error\">\n    <span *ngFor=\"let error of errors\">\n      <ul>\n        <li>{{error}}</li>\n      </ul>\n    </span>\n  </span>\n  <br/>\n  <span>Note: Changing the dropdown would clear the editor.</span>\n</air-modal>\n", styles: [".has-error,.is-required{color:#e21f26}.display-inline{display:inline-block}\n"] }]
        }], propDecorators: { tableProperty: [{
                type: Input
            }], cellData: [{
                type: Input
            }], displayModal: [{
                type: Input
            }], wirisMathUrl: [{
                type: Input
            }], rowOrColIndex: [{
                type: Input
            }], notifyIsModalOpen: [{
                type: Output
            }], updateRowLabel: [{
                type: Output
            }], updateColumnLabel: [{
                type: Output
            }] } });

class FocusTrapDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.focusableElements = [];
    }
    ngAfterViewInit() {
        this.updateFocusableElements();
        this.focusElement(0);
    }
    handleKeyboardEvent(event) {
        if (event.key === 'Tab') {
            this.trapFocus(event);
        }
    }
    trapFocus(event) {
        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
            // Shift + Tab, and on the first element: loop to the last
            event.preventDefault();
            lastElement.focus();
        }
        else if (!event.shiftKey && document.activeElement === lastElement) {
            // Tab, and on the last element: loop to the first
            event.preventDefault();
            firstElement.focus();
        }
    }
    focusElement(index) {
        this.focusableElements[index].focus();
    }
    updateFocusableElements() {
        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([type="hidden"]):not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ];
        this.focusableElements = Array.from(this.elementRef.nativeElement.querySelectorAll(focusableSelectors.join(',')));
    }
}
FocusTrapDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FocusTrapDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FocusTrapDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: FocusTrapDirective, selector: "[appFocusTrap]", host: { listeners: { "keydown": "handleKeyboardEvent($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FocusTrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[appFocusTrap]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class ChoiceMatrixRowsColsComponent {
    constructor(choiceMatrixValidationService, sanitizeInputService) {
        this.choiceMatrixValidationService = choiceMatrixValidationService;
        this.sanitizeInputService = sanitizeInputService;
        this.addingColumn = new EventEmitter();
        this.addingRow = new EventEmitter();
        this.updateRowLabel = new EventEmitter();
        this.updateColumnLabel = new EventEmitter();
        this.deletingRow = new EventEmitter();
        this.deletingColumn = new EventEmitter();
        this.selectingAnswer = new EventEmitter();
        this.rowsCount = 1;
        this.columnsCount = 1;
        this.maxColumnError = false;
        this.maxRowError = false;
        this.hasValidRowInput = true;
        this.hasValidColumnInput = true;
        this.columnCount = 1;
        this.cellData = '';
        this.tableProps = ChoiceMatrixTableProps;
        this.popoverButtonFocus = false;
        this.displayModal = false;
    }
    onUpdateColumnLabel(value, index) {
        this.updateColumnLabel.emit({ value, index });
    }
    onUpdateRowLabel(value, index) {
        this.updateRowLabel.emit({ value, index });
    }
    trackByIndex(index) {
        return index;
    }
    canAddRow() {
        return this.rowLabels.length < 10;
    }
    canAddColumn() {
        return this.columnLabels.length < 5;
    }
    showTrashIconForColumns() {
        return this.columnLabels.length > 1;
    }
    showTrashIconForRows() {
        return this.rowLabels.length > 1;
    }
    onSelectingAnswer(rowIndex, columnIndex) {
        this.selectingAnswer.emit({ rowIndex, columnIndex });
        setTimeout(() => {
            this.onAnswerSelect();
        });
    }
    onCheckSelected(rowIndex, columnIndex) {
        const selChecked = this.answers[rowIndex].find((ans) => ans === columnIndex);
        if (typeof selChecked === 'number') {
            return true;
        }
        return false;
    }
    onSetValue(value, isColumn) {
        if (!isColumn) {
            this.rowsCount = value;
            this.hasValidRowInput = this.checkNumericInput(value);
        }
        else {
            this.columnsCount = value;
            this.hasValidColumnInput = this.checkNumericInput(value);
        }
    }
    checkNumericInput(value) {
        return value && /^\d+$/.test(value);
    }
    onAddingRows(addRow) {
        if (this.hasValidRowInput) {
            const noOfRows = +this.rowsCount;
            const prevRowLength = this.rowLabels.length;
            if (+(prevRowLength + noOfRows) <= 10) {
                if (this.row_popover && addRow) {
                    this.row_popover.close();
                    this.addingRow.emit({ noOfRows });
                    this.resetPopoverValues();
                }
                this.maxRowError = false;
            }
            else {
                this.maxRowError = true;
            }
        }
    }
    onAddingColumns(addColumn) {
        if (this.hasValidColumnInput) {
            const noOfColumns = +this.columnsCount;
            const prevColumnLength = this.columnLabels.length;
            if (+(prevColumnLength + noOfColumns) <= 5) {
                if (this.column_popover && addColumn) {
                    this.column_popover.close();
                    this.addingColumn.emit({ noOfColumns });
                    this.resetPopoverValues();
                }
                this.maxColumnError = false;
            }
            else {
                this.maxColumnError = true;
            }
        }
    }
    resetPopoverValues() {
        this.maxRowError = false;
        this.maxColumnError = false;
        this.rowsCount = 1;
        this.columnsCount = 1;
    }
    onFocusOut(value, isColumn) {
        if (isColumn) {
            this.columnsCount = +value;
            this.onAddingColumns(false);
        }
        else {
            this.rowsCount = +value;
            this.onAddingRows(false);
        }
    }
    getAnswerError() {
        return this.choiceMatrixValidationService.getAnswerError();
    }
    getMaxRowColError(isColumn) {
        return isColumn ? [E_MAX_COLUMNS] : [E_MAX_ROWS];
    }
    onEditorFocusOut() {
        if (this.choiceMatrixValidationService.getRowColError().length) {
            return this.choiceMatrixValidationService.validateRowColsLabels(this.columnLabels, this.rowLabels);
        }
        return;
    }
    onAnswerSelect() {
        if (this.choiceMatrixValidationService.getCorrectAnswerError().length) {
            return this.choiceMatrixValidationService.validateCorrectAnswerMultipleResponse(this.answers, this.selectMultiplePerRow);
        }
        return;
    }
    sanitizeInput(event, allowMinus, allowDecimal) {
        return this.sanitizeInputService.sanitizeInput(event, false, false);
    }
    onDelete(index, isColumn) {
        if (isColumn) {
            this.deletingColumn.emit(index);
        }
        else {
            this.deletingRow.emit(index);
        }
        setTimeout(() => {
            this.onAnswerSelect();
            this.onEditorFocusOut();
        });
    }
    displayModalForCell(type, index) {
        this.displayModal = true;
        this.tableProperty = type;
        this.rowOrColIndex = index;
        this.cellData =
            this.tableProperty === ChoiceMatrixTableProps.ROW ? this.rowLabels[index] : this.columnLabels[index];
    }
    onModalStatusChange(status) {
        this.displayModal = status;
        const isModalClosed = !status;
        if (isModalClosed) {
            this.focusOnSelectedCell();
        }
    }
    getHeaderId(type, index) {
        return `${type.toString()}_${index}`;
    }
    // this code is needed so that after the cell was interacted with, the tabbing order would not start from the beginning of the page
    focusOnSelectedCell() {
        const selectedCellId = `${this.tableProperty.toString()}_${this.rowOrColIndex}`;
        const cell = this.tableCells.find(({ nativeElement }) => nativeElement.id === selectedCellId);
        setTimeout(() => cell?.nativeElement.focus(), 100);
    }
    getHeaderText(label) {
        return label || `Header`;
    }
    closePopover(button) {
        this.popoverButtonFocus && button.focus();
        this.popoverButtonFocus = false;
    }
}
ChoiceMatrixRowsColsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixRowsColsComponent, deps: [{ token: ChoiceMatrixValidationService }, { token: SanitizeInputService }], target: i0.ɵɵFactoryTarget.Component });
ChoiceMatrixRowsColsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ChoiceMatrixRowsColsComponent, selector: "aa-choice-matrix-rows-cols", inputs: { selectMultiplePerRow: "selectMultiplePerRow", rowLabels: "rowLabels", columnLabels: "columnLabels", answers: "answers", wirisMathUrl: "wirisMathUrl" }, outputs: { addingColumn: "addingColumn", addingRow: "addingRow", updateRowLabel: "updateRowLabel", updateColumnLabel: "updateColumnLabel", deletingRow: "deletingRow", deletingColumn: "deletingColumn", selectingAnswer: "selectingAnswer" }, viewQueries: [{ propertyName: "column_popover", first: true, predicate: ["columnPopover"], descendants: true }, { propertyName: "row_popover", first: true, predicate: ["rowPopover"], descendants: true }, { propertyName: "tableCells", predicate: ["tableCell"], descendants: true }], ngImport: i0, template: "<div>\n  <div>\n    <h2 class=\"choice-matrix-header\" translate>Matrix *</h2>\n  </div>\n\n  <div class=\"option-container pt-3\">\n    <div class=\"add-column-wrapper\">\n      <button\n        *ngIf=\"canAddColumn()\"\n        type=\"button\"\n        class=\"btn btn-tertiary add-column\"\n        #addColumnButton\n        #columnPopover=\"ngbPopover\"\n        placement=\"top\"\n        [ngbPopover]=\"columnPopoverRef\"\n        panelClass=\"row-column-popover\"\n        triggers=\"click\"\n        autoClose=\"outside\"\n        (hidden)=\"popoverButtonFocus && closePopover(addColumnButton)\"\n        translate\n      >\n        + Add column\n      </button>\n\n      <ng-template #columnPopoverRef>\n        <div\n          appFocusTrap\n          class=\"popover-content\"\n          (keydown.escape)=\"popoverButtonFocus = true\"\n        >\n          <div translate>How many columns?</div>\n          <div class=\"row-col-input\">\n            <div>\n              <input\n                #inptAddCols\n                type=\"number\"\n                class=\"form-control\"\n                [value]=\"columnsCount\"\n                [class.is-invalid]=\"maxColumnError\"\n                (change)=\"onSetValue(inptAddCols.value, true)\"\n                (blur)=\"onFocusOut(inptAddCols.value, true)\"\n                (keydown)=\"sanitizeInput($event, false, false)\"\n              />\n\n              <div class=\"form-text\" [class.text-danger]=\"maxColumnError\">\n                Max: 5 columns\n              </div>\n            </div>\n            <div>\n              <button\n                class=\"btn btn-primary add-button\"\n                type=\"button\"\n                [ngClass]=\"{ 'error-class-column': maxColumnError }\"\n                (click)=\"popoverButtonFocus = true; onAddingColumns(true)\"\n                translate\n              >\n                Add\n              </button>\n            </div>\n          </div>\n        </div>\n      </ng-template>\n    </div>\n    <div\n      class=\"rows-columns-container mb-3\"\n      [ngClass]=\"{ 'error-class': getAnswerError()?.length }\"\n    >\n      <table>\n        <tbody>\n          <tr class=\"row-wrappers\">\n            <td class=\"col-wrapper\"></td>\n            <td\n              class=\"col-wrapper\"\n              *ngFor=\"\n                let columnLabel of columnLabels;\n                let columnIndex = index;\n                trackBy: trackByIndex\n              \"\n            >\n              <button #tableCell\n                type=\"button\"\n                class=\"header-content\"\n                [id]=\"getHeaderId(tableProps.COLUMN, columnIndex)\"\n                (click)=\"displayModalForCell(tableProps.COLUMN, columnIndex)\"\n                [appMath]=\"columnLabel\"\n              >\n                {{getHeaderText(columnLabel)}}\n              </button>\n              <aa-trash-can\n                *ngIf=\"showTrashIconForColumns()\"\n                (delete)=\"onDelete(columnIndex, true)\"\n              ></aa-trash-can>\n            </td>\n          </tr>\n          <tr\n            class=\"row-wrappers\"\n            *ngFor=\"\n              let rowLabel of rowLabels;\n              let rowIndex = index;\n              trackBy: trackByIndex\n            \"\n          >\n            <td class=\"col-wrapper\">\n              <button #tableCell\n                type=\"button\"\n                class=\"header-content\"\n                [id]=\"getHeaderId(tableProps.ROW, rowIndex)\"\n                (click)=\"displayModalForCell(tableProps.ROW, rowIndex)\"\n                [appMath]=\"rowLabel\"\n              >\n                {{getHeaderText(rowLabel)}}\n              </button>\n              <aa-trash-can\n                *ngIf=\"showTrashIconForRows()\"\n                (delete)=\"onDelete(rowIndex, false)\"\n              >\n              </aa-trash-can>\n            </td>\n            <td\n              class=\"col-wrapper\"\n              *ngFor=\"let columnLabel of columnLabels; let colIndex = index\"\n            >\n              <div class=\"form-check\" *ngIf=\"!selectMultiplePerRow\">\n                <input class=\"form-check-input air-matrix-radio-and-checkbox-button\" type=\"radio\" \n                [checked]=\"answers[rowIndex][0] === colIndex ? true : false\"\n                (change)=\"onSelectingAnswer(rowIndex, colIndex)\">\n              </div>\n              <div class=\"form-check\" *ngIf=\"selectMultiplePerRow\">\n                <input class=\"form-check-input air-matrix-radio-and-checkbox-button\" type=\"checkbox\" \n                      [checked]=\"onCheckSelected(rowIndex, colIndex)\"\n                      (change)=\"onSelectingAnswer(rowIndex, colIndex)\">\n              </div>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n    </div>\n    <button\n      *ngIf=\"canAddRow()\"\n      type=\"button\"\n      class=\"btn btn-tertiary add-row\"\n      #addRowButton\n      #rowPopover=\"ngbPopover\"\n      placement=\"right\"\n      [ngbPopover]=\"rowPopoverRef\"\n      panelClass=\"row-column-popover\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      (hidden)=\"popoverButtonFocus && closePopover(addRowButton)\"\n      translate\n    >\n      + Add row\n    </button>\n\n    <ng-template #rowPopoverRef>\n      <div\n        appFocusTrap\n        class=\"popover-content\"\n        (keydown.escape)=\"popoverButtonFocus = true\"\n      >\n        <div translate>How many rows?</div>\n        <div class=\"row-col-input\">\n          <div>\n            <input\n              #inptAddRows\n              type=\"number\"\n              class=\"form-control\"\n              [value]=\"rowsCount\"\n              [class.is-invalid]=\"maxRowError\"\n              (change)=\"onSetValue(inptAddRows.value, false)\"\n              (blur)=\"onFocusOut(inptAddRows.value, false)\"\n              (keydown)=\"sanitizeInput($event, false, false)\"\n            />\n\n            <div class=\"form-text\" [class.text-danger]=\"maxRowError\">\n              Max: 10 rows\n            </div>\n          </div>\n          <div>\n            <button\n              class=\"btn btn-primary add-button\"\n              type=\"button\"\n              [ngClass]=\"{ 'error-class-row': maxRowError }\"\n              (click)=\"popoverButtonFocus = true; onAddingRows(true)\"\n              translate\n            >\n              Add\n            </button>\n          </div>\n        </div>\n      </div>\n    </ng-template>\n\n    <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n  </div>\n</div>\n\n<aa-choice-matrix-rows-cols-cell\n  (notifyIsModalOpen)=\"onModalStatusChange($event)\"\n  (updateRowLabel)=\"updateRowLabel.emit($event)\"\n  (updateColumnLabel)=\"updateColumnLabel.emit($event)\"\n  [cellData]=\"cellData\"\n  [rowOrColIndex]=\"rowOrColIndex\"\n  [tableProperty]=\"tableProperty\"\n  [displayModal]=\"displayModal\"\n  [wirisMathUrl]=\"wirisMathUrl\">\n</aa-choice-matrix-rows-cols-cell>\n", styles: [".choice-matrix-header{display:inline-block;margin-right:20px;margin-bottom:0;position:relative;top:20px}.option-container{display:inline-block;border-radius:5px}.option-container .add-column-wrapper{text-align:right;min-height:25px}.option-container .col-wrapper{border:1px solid #ccc;padding:12px 5px 12px 12px;text-align:center}.option-container .col-wrapper mhe-checkbox,.option-container .col-wrapper mhe-radio-button{position:relative;bottom:6px}.option-container .ahe-ui-radio,.option-container .ahe-ui-checkbox{position:relative;top:-6px}.option-container .trash-can{position:relative;top:32px}.error-class{border:1px solid #b0181e}.add-column{margin-bottom:5px}.add-row{margin-top:5px}mhe-editor{width:75%;float:left}.popover{display:block;top:585px;left:98.3594px}.red-error{color:#b11111}.red-border{border-color:#b11111}.popoverInput{width:80px;height:38px}.popover-wrapper{padding:25px}.row-col-input{display:flex;flex-direction:row;margin:10px 0}.add-button{margin-left:5px}.column-row-help-text{margin-bottom:10px}.air-matrix-radio-and-checkbox-button{float:none}.header-content{border:none;background:none;text-align:center}.header-content:focus{outline:2px auto #017d92}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i6.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "triggers", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "directive", type: MathDirective, selector: "[appMath]", inputs: ["appMath"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "component", type: ChoiceMatrixRowsColsCellComponent, selector: "aa-choice-matrix-rows-cols-cell", inputs: ["tableProperty", "cellData", "displayModal", "wirisMathUrl", "rowOrColIndex"], outputs: ["notifyIsModalOpen", "updateRowLabel", "updateColumnLabel"] }, { kind: "directive", type: FocusTrapDirective, selector: "[appFocusTrap]" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixRowsColsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-choice-matrix-rows-cols', template: "<div>\n  <div>\n    <h2 class=\"choice-matrix-header\" translate>Matrix *</h2>\n  </div>\n\n  <div class=\"option-container pt-3\">\n    <div class=\"add-column-wrapper\">\n      <button\n        *ngIf=\"canAddColumn()\"\n        type=\"button\"\n        class=\"btn btn-tertiary add-column\"\n        #addColumnButton\n        #columnPopover=\"ngbPopover\"\n        placement=\"top\"\n        [ngbPopover]=\"columnPopoverRef\"\n        panelClass=\"row-column-popover\"\n        triggers=\"click\"\n        autoClose=\"outside\"\n        (hidden)=\"popoverButtonFocus && closePopover(addColumnButton)\"\n        translate\n      >\n        + Add column\n      </button>\n\n      <ng-template #columnPopoverRef>\n        <div\n          appFocusTrap\n          class=\"popover-content\"\n          (keydown.escape)=\"popoverButtonFocus = true\"\n        >\n          <div translate>How many columns?</div>\n          <div class=\"row-col-input\">\n            <div>\n              <input\n                #inptAddCols\n                type=\"number\"\n                class=\"form-control\"\n                [value]=\"columnsCount\"\n                [class.is-invalid]=\"maxColumnError\"\n                (change)=\"onSetValue(inptAddCols.value, true)\"\n                (blur)=\"onFocusOut(inptAddCols.value, true)\"\n                (keydown)=\"sanitizeInput($event, false, false)\"\n              />\n\n              <div class=\"form-text\" [class.text-danger]=\"maxColumnError\">\n                Max: 5 columns\n              </div>\n            </div>\n            <div>\n              <button\n                class=\"btn btn-primary add-button\"\n                type=\"button\"\n                [ngClass]=\"{ 'error-class-column': maxColumnError }\"\n                (click)=\"popoverButtonFocus = true; onAddingColumns(true)\"\n                translate\n              >\n                Add\n              </button>\n            </div>\n          </div>\n        </div>\n      </ng-template>\n    </div>\n    <div\n      class=\"rows-columns-container mb-3\"\n      [ngClass]=\"{ 'error-class': getAnswerError()?.length }\"\n    >\n      <table>\n        <tbody>\n          <tr class=\"row-wrappers\">\n            <td class=\"col-wrapper\"></td>\n            <td\n              class=\"col-wrapper\"\n              *ngFor=\"\n                let columnLabel of columnLabels;\n                let columnIndex = index;\n                trackBy: trackByIndex\n              \"\n            >\n              <button #tableCell\n                type=\"button\"\n                class=\"header-content\"\n                [id]=\"getHeaderId(tableProps.COLUMN, columnIndex)\"\n                (click)=\"displayModalForCell(tableProps.COLUMN, columnIndex)\"\n                [appMath]=\"columnLabel\"\n              >\n                {{getHeaderText(columnLabel)}}\n              </button>\n              <aa-trash-can\n                *ngIf=\"showTrashIconForColumns()\"\n                (delete)=\"onDelete(columnIndex, true)\"\n              ></aa-trash-can>\n            </td>\n          </tr>\n          <tr\n            class=\"row-wrappers\"\n            *ngFor=\"\n              let rowLabel of rowLabels;\n              let rowIndex = index;\n              trackBy: trackByIndex\n            \"\n          >\n            <td class=\"col-wrapper\">\n              <button #tableCell\n                type=\"button\"\n                class=\"header-content\"\n                [id]=\"getHeaderId(tableProps.ROW, rowIndex)\"\n                (click)=\"displayModalForCell(tableProps.ROW, rowIndex)\"\n                [appMath]=\"rowLabel\"\n              >\n                {{getHeaderText(rowLabel)}}\n              </button>\n              <aa-trash-can\n                *ngIf=\"showTrashIconForRows()\"\n                (delete)=\"onDelete(rowIndex, false)\"\n              >\n              </aa-trash-can>\n            </td>\n            <td\n              class=\"col-wrapper\"\n              *ngFor=\"let columnLabel of columnLabels; let colIndex = index\"\n            >\n              <div class=\"form-check\" *ngIf=\"!selectMultiplePerRow\">\n                <input class=\"form-check-input air-matrix-radio-and-checkbox-button\" type=\"radio\" \n                [checked]=\"answers[rowIndex][0] === colIndex ? true : false\"\n                (change)=\"onSelectingAnswer(rowIndex, colIndex)\">\n              </div>\n              <div class=\"form-check\" *ngIf=\"selectMultiplePerRow\">\n                <input class=\"form-check-input air-matrix-radio-and-checkbox-button\" type=\"checkbox\" \n                      [checked]=\"onCheckSelected(rowIndex, colIndex)\"\n                      (change)=\"onSelectingAnswer(rowIndex, colIndex)\">\n              </div>\n            </td>\n          </tr>\n        </tbody>\n      </table>\n    </div>\n    <button\n      *ngIf=\"canAddRow()\"\n      type=\"button\"\n      class=\"btn btn-tertiary add-row\"\n      #addRowButton\n      #rowPopover=\"ngbPopover\"\n      placement=\"right\"\n      [ngbPopover]=\"rowPopoverRef\"\n      panelClass=\"row-column-popover\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      (hidden)=\"popoverButtonFocus && closePopover(addRowButton)\"\n      translate\n    >\n      + Add row\n    </button>\n\n    <ng-template #rowPopoverRef>\n      <div\n        appFocusTrap\n        class=\"popover-content\"\n        (keydown.escape)=\"popoverButtonFocus = true\"\n      >\n        <div translate>How many rows?</div>\n        <div class=\"row-col-input\">\n          <div>\n            <input\n              #inptAddRows\n              type=\"number\"\n              class=\"form-control\"\n              [value]=\"rowsCount\"\n              [class.is-invalid]=\"maxRowError\"\n              (change)=\"onSetValue(inptAddRows.value, false)\"\n              (blur)=\"onFocusOut(inptAddRows.value, false)\"\n              (keydown)=\"sanitizeInput($event, false, false)\"\n            />\n\n            <div class=\"form-text\" [class.text-danger]=\"maxRowError\">\n              Max: 10 rows\n            </div>\n          </div>\n          <div>\n            <button\n              class=\"btn btn-primary add-button\"\n              type=\"button\"\n              [ngClass]=\"{ 'error-class-row': maxRowError }\"\n              (click)=\"popoverButtonFocus = true; onAddingRows(true)\"\n              translate\n            >\n              Add\n            </button>\n          </div>\n        </div>\n      </div>\n    </ng-template>\n\n    <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n  </div>\n</div>\n\n<aa-choice-matrix-rows-cols-cell\n  (notifyIsModalOpen)=\"onModalStatusChange($event)\"\n  (updateRowLabel)=\"updateRowLabel.emit($event)\"\n  (updateColumnLabel)=\"updateColumnLabel.emit($event)\"\n  [cellData]=\"cellData\"\n  [rowOrColIndex]=\"rowOrColIndex\"\n  [tableProperty]=\"tableProperty\"\n  [displayModal]=\"displayModal\"\n  [wirisMathUrl]=\"wirisMathUrl\">\n</aa-choice-matrix-rows-cols-cell>\n", styles: [".choice-matrix-header{display:inline-block;margin-right:20px;margin-bottom:0;position:relative;top:20px}.option-container{display:inline-block;border-radius:5px}.option-container .add-column-wrapper{text-align:right;min-height:25px}.option-container .col-wrapper{border:1px solid #ccc;padding:12px 5px 12px 12px;text-align:center}.option-container .col-wrapper mhe-checkbox,.option-container .col-wrapper mhe-radio-button{position:relative;bottom:6px}.option-container .ahe-ui-radio,.option-container .ahe-ui-checkbox{position:relative;top:-6px}.option-container .trash-can{position:relative;top:32px}.error-class{border:1px solid #b0181e}.add-column{margin-bottom:5px}.add-row{margin-top:5px}mhe-editor{width:75%;float:left}.popover{display:block;top:585px;left:98.3594px}.red-error{color:#b11111}.red-border{border-color:#b11111}.popoverInput{width:80px;height:38px}.popover-wrapper{padding:25px}.row-col-input{display:flex;flex-direction:row;margin:10px 0}.add-button{margin-left:5px}.column-row-help-text{margin-bottom:10px}.air-matrix-radio-and-checkbox-button{float:none}.header-content{border:none;background:none;text-align:center}.header-content:focus{outline:2px auto #017d92}\n"] }]
        }], ctorParameters: function () { return [{ type: ChoiceMatrixValidationService }, { type: SanitizeInputService }]; }, propDecorators: { selectMultiplePerRow: [{
                type: Input
            }], rowLabels: [{
                type: Input
            }], columnLabels: [{
                type: Input
            }], answers: [{
                type: Input
            }], wirisMathUrl: [{
                type: Input
            }], addingColumn: [{
                type: Output
            }], addingRow: [{
                type: Output
            }], updateRowLabel: [{
                type: Output
            }], updateColumnLabel: [{
                type: Output
            }], deletingRow: [{
                type: Output
            }], deletingColumn: [{
                type: Output
            }], selectingAnswer: [{
                type: Output
            }], column_popover: [{
                type: ViewChild,
                args: ['columnPopover']
            }], row_popover: [{
                type: ViewChild,
                args: ['rowPopover']
            }], tableCells: [{
                type: ViewChildren,
                args: ['tableCell']
            }] } });

class ChoiceMatrixOptionsComponent {
    constructor(choiceMatrixValidationService) {
        this.choiceMatrixValidationService = choiceMatrixValidationService;
        this.toggleMultipleResponses = new EventEmitter();
        this.addingColumn = new EventEmitter();
        this.addingRow = new EventEmitter();
        this.updateRowLabel = new EventEmitter();
        this.updateColumnLabel = new EventEmitter();
        this.deletingRow = new EventEmitter();
        this.deletingColumn = new EventEmitter();
        this.selectingAnswer = new EventEmitter();
        this.openToggleMultipleAnswerModal = false;
    }
    onToggleMultipleResponses() {
        this.toggleMultipleResponses.emit(this.selectMultiplePerRow);
        this.choiceMatrixValidationService.resetErrors();
    }
    onUpdateColumnLabel({ value, index }) {
        this.updateColumnLabel.emit({ value, index });
    }
    onUpdateRowLabel({ value, index }) {
        this.updateRowLabel.emit({ value, index });
    }
    onAddingRow({ noOfRows }) {
        this.addingRow.emit({ noOfRows });
    }
    onAddingColumn({ noOfColumns }) {
        this.addingColumn.emit({ noOfColumns });
    }
    onDeletingRow(rowIndex) {
        this.deletingRow.emit(rowIndex);
    }
    onDeletingColumn(rowIndex) {
        this.deletingColumn.emit(rowIndex);
    }
    onSelectingAnswer({ rowIndex, columnIndex }) {
        this.selectingAnswer.emit({ rowIndex, columnIndex });
    }
    openModal() {
        this.openToggleMultipleAnswerModal = true;
    }
    changeAnswerSelection() {
        const hasAnswers = this.answers.some((answer) => answer.length > 0);
        return hasAnswers ? this.openModal() : this.onConfirmAction();
    }
    onConfirmAction() {
        this.selectMultiplePerRow = !this.selectMultiplePerRow;
        this.onToggleMultipleResponses();
        this.closeToggleMultipleAnswerModal();
    }
    closeToggleMultipleAnswerModal() {
        this.openToggleMultipleAnswerModal = false;
    }
}
ChoiceMatrixOptionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixOptionsComponent, deps: [{ token: ChoiceMatrixValidationService }], target: i0.ɵɵFactoryTarget.Component });
ChoiceMatrixOptionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ChoiceMatrixOptionsComponent, selector: "aa-choice-matrix-options", inputs: { selectMultiplePerRow: "selectMultiplePerRow", rowLabels: "rowLabels", columnLabels: "columnLabels", answers: "answers", wirisMathUrl: "wirisMathUrl" }, outputs: { toggleMultipleResponses: "toggleMultipleResponses", addingColumn: "addingColumn", addingRow: "addingRow", updateRowLabel: "updateRowLabel", updateColumnLabel: "updateColumnLabel", deletingRow: "deletingRow", deletingColumn: "deletingColumn", selectingAnswer: "selectingAnswer" }, ngImport: i0, template: "<h2 class=\"answer-options\"\n    translate>Answer options</h2>\n<div>\n  <div class=\"form-check\" #multipleAnswerCheckbox>\n    <input class=\"form-check-input\" type=\"checkbox\" id=\"allow-edit-text\" [checked]=\"selectMultiplePerRow\" (change)=\"changeAnswerSelection()\">\n    <label class=\"form-check-label\" for=\"allow-edit-text\" translate>Allow multiple responses per row</label>\n  </div>\n</div>\n<air-modal  [isOpen]=\"openToggleMultipleAnswerModal\"\n            [titleText]=\"'Continue?'\"\n            [confirmButtonText]=\"'Yes, proceed'\"\n            (cancel)=\"closeToggleMultipleAnswerModal()\"\n            (close)=\"closeToggleMultipleAnswerModal()\"\n            (confirm)=\"onConfirmAction()\">\n  <div>This will clear all correct answers. Do you wish to proceed?</div>\n</air-modal>\n\n<aa-choice-matrix-rows-cols [selectMultiplePerRow]=\"selectMultiplePerRow\"\n                            [rowLabels]=\"rowLabels\"\n                            [columnLabels]=\"columnLabels\"\n                            [answers]=\"answers\"\n                            [wirisMathUrl]=\"wirisMathUrl\"\n                            (updateColumnLabel)=\"onUpdateColumnLabel($event)\"\n                            (updateRowLabel)=\"onUpdateRowLabel($event)\"\n                            (addingColumn)=\"onAddingColumn($event)\"\n                            (addingRow)=\"onAddingRow($event)\"\n                            (deletingRow)=\"onDeletingRow($event)\"\n                            (deletingColumn)=\"onDeletingColumn($event)\"\n                            (selectingAnswer)=\"onSelectingAnswer($event)\">\n</aa-choice-matrix-rows-cols>\n", styles: [".answer-options{display:inline-block;margin-top:35px}.checkbox-container{position:relative;left:-12px}.ahe-ui-checkbox{font-weight:400}\n"], dependencies: [{ kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: ChoiceMatrixRowsColsComponent, selector: "aa-choice-matrix-rows-cols", inputs: ["selectMultiplePerRow", "rowLabels", "columnLabels", "answers", "wirisMathUrl"], outputs: ["addingColumn", "addingRow", "updateRowLabel", "updateColumnLabel", "deletingRow", "deletingColumn", "selectingAnswer"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ChoiceMatrixOptionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-choice-matrix-options', template: "<h2 class=\"answer-options\"\n    translate>Answer options</h2>\n<div>\n  <div class=\"form-check\" #multipleAnswerCheckbox>\n    <input class=\"form-check-input\" type=\"checkbox\" id=\"allow-edit-text\" [checked]=\"selectMultiplePerRow\" (change)=\"changeAnswerSelection()\">\n    <label class=\"form-check-label\" for=\"allow-edit-text\" translate>Allow multiple responses per row</label>\n  </div>\n</div>\n<air-modal  [isOpen]=\"openToggleMultipleAnswerModal\"\n            [titleText]=\"'Continue?'\"\n            [confirmButtonText]=\"'Yes, proceed'\"\n            (cancel)=\"closeToggleMultipleAnswerModal()\"\n            (close)=\"closeToggleMultipleAnswerModal()\"\n            (confirm)=\"onConfirmAction()\">\n  <div>This will clear all correct answers. Do you wish to proceed?</div>\n</air-modal>\n\n<aa-choice-matrix-rows-cols [selectMultiplePerRow]=\"selectMultiplePerRow\"\n                            [rowLabels]=\"rowLabels\"\n                            [columnLabels]=\"columnLabels\"\n                            [answers]=\"answers\"\n                            [wirisMathUrl]=\"wirisMathUrl\"\n                            (updateColumnLabel)=\"onUpdateColumnLabel($event)\"\n                            (updateRowLabel)=\"onUpdateRowLabel($event)\"\n                            (addingColumn)=\"onAddingColumn($event)\"\n                            (addingRow)=\"onAddingRow($event)\"\n                            (deletingRow)=\"onDeletingRow($event)\"\n                            (deletingColumn)=\"onDeletingColumn($event)\"\n                            (selectingAnswer)=\"onSelectingAnswer($event)\">\n</aa-choice-matrix-rows-cols>\n", styles: [".answer-options{display:inline-block;margin-top:35px}.checkbox-container{position:relative;left:-12px}.ahe-ui-checkbox{font-weight:400}\n"] }]
        }], ctorParameters: function () { return [{ type: ChoiceMatrixValidationService }]; }, propDecorators: { selectMultiplePerRow: [{
                type: Input
            }], rowLabels: [{
                type: Input
            }], columnLabels: [{
                type: Input
            }], answers: [{
                type: Input
            }], wirisMathUrl: [{
                type: Input
            }], toggleMultipleResponses: [{
                type: Output
            }], addingColumn: [{
                type: Output
            }], addingRow: [{
                type: Output
            }], updateRowLabel: [{
                type: Output
            }], updateColumnLabel: [{
                type: Output
            }], deletingRow: [{
                type: Output
            }], deletingColumn: [{
                type: Output
            }], selectingAnswer: [{
                type: Output
            }] } });

class AirChoiceMatrixComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirChoiceMatrix, LoadAirItemAction$c, ResetStoreAction$c);
        this.store = store;
        this.pureAirChoiceMatrix$ = this.store.select(getPureAirChoiceMatrix);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.airConfig$ = this.store.select(getConfigState);
        this.airChoiceMatrix$ = store.select(getAirChoiceMatrix);
    }
    onToggleMultipleResponses(checked) {
        this.store.dispatch(new ToggleMultipleResponsesAction(checked));
    }
    onAddingRow({ noOfRows }) {
        this.store.dispatch(new AddRowAction({ noOfRows }));
    }
    onAddingColumn({ noOfColumns }) {
        this.store.dispatch(new AddColumnAction({ noOfColumns }));
    }
    onUpdateRowLabel({ value, index }) {
        this.store.dispatch(new UpateRowLabelAction({ value, index }));
    }
    onUpdateColumnLabel({ value, index }) {
        this.store.dispatch(new UpateColumnLabelAction({ value, index }));
    }
    onDeletingRow(rowIndex) {
        this.store.dispatch(new DeleteRowAction(rowIndex));
    }
    onDeletingColumn(columnIndex) {
        this.store.dispatch(new DeleteColumnAction(columnIndex));
    }
    onSelectingAnswer({ rowIndex, columnIndex }) {
        this.store.dispatch(new SelectAnswerAction({ rowIndex, columnIndex }));
    }
}
AirChoiceMatrixComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirChoiceMatrixComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirChoiceMatrixComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirChoiceMatrixComponent, selector: "aa-air-choice-matrix", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-choice-matrix-options [selectMultiplePerRow]=\"(airChoiceMatrix$ | async)?.selectMultiplePerRow\"\n                          [rowLabels]=\"(airChoiceMatrix$ | async)?.rowLabels\"\n                          [columnLabels]=\"(airChoiceMatrix$ | async)?.columnLabels\"\n                          [answers]=\"(airChoiceMatrix$ | async)?.answers\"\n                          [wirisMathUrl]=\"(airConfig$ | async)?.wirisMathUrl\"\n                          (toggleMultipleResponses)=\"onToggleMultipleResponses($event)\"\n                          (updateColumnLabel)=\"onUpdateColumnLabel($event)\"\n                          (updateRowLabel)=\"onUpdateRowLabel($event)\"\n                          (addingRow)=\"onAddingRow($event)\"\n                          (addingColumn)=\"onAddingColumn($event)\"\n                          (deletingRow)=\"onDeletingRow($event)\"\n                          (deletingColumn)=\"onDeletingColumn($event)\"\n                          (selectingAnswer)=\"onSelectingAnswer($event)\">\n</aa-choice-matrix-options>\n", dependencies: [{ kind: "component", type: ChoiceMatrixOptionsComponent, selector: "aa-choice-matrix-options", inputs: ["selectMultiplePerRow", "rowLabels", "columnLabels", "answers", "wirisMathUrl"], outputs: ["toggleMultipleResponses", "addingColumn", "addingRow", "updateRowLabel", "updateColumnLabel", "deletingRow", "deletingColumn", "selectingAnswer"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirChoiceMatrixComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-choice-matrix', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-choice-matrix-options [selectMultiplePerRow]=\"(airChoiceMatrix$ | async)?.selectMultiplePerRow\"\n                          [rowLabels]=\"(airChoiceMatrix$ | async)?.rowLabels\"\n                          [columnLabels]=\"(airChoiceMatrix$ | async)?.columnLabels\"\n                          [answers]=\"(airChoiceMatrix$ | async)?.answers\"\n                          [wirisMathUrl]=\"(airConfig$ | async)?.wirisMathUrl\"\n                          (toggleMultipleResponses)=\"onToggleMultipleResponses($event)\"\n                          (updateColumnLabel)=\"onUpdateColumnLabel($event)\"\n                          (updateRowLabel)=\"onUpdateRowLabel($event)\"\n                          (addingRow)=\"onAddingRow($event)\"\n                          (addingColumn)=\"onAddingColumn($event)\"\n                          (deletingRow)=\"onDeletingRow($event)\"\n                          (deletingColumn)=\"onDeletingColumn($event)\"\n                          (selectingAnswer)=\"onSelectingAnswer($event)\">\n</aa-choice-matrix-options>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ClozeValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirCloze$ = this.store.select(getPureAirCloze);
        this.airCloze$ = this.store.select(getAirCloze);
        this.choiceError = '';
        this.answerError = '';
    }
    validate() {
        let isValid;
        this.pureAirCloze$.pipe(take(1)).subscribe((pureAirCloze) => {
            this.airCloze$.pipe(take(1)).subscribe((airCloze) => {
                const promptError = !!this.promptValidationService.validatePrompt(pureAirCloze.prompt);
                const minBlankError = !!this.promptValidationService.validateMinBlankCount(pureAirCloze.prompt);
                const maxBlankError = !!this.promptValidationService.validateMaxBlankCount(airCloze.tooManyAnswers);
                const correctAnswerError = !!this.validateAnswer(pureAirCloze.responses, pureAirCloze.answers);
                isValid = !(promptError || minBlankError || maxBlankError || correctAnswerError);
            });
        });
        return isValid;
    }
    getInvalidResponses(responses) {
        return responses.filter((response) => {
            return response.choices.filter((choice) => choice.value.trim()).length < 2;
        });
    }
    validateAnswer(responses, answers) {
        this.resetAnswerError();
        if (this.getInvalidResponses(responses).length) {
            this.choiceError = E_CLOZE_MIN_CHOICES;
        }
        if (answers.length < responses.length) {
            this.answerError = E_TOO_FEW_ANSWERS;
        }
        return this.answerError || this.choiceError;
    }
    validateAnswerOnFocusOut(responses, answers) {
        if (!this.getInvalidResponses(responses).length) {
            this.choiceError = '';
        }
        if (answers.length === responses.length) {
            this.answerError = '';
        }
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetAnswerError();
    }
    resetAnswerError() {
        this.choiceError = '';
        this.answerError = '';
    }
    getAnswerError() {
        const error = this.choiceError ? [this.choiceError] : [];
        return this.answerError ? error.concat(this.answerError) : error;
    }
}
ClozeValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClozeValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
ClozeValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClozeValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClozeValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class ClozeChoicesComponent {
    constructor(clozeValidationService, configService) {
        this.clozeValidationService = clozeValidationService;
        this.configService = configService;
        this.updateChoiceContent = new EventEmitter();
        this.addChoice = new EventEmitter();
        this.deleteChoice = new EventEmitter();
        this.updateCorrectAnswer = new EventEmitter();
        this.toggleChoiceShuffling = new EventEmitter();
        this.answerChoiceLimit = ANSWER_CHOICE_LIMIT_CLOZE;
        this.allowValidation = false;
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    onUpdateChoiceContent(choice, response, content) {
        this.updateChoiceContent.emit({ choice, response, content: content });
    }
    trackResponse(index, response) {
        return response.key;
    }
    trackKey(index, choice) {
        return choice.key;
    }
    canDeleteChoice(choices, choice) {
        return choices.length > 2;
    }
    canAddChoice(choices) {
        return choices.length < 20;
    }
    onDeleteChoice(choice, response) {
        this.deleteChoice.emit({ choice, response });
    }
    onUpdateCorrectAnswer(choiceKey, responseKey, value) {
        this.updateCorrectAnswer.emit({ choiceKey, responseKey, value });
        setTimeout(() => {
            this.clozeValidationService.validateAnswerOnFocusOut(this.responses, this.answers);
        });
    }
    isCorrectAnswer(response, choice) {
        return (this.answers.find((answer) => {
            return response.key === answer.response && choice.key === answer.choice;
        }) != null);
    }
    getAnswerError() {
        return (this.allowValidation && !!this.responses.length && this.clozeValidationService.getAnswerError()) || [];
    }
    onEditorFocusOut() {
        this.clozeValidationService.validateAnswerOnFocusOut(this.responses, this.answers);
    }
    toggleShuffle() {
        this.toggleChoiceShuffling.emit();
    }
}
ClozeChoicesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClozeChoicesComponent, deps: [{ token: ClozeValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
ClozeChoicesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ClozeChoicesComponent, selector: "aa-cloze-choices", inputs: { responses: "responses", answers: "answers", shuffle: "shuffle" }, outputs: { updateChoiceContent: "updateChoiceContent", addChoice: "addChoice", deleteChoice: "deleteChoice", updateCorrectAnswer: "updateCorrectAnswer", toggleChoiceShuffling: "toggleChoiceShuffling" }, ngImport: i0, template: "<div class=\"answer-choices-container\">\n  <div class=\"row\" *ngIf=\"responses.length\">\n    <div class=\"col\">\n      <h2 translate>Answers choices *</h2>\n    </div>\n\n    <div class=\"col-auto ms-auto\">\n      <div class=\"toggle-shuffle\">\n        <div class=\"form-check form-switch\">\n          <input\n            class=\"form-check-input\"\n            type=\"checkbox\"\n            [checked]=\"shuffle\"\n            id=\"shuffle-toggle-id\"\n            (change)=\"toggleShuffle()\"\n          />\n          <label class=\"form-check-label\" for=\"shuffle-toggle-id\">Shuffle</label>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <div\n    class=\"answer-wrapper\"\n    [ngClass]=\"{ 'answer-error': getAnswerError()?.length }\"\n    *ngIf=\"responses.length\"\n  >\n    <div\n      class=\"answer-choices\"\n      *ngFor=\"\n        let response of responses;\n        let key = index;\n        trackBy: trackResponse\n      \"\n    >\n      <div class=\"answer-sub-heading\" translate>Blank</div>\n      <div class=\"answer-sub-heading\">{{ key + 1 }}</div>\n      <div class=\"answer-sub-heading\" translate>answer choices</div>\n\n      <div\n        class=\"answer-choice row\"\n        *ngFor=\"\n          let choice of response.choices;\n          let choiceKey = index;\n          trackBy: trackKey\n        \"\n      >\n        <button\n          type=\"button\"\n          class=\"col-1 answer-selector\"\n          (click)=\"\n            onUpdateCorrectAnswer(choice.key, response.key, choice.value)\n          \"\n        >\n          <span\n            class=\"glyphicon glyphicon-ok\"\n            *ngIf=\"isCorrectAnswer(response, choice)\"\n          ></span>\n          <span\n            class=\"glyphicon glyphicon-remove\"\n            *ngIf=\"!isCorrectAnswer(response, choice)\"\n          ></span>\n        </button>\n\n        <div class=\"col-10 d-flex\">\n          <mhe-editor\n            class=\"option-editor w-100\"\n            [maxCharacter]=\"answerChoiceLimit\"\n            [content]=\"choice.value\"\n            [useRichText]=\"false\"\n            [showResize]=\"false\"\n            (editorFocusOut)=\"onEditorFocusOut()\"\n            (contentChange)=\"onUpdateChoiceContent(choice, response, $event)\"\n          ></mhe-editor>\n\n          <aa-trash-can\n            *ngIf=\"canDeleteChoice(response.choices, choice)\"\n            class=\"delete-choice\"\n            (delete)=\"onDeleteChoice(choice, response)\"\n          ></aa-trash-can>\n        </div>\n      </div>\n\n      <button\n        type=\"button\"\n        class=\"add-answer\"\n        *ngIf=\"canAddChoice(response.choices)\"\n        (click)=\"addChoice.emit(response)\"\n      >\n        <span translate>+ Add choice</span>\n      </button>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n</div>\n", styles: [".answer-choices-container{width:100%;display:inline-block}.answer-choices-container .answer-sub-heading{display:inline-block;margin-bottom:10px;margin-right:3px;font-size:15px}.answer-choices-container .answer-wrapper{background:#fbfbfb;border:1px solid #a9a9a9}.answer-choices-container .answer-wrapper.answer-error{border:1px solid #b0181e}.answer-choices-container .answer-choices{display:inline-block;width:50%;vertical-align:top;margin:1.2em 0;padding-left:3em}.answer-choices-container .answer-choices:nth-child(2n){border-left:1px solid #a9a9a9}.answer-choices-container .answer-choice{margin-top:6px}.answer-choices-container .answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px;margin-top:1.5em}.answer-choices-container .delete-choice{flex:0 0 auto;align-self:flex-end}.answer-choices-container .add-answer{background:transparent;border:none}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ClozeChoicesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-cloze-choices', template: "<div class=\"answer-choices-container\">\n  <div class=\"row\" *ngIf=\"responses.length\">\n    <div class=\"col\">\n      <h2 translate>Answers choices *</h2>\n    </div>\n\n    <div class=\"col-auto ms-auto\">\n      <div class=\"toggle-shuffle\">\n        <div class=\"form-check form-switch\">\n          <input\n            class=\"form-check-input\"\n            type=\"checkbox\"\n            [checked]=\"shuffle\"\n            id=\"shuffle-toggle-id\"\n            (change)=\"toggleShuffle()\"\n          />\n          <label class=\"form-check-label\" for=\"shuffle-toggle-id\">Shuffle</label>\n        </div>\n      </div>\n    </div>\n  </div>\n\n  <div\n    class=\"answer-wrapper\"\n    [ngClass]=\"{ 'answer-error': getAnswerError()?.length }\"\n    *ngIf=\"responses.length\"\n  >\n    <div\n      class=\"answer-choices\"\n      *ngFor=\"\n        let response of responses;\n        let key = index;\n        trackBy: trackResponse\n      \"\n    >\n      <div class=\"answer-sub-heading\" translate>Blank</div>\n      <div class=\"answer-sub-heading\">{{ key + 1 }}</div>\n      <div class=\"answer-sub-heading\" translate>answer choices</div>\n\n      <div\n        class=\"answer-choice row\"\n        *ngFor=\"\n          let choice of response.choices;\n          let choiceKey = index;\n          trackBy: trackKey\n        \"\n      >\n        <button\n          type=\"button\"\n          class=\"col-1 answer-selector\"\n          (click)=\"\n            onUpdateCorrectAnswer(choice.key, response.key, choice.value)\n          \"\n        >\n          <span\n            class=\"glyphicon glyphicon-ok\"\n            *ngIf=\"isCorrectAnswer(response, choice)\"\n          ></span>\n          <span\n            class=\"glyphicon glyphicon-remove\"\n            *ngIf=\"!isCorrectAnswer(response, choice)\"\n          ></span>\n        </button>\n\n        <div class=\"col-10 d-flex\">\n          <mhe-editor\n            class=\"option-editor w-100\"\n            [maxCharacter]=\"answerChoiceLimit\"\n            [content]=\"choice.value\"\n            [useRichText]=\"false\"\n            [showResize]=\"false\"\n            (editorFocusOut)=\"onEditorFocusOut()\"\n            (contentChange)=\"onUpdateChoiceContent(choice, response, $event)\"\n          ></mhe-editor>\n\n          <aa-trash-can\n            *ngIf=\"canDeleteChoice(response.choices, choice)\"\n            class=\"delete-choice\"\n            (delete)=\"onDeleteChoice(choice, response)\"\n          ></aa-trash-can>\n        </div>\n      </div>\n\n      <button\n        type=\"button\"\n        class=\"add-answer\"\n        *ngIf=\"canAddChoice(response.choices)\"\n        (click)=\"addChoice.emit(response)\"\n      >\n        <span translate>+ Add choice</span>\n      </button>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n</div>\n", styles: [".answer-choices-container{width:100%;display:inline-block}.answer-choices-container .answer-sub-heading{display:inline-block;margin-bottom:10px;margin-right:3px;font-size:15px}.answer-choices-container .answer-wrapper{background:#fbfbfb;border:1px solid #a9a9a9}.answer-choices-container .answer-wrapper.answer-error{border:1px solid #b0181e}.answer-choices-container .answer-choices{display:inline-block;width:50%;vertical-align:top;margin:1.2em 0;padding-left:3em}.answer-choices-container .answer-choices:nth-child(2n){border-left:1px solid #a9a9a9}.answer-choices-container .answer-choice{margin-top:6px}.answer-choices-container .answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px;margin-top:1.5em}.answer-choices-container .delete-choice{flex:0 0 auto;align-self:flex-end}.answer-choices-container .add-answer{background:transparent;border:none}\n"] }]
        }], ctorParameters: function () { return [{ type: ClozeValidationService }, { type: ConfigService }]; }, propDecorators: { updateChoiceContent: [{
                type: Output
            }], addChoice: [{
                type: Output
            }], deleteChoice: [{
                type: Output
            }], updateCorrectAnswer: [{
                type: Output
            }], toggleChoiceShuffling: [{
                type: Output
            }], responses: [{
                type: Input
            }], answers: [{
                type: Input
            }], shuffle: [{
                type: Input
            }] } });

class AirClozeComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirCloze, LoadAirItemAction$f, ResetStoreAction$f);
        this.store = store;
        this.airCloze$ = this.store.select(getAirCloze);
        this.pureAirCloze$ = this.store.select(getPureAirCloze);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
    onUpdateChoiceContent({ choice, response, content }) {
        this.store.dispatch(new UpdateChoiceContentAction({ choice, response, content }));
    }
    onAddChoice(response) {
        this.store.dispatch(new AddChoiceAction$1(response));
    }
    onDeleteChoice({ choice, response }) {
        this.store.dispatch(new DeleteChoiceAction$1({ choice, response }));
    }
    onUpdateCorrectAnswer({ choiceKey, responseKey, value }) {
        this.store.dispatch(new UpdateCorrectAnswerAction$1({ choiceKey, responseKey, value }));
    }
    onToggleChoiceShuffling() {
        this.store.dispatch(new ToggleChoicesShufflingAction());
    }
}
AirClozeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirClozeComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirClozeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirClozeComponent, selector: "aa-air-cloze", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-cloze-choices [responses]=\"(airCloze$ | async)?.responses\"\n                  [answers]=\"(airCloze$ | async)?.answers\"\n                  (addChoice)=\"onAddChoice($event)\"\n                  (deleteChoice)=\"onDeleteChoice($event)\"\n                  (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                  (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n                  (toggleChoiceShuffling)=\"onToggleChoiceShuffling()\"\n                  [shuffle]=\"(airCloze$ | async)?.randomize\"></aa-cloze-choices>\n", dependencies: [{ kind: "component", type: ClozeChoicesComponent, selector: "aa-cloze-choices", inputs: ["responses", "answers", "shuffle"], outputs: ["updateChoiceContent", "addChoice", "deleteChoice", "updateCorrectAnswer", "toggleChoiceShuffling"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirClozeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-cloze', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-cloze-choices [responses]=\"(airCloze$ | async)?.responses\"\n                  [answers]=\"(airCloze$ | async)?.answers\"\n                  (addChoice)=\"onAddChoice($event)\"\n                  (deleteChoice)=\"onDeleteChoice($event)\"\n                  (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                  (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n                  (toggleChoiceShuffling)=\"onToggleChoiceShuffling()\"\n                  [shuffle]=\"(airCloze$ | async)?.randomize\"></aa-cloze-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class EquationEntryValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirEquationEntry$ = this.store.select(getPureAirEquationEntry);
        this.airAuthoring$ = this.store.select(getAuthoringItem);
        this.correctAnswerError = '';
    }
    validate() {
        let isValid;
        this.pureAirEquationEntry$.pipe(take(1)).subscribe((airEquationEntry) => {
            this.airAuthoring$.pipe(take(1)).subscribe((airAuthoring) => {
                const promptError = !!this.promptValidationService.validatePrompt(airEquationEntry.prompt);
                let correctAnswerError = '';
                correctAnswerError = this.validateCorrectAnswers(airEquationEntry.answers);
                isValid = !(promptError || correctAnswerError);
            });
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetAnswerError();
    }
    resetAnswerError() {
        this.correctAnswerError = '';
    }
    validateCorrectAnswers(answers) {
        this.resetAnswerError();
        const validAnswers = answers.filter((answer) => answer);
        if (validAnswers.length < 1) {
            this.correctAnswerError = E_EE_CORRECT_ANSWER;
        }
        return this.correctAnswerError;
    }
    validateAnswerOnFocusOut(answers) {
        if (answers.length > 0) {
            this.correctAnswerError = '';
        }
    }
    getAnswerError() {
        return this.correctAnswerError ? [this.correctAnswerError] : [];
    }
}
EquationEntryValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquationEntryValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
EquationEntryValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquationEntryValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquationEntryValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class EquationEntryAnswersComponent {
    constructor(equationEntryService, store) {
        this.equationEntryService = equationEntryService;
        this.store = store;
        this.updateAnswer = new EventEmitter();
        this.addAnswer = new EventEmitter();
        this.changeEditorType = new EventEmitter();
        this.checkForNumericEquivalency = new EventEmitter();
        this.deleteAnswer = new EventEmitter();
        this.refreshEditors = false;
        this.airConfig$ = this.store.select(getConfigState);
    }
    isBasicMathEditor() {
        return this.mathEditorType === 'basic';
    }
    onChangingEditorType(type) {
        this.changeEditorType.emit(type);
        this.equationEntryService.resetAnswerError();
    }
    onNumericEquivalencyValueChange(event) {
        if (event.target.checked) {
            this.allowNumericEquivalency = true;
        }
        else {
            this.allowNumericEquivalency = false;
        }
        this.checkForNumericEquivalency.emit(this.allowNumericEquivalency);
    }
    isNumericEquivalencyEnabled() {
        return this.allowNumericEquivalency;
    }
    onDeletingAnswer(index) {
        this.deleteAnswer.emit(index);
        this.refreshEditors = true;
        setTimeout(() => {
            this.refreshEditors = false;
        });
    }
    onAddingAnswer() {
        this.addAnswer.emit();
    }
    onUpdatingAnswer(answer, index) {
        this.updateAnswer.emit({ index, answer });
    }
    trackIndex(index) {
        return index;
    }
    getAnswerError() {
        return this.equationEntryService.getAnswerError();
    }
    onEditorBlur() {
        this.equationEntryService.validateAnswerOnFocusOut(this.answers);
    }
}
EquationEntryAnswersComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquationEntryAnswersComponent, deps: [{ token: EquationEntryValidationService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
EquationEntryAnswersComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: EquationEntryAnswersComponent, selector: "aa-equation-entry-answers", inputs: { answers: "answers", mathEditorType: "mathEditorType", preset: "preset", errors: "errors" }, outputs: { updateAnswer: "updateAnswer", addAnswer: "addAnswer", changeEditorType: "changeEditorType", checkForNumericEquivalency: "checkForNumericEquivalency", deleteAnswer: "deleteAnswer" }, ngImport: i0, template: "<div class=\"editor-option d-flex\" *ngIf=\"preset\">\n  <h2 translate class=\"heading-title me-3\">Preset:</h2>\n  <span>{{ preset }}</span>\n</div>\n\n<div class=\"editor-option d-flex align-items-center\" *ngIf=\"!preset\">\n  <h2 translate class=\"heading-title me-3\">Math editor options</h2>\n  <div class=\"form-check me-2\">\n    <input\n      id=\"basicMath\" type=\"radio\" class=\"form-check-input\"\n      name=\"basicMath\"\n      [checked]=\"isBasicMathEditor()\"\n      (change)=\"onChangingEditorType('basic')\"\n    />\n    <label class=\"form-check-label\" for=\"basicMath\" translate>Basic Math</label>\n  </div>\n  <div class=\"form-check\">\n    <input\n      id=\"fullMath\" type=\"radio\" class=\"form-check-input\"\n      name=\"fullMath\"\n      [checked]=\"!isBasicMathEditor()\"\n      (change)=\"onChangingEditorType('full')\"\n    />\n    <label class=\"form-check-label\" for=\"fullMath\" translate>Full Math</label>\n  </div>\n</div>\n\n<div *ngIf=\"!refreshEditors\">\n  <h2 translate class=\"answer-heading\">\n    {{ answers.length > 1 ? \"Correct answers\" : \"Correct answer\" }}\n  </h2>\n\n  <div\n    class=\"correct-answer-container mb-4\"\n    [ngClass]=\"{ 'error-class': getAnswerError().length }\"\n  >\n    <div\n      *ngFor=\"\n        let answer of answers;\n        let answerIndex = index;\n        trackBy: trackIndex\n      \"\n      class=\"row mb-3\"\n    >\n      <div class=\"col\">\n        <aa-math-editor\n          [ngClass]=\"{ 'math-editor': answers.length > 1 }\"\n          (updateAnswer)=\"onUpdatingAnswer($event, answerIndex)\"\n          (editorBlur)=\"onEditorBlur()\"\n          [editorId]=\"'equation' + answerIndex\"\n          [mathml]=\"answer\"\n          [answers]=\"answers\"\n          [mathEditorType]=\"mathEditorType\"\n          [wirisMathUrl]=\"(airConfig$ | async).wirisMathUrl\"\n          [preset]=\"preset\"\n        ></aa-math-editor>\n      </div>\n\n      <div class=\"col-auto align-self-end\">\n        <aa-trash-can\n          *ngIf=\"answers.length > 1\"\n          class=\"delete-answer\"\n          (delete)=\"onDeletingAnswer(answerIndex)\"\n        ></aa-trash-can>\n      </div>\n    </div>\n\n    <button\n      *ngIf=\"answers.length < 20\"\n      type=\"button\"\n      class=\"add-answer\"\n      (click)=\"onAddingAnswer()\"\n    >\n      <span translate>+ Add correct answer</span>\n    </button>\n  </div>\n\n  <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n\n  <div class=\"answer-format row\" *ngIf=\"!preset\">\n    <div class=\"col-auto\">\n      <h2 translate class=\"heading-title me-3\">Answer formatting</h2>\n    </div>\n\n    <div class=\"col-auto ms-4\">\n      <div class=\"form-check\">\n        <input\n          type=\"checkbox\"\n          id=\"cbAllowEquivalency\"\n          class=\"form-check-input\"\n          (change)=\"onNumericEquivalencyValueChange($event)\"\n          [checked]=\"isNumericEquivalencyEnabled()\"\n        />\n        <label translate for=\"cbAllowEquivalency\" class=\"form-check-label\">\n          Numeric equivalency\n        </label>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".scoring-option{float:left;margin-right:50px}.heading-title{float:left}.editor-option{padding:5px 0}.ahe-ui-radio{margin-top:10px;font-weight:400}.answer-heading{margin-bottom:0}.add-answer{padding:0;background:transparent;border:none}.math-editor{display:block}.error-class{border:1px solid #b0181e;border-radius:6px;padding:25px;background:#fdfbfc}.answer-format{align-items:center}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: MathEditorComponent, selector: "aa-math-editor", inputs: ["editorId", "mathml", "mathEditorType", "answers", "wirisMathUrl", "preset"], outputs: ["updateAnswer", "editorBlur"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EquationEntryAnswersComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-equation-entry-answers', template: "<div class=\"editor-option d-flex\" *ngIf=\"preset\">\n  <h2 translate class=\"heading-title me-3\">Preset:</h2>\n  <span>{{ preset }}</span>\n</div>\n\n<div class=\"editor-option d-flex align-items-center\" *ngIf=\"!preset\">\n  <h2 translate class=\"heading-title me-3\">Math editor options</h2>\n  <div class=\"form-check me-2\">\n    <input\n      id=\"basicMath\" type=\"radio\" class=\"form-check-input\"\n      name=\"basicMath\"\n      [checked]=\"isBasicMathEditor()\"\n      (change)=\"onChangingEditorType('basic')\"\n    />\n    <label class=\"form-check-label\" for=\"basicMath\" translate>Basic Math</label>\n  </div>\n  <div class=\"form-check\">\n    <input\n      id=\"fullMath\" type=\"radio\" class=\"form-check-input\"\n      name=\"fullMath\"\n      [checked]=\"!isBasicMathEditor()\"\n      (change)=\"onChangingEditorType('full')\"\n    />\n    <label class=\"form-check-label\" for=\"fullMath\" translate>Full Math</label>\n  </div>\n</div>\n\n<div *ngIf=\"!refreshEditors\">\n  <h2 translate class=\"answer-heading\">\n    {{ answers.length > 1 ? \"Correct answers\" : \"Correct answer\" }}\n  </h2>\n\n  <div\n    class=\"correct-answer-container mb-4\"\n    [ngClass]=\"{ 'error-class': getAnswerError().length }\"\n  >\n    <div\n      *ngFor=\"\n        let answer of answers;\n        let answerIndex = index;\n        trackBy: trackIndex\n      \"\n      class=\"row mb-3\"\n    >\n      <div class=\"col\">\n        <aa-math-editor\n          [ngClass]=\"{ 'math-editor': answers.length > 1 }\"\n          (updateAnswer)=\"onUpdatingAnswer($event, answerIndex)\"\n          (editorBlur)=\"onEditorBlur()\"\n          [editorId]=\"'equation' + answerIndex\"\n          [mathml]=\"answer\"\n          [answers]=\"answers\"\n          [mathEditorType]=\"mathEditorType\"\n          [wirisMathUrl]=\"(airConfig$ | async).wirisMathUrl\"\n          [preset]=\"preset\"\n        ></aa-math-editor>\n      </div>\n\n      <div class=\"col-auto align-self-end\">\n        <aa-trash-can\n          *ngIf=\"answers.length > 1\"\n          class=\"delete-answer\"\n          (delete)=\"onDeletingAnswer(answerIndex)\"\n        ></aa-trash-can>\n      </div>\n    </div>\n\n    <button\n      *ngIf=\"answers.length < 20\"\n      type=\"button\"\n      class=\"add-answer\"\n      (click)=\"onAddingAnswer()\"\n    >\n      <span translate>+ Add correct answer</span>\n    </button>\n  </div>\n\n  <aa-inline-error [errors]=\"getAnswerError()\"></aa-inline-error>\n\n  <div class=\"answer-format row\" *ngIf=\"!preset\">\n    <div class=\"col-auto\">\n      <h2 translate class=\"heading-title me-3\">Answer formatting</h2>\n    </div>\n\n    <div class=\"col-auto ms-4\">\n      <div class=\"form-check\">\n        <input\n          type=\"checkbox\"\n          id=\"cbAllowEquivalency\"\n          class=\"form-check-input\"\n          (change)=\"onNumericEquivalencyValueChange($event)\"\n          [checked]=\"isNumericEquivalencyEnabled()\"\n        />\n        <label translate for=\"cbAllowEquivalency\" class=\"form-check-label\">\n          Numeric equivalency\n        </label>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".scoring-option{float:left;margin-right:50px}.heading-title{float:left}.editor-option{padding:5px 0}.ahe-ui-radio{margin-top:10px;font-weight:400}.answer-heading{margin-bottom:0}.add-answer{padding:0;background:transparent;border:none}.math-editor{display:block}.error-class{border:1px solid #b0181e;border-radius:6px;padding:25px;background:#fdfbfc}.answer-format{align-items:center}\n"] }]
        }], ctorParameters: function () { return [{ type: EquationEntryValidationService }, { type: i1.Store }]; }, propDecorators: { answers: [{
                type: Input
            }], mathEditorType: [{
                type: Input
            }], preset: [{
                type: Input
            }], errors: [{
                type: Input
            }], updateAnswer: [{
                type: Output
            }], addAnswer: [{
                type: Output
            }], changeEditorType: [{
                type: Output
            }], checkForNumericEquivalency: [{
                type: Output
            }], deleteAnswer: [{
                type: Output
            }] } });

class AirEquationEntryComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirEquationEntry, LoadAirItemAction$b, ResetStoreAction$b);
        this.store = store;
        this.airEquationEntry$ = this.store.select(getAirEquationEntry);
        this.pureAirEquationEntry$ = this.store.select(getPureAirEquationEntry);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
    onChangingEditorType(editorType) {
        this.store.dispatch(new UpdateEditorTypeAction(editorType));
    }
    onAddingAnswer() {
        this.store.dispatch(new AddAnswerAction$1());
    }
    onDeletingAnswer(deletingIndex) {
        this.store.dispatch(new DeleteAnswerAction$1(deletingIndex));
    }
    onUpdatingAnswer({ index, answer }) {
        this.store.dispatch(new UpdateAnswerAction$1({ index, answer }));
    }
    onCheckingAllowEquivalency(allowEquivalency) {
        this.store.dispatch(new UpdateAllowEquivalencyAction(allowEquivalency));
    }
}
AirEquationEntryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEquationEntryComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirEquationEntryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirEquationEntryComponent, selector: "aa-air-equation-entry", usesInheritance: true, ngImport: i0, template: "<aa-equation-entry-answers (updateAnswer)=\"onUpdatingAnswer($event)\"\n                           (addAnswer)=\"onAddingAnswer()\"\n                           (changeEditorType)=\"onChangingEditorType($event)\"\n                           (checkForNumericEquivalency)=\"onCheckingAllowEquivalency($event)\"\n                           (deleteAnswer)=\"onDeletingAnswer($event)\"\n                           [mathEditorType]=\"(airEquationEntry$ | async)?.editor\"\n                           [answers]=\"(airEquationEntry$ | async)?.answers\"\n                           [preset]=\"(airEquationEntry$ | async)?.preset\">\n</aa-equation-entry-answers>\n", dependencies: [{ kind: "component", type: EquationEntryAnswersComponent, selector: "aa-equation-entry-answers", inputs: ["answers", "mathEditorType", "preset", "errors"], outputs: ["updateAnswer", "addAnswer", "changeEditorType", "checkForNumericEquivalency", "deleteAnswer"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEquationEntryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-equation-entry', template: "<aa-equation-entry-answers (updateAnswer)=\"onUpdatingAnswer($event)\"\n                           (addAnswer)=\"onAddingAnswer()\"\n                           (changeEditorType)=\"onChangingEditorType($event)\"\n                           (checkForNumericEquivalency)=\"onCheckingAllowEquivalency($event)\"\n                           (deleteAnswer)=\"onDeletingAnswer($event)\"\n                           [mathEditorType]=\"(airEquationEntry$ | async)?.editor\"\n                           [answers]=\"(airEquationEntry$ | async)?.answers\"\n                           [preset]=\"(airEquationEntry$ | async)?.preset\">\n</aa-equation-entry-answers>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class EssayValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirEssay$ = this.store.select(getPureAirEssay);
        this.limitErrors = '';
    }
    validate() {
        let isValid;
        this.pureAirEssay$.pipe(take(1)).subscribe((airEssay) => {
            const limit = airEssay['maxCharacters'] || airEssay['maxWords'];
            const promptError = !!this.promptValidationService.validatePrompt(airEssay.prompt);
            const limitError = !!this.validateInputLimit(limit);
            isValid = !(promptError || limitError);
        });
        return isValid;
    }
    validateInputLimit(value) {
        const hasDecimal = value && value.toString().includes('.');
        if (hasDecimal) {
            this.limitErrors = E_DECIMAL_NOT_ALLOWED;
        }
        else {
            value = Number(value);
            this.limitErrors = value > RESPONSE_TEXT_LIMIT || value <= 0 || !value ? E_ESSAY_SA_LIMIT : '';
        }
        return this.limitErrors;
    }
    resetErrors() {
        this.resetLimitErrors();
        this.promptValidationService.resetErrors();
    }
    resetLimitErrors() {
        this.limitErrors = '';
    }
    getLimitErrors() {
        return this.limitErrors ? [this.limitErrors] : [];
    }
}
EssayValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EssayValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
EssayValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EssayValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EssayValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class ItemEditorTypeComponent {
    constructor() {
        this.editorTypes = [
            { viewValue: 'Standard', value: 'Standard' },
            { viewValue: 'Basic Math', value: 'Basic Math' },
            { viewValue: 'Basic English', value: 'Basic English' },
        ];
        this.selectedItemEditorOptions = this.editorTypes[0];
        this.onChangeEditorType = new EventEmitter();
    }
    ngOnChanges() {
        this.selectedItemEditorOptions = this.editorTypes.filter((editor) => editor.value === this.editorType)[0];
    }
    selectEditor(selectedItemEditorOptions) {
        this.onChangeEditorType.emit(selectedItemEditorOptions.value);
    }
}
ItemEditorTypeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemEditorTypeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ItemEditorTypeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemEditorTypeComponent, selector: "aa-item-editor-type", inputs: { editorType: "editorType" }, outputs: { onChangeEditorType: "onChangeEditorType" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"editorType-wrapper\">\n  <h2 translate class=\"editorType-heading\">Editor</h2>\n  <select\n    class=\"form-select d-inline w-auto\"\n    [ngModel]=\"selectedItemEditorOptions\"\n    (ngModelChange)=\"selectEditor($event)\">\n    <option\n      *ngFor=\"let option of editorTypes\"\n      [ngValue]=\"option\">\n      {{ option.viewValue }}\n    </option>\n  </select>\n</div>\n", styles: [".editorType-wrapper{margin-top:20px}.editorType-wrapper .editorType-heading{margin-right:130px;display:inline-block}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemEditorTypeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-editor-type', template: "<div class=\"editorType-wrapper\">\n  <h2 translate class=\"editorType-heading\">Editor</h2>\n  <select\n    class=\"form-select d-inline w-auto\"\n    [ngModel]=\"selectedItemEditorOptions\"\n    (ngModelChange)=\"selectEditor($event)\">\n    <option\n      *ngFor=\"let option of editorTypes\"\n      [ngValue]=\"option\">\n      {{ option.viewValue }}\n    </option>\n  </select>\n</div>\n", styles: [".editorType-wrapper{margin-top:20px}.editorType-wrapper .editorType-heading{margin-right:130px;display:inline-block}\n"] }]
        }], propDecorators: { onChangeEditorType: [{
                type: Output
            }], editorType: [{
                type: Input
            }] } });

class ItemResponseTextLimitComponent {
    constructor() {
        this.selectCharactersResponseLimit = new EventEmitter();
        this.selectWordsResponseLimit = new EventEmitter();
        this.updateCharactersResponseLimit = new EventEmitter();
        this.updateWordsResponseLimit = new EventEmitter();
        this.limitInputFocusOut = new EventEmitter();
        this.resetLimitErrors = new EventEmitter();
    }
    onFocusOut(value) {
        this.limitInputFocusOut.emit(value);
    }
    changeInput(value) {
        this.responseLimitInWords
            ? this.updateWordsResponseLimit.emit(value)
            : this.updateCharactersResponseLimit.emit(value);
    }
    changeLimitOption(isCharacter) {
        this.resetLimitErrors.emit();
        isCharacter ? this.selectCharactersResponseLimit.emit() : this.selectWordsResponseLimit.emit();
        const focusElement = document.getElementById('input-limit');
        if (!focusElement) {
            return;
        }
        setTimeout(() => focusElement.focus(), 0);
    }
    getValue() {
        return this.maxWords || this.maxCharacters;
    }
}
ItemResponseTextLimitComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemResponseTextLimitComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ItemResponseTextLimitComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemResponseTextLimitComponent, selector: "aa-item-response-text-limit", inputs: { maxCharacters: "maxCharacters", maxWords: "maxWords", responseLimitInWords: "responseLimitInWords", responseLimitInCharacters: "responseLimitInCharacters", limitErrors: "limitErrors" }, outputs: { selectCharactersResponseLimit: "selectCharactersResponseLimit", selectWordsResponseLimit: "selectWordsResponseLimit", updateCharactersResponseLimit: "updateCharactersResponseLimit", updateWordsResponseLimit: "updateWordsResponseLimit", limitInputFocusOut: "limitInputFocusOut", resetLimitErrors: "resetLimitErrors" }, ngImport: i0, template: "<div class=\"limit-container\">\n  <h2 translate>Response text limit *</h2>\n\n  <div class=\"limit-wrapper\">\n    <div class=\"row\">\n      <div class=\"col-sm-3 col-lg-2 label-heading\" translate>\n        Limit Type\n      </div>\n\n      <div class=\"col-sm-9 col-lg-10\">\n        <div class=\"d-flex flex-column\">\n\n          <div class=\"form-check\">\n            <input class=\"form-check-input\" type=\"radio\" value=\"Characters\" id=\"characters-id\" [checked]=\"responseLimitInCharacters\" (change)=\"changeLimitOption(true)\">\n            <label class=\"form-check-label\" for=\"characters-id\">\n              Characters\n            </label>\n          </div>\n\n        <div class=\"form-check\">\n          <input class=\"form-check-input\" type=\"radio\" value=\"Words\" id=\"words-id\" [checked]=\"responseLimitInWords\" (change)=\"changeLimitOption(false)\">\n          <label class=\"form-check-label\" for=\"words-id\">\n            Words\n          </label>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"row count-container\">\n      <div class=\"col-sm-3 col-lg-2\" translate>Limit Count</div>\n\n      <div class=\"col-sm-5 col-lg-4 count-input\">\n        <input\n        id=\"input-limit\"\n        type=\"number\"\n        [value]=\"getValue()\"\n        class=\"form-control\"\n        (input)=\"changeInput($event.target.value)\"\n        (blur)=\"onFocusOut($event.target.value)\"\n      />\n      <div *ngIf=\"limitErrors?.length\" class=\"custom-error\">\n        {{ limitErrors }}\n      </div>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".limit-container{margin-top:10px}.limit-container .limit-wrapper{border:1px solid #a9a9a9;background-color:#fdfbfb;padding:20px;position:relative}.limit-container .limit-wrapper label{width:100%}.limit-container .limit-wrapper .label-heading{margin-top:5px}.limit-container .limit-wrapper .count-container{margin-top:20px}.limit-container .limit-wrapper .count-container .count-input{margin-left:10px}.limit-container .limit-wrapper .count-container .count-input input.form-control{width:22%}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemResponseTextLimitComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-response-text-limit', template: "<div class=\"limit-container\">\n  <h2 translate>Response text limit *</h2>\n\n  <div class=\"limit-wrapper\">\n    <div class=\"row\">\n      <div class=\"col-sm-3 col-lg-2 label-heading\" translate>\n        Limit Type\n      </div>\n\n      <div class=\"col-sm-9 col-lg-10\">\n        <div class=\"d-flex flex-column\">\n\n          <div class=\"form-check\">\n            <input class=\"form-check-input\" type=\"radio\" value=\"Characters\" id=\"characters-id\" [checked]=\"responseLimitInCharacters\" (change)=\"changeLimitOption(true)\">\n            <label class=\"form-check-label\" for=\"characters-id\">\n              Characters\n            </label>\n          </div>\n\n        <div class=\"form-check\">\n          <input class=\"form-check-input\" type=\"radio\" value=\"Words\" id=\"words-id\" [checked]=\"responseLimitInWords\" (change)=\"changeLimitOption(false)\">\n          <label class=\"form-check-label\" for=\"words-id\">\n            Words\n          </label>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"row count-container\">\n      <div class=\"col-sm-3 col-lg-2\" translate>Limit Count</div>\n\n      <div class=\"col-sm-5 col-lg-4 count-input\">\n        <input\n        id=\"input-limit\"\n        type=\"number\"\n        [value]=\"getValue()\"\n        class=\"form-control\"\n        (input)=\"changeInput($event.target.value)\"\n        (blur)=\"onFocusOut($event.target.value)\"\n      />\n      <div *ngIf=\"limitErrors?.length\" class=\"custom-error\">\n        {{ limitErrors }}\n      </div>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".limit-container{margin-top:10px}.limit-container .limit-wrapper{border:1px solid #a9a9a9;background-color:#fdfbfb;padding:20px;position:relative}.limit-container .limit-wrapper label{width:100%}.limit-container .limit-wrapper .label-heading{margin-top:5px}.limit-container .limit-wrapper .count-container{margin-top:20px}.limit-container .limit-wrapper .count-container .count-input{margin-left:10px}.limit-container .limit-wrapper .count-container .count-input input.form-control{width:22%}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}\n"] }]
        }], propDecorators: { maxCharacters: [{
                type: Input
            }], maxWords: [{
                type: Input
            }], responseLimitInWords: [{
                type: Input
            }], responseLimitInCharacters: [{
                type: Input
            }], limitErrors: [{
                type: Input
            }], selectCharactersResponseLimit: [{
                type: Output
            }], selectWordsResponseLimit: [{
                type: Output
            }], updateCharactersResponseLimit: [{
                type: Output
            }], updateWordsResponseLimit: [{
                type: Output
            }], limitInputFocusOut: [{
                type: Output
            }], resetLimitErrors: [{
                type: Output
            }] } });

class AirEssayComponent extends AirBaseComponent {
    constructor(store, essayValidationService, configService) {
        super(store, getPureAirEssay, LoadAirItemAction$k, ResetStoreAction$k);
        this.store = store;
        this.essayValidationService = essayValidationService;
        this.configService = configService;
        this.airEssay$ = this.store.select(getAirEssay);
        this.pureAirEssay$ = this.store.select(getPureAirEssay);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onChangeEditorType(editorType) {
        this.store.dispatch(new UpdateEditorTypeAction$1(editorType));
    }
    selectCharactersResponseLimit() {
        this.store.dispatch(new SelectCharactersResponseLimitAction$1());
    }
    selectWordsResponseLimit() {
        this.store.dispatch(new SelectWordsResponseLimitAction$1());
    }
    updateCharactersResponseLimit(limit) {
        this.store.dispatch(new UpdateCharactersResponseLimitAction$1(Number(limit)));
    }
    updateWordsResponseLimit(limit) {
        this.store.dispatch(new UpdateWordsResponseLimitAction$1(Number(limit)));
    }
    getLimitErrors() {
        return this.allowInlineValidation && this.essayValidationService.getLimitErrors();
    }
    onResetLimitErrors() {
        this.essayValidationService.resetLimitErrors();
    }
    onLimitInputFocusOut(value) {
        this.essayValidationService.validateInputLimit(value);
    }
}
AirEssayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEssayComponent, deps: [{ token: i1.Store }, { token: EssayValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirEssayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirEssayComponent, selector: "aa-air-essay", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-item-response-text-limit (selectCharactersResponseLimit)=\"selectCharactersResponseLimit()\"\n                             (selectWordsResponseLimit)=\"selectWordsResponseLimit()\"\n                             (updateCharactersResponseLimit)=\"updateCharactersResponseLimit($event)\"\n                             (updateWordsResponseLimit)=\"updateWordsResponseLimit($event)\"\n                             (limitInputFocusOut)=\"onLimitInputFocusOut($event)\"\n                             (resetLimitErrors)=\"onResetLimitErrors()\"\n                             [maxCharacters]=\"(airEssay$ | async)?.maxCharacters\"\n                             [maxWords]=\"(airEssay$ | async)?.maxWords\"\n                             [limitErrors]=\"getLimitErrors()\"\n                             [responseLimitInCharacters]=\"(airEssay$ | async)?.responseLimitInCharacters\"\n                             [responseLimitInWords]=\"(airEssay$ | async)?.responseLimitInWords\">\n</aa-item-response-text-limit>\n<aa-item-editor-type [editorType]='(airEssay$ | async)?.editorType'\n                     (onChangeEditorType)=\"onChangeEditorType($event)\"></aa-item-editor-type>\n", dependencies: [{ kind: "component", type: ItemEditorTypeComponent, selector: "aa-item-editor-type", inputs: ["editorType"], outputs: ["onChangeEditorType"] }, { kind: "component", type: ItemResponseTextLimitComponent, selector: "aa-item-response-text-limit", inputs: ["maxCharacters", "maxWords", "responseLimitInWords", "responseLimitInCharacters", "limitErrors"], outputs: ["selectCharactersResponseLimit", "selectWordsResponseLimit", "updateCharactersResponseLimit", "updateWordsResponseLimit", "limitInputFocusOut", "resetLimitErrors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirEssayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-essay', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-item-response-text-limit (selectCharactersResponseLimit)=\"selectCharactersResponseLimit()\"\n                             (selectWordsResponseLimit)=\"selectWordsResponseLimit()\"\n                             (updateCharactersResponseLimit)=\"updateCharactersResponseLimit($event)\"\n                             (updateWordsResponseLimit)=\"updateWordsResponseLimit($event)\"\n                             (limitInputFocusOut)=\"onLimitInputFocusOut($event)\"\n                             (resetLimitErrors)=\"onResetLimitErrors()\"\n                             [maxCharacters]=\"(airEssay$ | async)?.maxCharacters\"\n                             [maxWords]=\"(airEssay$ | async)?.maxWords\"\n                             [limitErrors]=\"getLimitErrors()\"\n                             [responseLimitInCharacters]=\"(airEssay$ | async)?.responseLimitInCharacters\"\n                             [responseLimitInWords]=\"(airEssay$ | async)?.responseLimitInWords\">\n</aa-item-response-text-limit>\n<aa-item-editor-type [editorType]='(airEssay$ | async)?.editorType'\n                     (onChangeEditorType)=\"onChangeEditorType($event)\"></aa-item-editor-type>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: EssayValidationService }, { type: ConfigService }]; } });

class FitbValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirFib$ = this.store.select(getPureAirFib);
        this.airFib$ = this.store.select(getAirFib);
        this.answerError = '';
        this.validationErrors$ = new BehaviorSubject(this.getAnswerError());
        this.toleranceError = '';
    }
    validate() {
        let isValid;
        this.pureAirFib$.pipe(take(1)).subscribe((pureAirFib) => {
            this.airFib$.pipe(take(1)).subscribe((airFib) => {
                const promptError = !!this.promptValidationService.validatePrompt(pureAirFib.prompt);
                const minBlankError = !!this.promptValidationService.validateMinBlankCount(pureAirFib.prompt);
                const maxBlankError = !!this.promptValidationService.validateMaxBlankCount(pureAirFib.tooManyAnswers);
                const answerError = !!this.validateAnswer(pureAirFib.answers, airFib.ignorePunctuation);
                const toleranceError = pureAirFib.inputFormat === 'numeric' && airFib.tolerance && airFib.tolerance.type !== 'none'
                    ? !!this.validateTolerance(airFib.tolerance.value)
                    : false;
                isValid = !(minBlankError || promptError || answerError || maxBlankError || toleranceError);
            });
        });
        return isValid;
    }
    validatePunctuationAnswers(answers) {
        const pattern = /[^a-zA-Z\d\s]/;
        for (const item of answers) {
            for (const value of item.values) {
                if (pattern.test(value)) {
                    return E_CORRECT_ANSWER_CONTAINS_PUNCTUATION;
                }
            }
        }
        return '';
    }
    validateAnswer(answers, ignorePunctuation = false) {
        this.answerError = this.getInvalidAnswers(answers).length
            ? E_MIN_CORRECT_ANSWER
            : ignorePunctuation
                ? this.validatePunctuationAnswers(answers)
                : '';
        this.emitAnswerError();
        return this.answerError;
    }
    validateAnswerOnFocusOut(answers) {
        if (!this.getInvalidAnswers(answers).length) {
            this.resetAnswerError();
        }
    }
    getInvalidAnswers(answers) {
        return answers.filter((answer) => {
            const values = answer.values.filter((value) => !value).length;
            return answer.values.length === values;
        });
    }
    getAnswerError() {
        return this.answerError ? [this.answerError] : [];
    }
    emitAnswerError() {
        this.validationErrors$.next(this.getAnswerError());
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetAnswerError();
        this.resetToleranceError();
    }
    resetAnswerError() {
        this.answerError = '';
        this.emitAnswerError();
    }
    resetToleranceError() {
        this.toleranceError = '';
    }
    getToleranceError() {
        return this.toleranceError ? [this.toleranceError] : [];
    }
    validateTolerance(value) {
        this.toleranceError = '';
        if (!value || Number(value) < 0) {
            this.toleranceError = E_TOLERANCE_RANGE_ERROR;
        }
        return this.toleranceError;
    }
}
FitbValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FitbValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
FitbValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FitbValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FitbValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class ViewShortcutsComponent {
    constructor() {
        this.viewShortcuts = false;
        this.closeViewShortcutsModal = new EventEmitter();
    }
    getSplcharacterArray() {
        const splCharPalette = shared[this.selectedLanguage];
        return splCharPalette;
    }
}
ViewShortcutsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ViewShortcutsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ViewShortcutsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ViewShortcutsComponent, selector: "aa-view-shortcuts", inputs: { viewShortcuts: "viewShortcuts", selectedLanguage: "selectedLanguage" }, outputs: { closeViewShortcutsModal: "closeViewShortcutsModal" }, ngImport: i0, template: "<air-modal\n  titleText=\"Keyboard shortcuts for standard US layout\"\n  [showFooter]=\"false\"\n  [isOpen]=\"viewShortcuts\"\n  (close)=\"closeViewShortcutsModal.emit()\">\n  <div>\n    <table class=\"shortcut-table\">\n      <thead class=\"table-header\">\n        <tr class=\"col-xs-12\">\n          <th class=\"col-md-2 col-xs-2 table-cell\"\n           translate>Character</th>\n          <th class=\"col-md-4 col-xs-4 table-cell\"\n           translate>Windows</th>\n          <th class=\"col-md-6 col-xs-6 table-cell\"\n           translate>Mac OS</th>\n        </tr>\n      </thead>\n      <tbody>\n        <ng-container\n          *ngFor=\"let character of getSplcharacterArray(); let characterIndex = index\">\n          <tr class=\"col-md-12 col-xs-12 table-row\">\n            <td class =\"col-md-2 col-xs-2 table-cell\">\n              {{character.charLc}}\n            </td>\n            <td class=\"col-md-4 col-xs-4 table-cell\">\n              {{character.winLc}}\n            </td>\n            <td class=\"col-md-6 col-xs-6 table-cell\">\n                {{character.macLc}}\n            </td>\n          </tr>\n          <tr class=\"col-md-12 col-xs-12 table-row\"\n            *ngIf=\"character && character.winUc\">\n            <td class =\"col-md-2 col-xs-2 table-cell\">\n              {{character.charUc}}\n            </td>\n            <td class =\"col-md-4 col-xs-4 table-cell\">\n              {{character.winUc}}\n            </td>\n            <td class =\"col-md-6 col-xs-6 table-cell\">\n              {{character.macUc}}\n            </td>\n          </tr>\n        </ng-container>\n      </tbody>\n    </table>\n  </div>\n</air-modal>\n", styles: [".shortcut-table{border:1px solid #F0F3F4;width:100%;text-align:center}.shortcut-table .table-cell{padding:3px 10px;text-align:left;float:left}.shortcut-table .table-header{background-color:#007c91;color:#fff;word-wrap:break-word}.shortcut-table .table-row{margin:none;border-bottom:1px solid #D6D3D3;height:40px}table>tbody>tr:nth-child(odd){background-color:#f0f3f4}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ViewShortcutsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-view-shortcuts', template: "<air-modal\n  titleText=\"Keyboard shortcuts for standard US layout\"\n  [showFooter]=\"false\"\n  [isOpen]=\"viewShortcuts\"\n  (close)=\"closeViewShortcutsModal.emit()\">\n  <div>\n    <table class=\"shortcut-table\">\n      <thead class=\"table-header\">\n        <tr class=\"col-xs-12\">\n          <th class=\"col-md-2 col-xs-2 table-cell\"\n           translate>Character</th>\n          <th class=\"col-md-4 col-xs-4 table-cell\"\n           translate>Windows</th>\n          <th class=\"col-md-6 col-xs-6 table-cell\"\n           translate>Mac OS</th>\n        </tr>\n      </thead>\n      <tbody>\n        <ng-container\n          *ngFor=\"let character of getSplcharacterArray(); let characterIndex = index\">\n          <tr class=\"col-md-12 col-xs-12 table-row\">\n            <td class =\"col-md-2 col-xs-2 table-cell\">\n              {{character.charLc}}\n            </td>\n            <td class=\"col-md-4 col-xs-4 table-cell\">\n              {{character.winLc}}\n            </td>\n            <td class=\"col-md-6 col-xs-6 table-cell\">\n                {{character.macLc}}\n            </td>\n          </tr>\n          <tr class=\"col-md-12 col-xs-12 table-row\"\n            *ngIf=\"character && character.winUc\">\n            <td class =\"col-md-2 col-xs-2 table-cell\">\n              {{character.charUc}}\n            </td>\n            <td class =\"col-md-4 col-xs-4 table-cell\">\n              {{character.winUc}}\n            </td>\n            <td class =\"col-md-6 col-xs-6 table-cell\">\n              {{character.macUc}}\n            </td>\n          </tr>\n        </ng-container>\n      </tbody>\n    </table>\n  </div>\n</air-modal>\n", styles: [".shortcut-table{border:1px solid #F0F3F4;width:100%;text-align:center}.shortcut-table .table-cell{padding:3px 10px;text-align:left;float:left}.shortcut-table .table-header{background-color:#007c91;color:#fff;word-wrap:break-word}.shortcut-table .table-row{margin:none;border-bottom:1px solid #D6D3D3;height:40px}table>tbody>tr:nth-child(odd){background-color:#f0f3f4}\n"] }]
        }], propDecorators: { viewShortcuts: [{
                type: Input
            }], selectedLanguage: [{
                type: Input
            }], closeViewShortcutsModal: [{
                type: Output
            }] } });

class AnswerFibComponent {
    constructor(fitbValidationService, configService, sanitizeInputService, textUtilityService, cdr) {
        this.fitbValidationService = fitbValidationService;
        this.configService = configService;
        this.sanitizeInputService = sanitizeInputService;
        this.textUtilityService = textUtilityService;
        this.cdr = cdr;
        this.paletteLanguage = 'none';
        this.updateAnswerContent = new EventEmitter();
        this.addAnswer = new EventEmitter();
        this.deleteAnswer = new EventEmitter();
        this.toggleToleranceVisibility = new EventEmitter();
        this.toggleInputFormat = new EventEmitter();
        this.updatePaletteLanguage = new EventEmitter();
        this.answerLimit = CORRECT_ANSWER_LIMIT_FIB;
        this.allowValidation = false;
        this.charUppercase = false;
        this.inputModes = [
            {
                value: 'any',
                viewValue: 'Any',
            },
            {
                value: 'numeric',
                viewValue: 'Numeric Only',
            },
        ];
        this.languages = [
            {
                value: 'none',
                viewValue: 'None',
            },
            {
                value: 'french',
                viewValue: 'French',
            },
            {
                value: 'german',
                viewValue: 'German',
            },
            {
                value: 'italian',
                viewValue: 'Italian',
            },
            {
                value: 'spanish',
                viewValue: 'Spanish',
            },
        ];
        this.selectedInput = this.inputModes[0];
        this.selectedLanguage = this.languages[0];
        this.viewShortcuts = false;
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    ngAfterViewInit() {
        this.closeButtons.changes.subscribe(() => {
            // Focus the close button for the latest popover
            const lastCloseButton = this.closeButtons.last;
            if (lastCloseButton) {
                lastCloseButton.nativeElement.focus();
            }
        });
    }
    ngOnChanges() {
        this.selectedInput = this.inputModes.filter((type) => type.value === this.inputFormat)[0];
        this.selectedLanguage = this.languages.filter((type) => type.value === this.paletteLanguage)[0];
    }
    trackResponse(index, answer) {
        return answer.response;
    }
    trackIndex(index) {
        return index;
    }
    onSelectingInput(inputOptions) {
        const selectedValue = this.selectedInput.value;
        if (selectedValue === inputOptions.value) {
            return;
        }
        this.selectedInput = inputOptions;
        this.selectedLanguage = this.languages[0];
        this.toggleInputFormat.emit();
        this.fitbValidationService.resetToleranceError();
    }
    onSelectingLanguage(language) {
        this.selectedLanguage = language;
        this.updatePaletteLanguage.emit(this.selectedLanguage?.value);
    }
    getPaletteVisibility() {
        return !this.isNumeric() && this.selectedLanguage && this.selectedLanguage?.value !== 'none';
    }
    canDeleteAnswer(choicesLength) {
        return choicesLength > 1;
    }
    canAddAnswer(answers) {
        return answers.length < 100;
    }
    onAddAnswer(answer) {
        this.addAnswer.emit(answer);
    }
    onUpdateAnswerContent(answer, index, content) {
        this.updateAnswerContent.emit({ answer, index, content });
    }
    onKeyDown(event) {
        if (this.isNumeric()) {
            return this.sanitizeInputService.sanitizeInput(event, true, true);
        }
        return true;
    }
    onPaste(event) {
        const data = event.clipboardData.getData('Text');
        if (this.isNumeric()) {
            return this.textUtilityService.isOnlyNumeric(data);
        }
        return true;
    }
    onDeleteAnswer(answer, answerKey) {
        this.deleteAnswer.emit({
            answer: answer,
            answerIndex: answerKey,
        });
    }
    isNumeric() {
        return this.inputFormat === 'numeric';
    }
    getErrors() {
        return (this.allowValidation && !!this.answers.length && this.fitbValidationService.getAnswerError()) || [];
    }
    onInputBlur(answers) {
        this.fitbValidationService.validateAnswerOnFocusOut(answers);
    }
    closePopover() {
        this.popovers.find((popover) => popover.close());
    }
    getCharacterArray() {
        const splChar = this.selectedLanguage?.value;
        const charArray = shared[splChar].map((character) => {
            return this.charUppercase && character.charUc ? character.charUc : character.charLc;
        });
        return charArray;
    }
    toggleCase() {
        this.charUppercase = !this.charUppercase;
    }
    inputSplCharacter(answer, index, answersKey, content) {
        const id = 'input' + answersKey + index;
        const buttonValue = content;
        const input = document.getElementById(id);
        const inputValue = input.value;
        const cursorStart = input.selectionStart || undefined;
        const cursorEnd = input.selectionEnd || undefined;
        input.value = inputValue.slice(0, cursorStart) + buttonValue + inputValue.slice(cursorEnd);
        setTimeout(() => {
            input.selectionStart = input.selectionEnd = (cursorStart || 0) + buttonValue.length;
            input.focus();
        }, 0);
        content = input.value;
        this.updateAnswerContent.emit({ answer, index, content });
        this.closePopover();
    }
    openModal() {
        this.viewShortcuts = true;
        this.cdr.detectChanges();
    }
    closeViewShortcutsLink() {
        this.viewShortcuts = false;
    }
    warnCharLimit(value) {
        if (!value) {
            return false;
        }
        return this.answerLimit - value.length <= this.answerLimit / 10;
    }
    getCharCount(value) {
        if (!value) {
            return this.answerLimit;
        }
        return this.answerLimit - value.length;
    }
}
AnswerFibComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AnswerFibComponent, deps: [{ token: FitbValidationService }, { token: ConfigService }, { token: SanitizeInputService }, { token: TextUtilityService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
AnswerFibComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AnswerFibComponent, selector: "aa-answer-fib", inputs: { answers: "answers", inputFormat: "inputFormat", featureFlags: "featureFlags", paletteLanguage: "paletteLanguage" }, outputs: { updateAnswerContent: "updateAnswerContent", addAnswer: "addAnswer", deleteAnswer: "deleteAnswer", toggleToleranceVisibility: "toggleToleranceVisibility", toggleInputFormat: "toggleInputFormat", updatePaletteLanguage: "updatePaletteLanguage" }, viewQueries: [{ propertyName: "popovers", predicate: ["bottomArrowPopover"], descendants: true }, { propertyName: "closeButtons", predicate: ["closeButton"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"answer-choices-container\">\n  <h2 *ngIf=\"answers.length\">Answers *</h2>\n\n  <div\n    class=\"answer-wrapper\"\n    [ngClass]=\"{ 'answer-error': getErrors()?.length }\"\n    *ngIf=\"answers.length\"\n  >\n    <div\n      class=\"action-bar\"\n      *ngIf=\"featureFlags && featureFlags.allowSpecialCharacters\"\n    >\n      <label for=\"charDropdown\" translate>Characters allowed:</label>\n      <select\n        id=\"charDropdown\"\n        class=\"form-select d-inline w-auto\"\n        [disabled]=\"inputModes.length === 0\"\n        [ngModel]=\"selectedInput\"\n        (ngModelChange)=\"onSelectingInput($event)\"\n      >\n        <option\n          *ngFor=\"let option of inputModes\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n\n      <span *ngIf=\"!isNumeric()\">\n        <span class=\"vertical-separator\"></span>\n        <span class=\"special-char\">\n          <label for=\"languageDropdown\" translate\n            >Special character keyboard:</label\n          >\n          <select\n            id=\"languageDropdown\"\n            class=\"form-select d-inline w-auto\"\n            [disabled]=\"inputModes.length === 0\"\n            [ngModel]=\"selectedLanguage\"\n            (ngModelChange)=\"onSelectingLanguage($event)\"\n          >\n            <option\n              *ngFor=\"let option of languages\"\n              [ngValue]=\"option\">\n              {{ option.viewValue }}\n            </option>\n          </select>\n        </span>\n      </span>\n    </div>\n\n    <div\n      class=\"answer-choices\"\n      *ngFor=\"\n        let answer of answers;\n        trackBy: trackResponse;\n        let answersKey = index\n      \"\n    >\n      <div class=\"answer-label\">\n        <span class=\"answer-sub-heading\" translate>Blank</span>\n        <span class=\"answer-sub-heading\">{{ answersKey + 1 }}</span>\n        <span class=\"answer-sub-heading\" translate>answers</span>\n      </div>\n\n      <div\n        class=\"row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 gy-4 mx-0\"\n      >\n        <div\n          class=\"answer-choice col\"\n          *ngFor=\"\n            let value of answer.values;\n            let answerKey = index;\n            trackBy: trackIndex\n          \"\n        >\n        <div class=\"input-container\">\n          <div\n            class=\"character-count question-text\"\n            [class.character-limit-warning]=\"warnCharLimit(fibInput.value)\"\n          >\n            {{ getCharCount(fibInput.value) }}/{{ answerLimit }}\n          </div>\n          <input\n            id=\"{{ 'input' + answersKey + answerKey }}\"\n            #fibInput\n            aria-label=\"Blank {{ answersKey + 1 }} answer {{ answerKey + 1 }}\"\n            class=\"form-control col-lg-11 col-xs-10 col-sm-11 fill-in-answer\"\n            [ngClass]=\"{\n              'first-input': !canDeleteAnswer(answerKey),\n              'has-palette': getPaletteVisibility()\n            }\"\n            [maxlength]=\"answerLimit\"\n            type=\"text\"\n            [value]=\"value\"\n            autocomplete=\"off\"\n            (blur)=\"onInputBlur(answers)\"\n            (input)=\"onUpdateAnswerContent(answer, answerKey, $event.target.value)\"\n            (keydown)=\"onKeyDown($event)\"\n            (paste)=\"onPaste($event)\"\n          />\n        </div>\n          <div class=\"operations\">\n            <button\n              *ngIf=\"getPaletteVisibility()\"\n              type=\"button\"\n              #bottomArrowPopover=\"ngbPopover\"\n              class=\"btn palette-btn\"\n              [ngbPopover]=\"characterPopover\"\n              placement=\"bottom\"\n              triggers=\"click\"\n              (click)=\"charUppercase = false\"\n            >\n              <i aria-label=\"Keyboard\" class=\"dpg-icon dpg-icon-keyboard\"></i>\n            </button>\n            <aa-trash-can\n              *ngIf=\"canDeleteAnswer(answer.values.length)\"\n              class=\"delete-answer-key\"\n              (delete)=\"onDeleteAnswer(answer, answerKey)\"\n            ></aa-trash-can>\n          </div>\n          <ng-template #characterPopover>\n            <div appFocusTrap>\n              <div class=\"popover-header d-flex align-items-center\">\n                <span class=\"spl-char flex-grow-1\">Special characters</span>\n                <button\n                  #closeButton\n                  aria-label=\"Close\"\n                  class=\"btn-close ms-auto\"\n                  type=\"button\"\n                  (click)=\"closePopover()\"\n                  ></button>\n              </div>\n              <div class=\"palette-body\">\n                <div class=\"d-flex flex-column\">\n                  <button\n                    class=\"btn btn-outline-primary\"\n                    [ngClass]=\"{ 'is-uppercase-pressed': charUppercase }\"\n                    type=\"button\"\n                    (click)=\"toggleCase()\"\n                  >\n                    Uppercase\n                  </button>\n                  <button\n                    class=\"btn btn-outline-primary shortcuts-link\"\n                    type=\"button\"\n                    (click)=\"openModal()\"\n                  >\n                    View Shortcuts\n                  </button>\n                </div>\n  \n                <div class=\"keyboard-palette\">\n                  <div\n                    class=\"button-keys\"\n                    *ngFor=\"\n                      let buttonValue of getCharacterArray();\n                      let characterkey = index\n                    \"\n                  >\n                    <button\n                      id=\"key-{{ buttonValue }}\"\n                      class=\"btn-outline-primary character-keys\"\n                      (click)=\"\n                        inputSplCharacter(\n                          answer,\n                          answerKey,\n                          answersKey,\n                          buttonValue\n                        )\n                      \"\n                      type=\"button\"\n                    >\n                      {{ buttonValue }}\n                    </button>\n                  </div>\n                </div>\n              </div>\n            </div>\n          </ng-template>\n        </div>\n\n        <div class=\"col align-self-end\">\n          <button\n            *ngIf=\"canAddAnswer(answer.values)\"\n            type=\"button\"\n            class=\"add-correct-answer btn btn-link\"\n            (click)=\"onAddAnswer(answer)\"\n          >\n            <span translate>+ Add correct answer</span>\n          </button>\n        </div>\n      </div>\n\n      <hr class=\"mx-3\" />\n    </div>\n  </div>\n  <aa-inline-error [errors]=\"fitbValidationService.validationErrors$ | async\"></aa-inline-error>\n</div>\n\n\n<aa-view-shortcuts\n  *ngIf=\"selectedLanguage?.value\"\n  [viewShortcuts]=\"viewShortcuts\"\n  [selectedLanguage]=\"selectedLanguage?.value\"\n  (closeViewShortcutsModal)=\"closeViewShortcutsLink()\"\n></aa-view-shortcuts>\n", styles: [".answer-sub-heading{display:inline-block;margin-bottom:10px;margin-right:3px;font-size:15px}.answer-wrapper{background:#fbfbfb;border:1px solid #d1dbde;border-radius:4px;margin-bottom:16px}.answer-wrapper.answer-error{border:1px solid #b0181e}.fill-in-answer.has-palette{border-top-right-radius:0;border-bottom-right-radius:0}.action-bar{background-color:#f0f3f4;border-bottom:1px solid #d1dbde;padding:7px 7px 4px;margin-left:1px}.action-bar label{padding:10px;font-weight:400}.action-bar .vertical-separator{padding:10px;margin-right:6px;border-right:1px solid #d1dbde}.action-bar mhe-dropdown{margin-left:10px;background:#ffffff}.special-char{display:inline-block}.answer-choices{margin-top:16px;position:relative}.answer-choices .answer-label{margin-left:1em}.answer-choices .answer-choice{display:inline-flex;align-items:flex-end}.answer-choices:last-child .horizontal-separator{border:none}.character-count{padding:0 15px;text-align:right}.character-count.character-limit-warning{color:#b0181e}.palette-btn{color:#006a7c;border:1px solid #006A7C;border-top-left-radius:0;border-bottom-left-radius:0;padding-left:5px;padding-right:5px;width:36px}.palette-btn:focus:before,.palette-btn:active:before,.palette-btn:focus:active:before{border-top-left-radius:0;border-bottom-left-radius:0}.operations{display:inline-flex;flex-wrap:nowrap;align-items:flex-end}.keyboard-palette{padding-top:10px}.close{float:right}.spl-char{font-weight:600}.button-keys{display:inline-block;padding:2px;color:#46707d}.character-keys{align-items:center;background-color:#d2edf299;border:1px solid #006a7c;border-radius:2px;display:flex;height:32px;justify-content:center;width:32px}.character-keys:focus:before,.character-keys:focus:active:before{border-radius:2px}.palette-body{padding-top:10px}.shortcuts-link{float:right;background-color:transparent;border:none}.shortcuts-link:hover{background-color:transparent;border:0;border-radius:5px;color:var(--bs-btn-color);font-size:16px;height:36px;margin:0 1px;padding:0 16px;transition:background-color .2s ease-in-out;border:1px solid #006a7c}.shortcuts-link:focus{color:var(--bs-btn-color)}.is-uppercase-pressed{background-color:#e0e7e9!important}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i6.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "triggers", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "component", type: ViewShortcutsComponent, selector: "aa-view-shortcuts", inputs: ["viewShortcuts", "selectedLanguage"], outputs: ["closeViewShortcutsModal"] }, { kind: "directive", type: FocusTrapDirective, selector: "[appFocusTrap]" }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AnswerFibComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-answer-fib', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"answer-choices-container\">\n  <h2 *ngIf=\"answers.length\">Answers *</h2>\n\n  <div\n    class=\"answer-wrapper\"\n    [ngClass]=\"{ 'answer-error': getErrors()?.length }\"\n    *ngIf=\"answers.length\"\n  >\n    <div\n      class=\"action-bar\"\n      *ngIf=\"featureFlags && featureFlags.allowSpecialCharacters\"\n    >\n      <label for=\"charDropdown\" translate>Characters allowed:</label>\n      <select\n        id=\"charDropdown\"\n        class=\"form-select d-inline w-auto\"\n        [disabled]=\"inputModes.length === 0\"\n        [ngModel]=\"selectedInput\"\n        (ngModelChange)=\"onSelectingInput($event)\"\n      >\n        <option\n          *ngFor=\"let option of inputModes\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n\n      <span *ngIf=\"!isNumeric()\">\n        <span class=\"vertical-separator\"></span>\n        <span class=\"special-char\">\n          <label for=\"languageDropdown\" translate\n            >Special character keyboard:</label\n          >\n          <select\n            id=\"languageDropdown\"\n            class=\"form-select d-inline w-auto\"\n            [disabled]=\"inputModes.length === 0\"\n            [ngModel]=\"selectedLanguage\"\n            (ngModelChange)=\"onSelectingLanguage($event)\"\n          >\n            <option\n              *ngFor=\"let option of languages\"\n              [ngValue]=\"option\">\n              {{ option.viewValue }}\n            </option>\n          </select>\n        </span>\n      </span>\n    </div>\n\n    <div\n      class=\"answer-choices\"\n      *ngFor=\"\n        let answer of answers;\n        trackBy: trackResponse;\n        let answersKey = index\n      \"\n    >\n      <div class=\"answer-label\">\n        <span class=\"answer-sub-heading\" translate>Blank</span>\n        <span class=\"answer-sub-heading\">{{ answersKey + 1 }}</span>\n        <span class=\"answer-sub-heading\" translate>answers</span>\n      </div>\n\n      <div\n        class=\"row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 gy-4 mx-0\"\n      >\n        <div\n          class=\"answer-choice col\"\n          *ngFor=\"\n            let value of answer.values;\n            let answerKey = index;\n            trackBy: trackIndex\n          \"\n        >\n        <div class=\"input-container\">\n          <div\n            class=\"character-count question-text\"\n            [class.character-limit-warning]=\"warnCharLimit(fibInput.value)\"\n          >\n            {{ getCharCount(fibInput.value) }}/{{ answerLimit }}\n          </div>\n          <input\n            id=\"{{ 'input' + answersKey + answerKey }}\"\n            #fibInput\n            aria-label=\"Blank {{ answersKey + 1 }} answer {{ answerKey + 1 }}\"\n            class=\"form-control col-lg-11 col-xs-10 col-sm-11 fill-in-answer\"\n            [ngClass]=\"{\n              'first-input': !canDeleteAnswer(answerKey),\n              'has-palette': getPaletteVisibility()\n            }\"\n            [maxlength]=\"answerLimit\"\n            type=\"text\"\n            [value]=\"value\"\n            autocomplete=\"off\"\n            (blur)=\"onInputBlur(answers)\"\n            (input)=\"onUpdateAnswerContent(answer, answerKey, $event.target.value)\"\n            (keydown)=\"onKeyDown($event)\"\n            (paste)=\"onPaste($event)\"\n          />\n        </div>\n          <div class=\"operations\">\n            <button\n              *ngIf=\"getPaletteVisibility()\"\n              type=\"button\"\n              #bottomArrowPopover=\"ngbPopover\"\n              class=\"btn palette-btn\"\n              [ngbPopover]=\"characterPopover\"\n              placement=\"bottom\"\n              triggers=\"click\"\n              (click)=\"charUppercase = false\"\n            >\n              <i aria-label=\"Keyboard\" class=\"dpg-icon dpg-icon-keyboard\"></i>\n            </button>\n            <aa-trash-can\n              *ngIf=\"canDeleteAnswer(answer.values.length)\"\n              class=\"delete-answer-key\"\n              (delete)=\"onDeleteAnswer(answer, answerKey)\"\n            ></aa-trash-can>\n          </div>\n          <ng-template #characterPopover>\n            <div appFocusTrap>\n              <div class=\"popover-header d-flex align-items-center\">\n                <span class=\"spl-char flex-grow-1\">Special characters</span>\n                <button\n                  #closeButton\n                  aria-label=\"Close\"\n                  class=\"btn-close ms-auto\"\n                  type=\"button\"\n                  (click)=\"closePopover()\"\n                  ></button>\n              </div>\n              <div class=\"palette-body\">\n                <div class=\"d-flex flex-column\">\n                  <button\n                    class=\"btn btn-outline-primary\"\n                    [ngClass]=\"{ 'is-uppercase-pressed': charUppercase }\"\n                    type=\"button\"\n                    (click)=\"toggleCase()\"\n                  >\n                    Uppercase\n                  </button>\n                  <button\n                    class=\"btn btn-outline-primary shortcuts-link\"\n                    type=\"button\"\n                    (click)=\"openModal()\"\n                  >\n                    View Shortcuts\n                  </button>\n                </div>\n  \n                <div class=\"keyboard-palette\">\n                  <div\n                    class=\"button-keys\"\n                    *ngFor=\"\n                      let buttonValue of getCharacterArray();\n                      let characterkey = index\n                    \"\n                  >\n                    <button\n                      id=\"key-{{ buttonValue }}\"\n                      class=\"btn-outline-primary character-keys\"\n                      (click)=\"\n                        inputSplCharacter(\n                          answer,\n                          answerKey,\n                          answersKey,\n                          buttonValue\n                        )\n                      \"\n                      type=\"button\"\n                    >\n                      {{ buttonValue }}\n                    </button>\n                  </div>\n                </div>\n              </div>\n            </div>\n          </ng-template>\n        </div>\n\n        <div class=\"col align-self-end\">\n          <button\n            *ngIf=\"canAddAnswer(answer.values)\"\n            type=\"button\"\n            class=\"add-correct-answer btn btn-link\"\n            (click)=\"onAddAnswer(answer)\"\n          >\n            <span translate>+ Add correct answer</span>\n          </button>\n        </div>\n      </div>\n\n      <hr class=\"mx-3\" />\n    </div>\n  </div>\n  <aa-inline-error [errors]=\"fitbValidationService.validationErrors$ | async\"></aa-inline-error>\n</div>\n\n\n<aa-view-shortcuts\n  *ngIf=\"selectedLanguage?.value\"\n  [viewShortcuts]=\"viewShortcuts\"\n  [selectedLanguage]=\"selectedLanguage?.value\"\n  (closeViewShortcutsModal)=\"closeViewShortcutsLink()\"\n></aa-view-shortcuts>\n", styles: [".answer-sub-heading{display:inline-block;margin-bottom:10px;margin-right:3px;font-size:15px}.answer-wrapper{background:#fbfbfb;border:1px solid #d1dbde;border-radius:4px;margin-bottom:16px}.answer-wrapper.answer-error{border:1px solid #b0181e}.fill-in-answer.has-palette{border-top-right-radius:0;border-bottom-right-radius:0}.action-bar{background-color:#f0f3f4;border-bottom:1px solid #d1dbde;padding:7px 7px 4px;margin-left:1px}.action-bar label{padding:10px;font-weight:400}.action-bar .vertical-separator{padding:10px;margin-right:6px;border-right:1px solid #d1dbde}.action-bar mhe-dropdown{margin-left:10px;background:#ffffff}.special-char{display:inline-block}.answer-choices{margin-top:16px;position:relative}.answer-choices .answer-label{margin-left:1em}.answer-choices .answer-choice{display:inline-flex;align-items:flex-end}.answer-choices:last-child .horizontal-separator{border:none}.character-count{padding:0 15px;text-align:right}.character-count.character-limit-warning{color:#b0181e}.palette-btn{color:#006a7c;border:1px solid #006A7C;border-top-left-radius:0;border-bottom-left-radius:0;padding-left:5px;padding-right:5px;width:36px}.palette-btn:focus:before,.palette-btn:active:before,.palette-btn:focus:active:before{border-top-left-radius:0;border-bottom-left-radius:0}.operations{display:inline-flex;flex-wrap:nowrap;align-items:flex-end}.keyboard-palette{padding-top:10px}.close{float:right}.spl-char{font-weight:600}.button-keys{display:inline-block;padding:2px;color:#46707d}.character-keys{align-items:center;background-color:#d2edf299;border:1px solid #006a7c;border-radius:2px;display:flex;height:32px;justify-content:center;width:32px}.character-keys:focus:before,.character-keys:focus:active:before{border-radius:2px}.palette-body{padding-top:10px}.shortcuts-link{float:right;background-color:transparent;border:none}.shortcuts-link:hover{background-color:transparent;border:0;border-radius:5px;color:var(--bs-btn-color);font-size:16px;height:36px;margin:0 1px;padding:0 16px;transition:background-color .2s ease-in-out;border:1px solid #006a7c}.shortcuts-link:focus{color:var(--bs-btn-color)}.is-uppercase-pressed{background-color:#e0e7e9!important}\n"] }]
        }], ctorParameters: function () { return [{ type: FitbValidationService }, { type: ConfigService }, { type: SanitizeInputService }, { type: TextUtilityService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { answers: [{
                type: Input
            }], inputFormat: [{
                type: Input
            }], featureFlags: [{
                type: Input
            }], paletteLanguage: [{
                type: Input
            }], updateAnswerContent: [{
                type: Output
            }], addAnswer: [{
                type: Output
            }], deleteAnswer: [{
                type: Output
            }], toggleToleranceVisibility: [{
                type: Output
            }], toggleInputFormat: [{
                type: Output
            }], updatePaletteLanguage: [{
                type: Output
            }], popovers: [{
                type: ViewChildren,
                args: ['bottomArrowPopover']
            }], closeButtons: [{
                type: ViewChildren,
                args: ['closeButton']
            }] } });

class FibSettingComponent {
    constructor(sanitizeInputService, fitbValidationService) {
        this.sanitizeInputService = sanitizeInputService;
        this.fitbValidationService = fitbValidationService;
        this.toggleToleranceVisibility = new EventEmitter();
        this.toggleInputFormat = new EventEmitter();
        this.toggleCaseSensitive = new EventEmitter();
        this.toggleIgnoreAccents = new EventEmitter();
        this.toggleIgnoreSpacing = new EventEmitter();
        this.toggleIgnorePunction = new EventEmitter();
        this.selectToleranceType = new EventEmitter();
        this.updateToleranceValue = new EventEmitter();
        this.toggleIgnoreAnswerOrder = new EventEmitter();
        this.allowSpecialCharacters = false;
        this.toleranceOptions = [
            {
                value: 'none',
                viewValue: 'None',
            },
            {
                value: 'absolute',
                viewValue: 'Absolute',
            },
            {
                value: 'percentage',
                viewValue: 'Percentage',
            },
        ];
    }
    ngOnChanges() {
        const toleranceType = this.tolerance && this.isNumeric() ? this.tolerance.type : 'none';
        this.selectedToleranceOptions = this.toleranceOptions.filter((type) => type.value === toleranceType)[0];
        this.isToleranceInPercentage = toleranceType === 'percentage';
        this.toggleToleranceVisibility.emit(toleranceType !== 'none');
    }
    isNumeric() {
        return this.inputFormat === 'numeric';
    }
    onToggleInputFormat() {
        this.toggleInputFormat.emit();
        this.fitbValidationService.resetToleranceError();
    }
    onSelectToleranceType(selectedToleranceOptions) {
        this.selectedToleranceOptions = selectedToleranceOptions;
        const selectedType = selectedToleranceOptions.value;
        this.toggleToleranceVisibility.emit(selectedType !== 'none');
        this.isToleranceInPercentage = selectedType === 'percentage';
        this.selectToleranceType.emit(selectedType);
        this.fitbValidationService.resetToleranceError();
    }
    sanitizeInput(event) {
        return this.sanitizeInputService.sanitizeInput(event, false, true);
    }
    updateTolerance(event) {
        const inputElement = event.target;
        let value = inputElement.value;
        const countDecimals = value.split('.')[1];
        if (countDecimals && countDecimals.length > 10) {
            value = Number(value).toFixed(10);
        }
        inputElement.value = value;
        this.updateToleranceValue.emit(value);
    }
    getToleranceError() {
        return this.fitbValidationService.getToleranceError();
    }
    onFocusOut(value) {
        this.fitbValidationService.validateTolerance(value);
    }
}
FibSettingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FibSettingComponent, deps: [{ token: SanitizeInputService }, { token: FitbValidationService }], target: i0.ɵɵFactoryTarget.Component });
FibSettingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: FibSettingComponent, selector: "aa-fib-setting", inputs: { answers: "answers", inputFormat: "inputFormat", caseSensitive: "caseSensitive", ignoreAccents: "ignoreAccents", ignoreSpacing: "ignoreSpacing", ignorePunctuation: "ignorePunctuation", allowAnyOrder: "allowAnyOrder", tolerance: "tolerance", featureFlags: "featureFlags", showTextBoxForTolerance: "showTextBoxForTolerance" }, outputs: { toggleToleranceVisibility: "toggleToleranceVisibility", toggleInputFormat: "toggleInputFormat", toggleCaseSensitive: "toggleCaseSensitive", toggleIgnoreAccents: "toggleIgnoreAccents", toggleIgnoreSpacing: "toggleIgnoreSpacing", toggleIgnorePunction: "toggleIgnorePunction", selectToleranceType: "selectToleranceType", updateToleranceValue: "updateToleranceValue", toggleIgnoreAnswerOrder: "toggleIgnoreAnswerOrder" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"options-wrapper\" *ngIf=\"answers.length\">\n  <div\n    class=\"option-wrapper row mx-0\"\n    *ngIf=\"!(featureFlags && featureFlags.allowSpecialCharacters)\"\n  >\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Input option</h2>\n\n    <div class=\"col-12 col-sm option-type\">\n        <div class=\"form-check\">\n        <input\n          id=\"numericOnly\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"numericOnly\"\n          [checked]=\"isNumeric()\"\n          (change)=\"onToggleInputFormat()\"\n        />\n        <label class=\"form-check-label\" for=\"numericOnly\" translate>Numeric only</label>\n      </div>\n    </div>\n  </div>\n\n  <div class=\"option-wrapper row mx-0\" *ngIf=\"!isNumeric()\">\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Answers formatting</h2>\n\n    <div class=\"col-xs-6 col-sm option-type\">\n      <div class=\"form-check\">\n        <input\n          id=\"caseSensitive\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"caseSensitive\"\n          [checked]=\"caseSensitive\"\n          (change)=\"toggleCaseSensitive.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"caseSensitive\" translate>Case-sensitive</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignoreAccents\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignoreAccents\"\n          [checked]=\"ignoreAccents\"\n          (change)=\"toggleIgnoreAccents.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignoreAccents\" translate>Ignore accents</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignoreSpacing\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignoreSpacing\"\n          [checked]=\"ignoreSpacing\"\n          (change)=\"toggleIgnoreSpacing.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignoreSpacing\" translate>Ignore spacing</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignorePunctuation\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignorePunctuation\"\n          [checked]=\"ignorePunctuation\"\n          (change)=\"toggleIgnorePunction.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignorePunctuation\" translate>Ignore punctuation</label>\n      </div>\n\n      <div\n        *ngIf=\"\n          featureFlags &&\n          (featureFlags.showIgnoreAnswerOrder || featureFlags.showAllowAnyOrder)\n        \"\n        class=\"form-check\">\n        <input\n          id=\"allowAnyOrder\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"allowAnyOrder\"\n          [checked]=\"allowAnyOrder\"\n          (change)=\"toggleIgnoreAnswerOrder.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"allowAnyOrder\" translate>Allow answers in any order</label>\n      </div>\n\n    </div>\n  </div>\n\n  <div class=\"option-wrapper row align-items-center mx-0\" *ngIf=\"isNumeric()\">\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Tolerance</h2>\n\n    <div class=\"tolerance-options col\">\n      <select\n        class=\"form-select d-inline w-auto\"\n        [ngModel]=\"selectedToleranceOptions\"\n        (ngModelChange)=\"onSelectToleranceType($event)\">\n        <option\n          *ngFor=\"let option of toleranceOptions\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n      <div class=\"tolerance-input gap-2\" *ngIf=\"showTextBoxForTolerance\">\n        <span class=\"tolerance-params sign\">+/-</span>\n        <div>\n          <input\n            type=\"number\"\n            [value]=\"tolerance?.value || ''\"\n            class=\"form-control tolerance-values\"\n            (input)=\"updateTolerance($event)\"\n            (blur)=\"onFocusOut($event.target.value)\"\n          />\n          <div *ngIf=\"!!getToleranceError()?.length\" class=\"custom-error\">\n            {{ getToleranceError() }}\n          </div>\n        </div>\n        <span class=\"tolerance-params percent\" *ngIf=\"isToleranceInPercentage\"\n          >%\n        </span>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".option-wrapper{border-bottom:1px solid #a9a9a9;padding:10px 0}.option-wrapper h2{padding-left:0}.option-wrapper .option-type mhe-checkbox{display:block}.option-wrapper .option-label{font-weight:400}.option-wrapper .tolerance-options{display:flex}.option-wrapper .tolerance-options .tolerance-input{display:inline-flex;margin-left:1em;position:relative}.option-wrapper .tolerance-options .tolerance-input mhe-input{margin:0 5px}.option-wrapper .tolerance-options .tolerance-params{font-size:18px}.option-wrapper .tolerance-options .tolerance-params.sign{position:relative;top:4px}.option-wrapper .tolerance-options .tolerance-params.percent{position:absolute;left:110px;top:8px}:host ::ng-deep .dropdown-toggle{text-transform:capitalize}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}.tolerance-values{width:4.5em}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FibSettingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-fib-setting', template: "<div class=\"options-wrapper\" *ngIf=\"answers.length\">\n  <div\n    class=\"option-wrapper row mx-0\"\n    *ngIf=\"!(featureFlags && featureFlags.allowSpecialCharacters)\"\n  >\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Input option</h2>\n\n    <div class=\"col-12 col-sm option-type\">\n        <div class=\"form-check\">\n        <input\n          id=\"numericOnly\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"numericOnly\"\n          [checked]=\"isNumeric()\"\n          (change)=\"onToggleInputFormat()\"\n        />\n        <label class=\"form-check-label\" for=\"numericOnly\" translate>Numeric only</label>\n      </div>\n    </div>\n  </div>\n\n  <div class=\"option-wrapper row mx-0\" *ngIf=\"!isNumeric()\">\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Answers formatting</h2>\n\n    <div class=\"col-xs-6 col-sm option-type\">\n      <div class=\"form-check\">\n        <input\n          id=\"caseSensitive\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"caseSensitive\"\n          [checked]=\"caseSensitive\"\n          (change)=\"toggleCaseSensitive.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"caseSensitive\" translate>Case-sensitive</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignoreAccents\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignoreAccents\"\n          [checked]=\"ignoreAccents\"\n          (change)=\"toggleIgnoreAccents.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignoreAccents\" translate>Ignore accents</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignoreSpacing\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignoreSpacing\"\n          [checked]=\"ignoreSpacing\"\n          (change)=\"toggleIgnoreSpacing.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignoreSpacing\" translate>Ignore spacing</label>\n      </div>\n\n      <div class=\"form-check\">\n        <input\n          id=\"ignorePunctuation\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"ignorePunctuation\"\n          [checked]=\"ignorePunctuation\"\n          (change)=\"toggleIgnorePunction.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"ignorePunctuation\" translate>Ignore punctuation</label>\n      </div>\n\n      <div\n        *ngIf=\"\n          featureFlags &&\n          (featureFlags.showIgnoreAnswerOrder || featureFlags.showAllowAnyOrder)\n        \"\n        class=\"form-check\">\n        <input\n          id=\"allowAnyOrder\" type=\"checkbox\" class=\"form-check-input\"\n          name=\"allowAnyOrder\"\n          [checked]=\"allowAnyOrder\"\n          (change)=\"toggleIgnoreAnswerOrder.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"allowAnyOrder\" translate>Allow answers in any order</label>\n      </div>\n\n    </div>\n  </div>\n\n  <div class=\"option-wrapper row align-items-center mx-0\" *ngIf=\"isNumeric()\">\n    <h2 class=\"col-12 col-sm-5 col-md-4 col-lg-3\">Tolerance</h2>\n\n    <div class=\"tolerance-options col\">\n      <select\n        class=\"form-select d-inline w-auto\"\n        [ngModel]=\"selectedToleranceOptions\"\n        (ngModelChange)=\"onSelectToleranceType($event)\">\n        <option\n          *ngFor=\"let option of toleranceOptions\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n      <div class=\"tolerance-input gap-2\" *ngIf=\"showTextBoxForTolerance\">\n        <span class=\"tolerance-params sign\">+/-</span>\n        <div>\n          <input\n            type=\"number\"\n            [value]=\"tolerance?.value || ''\"\n            class=\"form-control tolerance-values\"\n            (input)=\"updateTolerance($event)\"\n            (blur)=\"onFocusOut($event.target.value)\"\n          />\n          <div *ngIf=\"!!getToleranceError()?.length\" class=\"custom-error\">\n            {{ getToleranceError() }}\n          </div>\n        </div>\n        <span class=\"tolerance-params percent\" *ngIf=\"isToleranceInPercentage\"\n          >%\n        </span>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".option-wrapper{border-bottom:1px solid #a9a9a9;padding:10px 0}.option-wrapper h2{padding-left:0}.option-wrapper .option-type mhe-checkbox{display:block}.option-wrapper .option-label{font-weight:400}.option-wrapper .tolerance-options{display:flex}.option-wrapper .tolerance-options .tolerance-input{display:inline-flex;margin-left:1em;position:relative}.option-wrapper .tolerance-options .tolerance-input mhe-input{margin:0 5px}.option-wrapper .tolerance-options .tolerance-params{font-size:18px}.option-wrapper .tolerance-options .tolerance-params.sign{position:relative;top:4px}.option-wrapper .tolerance-options .tolerance-params.percent{position:absolute;left:110px;top:8px}:host ::ng-deep .dropdown-toggle{text-transform:capitalize}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}.tolerance-values{width:4.5em}\n"] }]
        }], ctorParameters: function () { return [{ type: SanitizeInputService }, { type: FitbValidationService }]; }, propDecorators: { answers: [{
                type: Input
            }], inputFormat: [{
                type: Input
            }], caseSensitive: [{
                type: Input
            }], ignoreAccents: [{
                type: Input
            }], ignoreSpacing: [{
                type: Input
            }], ignorePunctuation: [{
                type: Input
            }], allowAnyOrder: [{
                type: Input
            }], tolerance: [{
                type: Input
            }], featureFlags: [{
                type: Input
            }], showTextBoxForTolerance: [{
                type: Input
            }], toggleToleranceVisibility: [{
                type: Output
            }], toggleInputFormat: [{
                type: Output
            }], toggleCaseSensitive: [{
                type: Output
            }], toggleIgnoreAccents: [{
                type: Output
            }], toggleIgnoreSpacing: [{
                type: Output
            }], toggleIgnorePunction: [{
                type: Output
            }], selectToleranceType: [{
                type: Output
            }], updateToleranceValue: [{
                type: Output
            }], toggleIgnoreAnswerOrder: [{
                type: Output
            }] } });

class AirFibComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirFib, LoadAirItemAction$i, ResetStoreAction$i);
        this.store = store;
        this.airFib$ = this.store.select(getAirFib);
        this.pureAirFib$ = this.store.select(getPureAirFib);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.showTextBoxForTolerance = false;
    }
    onAddAnswer(answer) {
        this.store.dispatch(new AddAnswerValueAction(answer));
    }
    onUpdateAnswerContent({ answer, index, content }) {
        this.store.dispatch(new UpdateAnswerValueAction({ answer, index, content }));
    }
    onDeleteAnswer(airAnswerDelete) {
        this.store.dispatch(new DeleteAnswerValueAction(airAnswerDelete));
    }
    onToggleInputFormat() {
        this.store.dispatch(new ToggleInputFormatAction());
    }
    onToggleCaseSensitive() {
        this.store.dispatch(new ToggleCaseSensitive());
    }
    onToggleIgnoreAccents() {
        this.store.dispatch(new ToggleIgnoreAccents());
    }
    onToggleIgnorePunction() {
        this.store.dispatch(new ToggleIgnorePunction());
    }
    onToggleIgnoreSpacing() {
        this.store.dispatch(new ToggleIgnoreSpacing());
    }
    onToggleIgnoreAnswerOrder() {
        this.store.dispatch(new ToggleIgnoreAnswerOrder());
    }
    onSelectToleranceType(toleranceType) {
        this.store.dispatch(new SelectToleranceType(toleranceType));
    }
    onUpdateToleranceValue(toleranceVal) {
        this.store.dispatch(new UpdateToleranceValue(toleranceVal));
    }
    onToggleToleranceVisibility(visible) {
        setTimeout(() => {
            this.showTextBoxForTolerance = visible;
        });
    }
    onUpdatePaletteLanguage(language) {
        this.store.dispatch(new UpdatePaletteLanguage(language));
    }
}
AirFibComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirFibComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirFibComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirFibComponent, selector: "aa-air-fib", inputs: { featureFlags: "featureFlags" }, usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-fib [answers]=\"(airFib$ | async)?.answers\"\n               [inputFormat]=\"(airFib$ | async)?.inputFormat\"\n               [paletteLanguage]=\"(airFib$ | async)?.paletteLanguage\"\n               [featureFlags]=\"featureFlags\"\n               (addAnswer)=\"onAddAnswer($event)\"\n               (updateAnswerContent)=\"onUpdateAnswerContent($event)\"\n               (deleteAnswer)=\"onDeleteAnswer($event)\"\n               (toggleInputFormat)=\"onToggleInputFormat()\"\n               (updatePaletteLanguage)=\"onUpdatePaletteLanguage($event)\"\n               (toggleToleranceVisibility)=\"onToggleToleranceVisibility($event)\"></aa-answer-fib>\n<aa-fib-setting [answers]=\"(airFib$ | async)?.answers\"\n                [inputFormat]=\"(airFib$ | async)?.inputFormat\"\n                [caseSensitive]=\"(airFib$ | async)?.caseSensitive\"\n                [ignoreAccents]=\"(airFib$ | async)?.ignoreAccents\"\n                [ignoreSpacing]=\"(airFib$ | async)?.ignoreSpacing\"\n                [ignorePunctuation]=\"(airFib$ | async)?.ignorePunctuation\"\n                [tolerance]=\"(airFib$ | async)?.tolerance\"\n                [allowAnyOrder]=\"(airFib$ | async).allowAnyOrder\"\n                [featureFlags]=\"featureFlags\"\n                [showTextBoxForTolerance]=\"showTextBoxForTolerance\"\n                (toggleCaseSensitive)=\"onToggleCaseSensitive()\"\n                (toggleIgnoreAccents)=\"onToggleIgnoreAccents()\"\n                (toggleIgnoreSpacing)=\"onToggleIgnoreSpacing()\"\n                (toggleIgnorePunction)=\"onToggleIgnorePunction()\"\n                (toggleIgnoreAnswerOrder)=\"onToggleIgnoreAnswerOrder()\"\n                (toggleInputFormat)=\"onToggleInputFormat()\"\n                (selectToleranceType)=\"onSelectToleranceType($event)\"\n                (updateToleranceValue)=\"onUpdateToleranceValue($event)\"\n                (toggleToleranceVisibility)=\"onToggleToleranceVisibility($event)\"></aa-fib-setting>\n", dependencies: [{ kind: "component", type: AnswerFibComponent, selector: "aa-answer-fib", inputs: ["answers", "inputFormat", "featureFlags", "paletteLanguage"], outputs: ["updateAnswerContent", "addAnswer", "deleteAnswer", "toggleToleranceVisibility", "toggleInputFormat", "updatePaletteLanguage"] }, { kind: "component", type: FibSettingComponent, selector: "aa-fib-setting", inputs: ["answers", "inputFormat", "caseSensitive", "ignoreAccents", "ignoreSpacing", "ignorePunctuation", "allowAnyOrder", "tolerance", "featureFlags", "showTextBoxForTolerance"], outputs: ["toggleToleranceVisibility", "toggleInputFormat", "toggleCaseSensitive", "toggleIgnoreAccents", "toggleIgnoreSpacing", "toggleIgnorePunction", "selectToleranceType", "updateToleranceValue", "toggleIgnoreAnswerOrder"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirFibComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-fib', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-fib [answers]=\"(airFib$ | async)?.answers\"\n               [inputFormat]=\"(airFib$ | async)?.inputFormat\"\n               [paletteLanguage]=\"(airFib$ | async)?.paletteLanguage\"\n               [featureFlags]=\"featureFlags\"\n               (addAnswer)=\"onAddAnswer($event)\"\n               (updateAnswerContent)=\"onUpdateAnswerContent($event)\"\n               (deleteAnswer)=\"onDeleteAnswer($event)\"\n               (toggleInputFormat)=\"onToggleInputFormat()\"\n               (updatePaletteLanguage)=\"onUpdatePaletteLanguage($event)\"\n               (toggleToleranceVisibility)=\"onToggleToleranceVisibility($event)\"></aa-answer-fib>\n<aa-fib-setting [answers]=\"(airFib$ | async)?.answers\"\n                [inputFormat]=\"(airFib$ | async)?.inputFormat\"\n                [caseSensitive]=\"(airFib$ | async)?.caseSensitive\"\n                [ignoreAccents]=\"(airFib$ | async)?.ignoreAccents\"\n                [ignoreSpacing]=\"(airFib$ | async)?.ignoreSpacing\"\n                [ignorePunctuation]=\"(airFib$ | async)?.ignorePunctuation\"\n                [tolerance]=\"(airFib$ | async)?.tolerance\"\n                [allowAnyOrder]=\"(airFib$ | async).allowAnyOrder\"\n                [featureFlags]=\"featureFlags\"\n                [showTextBoxForTolerance]=\"showTextBoxForTolerance\"\n                (toggleCaseSensitive)=\"onToggleCaseSensitive()\"\n                (toggleIgnoreAccents)=\"onToggleIgnoreAccents()\"\n                (toggleIgnoreSpacing)=\"onToggleIgnoreSpacing()\"\n                (toggleIgnorePunction)=\"onToggleIgnorePunction()\"\n                (toggleIgnoreAnswerOrder)=\"onToggleIgnoreAnswerOrder()\"\n                (toggleInputFormat)=\"onToggleInputFormat()\"\n                (selectToleranceType)=\"onSelectToleranceType($event)\"\n                (updateToleranceValue)=\"onUpdateToleranceValue($event)\"\n                (toggleToleranceVisibility)=\"onToggleToleranceVisibility($event)\"></aa-fib-setting>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; }, propDecorators: { featureFlags: [{
                type: Input
            }] } });

class AirFileUploadComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirFileUpload, LoadAirItemAction$h, ResetStoreAction$h);
        this.store = store;
        this.airFileUpload$ = this.store.select(getAirFileUpload);
        this.pureAirFileUpload$ = this.store.select(getPureAirFileUpload);
    }
}
AirFileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirFileUploadComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirFileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirFileUploadComponent, selector: "aa-air-file-upload", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-file-upload', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ImageLabelingValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirImageLabeling$ = this.store.select(getPureAirImageLabeling);
        this.imageError = '';
        this.labelError = '';
    }
    validate() {
        let isValid;
        this.pureAirImageLabeling$.pipe(take(1)).subscribe((airImageLabeling) => {
            const promptError = !!this.promptValidationService.validatePrompt(airImageLabeling.prompt);
            const imageError = !!this.validateImage(!!airImageLabeling.backgroundImage);
            const labelError = !!this.validateLabelCount(airImageLabeling.labels, airImageLabeling.answers, airImageLabeling.choices);
            isValid = !(promptError || imageError || labelError);
        });
        return isValid;
    }
    getImageError() {
        return this.imageError ? [this.imageError] : [];
    }
    getLabelError() {
        return this.labelError ? [this.labelError] : [];
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.imageError = '';
        this.labelError = '';
    }
    validateLabelCount(labels, answers, choices) {
        const answerChoices = answers.map((answer) => answer.choice);
        const distractorChoice = choices.filter((choice) => !answerChoices.includes(choice.key));
        const nonDistractorChoice = choices.filter((choice) => answerChoices.includes(choice.key));
        const answerLabels = answers.map((answer) => answer.label);
        const distractorDropZones = labels.filter((label) => !answerLabels.includes(label.key));
        this.labelError = '';
        if (labels.length === 0 ||
            answers.length === 0 ||
            (nonDistractorChoice.length === 1 && distractorChoice.length + distractorDropZones.length < 1)) {
            this.labelError = E_IMAGE_CHOICE_ERROR;
        }
        return this.labelError;
    }
    validateImage(isImageLoaded) {
        this.imageError = isImageLoaded ? '' : E_IMAGE_ERROR;
        return this.imageError;
    }
}
ImageLabelingValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelingValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
ImageLabelingValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelingValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelingValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class ImageLabelsComponent {
    constructor(htmlToPlaintextPipe, imageLabelService) {
        this.htmlToPlaintextPipe = htmlToPlaintextPipe;
        this.imageLabelService = imageLabelService;
        this.isImageLoaded = false;
        this.addLabelOrDistractor = new EventEmitter();
        this.updateLabelOrDistractor = new EventEmitter();
        this.confirmAction = new EventEmitter();
        this.deleteLabelOrDistractor = new EventEmitter();
        this.moveChoiceDown = new EventEmitter();
        this.moveChoiceUp = new EventEmitter();
        this.draggingEnd = new EventEmitter();
        this.draggingStart = new EventEmitter();
        this.deleteLabelFromDropZone = new EventEmitter();
        this.setRelativeCoordinate = new EventEmitter();
        this.runImageValidation = new EventEmitter();
        this.runLabelValidation = new EventEmitter();
        this.openModal = false;
        this.editedObject = null;
        this.labelContentLimit = LABEL_CONTENT_LIMIT;
        this.draggingElement = null;
        this.isFirstCall = false;
        this.dragStart = false;
        this.kebabMenuOptions = [
            {
                optionText: 'Edit',
                value: 'edit',
                behavior: OptionBehavior.OpensModal,
                optionAutomationIdPrefix: 'edit',
            },
            {
                optionText: 'Delete',
                value: 'delete',
                behavior: OptionBehavior.OpensModal,
                optionAutomationIdPrefix: 'delete',
            },
        ];
        this.showEditor = false;
    }
    ngOnChanges(changes) {
        if (this.isImageLoaded && !this.isFirstCall) {
            this.isFirstCall = true;
            const imageCoordinate = this.imageLabelService.getImageCoordinate();
            this.labels.forEach((label) => {
                const draggedChoice = this.answers.find((answer) => label.key === answer.label);
                let labelElement;
                if (draggedChoice) {
                    const choiceIndex = this.choices.map((choice) => choice.key).indexOf(draggedChoice.choice);
                    labelElement = document.getElementById('label-' + choiceIndex + '-front');
                }
                else {
                    labelElement = document.getElementById('distractor-dropzone-back');
                }
                if (labelElement) {
                    const labelCoordinate = this.imageLabelService.getElementCoordinate(labelElement);
                    const relative_X = imageCoordinate.left + label.x - labelCoordinate.left;
                    const relative_Y = imageCoordinate.top + label.y - labelCoordinate.top;
                    const relativeCoordinate = {
                        x: relative_X,
                        y: relative_Y,
                    };
                    setTimeout(() => {
                        if (!label.relativeCoordinate) {
                            this.setRelativeCoordinate.emit({ key: label.key || '', relativeCoordinate });
                        }
                    });
                }
            });
        }
        if (changes['isImageLoaded']) {
            this.runImageValidation.emit(this.isImageLoaded);
        }
        if (changes['labels']) {
            this.runLabelValidation.emit({ labels: this.labels, answers: this.answers, choices: this.choices });
        }
    }
    onClickingOpenModal() {
        this.content = '';
        this.openModal = true;
        this.editedObject = null;
        this.showEditor = true;
    }
    getLabelPosition(choice) {
        const draggedLabel = this.answers.find((answer) => answer.choice === choice.key);
        const defaultCoOrdinate = { x: 0, y: 0 };
        if (draggedLabel) {
            const labelItem = this.labels.find((label) => label.key === draggedLabel.label);
            return labelItem ? labelItem.relativeCoordinate : defaultCoOrdinate;
        }
        return defaultCoOrdinate;
    }
    onCloseModal() {
        this.showEditor = false;
        this.openModal = false;
    }
    onConfirmAction() {
        if (this.editedObject) {
            const payload = {
                content: this.content,
                editedObject: this.editedObject,
            };
            this.updateLabelOrDistractor.emit(payload);
        }
        else {
            this.addLabelOrDistractor.emit({ content: this.content });
        }
        this.onCloseModal();
    }
    onContentChange(value) {
        this.content = value;
    }
    hasImage(content) {
        return content.indexOf('<img') >= 0;
    }
    onSelectedOption(selectedOptions, selectedObject) {
        this.editedObject = selectedObject;
        if (selectedOptions.value === 'edit') {
            const content = selectedObject.content;
            this.content = content;
            this.openModal = true;
            setTimeout(() => {
                this.showEditor = true;
            });
        }
        else {
            this.deleteLabelOrDistractor.emit(selectedObject);
        }
    }
    getModalText() {
        return this.editedObject ? 'Edit label' : 'Add label';
    }
    getButtonText() {
        return this.editedObject ? 'Save' : 'Add label';
    }
    canMoveUp(choice) {
        return this.choices.indexOf(choice) !== 0;
    }
    canMoveDown(choice) {
        return this.choices.indexOf(choice) < this.choices.length - 1;
    }
    getChoiceContent(content) {
        if (content) {
            if (this.hasImage(content)) {
                return content;
            }
            return this.htmlToPlaintextPipe.transform(content);
        }
        return '';
    }
    isDraggable() {
        return this.isImageLoaded;
    }
    onDragBegin(element, choiceKey) {
        this.draggingElement = element;
        this.draggingStart.emit(choiceKey);
        this.dragStart = true;
    }
    onMoveEnd(coordinate, choiceKey) {
        this.draggingEnd.emit({ coordinate, draggingElement: this.draggingElement, choiceKey });
        this.dragStart = false;
    }
    deleteLabelDropZone(choice) {
        this.deleteLabelFromDropZone.emit(choice);
    }
}
ImageLabelsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelsComponent, deps: [{ token: HtmlToPlaintextPipe }, { token: ImageLabelDndService }], target: i0.ɵɵFactoryTarget.Component });
ImageLabelsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ImageLabelsComponent, selector: "aa-image-labels", inputs: { choices: "choices", labels: "labels", answers: "answers", backgroundImage: "backgroundImage", isImageLoaded: "isImageLoaded" }, outputs: { addLabelOrDistractor: "addLabelOrDistractor", updateLabelOrDistractor: "updateLabelOrDistractor", confirmAction: "confirmAction", deleteLabelOrDistractor: "deleteLabelOrDistractor", moveChoiceDown: "moveChoiceDown", moveChoiceUp: "moveChoiceUp", draggingEnd: "draggingEnd", draggingStart: "draggingStart", deleteLabelFromDropZone: "deleteLabelFromDropZone", setRelativeCoordinate: "setRelativeCoordinate", runImageValidation: "runImageValidation", runLabelValidation: "runLabelValidation" }, viewQueries: [{ propertyName: "focusElementOnClose", first: true, predicate: ["addLabelRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<h2 translate\n    class=\"label-header\">Labels*</h2>\n<div *ngIf=\"choices.length\"\n     class=\"label-instructions\"\n     translate>Labels will display in the same order for students unless randomization is turned on.</div>\n<div class=\"image-labels\">\n  <air-modal  [isOpen]=\"openModal\"\n              [titleText]=\"getModalText()\"\n              [confirmButtonText]=\"getButtonText()\"\n              [disableConfirm]=\"!content\"\n              [focusElementOnClose]=\"focusElementOnClose\"\n              (close)=\"onCloseModal()\"\n              (cancel)=\"onCloseModal()\"\n              (confirm)=\"onConfirmAction()\">\n    <mhe-editor *ngIf=\"showEditor\"\n                [toggleRichTextMenuBar]=\"false\"\n                [maxCharacter]=\"90\"\n                [noMedia]=\"true\"\n                [imageOnly]=\"true\"\n                [content]=\"content\"\n                [showResize]=\"false\"\n                (contentChange)=\"onContentChange($event)\"></mhe-editor>\n  </air-modal>\n  <div class=\"image-label-wrapper\"\n       *ngIf=\"choices.length\">\n    <div class=\"label-panel\"\n         *ngFor=\"let choice of choices; let index = index\">\n         <div class=\"arrow\">\n           <button class=\"ahe-icon ahe-icon-up-arrow\"\n                   *ngIf=\"canMoveUp(choice)\"\n                   [ngClass]=\"{'single-arrow': !canMoveDown(choice)}\"\n                   aria-label=\"Move up\"\n                   (click)=\"moveChoiceUp.emit(choice)\"></button>\n           <button class=\"ahe-icon ahe-icon-down-arrow\"\n                   *ngIf=\"canMoveDown(choice)\"\n                   [ngClass]=\"{'single-arrow': !canMoveUp(choice)}\"\n                   aria-label=\"Move down\"\n                   (click)=\"moveChoiceDown.emit(choice)\"></button>\n         </div>\n      <div [id]=\"'label-'+ index +'-back'\"\n        class=\"label-wrapper hidden-label\"\n        [ngClass]=\"hasImage(choice.content) ? 'image-width' : ''\">\n        <div class=\"image-label-box left-option\"\n             [ngClass]=\"hasImage(choice.content) ? 'label-image-wrapper' : 'label-text-wrapper'\"\n             tabindex=\"0\">\n          <span></span>\n          <div class=\"image-label-text\">\n            <div class=\"image-label-text-content\"\n                 [title]=\"getChoiceContent(choice.content)\"\n                 [ngClass]=\"{'has-image': hasImage(choice.content)}\"\n                 [innerHTML]=\"getChoiceContent(choice.content)\"></div>\n          </div>\n          <div class=\"label_options\">\n          <lh-drop-menu\n            [options]=\"kebabMenuOptions\"\n            automationIdPrefix=\"choice-{{index}}\"\n            (optionSelected)=\"onSelectedOption($event, choice)\"\n          >\n            <i class=\"dpg-icon-system-kebab\"></i>\n          </lh-drop-menu>\n          </div>\n        </div>\n      </div>\n      <div [id]=\"'label-'+ index +'-front'\"\n           class=\"label-wrapper draggable\"\n           [ngClass]=\"[(getLabelPosition(choice)?.x) ? 'label-on-image' : '',\n                       hasImage(choice.content) ? 'image-width' : '',\n                       dragStart ? 'drag-start' : '']\"\n           [ngDraggable]=\"isDraggable()\"\n           [handle]=\"dragHandle\"\n           [position]=\"getLabelPosition(choice)\"\n           (started)=\"onDragBegin($event, choice.key)\"\n           (endOffset)=\"onMoveEnd($event, choice.key)\">\n        <div class=\"image-label-box\"\n             [ngClass]=\"hasImage(choice.content) ? 'label-image-wrapper' : 'label-text-wrapper'\"\n             tabindex=\"0\">\n          <div class=\"moving-arrow\"\n               #dragHandle>\n            <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n          </div>\n          <div class=\"image-label-text\">\n            <div class=\"image-label-text-content\"\n                 [title]=\"getChoiceContent(choice.content)\"\n                 [ngClass]=\"{'has-image': hasImage(choice.content)}\"\n                 [innerHTML]=\"getChoiceContent(choice.content)\"></div>\n          </div>\n          <div *ngIf=\"!getLabelPosition(choice)?.x && !getLabelPosition(choice)?.y\"\n               class=\"label_options\">\n            <lh-drop-menu\n              [options]=\"kebabMenuOptions\"\n              automationIdPrefix=\"choice-{{index}}\"\n              (optionSelected)=\"onSelectedOption($event, choice)\"\n            >\n              <i class=\"dpg-icon-system-kebab\"></i>\n            </lh-drop-menu>\n          </div>\n          <div *ngIf=\"getLabelPosition(choice)?.x\">\n            <aa-trash-can (delete)=\"deleteLabelDropZone(choice)\"></aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n<button #addLabelRef\n        type=\"button\"\n        class=\"add-label\"\n        *ngIf=\"choices.length < 20\"\n        (click)=\"onClickingOpenModal()\"><span translate>+ Add Label</span></button>\n", styles: [".add-label{background:transparent;border:none}.add-label:focus{border:2px solid #006a7c}.label-instructions{font-size:14px}.label_options{display:inline}.image-labels{padding-left:18px}.image-labels .upload-option{width:100%}.image-labels .image-label-wrapper .label-panel{position:relative;margin:10px 0 20px;display:flex}.image-labels .image-label-wrapper .label-wrapper{background:#fff;text-align:left;position:relative;border-radius:6px}.image-labels .image-label-wrapper .label-wrapper.drag-start{z-index:2}.image-labels .image-label-wrapper .label-wrapper.image-width{width:140px;display:inline-block}.image-labels .image-label-wrapper .label-wrapper.draggable{height:100%}.image-labels .image-label-wrapper .label-wrapper.hidden-label{position:absolute;margin:0;top:0}.image-labels .image-label-wrapper .label-wrapper .image-label-box{background:white;border:1px solid;border-radius:5px;text-align:left;padding:7px 5px;display:flex;align-items:center;justify-content:space-between}.image-labels .image-label-wrapper .label-wrapper .image-label-box.left-option{width:calc(100% - 20px)}.image-labels .image-label-wrapper .label-wrapper .image-label-box.label-text-wrapper{height:44px;max-width:200px;min-width:160px;width:100%}.image-labels .image-label-wrapper .label-wrapper .image-label-box.label-image-wrapper{height:86px;width:140px}.image-labels .image-label-wrapper .label-wrapper.label-on-image{z-index:1}.image-labels .image-label-wrapper .label-wrapper .moving-arrow{display:flex;flex-direction:column;justify-content:center;cursor:pointer}.image-labels .image-label-wrapper .label-wrapper .ahe-icon{height:18px;width:18px}.image-labels .image-label-wrapper .label-wrapper .image-label-text{display:flex;flex-direction:column;justify-content:center;text-align:center;width:80%}.image-labels .image-label-wrapper .label-wrapper .image-label-text .text-wrapper{height:26px;max-width:200px;min-width:160px}.image-labels .image-label-wrapper .label-wrapper .image-label-text .image-wrapper{height:70px;width:140px}.image-labels .image-label-wrapper .label-wrapper .image-label-text-content{display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.image-labels .image-label-wrapper .label-wrapper .image-label-text-content.has-image{height:80px;width:80px}.image-labels .image-label-wrapper .label-wrapper .kebab-btn{float:right;padding-top:4px}.image-labels .image-label-wrapper .arrow{width:5px;display:flex;position:absolute;left:-20px;flex-direction:column;justify-content:center}.image-labels .image-label-wrapper .arrow .ahe-icon{height:13px;width:13px;margin-left:3px;display:inline-block;background-repeat:no-repeat;border:none;padding:0}.image-labels .image-label-wrapper .arrow .ahe-icon.ahe-icon-up-arrow{margin-bottom:5px;background:transparent}.image-labels .image-label-wrapper .arrow .ahe-icon.ahe-icon-down-arrow{background:transparent}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "directive", type: i5.AngularDraggableDirective, selector: "[ngDraggable]", inputs: ["outOfBounds", "gridSize", "inBounds", "trackPosition", "scale", "preventDefaultEvent", "position", "lockAxis", "zIndex", "ngDraggable", "bounds", "handle", "zIndexMoving"], outputs: ["started", "stopped", "edge", "movingOffset", "endOffset"], exportAs: ["ngDraggable"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: i3$1.LhDropMenuComponent, selector: "lh-drop-menu", inputs: ["options", "tooltipText", "btnClass", "uniqueName", "automationIdPrefix"], outputs: ["optionSelected"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageLabelsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-image-labels', changeDetection: ChangeDetectionStrategy.OnPush, template: "<h2 translate\n    class=\"label-header\">Labels*</h2>\n<div *ngIf=\"choices.length\"\n     class=\"label-instructions\"\n     translate>Labels will display in the same order for students unless randomization is turned on.</div>\n<div class=\"image-labels\">\n  <air-modal  [isOpen]=\"openModal\"\n              [titleText]=\"getModalText()\"\n              [confirmButtonText]=\"getButtonText()\"\n              [disableConfirm]=\"!content\"\n              [focusElementOnClose]=\"focusElementOnClose\"\n              (close)=\"onCloseModal()\"\n              (cancel)=\"onCloseModal()\"\n              (confirm)=\"onConfirmAction()\">\n    <mhe-editor *ngIf=\"showEditor\"\n                [toggleRichTextMenuBar]=\"false\"\n                [maxCharacter]=\"90\"\n                [noMedia]=\"true\"\n                [imageOnly]=\"true\"\n                [content]=\"content\"\n                [showResize]=\"false\"\n                (contentChange)=\"onContentChange($event)\"></mhe-editor>\n  </air-modal>\n  <div class=\"image-label-wrapper\"\n       *ngIf=\"choices.length\">\n    <div class=\"label-panel\"\n         *ngFor=\"let choice of choices; let index = index\">\n         <div class=\"arrow\">\n           <button class=\"ahe-icon ahe-icon-up-arrow\"\n                   *ngIf=\"canMoveUp(choice)\"\n                   [ngClass]=\"{'single-arrow': !canMoveDown(choice)}\"\n                   aria-label=\"Move up\"\n                   (click)=\"moveChoiceUp.emit(choice)\"></button>\n           <button class=\"ahe-icon ahe-icon-down-arrow\"\n                   *ngIf=\"canMoveDown(choice)\"\n                   [ngClass]=\"{'single-arrow': !canMoveUp(choice)}\"\n                   aria-label=\"Move down\"\n                   (click)=\"moveChoiceDown.emit(choice)\"></button>\n         </div>\n      <div [id]=\"'label-'+ index +'-back'\"\n        class=\"label-wrapper hidden-label\"\n        [ngClass]=\"hasImage(choice.content) ? 'image-width' : ''\">\n        <div class=\"image-label-box left-option\"\n             [ngClass]=\"hasImage(choice.content) ? 'label-image-wrapper' : 'label-text-wrapper'\"\n             tabindex=\"0\">\n          <span></span>\n          <div class=\"image-label-text\">\n            <div class=\"image-label-text-content\"\n                 [title]=\"getChoiceContent(choice.content)\"\n                 [ngClass]=\"{'has-image': hasImage(choice.content)}\"\n                 [innerHTML]=\"getChoiceContent(choice.content)\"></div>\n          </div>\n          <div class=\"label_options\">\n          <lh-drop-menu\n            [options]=\"kebabMenuOptions\"\n            automationIdPrefix=\"choice-{{index}}\"\n            (optionSelected)=\"onSelectedOption($event, choice)\"\n          >\n            <i class=\"dpg-icon-system-kebab\"></i>\n          </lh-drop-menu>\n          </div>\n        </div>\n      </div>\n      <div [id]=\"'label-'+ index +'-front'\"\n           class=\"label-wrapper draggable\"\n           [ngClass]=\"[(getLabelPosition(choice)?.x) ? 'label-on-image' : '',\n                       hasImage(choice.content) ? 'image-width' : '',\n                       dragStart ? 'drag-start' : '']\"\n           [ngDraggable]=\"isDraggable()\"\n           [handle]=\"dragHandle\"\n           [position]=\"getLabelPosition(choice)\"\n           (started)=\"onDragBegin($event, choice.key)\"\n           (endOffset)=\"onMoveEnd($event, choice.key)\">\n        <div class=\"image-label-box\"\n             [ngClass]=\"hasImage(choice.content) ? 'label-image-wrapper' : 'label-text-wrapper'\"\n             tabindex=\"0\">\n          <div class=\"moving-arrow\"\n               #dragHandle>\n            <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n          </div>\n          <div class=\"image-label-text\">\n            <div class=\"image-label-text-content\"\n                 [title]=\"getChoiceContent(choice.content)\"\n                 [ngClass]=\"{'has-image': hasImage(choice.content)}\"\n                 [innerHTML]=\"getChoiceContent(choice.content)\"></div>\n          </div>\n          <div *ngIf=\"!getLabelPosition(choice)?.x && !getLabelPosition(choice)?.y\"\n               class=\"label_options\">\n            <lh-drop-menu\n              [options]=\"kebabMenuOptions\"\n              automationIdPrefix=\"choice-{{index}}\"\n              (optionSelected)=\"onSelectedOption($event, choice)\"\n            >\n              <i class=\"dpg-icon-system-kebab\"></i>\n            </lh-drop-menu>\n          </div>\n          <div *ngIf=\"getLabelPosition(choice)?.x\">\n            <aa-trash-can (delete)=\"deleteLabelDropZone(choice)\"></aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n<button #addLabelRef\n        type=\"button\"\n        class=\"add-label\"\n        *ngIf=\"choices.length < 20\"\n        (click)=\"onClickingOpenModal()\"><span translate>+ Add Label</span></button>\n", styles: [".add-label{background:transparent;border:none}.add-label:focus{border:2px solid #006a7c}.label-instructions{font-size:14px}.label_options{display:inline}.image-labels{padding-left:18px}.image-labels .upload-option{width:100%}.image-labels .image-label-wrapper .label-panel{position:relative;margin:10px 0 20px;display:flex}.image-labels .image-label-wrapper .label-wrapper{background:#fff;text-align:left;position:relative;border-radius:6px}.image-labels .image-label-wrapper .label-wrapper.drag-start{z-index:2}.image-labels .image-label-wrapper .label-wrapper.image-width{width:140px;display:inline-block}.image-labels .image-label-wrapper .label-wrapper.draggable{height:100%}.image-labels .image-label-wrapper .label-wrapper.hidden-label{position:absolute;margin:0;top:0}.image-labels .image-label-wrapper .label-wrapper .image-label-box{background:white;border:1px solid;border-radius:5px;text-align:left;padding:7px 5px;display:flex;align-items:center;justify-content:space-between}.image-labels .image-label-wrapper .label-wrapper .image-label-box.left-option{width:calc(100% - 20px)}.image-labels .image-label-wrapper .label-wrapper .image-label-box.label-text-wrapper{height:44px;max-width:200px;min-width:160px;width:100%}.image-labels .image-label-wrapper .label-wrapper .image-label-box.label-image-wrapper{height:86px;width:140px}.image-labels .image-label-wrapper .label-wrapper.label-on-image{z-index:1}.image-labels .image-label-wrapper .label-wrapper .moving-arrow{display:flex;flex-direction:column;justify-content:center;cursor:pointer}.image-labels .image-label-wrapper .label-wrapper .ahe-icon{height:18px;width:18px}.image-labels .image-label-wrapper .label-wrapper .image-label-text{display:flex;flex-direction:column;justify-content:center;text-align:center;width:80%}.image-labels .image-label-wrapper .label-wrapper .image-label-text .text-wrapper{height:26px;max-width:200px;min-width:160px}.image-labels .image-label-wrapper .label-wrapper .image-label-text .image-wrapper{height:70px;width:140px}.image-labels .image-label-wrapper .label-wrapper .image-label-text-content{display:inline-block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.image-labels .image-label-wrapper .label-wrapper .image-label-text-content.has-image{height:80px;width:80px}.image-labels .image-label-wrapper .label-wrapper .kebab-btn{float:right;padding-top:4px}.image-labels .image-label-wrapper .arrow{width:5px;display:flex;position:absolute;left:-20px;flex-direction:column;justify-content:center}.image-labels .image-label-wrapper .arrow .ahe-icon{height:13px;width:13px;margin-left:3px;display:inline-block;background-repeat:no-repeat;border:none;padding:0}.image-labels .image-label-wrapper .arrow .ahe-icon.ahe-icon-up-arrow{margin-bottom:5px;background:transparent}.image-labels .image-label-wrapper .arrow .ahe-icon.ahe-icon-down-arrow{background:transparent}\n"] }]
        }], ctorParameters: function () { return [{ type: HtmlToPlaintextPipe }, { type: ImageLabelDndService }]; }, propDecorators: { choices: [{
                type: Input
            }], labels: [{
                type: Input
            }], answers: [{
                type: Input
            }], backgroundImage: [{
                type: Input
            }], isImageLoaded: [{
                type: Input
            }], addLabelOrDistractor: [{
                type: Output
            }], updateLabelOrDistractor: [{
                type: Output
            }], confirmAction: [{
                type: Output
            }], deleteLabelOrDistractor: [{
                type: Output
            }], moveChoiceDown: [{
                type: Output
            }], moveChoiceUp: [{
                type: Output
            }], draggingEnd: [{
                type: Output
            }], draggingStart: [{
                type: Output
            }], deleteLabelFromDropZone: [{
                type: Output
            }], setRelativeCoordinate: [{
                type: Output
            }], runImageValidation: [{
                type: Output
            }], runLabelValidation: [{
                type: Output
            }], focusElementOnClose: [{
                type: ViewChild,
                args: ['addLabelRef', { static: false }]
            }] } });

class ImageDistractorsComponent {
    constructor(store, imageLabelService, elRef) {
        this.store = store;
        this.imageLabelService = imageLabelService;
        this.elRef = elRef;
        this.setRelativeCoordinate = new EventEmitter();
        this.distractorDragEnd = new EventEmitter();
        this.distractorMoveEnd = new EventEmitter();
        this.airImageLabeling$ = this.store.select(getAirImageLabeling);
        this.distractorDropzoneCount = 3;
    }
    onDistractorDragEnd($event) {
        this.draggingDistractor = $event;
    }
    getDistractorPosition(key) {
        const label = this.labels.find((label) => label.key === key);
        return label && label.relativeCoordinate;
    }
    onDistractorMoveEnd(coordinate) {
        const imageCoordinate = this.imageLabelService.getImageCoordinate();
        const distractorElement = document.getElementById('distractor-dropzone-back');
        if (!distractorElement) {
            return;
        }
        const distractorCoordinate = this.imageLabelService.getElementCoordinate(distractorElement);
        const isDistractorInDropZone = this.imageLabelService.isDropingZone(imageCoordinate, distractorCoordinate, coordinate, distractorElement);
        const distractorCoordinateWRTImage = this.imageLabelService.calculateLabelCoordinateWRTImage(imageCoordinate, distractorCoordinate, coordinate);
        const isDropzoneAlreadyDragged = this.draggingDistractor.getAttribute('isDragged');
        if (isDropzoneAlreadyDragged === 'false') {
            const distractorDropzoneId = 'distractor-' + this.distractorDropzoneCount + '-dropzone-back';
            this.container.createEmbeddedView(this.template);
            this.draggingDistractor.setAttribute('isDragged', 'true');
            this.distractorDropzoneCount++;
            this.draggingDistractor.setAttribute('id', distractorDropzoneId);
            const trashCanElem = this.elRef.nativeElement.querySelector('#' + distractorDropzoneId + ' button.trash-can');
            trashCanElem.addEventListener('click', this.deleteDistractorDropzone.bind(this, distractorDropzoneId));
        }
        if (isDistractorInDropZone) {
            this.store.dispatch(new AddOrUpdateDistractorDropzoneAction({
                key: this.draggingDistractor.getAttribute('id'),
                x: distractorCoordinateWRTImage.x,
                y: distractorCoordinateWRTImage.y,
                height: distractorCoordinate.height,
                width: distractorCoordinate.width,
                relativeCoordinate: isDistractorInDropZone ? coordinate : { x: 0, y: 0 },
            }));
            this.airImageLabeling$.pipe(take(1)).subscribe((item) => {
                let isOverLapping = false;
                const labelKey = this.getLabelKeyOfDistractorDropzone(this.draggingDistractor, item.distractorDropzones);
                item.labels.forEach((label) => {
                    if (label.key !== labelKey && !isOverLapping) {
                        isOverLapping = this.imageLabelService.isOverlapping(label, {
                            key: '',
                            x: distractorCoordinateWRTImage.x,
                            y: distractorCoordinateWRTImage.y,
                            height: distractorCoordinate.height,
                            width: distractorCoordinate.width,
                        });
                    }
                });
                if (isOverLapping) {
                    const draggingDropZoneId = this.draggingDistractor.getAttribute('id');
                    document.getElementById(draggingDropZoneId)?.remove();
                    this.store.dispatch(new RemoveDistractorDropzoneAction({ dropzoneKey: draggingDropZoneId }));
                }
            });
        }
        else {
            const draggingDropZoneId = this.draggingDistractor.getAttribute('id');
            document.getElementById(draggingDropZoneId)?.remove();
            this.store.dispatch(new RemoveDistractorDropzoneAction({ dropzoneKey: draggingDropZoneId }));
        }
    }
    getLabelKeyOfDistractorDropzone(draggingElement, distractorDropzoneList) {
        const draggingDropZoneId = draggingElement.getAttribute('id');
        const label = distractorDropzoneList.length &&
            distractorDropzoneList.find((dropzone) => {
                return dropzone.dropzoneId === draggingDropZoneId;
            });
        return label && label.labelKey;
    }
    deleteDropZone(dropzone) {
        const dropzoneId = dropzone.getAttribute('id');
        dropzone.remove();
        this.store.dispatch(new DeleteDistractorDropZoneAction(dropzoneId));
    }
    deleteDistractorDropzone(dropzoneId) {
        this.deleteDropZone(document.getElementById(dropzoneId));
    }
}
ImageDistractorsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageDistractorsComponent, deps: [{ token: i1.Store }, { token: ImageLabelDndService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
ImageDistractorsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ImageDistractorsComponent, selector: "aa-image-distractors", inputs: { distractorDropzones: "distractorDropzones", labels: "labels", answers: "answers", choices: "choices", isImageLoaded: "isImageLoaded", isEditMode: "isEditMode", draggingDistractor: "draggingDistractor" }, outputs: { setRelativeCoordinate: "setRelativeCoordinate", distractorDragEnd: "distractorDragEnd", distractorMoveEnd: "distractorMoveEnd" }, viewQueries: [{ propertyName: "template", first: true, predicate: ["clone"], descendants: true, static: true }, { propertyName: "container", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef, static: true }], ngImport: i0, template: "<span translate>Drag to a location on background image to create a distractor drop zone.</span>\n<div #container\n     id=\"distractor-dropzone-back\"\n     class=\"distractor-dropzone hidden-distractor\">\n  <div class=\"moving-arrow\">\n    <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n  </div>\n  <span translate>Distractor drop zone</span>\n</div>\n<div *ngIf=\"isEditMode\">\n  <div *ngFor=\"let distractorDropzone of distractorDropzones; let index = index\"\n       ngDraggable\n       class=\"distractor-dropzone\"\n       [position]=\"getDistractorPosition(distractorDropzone.labelKey)\"\n       [id]=\"distractorDropzone.dropzoneId\"\n       [attr.index]=index\n       [attr.isDragged]=true\n       (stopped)=\"onDistractorDragEnd($event)\"\n       (endOffset)=\"onDistractorMoveEnd($event)\">\n    <div class=\"moving-arrow\">\n      <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n    </div>\n    <span translate>Distractor drop zone</span>\n    <aa-trash-can (delete)=\"deleteDistractorDropzone(distractorDropzone?.dropzoneId)\"></aa-trash-can>\n  </div>\n</div>\n<div class=\"distractor-dropzone\"\n     ngDraggable\n     [attr.isDragged]=false\n     (stopped)=\"onDistractorDragEnd($event)\"\n     (endOffset)=\"onDistractorMoveEnd($event)\">\n  <div class=\"moving-arrow\">\n    <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n  </div>\n  <span translate class=\"text-truncate\">Distractor drop zone</span>\n  <aa-trash-can></aa-trash-can>\n</div>\n<ng-template #clone>\n  <div class=\"distractor-dropzone\"\n       ngDraggable\n       [attr.isDragged]=false\n       (stopped)=\"onDistractorDragEnd($event)\"\n       (endOffset)=\"onDistractorMoveEnd($event)\">\n    <div class=\"moving-arrow\">\n      <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n    </div>\n    <span translate>Distractor drop zone</span>\n    <aa-trash-can></aa-trash-can>\n  </div>\n</ng-template>\n", styles: [".distractor-dropzone{border:1px solid #a9a9a9;height:44px;width:200px;padding:9px;border-radius:4px;background:white;display:flex;position:absolute;margin:20px 0}.distractor-dropzone .moving-arrow{display:inline-block}.distractor-dropzone .moving-arrow .ahe-icon{height:18px;width:18px;margin-top:1px;cursor:pointer}.distractor-dropzone[isdragged=false] aa-trash-can{display:none}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i5.AngularDraggableDirective, selector: "[ngDraggable]", inputs: ["outOfBounds", "gridSize", "inBounds", "trackPosition", "scale", "preventDefaultEvent", "position", "lockAxis", "zIndex", "ngDraggable", "bounds", "handle", "zIndexMoving"], outputs: ["started", "stopped", "edge", "movingOffset", "endOffset"], exportAs: ["ngDraggable"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageDistractorsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-image-distractors', template: "<span translate>Drag to a location on background image to create a distractor drop zone.</span>\n<div #container\n     id=\"distractor-dropzone-back\"\n     class=\"distractor-dropzone hidden-distractor\">\n  <div class=\"moving-arrow\">\n    <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n  </div>\n  <span translate>Distractor drop zone</span>\n</div>\n<div *ngIf=\"isEditMode\">\n  <div *ngFor=\"let distractorDropzone of distractorDropzones; let index = index\"\n       ngDraggable\n       class=\"distractor-dropzone\"\n       [position]=\"getDistractorPosition(distractorDropzone.labelKey)\"\n       [id]=\"distractorDropzone.dropzoneId\"\n       [attr.index]=index\n       [attr.isDragged]=true\n       (stopped)=\"onDistractorDragEnd($event)\"\n       (endOffset)=\"onDistractorMoveEnd($event)\">\n    <div class=\"moving-arrow\">\n      <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n    </div>\n    <span translate>Distractor drop zone</span>\n    <aa-trash-can (delete)=\"deleteDistractorDropzone(distractorDropzone?.dropzoneId)\"></aa-trash-can>\n  </div>\n</div>\n<div class=\"distractor-dropzone\"\n     ngDraggable\n     [attr.isDragged]=false\n     (stopped)=\"onDistractorDragEnd($event)\"\n     (endOffset)=\"onDistractorMoveEnd($event)\">\n  <div class=\"moving-arrow\">\n    <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n  </div>\n  <span translate class=\"text-truncate\">Distractor drop zone</span>\n  <aa-trash-can></aa-trash-can>\n</div>\n<ng-template #clone>\n  <div class=\"distractor-dropzone\"\n       ngDraggable\n       [attr.isDragged]=false\n       (stopped)=\"onDistractorDragEnd($event)\"\n       (endOffset)=\"onDistractorMoveEnd($event)\">\n    <div class=\"moving-arrow\">\n      <span class=\"ahe-icon ahe-icon-movingtile\"></span>\n    </div>\n    <span translate>Distractor drop zone</span>\n    <aa-trash-can></aa-trash-can>\n  </div>\n</ng-template>\n", styles: [".distractor-dropzone{border:1px solid #a9a9a9;height:44px;width:200px;padding:9px;border-radius:4px;background:white;display:flex;position:absolute;margin:20px 0}.distractor-dropzone .moving-arrow{display:inline-block}.distractor-dropzone .moving-arrow .ahe-icon{height:18px;width:18px;margin-top:1px;cursor:pointer}.distractor-dropzone[isdragged=false] aa-trash-can{display:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: ImageLabelDndService }, { type: i0.ElementRef }]; }, propDecorators: { distractorDropzones: [{
                type: Input
            }], labels: [{
                type: Input
            }], answers: [{
                type: Input
            }], choices: [{
                type: Input
            }], isImageLoaded: [{
                type: Input
            }], setRelativeCoordinate: [{
                type: Output
            }], distractorDragEnd: [{
                type: Output
            }], distractorMoveEnd: [{
                type: Output
            }], isEditMode: [{
                type: Input
            }], draggingDistractor: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['clone', { static: true }]
            }], container: [{
                type: ViewChild,
                args: ['container', { read: ViewContainerRef, static: true }]
            }] } });

class FileUploadComponent$1 {
    constructor(http, fileUploadService) {
        this.http = http;
        this.fileUploadService = fileUploadService;
        this.onUploadedFile = new EventEmitter();
        this.onRemoveFile = new EventEmitter();
        this.fileName = '';
        this.isUploadingImage = false;
        this.isReturningFormData = false;
        this.isQti = false;
        this.imageLoad = new EventEmitter();
        this.openDeleteModal = false;
        this.isSaving = false;
        this.isUploadError = false;
        this.uploadUrl = '';
    }
    ngOnInit() {
        this.uploadUrl = `${this.fileUploadService.mmsUrl}/api/v1/` + this.url + `/en-us`;
        this.allowedExtensions = this.formats.split(/,\s*/).map((ext) => ext.trim());
    }
    startUpload() {
        this.isSaving = true;
        this.isUploadError = false;
    }
    finishUpload(error = false) {
        this.isSaving = false;
        this.isUploadError = error;
    }
    get fileTypeError() {
        // No error if files haven't been uploaded
        if (!this.files || !this.files.length) {
            return false;
        }
        // Error if the file extension doesn't match any allowed extensions
        return !this.allowedExtensions.some((ext) => {
            return this.files[0].name.substr(0 - ext.length).toLowerCase() === ext.toLowerCase();
        });
    }
    uploadFile(event) {
        const target = event.target;
        const files = target.files;
        if (!files || !files.length) {
            return;
        }
        this.files = files;
        if (this.fileTypeError) {
            return;
        }
        const formData = new FormData();
        formData.append('file', this.files[0]);
        const name = this.files[0].name;
        const body = formData;
        if (this.isReturningFormData) {
            this.onUploadedFile.emit({ content: body, url: '' });
            this.fileName = this.files[0].name;
            return;
        }
        this.startUpload();
        this.http.request('POST', this.uploadUrl, { body }).subscribe((response) => {
            this.onSuccessfulFileUpload({ content: name, url: response['url'] });
            this.finishUpload();
        }, (err) => {
            const treatAsSuccess = err.status === 409;
            if (treatAsSuccess) {
                this.onSuccessfulFileUpload({ content: name, url: err.error.url });
            }
            this.finishUpload(!treatAsSuccess);
        });
    }
    onSuccessfulFileUpload(data) {
        this.onUploadedFile.emit({ content: data.content, url: data.url });
        this.isSaving = false;
        this.fileName = this.isUploadingImage ? data.url : data.content;
    }
    removeFileConfirmation() {
        if (this.isQti) {
            this.removeFile();
        }
        else {
            this.openDeleteModal = true;
        }
    }
    removeFile() {
        this.fileName = '';
        this.onRemoveFile.emit({ content: '', url: '' });
        this.openDeleteModal = false;
    }
    getFormats() {
        return this.formats.replace(/\./g, '');
    }
    onImageLoad() {
        this.imageLoad.emit();
    }
    get supportedMediaMessage() {
        const hasMultipleFormats = this.formats.split(',').length;
        const formats = this.getFormats().toUpperCase();
        if (hasMultipleFormats === 1) {
            return `Only ${formats} files are supported.`;
        }
        return `File types supported are ${formats}.`;
    }
}
FileUploadComponent$1.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadComponent$1, deps: [{ token: i2.HttpClient }, { token: FileUploadService }], target: i0.ɵɵFactoryTarget.Component });
FileUploadComponent$1.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: FileUploadComponent$1, selector: "aa-file-upload", inputs: { fileName: "fileName", formats: "formats", url: "url", isUploadingImage: "isUploadingImage", isReturningFormData: "isReturningFormData", isQti: "isQti" }, outputs: { onUploadedFile: "onUploadedFile", onRemoveFile: "onRemoveFile", imageLoad: "imageLoad" }, ngImport: i0, template: "<div *ngIf=\"fileName\">\n  <span class=\"file-name-wrapper\"\n        *ngIf=\"!isUploadingImage\">\n    <span >{{ fileName }}</span>\n  <button class=\"remove-file\"\n          type=\"button\"\n          (click)=\"removeFileConfirmation()\">\n    <span aria-hidden=\"true\" class=\"ahe-icon ahe-icon-close remove-file-icon\"></span>\n  </button>\n  </span>\n  <img *ngIf=\"isUploadingImage\"\n       class=\"image\"\n       (load)=\"onImageLoad()\"\n       [src]=\"fileName\" />\n</div>\n<div *ngIf=\"!fileName\"\n     class=\"file-input-wrapper\">\n  <input class=\"input-file\"\n         type=\"file\"\n         (change)=\"uploadFile($event)\"\n         [accept]=\"formats\" />\n  <span *ngIf=\"isUploadError\"\n        translate\n        class=\"file-upload-error\">There was a problem uploading your file. Please try again.</span>\n  <span class=\"ahe-ui-loading-spinner overlay\"\n        *ngIf=\"isSaving\">\n\t\t<div class=\"loader spinner\">\n\t\t<svg class=\"circular\"\n\t\t\t   viewBox=\"25 25 50 50\">\n\t\t  <circle class=\"path\" cx=\"50\" cy=\"50\" r=\"10\" fill=\"none\" stroke-width=\"2\" stroke-miterlimit=\"10\"></circle>\n\t\t</svg>\n\t\t</div>\n\t</span>\n  <div class=\"file-limits\"\n       [ngClass]=\"{\n         'file-upload-error': fileTypeError\n       }\"\n       translate>{{ supportedMediaMessage }}</div>\n</div>\n<air-modal  [isOpen]=\"openDeleteModal\"\n           (close)=\"openDeleteModal = false\"\n           (cancel)=\"openDeleteModal = false\"\n           [titleText]=\"'Delete'\"\n           [confirmButtonText]=\"'Yes, delete'\"\n           (confirm)=\"removeFile()\">\n  <p translate>Are you sure you want to delete this file?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".remove-file{width:21px;height:21px;background:transparent;margin:0;padding:0;border:none}.remove-file-icon{width:10px;height:10px;line-height:10px;display:inline-block;background-repeat:no-repeat}.input-file{display:inline}.file-name-wrapper{background-color:#d2dbde;padding:3px 2px 3px 5px;border-radius:3px;color:#003b4c}.file-input-wrapper{position:relative}.file-limits{font-size:14px;margin-top:5px}.format_types{text-transform:uppercase}.file-upload-error{color:#e21f26}.image{max-width:650px;max-height:500px}.ahe-ui-loading-spinner.overlay{background:transparent;position:absolute;right:auto;left:auto}.ahe-ui-loading-spinner.overlay .spinner{width:35px;margin:0;float:right;top:10px}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadComponent$1, decorators: [{
            type: Component,
            args: [{ selector: 'aa-file-upload', template: "<div *ngIf=\"fileName\">\n  <span class=\"file-name-wrapper\"\n        *ngIf=\"!isUploadingImage\">\n    <span >{{ fileName }}</span>\n  <button class=\"remove-file\"\n          type=\"button\"\n          (click)=\"removeFileConfirmation()\">\n    <span aria-hidden=\"true\" class=\"ahe-icon ahe-icon-close remove-file-icon\"></span>\n  </button>\n  </span>\n  <img *ngIf=\"isUploadingImage\"\n       class=\"image\"\n       (load)=\"onImageLoad()\"\n       [src]=\"fileName\" />\n</div>\n<div *ngIf=\"!fileName\"\n     class=\"file-input-wrapper\">\n  <input class=\"input-file\"\n         type=\"file\"\n         (change)=\"uploadFile($event)\"\n         [accept]=\"formats\" />\n  <span *ngIf=\"isUploadError\"\n        translate\n        class=\"file-upload-error\">There was a problem uploading your file. Please try again.</span>\n  <span class=\"ahe-ui-loading-spinner overlay\"\n        *ngIf=\"isSaving\">\n\t\t<div class=\"loader spinner\">\n\t\t<svg class=\"circular\"\n\t\t\t   viewBox=\"25 25 50 50\">\n\t\t  <circle class=\"path\" cx=\"50\" cy=\"50\" r=\"10\" fill=\"none\" stroke-width=\"2\" stroke-miterlimit=\"10\"></circle>\n\t\t</svg>\n\t\t</div>\n\t</span>\n  <div class=\"file-limits\"\n       [ngClass]=\"{\n         'file-upload-error': fileTypeError\n       }\"\n       translate>{{ supportedMediaMessage }}</div>\n</div>\n<air-modal  [isOpen]=\"openDeleteModal\"\n           (close)=\"openDeleteModal = false\"\n           (cancel)=\"openDeleteModal = false\"\n           [titleText]=\"'Delete'\"\n           [confirmButtonText]=\"'Yes, delete'\"\n           (confirm)=\"removeFile()\">\n  <p translate>Are you sure you want to delete this file?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".remove-file{width:21px;height:21px;background:transparent;margin:0;padding:0;border:none}.remove-file-icon{width:10px;height:10px;line-height:10px;display:inline-block;background-repeat:no-repeat}.input-file{display:inline}.file-name-wrapper{background-color:#d2dbde;padding:3px 2px 3px 5px;border-radius:3px;color:#003b4c}.file-input-wrapper{position:relative}.file-limits{font-size:14px;margin-top:5px}.format_types{text-transform:uppercase}.file-upload-error{color:#e21f26}.image{max-width:650px;max-height:500px}.ahe-ui-loading-spinner.overlay{background:transparent;position:absolute;right:auto;left:auto}.ahe-ui-loading-spinner.overlay .spinner{width:35px;margin:0;float:right;top:10px}\n"] }]
        }], ctorParameters: function () { return [{ type: i2.HttpClient }, { type: FileUploadService }]; }, propDecorators: { onUploadedFile: [{
                type: Output
            }], onRemoveFile: [{
                type: Output
            }], fileName: [{
                type: Input
            }], formats: [{
                type: Input
            }], url: [{
                type: Input
            }], isUploadingImage: [{
                type: Input
            }], isReturningFormData: [{
                type: Input
            }], isQti: [{
                type: Input
            }], imageLoad: [{
                type: Output
            }] } });

class ImageUploadComponent {
    constructor() {
        this.imageUrl = '';
        this.showDeleteModal = true;
        this.updateBackgroundImage = new EventEmitter();
        this.deleteBackgroundImage = new EventEmitter();
        this.imageLoad = new EventEmitter();
        this.deleteBgImageModal = false;
        this.kebabMenuOptions = [
            {
                optionText: 'Delete',
                value: 'delete',
                behavior: OptionBehavior.OpensModal,
                optionAutomationIdPrefix: 'delete',
            },
        ];
    }
    removeFile() {
        this.updateBackgroundImage.emit({ content: '', url: '' });
    }
    uploadFile(fileData) {
        this.updateBackgroundImage.emit(fileData);
    }
    onSelectedOption(selection) {
        if (selection.value === 'delete') {
            if (this.showDeleteModal) {
                setTimeout(() => {
                    this.deleteBgImageModal = true;
                });
            }
            else {
                this.deleteBackgroundImage.emit();
            }
        }
    }
    onCloseBgImgModal() {
        this.deleteBgImageModal = false;
    }
    onDeletingBgImage() {
        this.deleteBackgroundImage.emit();
        this.onCloseBgImgModal();
    }
    onImageLoad() {
        this.imageLoad.emit();
    }
}
ImageUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageUploadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ImageUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ImageUploadComponent, selector: "aa-image-upload", inputs: { fileName: "fileName", imageUrl: "imageUrl", showDeleteModal: "showDeleteModal" }, outputs: { updateBackgroundImage: "updateBackgroundImage", deleteBackgroundImage: "deleteBackgroundImage", imageLoad: "imageLoad" }, ngImport: i0, template: "<div class=\"image-boundary\">\n  <aa-file-upload class=\"file-upload\"\n                  [fileName]=\"fileName\"\n                  [url]=\"'media/image'\"\n                  [formats]=\"'.jpg, .jpeg, .png'\"\n                  [isUploadingImage]=\"true\"\n                  (onRemoveFile)=\"removeFile()\"\n                  (imageLoad)=\"onImageLoad()\"\n                  (onUploadedFile)=\"uploadFile($event)\"></aa-file-upload>\n</div>\n\n<lh-drop-menu\n  *ngIf=\"fileName\"\n  [options]=\"kebabMenuOptions\"\n  automationIdPrefix=\"bg-image\"\n  (optionSelected)=\"onSelectedOption($event)\"\n>\n  <i class=\"dpg-icon-system-kebab\"></i>\n</lh-drop-menu>\n\n<air-modal  [isOpen]=\"deleteBgImageModal\"\n           (close)=\"onCloseBgImgModal()\"\n           (cancel)=\"onCloseBgImgModal()\"\n           [titleText]=\"'Delete?'\"\n           [confirmButtonText]=\"'Yes, delete'\"\n           (confirm)=\"onDeletingBgImage()\">\n  <p translate>Are you sure you want to delete this image?</p>\n  <p translate>\n    If you delete this image, all drop zones previously moved to the image will be removed from their locations.\n  </p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".file-upload{float:left}.image-boundary{max-height:500px;max-width:650px}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: i3$1.LhDropMenuComponent, selector: "lh-drop-menu", inputs: ["options", "tooltipText", "btnClass", "uniqueName", "automationIdPrefix"], outputs: ["optionSelected"] }, { kind: "component", type: FileUploadComponent$1, selector: "aa-file-upload", inputs: ["fileName", "formats", "url", "isUploadingImage", "isReturningFormData", "isQti"], outputs: ["onUploadedFile", "onRemoveFile", "imageLoad"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ImageUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-image-upload', template: "<div class=\"image-boundary\">\n  <aa-file-upload class=\"file-upload\"\n                  [fileName]=\"fileName\"\n                  [url]=\"'media/image'\"\n                  [formats]=\"'.jpg, .jpeg, .png'\"\n                  [isUploadingImage]=\"true\"\n                  (onRemoveFile)=\"removeFile()\"\n                  (imageLoad)=\"onImageLoad()\"\n                  (onUploadedFile)=\"uploadFile($event)\"></aa-file-upload>\n</div>\n\n<lh-drop-menu\n  *ngIf=\"fileName\"\n  [options]=\"kebabMenuOptions\"\n  automationIdPrefix=\"bg-image\"\n  (optionSelected)=\"onSelectedOption($event)\"\n>\n  <i class=\"dpg-icon-system-kebab\"></i>\n</lh-drop-menu>\n\n<air-modal  [isOpen]=\"deleteBgImageModal\"\n           (close)=\"onCloseBgImgModal()\"\n           (cancel)=\"onCloseBgImgModal()\"\n           [titleText]=\"'Delete?'\"\n           [confirmButtonText]=\"'Yes, delete'\"\n           (confirm)=\"onDeletingBgImage()\">\n  <p translate>Are you sure you want to delete this image?</p>\n  <p translate>\n    If you delete this image, all drop zones previously moved to the image will be removed from their locations.\n  </p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".file-upload{float:left}.image-boundary{max-height:500px;max-width:650px}\n"] }]
        }], propDecorators: { fileName: [{
                type: Input
            }], imageUrl: [{
                type: Input
            }], showDeleteModal: [{
                type: Input
            }], updateBackgroundImage: [{
                type: Output
            }], deleteBackgroundImage: [{
                type: Output
            }], imageLoad: [{
                type: Output
            }] } });

class AirImageLabelingComponent extends AirBaseComponent {
    constructor(store, imageLabelService, imageLabelingValidationService, configService) {
        super(store, getPureAirImageLabeling, LoadAirItemAction$a, ResetStoreAction$a);
        this.store = store;
        this.imageLabelService = imageLabelService;
        this.imageLabelingValidationService = imageLabelingValidationService;
        this.configService = configService;
        this.airImageLabeling$ = this.store.select(getAirImageLabeling);
        this.pureAirImageLabeling$ = this.store.select(getPureAirImageLabeling);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.fileName = '';
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onUpdateBackgroundImage(backgroundImage) {
        this.fileName = backgroundImage.url;
        this.store.dispatch(new UpdateBackgroundImageAction$1(backgroundImage));
    }
    onAddingLabelOrDistractor({ content }) {
        this.store.dispatch(new AddLabelOrDistractorAction({ content }));
    }
    onDeletingLabelOrDistractor(choice) {
        this.store.dispatch(new DeleteLabelOrDistractorAction(choice));
    }
    onUpdatingLabelOrDistractor({ content, editedObject }) {
        this.store.dispatch(new UpdateLabelOrDistractorAction({ content, editedObject }));
    }
    onMovingChoiceDown(choice) {
        this.store.dispatch(new MoveLabelDownAction(choice));
    }
    onMovingChoiceUp(choice) {
        this.store.dispatch(new MoveLabelUpAction(choice));
    }
    onDeleteBackgroundImage() {
        this.store.dispatch(new DeleteBackgroundImageAction$1());
        this.fileName = '';
    }
    onMoveEnd({ coordinate, draggingElement, choiceKey }) {
        const imageCoordinate = this.imageLabelService.getImageCoordinate();
        const labelElement = this.imageLabelService.getHiddenLabelElement(draggingElement);
        const labelCoordinate = this.imageLabelService.getElementCoordinate(labelElement);
        const isLabelInDropZone = this.imageLabelService.isDropingZone(imageCoordinate, labelCoordinate, coordinate, labelElement);
        const labelCoordinateWRTImage = this.imageLabelService.calculateLabelCoordinateWRTImage(imageCoordinate, labelCoordinate, coordinate);
        this.airImageLabeling$.pipe(take(1)).subscribe((item) => {
            const draggedLabel = item.answers.find((answer) => answer.choice === choiceKey);
            const draggedLabelKey = draggedLabel && draggedLabel.label;
            const isLabelOverLapping = item.labels.find((label) => {
                return (label.key !== draggedLabelKey &&
                    this.imageLabelService.isOverlapping({
                        key: '',
                        x: labelCoordinateWRTImage.x,
                        y: labelCoordinateWRTImage.y,
                        height: labelCoordinate.height,
                        width: labelCoordinate.width,
                    }, label));
            }) != null;
            if (isLabelOverLapping || !isLabelInDropZone) {
                this.store.dispatch(new RemoveLabelAction({ choiceKey }));
            }
            else if (draggedLabel) {
                this.store.dispatch(new UpdateChoiceInLabel({
                    key: draggedLabelKey,
                    x: labelCoordinateWRTImage.x,
                    y: labelCoordinateWRTImage.y,
                    height: labelCoordinate.height,
                    width: labelCoordinate.width,
                    relativeCoordinate: coordinate,
                }));
            }
            else {
                this.store.dispatch(new AddChoiceInLabel({
                    key: choiceKey,
                    x: labelCoordinateWRTImage.x,
                    y: labelCoordinateWRTImage.y,
                    height: labelCoordinate.height,
                    width: labelCoordinate.width,
                    relativeCoordinate: coordinate,
                }));
            }
        });
    }
    onImageLoad() {
        this.store.dispatch(new SetImageLoadingIndicator(true));
    }
    onDeleteLabelFromDropZone(choice) {
        this.store.dispatch(new DeleteLabelDropZoneAction(choice));
    }
    onSettingRelativeCoordinate({ key, relativeCoordinate }) {
        this.store.dispatch(new SetRelativeCoordinateAction({ key, relativeCoordinate }));
    }
    getImageError() {
        return this.allowInlineValidation && this.imageLabelingValidationService.getImageError();
    }
    getLabelError() {
        return this.allowInlineValidation && this.imageLabelingValidationService.getLabelError();
    }
    validateImage(isImageLoaded) {
        if (this.imageLabelingValidationService.getImageError().length) {
            this.imageLabelingValidationService.validateImage(isImageLoaded);
        }
    }
    validateLabels(payload) {
        if (this.imageLabelingValidationService.getLabelError().length) {
            const labels = payload.labels;
            const answers = payload.answers;
            const choices = payload.choices;
            this.imageLabelingValidationService.validateLabelCount(labels, answers, choices);
        }
    }
}
AirImageLabelingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirImageLabelingComponent, deps: [{ token: i1.Store }, { token: ImageLabelDndService }, { token: ImageLabelingValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirImageLabelingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirImageLabelingComponent, selector: "aa-air-image-labeling", usesInheritance: true, ngImport: i0, template: "<h2 translate>Background image and labels *</h2>\n\n<div class=\"image-label-container\"\n     [ngClass]=\"{'label-error': getLabelError()?.length}\">\n  <div class=\"label-container\">\n    <aa-image-labels [choices]=\"(airImageLabeling$ | async)?.choices\"\n                     [labels]=\"(airImageLabeling$ | async)?.labels\"\n                     [answers]=\"(airImageLabeling$ | async)?.answers\"\n                     [isImageLoaded]=\"(airImageLabeling$ | async)?.isImageLoaded\"\n                     [backgroundImage]=\"(airImageLabeling$ | async)?.backgroundImage\"\n                     (addLabelOrDistractor)=\"onAddingLabelOrDistractor($event)\"\n                     (updateLabelOrDistractor)=\"onUpdatingLabelOrDistractor($event)\"\n                     (deleteLabelOrDistractor)=\"onDeletingLabelOrDistractor($event)\"\n                     (moveChoiceDown)=\"onMovingChoiceDown($event)\"\n                     (moveChoiceUp)=\"onMovingChoiceUp($event)\"\n                     (deleteLabelFromDropZone)=\"onDeleteLabelFromDropZone($event)\"\n                     (draggingEnd)=\"onMoveEnd($event)\"\n                     (runLabelValidation)=\"validateLabels($event)\"\n                     (runImageValidation)=\"validateImage($event)\"\n                     (setRelativeCoordinate)=\"onSettingRelativeCoordinate($event)\">\n    </aa-image-labels>\n  </div>\n  <div class=\"image-container\">\n    <div class=\"upload-wrapper\">\n      <h2 translate>Background image *</h2>\n      <span *ngIf=\"(airImageLabeling$ | async)?.backgroundImage\"\n            class=\"item-instructions\">Drag labels to the background image.</span>\n      <div class=\"dropzone-wrapper\"\n           [ngClass]=\"{'image-error': getImageError()?.length}\">\n        <aa-image-upload id=\"image-upload\"\n                         [fileName]=\"(airImageLabeling$ | async)?.backgroundImage\"\n                         (imageLoad)=\"onImageLoad()\"\n                         (updateBackgroundImage)=\"onUpdateBackgroundImage($event)\"\n                         (deleteBackgroundImage)=\"onDeleteBackgroundImage()\">\n        </aa-image-upload>\n      </div>\n      <aa-inline-error [errors]=\"getImageError()\"></aa-inline-error>\n      <div class=\"distractor-wrapper\">\n        <div class=\"distractors\"\n             *ngIf=\"(airImageLabeling$ | async)?.backgroundImage\">\n          <aa-image-distractors [isEditMode]=\"(airImageLabeling$ | async)?.isEditMode\"\n                                [draggingDistractor]=\"draggingDistractor\"\n                                [isImageLoaded]=\"(airImageLabeling$ | async)?.isImageLoaded\"\n                                [distractorDropzones]=\"(airImageLabeling$ | async)?.initialDropzones\"\n                                [choices]=\"(airImageLabeling$ | async)?.choices\"\n                                [labels]=\"(airImageLabeling$ | async)?.labels\"\n                                [answers]=\"(airImageLabeling$ | async)?.answers\"\n                                (setRelativeCoordinate)=\"onSettingRelativeCoordinate($event)\">\n          </aa-image-distractors>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n<aa-inline-error [errors]=\"getLabelError()\"></aa-inline-error>\n", styles: [".image-label-container{border:1px solid #a9a9a9;width:100%;min-height:300px;display:flex}.image-label-container.label-error{border:1px solid #b0181e;border-radius:3px}.image-label-container .label-container{background:#f8f8f8;border-right:1px solid #a9a9a9;width:250px;padding:10px 10px 10px 20px}.image-label-container .label-container .add-label{background:transparent;border:none;padding-left:0}.image-label-container .image-container{width:70%;margin:10px 10px 10px 30px;padding-bottom:70px}.image-label-container .image-container .item-instructions{margin-bottom:10px;display:inline-block}.image-label-container .image-container .distractors{margin-top:24px}.image-label-container .image-container .upload-wrapper,.image-label-container .image-container .distractor-wrapper{display:inline-block;position:relative;width:100%}.image-label-container .image-container .upload-wrapper .dropzone-wrapper,.image-label-container .image-container .distractor-wrapper .dropzone-wrapper{position:relative}.image-label-container .image-container .upload-wrapper .dropzone-wrapper.image-error,.image-label-container .image-container .distractor-wrapper .dropzone-wrapper.image-error{border-radius:5px;border:1px solid #b0181e;display:inline-block;padding:10px;width:100%}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ImageLabelsComponent, selector: "aa-image-labels", inputs: ["choices", "labels", "answers", "backgroundImage", "isImageLoaded"], outputs: ["addLabelOrDistractor", "updateLabelOrDistractor", "confirmAction", "deleteLabelOrDistractor", "moveChoiceDown", "moveChoiceUp", "draggingEnd", "draggingStart", "deleteLabelFromDropZone", "setRelativeCoordinate", "runImageValidation", "runLabelValidation"] }, { kind: "component", type: ImageDistractorsComponent, selector: "aa-image-distractors", inputs: ["distractorDropzones", "labels", "answers", "choices", "isImageLoaded", "isEditMode", "draggingDistractor"], outputs: ["setRelativeCoordinate", "distractorDragEnd", "distractorMoveEnd"] }, { kind: "component", type: ImageUploadComponent, selector: "aa-image-upload", inputs: ["fileName", "imageUrl", "showDeleteModal"], outputs: ["updateBackgroundImage", "deleteBackgroundImage", "imageLoad"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirImageLabelingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-image-labeling', template: "<h2 translate>Background image and labels *</h2>\n\n<div class=\"image-label-container\"\n     [ngClass]=\"{'label-error': getLabelError()?.length}\">\n  <div class=\"label-container\">\n    <aa-image-labels [choices]=\"(airImageLabeling$ | async)?.choices\"\n                     [labels]=\"(airImageLabeling$ | async)?.labels\"\n                     [answers]=\"(airImageLabeling$ | async)?.answers\"\n                     [isImageLoaded]=\"(airImageLabeling$ | async)?.isImageLoaded\"\n                     [backgroundImage]=\"(airImageLabeling$ | async)?.backgroundImage\"\n                     (addLabelOrDistractor)=\"onAddingLabelOrDistractor($event)\"\n                     (updateLabelOrDistractor)=\"onUpdatingLabelOrDistractor($event)\"\n                     (deleteLabelOrDistractor)=\"onDeletingLabelOrDistractor($event)\"\n                     (moveChoiceDown)=\"onMovingChoiceDown($event)\"\n                     (moveChoiceUp)=\"onMovingChoiceUp($event)\"\n                     (deleteLabelFromDropZone)=\"onDeleteLabelFromDropZone($event)\"\n                     (draggingEnd)=\"onMoveEnd($event)\"\n                     (runLabelValidation)=\"validateLabels($event)\"\n                     (runImageValidation)=\"validateImage($event)\"\n                     (setRelativeCoordinate)=\"onSettingRelativeCoordinate($event)\">\n    </aa-image-labels>\n  </div>\n  <div class=\"image-container\">\n    <div class=\"upload-wrapper\">\n      <h2 translate>Background image *</h2>\n      <span *ngIf=\"(airImageLabeling$ | async)?.backgroundImage\"\n            class=\"item-instructions\">Drag labels to the background image.</span>\n      <div class=\"dropzone-wrapper\"\n           [ngClass]=\"{'image-error': getImageError()?.length}\">\n        <aa-image-upload id=\"image-upload\"\n                         [fileName]=\"(airImageLabeling$ | async)?.backgroundImage\"\n                         (imageLoad)=\"onImageLoad()\"\n                         (updateBackgroundImage)=\"onUpdateBackgroundImage($event)\"\n                         (deleteBackgroundImage)=\"onDeleteBackgroundImage()\">\n        </aa-image-upload>\n      </div>\n      <aa-inline-error [errors]=\"getImageError()\"></aa-inline-error>\n      <div class=\"distractor-wrapper\">\n        <div class=\"distractors\"\n             *ngIf=\"(airImageLabeling$ | async)?.backgroundImage\">\n          <aa-image-distractors [isEditMode]=\"(airImageLabeling$ | async)?.isEditMode\"\n                                [draggingDistractor]=\"draggingDistractor\"\n                                [isImageLoaded]=\"(airImageLabeling$ | async)?.isImageLoaded\"\n                                [distractorDropzones]=\"(airImageLabeling$ | async)?.initialDropzones\"\n                                [choices]=\"(airImageLabeling$ | async)?.choices\"\n                                [labels]=\"(airImageLabeling$ | async)?.labels\"\n                                [answers]=\"(airImageLabeling$ | async)?.answers\"\n                                (setRelativeCoordinate)=\"onSettingRelativeCoordinate($event)\">\n          </aa-image-distractors>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n<aa-inline-error [errors]=\"getLabelError()\"></aa-inline-error>\n", styles: [".image-label-container{border:1px solid #a9a9a9;width:100%;min-height:300px;display:flex}.image-label-container.label-error{border:1px solid #b0181e;border-radius:3px}.image-label-container .label-container{background:#f8f8f8;border-right:1px solid #a9a9a9;width:250px;padding:10px 10px 10px 20px}.image-label-container .label-container .add-label{background:transparent;border:none;padding-left:0}.image-label-container .image-container{width:70%;margin:10px 10px 10px 30px;padding-bottom:70px}.image-label-container .image-container .item-instructions{margin-bottom:10px;display:inline-block}.image-label-container .image-container .distractors{margin-top:24px}.image-label-container .image-container .upload-wrapper,.image-label-container .image-container .distractor-wrapper{display:inline-block;position:relative;width:100%}.image-label-container .image-container .upload-wrapper .dropzone-wrapper,.image-label-container .image-container .distractor-wrapper .dropzone-wrapper{position:relative}.image-label-container .image-container .upload-wrapper .dropzone-wrapper.image-error,.image-label-container .image-container .distractor-wrapper .dropzone-wrapper.image-error{border-radius:5px;border:1px solid #b0181e;display:inline-block;padding:10px;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: ImageLabelDndService }, { type: ImageLabelingValidationService }, { type: ConfigService }]; } });

class MatchingValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.airMatching$ = this.store.select(getAirMatching);
        this.pureAirMatching$ = this.store.select(getPureAirMatching);
        this.matchPromptError = '';
        this.matchChoiceError = '';
    }
    validate() {
        let isValid;
        this.pureAirMatching$.pipe(take(1)).subscribe((pureAirMatching) => {
            this.airMatching$.pipe(take(1)).subscribe((airMatching) => {
                const promptError = !!this.promptValidationService.validatePrompt(pureAirMatching.prompt);
                const matchPromptError = !!this.validateMatchPrompts(pureAirMatching.prompts, airMatching.choices);
                const matchChoiceError = !!this.validateChoice(pureAirMatching.prompts, airMatching.distractors);
                isValid = !(promptError || matchPromptError || matchChoiceError);
            });
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetMatchingErrors();
    }
    resetMatchingErrors() {
        this.matchPromptError = '';
        this.matchChoiceError = '';
    }
    getFilteredComponent(elements) {
        return elements.filter((element) => element.content.trim());
    }
    getMatchedCount(prompts) {
        return prompts.reduce((count, prompt) => {
            return (count += prompt.matches);
        }, 0);
    }
    // check if there is at least one unmatched prompt
    validateMatchPrompts(prompts, choices) {
        const validChoices = this.getFilteredComponent(choices);
        const unmatchedPrompts = !prompts.length || this.getMatchedCount(prompts) !== validChoices.length;
        this.matchPromptError = unmatchedPrompts ? E_MATCHING_UNMATCHED_PROMPT : '';
        return this.matchPromptError;
    }
    // check if there is at least two matching choices or distractors
    validateChoice(prompts, distractors) {
        const validDistractors = this.getFilteredComponent(distractors);
        const lessChoices = this.getMatchedCount(prompts) === 1 && !validDistractors.length && !this.matchPromptError;
        const unmatchedChoice = prompts.some((prompt) => !prompt.matches);
        if (unmatchedChoice) {
            this.matchChoiceError = E_MATCHING_UNMATCHED_CHOICE;
        }
        else {
            this.matchChoiceError = lessChoices ? E_MATCHING_LESS_CHOICES : '';
        }
        return this.matchChoiceError;
    }
    getMatchErrors() {
        const error = this.getPromptError();
        return this.matchChoiceError ? error.concat(this.matchChoiceError) : error;
    }
    getChoiceError() {
        return this.matchChoiceError ? [this.matchChoiceError] : [];
    }
    getPromptError() {
        return this.matchPromptError ? [this.matchPromptError] : [];
    }
}
MatchingValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MatchingValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
MatchingValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MatchingValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MatchingValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class MatchingChoicesComponent {
    constructor(matchingValidationService, configService) {
        this.matchingValidationService = matchingValidationService;
        this.configService = configService;
        this.addChoice = new EventEmitter();
        this.addPrompt = new EventEmitter();
        this.updateChoiceContent = new EventEmitter();
        this.updatePromptContent = new EventEmitter();
        this.deleteChoice = new EventEmitter();
        this.deletePrompt = new EventEmitter();
        this.addDistractor = new EventEmitter();
        this.updateDistractorContent = new EventEmitter();
        this.deleteDistractor = new EventEmitter();
        this.promptLimit = PROMPT_LIMIT_MATCHING;
        this.choiceLimit = CHOICE_LIMIT_MATCHING;
        this.distractorLimit = DISTRACTOR_LIMIT_MATCHING;
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && !!config['allowInlineValidation'];
    }
    canDeletePrompt() {
        return this.answers.length > 1;
    }
    canDeleteChoice(answer) {
        return answer.choices.length > 1;
    }
    canDeleteDistractor() {
        return this.distractors.length > 1;
    }
    canAddChoice() {
        return this.choices.length + this.distractors.length < 10;
    }
    trackByPrompt(index, item) {
        return item.prompt;
    }
    trackByKey(index, item) {
        return item.key;
    }
    getPrompt(promptKey) {
        return this.prompts.find((prompt) => prompt.key === promptKey)?.content;
    }
    getChoice(choiceKey) {
        return this.choices.find((choice) => choice.key === choiceKey)?.content;
    }
    onPromptContentUpdate(content, promptKey) {
        this.updatePromptContent.emit({ promptKey, content: content });
    }
    onChoiceContentUpdate(content, choiceKey) {
        this.updateChoiceContent.emit({ choiceKey, content: content });
    }
    onDeleteChoice(promptKey, choiceKey) {
        this.deleteChoice.emit({ promptKey, choiceKey });
    }
    onDistractorContentChange(content, distractor) {
        this.updateDistractorContent.emit({ distractor, content: content });
    }
    getErrors() {
        return (this.allowInlineValidation && this.matchingValidationService.getMatchErrors()) || [];
    }
    onEditorBlur() {
        const promptError = this.matchingValidationService.getPromptError().length &&
            this.matchingValidationService.validateMatchPrompts(this.purePrompts, this.choices);
        const choiceError = this.matchingValidationService.getChoiceError().length &&
            this.matchingValidationService.validateChoice(this.purePrompts, this.distractors);
        return promptError || choiceError;
    }
}
MatchingChoicesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MatchingChoicesComponent, deps: [{ token: MatchingValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
MatchingChoicesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: MatchingChoicesComponent, selector: "aa-matching-choices", inputs: { prompts: "prompts", purePrompts: "purePrompts", choices: "choices", answers: "answers", distractors: "distractors" }, outputs: { addChoice: "addChoice", addPrompt: "addPrompt", updateChoiceContent: "updateChoiceContent", updatePromptContent: "updatePromptContent", deleteChoice: "deleteChoice", deletePrompt: "deletePrompt", addDistractor: "addDistractor", updateDistractorContent: "updateDistractorContent", deleteDistractor: "deleteDistractor" }, ngImport: i0, template: "<h2 translate>Prompts and matching choices *</h2>\n\n<div class=\"info-wrapper\">\n  <span translate>Choices will be shuffled.</span>\n</div>\n\n<div class=\"matching-container\"\n     [ngClass]=\"{'answer-error': getErrors()?.length}\">\n  <div class=\"title-wrapper\">\n    <span translate\n          class=\"prompt-label\">Prompts</span>\n    <span translate\n          class=\"choice-label\">Choices</span>\n  </div>\n\n  <div class=\"matching-wrapper\"\n       *ngFor=\"let answer of answers, trackBy: trackByPrompt\">\n    <div class=\"prompt-wrapper\">\n      <mhe-editor class=\"prompt-editor\"\n                  [content]=\"getPrompt(answer.prompt)\"\n                  [maxCharacter]=\"promptLimit\"\n                  [noMedia]=\"true\"\n                  [imageOnly]=\"true\"\n                  [showResize]=\"false\"\n                  (contentChange)=\"onPromptContentUpdate($event, answer.prompt)\"\n                  (editorFocusOut)=\"onEditorBlur()\">\n      </mhe-editor>\n\n      <aa-trash-can class=\"delete-prompt\"\n                    *ngIf=\"canDeletePrompt()\"\n                    (delete)=\"deletePrompt.emit(answer)\">\n      </aa-trash-can>\n    </div>\n\n    <div class=\"glyphicon glyphicon-resize-horizontal double-arrow\"></div>\n\n    <div [ngClass]=\"{'choices-ruler': answer?.choices?.length > 1}\"\n         class=\"choices-wrapper\">\n      <div *ngFor=\"let choiceKey of answer.choices\"\n           class=\"choice-wrapper\">\n        <mhe-editor class=\"choice-editor\"\n                    [content]=\"getChoice(choiceKey)\"\n                    [maxCharacter]=\"choiceLimit\"\n                    [noMedia]=\"true\"\n                    [imageOnly]=\"true\"\n                    [showResize]=\"false\"\n                    (contentChange)=\"onChoiceContentUpdate($event, choiceKey)\"\n                    (editorFocusOut)=\"onEditorBlur()\">\n        </mhe-editor>\n\n        <aa-trash-can *ngIf=\"canDeleteChoice(answer)\"\n                      class=\"delete-choice\"\n                      (delete)=\"onDeleteChoice(answer.prompt, choiceKey)\">\n        </aa-trash-can>\n      </div>\n    </div>\n\n    <button class=\"add-choice\"\n            *ngIf=\"canAddChoice()\"\n            (click)=\"addChoice.emit(answer)\"><span translate>+ Add choice</span></button>\n  </div>\n\n  <button *ngIf=\"canAddChoice()\"\n          type=\"button\"\n          class=\"add-prompt\"\n          (click)=\"addPrompt.emit()\"><span translate>+ Add prompt</span></button>\n\n  <div class=\"distractor-container\">\n    <p class=\"distractor-label\">Distractors</p>\n\n    <div class=\"distractor-wrapper\"\n         *ngFor=\"let distractor of distractors, trackBy: trackByKey\">\n      <mhe-editor class=\"distractor-editor\"\n                  [content]=\"distractor.content\"\n                  [maxCharacter]=\"distractorLimit\"\n                  [noMedia]=\"true\"\n                  [imageOnly]=\"true\"\n                  [showResize]=\"false\"\n                  (contentChange)=\"onDistractorContentChange($event, distractor)\"\n                  (editorFocusOut)=\"onEditorBlur()\">\n      </mhe-editor>\n\n      <aa-trash-can *ngIf=\"canDeleteDistractor()\"\n                    class=\"delete-distractor\"\n                    (delete)=\"deleteDistractor.emit(distractor)\">\n      </aa-trash-can>\n    </div>\n\n    <button *ngIf=\"canAddChoice()\"\n            type=\"button\"\n            class=\"add-distractor\"\n            (click)=\"addDistractor.emit()\"><span translate>+ Add distractor</span></button>\n  </div>\n</div>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>", styles: [".info-wrapper{margin-bottom:10px}.matching-container{background-color:#fdfbfb;border:1px solid #a9a9a9;border-radius:2px;padding:30px 0 30px 30px}.matching-container.answer-error{border:1px solid #b0181e}.matching-container .title-wrapper{font-size:14px;font-weight:700}.matching-container .title-wrapper .prompt-label{width:calc(44% + 64px);float:left}.matching-container .matching-wrapper:first-child .double-arrow{margin-left:16px}.matching-container .matching-wrapper{overflow:auto;clear:both;display:flex;align-items:center}.matching-container .prompt-wrapper{padding:0;position:relative;width:40%;margin:10px 2% 10px 0}.matching-container .prompt-wrapper .prompt-editor{float:left;width:97%;margin:6px 0 10px}.matching-container .choices-ruler{border-left:5px solid #cccccc}.matching-container .choices-wrapper{float:right;margin:10px 0;width:40%}.matching-container .choices-wrapper .choice-wrapper{padding-left:3px;position:relative;width:100%;float:left;margin:5px 0}.matching-container .choices-wrapper .choice-wrapper .choice-editor{margin:6px 0 10px 2px;float:left;width:97%}.matching-container .delete-choice,.matching-container .delete-prompt{position:absolute;bottom:.5em}.matching-container .delete-distractor{position:absolute;bottom:0}.matching-container .add-choice{border:0;background:transparent}.matching-container .add-prompt{margin:10px 0 0;display:inline-block;border:0;background:transparent;padding:0}.matching-container .distractor-container{margin-top:1.2em;border-top:.5px solid #d6d6d6;padding-top:16px;overflow:auto}.matching-container .distractor-container .distractor-label{font-size:14px;font-weight:700}.matching-container .distractor-container .distractor-wrapper{margin:10px 2% 10px 0;width:42%;position:relative;float:left;clear:both}.matching-container .distractor-container .distractor-wrapper .distractor-editor{width:97%;float:left}.matching-container .distractor-container .add-distractor{margin:10px 0 0;display:inline-block;border:0;background:transparent;padding:0;float:left;clear:both}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MatchingChoicesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-matching-choices', template: "<h2 translate>Prompts and matching choices *</h2>\n\n<div class=\"info-wrapper\">\n  <span translate>Choices will be shuffled.</span>\n</div>\n\n<div class=\"matching-container\"\n     [ngClass]=\"{'answer-error': getErrors()?.length}\">\n  <div class=\"title-wrapper\">\n    <span translate\n          class=\"prompt-label\">Prompts</span>\n    <span translate\n          class=\"choice-label\">Choices</span>\n  </div>\n\n  <div class=\"matching-wrapper\"\n       *ngFor=\"let answer of answers, trackBy: trackByPrompt\">\n    <div class=\"prompt-wrapper\">\n      <mhe-editor class=\"prompt-editor\"\n                  [content]=\"getPrompt(answer.prompt)\"\n                  [maxCharacter]=\"promptLimit\"\n                  [noMedia]=\"true\"\n                  [imageOnly]=\"true\"\n                  [showResize]=\"false\"\n                  (contentChange)=\"onPromptContentUpdate($event, answer.prompt)\"\n                  (editorFocusOut)=\"onEditorBlur()\">\n      </mhe-editor>\n\n      <aa-trash-can class=\"delete-prompt\"\n                    *ngIf=\"canDeletePrompt()\"\n                    (delete)=\"deletePrompt.emit(answer)\">\n      </aa-trash-can>\n    </div>\n\n    <div class=\"glyphicon glyphicon-resize-horizontal double-arrow\"></div>\n\n    <div [ngClass]=\"{'choices-ruler': answer?.choices?.length > 1}\"\n         class=\"choices-wrapper\">\n      <div *ngFor=\"let choiceKey of answer.choices\"\n           class=\"choice-wrapper\">\n        <mhe-editor class=\"choice-editor\"\n                    [content]=\"getChoice(choiceKey)\"\n                    [maxCharacter]=\"choiceLimit\"\n                    [noMedia]=\"true\"\n                    [imageOnly]=\"true\"\n                    [showResize]=\"false\"\n                    (contentChange)=\"onChoiceContentUpdate($event, choiceKey)\"\n                    (editorFocusOut)=\"onEditorBlur()\">\n        </mhe-editor>\n\n        <aa-trash-can *ngIf=\"canDeleteChoice(answer)\"\n                      class=\"delete-choice\"\n                      (delete)=\"onDeleteChoice(answer.prompt, choiceKey)\">\n        </aa-trash-can>\n      </div>\n    </div>\n\n    <button class=\"add-choice\"\n            *ngIf=\"canAddChoice()\"\n            (click)=\"addChoice.emit(answer)\"><span translate>+ Add choice</span></button>\n  </div>\n\n  <button *ngIf=\"canAddChoice()\"\n          type=\"button\"\n          class=\"add-prompt\"\n          (click)=\"addPrompt.emit()\"><span translate>+ Add prompt</span></button>\n\n  <div class=\"distractor-container\">\n    <p class=\"distractor-label\">Distractors</p>\n\n    <div class=\"distractor-wrapper\"\n         *ngFor=\"let distractor of distractors, trackBy: trackByKey\">\n      <mhe-editor class=\"distractor-editor\"\n                  [content]=\"distractor.content\"\n                  [maxCharacter]=\"distractorLimit\"\n                  [noMedia]=\"true\"\n                  [imageOnly]=\"true\"\n                  [showResize]=\"false\"\n                  (contentChange)=\"onDistractorContentChange($event, distractor)\"\n                  (editorFocusOut)=\"onEditorBlur()\">\n      </mhe-editor>\n\n      <aa-trash-can *ngIf=\"canDeleteDistractor()\"\n                    class=\"delete-distractor\"\n                    (delete)=\"deleteDistractor.emit(distractor)\">\n      </aa-trash-can>\n    </div>\n\n    <button *ngIf=\"canAddChoice()\"\n            type=\"button\"\n            class=\"add-distractor\"\n            (click)=\"addDistractor.emit()\"><span translate>+ Add distractor</span></button>\n  </div>\n</div>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>", styles: [".info-wrapper{margin-bottom:10px}.matching-container{background-color:#fdfbfb;border:1px solid #a9a9a9;border-radius:2px;padding:30px 0 30px 30px}.matching-container.answer-error{border:1px solid #b0181e}.matching-container .title-wrapper{font-size:14px;font-weight:700}.matching-container .title-wrapper .prompt-label{width:calc(44% + 64px);float:left}.matching-container .matching-wrapper:first-child .double-arrow{margin-left:16px}.matching-container .matching-wrapper{overflow:auto;clear:both;display:flex;align-items:center}.matching-container .prompt-wrapper{padding:0;position:relative;width:40%;margin:10px 2% 10px 0}.matching-container .prompt-wrapper .prompt-editor{float:left;width:97%;margin:6px 0 10px}.matching-container .choices-ruler{border-left:5px solid #cccccc}.matching-container .choices-wrapper{float:right;margin:10px 0;width:40%}.matching-container .choices-wrapper .choice-wrapper{padding-left:3px;position:relative;width:100%;float:left;margin:5px 0}.matching-container .choices-wrapper .choice-wrapper .choice-editor{margin:6px 0 10px 2px;float:left;width:97%}.matching-container .delete-choice,.matching-container .delete-prompt{position:absolute;bottom:.5em}.matching-container .delete-distractor{position:absolute;bottom:0}.matching-container .add-choice{border:0;background:transparent}.matching-container .add-prompt{margin:10px 0 0;display:inline-block;border:0;background:transparent;padding:0}.matching-container .distractor-container{margin-top:1.2em;border-top:.5px solid #d6d6d6;padding-top:16px;overflow:auto}.matching-container .distractor-container .distractor-label{font-size:14px;font-weight:700}.matching-container .distractor-container .distractor-wrapper{margin:10px 2% 10px 0;width:42%;position:relative;float:left;clear:both}.matching-container .distractor-container .distractor-wrapper .distractor-editor{width:97%;float:left}.matching-container .distractor-container .add-distractor{margin:10px 0 0;display:inline-block;border:0;background:transparent;padding:0;float:left;clear:both}\n"] }]
        }], ctorParameters: function () { return [{ type: MatchingValidationService }, { type: ConfigService }]; }, propDecorators: { prompts: [{
                type: Input
            }], purePrompts: [{
                type: Input
            }], choices: [{
                type: Input
            }], answers: [{
                type: Input
            }], distractors: [{
                type: Input
            }], addChoice: [{
                type: Output
            }], addPrompt: [{
                type: Output
            }], updateChoiceContent: [{
                type: Output
            }], updatePromptContent: [{
                type: Output
            }], deleteChoice: [{
                type: Output
            }], deletePrompt: [{
                type: Output
            }], addDistractor: [{
                type: Output
            }], updateDistractorContent: [{
                type: Output
            }], deleteDistractor: [{
                type: Output
            }] } });

class AirMatchingComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirMatching, LoadAirItemAction$g, ResetStoreAction$g);
        this.store = store;
        this.airMatching$ = this.store.select(getAirMatching);
        this.pureAirMatching$ = this.store.select(getPureAirMatching);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
    onAddPrompt() {
        this.store.dispatch(new AddPromptAction());
    }
    onAddChoice(answer) {
        this.store.dispatch(new AddChoiceAction$2(answer));
    }
    onUpdatePromptContent({ promptKey, content }) {
        this.store.dispatch(new UpdatePromptContentAction({ promptKey, content }));
    }
    onUpdateChoiceContent({ choiceKey, content }) {
        this.store.dispatch(new UpdateChoiceContentAction$1({ choiceKey, content }));
    }
    onDeletePrompt(answer) {
        this.store.dispatch(new DeletePromptAction(answer));
    }
    onDeleteChoice({ promptKey, choiceKey }) {
        this.store.dispatch(new DeleteChoiceAction$2({ promptKey, choiceKey }));
    }
    onAddDistractor() {
        this.store.dispatch(new AddDistractorAction());
    }
    onUpdateDistractorContent({ distractor, content }) {
        this.store.dispatch(new UpdateDistractorContentAction({ distractor, content }));
    }
    onDeleteDistractor(distractor) {
        this.store.dispatch(new DeleteDistractorAction(distractor));
    }
}
AirMatchingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMatchingComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirMatchingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirMatchingComponent, selector: "aa-air-matching", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-matching-choices [prompts]=\"(airMatching$ | async)?.prompts\"\n                     [purePrompts]=\"(pureAirMatching$ | async)?.prompts\"\n                     [choices]=\"(airMatching$ | async)?.choices\"\n                     [answers]=\"(airMatching$ | async)?.answers\"\n                     [distractors]=\"(airMatching$ | async)?.distractors\"\n                     (addChoice)=\"onAddChoice($event)\"\n                     (addPrompt)=\"onAddPrompt()\"\n                     (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                     (updatePromptContent)=\"onUpdatePromptContent($event)\"\n                     (deleteChoice)=\"onDeleteChoice($event)\"\n                     (deletePrompt)=\"onDeletePrompt($event)\"\n                     (addDistractor)=\"onAddDistractor()\"\n                     (updateDistractorContent)=\"onUpdateDistractorContent($event)\"\n                     (deleteDistractor)=\"onDeleteDistractor($event)\"></aa-matching-choices>\n", dependencies: [{ kind: "component", type: MatchingChoicesComponent, selector: "aa-matching-choices", inputs: ["prompts", "purePrompts", "choices", "answers", "distractors"], outputs: ["addChoice", "addPrompt", "updateChoiceContent", "updatePromptContent", "deleteChoice", "deletePrompt", "addDistractor", "updateDistractorContent", "deleteDistractor"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMatchingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-matching', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-matching-choices [prompts]=\"(airMatching$ | async)?.prompts\"\n                     [purePrompts]=\"(pureAirMatching$ | async)?.prompts\"\n                     [choices]=\"(airMatching$ | async)?.choices\"\n                     [answers]=\"(airMatching$ | async)?.answers\"\n                     [distractors]=\"(airMatching$ | async)?.distractors\"\n                     (addChoice)=\"onAddChoice($event)\"\n                     (addPrompt)=\"onAddPrompt()\"\n                     (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                     (updatePromptContent)=\"onUpdatePromptContent($event)\"\n                     (deleteChoice)=\"onDeleteChoice($event)\"\n                     (deletePrompt)=\"onDeletePrompt($event)\"\n                     (addDistractor)=\"onAddDistractor()\"\n                     (updateDistractorContent)=\"onUpdateDistractorContent($event)\"\n                     (deleteDistractor)=\"onDeleteDistractor($event)\"></aa-matching-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class MultipleChoiceValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirMultipleChoice$ = this.store.select(getPureAirMultipleChoice);
        this.choicesErrors = [];
        this.answerErrors = [];
        this.airMultipleChoice$ = store.select(getAirMultipleChoice);
    }
    validate() {
        let isValid;
        this.resetChoicesErrors();
        this.pureAirMultipleChoice$.pipe(take(1)).subscribe((airMultipleChoice) => {
            const promptError = !!this.validateQuestionText(airMultipleChoice.prompt);
            const answers = airMultipleChoice.answer || airMultipleChoice.answers;
            const choicesErrors = this.validateChoices(airMultipleChoice.choices).length > 0;
            const answerErrors = this.validateAnswers(answers).length > 0;
            isValid = !(promptError || choicesErrors || answerErrors);
        });
        return isValid;
    }
    validateQuestionText(value) {
        // Check if prompt is empty
        return this.promptValidationService.validatePrompt((value || '').trim());
    }
    validateChoices(choices) {
        this.resetChoicesErrors();
        const validChoices = choices.filter((choice) => (choice.content || '').trim());
        // Check if at least two choices have content
        if (validChoices.length < 2) {
            this.choicesErrors.push(E_INVALID_CHOICES);
        }
        else if (hasDuplicate(validChoices, 'content')) {
            this.choicesErrors.push(E_NO_IDENTICAL_CHOICES);
        }
        return this.choicesErrors;
    }
    validateAnswers(answers) {
        this.resetAnswerErrors();
        // Check if multiple choice has correct answer
        if (!answers || (answers && !answers.length)) {
            this.answerErrors.push(E_NO_CORRECT_ANSWER_SELECTED);
        }
        return this.answerErrors;
    }
    resetErrors() {
        this.resetChoicesErrors();
        this.resetAnswerErrors();
        this.promptValidationService.resetErrors();
    }
    resetChoicesErrors() {
        this.choicesErrors = [];
    }
    resetAnswerErrors() {
        this.answerErrors = [];
    }
    getChoicesErrors() {
        return this.answerErrors ? this.choicesErrors.concat(this.answerErrors) : this.choicesErrors;
    }
    validateAnswerChoicesOnFocusOut(choices, answers) {
        if (this.choicesErrors.length) {
            this.resetChoicesErrors();
            this.validateChoices(choices);
        }
        if (this.answerErrors.length) {
            this.resetAnswerErrors();
            this.validateAnswers(answers);
        }
    }
}
MultipleChoiceValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
MultipleChoiceValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class MultipleChoiceAnswersComponent {
    constructor(multipleChoiceValidationService, configService) {
        this.multipleChoiceValidationService = multipleChoiceValidationService;
        this.configService = configService;
        this.addChoice = new EventEmitter();
        this.deleteChoice = new EventEmitter();
        this.updateChoiceContent = new EventEmitter();
        this.updateRationaleContent = new EventEmitter();
        this.toggleRationaleVisibility = new EventEmitter();
        this.addAnswer = new EventEmitter();
        this.deleteAnswer = new EventEmitter();
        this.toggleChoiceShuffling = new EventEmitter();
        this.moveUp = new EventEmitter();
        this.moveDown = new EventEmitter();
        this.resetChoicesErrors = new EventEmitter();
        this.choicesFocusOut = new EventEmitter();
        this.answerChoiceLimit = ANSWER_CHOICE_LIMIT;
        this.rationaleLimit = RATIONALE_LIMIT;
        this.allowValidation = false;
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    onUpdateChoiceContent(content, choiceIndex) {
        const choice = this.choices ? this.choices[choiceIndex] : undefined;
        if (choice) {
            this.updateChoiceContent.emit({ choice, content });
        }
    }
    onUpdateRationaleContent(content, choiceIndex) {
        const choice = this.choices ? this.choices[choiceIndex] : undefined;
        if (choice) {
            this.updateRationaleContent.emit({ choice, content });
        }
    }
    onDeleteChoice(choice) {
        const answers = this.answers;
        this.deleteChoice.emit({ choice: choice, answers: answers });
    }
    canDeleteChoice() {
        return this.choices && this.choices.length > 2;
    }
    canAddChoice() {
        return this.choices && this.choices.length < 10;
    }
    canMoveUp(choiceIndex) {
        return choiceIndex !== 0;
    }
    canMoveDown(choiceIndex) {
        return choiceIndex < (this.choices ? this.choices.length - 1 : 0);
    }
    isCorrectAnswer(choiceIndex) {
        const choice = this.choices ? this.choices[choiceIndex] : undefined;
        if (!choice) {
            return;
        }
        if (this.selectMultiple) {
            return this.answers.includes(choice.key);
        }
        else {
            return this.answer === choice.key;
        }
    }
    choiceIndices() {
        return this.choices ? this.choices.map((choice, i) => i) : [];
    }
    onFocusOut(value) {
        this.choicesFocusOut.emit(value);
    }
    getErrors() {
        return this.allowValidation && this.multipleChoiceValidationService.getChoicesErrors();
    }
    onDeleteAnswer(choiceIndex) {
        if (this.choices) {
            this.deleteAnswer.emit(this.choices[choiceIndex]);
            this.validateOnFocusOut();
        }
    }
    onAddAnswer(choiceIndex) {
        if (this.choices) {
            this.addAnswer.emit(this.choices[choiceIndex]);
            this.validateOnFocusOut();
        }
    }
    validateOnFocusOut() {
        setTimeout(() => {
            const answers = this.selectMultiple ? this.answers : this.answer;
            if (this.choices) {
                this.multipleChoiceValidationService.validateAnswerChoicesOnFocusOut(this.choices, answers);
            }
        });
    }
}
MultipleChoiceAnswersComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceAnswersComponent, deps: [{ token: MultipleChoiceValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
MultipleChoiceAnswersComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: MultipleChoiceAnswersComponent, selector: "aa-answer-choices", inputs: { choices: "choices", answers: "answers", answer: "answer", showRationales: "showRationales", selectMultiple: "selectMultiple", random: "random", choicesErrors: "choicesErrors" }, outputs: { addChoice: "addChoice", deleteChoice: "deleteChoice", updateChoiceContent: "updateChoiceContent", updateRationaleContent: "updateRationaleContent", toggleRationaleVisibility: "toggleRationaleVisibility", addAnswer: "addAnswer", deleteAnswer: "deleteAnswer", toggleChoiceShuffling: "toggleChoiceShuffling", moveUp: "moveUp", moveDown: "moveDown", resetChoicesErrors: "resetChoicesErrors", choicesFocusOut: "choicesFocusOut" }, ngImport: i0, template: "<div class=\"row\">\n  <div class=\"col\">\n    <h2 translate>Answer choices *</h2>\n  </div>\n\n  <div class=\"col-auto ms-auto d-flex\">\n    <div>\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"showRationales\"\n          (change)=\"toggleRationaleVisibility.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n\n    <div class=\"ms-3\">\n\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"shuffle-toggle-id\"\n          [checked]=\"random\"\n          (change)=\"toggleChoiceShuffling.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"shuffle-toggle-id\">Shuffle</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  class=\"answer-choices-container\"\n  [class.answer-choices-error]=\"getErrors()?.length\"\n>\n  <div class=\"answer-choice\" *ngFor=\"let choiceIndex of choiceIndices()\">\n    <div class=\"row choice-wrapper\">\n      <button\n        *ngIf=\"isCorrectAnswer(choiceIndex)\"\n        class=\"col-1 answer-selector\"\n        type=\"button\"\n        (click)=\"onDeleteAnswer(choiceIndex)\"\n      >\n        <span class=\"glyphicon glyphicon-ok\"></span>\n      </button>\n\n      <button\n        *ngIf=\"!isCorrectAnswer(choiceIndex)\"\n        class=\"col-1 answer-selector\"\n        type=\"button\"\n        (click)=\"onAddAnswer(choiceIndex)\"\n      >\n        <span class=\"glyphicon glyphicon-remove\"></span>\n      </button>\n\n      <mhe-editor\n        class=\"col-8\"\n        [maxCharacter]=\"answerChoiceLimit\"\n        [content]=\"choices[choiceIndex]?.content\"\n        [noMedia]=\"true\"\n        [imageOnly]=\"true\"\n        [showResize]=\"false\"\n        (editorFocusOut)=\"validateOnFocusOut()\"\n        (contentChange)=\"onUpdateChoiceContent($event, choiceIndex)\"\n      >\n      </mhe-editor>\n\n      <div class=\"col-auto align-self-end\">\n        <div class=\"d-flex\">\n          <aa-trash-can\n            *ngIf=\"canDeleteChoice()\"\n            class=\"delete-choice\"\n            (delete)=\"onDeleteChoice(choices[choiceIndex])\"\n          ></aa-trash-can>\n\n          <button\n            *ngIf=\"canMoveUp(choiceIndex)\"\n            type=\"button\"\n            class=\"arrows up-arrow\"\n            type=\"button\"\n            aria-label=\"up arrow\"\n            (click)=\"moveUp.emit(choices[choiceIndex])\"\n          >\n            <i class=\"dpg-icon dpg-icon-up-arrow\"></i>\n          </button>\n\n          <button\n            *ngIf=\"canMoveDown(choiceIndex)\"\n            type=\"button\"\n            class=\"arrows down-arrow\"\n            type=\"button\"\n            aria-label=\"down arrow\"\n            (click)=\"moveDown.emit(choices[choiceIndex])\"\n          >\n            <i class=\"dpg-icon dpg-icon-down-arrow\"></i>\n          </button>\n        </div>\n      </div>\n    </div>\n\n    <div *ngIf=\"showRationales\" class=\"row rationale-wrapper underline\">\n      <span translate class=\"col-1 rationale-label\">Reason</span>\n      <div class=\"arrow-up-border col-2\"></div>\n      <div class=\"arrow-up col-2 color-gray\"></div>\n      <mhe-editor\n        class=\"col-8\"\n        [maxCharacter]=\"rationaleLimit\"\n        [noMedia]=\"true\"\n        [content]=\"choices[choiceIndex].rationale\"\n        [showResize]=\"false\"\n        (contentChange)=\"onUpdateRationaleContent($event, choiceIndex)\"\n      >\n      </mhe-editor>\n    </div>\n  </div>\n\n  <button\n    type=\"button\"\n    *ngIf=\"canAddChoice()\"\n    class=\"add-choice\"\n    (click)=\"addChoice.emit()\"\n  >\n    <span translate>+ Add choice</span>\n  </button>\n</div>\n\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".toggle-switch{float:right;background-color:transparent;border:0;padding:0;margin:25px 130px 5px 0;display:flex;align-content:center}label.answer-choices{margin-top:20px}.answer-choices-container{border:1px solid #a9a9a9;border-radius:2px;padding:20px;background-color:#fdfbfb}.answer-choices-container.answer-choices-error{border:1px solid #b0181e}.answer-choices-container .answer-choice{margin:15px 0 20px 15px;width:100%}.answer-choices-container .answer-choice:last-of-type .rationale-wrapper{margin-bottom:0}.answer-choices-container .answer-choice .choice-wrapper{margin:0 0 2em;position:relative}.answer-choices-container .answer-choice .choice-wrapper .answer-selector{height:35px;width:35px;padding:0;margin-right:20px;background:transparent;border:1px solid #a9a9a9}.answer-choices-container .answer-choice .choice-wrapper .arrows{background:none;border:none}.answer-choices-container .answer-choice .rationale-wrapper{margin:0;display:flex;align-items:center;position:relative;padding-bottom:20px}.answer-choices-container .answer-choice .rationale-wrapper.underline{border-bottom:.5px solid #d6d6d6}.answer-choices-container .answer-choice .rationale-wrapper .rationale-label{padding:0;font-size:14px;width:55px;font-weight:700}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up-border{width:0;height:0;border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:14px solid #a9a9a9;padding:0;position:absolute;top:-12px;left:37%}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up{width:0;height:0;border-left:13px solid transparent;border-right:13px solid transparent;border-bottom:13px solid #ffffff;padding:0;position:absolute;left:37.1%;top:-10px;z-index:1}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up.color-gray{border-bottom:13px solid #f0f0f0}.answer-choices-container .add-choice{margin-left:84px;display:inline-block;border:0;background:transparent;padding:0}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceAnswersComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-answer-choices', template: "<div class=\"row\">\n  <div class=\"col\">\n    <h2 translate>Answer choices *</h2>\n  </div>\n\n  <div class=\"col-auto ms-auto d-flex\">\n    <div>\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"showRationales\"\n          (change)=\"toggleRationaleVisibility.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n\n    <div class=\"ms-3\">\n\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"shuffle-toggle-id\"\n          [checked]=\"random\"\n          (change)=\"toggleChoiceShuffling.emit()\"\n        />\n        <label class=\"form-check-label\" for=\"shuffle-toggle-id\">Shuffle</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  class=\"answer-choices-container\"\n  [class.answer-choices-error]=\"getErrors()?.length\"\n>\n  <div class=\"answer-choice\" *ngFor=\"let choiceIndex of choiceIndices()\">\n    <div class=\"row choice-wrapper\">\n      <button\n        *ngIf=\"isCorrectAnswer(choiceIndex)\"\n        class=\"col-1 answer-selector\"\n        type=\"button\"\n        (click)=\"onDeleteAnswer(choiceIndex)\"\n      >\n        <span class=\"glyphicon glyphicon-ok\"></span>\n      </button>\n\n      <button\n        *ngIf=\"!isCorrectAnswer(choiceIndex)\"\n        class=\"col-1 answer-selector\"\n        type=\"button\"\n        (click)=\"onAddAnswer(choiceIndex)\"\n      >\n        <span class=\"glyphicon glyphicon-remove\"></span>\n      </button>\n\n      <mhe-editor\n        class=\"col-8\"\n        [maxCharacter]=\"answerChoiceLimit\"\n        [content]=\"choices[choiceIndex]?.content\"\n        [noMedia]=\"true\"\n        [imageOnly]=\"true\"\n        [showResize]=\"false\"\n        (editorFocusOut)=\"validateOnFocusOut()\"\n        (contentChange)=\"onUpdateChoiceContent($event, choiceIndex)\"\n      >\n      </mhe-editor>\n\n      <div class=\"col-auto align-self-end\">\n        <div class=\"d-flex\">\n          <aa-trash-can\n            *ngIf=\"canDeleteChoice()\"\n            class=\"delete-choice\"\n            (delete)=\"onDeleteChoice(choices[choiceIndex])\"\n          ></aa-trash-can>\n\n          <button\n            *ngIf=\"canMoveUp(choiceIndex)\"\n            type=\"button\"\n            class=\"arrows up-arrow\"\n            type=\"button\"\n            aria-label=\"up arrow\"\n            (click)=\"moveUp.emit(choices[choiceIndex])\"\n          >\n            <i class=\"dpg-icon dpg-icon-up-arrow\"></i>\n          </button>\n\n          <button\n            *ngIf=\"canMoveDown(choiceIndex)\"\n            type=\"button\"\n            class=\"arrows down-arrow\"\n            type=\"button\"\n            aria-label=\"down arrow\"\n            (click)=\"moveDown.emit(choices[choiceIndex])\"\n          >\n            <i class=\"dpg-icon dpg-icon-down-arrow\"></i>\n          </button>\n        </div>\n      </div>\n    </div>\n\n    <div *ngIf=\"showRationales\" class=\"row rationale-wrapper underline\">\n      <span translate class=\"col-1 rationale-label\">Reason</span>\n      <div class=\"arrow-up-border col-2\"></div>\n      <div class=\"arrow-up col-2 color-gray\"></div>\n      <mhe-editor\n        class=\"col-8\"\n        [maxCharacter]=\"rationaleLimit\"\n        [noMedia]=\"true\"\n        [content]=\"choices[choiceIndex].rationale\"\n        [showResize]=\"false\"\n        (contentChange)=\"onUpdateRationaleContent($event, choiceIndex)\"\n      >\n      </mhe-editor>\n    </div>\n  </div>\n\n  <button\n    type=\"button\"\n    *ngIf=\"canAddChoice()\"\n    class=\"add-choice\"\n    (click)=\"addChoice.emit()\"\n  >\n    <span translate>+ Add choice</span>\n  </button>\n</div>\n\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".toggle-switch{float:right;background-color:transparent;border:0;padding:0;margin:25px 130px 5px 0;display:flex;align-content:center}label.answer-choices{margin-top:20px}.answer-choices-container{border:1px solid #a9a9a9;border-radius:2px;padding:20px;background-color:#fdfbfb}.answer-choices-container.answer-choices-error{border:1px solid #b0181e}.answer-choices-container .answer-choice{margin:15px 0 20px 15px;width:100%}.answer-choices-container .answer-choice:last-of-type .rationale-wrapper{margin-bottom:0}.answer-choices-container .answer-choice .choice-wrapper{margin:0 0 2em;position:relative}.answer-choices-container .answer-choice .choice-wrapper .answer-selector{height:35px;width:35px;padding:0;margin-right:20px;background:transparent;border:1px solid #a9a9a9}.answer-choices-container .answer-choice .choice-wrapper .arrows{background:none;border:none}.answer-choices-container .answer-choice .rationale-wrapper{margin:0;display:flex;align-items:center;position:relative;padding-bottom:20px}.answer-choices-container .answer-choice .rationale-wrapper.underline{border-bottom:.5px solid #d6d6d6}.answer-choices-container .answer-choice .rationale-wrapper .rationale-label{padding:0;font-size:14px;width:55px;font-weight:700}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up-border{width:0;height:0;border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:14px solid #a9a9a9;padding:0;position:absolute;top:-12px;left:37%}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up{width:0;height:0;border-left:13px solid transparent;border-right:13px solid transparent;border-bottom:13px solid #ffffff;padding:0;position:absolute;left:37.1%;top:-10px;z-index:1}.answer-choices-container .answer-choice .rationale-wrapper .arrow-up.color-gray{border-bottom:13px solid #f0f0f0}.answer-choices-container .add-choice{margin-left:84px;display:inline-block;border:0;background:transparent;padding:0}\n"] }]
        }], ctorParameters: function () { return [{ type: MultipleChoiceValidationService }, { type: ConfigService }]; }, propDecorators: { choices: [{
                type: Input
            }], answers: [{
                type: Input
            }], answer: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], selectMultiple: [{
                type: Input
            }], random: [{
                type: Input
            }], choicesErrors: [{
                type: Input
            }], addChoice: [{
                type: Output
            }], deleteChoice: [{
                type: Output
            }], updateChoiceContent: [{
                type: Output
            }], updateRationaleContent: [{
                type: Output
            }], toggleRationaleVisibility: [{
                type: Output
            }], addAnswer: [{
                type: Output
            }], deleteAnswer: [{
                type: Output
            }], toggleChoiceShuffling: [{
                type: Output
            }], moveUp: [{
                type: Output
            }], moveDown: [{
                type: Output
            }], resetChoicesErrors: [{
                type: Output
            }], choicesFocusOut: [{
                type: Output
            }] } });

class AirMultipleChoiceComponent extends AirBaseComponent {
    constructor(store, multipleChoiceValidationService, configService) {
        super(store, getPureAirMultipleChoice, LoadAirItemAction$n, ResetStoreAction$o);
        this.store = store;
        this.multipleChoiceValidationService = multipleChoiceValidationService;
        this.configService = configService;
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.allowInlineValidation = false;
        this.airMultipleChoice$ = store.select(getAirMultipleChoice);
        this.pureAirMultipleChoice$ = store.select(getPureAirMultipleChoice);
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    ngOnInit() {
        super.ngOnInit();
        this.store.dispatch(new UpdateSelectMultipleAction(this.selectMultiple));
    }
    onAddChoice() {
        this.store.dispatch(new AddChoiceAction$4());
    }
    onUpdateChoiceContent({ choice, content }) {
        this.store.dispatch(new UpdateChoiceContentAction$3({ choice, content }));
    }
    onDeleteChoice({ choice, answers }) {
        this.store.dispatch(new DeleteChoiceAction$4({ choice, answers }));
    }
    onAddAnswer(answer) {
        this.store.dispatch(new AddAnswerAction$2(answer));
    }
    onDeleteAnswer(answer) {
        this.store.dispatch(new DeleteAnswerAction$2(answer));
    }
    onUpdateRationaleContent({ choice, content }) {
        this.store.dispatch(new UpdateRationaleContentAction$1({ choice, content }));
    }
    onToggleRationaleVisibility() {
        this.store.dispatch(new ToggleRationaleVisibilityAction$2());
    }
    onToggleChoiceShuffling() {
        this.store.dispatch(new ToggleChoicesShufflingAction$1());
    }
    onMoveChoiceUp(choice) {
        this.store.dispatch(new MoveChoiceUpAction(choice));
    }
    onMoveChoiceDown(choice) {
        this.store.dispatch(new MoveChoiceDownAction(choice));
    }
    onQuestionTextInputFocusOut(value) {
        this.multipleChoiceValidationService.validateQuestionText(value);
    }
    getChoicesErrors() {
        return this.allowInlineValidation && this.multipleChoiceValidationService.getChoicesErrors();
    }
}
AirMultipleChoiceComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMultipleChoiceComponent, deps: [{ token: i1.Store }, { token: MultipleChoiceValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirMultipleChoiceComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirMultipleChoiceComponent, selector: "aa-air-multiple-choice", inputs: { selectMultiple: "selectMultiple" }, usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-choices [choices]=\"(airMultipleChoice$ | async)?.choices\"\n                   [answers]=\"(airMultipleChoice$ | async)?.answers\"\n                   [answer]=\"(airMultipleChoice$ | async)?.answer\"\n                   [showRationales]=\"(airMultipleChoice$ | async)?.showRationales\"\n                   [selectMultiple]=\"(airMultipleChoice$ | async)?.selectMultiple\"\n                   [random]=\"(airMultipleChoice$ | async)?.random\"\n                   [choicesErrors]=\"getChoicesErrors()\"\n                   (addChoice)=\"onAddChoice()\"\n                   (deleteChoice)=\"onDeleteChoice($event)\"\n                   (moveUp)=\"onMoveChoiceUp($event)\"\n                   (moveDown)=\"onMoveChoiceDown($event)\"\n                   (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                   (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n                   (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\"\n                   (addAnswer)=\"onAddAnswer($event)\"\n                   (deleteAnswer)=\"onDeleteAnswer($event)\"\n                   (toggleChoiceShuffling)=\"onToggleChoiceShuffling()\">\n</aa-answer-choices>\n", dependencies: [{ kind: "component", type: MultipleChoiceAnswersComponent, selector: "aa-answer-choices", inputs: ["choices", "answers", "answer", "showRationales", "selectMultiple", "random", "choicesErrors"], outputs: ["addChoice", "deleteChoice", "updateChoiceContent", "updateRationaleContent", "toggleRationaleVisibility", "addAnswer", "deleteAnswer", "toggleChoiceShuffling", "moveUp", "moveDown", "resetChoicesErrors", "choicesFocusOut"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMultipleChoiceComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-multiple-choice', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-choices [choices]=\"(airMultipleChoice$ | async)?.choices\"\n                   [answers]=\"(airMultipleChoice$ | async)?.answers\"\n                   [answer]=\"(airMultipleChoice$ | async)?.answer\"\n                   [showRationales]=\"(airMultipleChoice$ | async)?.showRationales\"\n                   [selectMultiple]=\"(airMultipleChoice$ | async)?.selectMultiple\"\n                   [random]=\"(airMultipleChoice$ | async)?.random\"\n                   [choicesErrors]=\"getChoicesErrors()\"\n                   (addChoice)=\"onAddChoice()\"\n                   (deleteChoice)=\"onDeleteChoice($event)\"\n                   (moveUp)=\"onMoveChoiceUp($event)\"\n                   (moveDown)=\"onMoveChoiceDown($event)\"\n                   (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                   (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n                   (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\"\n                   (addAnswer)=\"onAddAnswer($event)\"\n                   (deleteAnswer)=\"onDeleteAnswer($event)\"\n                   (toggleChoiceShuffling)=\"onToggleChoiceShuffling()\">\n</aa-answer-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: MultipleChoiceValidationService }, { type: ConfigService }]; }, propDecorators: { selectMultiple: [{
                type: Input
            }] } });

class OrderingValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirOrdering$ = this.store.select(getPureAirOrdering);
        this.choiceError = '';
        this.toleranceError = '';
    }
    validate() {
        let isValid;
        this.pureAirOrdering$.pipe(take(1)).subscribe((airOrdering) => {
            const promptError = !!this.promptValidationService.validatePrompt(airOrdering.prompt);
            const choiceError = !!this.validateChoices(airOrdering.answers);
            isValid = !(promptError || choiceError);
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetChoiceError();
    }
    validateChoices(answers) {
        this.choiceError = answers.length > 2 ? '' : E_INVALID_ANSWER_CHOICES;
        return this.choiceError;
    }
    getChoiceError() {
        return this.choiceError ? [this.choiceError] : [];
    }
    resetChoiceError() {
        this.choiceError = '';
    }
}
OrderingValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderingValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
OrderingValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderingValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderingValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class OrderingChoicesComponent {
    constructor(orderingValidationService) {
        this.orderingValidationService = orderingValidationService;
        this.addChoice = new EventEmitter();
        this.updateChoiceContent = new EventEmitter();
        this.deleteChoice = new EventEmitter();
        this.addDistractor = new EventEmitter();
        this.updateDistractorContent = new EventEmitter();
        this.deleteDistractor = new EventEmitter();
        this.toggleDistractorVisibility = new EventEmitter();
        this.choiceLimit = CHOICE_LIMIT_ORDERING;
        this.distractorLimit = DISTRACTOR_LIMIT_ORDERING;
    }
    onChoiceContentUpdate(content, choice) {
        this.updateChoiceContent.emit({ choice, content });
    }
    onDistractorContentUpdate(content, distractor) {
        this.updateDistractorContent.emit({ distractor, content });
    }
    canDeleteDistractor() {
        return this.distractors.length > 1;
    }
    disableCheckbox() {
        return !this.canAddChoice() && this.distractors.length < 1;
    }
    trackByKey(index, item) {
        return item.key;
    }
    canAddChoice() {
        return this.choices.length + this.distractors.length < 10;
    }
    canDeleteChoice() {
        return this.choices.length > 3;
    }
    getChoiceError() {
        return this.orderingValidationService.getChoiceError();
    }
    onEditorFocusOut(answers) {
        if (this.orderingValidationService.getChoiceError().length) {
            return this.orderingValidationService.validateChoices(answers);
        }
        return false;
    }
    onToggleDistractorVisibility() {
        this.toggleDistractorVisibility.emit();
    }
}
OrderingChoicesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderingChoicesComponent, deps: [{ token: OrderingValidationService }], target: i0.ɵɵFactoryTarget.Component });
OrderingChoicesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: OrderingChoicesComponent, selector: "aa-ordering-choices", inputs: { choices: "choices", distractors: "distractors", showDistractors: "showDistractors", answers: "answers" }, outputs: { addChoice: "addChoice", updateChoiceContent: "updateChoiceContent", deleteChoice: "deleteChoice", addDistractor: "addDistractor", updateDistractorContent: "updateDistractorContent", deleteDistractor: "deleteDistractor", toggleDistractorVisibility: "toggleDistractorVisibility" }, ngImport: i0, template: "<h2 translate>Ordering choices *</h2>\n\n<div class=\"sub-heading-wrapper\">\n  <span translate>Choices will be shuffled.</span>\n</div>\n\n<div class=\"choice-container\">\n  <div\n    class=\"choices-wrapper\"\n    [ngClass]=\"{ 'error-class': getChoiceError()?.length }\"\n  >\n    <div class=\"ordering-list\">\n      <strong class=\"mb-2\">Choices in order</strong>\n\n      <div\n        class=\"ordering-choice row\"\n        *ngFor=\"\n          let choice of choices;\n          let choiceIndex = index;\n          trackBy: trackByKey\n        \"\n      >\n        <mhe-editor\n          class=\"col-6 choice-editor\"\n          [maxCharacter]=\"choiceLimit\"\n          [content]=\"choice.text\"\n          [noMedia]=\"true\"\n          [imageOnly]=\"true\"\n          [showResize]=\"false\"\n          (contentChange)=\"onChoiceContentUpdate($event, choice)\"\n          (editorFocusOut)=\"onEditorFocusOut(answers)\"\n        ></mhe-editor>\n\n        <aa-trash-can\n          class=\"delete-choice col-auto align-self-end\"\n          *ngIf=\"canDeleteChoice()\"\n          (delete)=\"deleteChoice.emit(choice)\"\n        ></aa-trash-can>\n      </div>\n\n      <button\n        type=\"button\"\n        class=\"add-choice\"\n        *ngIf=\"canAddChoice()\"\n        (click)=\"addChoice.emit()\"\n      >\n        <span translate>+ Add choice</span>\n      </button>\n\n      <hr />\n\n      <div class=\"mb-3\">\n        <label translate class=\"form-check form-check-label\">\n          <input\n            type=\"checkbox\"\n            class=\"form-check-input\"\n            name=\"distractor\"\n            [disabled]=\"disableCheckbox()\"\n            [checked]=\"showDistractors\"\n            (change)=\"onToggleDistractorVisibility()\"\n            #distractorCheckbox\n          />\n          Drag choices to separate dock location\n        </label>\n      </div>\n\n      <div *ngIf=\"showDistractors\" class=\"distractor-container\">\n        <strong translate>Distractors</strong>\n\n        <div\n          class=\"ordering-choice row\"\n          *ngFor=\"let distractor of distractors; trackBy: trackByKey\"\n        >\n          <mhe-editor\n            class=\"col-6 choice-editor\"\n            [maxCharacter]=\"distractorLimit\"\n            [content]=\"distractor.text\"\n            [noMedia]=\"true\"\n            [imageOnly]=\"true\"\n            [showResize]=\"false\"\n            (contentChange)=\"onDistractorContentUpdate($event, distractor)\"\n          ></mhe-editor>\n\n          <aa-trash-can\n            *ngIf=\"canDeleteDistractor()\"\n            class=\"delete-distractor col-auto align-self-end\"\n            (delete)=\"deleteDistractor.emit(distractor)\"\n          ></aa-trash-can>\n        </div>\n\n        <div>\n          <button\n            type=\"button\"\n            class=\"add-choice\"\n            *ngIf=\"canAddChoice()\"\n            (click)=\"addDistractor.emit()\"\n          >\n            <span translate>+ Add distractor</span>\n          </button>\n        </div>\n      </div>\n    </div>\n  </div>\n  <aa-inline-error [errors]=\"getChoiceError()\"></aa-inline-error>\n</div>\n", styles: [".sub-heading-wrapper{overflow:auto}.ordering-choices-label{margin-top:15px;margin-bottom:.2em}.choice-container{margin:10px 0}.choice-container .choices-wrapper{border:1px solid #a9a9a9;background-color:#fdfbfb}.choice-container .choices-wrapper .ordering-list{margin:3em 0 3em 3em}.choice-container .choices-wrapper .ordering-list .ordering-choice{margin-bottom:1.5rem;display:flex;position:relative}.choice-container .choices-wrapper .ordering-list .ordering-choice .choice-editor{resize:vertical;border-radius:5px}.choice-container .choices-wrapper .ordering-list hr{border-top:1px solid #d6d6d6}.choice-container .choices-wrapper .ordering-list .add-choice{border:0;margin-left:1em;background:transparent;padding:0}.choice-container .choices-wrapper .ordering-list .checkbox-title{font-size:15px;font-weight:400;padding-left:5px}.choice-container .error-class{border:1px solid #b0181e}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OrderingChoicesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-ordering-choices', template: "<h2 translate>Ordering choices *</h2>\n\n<div class=\"sub-heading-wrapper\">\n  <span translate>Choices will be shuffled.</span>\n</div>\n\n<div class=\"choice-container\">\n  <div\n    class=\"choices-wrapper\"\n    [ngClass]=\"{ 'error-class': getChoiceError()?.length }\"\n  >\n    <div class=\"ordering-list\">\n      <strong class=\"mb-2\">Choices in order</strong>\n\n      <div\n        class=\"ordering-choice row\"\n        *ngFor=\"\n          let choice of choices;\n          let choiceIndex = index;\n          trackBy: trackByKey\n        \"\n      >\n        <mhe-editor\n          class=\"col-6 choice-editor\"\n          [maxCharacter]=\"choiceLimit\"\n          [content]=\"choice.text\"\n          [noMedia]=\"true\"\n          [imageOnly]=\"true\"\n          [showResize]=\"false\"\n          (contentChange)=\"onChoiceContentUpdate($event, choice)\"\n          (editorFocusOut)=\"onEditorFocusOut(answers)\"\n        ></mhe-editor>\n\n        <aa-trash-can\n          class=\"delete-choice col-auto align-self-end\"\n          *ngIf=\"canDeleteChoice()\"\n          (delete)=\"deleteChoice.emit(choice)\"\n        ></aa-trash-can>\n      </div>\n\n      <button\n        type=\"button\"\n        class=\"add-choice\"\n        *ngIf=\"canAddChoice()\"\n        (click)=\"addChoice.emit()\"\n      >\n        <span translate>+ Add choice</span>\n      </button>\n\n      <hr />\n\n      <div class=\"mb-3\">\n        <label translate class=\"form-check form-check-label\">\n          <input\n            type=\"checkbox\"\n            class=\"form-check-input\"\n            name=\"distractor\"\n            [disabled]=\"disableCheckbox()\"\n            [checked]=\"showDistractors\"\n            (change)=\"onToggleDistractorVisibility()\"\n            #distractorCheckbox\n          />\n          Drag choices to separate dock location\n        </label>\n      </div>\n\n      <div *ngIf=\"showDistractors\" class=\"distractor-container\">\n        <strong translate>Distractors</strong>\n\n        <div\n          class=\"ordering-choice row\"\n          *ngFor=\"let distractor of distractors; trackBy: trackByKey\"\n        >\n          <mhe-editor\n            class=\"col-6 choice-editor\"\n            [maxCharacter]=\"distractorLimit\"\n            [content]=\"distractor.text\"\n            [noMedia]=\"true\"\n            [imageOnly]=\"true\"\n            [showResize]=\"false\"\n            (contentChange)=\"onDistractorContentUpdate($event, distractor)\"\n          ></mhe-editor>\n\n          <aa-trash-can\n            *ngIf=\"canDeleteDistractor()\"\n            class=\"delete-distractor col-auto align-self-end\"\n            (delete)=\"deleteDistractor.emit(distractor)\"\n          ></aa-trash-can>\n        </div>\n\n        <div>\n          <button\n            type=\"button\"\n            class=\"add-choice\"\n            *ngIf=\"canAddChoice()\"\n            (click)=\"addDistractor.emit()\"\n          >\n            <span translate>+ Add distractor</span>\n          </button>\n        </div>\n      </div>\n    </div>\n  </div>\n  <aa-inline-error [errors]=\"getChoiceError()\"></aa-inline-error>\n</div>\n", styles: [".sub-heading-wrapper{overflow:auto}.ordering-choices-label{margin-top:15px;margin-bottom:.2em}.choice-container{margin:10px 0}.choice-container .choices-wrapper{border:1px solid #a9a9a9;background-color:#fdfbfb}.choice-container .choices-wrapper .ordering-list{margin:3em 0 3em 3em}.choice-container .choices-wrapper .ordering-list .ordering-choice{margin-bottom:1.5rem;display:flex;position:relative}.choice-container .choices-wrapper .ordering-list .ordering-choice .choice-editor{resize:vertical;border-radius:5px}.choice-container .choices-wrapper .ordering-list hr{border-top:1px solid #d6d6d6}.choice-container .choices-wrapper .ordering-list .add-choice{border:0;margin-left:1em;background:transparent;padding:0}.choice-container .choices-wrapper .ordering-list .checkbox-title{font-size:15px;font-weight:400;padding-left:5px}.choice-container .error-class{border:1px solid #b0181e}\n"] }]
        }], ctorParameters: function () { return [{ type: OrderingValidationService }]; }, propDecorators: { choices: [{
                type: Input
            }], distractors: [{
                type: Input
            }], showDistractors: [{
                type: Input
            }], answers: [{
                type: Input
            }], addChoice: [{
                type: Output
            }], updateChoiceContent: [{
                type: Output
            }], deleteChoice: [{
                type: Output
            }], addDistractor: [{
                type: Output
            }], updateDistractorContent: [{
                type: Output
            }], deleteDistractor: [{
                type: Output
            }], toggleDistractorVisibility: [{
                type: Output
            }] } });

class AirOrderingComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirOrdering, LoadAirItemAction$m, ResetStoreAction$m);
        this.store = store;
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.airOrdering$ = this.store.select(getAirOrdering);
        this.pureAirOrdering$ = this.store.select(getPureAirOrdering);
    }
    onAddChoice() {
        this.store.dispatch(new AddChoiceAction$3());
    }
    onUpdateChoiceContent({ choice, content }) {
        this.store.dispatch(new UpdateChoiceContentAction$2({ choice, content }));
    }
    onDeleteChoice(choice) {
        this.store.dispatch(new DeleteChoiceAction$3(choice));
    }
    onAddDistractor() {
        this.store.dispatch(new AddDistractorAction$1());
    }
    onUpdateDistractorContent({ distractor, content }) {
        this.store.dispatch(new UpdateDistractorContentAction$1({ distractor, content }));
    }
    onDeleteDistractor(distractor) {
        this.store.dispatch(new DeleteDistractorAction$1(distractor));
    }
    onToggleDistractorVisibility() {
        this.store.dispatch(new ToggleDistractorVisibilityAction());
    }
}
AirOrderingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirOrderingComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirOrderingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirOrderingComponent, selector: "aa-air-ordering", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-ordering-choices [choices]=\"(airOrdering$ | async)?.choices\"\n                     [distractors]=\"(airOrdering$ | async)?.distractors\"\n                     [answers]=\"(pureAirOrdering$ | async)?.answers\"\n                     [showDistractors]=\"(airOrdering$ | async)?.showDistractors\"\n                     (addChoice)=\"onAddChoice()\"\n                     (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                     (deleteChoice)=\"onDeleteChoice($event)\"\n                     (addDistractor)=\"onAddDistractor()\"\n                     (updateDistractorContent)=\"onUpdateDistractorContent($event)\"\n                     (toggleDistractorVisibility)=\"onToggleDistractorVisibility()\"\n                     (deleteDistractor)=\"onDeleteDistractor($event)\"></aa-ordering-choices>\n", dependencies: [{ kind: "component", type: OrderingChoicesComponent, selector: "aa-ordering-choices", inputs: ["choices", "distractors", "showDistractors", "answers"], outputs: ["addChoice", "updateChoiceContent", "deleteChoice", "addDistractor", "updateDistractorContent", "deleteDistractor", "toggleDistractorVisibility"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirOrderingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-ordering', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-ordering-choices [choices]=\"(airOrdering$ | async)?.choices\"\n                     [distractors]=\"(airOrdering$ | async)?.distractors\"\n                     [answers]=\"(pureAirOrdering$ | async)?.answers\"\n                     [showDistractors]=\"(airOrdering$ | async)?.showDistractors\"\n                     (addChoice)=\"onAddChoice()\"\n                     (updateChoiceContent)=\"onUpdateChoiceContent($event)\"\n                     (deleteChoice)=\"onDeleteChoice($event)\"\n                     (addDistractor)=\"onAddDistractor()\"\n                     (updateDistractorContent)=\"onUpdateDistractorContent($event)\"\n                     (toggleDistractorVisibility)=\"onToggleDistractorVisibility()\"\n                     (deleteDistractor)=\"onDeleteDistractor($event)\"></aa-ordering-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const MIN_SELECTION_CHOCIES = 2;
class SelectTextValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirSelectText$ = this.store.select(getPureAirSelectText);
        this.airSelectText$ = this.store.select(getAirSelectText);
        this.selectionTextError = '';
        this.choicesError = '';
        this.answersError = '';
    }
    validate() {
        let isValid;
        this.pureAirSelectText$.pipe(take(1)).subscribe((pureAirSelectText) => {
            this.airSelectText$.pipe(take(1)).subscribe((airSelectText) => {
                const hasPromptError = !!this.promptValidationService.validatePrompt(pureAirSelectText.prompt);
                const hasSelectionTextError = !!this.validateSelectionText(pureAirSelectText.selectionText);
                const hasChoicesError = !!this.validateChoices(pureAirSelectText.selectionText);
                const hasAnswersError = !!this.validateAnswers(airSelectText.answers, airSelectText.selectionUnits, airSelectText.editSelectedText);
                isValid = !(hasPromptError || hasSelectionTextError || hasChoicesError || hasAnswersError);
            });
        });
        return isValid;
    }
    validateSelectionText(value) {
        // Check if selection text prompt is empty
        this.selectionTextError = '';
        if (!value || value.length === 0) {
            this.selectionTextError = E_INVALID_SELECTION_TEXT;
        }
        return this.selectionTextError;
    }
    validateChoices(choicesOrFillers) {
        this.resetChoicesError();
        const selectionChoices = choicesOrFillers.filter((selectionUnit) => selectionUnit.type === 'choice');
        // Check if at least two choices are selected
        if (!selectionChoices || selectionChoices.length < MIN_SELECTION_CHOCIES) {
            this.choicesError = E_TOO_FEW_CHOICES_SELECTED;
        }
        return this.choicesError;
    }
    validateAnswers(answers, selectionUnits, editSelectedText = false) {
        this.resetAnswersError();
        if (editSelectedText) {
            const hasNoAnswers = answers.some((answer) => {
                if (typeof answer === 'string') {
                    return false;
                }
                if (answer.values && !answer.values.some((values) => values.trim() !== '')) {
                    return true;
                }
                return false;
            });
            // Check if at least one selection unit is selected as correct answer.
            if (hasNoAnswers) {
                this.answersError = E_BLANK_CORRECT_ANSWER;
            }
            else {
                const choiceAsAnswers = answers.filter((answer) => {
                    if (typeof answer === 'string') {
                        return false;
                    }
                    return answer.isAnswerChoiceCorrect;
                });
                // Check if all the answers are marked as choiced as correct.
                if (choiceAsAnswers.length === answers.length) {
                    this.answersError = E_ALL_CHOICE_AS_CORRECT;
                }
                else {
                    // Check if one of the answers match with the original answer if choice is not marked as correct.
                    this.answersError = answers.some((answer) => {
                        if (typeof answer === 'string') {
                            return false;
                        }
                        const choiceText = selectionUnits.find((unit) => unit.key === answer.key)?.text;
                        return !answer.isAnswerChoiceCorrect && answer.values.some((value) => value === choiceText);
                    })
                        ? E_SAME_ANSWER_AS_CHOICE
                        : '';
                }
            }
        }
        else {
            if (!answers || !answers.length) {
                // No correct answers added for multi-answers.
                this.answersError = E_NO_CORRECT_ANSWER_MULTIPLE_RESPONSE;
            }
            else if (selectionUnits.filter((unit) => unit.type === 'choice').length === answers.length) {
                this.answersError = E_CHOICE_ANSWER_SAME;
            }
        }
        return this.answersError;
    }
    getSelectionTextErrors() {
        return this.selectionTextError ? [this.selectionTextError] : [];
    }
    getChoicesErrors() {
        return this.choicesError ? [this.choicesError] : [];
    }
    getAnswersErrors() {
        return this.answersError ? [this.answersError] : [];
    }
    resetSelectionTextError() {
        this.selectionTextError = '';
    }
    resetChoicesError() {
        this.choicesError = '';
    }
    resetAnswersError() {
        this.answersError = '';
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.resetSelectionTextError();
        this.resetChoicesError();
        this.resetAnswersError();
    }
    validateOnUpdateChoices() {
        if (this.choicesError.length) {
            this.pureAirSelectText$.pipe(take(1)).subscribe((airSelectText) => {
                this.validateChoices(airSelectText.selectionText);
            });
        }
    }
    validateOnUpdateAnswers() {
        if (this.answersError.length) {
            this.airSelectText$.pipe(take(1)).subscribe((airSelectText) => {
                this.validateAnswers(airSelectText.answers, airSelectText.selectionUnits, airSelectText.editSelectedText);
            });
        }
    }
}
SelectTextValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectTextValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
SelectTextValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectTextValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectTextValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

// prettier-ignore
class SelectionUnitComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.toggleChoice = new EventEmitter();
        this.toggleAnswer = new EventEmitter();
    }
    ngOnChanges(changes) {
        const selectionUnitChange = changes.selectionUnit;
        if (selectionUnitChange && selectionUnitChange.currentValue !== selectionUnitChange.previousValue) {
            this.selectionUnitRef.nativeElement.innerHTML = this.innerHtml();
        }
    }
    classes() {
        return {
            answer: this.isAnswer,
            choice: this.selectionUnit.type === 'choice',
            'can-toggle-choice': this.canToggleChoice,
            'can-toggle-answer': this.canToggleAnswer,
            selectable: this.canSelectCustomRange
        };
    }
    onClick() {
        if (this.canToggleChoice) {
            this.toggleChoice.emit(this.selectionUnit.key);
        }
        if (this.canToggleAnswer) {
            this.toggleAnswer.emit(this.selectionUnit.key);
        }
    }
    innerHtml() {
        const html = this.selectionUnit.text
            .split('\n')
            .map((line, idx) => `<span row="${idx}">${line}</span>`)
            .join('<br>');
        return this.isSelectionCustom
            ? html
            : this.selectionUnit.text.replace('\n', '<br>');
    }
    hasChild(element) {
        const children = Array.from(this.elementRef.nativeElement.children);
        return children.includes(element);
    }
}
SelectionUnitComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
SelectionUnitComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SelectionUnitComponent, selector: "aa-selection-unit", inputs: { selectionUnit: "selectionUnit", isAnswer: "isAnswer", canToggleChoice: "canToggleChoice", canToggleAnswer: "canToggleAnswer", isSelectionCustom: "isSelectionCustom", canSelectCustomRange: "canSelectCustomRange" }, outputs: { toggleChoice: "toggleChoice", toggleAnswer: "toggleAnswer" }, viewQueries: [{ propertyName: "selectionUnitRef", first: true, predicate: ["unit"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: `<span #unit
                   class="selection-unit"
                   [ngClass]="classes()"
                   (click)="onClick()"></span>`, isInline: true, styles: [".selection-unit.can-toggle-choice,.selection-unit.can-toggle-answer{cursor:pointer}.selection-unit.choice,.selection-unit.can-toggle-choice:hover{background:lightgray}.selection-unit.answer,.selection-unit.choice.can-toggle-answer:hover{background:lightgreen}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-selection-unit', template: `<span #unit
                   class="selection-unit"
                   [ngClass]="classes()"
                   (click)="onClick()"></span>`, styles: [".selection-unit.can-toggle-choice,.selection-unit.can-toggle-answer{cursor:pointer}.selection-unit.choice,.selection-unit.can-toggle-choice:hover{background:lightgray}.selection-unit.answer,.selection-unit.choice.can-toggle-answer:hover{background:lightgreen}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { selectionUnit: [{
                type: Input
            }], isAnswer: [{
                type: Input
            }], canToggleChoice: [{
                type: Input
            }], canToggleAnswer: [{
                type: Input
            }], isSelectionCustom: [{
                type: Input
            }], canSelectCustomRange: [{
                type: Input
            }], toggleChoice: [{
                type: Output
            }], toggleAnswer: [{
                type: Output
            }], selectionUnitRef: [{
                type: ViewChild,
                args: ['unit', { static: true }]
            }] } });

class SelectionUnitsComponent {
    constructor(selectTextValidationService, configService) {
        this.selectTextValidationService = selectTextValidationService;
        this.configService = configService;
        this.answers = [];
        this.errors = [];
        this.toggleChoice = new EventEmitter();
        this.toggleAnswer = new EventEmitter();
        this.selectedCustomRange = new EventEmitter();
        this.updateRationaleContent = new EventEmitter();
        this.allowValidation = false;
        this.answerLimit = RATIONALE_LIMIT_SELECT_TEXT;
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    ngOnChanges(changes) {
        if (changes['selectionUnits']) {
            this.choiceCount = this.selectionUnits.filter((unit) => unit.type === 'choice').length;
        }
        if (changes['allowedEditSelectedText']) {
            this.validateOnMouseup();
        }
    }
    onMouseup() {
        this.validateOnMouseup();
        if (!this.canSelectCustomRange) {
            return;
        }
        const range = window.getSelection()?.getRangeAt(0);
        if (!range) {
            return;
        }
        this.onSelectedRange(range);
    }
    onSelectedRange(range) {
        if (range.collapsed) {
            return;
        }
        const startContainerElement = range.startContainer.parentElement;
        const endContainerElement = range.endContainer.parentElement;
        const startParent = startContainerElement?.parentElement;
        const endParent = endContainerElement?.parentElement;
        if (!startParent || !endParent) {
            return;
        }
        const startComponent = this.selectionUnitComponents.find((component) => component.hasChild(startParent));
        const endComponent = this.selectionUnitComponents.find((component) => component.hasChild(endParent));
        if (startComponent && endComponent) {
            const startRow = startContainerElement.getAttribute('row');
            const startRowNum = startRow ? +startRow : 0;
            const startOffset = this.getOffsetWRTUnit(startComponent.selectionUnit.text, startRowNum, range.startOffset);
            const endRow = endContainerElement.getAttribute('row');
            const endRowNum = endRow ? +endRow : 0;
            const endOffset = this.getOffsetWRTUnit(endComponent.selectionUnit.text, endRowNum, range.endOffset);
            this.selectedCustomRange.emit({
                startUnitKey: startComponent.selectionUnit.key,
                startOffset: startOffset,
                endUnitKey: endComponent.selectionUnit.key,
                endOffset: endOffset,
            });
            range.collapse(false);
        }
    }
    getOffsetWRTUnit(text, rowNumber, offset) {
        const lines = text.split('\n');
        let relativeOffset = 0;
        for (let i = 0; i < lines.length && i !== rowNumber; i++) {
            relativeOffset += lines[i].length + 1;
        }
        return relativeOffset + offset;
    }
    onToggleChoice(selectionUnit) {
        this.toggleChoice.emit(selectionUnit.key);
    }
    onToggleAnswer(selectionUnit) {
        this.toggleAnswer.emit(selectionUnit);
    }
    trackByIndex(index) {
        return index;
    }
    isAnswer(selectionUnit) {
        const answerKeys = this.answers.map((answer) => {
            if (answer['key']) {
                return answer['key'];
            }
            return answer;
        });
        return answerKeys.includes(selectionUnit.key);
    }
    canToggleChoice(selectionUnit) {
        const isCustomFiller = this.canSelectCustomRange && selectionUnit.type === 'filler';
        const willExceedCapacity = selectionUnit.type === 'filler' && this.choiceCount >= 100;
        return this.canToggleChoices && !isCustomFiller && !willExceedCapacity && !this.isWhitespace(selectionUnit);
    }
    canToggleAnswer(selectionUnit) {
        const isChoice = selectionUnit.type === 'choice';
        const willExceedCapacity = this.answers.length >= 100 && !this.isAnswer(selectionUnit);
        return this.canToggleAnswers && isChoice && !willExceedCapacity;
    }
    isWhitespace(selectionUnit) {
        return selectionUnit.type === 'filler' && selectionUnit.text.trim() === '';
    }
    getErrors() {
        return this.allowValidation && this.errors;
    }
    validateOnMouseup() {
        setTimeout(() => {
            this.selectTextValidationService.validateOnUpdateChoices();
            this.selectTextValidationService.validateOnUpdateAnswers();
        });
    }
    selectionTextChoices() {
        const choiceSet = this.selectionUnits ? this.selectionUnits.filter((unit) => unit.type === 'choice') : [];
        return choiceSet;
    }
    onUpdateRationaleContent(choice, content) {
        const { key: choiceKey } = choice;
        const selectionUnit = this.selectionUnits.filter((unit) => unit.key === choiceKey);
        if (selectionUnit.length > 0) {
            this.updateRationaleContent.emit({ choice, content: content });
        }
    }
}
SelectionUnitsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitsComponent, deps: [{ token: SelectTextValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
SelectionUnitsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SelectionUnitsComponent, selector: "aa-selection-units", inputs: { allowedEditSelectedText: "allowedEditSelectedText", selectionUnits: "selectionUnits", answers: "answers", isSelectionCustom: "isSelectionCustom", canSelectCustomRange: "canSelectCustomRange", canToggleChoices: "canToggleChoices", canToggleAnswers: "canToggleAnswers", errors: "errors", showRationales: "showRationales" }, outputs: { toggleChoice: "toggleChoice", toggleAnswer: "toggleAnswer", selectedCustomRange: "selectedCustomRange", updateRationaleContent: "updateRationaleContent" }, viewQueries: [{ propertyName: "selectionUnitComponents", predicate: SelectionUnitComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"well selection-units\"\n  [class.has-error]=\"errors.length\"\n  [ngClass]=\"{ selectable: canSelectCustomRange }\"\n  (mouseup)=\"onMouseup()\"\n>\n  <aa-selection-unit\n    *ngFor=\"let selectionUnit of selectionUnits; trackBy: trackByIndex\"\n    [selectionUnit]=\"selectionUnit\"\n    [isAnswer]=\"isAnswer(selectionUnit)\"\n    [isSelectionCustom]=\"isSelectionCustom\"\n    [canSelectCustomRange]=\"canSelectCustomRange\"\n    [canToggleChoice]=\"canToggleChoice(selectionUnit)\"\n    [canToggleAnswer]=\"canToggleAnswer(selectionUnit)\"\n    (toggleChoice)=\"onToggleChoice(selectionUnit)\"\n    (toggleAnswer)=\"onToggleAnswer(selectionUnit)\"\n  ></aa-selection-unit>\n</div>\n\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n\n<div class=\"rationale-wrapper\" *ngIf=\"showRationales\">\n  <div\n    class=\"rationale-unit\"\n    *ngFor=\"let choice of selectionTextChoices(); trackBy: trackByIndex\"\n  >\n    <div class=\"row mb-3\">\n      <div class=\"col-2\" translate>Answer Choice</div>\n      <div class=\"col-10\">{{ choice.text }}</div>\n    </div>\n\n    <div class=\"rationale-label row mb-3\">\n      <span translate class=\"col-2 col-form-label\">Reason</span>\n      <mhe-editor\n        class=\"col-10 rationale-editor\"\n        [content]=\"choice.rationale\"\n        [maxCharacter]=\"answerLimit\"\n        [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n        [useRichText]=\"false\"\n        [showResize]=\"false\"\n        (contentChange)=\"onUpdateRationaleContent(choice, $event)\"\n      ></mhe-editor>\n    </div>\n  </div>\n</div>\n", styles: [".selection-units:not(.selectable){-webkit-user-select:none;-moz-user-select:none;user-select:none}.has-error{margin-bottom:0;border:1px solid #b0181e}.rationale-wrapper{display:flex;flex-direction:column}.rationale-unit{border-bottom:1px solid #d1dbde;display:flex;flex-direction:column;margin:10px 0}.rationale-unit .col-xs-2{padding-left:0}.rationale-unit .col-xs-10{padding-right:0}.rationale-label{display:flex;align-items:center}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: SelectionUnitComponent, selector: "aa-selection-unit", inputs: ["selectionUnit", "isAnswer", "canToggleChoice", "canToggleAnswer", "isSelectionCustom", "canSelectCustomRange"], outputs: ["toggleChoice", "toggleAnswer"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-selection-units', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  class=\"well selection-units\"\n  [class.has-error]=\"errors.length\"\n  [ngClass]=\"{ selectable: canSelectCustomRange }\"\n  (mouseup)=\"onMouseup()\"\n>\n  <aa-selection-unit\n    *ngFor=\"let selectionUnit of selectionUnits; trackBy: trackByIndex\"\n    [selectionUnit]=\"selectionUnit\"\n    [isAnswer]=\"isAnswer(selectionUnit)\"\n    [isSelectionCustom]=\"isSelectionCustom\"\n    [canSelectCustomRange]=\"canSelectCustomRange\"\n    [canToggleChoice]=\"canToggleChoice(selectionUnit)\"\n    [canToggleAnswer]=\"canToggleAnswer(selectionUnit)\"\n    (toggleChoice)=\"onToggleChoice(selectionUnit)\"\n    (toggleAnswer)=\"onToggleAnswer(selectionUnit)\"\n  ></aa-selection-unit>\n</div>\n\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n\n<div class=\"rationale-wrapper\" *ngIf=\"showRationales\">\n  <div\n    class=\"rationale-unit\"\n    *ngFor=\"let choice of selectionTextChoices(); trackBy: trackByIndex\"\n  >\n    <div class=\"row mb-3\">\n      <div class=\"col-2\" translate>Answer Choice</div>\n      <div class=\"col-10\">{{ choice.text }}</div>\n    </div>\n\n    <div class=\"rationale-label row mb-3\">\n      <span translate class=\"col-2 col-form-label\">Reason</span>\n      <mhe-editor\n        class=\"col-10 rationale-editor\"\n        [content]=\"choice.rationale\"\n        [maxCharacter]=\"answerLimit\"\n        [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n        [useRichText]=\"false\"\n        [showResize]=\"false\"\n        (contentChange)=\"onUpdateRationaleContent(choice, $event)\"\n      ></mhe-editor>\n    </div>\n  </div>\n</div>\n", styles: [".selection-units:not(.selectable){-webkit-user-select:none;-moz-user-select:none;user-select:none}.has-error{margin-bottom:0;border:1px solid #b0181e}.rationale-wrapper{display:flex;flex-direction:column}.rationale-unit{border-bottom:1px solid #d1dbde;display:flex;flex-direction:column;margin:10px 0}.rationale-unit .col-xs-2{padding-left:0}.rationale-unit .col-xs-10{padding-right:0}.rationale-label{display:flex;align-items:center}\n"] }]
        }], ctorParameters: function () { return [{ type: SelectTextValidationService }, { type: ConfigService }]; }, propDecorators: { allowedEditSelectedText: [{
                type: Input
            }], selectionUnits: [{
                type: Input
            }], answers: [{
                type: Input
            }], isSelectionCustom: [{
                type: Input
            }], canSelectCustomRange: [{
                type: Input
            }], canToggleChoices: [{
                type: Input
            }], canToggleAnswers: [{
                type: Input
            }], errors: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], toggleChoice: [{
                type: Output
            }], toggleAnswer: [{
                type: Output
            }], selectedCustomRange: [{
                type: Output
            }], updateRationaleContent: [{
                type: Output
            }], selectionUnitComponents: [{
                type: ViewChildren,
                args: [SelectionUnitComponent]
            }] } });

class SelectTextEditComponent {
    constructor(configService, selectTextValidationService) {
        this.configService = configService;
        this.selectTextValidationService = selectTextValidationService;
        this.errors = [];
        this.selectionUnits = [];
        this.updateSelectEditAnswer = new EventEmitter();
        this.addAnswer = new EventEmitter();
        this.deleteAnswer = new EventEmitter();
        this.correctAnswerChange = new EventEmitter();
        this.updateRationaleContent = new EventEmitter();
        this.answerLimit = RATIONALE_LIMIT_SELECT_TEXT;
        this.valueLimit = SELECT_TEXT_CORRECT_ANSWER_LIMIT;
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        this.allowValidation = false;
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    ngOnChanges(changes) {
        if (changes['allowedEditSelectedText']) {
            this.validateOnUpdate();
        }
    }
    selectionTextChoices() {
        return this.selectionUnits.filter((unit) => {
            return unit.type === 'choice';
        });
    }
    onMouseup() {
        this.validateOnUpdate();
    }
    validateOnUpdate() {
        setTimeout(() => {
            this.selectTextValidationService.validateOnUpdateChoices();
            this.selectTextValidationService.validateOnUpdateAnswers();
        });
    }
    getErrors() {
        return this.allowValidation && this.errors;
    }
    getChoice(answerKey) {
        return this.selectionTextChoices().find((choice) => choice.key === answerKey);
    }
    onInputChange(answerKey, valueIndex, content) {
        this.updateSelectEditAnswer.emit({ answerKey, valueIndex, content });
    }
    onAddingAnswer(answerKey) {
        this.addAnswer.emit(answerKey);
    }
    trackIndex(index) {
        return index;
    }
    trackAnswer(index, answer) {
        return answer.key;
    }
    handleCorrectAnswerChange(answerKey, isChoiceCorrect) {
        this.correctAnswerChange.emit({ answerKey, isChoiceCorrect });
    }
    canAddCorrectanswer(values) {
        return values && values.length < 5;
    }
    canDeleteAnswer(values) {
        return values && values.length > 1;
    }
    onDeleteAnswer(answerKey, valueIndex) {
        this.deleteAnswer.emit({ answerKey, valueIndex });
    }
    onUpdateRationaleContent(choice, content) {
        const { key: choiceKey } = choice;
        const selectionUnit = this.selectionUnits.filter((unit) => unit.key === choiceKey);
        if (selectionUnit.length) {
            this.updateRationaleContent.emit({ choice, content: content });
        }
    }
}
SelectTextEditComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectTextEditComponent, deps: [{ token: ConfigService }, { token: SelectTextValidationService }], target: i0.ɵɵFactoryTarget.Component });
SelectTextEditComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SelectTextEditComponent, selector: "aa-selection-correct-ans", inputs: { allowedEditSelectedText: "allowedEditSelectedText", answers: "answers", errors: "errors", selectionUnits: "selectionUnits", showRationales: "showRationales" }, outputs: { updateSelectEditAnswer: "updateSelectEditAnswer", addAnswer: "addAnswer", deleteAnswer: "deleteAnswer", correctAnswerChange: "correctAnswerChange", updateRationaleContent: "updateRationaleContent" }, usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"well selection-units\"\n  [class.has-error]=\"errors.length\"\n  (mouseup)=\"onMouseup()\"\n>\n  <div\n    class=\"select-and-edit\"\n    *ngFor=\"\n      let answer of answers;\n      let answerIndex = index;\n      trackBy: trackAnswer\n    \"\n  >\n    <div class=\"row answer-choice\">\n      <div class=\"col-2 sub-header\" translate>Answer Choice</div>\n      <div class=\"col-10\">{{ getChoice(answer.key)?.text }}</div>\n    </div>\n    <div class=\"row correct-answer-block\">\n      <div class=\"col-2 sub-header\" translate>Correct Answer</div>\n      <div class=\"col-10 correct-answer-wrapper\">\n        <div\n          class=\"correct-answer-input\"\n          *ngFor=\"\n            let value of answer.values;\n            let valueIndex = index;\n            trackBy: trackIndex\n          \"\n        >\n        <div class=\"select-text-answer\">\n          <input\n            type=\"text\"\n            [value]=\"value\"\n            [maxlength]=\"valueLimit\"\n            [disabled]=\"answer?.isAnswerChoiceCorrect\"\n            class=\"form-control\"\n            (input)=\"onInputChange(answer.key, valueIndex, $event.target.value)\"\n          />\n        </div>\n        \n          <aa-trash-can\n            *ngIf=\"canDeleteAnswer(answer?.values)\"\n            class=\"delete-answer-key\"\n            (delete)=\"onDeleteAnswer(answer.key, valueIndex)\"\n          >\n          </aa-trash-can>\n        </div>\n        <button\n          class=\"add-correct-answer\"\n          *ngIf=\"canAddCorrectanswer(answer?.values)\"\n          [ngClass]=\"{ 'disabled-link': answer?.isAnswerChoiceCorrect }\"\n          [disabled]=\"answer?.isAnswerChoiceCorrect\"\n          (click)=\"onAddingAnswer(answer.key)\"\n        >\n          <span translate>+ Add correct answer</span>\n        </button>\n        <div class=\"correct-choice-checkbox\">\n          <div class=\"form-check\">\n            <input\n              id=\"answer-choice\" name=\"answer-choice\"\n               type=\"checkbox\" class=\"form-check-input\"\n              [checked]=\"answer?.isAnswerChoiceCorrect\"\n              (change)=\"handleCorrectAnswerChange(answer.key, $event.target.checked)\"\n            />\n            <label class=\"form-check-label\" for=\"answer-choice\" translate>Answer choice is correct</label>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div *ngIf=\"showRationales\" class=\"row rationale-label\">\n      <div translate class=\"col-2 sub-header\">Reason</div>\n      <mhe-editor\n        class=\"col-8 rationale-editor\"\n        [content]=\"getChoice(answer.key)?.rationale\"\n        [maxCharacter]=\"answerLimit\"\n        [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n        [useRichText]=\"false\"\n        [showResize]=\"false\"\n        (contentChange)=\"\n          onUpdateRationaleContent(getChoice(answer.key), $event)\n        \"\n      >\n      </mhe-editor>\n    </div>\n    <hr />\n  </div>\n</div>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".has-error{margin-bottom:0;border:1px solid #b0181e}.select-and-edit .answer-choice,.select-and-edit .correct-answer-block{padding:10px 0}.select-and-edit .correct-answer-block .correct-choice-checkbox{display:flex;clear:both}.select-and-edit .correct-answer-wrapper{position:relative}.select-and-edit .correct-answer-input{display:inline-flex;flex-direction:row;margin-right:40px;position:relative;padding-right:12px;float:left;align-items:baseline}.select-and-edit .correct-answer-input .select-text-answer{width:12em}.select-and-edit .add-correct-answer{background:transparent;border:0;padding:0;position:relative;top:18px;margin-bottom:15px;display:inline-flex;flex-direction:row;float:left}.select-and-edit .disabled-link{color:gray}.select-and-edit .rationale-label .rationale-editor{position:relative;bottom:20px}.select-and-edit .sub-header{font-weight:500}.select-and-edit hr{border-top:1px solid grey}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectTextEditComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-selection-correct-ans', template: "<div\n  class=\"well selection-units\"\n  [class.has-error]=\"errors.length\"\n  (mouseup)=\"onMouseup()\"\n>\n  <div\n    class=\"select-and-edit\"\n    *ngFor=\"\n      let answer of answers;\n      let answerIndex = index;\n      trackBy: trackAnswer\n    \"\n  >\n    <div class=\"row answer-choice\">\n      <div class=\"col-2 sub-header\" translate>Answer Choice</div>\n      <div class=\"col-10\">{{ getChoice(answer.key)?.text }}</div>\n    </div>\n    <div class=\"row correct-answer-block\">\n      <div class=\"col-2 sub-header\" translate>Correct Answer</div>\n      <div class=\"col-10 correct-answer-wrapper\">\n        <div\n          class=\"correct-answer-input\"\n          *ngFor=\"\n            let value of answer.values;\n            let valueIndex = index;\n            trackBy: trackIndex\n          \"\n        >\n        <div class=\"select-text-answer\">\n          <input\n            type=\"text\"\n            [value]=\"value\"\n            [maxlength]=\"valueLimit\"\n            [disabled]=\"answer?.isAnswerChoiceCorrect\"\n            class=\"form-control\"\n            (input)=\"onInputChange(answer.key, valueIndex, $event.target.value)\"\n          />\n        </div>\n        \n          <aa-trash-can\n            *ngIf=\"canDeleteAnswer(answer?.values)\"\n            class=\"delete-answer-key\"\n            (delete)=\"onDeleteAnswer(answer.key, valueIndex)\"\n          >\n          </aa-trash-can>\n        </div>\n        <button\n          class=\"add-correct-answer\"\n          *ngIf=\"canAddCorrectanswer(answer?.values)\"\n          [ngClass]=\"{ 'disabled-link': answer?.isAnswerChoiceCorrect }\"\n          [disabled]=\"answer?.isAnswerChoiceCorrect\"\n          (click)=\"onAddingAnswer(answer.key)\"\n        >\n          <span translate>+ Add correct answer</span>\n        </button>\n        <div class=\"correct-choice-checkbox\">\n          <div class=\"form-check\">\n            <input\n              id=\"answer-choice\" name=\"answer-choice\"\n               type=\"checkbox\" class=\"form-check-input\"\n              [checked]=\"answer?.isAnswerChoiceCorrect\"\n              (change)=\"handleCorrectAnswerChange(answer.key, $event.target.checked)\"\n            />\n            <label class=\"form-check-label\" for=\"answer-choice\" translate>Answer choice is correct</label>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div *ngIf=\"showRationales\" class=\"row rationale-label\">\n      <div translate class=\"col-2 sub-header\">Reason</div>\n      <mhe-editor\n        class=\"col-8 rationale-editor\"\n        [content]=\"getChoice(answer.key)?.rationale\"\n        [maxCharacter]=\"answerLimit\"\n        [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n        [useRichText]=\"false\"\n        [showResize]=\"false\"\n        (contentChange)=\"\n          onUpdateRationaleContent(getChoice(answer.key), $event)\n        \"\n      >\n      </mhe-editor>\n    </div>\n    <hr />\n  </div>\n</div>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".has-error{margin-bottom:0;border:1px solid #b0181e}.select-and-edit .answer-choice,.select-and-edit .correct-answer-block{padding:10px 0}.select-and-edit .correct-answer-block .correct-choice-checkbox{display:flex;clear:both}.select-and-edit .correct-answer-wrapper{position:relative}.select-and-edit .correct-answer-input{display:inline-flex;flex-direction:row;margin-right:40px;position:relative;padding-right:12px;float:left;align-items:baseline}.select-and-edit .correct-answer-input .select-text-answer{width:12em}.select-and-edit .add-correct-answer{background:transparent;border:0;padding:0;position:relative;top:18px;margin-bottom:15px;display:inline-flex;flex-direction:row;float:left}.select-and-edit .disabled-link{color:gray}.select-and-edit .rationale-label .rationale-editor{position:relative;bottom:20px}.select-and-edit .sub-header{font-weight:500}.select-and-edit hr{border-top:1px solid grey}\n"] }]
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: SelectTextValidationService }]; }, propDecorators: { allowedEditSelectedText: [{
                type: Input
            }], answers: [{
                type: Input
            }], errors: [{
                type: Input
            }], selectionUnits: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], updateSelectEditAnswer: [{
                type: Output
            }], addAnswer: [{
                type: Output
            }], deleteAnswer: [{
                type: Output
            }], correctAnswerChange: [{
                type: Output
            }], updateRationaleContent: [{
                type: Output
            }] } });

class SelectionUnitTypesComponent {
    constructor() {
        this.updateUnitType = new EventEmitter();
        this.allowEditText = new EventEmitter();
        this.selectUnitType = new EventEmitter();
        this.openConfirmationModal = false;
        this.unAllowedTypeSelected = false;
        this.selectTextUnitTypes = [
            { value: 'word', viewValue: 'Word' },
            { value: 'sentence', viewValue: 'Sentence' },
            { value: 'paragraph', viewValue: 'Paragraph' },
            { value: 'custom', viewValue: 'Custom' },
        ];
        this.editTextUnitTypes = [
            { value: 'word', viewValue: 'Word' },
            { value: 'custom', viewValue: 'Custom' },
        ];
    }
    onSelect(selectedSelectionUnitTypeOptions) {
        const currentUnitType = this.selectedSelectionUnitTypeOptions.value;
        const selectedUnitType = selectedSelectionUnitTypeOptions.value;
        this.selectedDropdownOption = selectedUnitType;
        if (selectedUnitType !== currentUnitType) {
            this.selectedSelectionUnitTypeOptions = selectedSelectionUnitTypeOptions;
            this.openConfirmationModal = true;
        }
    }
    ngOnChanges(changes) {
        const checkboxState = changes['checkboxSelected'];
        if (changes['unitType']) {
            this.warningMessage = W_DROPDOWN_CHANGE;
            this.updateUnitTypeDropdown();
        }
        if (checkboxState &&
            checkboxState.currentValue !== undefined &&
            checkboxState.previousValue !== checkboxState.currentValue) {
            this.unAllowedTypeSelected = !this.editTextUnitTypes.some((allowedTypes) => {
                return allowedTypes.value === this.selectedSelectionUnitTypeOptions.value;
            });
            if (this.checkboxSelected && this.unAllowedTypeSelected && !this.isEditTextConfirmed) {
                this.warningMessage = W_DROPDOWN_CHANGE;
                this.openConfirmationModal = true;
            }
        }
    }
    onConfirmAction() {
        let unitType = this.selectedDropdownOption;
        this.isEditTextConfirmed = false;
        this.openConfirmationModal = false;
        if (this.unAllowedTypeSelected) {
            unitType =
                this.checkboxSelected && this.isWordOrCustom(unitType)
                    ? this.selectedDropdownOption
                    : this.editTextUnitTypes[0].value;
            this.checkboxSelected = true;
            this.isEditTextConfirmed = true;
            this.unAllowedTypeSelected = false;
        }
        this.updateCheckboxState();
        this.updateUnitTypeDropdown();
        this.updateUnitType.emit(unitType);
    }
    onCloseAction() {
        if (this.openConfirmationModal) {
            this.openConfirmationModal = false;
            if (this.checkboxSelected) {
                if (!(this.isWordOrCustom(this.unitType) && this.isWordOrCustom(this.selectedDropdownOption))) {
                    this.checkboxSelected = false;
                }
            }
            this.updateCheckboxState();
            this.updateUnitTypeDropdown();
        }
    }
    isWordOrCustom(type) {
        return type === 'custom' || type === 'word';
    }
    updateCheckboxState() {
        this.allowEditText.emit(this.checkboxSelected);
    }
    updateUnitTypeDropdown() {
        this.selectedSelectionUnitTypeOptions = (this.checkboxSelected ? this.editTextUnitTypes : this.selectTextUnitTypes).filter((type) => type.value === this.unitType)[0];
        this.selectedDropdownOption = this.selectedSelectionUnitTypeOptions?.value;
    }
}
SelectionUnitTypesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitTypesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SelectionUnitTypesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: SelectionUnitTypesComponent, selector: "aa-selection-unit-types", inputs: { unitType: "unitType", checkboxSelected: "checkboxSelected" }, outputs: { updateUnitType: "updateUnitType", allowEditText: "allowEditText", selectUnitType: "selectUnitType" }, usesOnChanges: true, ngImport: i0, template: "<div>\n  <select\n    class=\"form-select d-inline w-auto\"\n    [ngModel]=\"selectedSelectionUnitTypeOptions\"\n    (ngModelChange)=\"onSelect($event)\"\n  >\n    <option\n      *ngFor=\"let option of checkboxSelected ? editTextUnitTypes : selectTextUnitTypes\"\n      [ngValue]=\"option\">\n      {{ option.viewValue }}\n    </option>\n  </select>\n</div>\n<air-modal\n  [isOpen]=\"openConfirmationModal\"\n  [confirmButtonText]=\"'Yes'\"\n  [cancelButtonText]=\"'No'\"\n  (confirm)=\"onConfirmAction()\"\n  (close)=\"onCloseAction()\"\n  (cancel)=\"onCloseAction()\">\n  <div translate>\n    {{warningMessage}}\n  </div>\n</air-modal>\n", dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectionUnitTypesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-selection-unit-types', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div>\n  <select\n    class=\"form-select d-inline w-auto\"\n    [ngModel]=\"selectedSelectionUnitTypeOptions\"\n    (ngModelChange)=\"onSelect($event)\"\n  >\n    <option\n      *ngFor=\"let option of checkboxSelected ? editTextUnitTypes : selectTextUnitTypes\"\n      [ngValue]=\"option\">\n      {{ option.viewValue }}\n    </option>\n  </select>\n</div>\n<air-modal\n  [isOpen]=\"openConfirmationModal\"\n  [confirmButtonText]=\"'Yes'\"\n  [cancelButtonText]=\"'No'\"\n  (confirm)=\"onConfirmAction()\"\n  (close)=\"onCloseAction()\"\n  (cancel)=\"onCloseAction()\">\n  <div translate>\n    {{warningMessage}}\n  </div>\n</air-modal>\n" }]
        }], propDecorators: { updateUnitType: [{
                type: Output
            }], allowEditText: [{
                type: Output
            }], selectUnitType: [{
                type: Output
            }], unitType: [{
                type: Input
            }], checkboxSelected: [{
                type: Input
            }] } });

class AirSelectTextComponent extends AirBaseComponent {
    constructor(store, selectTextValidationService, configService) {
        super(store, getPureAirSelectText, LoadAirItemAction$e, ResetStoreAction$e);
        this.store = store;
        this.selectTextValidationService = selectTextValidationService;
        this.configService = configService;
        this.promptLimit = QUESTION_TEXT_LIMIT;
        this.selectionTextLimit = SELECT_TEXT_SELECTION_TEXT_LIMIT;
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        this.pureAirSelectText$ = this.store.select(getPureAirSelectText);
        this.airSelectText$ = this.store.select(getAirSelectText);
        this.isAllowedToEdit = false;
        this.allowInlineValidation = false;
        this.allowSelectAndEdit = true;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    ngOnChanges() {
        this.allowSelectAndEdit = !(this.schema && this.schema.version === 1 && !this.schema.revision);
        setTimeout(() => {
            this.store.dispatch(new SaveSchemaVersionAction(this.schema));
        });
    }
    onUpdateSelectionTextHtml(selectionText) {
        this.store.dispatch(new UpdateSelectionTextHtmlAction(selectionText));
    }
    onToggleChoice(selectionUnitKey) {
        this.store.dispatch(new ToggleChoiceAction(selectionUnitKey));
    }
    onToggleAnswer(selectionUnit) {
        this.store.dispatch(new ToggleAnswerAction(selectionUnit));
    }
    onUpdateUnitType(unitType) {
        this.store.dispatch(new UpdateUnitTypeAction(unitType));
        this.airSelectText$.pipe(take(1)).subscribe((airSelectText) => {
            this.store.dispatch(new UpdateSelectionTextHtmlAction(airSelectText.selectionTextHtml));
        });
    }
    onUpdateSelectEditAnswer(payload) {
        this.store.dispatch(new UpdateSelectEditCorrectAnswerAction(payload));
    }
    onAddingAnswer(choiceKey) {
        this.store.dispatch(new AddCorrectAnswerAction(choiceKey));
    }
    onDeletingAnswer(payload) {
        this.store.dispatch(new DeleteCorrectAnswerAction(payload));
    }
    onSelectedCustomRange(payload) {
        this.store.dispatch(new SelectedCustomRangeAction(payload));
    }
    onEditorBlurSelectionTextHtml(content) {
        this.selectTextValidationService.validateSelectionText(content);
    }
    getSelectionTextErrors() {
        return this.allowInlineValidation && this.selectTextValidationService.getSelectionTextErrors();
    }
    getChoicesErrors() {
        const choiceErrors = this.allowInlineValidation ? this.selectTextValidationService.getChoicesErrors() : [];
        return choiceErrors;
    }
    getAnswersErrors() {
        const answerErrors = this.allowInlineValidation ? this.selectTextValidationService.getAnswersErrors() : [];
        return answerErrors;
    }
    validateOnUpdateChoices() {
        if (this.getChoicesErrors().length) {
            setTimeout(() => {
                this.selectTextValidationService.validateOnUpdateChoices();
            });
        }
    }
    validateOnUpdateAnswers() {
        if (this.getAnswersErrors().length) {
            setTimeout(() => {
                this.selectTextValidationService.validateOnUpdateAnswers();
            });
        }
    }
    onUpdateRationaleContent({ choice, content }) {
        this.store.dispatch(new UpdateRationaleContentAction({ choice, content }));
    }
    onToggleRationaleVisibility() {
        this.store.dispatch(new ToggleRationaleVisibilityAction());
    }
    changeAnswerSelection() {
        this.isAllowedToEdit = !this.isAllowedToEdit;
        this.store.dispatch(new AllowEditTextAction(this.isAllowedToEdit));
    }
    onAllowEditText(checkboxSelected) {
        this.isAllowedToEdit = checkboxSelected;
        this.store.dispatch(new AllowEditTextAction(checkboxSelected));
    }
    getCorrectAnswerText() {
        const correctAnswerText = this.isAllowedToEdit
            ? E_NO_CORRECT_ANSWER_CHOICE
            : E_NO_CORRECT_ANSWER_MULTIPLE_RESPONSE;
        return correctAnswerText;
    }
    onCorrectAnswerChange(payload) {
        this.store.dispatch(new IsChoiceCorrectAction(payload));
    }
}
AirSelectTextComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirSelectTextComponent, deps: [{ token: i1.Store }, { token: SelectTextValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirSelectTextComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirSelectTextComponent, selector: "aa-air-select-text", inputs: { schema: "schema" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n<div *ngIf=\"allowSelectAndEdit\" class=\"mb-4\">\n  <h2 class=\"answer-options\" translate>Answer options</h2>\n  <div class=\"checkbox-container\">\n    <div class=\"form-check\">\n      <input\n        type=\"checkbox\"\n        id=\"allow-edit-text\"\n        class=\"form-check-input\"\n        [checked]=\"(airSelectText$ | async)?.editSelectedText\"\n        (change)=\"changeAnswerSelection()\"\n      />\n\n      <label translate for=\"allow-edit-text\" class=\"form-check-label\">\n        Allow students to edit selected text\n      </label>\n    </div>\n  </div>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection unit type *</h2>\n\n  <aa-selection-unit-types\n    (updateUnitType)=\"onUpdateUnitType($event)\"\n    [unitType]=\"(airSelectText$ | async).unitType\"\n    [checkboxSelected]=\"(airSelectText$ | async)?.editSelectedText\"\n    (allowEditText)=\"onAllowEditText($event)\"\n  ></aa-selection-unit-types>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection text *</h2>\n\n  <mhe-editor\n    class=\"selection-text-editor\"\n    [maxCharacter]=\"selectionTextLimit\"\n    [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n    [useRichText]=\"false\"\n    [basicMode]=\"true\"\n    [content]=\"(airSelectText$ | async).selectionTextHtml\"\n    [errors]=\"getSelectionTextErrors()\"\n    [showResize]=\"false\"\n    (contentChange)=\"onUpdateSelectionTextHtml($event)\"\n    (editorFocusOut)=\"onEditorBlurSelectionTextHtml($event)\"\n  ></mhe-editor>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection choices *</h2>\n\n  <div class=\"item-instructions\" translate>\n    Select at least two choices to select correct answers.\n  </div>\n\n  <aa-selection-units\n    [selectionUnits]=\"(airSelectText$ | async).selectionUnits\"\n    [canToggleChoices]=\"true\"\n    [canToggleAnswers]=\"false\"\n    [isSelectionCustom]=\"(airSelectText$ | async).unitType === 'custom'\"\n    [canSelectCustomRange]=\"(airSelectText$ | async).unitType === 'custom'\"\n    (selectedCustomRange)=\"onSelectedCustomRange($event)\"\n    [errors]=\"getChoicesErrors()\"\n    (contentChange)=\"validateOnUpdateChoices()\"\n    (toggleChoice)=\"onToggleChoice($event)\"\n  ></aa-selection-units>\n</div>\n\n<div\n  *ngIf=\"\n    !(airSelectText$ | async)?.editSelectedText ||\n    !!(airSelectText$ | async)?.answers.length\n  \"\n  class=\"row align-items-start\"\n>\n  <div class=\"col-8\">\n    <h2 translate>Correct answers *</h2>\n    <div\n      *ngIf=\"(airSelectText$ | async)?.editSelectedText; else no_edit\"\n      class=\"correct-answer-instructions\"\n      translate\n    >\n      For each answer choice, enter the correct answer \u2014 or select the checkbox\n      if no correction is needed.\n    </div>\n  </div>\n\n  <ng-template #no_edit>\n    <div translate>{{ getCorrectAnswerText() }}</div>\n  </ng-template>\n\n  <div class=\"col-4 d-flex justify-content-end\">\n    <div class=\"toggle-switch\">\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"(airSelectText$ | async)?.showRationales\"\n          (change)=\"onToggleRationaleVisibility()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<ng-template #select_text_no_edit>\n  <aa-selection-units\n    [allowedEditSelectedText]=\"(airSelectText$ | async).editSelectedText\"\n    [selectionUnits]=\"(airSelectText$ | async)?.selectionUnits\"\n    [answers]=\"(airSelectText$ | async)?.answers\"\n    [canToggleChoices]=\"false\"\n    [canToggleAnswers]=\"true\"\n    [isSelectionCustom]=\"(airSelectText$ | async)?.unitType === 'custom'\"\n    [canSelectCustomRange]=\"false\"\n    [errors]=\"getAnswersErrors()\"\n    [showRationales]=\"(airSelectText$ | async)?.showRationales\"\n    (contentChange)=\"validateOnUpdateAnswers()\"\n    (toggleAnswer)=\"onToggleAnswer($event)\"\n    (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n    (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\"\n  >\n  </aa-selection-units>\n</ng-template>\n\n<aa-selection-correct-ans\n  *ngIf=\"(airSelectText$ | async)?.editSelectedText; else select_text_no_edit\"\n  [allowedEditSelectedText]=\"(airSelectText$ | async).editSelectedText\"\n  [selectionUnits]=\"(airSelectText$ | async)?.selectionUnits\"\n  [answers]=\"(airSelectText$ | async)?.answers\"\n  [errors]=\"getAnswersErrors()\"\n  [showRationales]=\"(airSelectText$ | async)?.showRationales\"\n  (updateSelectEditAnswer)=\"onUpdateSelectEditAnswer($event)\"\n  (addAnswer)=\"onAddingAnswer($event)\"\n  (contentChange)=\"validateOnUpdateAnswers()\"\n  (deleteAnswer)=\"onDeletingAnswer($event)\"\n  (correctAnswerChange)=\"onCorrectAnswerChange($event)\"\n  (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n></aa-selection-correct-ans>\n", styles: [".answer-options{display:inline-block;margin-top:35px}.correct-answer-instructions{font-size:14px}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: SelectionUnitsComponent, selector: "aa-selection-units", inputs: ["allowedEditSelectedText", "selectionUnits", "answers", "isSelectionCustom", "canSelectCustomRange", "canToggleChoices", "canToggleAnswers", "errors", "showRationales"], outputs: ["toggleChoice", "toggleAnswer", "selectedCustomRange", "updateRationaleContent"] }, { kind: "component", type: SelectTextEditComponent, selector: "aa-selection-correct-ans", inputs: ["allowedEditSelectedText", "answers", "errors", "selectionUnits", "showRationales"], outputs: ["updateSelectEditAnswer", "addAnswer", "deleteAnswer", "correctAnswerChange", "updateRationaleContent"] }, { kind: "component", type: SelectionUnitTypesComponent, selector: "aa-selection-unit-types", inputs: ["unitType", "checkboxSelected"], outputs: ["updateUnitType", "allowEditText", "selectUnitType"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirSelectTextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-select-text', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n<div *ngIf=\"allowSelectAndEdit\" class=\"mb-4\">\n  <h2 class=\"answer-options\" translate>Answer options</h2>\n  <div class=\"checkbox-container\">\n    <div class=\"form-check\">\n      <input\n        type=\"checkbox\"\n        id=\"allow-edit-text\"\n        class=\"form-check-input\"\n        [checked]=\"(airSelectText$ | async)?.editSelectedText\"\n        (change)=\"changeAnswerSelection()\"\n      />\n\n      <label translate for=\"allow-edit-text\" class=\"form-check-label\">\n        Allow students to edit selected text\n      </label>\n    </div>\n  </div>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection unit type *</h2>\n\n  <aa-selection-unit-types\n    (updateUnitType)=\"onUpdateUnitType($event)\"\n    [unitType]=\"(airSelectText$ | async).unitType\"\n    [checkboxSelected]=\"(airSelectText$ | async)?.editSelectedText\"\n    (allowEditText)=\"onAllowEditText($event)\"\n  ></aa-selection-unit-types>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection text *</h2>\n\n  <mhe-editor\n    class=\"selection-text-editor\"\n    [maxCharacter]=\"selectionTextLimit\"\n    [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n    [useRichText]=\"false\"\n    [basicMode]=\"true\"\n    [content]=\"(airSelectText$ | async).selectionTextHtml\"\n    [errors]=\"getSelectionTextErrors()\"\n    [showResize]=\"false\"\n    (contentChange)=\"onUpdateSelectionTextHtml($event)\"\n    (editorFocusOut)=\"onEditorBlurSelectionTextHtml($event)\"\n  ></mhe-editor>\n</div>\n\n<div class=\"mb-4\">\n  <h2 translate>Selection choices *</h2>\n\n  <div class=\"item-instructions\" translate>\n    Select at least two choices to select correct answers.\n  </div>\n\n  <aa-selection-units\n    [selectionUnits]=\"(airSelectText$ | async).selectionUnits\"\n    [canToggleChoices]=\"true\"\n    [canToggleAnswers]=\"false\"\n    [isSelectionCustom]=\"(airSelectText$ | async).unitType === 'custom'\"\n    [canSelectCustomRange]=\"(airSelectText$ | async).unitType === 'custom'\"\n    (selectedCustomRange)=\"onSelectedCustomRange($event)\"\n    [errors]=\"getChoicesErrors()\"\n    (contentChange)=\"validateOnUpdateChoices()\"\n    (toggleChoice)=\"onToggleChoice($event)\"\n  ></aa-selection-units>\n</div>\n\n<div\n  *ngIf=\"\n    !(airSelectText$ | async)?.editSelectedText ||\n    !!(airSelectText$ | async)?.answers.length\n  \"\n  class=\"row align-items-start\"\n>\n  <div class=\"col-8\">\n    <h2 translate>Correct answers *</h2>\n    <div\n      *ngIf=\"(airSelectText$ | async)?.editSelectedText; else no_edit\"\n      class=\"correct-answer-instructions\"\n      translate\n    >\n      For each answer choice, enter the correct answer \u2014 or select the checkbox\n      if no correction is needed.\n    </div>\n  </div>\n\n  <ng-template #no_edit>\n    <div translate>{{ getCorrectAnswerText() }}</div>\n  </ng-template>\n\n  <div class=\"col-4 d-flex justify-content-end\">\n    <div class=\"toggle-switch\">\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"(airSelectText$ | async)?.showRationales\"\n          (change)=\"onToggleRationaleVisibility()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<ng-template #select_text_no_edit>\n  <aa-selection-units\n    [allowedEditSelectedText]=\"(airSelectText$ | async).editSelectedText\"\n    [selectionUnits]=\"(airSelectText$ | async)?.selectionUnits\"\n    [answers]=\"(airSelectText$ | async)?.answers\"\n    [canToggleChoices]=\"false\"\n    [canToggleAnswers]=\"true\"\n    [isSelectionCustom]=\"(airSelectText$ | async)?.unitType === 'custom'\"\n    [canSelectCustomRange]=\"false\"\n    [errors]=\"getAnswersErrors()\"\n    [showRationales]=\"(airSelectText$ | async)?.showRationales\"\n    (contentChange)=\"validateOnUpdateAnswers()\"\n    (toggleAnswer)=\"onToggleAnswer($event)\"\n    (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n    (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\"\n  >\n  </aa-selection-units>\n</ng-template>\n\n<aa-selection-correct-ans\n  *ngIf=\"(airSelectText$ | async)?.editSelectedText; else select_text_no_edit\"\n  [allowedEditSelectedText]=\"(airSelectText$ | async).editSelectedText\"\n  [selectionUnits]=\"(airSelectText$ | async)?.selectionUnits\"\n  [answers]=\"(airSelectText$ | async)?.answers\"\n  [errors]=\"getAnswersErrors()\"\n  [showRationales]=\"(airSelectText$ | async)?.showRationales\"\n  (updateSelectEditAnswer)=\"onUpdateSelectEditAnswer($event)\"\n  (addAnswer)=\"onAddingAnswer($event)\"\n  (contentChange)=\"validateOnUpdateAnswers()\"\n  (deleteAnswer)=\"onDeletingAnswer($event)\"\n  (correctAnswerChange)=\"onCorrectAnswerChange($event)\"\n  (updateRationaleContent)=\"onUpdateRationaleContent($event)\"\n></aa-selection-correct-ans>\n", styles: [".answer-options{display:inline-block;margin-top:35px}.correct-answer-instructions{font-size:14px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: SelectTextValidationService }, { type: ConfigService }]; }, propDecorators: { schema: [{
                type: Input
            }] } });

class ShortAnswerValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.limitErrors = '';
        this.pureAirShortAnswer$ = this.store.select(getPureAirShortAnswer);
    }
    validate() {
        let isValid;
        this.pureAirShortAnswer$.pipe(take(1)).subscribe((airShortAnswer) => {
            const limit = airShortAnswer['maxCharacters'] || airShortAnswer['maxWords'];
            const promptError = !!this.promptValidationService.validatePrompt(airShortAnswer.prompt);
            const limitError = !!this.validateInputLimit(limit);
            isValid = !(promptError || limitError);
        });
        return isValid;
    }
    validateInputLimit(value) {
        const hasDecimal = value && value.toString().includes('.');
        if (hasDecimal) {
            this.limitErrors = E_DECIMAL_NOT_ALLOWED;
        }
        else {
            value = Number(value);
            this.limitErrors = value > RESPONSE_TEXT_LIMIT || value <= 0 || !value ? E_ESSAY_SA_LIMIT : '';
        }
        return this.limitErrors;
    }
    resetErrors() {
        this.resetLimitErrors();
        this.promptValidationService.resetErrors();
    }
    resetLimitErrors() {
        this.limitErrors = '';
    }
    getLimitErrors() {
        return this.limitErrors ? [this.limitErrors] : [];
    }
}
ShortAnswerValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ShortAnswerValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
ShortAnswerValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ShortAnswerValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ShortAnswerValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class AirShortAnswerComponent extends AirBaseComponent {
    constructor(store, shortAnswerValidationService, configService) {
        super(store, getPureAirShortAnswer, LoadAirItemAction$j, ResetStoreAction$j);
        this.store = store;
        this.shortAnswerValidationService = shortAnswerValidationService;
        this.configService = configService;
        this.airShortAnswer$ = this.store.select(getAirShortAnswer);
        this.pureAirShortAnswer$ = this.store.select(getPureAirShortAnswer);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    selectCharactersResponseLimit() {
        this.store.dispatch(new SelectCharactersResponseLimitAction());
    }
    selectWordsResponseLimit() {
        this.store.dispatch(new SelectWordsResponseLimitAction());
    }
    updateCharactersResponseLimit(limit) {
        this.store.dispatch(new UpdateCharactersResponseLimitAction(Number(limit)));
    }
    updateWordsResponseLimit(limit) {
        this.store.dispatch(new UpdateWordsResponseLimitAction(Number(limit)));
    }
    getLimitErrors() {
        return this.allowInlineValidation && this.shortAnswerValidationService.getLimitErrors();
    }
    onResetLimitErrors() {
        this.shortAnswerValidationService.resetLimitErrors();
    }
    onLimitInputFocusOut(value) {
        this.shortAnswerValidationService.validateInputLimit(value);
    }
}
AirShortAnswerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirShortAnswerComponent, deps: [{ token: i1.Store }, { token: ShortAnswerValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirShortAnswerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirShortAnswerComponent, selector: "aa-air-short-answer", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-item-response-text-limit (selectCharactersResponseLimit)=\"selectCharactersResponseLimit()\"\n                             (selectWordsResponseLimit)=\"selectWordsResponseLimit()\"\n                             (updateCharactersResponseLimit)=\"updateCharactersResponseLimit($event)\"\n                             (updateWordsResponseLimit)=\"updateWordsResponseLimit($event)\"\n                             (limitInputFocusOut)=\"onLimitInputFocusOut($event)\"\n                             (resetLimitErrors)=\"onResetLimitErrors()\"\n                             [maxCharacters]=\"(airShortAnswer$ | async)?.maxCharacters\"\n                             [maxWords]=\"(airShortAnswer$ | async)?.maxWords\"\n                             [responseLimitInCharacters]=\"(airShortAnswer$ | async)?.responseLimitInCharacters\"\n                             [responseLimitInWords]=\"(airShortAnswer$ | async)?.responseLimitInWords\"\n                             [limitErrors]=\"getLimitErrors()\">\n</aa-item-response-text-limit>\n", dependencies: [{ kind: "component", type: ItemResponseTextLimitComponent, selector: "aa-item-response-text-limit", inputs: ["maxCharacters", "maxWords", "responseLimitInWords", "responseLimitInCharacters", "limitErrors"], outputs: ["selectCharactersResponseLimit", "selectWordsResponseLimit", "updateCharactersResponseLimit", "updateWordsResponseLimit", "limitInputFocusOut", "resetLimitErrors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirShortAnswerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-short-answer', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-item-response-text-limit (selectCharactersResponseLimit)=\"selectCharactersResponseLimit()\"\n                             (selectWordsResponseLimit)=\"selectWordsResponseLimit()\"\n                             (updateCharactersResponseLimit)=\"updateCharactersResponseLimit($event)\"\n                             (updateWordsResponseLimit)=\"updateWordsResponseLimit($event)\"\n                             (limitInputFocusOut)=\"onLimitInputFocusOut($event)\"\n                             (resetLimitErrors)=\"onResetLimitErrors()\"\n                             [maxCharacters]=\"(airShortAnswer$ | async)?.maxCharacters\"\n                             [maxWords]=\"(airShortAnswer$ | async)?.maxWords\"\n                             [responseLimitInCharacters]=\"(airShortAnswer$ | async)?.responseLimitInCharacters\"\n                             [responseLimitInWords]=\"(airShortAnswer$ | async)?.responseLimitInWords\"\n                             [limitErrors]=\"getLimitErrors()\">\n</aa-item-response-text-limit>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: ShortAnswerValidationService }, { type: ConfigService }]; } });

class TrueFalseValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirTrueFalse$ = this.store.select(getPureAirTrueFalse);
        this.correctAnswerError = '';
    }
    validate() {
        let isValid;
        this.pureAirTrueFalse$.pipe(take(1)).subscribe((airTrueFalse) => {
            const hasPromptError = !!this.promptValidationService.validatePrompt(airTrueFalse.prompt);
            const hasCorrectAnswerError = !!this.validateCorrectAnswer(airTrueFalse.answer);
            isValid = !(hasPromptError || hasCorrectAnswerError);
        });
        return isValid;
    }
    validateCorrectAnswer(answer) {
        const error = answer ? '' : E_NO_CORRECT_ANSWER_SELECTED;
        this.correctAnswerError = error;
        return error;
    }
    resetErrors() {
        this.resetCorrectAnswerErrors();
        this.promptValidationService.resetErrors();
    }
    resetCorrectAnswerErrors() {
        this.correctAnswerError = '';
    }
    getCorrectAnswerErrors() {
        return this.correctAnswerError ? [this.correctAnswerError] : [];
    }
}
TrueFalseValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrueFalseValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
TrueFalseValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrueFalseValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TrueFalseValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class AnswerBooleanComponent {
    constructor(validationService, configService) {
        this.validationService = validationService;
        this.configService = configService;
        this.updateCorrectAnswer = new EventEmitter();
        this.updateTrueRationaleContent = new EventEmitter();
        this.updateFalseRationaleContent = new EventEmitter();
        this.toggleRationaleVisibility = new EventEmitter();
        this.rationaleLimit = ANSWER_CHOICE_LIMIT;
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onToggleRationaleVisibility() {
        this.toggleRationaleVisibility.emit();
    }
    isCorrectAnswer(choice) {
        return this.answer === choice;
    }
    onUpdateCorrectAnswer(value) {
        this.validationService.resetCorrectAnswerErrors();
        this.updateCorrectAnswer.emit(value);
    }
    getErrors() {
        if (this.allowInlineValidation) {
            return this.validationService.getCorrectAnswerErrors();
        }
        return [];
    }
}
AnswerBooleanComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AnswerBooleanComponent, deps: [{ token: TrueFalseValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AnswerBooleanComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AnswerBooleanComponent, selector: "aa-answer-boolean", inputs: { trueRationale: "trueRationale", falseRationale: "falseRationale", showRationales: "showRationales", answer: "answer" }, outputs: { updateCorrectAnswer: "updateCorrectAnswer", updateTrueRationaleContent: "updateTrueRationaleContent", updateFalseRationaleContent: "updateFalseRationaleContent", toggleRationaleVisibility: "toggleRationaleVisibility" }, ngImport: i0, template: "<div class=\"row align-items-center\">\n  <div class=\"col\">\n    <h2 translate class=\"answer-heading\">Answer choices *</h2>\n  </div>\n\n  <div class=\"col-auto\">\n    <div class=\"toggle-switch\">\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"showRationales\"\n          (change)=\"onToggleRationaleVisibility()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"answer-boolean-container\">\n  <div class=\"answer-boolean\" [class.has-error]=\"getErrors().length\">\n    <div class=\"row align-items-start\">\n      <div class=\"choices-wrapper col\">\n        <button\n          *ngIf=\"isCorrectAnswer('true')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to incorrect\"\n          (click)=\"onUpdateCorrectAnswer('true')\"\n        >\n          <span class=\"glyphicon glyphicon-ok\"></span>\n        </button>\n\n        <button\n          *ngIf=\"!isCorrectAnswer('true')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to correct\"\n          (click)=\"onUpdateCorrectAnswer('true')\"\n        >\n          <span class=\"glyphicon glyphicon-remove\"></span>\n        </button>\n\n        <label translate class=\"answer-selector boolean-label\">True</label>\n      </div>\n\n      <div\n        *ngIf=\"showRationales\"\n        class=\"rationale-wrapper col-12 col-md-9 col-xl-10\"\n      >\n        <span class=\"rationale-label\" translate>True reason</span><br />\n\n        <mhe-editor\n          [maxCharacter]=\"rationaleLimit\"\n          [noMedia]=\"true\"\n          [content]=\"trueRationale\"\n          [showResize]=\"false\"\n          (contentChange)=\"updateTrueRationaleContent.emit($event)\"\n        ></mhe-editor>\n      </div>\n    </div>\n\n    <hr *ngIf=\"showRationales\" />\n\n    <div class=\"row align-items-start\">\n      <div class=\"choices-wrapper col\">\n        <button\n          *ngIf=\"isCorrectAnswer('false')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to incorrect\"\n          (click)=\"onUpdateCorrectAnswer('false')\"\n        >\n          <span class=\"glyphicon glyphicon-ok\"></span>\n        </button>\n\n        <button\n          *ngIf=\"!isCorrectAnswer('false')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to correct\"\n          (click)=\"onUpdateCorrectAnswer('false')\"\n        >\n          <span class=\"glyphicon glyphicon-remove\"></span>\n        </button>\n\n        <label translate class=\"answer-selector boolean-label\">False</label>\n      </div>\n\n      <div\n        *ngIf=\"showRationales\"\n        class=\"rationale-wrapper col-12 col-md-9 col-xl-10\"\n      >\n        <span class=\"rationale-label\" translate>False reason</span><br />\n        <mhe-editor\n          [maxCharacter]=\"rationaleLimit\"\n          [noMedia]=\"true\"\n          [content]=\"falseRationale\"\n          [showResize]=\"false\"\n          (contentChange)=\"updateFalseRationaleContent.emit($event)\"\n        ></mhe-editor>\n      </div>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n</div>\n", styles: [".answer-boolean-container .answer-boolean{display:inline-block;width:100%;border-radius:2px;padding:1.7em;background:#fdfbfb;border:1px solid #a9a9a9}.answer-boolean-container .answer-boolean.has-error{border:1px solid #b0181e}.answer-boolean-container .answer-boolean>.row:not(:last-of-type){margin-bottom:1em}.answer-boolean-container .answer-boolean .choices-wrapper{display:flex;align-items:center}.answer-boolean-container .answer-boolean .rationale-wrapper{overflow:auto}.answer-boolean-container .answer-boolean .rationale-label{display:inline-block;padding-bottom:.8em;font-weight:700;font-size:.85rem}.answer-boolean-container .answer-boolean .answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px}.answer-boolean-container .answer-boolean .boolean-label{width:6em;font-weight:700;padding:5px;text-align:center}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AnswerBooleanComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-answer-boolean', template: "<div class=\"row align-items-center\">\n  <div class=\"col\">\n    <h2 translate class=\"answer-heading\">Answer choices *</h2>\n  </div>\n\n  <div class=\"col-auto\">\n    <div class=\"toggle-switch\">\n      <div class=\"form-check form-switch\">\n        <input\n          class=\"form-check-input\"\n          type=\"checkbox\"\n          id=\"reason-toggle-id\"\n          [checked]=\"showRationales\"\n          (change)=\"onToggleRationaleVisibility()\"\n        />\n        <label class=\"form-check-label\" for=\"reason-toggle-id\">Reason</label>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div class=\"answer-boolean-container\">\n  <div class=\"answer-boolean\" [class.has-error]=\"getErrors().length\">\n    <div class=\"row align-items-start\">\n      <div class=\"choices-wrapper col\">\n        <button\n          *ngIf=\"isCorrectAnswer('true')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to incorrect\"\n          (click)=\"onUpdateCorrectAnswer('true')\"\n        >\n          <span class=\"glyphicon glyphicon-ok\"></span>\n        </button>\n\n        <button\n          *ngIf=\"!isCorrectAnswer('true')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to correct\"\n          (click)=\"onUpdateCorrectAnswer('true')\"\n        >\n          <span class=\"glyphicon glyphicon-remove\"></span>\n        </button>\n\n        <label translate class=\"answer-selector boolean-label\">True</label>\n      </div>\n\n      <div\n        *ngIf=\"showRationales\"\n        class=\"rationale-wrapper col-12 col-md-9 col-xl-10\"\n      >\n        <span class=\"rationale-label\" translate>True reason</span><br />\n\n        <mhe-editor\n          [maxCharacter]=\"rationaleLimit\"\n          [noMedia]=\"true\"\n          [content]=\"trueRationale\"\n          [showResize]=\"false\"\n          (contentChange)=\"updateTrueRationaleContent.emit($event)\"\n        ></mhe-editor>\n      </div>\n    </div>\n\n    <hr *ngIf=\"showRationales\" />\n\n    <div class=\"row align-items-start\">\n      <div class=\"choices-wrapper col\">\n        <button\n          *ngIf=\"isCorrectAnswer('false')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to incorrect\"\n          (click)=\"onUpdateCorrectAnswer('false')\"\n        >\n          <span class=\"glyphicon glyphicon-ok\"></span>\n        </button>\n\n        <button\n          *ngIf=\"!isCorrectAnswer('false')\"\n          class=\"answer-selector\"\n          type=\"button\"\n          aria-label=\"Change to correct\"\n          (click)=\"onUpdateCorrectAnswer('false')\"\n        >\n          <span class=\"glyphicon glyphicon-remove\"></span>\n        </button>\n\n        <label translate class=\"answer-selector boolean-label\">False</label>\n      </div>\n\n      <div\n        *ngIf=\"showRationales\"\n        class=\"rationale-wrapper col-12 col-md-9 col-xl-10\"\n      >\n        <span class=\"rationale-label\" translate>False reason</span><br />\n        <mhe-editor\n          [maxCharacter]=\"rationaleLimit\"\n          [noMedia]=\"true\"\n          [content]=\"falseRationale\"\n          [showResize]=\"false\"\n          (contentChange)=\"updateFalseRationaleContent.emit($event)\"\n        ></mhe-editor>\n      </div>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n</div>\n", styles: [".answer-boolean-container .answer-boolean{display:inline-block;width:100%;border-radius:2px;padding:1.7em;background:#fdfbfb;border:1px solid #a9a9a9}.answer-boolean-container .answer-boolean.has-error{border:1px solid #b0181e}.answer-boolean-container .answer-boolean>.row:not(:last-of-type){margin-bottom:1em}.answer-boolean-container .answer-boolean .choices-wrapper{display:flex;align-items:center}.answer-boolean-container .answer-boolean .rationale-wrapper{overflow:auto}.answer-boolean-container .answer-boolean .rationale-label{display:inline-block;padding-bottom:.8em;font-weight:700;font-size:.85rem}.answer-boolean-container .answer-boolean .answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px}.answer-boolean-container .answer-boolean .boolean-label{width:6em;font-weight:700;padding:5px;text-align:center}\n"] }]
        }], ctorParameters: function () { return [{ type: TrueFalseValidationService }, { type: ConfigService }]; }, propDecorators: { trueRationale: [{
                type: Input
            }], falseRationale: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], answer: [{
                type: Input
            }], updateCorrectAnswer: [{
                type: Output
            }], updateTrueRationaleContent: [{
                type: Output
            }], updateFalseRationaleContent: [{
                type: Output
            }], toggleRationaleVisibility: [{
                type: Output
            }] } });

class AirTrueFalseComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirTrueFalse, LoadAirItemAction$l, ResetStoreAction$l);
        this.store = store;
        this.airTrueFalse$ = this.store.select(getAirTrueFalse);
        this.pureAirTrueFalse$ = this.store.select(getPureAirTrueFalse);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
    onUpdateCorrectAnswer(correctAnswer) {
        this.store.dispatch(new UpdateCorrectAnswerAction$2(correctAnswer));
    }
    onUpdateTrueRationaleContent(content) {
        this.store.dispatch(new UpdateTrueRationaleContentAction(content));
    }
    onUpdateFalseRationaleContent(content) {
        this.store.dispatch(new UpdateFalseRationaleContentAction(content));
    }
    onToggleRationaleVisibility() {
        this.store.dispatch(new ToggleRationaleVisibilityAction$1());
    }
}
AirTrueFalseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTrueFalseComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirTrueFalseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirTrueFalseComponent, selector: "aa-air-true-false", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-boolean [trueRationale]=\"(airTrueFalse$ | async)?.trueRationale\"\n                   [falseRationale]=\"(airTrueFalse$ | async)?.falseRationale\"\n                   [showRationales]=\"(airTrueFalse$ | async)?.showRationales\"\n                   [answer]=\"(airTrueFalse$ | async)?.answer\"\n                   (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n                   (updateTrueRationaleContent)=\"onUpdateTrueRationaleContent($event)\"\n                   (updateFalseRationaleContent)=\"onUpdateFalseRationaleContent($event)\"\n                   (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\">\n</aa-answer-boolean>\n", dependencies: [{ kind: "component", type: AnswerBooleanComponent, selector: "aa-answer-boolean", inputs: ["trueRationale", "falseRationale", "showRationales", "answer"], outputs: ["updateCorrectAnswer", "updateTrueRationaleContent", "updateFalseRationaleContent", "toggleRationaleVisibility"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTrueFalseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-true-false', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<aa-answer-boolean [trueRationale]=\"(airTrueFalse$ | async)?.trueRationale\"\n                   [falseRationale]=\"(airTrueFalse$ | async)?.falseRationale\"\n                   [showRationales]=\"(airTrueFalse$ | async)?.showRationales\"\n                   [answer]=\"(airTrueFalse$ | async)?.answer\"\n                   (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n                   (updateTrueRationaleContent)=\"onUpdateTrueRationaleContent($event)\"\n                   (updateFalseRationaleContent)=\"onUpdateFalseRationaleContent($event)\"\n                   (toggleRationaleVisibility)=\"onToggleRationaleVisibility()\">\n</aa-answer-boolean>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ItemAllowMediaComponent {
}
ItemAllowMediaComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemAllowMediaComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ItemAllowMediaComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemAllowMediaComponent, selector: "aa-item-allow-media", ngImport: i0, template: "<div class=\"allowMedia-checkbox\">\n  <h2 translate\n      class=\"allowMedia-heading\">Allow students to add media?</h2>\n\n  <input type=\"checkbox\"\n         id=\"allowMedia\">\n</div>\n", styles: [".allowMedia-checkbox{margin-top:10px}.allowMedia-checkbox .allowMedia-heading{margin-right:20px;padding-left:.5em;display:inline-block}.allowMedia-checkbox input[type=checkbox]{width:20px;height:20px;margin:0 10px;border:0;text-align:center;vertical-align:middle}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemAllowMediaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-allow-media', template: "<div class=\"allowMedia-checkbox\">\n  <h2 translate\n      class=\"allowMedia-heading\">Allow students to add media?</h2>\n\n  <input type=\"checkbox\"\n         id=\"allowMedia\">\n</div>\n", styles: [".allowMedia-checkbox{margin-top:10px}.allowMedia-checkbox .allowMedia-heading{margin-right:20px;padding-left:.5em;display:inline-block}.allowMedia-checkbox input[type=checkbox]{width:20px;height:20px;margin:0 10px;border:0;text-align:center;vertical-align:middle}\n"] }]
        }] });

const baseViewModes = [
    {
        value: 'itemPreview',
        viewValue: 'Student Preview',
    },
    {
        value: 'correctAnswer',
        viewValue: 'Correct Answer',
    },
    {
        value: 'itemAnswersPreview',
        viewValue: 'Scoresheet',
    },
    {
        value: 'itemPrintPreview',
        viewValue: 'Print',
    },
    {
        value: 'printScoring',
        viewValue: 'Print Student Answer',
    },
    {
        value: 'printCorrectAnswer',
        viewValue: 'Print Correct Answer',
    },
    {
        value: 'printStudentReview',
        viewValue: 'Print Scoresheet',
    },
];
const viewModes = [
    ...baseViewModes,
    {
        value: 'explanation',
        viewValue: 'Explanation',
    },
];
const algoViewModes = [...baseViewModes];
const passageViewModes = [
    {
        value: 'itemPreview',
        viewValue: 'Student Preview',
    },
    {
        value: 'printItem',
        viewValue: 'Print',
    },
];
const referenceSheetViewModes = [
    {
        value: 'itemPreview',
        viewValue: 'Student Preview',
    },
];

class ItemPreviewComponent {
    constructor(store) {
        this.store = store;
        this.selectedMode = this.dropdownViewModes[0];
        this.showPreview = false;
        this.showModes = false;
        this.showPreviewExplanation = false;
        this.showPreviewExampleResponse = false;
        this.showPreviewHint = false;
        this.showPreviewExample = false;
        this.enableSpinner = false;
        this.disableUploads = false;
        this.viewMode = this.selectedMode.value;
        this.store.select(getConfigState).subscribe((config) => {
            this.showModes = config['showModes'] || false;
            this.showPreview = config['showPreviewByDefault'] || false;
            this.enableSpinner = config.enableSpinner || false;
            this.disableUploads = config.disableUploads || false;
            this.apiConfig = config.apiConfig || null;
            this.wirisMathUrl = config['wirisMathUrl'];
        });
    }
    ngOnChanges() {
        this.showPreviewExplanation = false;
        this.showPreviewExampleResponse = false;
        this.showPreviewHint = false;
        this.showPreviewExample = false;
    }
    ngOnInit() {
        window['AAUI_ITEM_PREVIEW_DEBUG'] = window['AAUI_ITEM_PREVIEW_DEBUG'] || false;
    }
    get dropdownViewModes() {
        switch (this.itemType) {
            case 'passage':
                return passageViewModes;
            case 'reference_sheet':
                return referenceSheetViewModes;
            case 'external':
                return algoViewModes;
            default:
                return viewModes;
        }
    }
    debugMode() {
        return window['AAUI_ITEM_PREVIEW_DEBUG'];
    }
    canPreview() {
        return this.air && this.itemType;
    }
    toggleShowPreview() {
        this.showPreview = !this.showPreview;
    }
    onSelectMode(selectedModes) {
        this.selectedMode = selectedModes;
        this.viewMode = this.selectedMode.value;
        this.showPreviewExplanation = false;
        this.showPreviewExampleResponse = false;
        this.showPreviewHint = false;
        this.showPreviewExample = false;
    }
    onResponse(itemJson) {
        if (itemJson.metadata) {
            this.air.metadata = itemJson.metadata;
        }
        if (itemJson.renderData) {
            this.air.renderData = itemJson.renderData;
        }
        this.air.response = itemJson.response;
    }
    onCloseHint() {
        this.showPreviewHint = false;
    }
    onCloseExample() {
        this.showPreviewExample = false;
    }
    get hasExplanation() {
        return this.hasResource('explanation');
    }
    get hasHint() {
        return this.hasResource('hint');
    }
    get hasExample() {
        return this.hasResource('example');
    }
    hasResource(resourceType) {
        const resources = (this.air.resources || {})[resourceType];
        return !!(resources && resources.content);
    }
    get hasExampleResponse() {
        return this.hasResource('example_response');
    }
    toggleExplanation() {
        this.showPreviewExplanation = !this.showPreviewExplanation;
    }
    toggleExampleResponse() {
        this.showPreviewExampleResponse = !this.showPreviewExampleResponse;
    }
    toggleHint() {
        this.showPreviewHint = !this.showPreviewHint;
    }
    toggleExample() {
        this.showPreviewExample = !this.showPreviewExample;
    }
}
ItemPreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemPreviewComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
ItemPreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemPreviewComponent, selector: "aa-item-preview", inputs: { air: "air", itemType: "itemType", preserveAirState: "preserveAirState", viewMode: "viewMode", showRationales: "showRationales", viewExplanationButton: "viewExplanationButton", aleksConfig: "aleksConfig" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"item-preview-container\" *ngIf=\"canPreview()\">\n  <button class=\"btn btn-outline-primary\" type=\"button\" (click)=\"toggleShowPreview()\">\n    <span class=\"button-text-clickable\">\n      {{ showPreview ? \"Hide\" : \"Show\" }} Preview\n    </span>\n  </button>\n\n  <div\n    class=\"well preview-well p-3\"\n    [ngStyle]=\"{ display: showPreview ? 'block' : 'none' }\"\n  >\n    <div class=\"item-mode-wrapper\" *ngIf=\"showModes\">\n      <label class=\"item-mode fw-bold\" for=\"previewModeDropdown\">\n        Preview mode:\n      </label>\n      <select\n        id=\"previewModeDropdown\"\n        class=\"form-select d-inline w-auto\"\n        [ngModel]=\"selectedMode\"\n        (ngModelChange)=\"onSelectMode($event)\"\n      >\n        <option\n          *ngFor=\"let option of dropdownViewModes\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n      <button\n        *ngIf=\"\n          viewMode === 'itemAnswersPreview' &&\n          hasExplanation &&\n          !viewExplanationButton\n        \"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-explanation-button\"\n        (click)=\"toggleExplanation()\"\n      >\n        {{ showPreviewExplanation ? \"Hide\" : \"View\" }} Explanations\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemPreview' && hasHint\"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-hint-button\"\n        (click)=\"toggleHint()\"\n      >\n        {{ showPreviewHint ? \"Hide\" : \"View\" }} Hint\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemPreview' && hasExample\"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-example-button\"\n        (click)=\"toggleExample()\"\n      >\n        {{ showPreviewExample ? \"Hide\" : \"View\" }} Example\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemAnswersPreview' && hasExampleResponse\"\n        class=\"btn-outline-primary resource-toggle-btn example-response-link\"\n        type=\"button\"\n        data-automation-id=\"preview-explanation-button\"\n        (click)=\"toggleExampleResponse()\"\n      >\n        {{ showPreviewExampleResponse ? \"Hide\" : \"View\" }} Example Response\n      </button>\n    </div>\n\n    <aa-air-item\n      *ngIf=\"showPreview\"\n      [air]=\"air\"\n      [aleksConfig]=\"aleksConfig\"\n      [apiConfig]=\"apiConfig\"\n      [preserveAirState]=\"preserveAirState\"\n      [enableSpinner]=\"enableSpinner\"\n      [disableUploads]=\"disableUploads\"\n      [itemType]=\"itemType\"\n      [viewMode]=\"viewMode\"\n      [debugMode]=\"debugMode()\"\n      [showExplanation]=\"showPreviewExplanation\"\n      [showExampleResponse]=\"showPreviewExampleResponse\"\n      [viewExplanationButton]=\"viewExplanationButton\"\n      [showHint]=\"showPreviewHint\"\n      [showExample]=\"showPreviewExample\"\n      [showRationales]=\"showRationales\"\n      [wirisMathUrl]=\"wirisMathUrl\"\n      (response)=\"onResponse($event)\"\n      (closeHint)=\"onCloseHint()\"\n      (closeExample)=\"onCloseExample()\"\n    ></aa-air-item>\n  </div>\n</div>\n", styles: [".item-preview-container{margin-bottom:16px;width:100%}.item-mode-wrapper{margin-bottom:20px}.item-mode{margin-right:10px;font-size:18px}.preview-well{border:1px solid #a9a9a9;background:none;margin-top:16px;min-height:310px}aa-air-item{display:block}\n"], dependencies: [{ kind: "component", type: i2$1.AirItemComponent, selector: "aa-air-item", inputs: ["air", "itemType", "preserveAirState", "airVersion", "viewMode", "debugMode", "transform", "showRationales", "viewExplanationButton", "showExampleResponse", "showExplanation", "showHint", "showExample", "scrollParent", "aleksConfig", "apiConfig", "enableSpinner", "disableUploads", "wirisMathUrl"], outputs: ["response", "closeHint", "closeExample"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemPreviewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-preview', template: "<div class=\"item-preview-container\" *ngIf=\"canPreview()\">\n  <button class=\"btn btn-outline-primary\" type=\"button\" (click)=\"toggleShowPreview()\">\n    <span class=\"button-text-clickable\">\n      {{ showPreview ? \"Hide\" : \"Show\" }} Preview\n    </span>\n  </button>\n\n  <div\n    class=\"well preview-well p-3\"\n    [ngStyle]=\"{ display: showPreview ? 'block' : 'none' }\"\n  >\n    <div class=\"item-mode-wrapper\" *ngIf=\"showModes\">\n      <label class=\"item-mode fw-bold\" for=\"previewModeDropdown\">\n        Preview mode:\n      </label>\n      <select\n        id=\"previewModeDropdown\"\n        class=\"form-select d-inline w-auto\"\n        [ngModel]=\"selectedMode\"\n        (ngModelChange)=\"onSelectMode($event)\"\n      >\n        <option\n          *ngFor=\"let option of dropdownViewModes\"\n          [ngValue]=\"option\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n\n      <button\n        *ngIf=\"\n          viewMode === 'itemAnswersPreview' &&\n          hasExplanation &&\n          !viewExplanationButton\n        \"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-explanation-button\"\n        (click)=\"toggleExplanation()\"\n      >\n        {{ showPreviewExplanation ? \"Hide\" : \"View\" }} Explanations\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemPreview' && hasHint\"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-hint-button\"\n        (click)=\"toggleHint()\"\n      >\n        {{ showPreviewHint ? \"Hide\" : \"View\" }} Hint\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemPreview' && hasExample\"\n        class=\"btn-outline-primary resource-toggle-btn\"\n        type=\"button\"\n        data-automation-id=\"preview-example-button\"\n        (click)=\"toggleExample()\"\n      >\n        {{ showPreviewExample ? \"Hide\" : \"View\" }} Example\n      </button>\n      <button\n        *ngIf=\"viewMode === 'itemAnswersPreview' && hasExampleResponse\"\n        class=\"btn-outline-primary resource-toggle-btn example-response-link\"\n        type=\"button\"\n        data-automation-id=\"preview-explanation-button\"\n        (click)=\"toggleExampleResponse()\"\n      >\n        {{ showPreviewExampleResponse ? \"Hide\" : \"View\" }} Example Response\n      </button>\n    </div>\n\n    <aa-air-item\n      *ngIf=\"showPreview\"\n      [air]=\"air\"\n      [aleksConfig]=\"aleksConfig\"\n      [apiConfig]=\"apiConfig\"\n      [preserveAirState]=\"preserveAirState\"\n      [enableSpinner]=\"enableSpinner\"\n      [disableUploads]=\"disableUploads\"\n      [itemType]=\"itemType\"\n      [viewMode]=\"viewMode\"\n      [debugMode]=\"debugMode()\"\n      [showExplanation]=\"showPreviewExplanation\"\n      [showExampleResponse]=\"showPreviewExampleResponse\"\n      [viewExplanationButton]=\"viewExplanationButton\"\n      [showHint]=\"showPreviewHint\"\n      [showExample]=\"showPreviewExample\"\n      [showRationales]=\"showRationales\"\n      [wirisMathUrl]=\"wirisMathUrl\"\n      (response)=\"onResponse($event)\"\n      (closeHint)=\"onCloseHint()\"\n      (closeExample)=\"onCloseExample()\"\n    ></aa-air-item>\n  </div>\n</div>\n", styles: [".item-preview-container{margin-bottom:16px;width:100%}.item-mode-wrapper{margin-bottom:20px}.item-mode{margin-right:10px;font-size:18px}.preview-well{border:1px solid #a9a9a9;background:none;margin-top:16px;min-height:310px}aa-air-item{display:block}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; }, propDecorators: { air: [{
                type: Input
            }], itemType: [{
                type: Input
            }], preserveAirState: [{
                type: Input
            }], viewMode: [{
                type: Input
            }], showRationales: [{
                type: Input
            }], viewExplanationButton: [{
                type: Input
            }], aleksConfig: [{
                type: Input
            }] } });

class AirInstructionItemComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirInstructionItem, LoadAirItemAction$9, ResetStoreAction$9);
        this.store = store;
        this.airInstruction$ = this.store.select(getAirInstructionItem);
        this.pureAirInstruction$ = this.store.select(getPureAirInstructionItem);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
}
AirInstructionItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirInstructionItemComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirInstructionItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirInstructionItemComponent, selector: "aa-air-instruction-item", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirInstructionItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-instruction-item', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class GridInChoicesComponent {
    constructor(store) {
        this.store = store;
        this.optionTypes = [
            {
                value: 'numeric',
                viewValue: 'Numeric',
            },
            {
                value: 'decimal',
                viewValue: 'Decimal',
            },
            {
                value: 'alpha',
                viewValue: 'Alpha',
            },
        ];
        this.selectedOptionType = this.optionTypes[0];
        this.currentOptionType = this.optionTypes[0];
        this.answerChoiceContainer = {
            numeric: this.convertToChoiceOptions('0123456789', 'n'),
            decimal: this.convertToChoiceOptions('.0123456789', 'd'),
            alpha: this.convertToChoiceOptions('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 'a'),
        };
        this.openModal = false;
    }
    ngOnChanges(changes) {
        if (changes['optionType']?.currentValue) {
            this.selectedOptionType = this.currentOptionType = this.optionTypes.filter((option) => option.value === changes['optionType']?.currentValue)[0];
        }
    }
    onOptionTypeChange(selected) {
        if (selected.value !== this.optionType) {
            this.selectedOptionType = selected;
            this.openModal = true;
        }
    }
    confirmOptionTypeChange() {
        this.store.dispatch(new UpdateOptionTypeAction(this.selectedOptionType.value));
        this.openModal = false;
    }
    cancelOptionTypeChange() {
        this.selectedOptionType = this.currentOptionType;
        this.openModal = false;
    }
    getChoiceOptions() {
        return this.answerChoiceContainer[this.optionType];
    }
    convertToChoiceOptions(data, optionType) {
        const options = data.split('');
        return options.map((e) => {
            return {
                value: optionType === 'a' ? e.toLowerCase() : e,
                viewValue: e,
            };
        });
    }
    onAnswerChange(selected, idx) {
        this.store.dispatch(new UpdateAnswerAction({
            key: `response${idx}`,
            updatedValue: selected,
        }));
    }
}
GridInChoicesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GridInChoicesComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
GridInChoicesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: GridInChoicesComponent, selector: "aa-grid-in-choices", inputs: { optionType: "optionType", answers: "answers" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"choice-wrapper row\">\n  <h2 translate class=\"col-3\">Grid type*</h2>\n  <div class=\"col-9\">\n    <select class=\"form-select d-inline w-auto\"\n      [ngModel]=\"selectedOptionType\"\n      (ngModelChange)=\"onOptionTypeChange($event)\">\n      <option\n        *ngFor=\"let option of optionTypes\"\n        [ngValue]=\"option\">\n        {{ option.viewValue }}\n      </option>\n    </select>\n  </div>\n</div>\n\n<div class=\"choice-wrapper row\">\n  <h2 translate *ngIf=\"answers?.length\" class=\"col-3\">Correct answer*</h2>\n  <div class=\"col-9\">\n    <span class=\"grid-choice\" *ngFor=\"let answer of answers; let idx = index\">\n      <select class=\"form-select d-inline w-auto\"\n        (change)=\"onAnswerChange($event.target['value'], idx)\">\n        <option\n          *ngFor=\"let option of getChoiceOptions()\"\n          [value]=\"option.value\"\n          [selected]=\"option.value === answer.value\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n    </span>\n  </div>\n</div>\n\n<air-modal\n  [isOpen]=\"openModal\"\n  [confirmButtonText]=\"'Proceed'\"\n  (cancel)=\"cancelOptionTypeChange()\"\n  (close)=\"cancelOptionTypeChange()\"\n  (confirm)=\"confirmOptionTypeChange()\"\n>\n  <div translate>Changing grid type will clear the correct answer.</div>\n</air-modal>\n", styles: [".choice-wrapper{padding-top:15px}.choice-wrapper h2{margin:10px 0}.grid-choice{display:inline-block;height:45px;padding-right:20px}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GridInChoicesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-grid-in-choices', template: "<div class=\"choice-wrapper row\">\n  <h2 translate class=\"col-3\">Grid type*</h2>\n  <div class=\"col-9\">\n    <select class=\"form-select d-inline w-auto\"\n      [ngModel]=\"selectedOptionType\"\n      (ngModelChange)=\"onOptionTypeChange($event)\">\n      <option\n        *ngFor=\"let option of optionTypes\"\n        [ngValue]=\"option\">\n        {{ option.viewValue }}\n      </option>\n    </select>\n  </div>\n</div>\n\n<div class=\"choice-wrapper row\">\n  <h2 translate *ngIf=\"answers?.length\" class=\"col-3\">Correct answer*</h2>\n  <div class=\"col-9\">\n    <span class=\"grid-choice\" *ngFor=\"let answer of answers; let idx = index\">\n      <select class=\"form-select d-inline w-auto\"\n        (change)=\"onAnswerChange($event.target['value'], idx)\">\n        <option\n          *ngFor=\"let option of getChoiceOptions()\"\n          [value]=\"option.value\"\n          [selected]=\"option.value === answer.value\">\n          {{ option.viewValue }}\n        </option>\n      </select>\n    </span>\n  </div>\n</div>\n\n<air-modal\n  [isOpen]=\"openModal\"\n  [confirmButtonText]=\"'Proceed'\"\n  (cancel)=\"cancelOptionTypeChange()\"\n  (close)=\"cancelOptionTypeChange()\"\n  (confirm)=\"confirmOptionTypeChange()\"\n>\n  <div translate>Changing grid type will clear the correct answer.</div>\n</air-modal>\n", styles: [".choice-wrapper{padding-top:15px}.choice-wrapper h2{margin:10px 0}.grid-choice{display:inline-block;height:45px;padding-right:20px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; }, propDecorators: { optionType: [{
                type: Input
            }], answers: [{
                type: Input
            }] } });

class AirGridInComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirGridIn, LoadAirItemAction$8, ResetStoreAction$8);
        this.store = store;
        this.airGridIn$ = this.store.select(getAirGridIn);
        this.pureAirGridIn$ = this.store.select(getPureAirGridIn);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
    }
}
AirGridInComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirGridInComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirGridInComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirGridInComponent, selector: "aa-air-grid-in", usesInheritance: true, ngImport: i0, template: "<aa-grid-in-choices\n  [optionType]=\"(airGridIn$ | async).optionType\"\n  [answers]=\"(airGridIn$ | async).answers\"\n></aa-grid-in-choices>\n", dependencies: [{ kind: "component", type: GridInChoicesComponent, selector: "aa-grid-in-choices", inputs: ["optionType", "answers"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirGridInComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-grid-in', template: "<aa-grid-in-choices\n  [optionType]=\"(airGridIn$ | async).optionType\"\n  [answers]=\"(airGridIn$ | async).answers\"\n></aa-grid-in-choices>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class AudioValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirAudio$ = this.store.select(getPureAirAudioRecording);
        this.audioError = '';
    }
    validate() {
        let isValid;
        this.pureAirAudio$.pipe(take(1)).subscribe((airAudio) => {
            const promptError = !!this.promptValidationService.validatePrompt(airAudio.prompt);
            const audioError = !!this.validateAudioLength(airAudio.maximumLength);
            isValid = !(promptError || audioError);
        });
        return isValid;
    }
    validateAudioLength(value) {
        this.audioError = '';
        const numericVal = parseInt(value, 10);
        if (isNaN(numericVal)) {
            this.audioError = E_AUDIO_VALID_LENGTH;
        }
        else if (value.toString().includes('.')) {
            this.audioError = E_DECIMAL_NOT_ALLOWED;
        }
        else if (numericVal < 5) {
            this.audioError = E_TOO_LOW_AUDIO_SPAN;
        }
        else if (numericVal > 120) {
            this.audioError = E_TOO_LARGE_AUDIO_SPAN;
        }
        return this.audioError;
    }
    resetErrors() {
        this.audioError = '';
        this.promptValidationService.resetErrors();
    }
    getAudioErrors() {
        return this.audioError ? [this.audioError] : [];
    }
}
AudioValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AudioValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
AudioValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AudioValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AudioValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class AirAudioRecordingComponent extends AirBaseComponent {
    constructor(store, audioValidationService, configService) {
        super(store, getPureAirAudioRecording, LoadAirItemAction$7, ResetStoreAction$7);
        this.store = store;
        this.audioValidationService = audioValidationService;
        this.configService = configService;
        this.allowInlineValidation = false;
        this.airAudioRecording$ = this.store.select(getAirAudioRecording);
        this.pureAirAudioRecording$ = this.store.select(getPureAirAudioRecording);
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onAudioSpanChange(value) {
        this.store.dispatch(new UpdateAudioSpanAction(value));
    }
    onFocusOut(value) {
        this.audioValidationService.validateAudioLength(value);
    }
    getErrors() {
        return this.allowInlineValidation && this.audioValidationService.getAudioErrors();
    }
}
AirAudioRecordingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAudioRecordingComponent, deps: [{ token: i1.Store }, { token: AudioValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirAudioRecordingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirAudioRecordingComponent, selector: "aa-air-audio-recording", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<h2 translate>Maximum length*</h2>\n\n<div class=\"length_instruction form-text\" translate>\n  Enter a value between 5 and 120.\n</div>\n\n<div class=\"row\">\n  <div class=\"col-sm-6 col-md-4 col-lg-3\">\n    <div class=\"audio_container input-group has-validation\">\n      <input\n        #inptSeconds\n        type=\"number\"\n        class=\"form-control\"\n        [value]=\"(airAudioRecording$ | async)?.maximumLength\"\n        [class.is-invalid]=\"!!getErrors()?.length\"\n        (change)=\"onAudioSpanChange(inptSeconds.value)\"\n        (blur)=\"onFocusOut(inptSeconds.value)\"\n      />\n\n      <span class=\"seconds input-group-text\" translate>Seconds</span>\n      <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n    </div>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAudioRecordingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-audio-recording', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<h2 translate>Maximum length*</h2>\n\n<div class=\"length_instruction form-text\" translate>\n  Enter a value between 5 and 120.\n</div>\n\n<div class=\"row\">\n  <div class=\"col-sm-6 col-md-4 col-lg-3\">\n    <div class=\"audio_container input-group has-validation\">\n      <input\n        #inptSeconds\n        type=\"number\"\n        class=\"form-control\"\n        [value]=\"(airAudioRecording$ | async)?.maximumLength\"\n        [class.is-invalid]=\"!!getErrors()?.length\"\n        (change)=\"onAudioSpanChange(inptSeconds.value)\"\n        (blur)=\"onFocusOut(inptSeconds.value)\"\n      />\n\n      <span class=\"seconds input-group-text\" translate>Seconds</span>\n      <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: AudioValidationService }, { type: ConfigService }]; } });

class NumberLineValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirNumberLine$ = this.store.select(getPureAirNumberLine);
        this.correctAnswerError = '';
        this.minError = '';
        this.maxError = '';
        this.minMaxErrorForMin = '';
        this.minMaxErrorForMax = '';
        this.tickIntervalError = '';
        this.labelIntervalError = '';
        this.tickRangeError = '';
    }
    validate() {
        let isValid;
        this.pureAirNumberLine$.pipe(take(1)).subscribe((airNumberLine) => {
            const hasPromptError = !!this.promptValidationService.validatePrompt(airNumberLine.prompt);
            const hasCorrectAnswerError = !!this.validateCorrectAnswer(airNumberLine.answers);
            isValid = !(hasPromptError || hasCorrectAnswerError || this.hasInputErrors());
        });
        return isValid;
    }
    hasInputErrors() {
        let inputErrors = false;
        const nLIdentifier = document.getElementsByTagName('aa-air-number-line')[0];
        const errorBlocks = nLIdentifier.getElementsByClassName('help-block');
        for (const key of Object.keys(errorBlocks)) {
            if (errorBlocks[key].innerText) {
                inputErrors = true;
            }
        }
        return inputErrors;
    }
    validateInputs(context, field) {
        const hasMinError = !!this.validateMin(context.minVal);
        const hasMaxError = !!this.validateMax(context.maxVal);
        let hasMinMaxError = false;
        const hasTickError = !!this.validateTickInterval(context.tickVal);
        const hasLabelError = !!this.validateLabelInterval(context.labelVal);
        let hasTickIntRangeError = false;
        if (!(hasMinError || hasMaxError)) {
            hasMinMaxError = !!this.validateMinMax(context, field);
        }
        if (!(hasMinError || hasMaxError || hasMinMaxError || hasTickError || hasLabelError)) {
            hasTickIntRangeError = !!this.validateTickIntervalRange(context);
        }
        const isValid = !(hasMinError ||
            hasMaxError ||
            hasMinMaxError ||
            hasTickError ||
            hasLabelError ||
            hasTickIntRangeError);
        return isValid;
    }
    validateCorrectAnswer(answers) {
        const error = answers.length ? '' : E_NO_POINT_PLOTTED;
        this.correctAnswerError = error;
        return error;
    }
    validateMin(minVal) {
        const error = !this.isBlank(this.getValue(minVal)) ? '' : E_MIN_VALUE_BLANK;
        this.minError = error;
        return error;
    }
    validateMinMax(context, field) {
        let error = '';
        if (fraction(this.getValue(context.maxVal)) <= fraction(this.getValue(context.minVal))) {
            if (field === 'min') {
                error = E_MIN_VALUE_INVALID;
                this.minMaxErrorForMin = error;
            }
            if (field === 'max') {
                error = E_MAX_VALUE_INVALID;
                this.minMaxErrorForMax = error;
            }
        }
        return error;
    }
    validateMax(maxVal) {
        const error = !this.isBlank(this.getValue(maxVal)) ? '' : E_MAX_VALUE_BLANK;
        this.maxError = error;
        return error;
    }
    validateTickInterval(tickVal) {
        let error = '';
        if (this.isBlank(this.getValue(tickVal))) {
            error = E_TICK_INT_BLANK;
        }
        else {
            const value = fraction(this.getValue(tickVal));
            if (value.n <= 0) {
                error = E_TICK_INT_VALUE;
            }
        }
        this.tickIntervalError = error;
        return error;
    }
    validateLabelInterval(labelVal) {
        let error = '';
        if (this.isBlank(this.getValue(labelVal))) {
            error = E_LABEL_INT_BLANK;
        }
        else {
            const value = fraction(this.getValue(labelVal));
            if (value.n <= 0) {
                error = E_LABEL_INT_VALUE;
            }
        }
        this.labelIntervalError = error;
        return error;
    }
    validateTickIntervalRange(context) {
        let error = '';
        const maxValue = fraction(this.getValue(context.maxVal));
        const minValue = fraction(this.getValue(context.minVal));
        const tickValue = fraction(this.getValue(context.tickVal));
        const intRange = abs(subtract(maxValue, minValue));
        if (divide(intRange, tickValue) > 40) {
            error = E_TICK_RANGE_MAX;
            this.tickRangeError = error;
        }
        return error;
    }
    resetErrors() {
        this.resetCorrectAnswerError();
        this.promptValidationService.resetErrors();
        this.resetMinMaxErrors();
        this.resetTickIntError();
        this.resetLabelIntError();
        this.resetTickRangeError();
    }
    resetCorrectAnswerError() {
        this.correctAnswerError = '';
    }
    resetMinMaxErrors() {
        this.minMaxErrorForMin = '';
        this.minMaxErrorForMax = '';
    }
    resetTickIntError() {
        this.tickIntervalError = '';
    }
    resetLabelIntError() {
        this.labelIntervalError = '';
    }
    resetTickRangeError() {
        this.tickRangeError = '';
    }
    getCorrectAnswerErrors() {
        return this.correctAnswerError ? [this.correctAnswerError] : [];
    }
    getMinError() {
        const errors = this.minError ? [this.minError] : [];
        if (this.minMaxErrorForMin) {
            errors.push(this.minMaxErrorForMin);
        }
        return errors;
    }
    getMaxError() {
        const errors = this.maxError ? [this.maxError] : [];
        if (this.minMaxErrorForMax) {
            errors.push(this.minMaxErrorForMax);
        }
        return errors;
    }
    getTickIntervalError() {
        return this.tickIntervalError ? [this.tickIntervalError] : [];
    }
    getLabelIntervalError() {
        return this.labelIntervalError ? [this.labelIntervalError] : [];
    }
    getTickRangeError() {
        return this.tickRangeError ? [this.tickRangeError] : [];
    }
    isBlank(value) {
        return value === '';
    }
    getValue(prop) {
        return prop.nativeElement.value;
    }
    toFixed(value) {
        return value.toString().replace(/(\.\d\d)\d+|([\d.]*)[^\d.]/, '$1$2');
    }
}
NumberLineValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NumberLineValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
NumberLineValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NumberLineValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NumberLineValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class ConfigurePointsComponent {
    constructor() {
        this.updatePlot = new EventEmitter();
        this.plotOptions = [
            { value: 'open', viewValue: 'Open Plot' },
            { value: 'closed', viewValue: 'Closed Plot' },
        ];
        this.selectedPlot = [];
    }
    checkValue(value) {
        return !value.endsWith('/') && !value.startsWith('/') && !value.endsWith('.') && !value.endsWith('/0');
    }
    onPlotChange(pointType) {
        this.updatePlot.emit(pointType);
    }
}
ConfigurePointsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ConfigurePointsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ConfigurePointsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ConfigurePointsComponent, selector: "aa-configure-points", inputs: { selectedAnswer: "selectedAnswer", allowSnapToTick: "allowSnapToTick" }, outputs: { updatePlot: "updatePlot" }, viewQueries: [{ propertyName: "mathEditor", first: true, predicate: ["mathEditor"], descendants: true }], ngImport: i0, template: "<div class=\"plot-radio-group\">\n  <fieldset>\n    <div class=\"form-check me-3\">\n      <input\n        id=\"closedPoint\" type=\"radio\" class=\"form-check-input\"\n        name=\"pointType\"\n        [checked]=\"selectedAnswer?.pointType === 'closed'\"\n        (change)=\"onPlotChange('closed')\"\n      />\n      <label class=\"form-check-label\" for=\"closedPoint\">Closed Point</label>\n    </div>\n    <div class=\"form-check\">\n      <input\n        id=\"allowPointBetweenTickMarks\" type=\"radio\" class=\"form-check-input\"\n        name=\"pointType\"\n        [checked]=\"selectedAnswer?.pointType === 'open'\"\n        (change)=\"onPlotChange('open')\"\n      />\n      <label class=\"form-check-label\" for=\"allowPointBetweenTickMarks\">Open Point</label>\n    </div>\n  </fieldset>\n\n</div>\n\n", styles: [".math-editor{display:block;width:98%}.tolerance-params{margin-top:.5em;margin-right:4px;font-weight:700}.tolerance-params.sign{margin-left:1em}.tolerance-params.percent{margin-left:4px}.tolerance mhe-dropdown{display:inline-flex}.tolerance .tolerance-input{display:inline-flex}.tolerance .tolerance-input mhe-input{width:70px}.error-class{color:#b0181e}:host ::ng-deep label.ahe-ui-radio{padding:8px 8px 8px 36px}:host ::ng-deep label.ahe-ui-radio input[type=radio]{margin-left:-36px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ConfigurePointsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-configure-points', template: "<div class=\"plot-radio-group\">\n  <fieldset>\n    <div class=\"form-check me-3\">\n      <input\n        id=\"closedPoint\" type=\"radio\" class=\"form-check-input\"\n        name=\"pointType\"\n        [checked]=\"selectedAnswer?.pointType === 'closed'\"\n        (change)=\"onPlotChange('closed')\"\n      />\n      <label class=\"form-check-label\" for=\"closedPoint\">Closed Point</label>\n    </div>\n    <div class=\"form-check\">\n      <input\n        id=\"allowPointBetweenTickMarks\" type=\"radio\" class=\"form-check-input\"\n        name=\"pointType\"\n        [checked]=\"selectedAnswer?.pointType === 'open'\"\n        (change)=\"onPlotChange('open')\"\n      />\n      <label class=\"form-check-label\" for=\"allowPointBetweenTickMarks\">Open Point</label>\n    </div>\n  </fieldset>\n\n</div>\n\n", styles: [".math-editor{display:block;width:98%}.tolerance-params{margin-top:.5em;margin-right:4px;font-weight:700}.tolerance-params.sign{margin-left:1em}.tolerance-params.percent{margin-left:4px}.tolerance mhe-dropdown{display:inline-flex}.tolerance .tolerance-input{display:inline-flex}.tolerance .tolerance-input mhe-input{width:70px}.error-class{color:#b0181e}:host ::ng-deep label.ahe-ui-radio{padding:8px 8px 8px 36px}:host ::ng-deep label.ahe-ui-radio input[type=radio]{margin-left:-36px}\n"] }]
        }], propDecorators: { selectedAnswer: [{
                type: Input
            }], allowSnapToTick: [{
                type: Input
            }], updatePlot: [{
                type: Output
            }], mathEditor: [{
                type: ViewChild,
                args: ['mathEditor']
            }] } });

class LineGraphComponent {
    constructor(store, element) {
        this.store = store;
        this.element = element;
        this.minTicks = -5;
        this.maxTicks = 5;
        this.tickInterval = 1;
        this.labelInterval = 1;
        this.showGraphAs = graphType.DECIMALS;
        this.showResponseModeAs = responseType.POINT;
        this.divisionsBetweenTicks = 0;
        this.allowSnapToTick = true;
        this.showMathModal = false;
        this.updateCorrectAnswer = new EventEmitter();
        this.updateAnswerLabel = new EventEmitter();
        this.updateInternalTicks = new EventEmitter();
        this.updateToleranceRange = new EventEmitter();
        this.updateAnswerSegment = new EventEmitter();
        this.updatePlot = new EventEmitter();
        this.openOrShowMathModal = new EventEmitter();
        this.internalTicks = 0;
        this.visibleTicks = 0;
        this.sizePercentage = 0.65;
        this.lineGraphConfig = {
            height: 200 * this.sizePercentage,
            lineX1: 30,
            lineX2: 520,
            lineY: 100 * this.sizePercentage,
            labelY: 100 * this.sizePercentage - 30,
            editLabelY: 100 * this.sizePercentage - 30 + 'px',
            pointRadius: 4,
            squareHeight: 26,
            squareWidth: 10,
            tickHeight: 14,
            width: 570,
        };
        this.xTickDifference = 0;
        this.xLabelDifference = 0;
        this.hasFraction = false;
        this.authorLabelModal = false;
        this.authoredLabel = null;
        this.rangeArray = [];
        this.placementBottom = 'bottom';
        this.labelDecimalPlace = 2;
        this.valueDecimalPlace = 5;
        this.airConfig$ = this.store.select(getConfigState);
    }
    ngOnChanges(changes) {
        if (changes.answers) {
            this.rangeArray = this.answers.map((answer) => answer.toleranceRange).filter((tolerance) => tolerance);
        }
    }
    ngAfterViewInit() {
        this.closeButtons.changes.subscribe(() => {
            // Focus the close button for the latest popover
            const lastCloseButton = this.closeButtons.last;
            if (lastCloseButton) {
                lastCloseButton.nativeElement.focus();
            }
        });
    }
    sequence(upperBound) {
        return Array.from(Array(upperBound), (_placeholder, index) => {
            return index;
        });
    }
    getLeftPathStyles() {
        const hasInfinity = this.getAnswerSegments().find((answer) => answer.from === '-infinity');
        return hasInfinity ? { fill: '#007c91' } : { fill: '#366472' };
    }
    getRightPathStyles() {
        const hasInfinity = this.getAnswerSegments().find((answer) => answer.to === '+infinity');
        return hasInfinity ? { fill: '#007c91' } : { fill: '#366472' };
    }
    getLineStyles() {
        return {
            stroke: '#366472',
            strokeWidth: 1,
        };
    }
    getInternalLineStyles() {
        return {
            stroke: 'transparent',
            strokeWidth: 1,
        };
    }
    getAllTicks() {
        const lineGraphConfig = this.lineGraphConfig;
        const lineLength = lineGraphConfig.lineX2 - lineGraphConfig.lineX1;
        if (this.tickInterval <= 0) {
            return [];
        }
        let tickDifference = 0;
        let ticks = 1;
        tickDifference = subtract(fraction(this.maxTicks), fraction(this.minTicks));
        const calc = divide(fraction(tickDifference), fraction(this.tickInterval));
        ticks = floor(number(calc)) + 1;
        if (fraction(tickDifference)) {
            this.xTickDifference = multiply(divide(lineLength, fraction(tickDifference)), fraction(this.tickInterval));
        }
        this.visibleTicks = ticks;
        if (ticks <= 0) {
            return [];
        }
        return this.sequence(ticks);
    }
    getAnswerPoints() {
        return this.answers.filter((answer) => answer.type === responseType.POINT);
    }
    getAnswerSegments() {
        return this.answers.filter((answer) => answer.type === responseType.CONNECTION);
    }
    getEditLabelX(answer) {
        return this.getCircleX(answer) - 5;
    }
    getMathLabelX(answer) {
        return this.getCircleX(answer) + 15;
    }
    getInternalTicks() {
        if (this.visibleTicks > 1) {
            this.internalTicks = floor((this.getTickX(1) - this.getTickX(0)) / this.lineGraphConfig.squareWidth) + 1;
            if (this.divisionsBetweenTicks !== this.internalTicks - 1 && this.isAdvancedNumberLine) {
                this.updateInternalTicks.emit(this.internalTicks);
            }
        }
        return this.sequence(this.internalTicks);
    }
    getTickBackground(tickIndex, internalTickIndex) {
        const dataVal = this.calculateAbsolutePointVal(tickIndex, internalTickIndex);
        const isTickWithinRange = !!this.rangeArray.find((tolerance) => largerEq(fraction(dataVal), fraction(tolerance.from)) &&
            smallerEq(fraction(dataVal), fraction(tolerance.to)));
        return isTickWithinRange ? 'rgba(0,0,0,0.1)' : 'transparent';
    }
    getAllLabels() {
        if (this.labelInterval <= 0) {
            return [];
        }
        const lineGraphConfig = this.lineGraphConfig;
        const lineLength = lineGraphConfig.lineX2 - lineGraphConfig.lineX1;
        let tickDifference = 0;
        let labels = 0;
        tickDifference = subtract(fraction(this.maxTicks), fraction(this.minTicks));
        const calc = divide(fraction(tickDifference), fraction(this.labelInterval));
        labels = floor(number(calc)) + 1;
        if (fraction(tickDifference)) {
            this.xLabelDifference = multiply(divide(lineLength, fraction(tickDifference)), fraction(this.labelInterval));
        }
        if (labels <= 0) {
            return [];
        }
        return this.sequence(labels);
    }
    getTickKey(index) {
        return 'line_' + this.xTickDifference + index;
    }
    getLabelKey(index) {
        return 'label_' + this.xLabelDifference + index;
    }
    getTickX(index) {
        const lineGraphConfig = this.lineGraphConfig;
        return lineGraphConfig.lineX1 + this.xTickDifference * index;
    }
    getInternalTickX(index, internalTickIndex) {
        const visibleIndexX = this.getTickX(index);
        return visibleIndexX + (this.xTickDifference / this.internalTicks) * internalTickIndex;
    }
    isNotLastIndex(tickIndex) {
        return this.visibleTicks - 1 > tickIndex;
    }
    getTickY(pointOne) {
        const lineGraphConfig = this.lineGraphConfig;
        const calculatedTickHeight = lineGraphConfig.tickHeight / 2;
        return pointOne ? lineGraphConfig.lineY - calculatedTickHeight : lineGraphConfig.lineY + calculatedTickHeight;
    }
    getRectX(tickX) {
        const lineGraphConfig = this.lineGraphConfig;
        return tickX - lineGraphConfig.squareWidth / 2;
    }
    getRectY() {
        const lineGraphConfig = this.lineGraphConfig;
        return lineGraphConfig.lineY - lineGraphConfig.squareHeight / 2;
    }
    getTickLabel(index) {
        return Number(Number(this.getLabelValue(index)).toFixed(this.labelDecimalPlace));
    }
    getLabelValue(index) {
        const value = add(fraction(this.minTicks), multiply(fraction(this.labelInterval), index));
        if (this.showGraphAs === 'fraction') {
            this.hasFraction = true;
            return value;
        }
        else {
            this.hasFraction = false;
            return number(value);
        }
    }
    getFractionBar(labelvalue) {
        let fractionDividerLength = (labelvalue.n > labelvalue.d ? labelvalue.n : labelvalue.d).toString().length;
        let bar = '_';
        while (--fractionDividerLength) {
            bar += '_';
        }
        return bar;
    }
    isFraction(value) {
        return value.toString().indexOf('/') > -1;
    }
    getSignValue(index) {
        const sign = this.getLabelValue(index) && this.getLabelValue(index).s;
        return sign < 0 ? '-' : '';
    }
    getFractionText(index) {
        const labelVal = this.getLabelValue(index);
        const numerator = labelVal['s'] * labelVal['n'];
        const denominator = labelVal['d'] === 1 ? '' : '/' + labelVal['d'];
        return numerator + denominator;
    }
    getTextX(index, isDecimal = false) {
        const lineGraphConfig = this.lineGraphConfig;
        let nudgePixels = 0;
        const labelVal = this.getLabelValue(index);
        const numerator = String(labelVal['n']);
        const denominator = String(labelVal['d']);
        const fracLen = numerator.length > denominator.length ? numerator.length : denominator.length;
        if (isDecimal) {
            nudgePixels = Math.round(String(this.getTickLabel(index)).length * 3.15);
        }
        else {
            nudgePixels = Math.round(fracLen * 3.15);
        }
        return lineGraphConfig.lineX1 + this.xLabelDifference * index - nudgePixels;
    }
    getSignX(index) {
        return this.getTextX(index) - 10;
    }
    getCircleKey(answer) {
        return 'point_' + this.getCircleX(answer);
    }
    getCircleX(answer) {
        const lineConfig = this.lineGraphConfig;
        const lineLength = lineConfig.lineX2 - lineConfig.lineX1;
        if (this.minTicks && this.maxTicks) {
            const tickDifference = subtract(fraction(this.maxTicks), fraction(this.minTicks));
            const xDifference = multiply(divide(fraction(lineLength), fraction(tickDifference)), fraction(this.tickInterval));
            return add(fraction(lineConfig.lineX1), multiply(fraction(xDifference), fraction(this.getPointData(answer))));
        }
    }
    getPointData(answer) {
        const answerVal = answer && answer.value ? answer.value : answer;
        if (answerVal && this.minTicks && this.tickInterval) {
            return divide(subtract(fraction(answerVal), fraction(this.minTicks)), fraction(this.tickInterval));
        }
    }
    getRefDifference() {
        const lineConfig = this.lineGraphConfig;
        const lineLength = lineConfig.lineX2 - lineConfig.lineX1;
        const arrowWidth = 8;
        let xDifference = 1;
        if (this.minTicks && this.maxTicks) {
            const tickDifference = subtract(fraction(this.maxTicks), fraction(this.minTicks));
            const calc = multiply(divide(fraction(lineLength), fraction(tickDifference)), fraction(this.tickInterval));
            xDifference = Math.floor(number(calc)) + arrowWidth;
        }
        if (xDifference > 30) {
            xDifference = 30;
        }
        return xDifference;
    }
    getTickId(index, internalTickIndex) {
        return 'tick_' + this.calculateFinalVal(index, internalTickIndex, true);
    }
    getMathLabelId(index) {
        return 'label_' + index;
    }
    onIconClick(answer) {
        this.selectedAnswer = answer;
        this.authorLabelModal = true;
    }
    handleKeydown(event, index, internalTickIndex = -1) {
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            this.handleToggle(index, internalTickIndex);
        }
    }
    closeAuthorModal(event) {
        this.authorLabelModal = event;
    }
    saveLabel() {
        this.updateAnswerLabel.emit({
            labelledAnswer: this.selectedAnswer,
            event: this.authoredLabel,
        });
        this.openOrShowMathModal.emit(false);
    }
    getEditedLabelKey(index) {
        return 'edited_label_' + index;
    }
    calculateAbsolutePointVal(index, internalTickIndex = -1, skipSnap = false) {
        let interTickVal = multiply(divide(fraction(this.tickInterval), fraction(this.internalTicks)), fraction(internalTickIndex));
        interTickVal = skipSnap ? interTickVal : this.calculateSnapOnInnerTicks(interTickVal, internalTickIndex);
        let dataVal = add(fraction(this.minTicks), multiply(index, fraction(this.tickInterval)));
        dataVal = internalTickIndex > -1 ? add(fraction(dataVal), fraction(interTickVal)) : dataVal;
        return dataVal;
    }
    calculateSnapOnInnerTicks(interTickVal, internalTickIndex) {
        if (this.allowSnapToTick) {
            if ((this.internalTicks - 1) / 2 >= internalTickIndex) {
                return 0;
            }
            else {
                return this.tickInterval;
            }
        }
        return interTickVal;
    }
    calculateFinalVal(index, internalTickIndex, skipSnap = false) {
        const dataVal = this.calculateAbsolutePointVal(index, internalTickIndex, skipSnap);
        if (this.hasFraction) {
            const hasFractionInAnswers = typeof dataVal === 'object';
            const numerator = hasFractionInAnswers ? dataVal.n : dataVal;
            const sign = hasFractionInAnswers && dataVal.s < 0 ? '-' : '';
            const denominator = hasFractionInAnswers && dataVal.d !== 1 ? '/' + dataVal.d : '';
            return sign + numerator + denominator;
        }
        return Number(Number(number(dataVal)).toFixed(this.valueDecimalPlace));
    }
    addPoints(index, internalTickIndex) {
        const dataVal = this.calculateFinalVal(index, internalTickIndex, false).toString();
        const sameResponse = this.answers.find((answer) => answer.value === dataVal);
        if (!this.isAdvancedNumberLine || !sameResponse) {
            this.updateCorrectAnswer.emit(dataVal);
            const isPointWithinSegment = this.getAnswerSegments().find((segment) => {
                return ((segment.from === '-infinity' ||
                    largerEq(number(fraction(dataVal)), number(fraction(segment.from)))) &&
                    (segment.to === '+infinity' || largerEq(number(fraction(segment.to)), number(fraction(dataVal)))));
            });
            if (isPointWithinSegment) {
                this.selectedSegment = isPointWithinSegment;
                this.removeSegment();
            }
        }
        setTimeout(() => {
            const selectedBtn = document.querySelector(`button[originalval='${dataVal}']`);
            if (selectedBtn && this.isAdvancedNumberLine) {
                selectedBtn.focus();
                selectedBtn.click();
            }
        }, 0);
    }
    getSegmentWidth(segment) {
        if (!segment.to || !segment.from) {
            return 0;
        }
        return this.getSegmentX(segment.to) - this.getSegmentX(segment.from);
    }
    openPointPopover(event, answer) {
        console.log('answer : ', answer);
        if (this.getResponseAsPoint()) {
            this.triggeringBtn = event && event.target;
            if (!this.isAdvancedNumberLine) {
                this.updateCorrectAnswer.emit(answer.value);
            }
            this.selectedAnswer = answer;
        }
    }
    openSegmentPopover(event) {
        if (!this.getResponseAsPoint()) {
            this.triggeringBtn = event.target;
        }
    }
    removePoint() {
        this.updateCorrectAnswer.emit(this.selectedAnswer.value);
        this.closePointPopover();
    }
    removeSegment() {
        this.updateAnswerSegment.emit({
            from: this.selectedSegment.from,
            to: this.selectedSegment.to,
        });
        this.closeSegmentPopover();
    }
    sortArrays(array) {
        return array.sort(function (a, b) {
            return subtract(fraction(a), fraction(b));
        });
    }
    getResponseAsPoint() {
        return !this.isAdvancedNumberLine || this.showResponseModeAs === responseType.POINT;
    }
    getSegmentX(dimension) {
        if (dimension === '+infinity') {
            return this.lineGraphConfig.lineX2 + this.lineGraphConfig.lineX1 - 5;
        }
        else if (dimension === '-infinity') {
            return -2;
        }
        else {
            return this.getCircleX(dimension) - 2;
        }
    }
    getAllSegments() {
        const answerPoints = this.sortArrays(this.getAnswerPoints().map((point) => point.value));
        if (!answerPoints.length) {
            return [];
        }
        let segmentsArray = [];
        answerPoints.forEach((element, index) => {
            if (!index) {
                segmentsArray = segmentsArray.concat({
                    from: '-infinity',
                    to: element,
                });
            }
            if (index === answerPoints.length - 1) {
                segmentsArray = segmentsArray.concat({
                    from: element,
                    to: '+infinity',
                });
            }
            else {
                segmentsArray = segmentsArray.concat({
                    from: element,
                    to: answerPoints[index + 1],
                });
            }
        });
        return segmentsArray;
    }
    handleSegmentClick(event, segment) {
        this.selectedSegment = segment;
        this.openSegmentPopover(event);
        if (this.isAnswerSegment(segment) || this.getResponseAsPoint()) {
            return;
        }
        this.updateAnswerSegment.emit({
            from: segment.from,
            to: segment.to,
        });
    }
    isAnswerSegment(segment) {
        return this.getAnswerSegments().find((answerSegment) => {
            return segment.to === (answerSegment.to || '+infinity') && segment.from === (answerSegment.from || '-infinity');
        });
    }
    handleToggle(index, internalTickIndex = -1) {
        if (this.getResponseAsPoint()) {
            this.addPoints(index, internalTickIndex);
        }
    }
    onUpdatePlot(event) {
        this.updatePlot.emit({
            labelledAnswer: this.selectedAnswer,
            pointType: event,
        });
    }
    getSelectedAnswer() {
        return (this.answers &&
            this.answers.find((answer) => (answer && answer.uuid) === (this.selectedAnswer && this.selectedAnswer.uuid)));
    }
    closePointPopover() {
        this.pointConfigPopover.forEach((popover) => {
            popover.triggeringButton = this.triggeringBtn;
            popover.close();
        });
        this.element.nativeElement.querySelector(`[tickValue="${this.selectedAnswer.value}"]`)?.focus();
    }
    closeSegmentPopover() {
        this.segmentConfigPopover.forEach((popover) => {
            popover.triggeringButton = this.triggeringBtn;
            popover.close();
        });
        this.element.nativeElement
            .querySelector(`[data-val="${this.selectedSegment.from}_${this.selectedSegment.to}"]`)
            ?.focus();
    }
    trackByUuid(index, elem) {
        return elem.uuid;
    }
    onUpdatingAnswer(event) {
        this.authoredLabel = event;
    }
    initializeMathModal() {
        this.closePointPopover();
        this.openOrShowMathModal.emit(true);
        setTimeout(() => {
            if (this.mathEditor) {
                this.mathEditor.initializeMathEditor();
            }
        }, 100);
    }
    getSegmentClasses(segment) {
        if (this.isAnswerSegment(segment)) {
            return 'segment-btn show-segment';
        }
        else {
            if (!this.getResponseAsPoint()) {
                return 'segment-btn click-segment';
            }
            return 'segment-btn';
        }
    }
    getPointClasses(answer) {
        const isAdvanced = this.isAdvancedNumberLine ? 'advanced' : '';
        return answer.pointType === 'open'
            ? `circle-btn open-point ${isAdvanced}`
            : `circle-btn closed-point ${isAdvanced}`;
    }
    getSegmentStyles(segment) {
        if (!segment.from) {
            return;
        }
        return {
            left: this.getSegmentX(segment.from) + this.lineGraphConfig.lineX1 + 'px',
            width: this.getSegmentWidth(segment) - 5 + 'px',
            'z-index': !this.getResponseAsPoint() ? 1 : 0,
        };
    }
    getButtonLabel() {
        if (this.selectedAnswer && this.selectedAnswer.labels[0]) {
            return 'Edit Label';
        }
        return 'Add Label';
    }
    getPopoverGenre() {
        return this.selectedSegment.from === '-infinity' || this.selectedSegment.to === '+infinity'
            ? 'Arrow'
            : 'Connection';
    }
}
LineGraphComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LineGraphComponent, deps: [{ token: i1.Store }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
LineGraphComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: LineGraphComponent, selector: "aa-line-graph", inputs: { minTicks: "minTicks", maxTicks: "maxTicks", tickInterval: "tickInterval", labelInterval: "labelInterval", answers: "answers", showGraphAs: "showGraphAs", showResponseModeAs: "showResponseModeAs", isAdvancedNumberLine: "isAdvancedNumberLine", divisionsBetweenTicks: "divisionsBetweenTicks", allowSnapToTick: "allowSnapToTick", showMathModal: "showMathModal" }, outputs: { updateCorrectAnswer: "updateCorrectAnswer", updateAnswerLabel: "updateAnswerLabel", updateInternalTicks: "updateInternalTicks", updateToleranceRange: "updateToleranceRange", updateAnswerSegment: "updateAnswerSegment", updatePlot: "updatePlot", openOrShowMathModal: "openOrShowMathModal" }, viewQueries: [{ propertyName: "mathEditor", first: true, predicate: ["mathEditor"], descendants: true }, { propertyName: "pointConfigPopover", predicate: ["pointConfigPopover"], descendants: true }, { propertyName: "segmentConfigPopover", predicate: ["segmentConfigPopover"], descendants: true }, { propertyName: "closeButtons", predicate: ["closeButton"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<svg\n  class=\"numberLine-main-container\"\n  [attr.height]=\"lineGraphConfig.height\"\n  [attr.width]=\"lineGraphConfig.width\"\n  unselectable=\"on\"\n  version=\"1.1\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <defs>\n    <marker\n      id=\"arrowStartAuthoring\"\n      markerHeight=\"14\"\n      markerWidth=\"34\"\n      orient=\"auto\"\n      [attr.refX]=\"getRefDifference()\"\n      refY=\"5\"\n    >\n      <path d=\"M 0,5 L 8,0 L 8,10 Z\" [ngStyle]=\"getLeftPathStyles()\" />\n      <line\n        stroke=\"#366472\"\n        stroke-width=\"1\"\n        x1=\"5\"\n        [attr.x2]=\"getRefDifference()\"\n        y1=\"5\"\n        y2=\"5\"\n      />\n    </marker>\n    <marker\n      id=\"arrowEndAuthoring\"\n      markerHeight=\"14\"\n      markerWidth=\"34\"\n      orient=\"-180\"\n      [attr.refX]=\"getRefDifference()\"\n      refY=\"5\"\n    >\n      <path d=\"M 0,5 L 8,0 L 8,10 Z\" [ngStyle]=\"getRightPathStyles()\" />\n      <line\n        stroke=\"#366472\"\n        stroke-width=\"1\"\n        x1=\"5\"\n        [attr.x2]=\"getRefDifference()\"\n        y1=\"5\"\n        y2=\"5\"\n      />\n    </marker>\n  </defs>\n  <line\n    marker-end=\"url(#arrowEndAuthoring)\"\n    marker-start=\"url(#arrowStartAuthoring)\"\n    [ngStyle]=\"getLineStyles()\"\n    [attr.x1]=\"lineGraphConfig.lineX1\"\n    [attr.x2]=\"lineGraphConfig.lineX2\"\n    [attr.y1]=\"lineGraphConfig.lineY\"\n    [attr.y2]=\"lineGraphConfig.lineY\"\n  />\n  <svg\n    className=\"tick\"\n    *ngFor=\"let tick of getAllTicks(); let tickIndex = index\"\n    [attr.key]=\"getTickKey(tickIndex)\"\n  >\n    <svg\n      className=\"tick\"\n      *ngFor=\"\n        let internalTick of getInternalTicks();\n        let internalTickIndex = index\n      \"\n      [attr.key]=\"getTickKey(internalTickIndex)\"\n    >\n      <line\n        *ngIf=\"!internalTickIndex\"\n        [attr.x1]=\"getTickX(tickIndex)\"\n        [attr.x2]=\"getTickX(tickIndex)\"\n        [attr.y1]=\"getTickY(true)\"\n        [attr.y2]=\"getTickY(false)\"\n        [ngStyle]=\"getLineStyles()\"\n      />\n      <rect\n        *ngIf=\"!internalTickIndex\"\n        [attr.id]=\"getTickId(tickIndex, internalTickIndex)\"\n        [attr.height]=\"lineGraphConfig.squareHeight\"\n        [attr.tickValue]=\"calculateFinalVal(tickIndex, internalTickIndex, true)\"\n        [attr.width]=\"lineGraphConfig.squareWidth\"\n        [attr.x]=\"getRectX(getTickX(tickIndex))\"\n        [attr.y]=\"getRectY()\"\n        [attr.class]=\"\n          allowSnapToTick ? 'visibleTick' : 'allowClick visibleTick'\n        \"\n        [attr.fill]=\"getTickBackground(tickIndex, internalTickIndex)\"\n        [tabindex]=\"getResponseAsPoint() ? 0 : -1\"\n        (click)=\"handleToggle(tickIndex)\"\n        (keydown)=\"handleKeydown($event, tickIndex)\"\n      />\n      <line\n        *ngIf=\"\n          isAdvancedNumberLine && internalTickIndex && isNotLastIndex(tickIndex)\n        \"\n        [attr.x1]=\"getInternalTickX(tickIndex, internalTickIndex)\"\n        [attr.x2]=\"getInternalTickX(tickIndex, internalTickIndex)\"\n        [attr.y1]=\"getTickY(true)\"\n        [attr.y2]=\"getTickY(false)\"\n        [ngStyle]=\"getInternalLineStyles()\"\n      />\n      <rect\n        *ngIf=\"\n          isAdvancedNumberLine && internalTickIndex && isNotLastIndex(tickIndex)\n        \"\n        [attr.id]=\"getTickId(tickIndex, internalTickIndex)\"\n        [attr.height]=\"lineGraphConfig.squareHeight\"\n        [attr.tickValue]=\"calculateFinalVal(tickIndex, internalTickIndex, true)\"\n        [attr.width]=\"lineGraphConfig.squareWidth\"\n        [attr.x]=\"getRectX(getInternalTickX(tickIndex, internalTickIndex))\"\n        [attr.y]=\"getRectY()\"\n        [attr.fill]=\"getTickBackground(tickIndex, internalTickIndex)\"\n        [tabindex]=\"allowSnapToTick ? -1 : getResponseAsPoint() ? 0 : -1\"\n        [attr.class]=\"allowSnapToTick ? 'skipClick' : 'allowClick'\"\n        (click)=\"handleToggle(tickIndex, internalTickIndex)\"\n        (keydown)=\"handleKeydown($event, tickIndex, internalTickIndex)\"\n      />\n    </svg>\n  </svg>\n  <text\n    *ngFor=\"let tick of getAllLabels(); let labelIndex = index\"\n    [attr.key]=\"getLabelKey(labelIndex)\"\n    [attr.x]=\"getTextX(labelIndex)\"\n    [attr.y]=\"100\"\n    [attr.val]=\"\n      hasFraction ? getFractionText(labelIndex) : getLabelValue(labelIndex)\n    \"\n  >\n    <tspan *ngIf=\"!hasFraction\" [attr.x]=\"getTextX(labelIndex, true)\" dy=\".1em\">\n      {{ getTickLabel(labelIndex) }}\n    </tspan>\n    <tspan *ngIf=\"hasFraction\" [attr.x]=\"getSignX(labelIndex)\" dy=\".1em\">\n      {{ getSignValue(labelIndex) }}\n    </tspan>\n    <tspan *ngIf=\"hasFraction\" [attr.x]=\"getTextX(labelIndex)\" dy=\".1em\">\n      {{ getLabelValue(labelIndex)?.n }}\n    </tspan>\n    <tspan\n      *ngIf=\"getLabelValue(labelIndex)?.d !== 1 && hasFraction\"\n      [attr.x]=\"getTextX(labelIndex)\"\n      dy=\".1em\"\n    >\n      {{ getFractionBar(getLabelValue(labelIndex)) }}\n    </tspan>\n    <tspan\n      *ngIf=\"getLabelValue(labelIndex)?.d !== 1 && hasFraction\"\n      [attr.x]=\"getTextX(labelIndex)\"\n      dy=\".8em\"\n    >\n      {{ getLabelValue(labelIndex)?.d }}\n    </tspan>\n  </text>\n</svg>\n<div class=\"config-wrapper\">\n  <div\n    *ngFor=\"\n      let answer of getAnswerPoints();\n      trackBy: trackByUuid;\n      let answerLabelIndex = index\n    \"\n  >\n    <div\n      *ngIf=\"(answer?.labels)[0]\"\n      class=\"custom-label\"\n      [attr.id]=\"getMathLabelId(answerLabelIndex)\"\n      [ngStyle]=\"{ left: getMathLabelX(answer) + 8 + 'px' }\"\n      [appMath]=\"(answer?.labels)[0]\"\n    ></div>\n    <button\n      type=\"button\"\n      [class]=\"getPointClasses(answer)\"\n      [ngStyle]=\"{\n        left: getCircleX(answer) + 19 + 'px',\n        'z-index': getResponseAsPoint() ? 1 : 0\n      }\"\n      [attr.data-val]=\"getPointData(answer)\"\n      [attr.originalval]=\"answer?.value\"\n      #pointConfigPopover=\"ngbPopover\"\n      panelClass=\"nl-point-popover\"\n      [placement]=\"placementBottom\"\n      [ngbPopover]=\"pointConfigPopoverRef\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      tabindex=\"-1\"\n      (click)=\"openPointPopover($event, answer)\"\n      (hidden)=\"closePointPopover()\"\n    >\n    </button>\n  </div>\n\n  <div\n    *ngFor=\"\n      let segment of getAllSegments();\n      trackBy: trackByUuid;\n      let segmentIndex = index\n    \"\n  >\n    <button\n      type=\"button\"\n      [class]=\"getSegmentClasses(segment)\"\n      [ngStyle]=\"getSegmentStyles(segment)\"\n      [attr.data-val]=\"segment?.from + '_' + segment?.to\"\n      #segmentConfigPopover=\"ngbPopover\"\n      [placement]=\"placementBottom\"\n      panelClass=\"nl-connection-popover\"\n      [ngbPopover]=\"segmentConfigPopoverRef\"\n      [tabindex]=\"!getResponseAsPoint() ? 0 : -1\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      (click)=\"handleSegmentClick($event, segment)\"\n      (hidden)=\"closeSegmentPopover()\"\n    >\n    </button>\n  </div>\n</div>\n\n<ng-template #pointConfigHeaderPopoverRef>\n  <div class=\"d-flex align-items-center\">\n    <strong class=\"flex-grow-1\" translate>Point Options</strong>\n    <button\n      #closeButton\n      aria-label=\"Close\"\n      class=\"btn-close  ms-auto\"\n      type=\"button\"\n      (click)=\"closePointPopover()\"\n      ></button>\n  </div>\n</ng-template>\n<ng-template #pointConfigPopoverRef>\n  <div appFocusTrap>\n    <div class=\"popover-header d-flex align-items-center\">\n      <strong class=\"flex-grow-1\" translate>Point Options</strong>\n      <button\n        #closeButton\n        aria-label=\"Close\"\n        class=\"btn-close  ms-auto\"\n        type=\"button\"\n        (click)=\"closePointPopover()\"\n        ></button>\n    </div>\n    <div class=\"mb-2 popover-content\">\n      <aa-configure-points\n        [selectedAnswer]=\"getSelectedAnswer()\"\n        [allowSnapToTick]=\"allowSnapToTick\"\n        (updatePlot)=\"onUpdatePlot($event)\"\n      ></aa-configure-points>\n    </div>\n\n    <div class=\"popover-footer\">\n      <button\n        class=\"btn btn-outline-primary me-1\"\n        (click)=\"initializeMathModal()\"\n      >\n        {{ getButtonLabel() }}\n      </button>\n      <button\n        class=\"btn btn-outline-primary\"\n        (click)=\"removePoint()\"\n      >\n        Clear Point\n      </button>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #segmentConfigHeaderPopoverRef>\n  <div class=\"d-flex align-items-center\">\n    <strong class=\"flex-grow-1\" translate\n      >{{ getPopoverGenre() }} Options</strong\n    >\n    <button\n      #closeButton\n      aria-label=\"Close\"\n      class=\"btn-close\"\n      type=\"button\"\n      (click)=\"closeSegmentPopover()\"\n    ></button>\n  </div>\n</ng-template>\n<ng-template #segmentConfigPopoverRef>\n  <div appFocusTrap>\n    <div class=\"popover-header d-flex align-items-center\">\n      <strong class=\"flex-grow-1\" translate\n        >{{ getPopoverGenre() }} Options</strong\n      >\n      <button\n        #closeButton\n        aria-label=\"Close\"\n        class=\"btn-close\"\n        type=\"button\"\n        (click)=\"closeSegmentPopover()\"\n      ></button>\n    </div>\n    <div class=\"border-0 popover-footer\">\n        <button\n          class=\"btn btn-outline-primary\"\n          (click)=\"removeSegment()\"\n        >\n          Clear {{ getPopoverGenre() }}\n        </button>\n    </div>\n  </div>\n</ng-template>\n\n<air-modal\n  [isOpen]=\"showMathModal\"\n  [titleText]=\"getButtonLabel()\"\n  [confirmButtonText]=\"'Confirm'\"\n  panelClass=\"author-labels\"\n  (cancel)=\"openOrShowMathModal.emit(false)\"\n  (close)=\"openOrShowMathModal.emit(false)\"\n  (confirm)=\"saveLabel()\"\n>\n  <aa-math-editor\n    class=\"math-editor\"\n    #mathEditor\n    [editorId]=\"getSelectedAnswer()?.uuid\"\n    (updateAnswer)=\"onUpdatingAnswer($event)\"\n    [mathml]=\"(getSelectedAnswer()?.labels)[0] || ''\"\n    [mathEditorType]=\"'basic'\"\n    [wirisMathUrl]=\"(airConfig$ | async)?.wirisMathUrl\"\n  ></aa-math-editor>\n</air-modal>\n", styles: ["rect.allowClick:hover,circle.allowClick:hover{cursor:crosshair}rect.skipClick:focus,circle.skipClick:focus{outline:0}line.allowClick:hover{cursor:pointer}rect:focus{outline:5px auto #017d92}.config-wrapper .circle-btn{position:absolute;height:10px;width:10px;top:60px;padding:0;background-color:#366472;border:0;border-radius:5px;border:2px solid #366472;z-index:2}.config-wrapper .circle-btn:focus:before{box-shadow:none!important}.config-wrapper .circle-btn.open-point{background:#fff}.config-wrapper .circle-btn.advanced{top:107px}.config-wrapper .segment-btn{position:absolute;height:0;top:110px;padding:0;background-color:transparent;border:0}.config-wrapper .segment-btn.show-segment{height:4px;border-radius:5px;border:1px solid #366472;background-color:#366472}.config-wrapper .segment-btn.click-segment{height:3px}.config-wrapper .segment-btn:focus{box-shadow:0 0 0 2px #007c91}.config-wrapper .segment-btn:focus:before{box-shadow:none}:host ::ng-deep .custom-label{top:72px;position:absolute}:host ::ng-deep .custom-label .MathJax,:host ::ng-deep .custom-label .MathJax_CHTML{padding:3px;min-width:29px;text-align:center;display:inline-block;border:2px solid #4399b2;border-radius:5px;left:calc(-50% + 3px)}:host ::ng-deep .custom-label .MathJax:before,:host ::ng-deep .custom-label .MathJax:after,:host ::ng-deep .custom-label .MathJax_CHTML:before,:host ::ng-deep .custom-label .MathJax_CHTML:after{display:inline-block;content:\"\";position:absolute;left:calc(50% - 10px);border-style:solid;border-width:10px}:host ::ng-deep .custom-label .MathJax:before,:host ::ng-deep .custom-label .MathJax_CHTML:before{bottom:-20px;border-color:#4399b2 transparent transparent}:host ::ng-deep .custom-label .MathJax:after,:host ::ng-deep .custom-label .MathJax_CHTML:after{bottom:-17px;border-color:white transparent transparent}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i6.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "triggers", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "component", type: MathEditorComponent, selector: "aa-math-editor", inputs: ["editorId", "mathml", "mathEditorType", "answers", "wirisMathUrl", "preset"], outputs: ["updateAnswer", "editorBlur"] }, { kind: "directive", type: MathDirective, selector: "[appMath]", inputs: ["appMath"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: ConfigurePointsComponent, selector: "aa-configure-points", inputs: ["selectedAnswer", "allowSnapToTick"], outputs: ["updatePlot"] }, { kind: "directive", type: FocusTrapDirective, selector: "[appFocusTrap]" }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: LineGraphComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-line-graph', template: "<svg\n  class=\"numberLine-main-container\"\n  [attr.height]=\"lineGraphConfig.height\"\n  [attr.width]=\"lineGraphConfig.width\"\n  unselectable=\"on\"\n  version=\"1.1\"\n  xmlns=\"http://www.w3.org/2000/svg\"\n>\n  <defs>\n    <marker\n      id=\"arrowStartAuthoring\"\n      markerHeight=\"14\"\n      markerWidth=\"34\"\n      orient=\"auto\"\n      [attr.refX]=\"getRefDifference()\"\n      refY=\"5\"\n    >\n      <path d=\"M 0,5 L 8,0 L 8,10 Z\" [ngStyle]=\"getLeftPathStyles()\" />\n      <line\n        stroke=\"#366472\"\n        stroke-width=\"1\"\n        x1=\"5\"\n        [attr.x2]=\"getRefDifference()\"\n        y1=\"5\"\n        y2=\"5\"\n      />\n    </marker>\n    <marker\n      id=\"arrowEndAuthoring\"\n      markerHeight=\"14\"\n      markerWidth=\"34\"\n      orient=\"-180\"\n      [attr.refX]=\"getRefDifference()\"\n      refY=\"5\"\n    >\n      <path d=\"M 0,5 L 8,0 L 8,10 Z\" [ngStyle]=\"getRightPathStyles()\" />\n      <line\n        stroke=\"#366472\"\n        stroke-width=\"1\"\n        x1=\"5\"\n        [attr.x2]=\"getRefDifference()\"\n        y1=\"5\"\n        y2=\"5\"\n      />\n    </marker>\n  </defs>\n  <line\n    marker-end=\"url(#arrowEndAuthoring)\"\n    marker-start=\"url(#arrowStartAuthoring)\"\n    [ngStyle]=\"getLineStyles()\"\n    [attr.x1]=\"lineGraphConfig.lineX1\"\n    [attr.x2]=\"lineGraphConfig.lineX2\"\n    [attr.y1]=\"lineGraphConfig.lineY\"\n    [attr.y2]=\"lineGraphConfig.lineY\"\n  />\n  <svg\n    className=\"tick\"\n    *ngFor=\"let tick of getAllTicks(); let tickIndex = index\"\n    [attr.key]=\"getTickKey(tickIndex)\"\n  >\n    <svg\n      className=\"tick\"\n      *ngFor=\"\n        let internalTick of getInternalTicks();\n        let internalTickIndex = index\n      \"\n      [attr.key]=\"getTickKey(internalTickIndex)\"\n    >\n      <line\n        *ngIf=\"!internalTickIndex\"\n        [attr.x1]=\"getTickX(tickIndex)\"\n        [attr.x2]=\"getTickX(tickIndex)\"\n        [attr.y1]=\"getTickY(true)\"\n        [attr.y2]=\"getTickY(false)\"\n        [ngStyle]=\"getLineStyles()\"\n      />\n      <rect\n        *ngIf=\"!internalTickIndex\"\n        [attr.id]=\"getTickId(tickIndex, internalTickIndex)\"\n        [attr.height]=\"lineGraphConfig.squareHeight\"\n        [attr.tickValue]=\"calculateFinalVal(tickIndex, internalTickIndex, true)\"\n        [attr.width]=\"lineGraphConfig.squareWidth\"\n        [attr.x]=\"getRectX(getTickX(tickIndex))\"\n        [attr.y]=\"getRectY()\"\n        [attr.class]=\"\n          allowSnapToTick ? 'visibleTick' : 'allowClick visibleTick'\n        \"\n        [attr.fill]=\"getTickBackground(tickIndex, internalTickIndex)\"\n        [tabindex]=\"getResponseAsPoint() ? 0 : -1\"\n        (click)=\"handleToggle(tickIndex)\"\n        (keydown)=\"handleKeydown($event, tickIndex)\"\n      />\n      <line\n        *ngIf=\"\n          isAdvancedNumberLine && internalTickIndex && isNotLastIndex(tickIndex)\n        \"\n        [attr.x1]=\"getInternalTickX(tickIndex, internalTickIndex)\"\n        [attr.x2]=\"getInternalTickX(tickIndex, internalTickIndex)\"\n        [attr.y1]=\"getTickY(true)\"\n        [attr.y2]=\"getTickY(false)\"\n        [ngStyle]=\"getInternalLineStyles()\"\n      />\n      <rect\n        *ngIf=\"\n          isAdvancedNumberLine && internalTickIndex && isNotLastIndex(tickIndex)\n        \"\n        [attr.id]=\"getTickId(tickIndex, internalTickIndex)\"\n        [attr.height]=\"lineGraphConfig.squareHeight\"\n        [attr.tickValue]=\"calculateFinalVal(tickIndex, internalTickIndex, true)\"\n        [attr.width]=\"lineGraphConfig.squareWidth\"\n        [attr.x]=\"getRectX(getInternalTickX(tickIndex, internalTickIndex))\"\n        [attr.y]=\"getRectY()\"\n        [attr.fill]=\"getTickBackground(tickIndex, internalTickIndex)\"\n        [tabindex]=\"allowSnapToTick ? -1 : getResponseAsPoint() ? 0 : -1\"\n        [attr.class]=\"allowSnapToTick ? 'skipClick' : 'allowClick'\"\n        (click)=\"handleToggle(tickIndex, internalTickIndex)\"\n        (keydown)=\"handleKeydown($event, tickIndex, internalTickIndex)\"\n      />\n    </svg>\n  </svg>\n  <text\n    *ngFor=\"let tick of getAllLabels(); let labelIndex = index\"\n    [attr.key]=\"getLabelKey(labelIndex)\"\n    [attr.x]=\"getTextX(labelIndex)\"\n    [attr.y]=\"100\"\n    [attr.val]=\"\n      hasFraction ? getFractionText(labelIndex) : getLabelValue(labelIndex)\n    \"\n  >\n    <tspan *ngIf=\"!hasFraction\" [attr.x]=\"getTextX(labelIndex, true)\" dy=\".1em\">\n      {{ getTickLabel(labelIndex) }}\n    </tspan>\n    <tspan *ngIf=\"hasFraction\" [attr.x]=\"getSignX(labelIndex)\" dy=\".1em\">\n      {{ getSignValue(labelIndex) }}\n    </tspan>\n    <tspan *ngIf=\"hasFraction\" [attr.x]=\"getTextX(labelIndex)\" dy=\".1em\">\n      {{ getLabelValue(labelIndex)?.n }}\n    </tspan>\n    <tspan\n      *ngIf=\"getLabelValue(labelIndex)?.d !== 1 && hasFraction\"\n      [attr.x]=\"getTextX(labelIndex)\"\n      dy=\".1em\"\n    >\n      {{ getFractionBar(getLabelValue(labelIndex)) }}\n    </tspan>\n    <tspan\n      *ngIf=\"getLabelValue(labelIndex)?.d !== 1 && hasFraction\"\n      [attr.x]=\"getTextX(labelIndex)\"\n      dy=\".8em\"\n    >\n      {{ getLabelValue(labelIndex)?.d }}\n    </tspan>\n  </text>\n</svg>\n<div class=\"config-wrapper\">\n  <div\n    *ngFor=\"\n      let answer of getAnswerPoints();\n      trackBy: trackByUuid;\n      let answerLabelIndex = index\n    \"\n  >\n    <div\n      *ngIf=\"(answer?.labels)[0]\"\n      class=\"custom-label\"\n      [attr.id]=\"getMathLabelId(answerLabelIndex)\"\n      [ngStyle]=\"{ left: getMathLabelX(answer) + 8 + 'px' }\"\n      [appMath]=\"(answer?.labels)[0]\"\n    ></div>\n    <button\n      type=\"button\"\n      [class]=\"getPointClasses(answer)\"\n      [ngStyle]=\"{\n        left: getCircleX(answer) + 19 + 'px',\n        'z-index': getResponseAsPoint() ? 1 : 0\n      }\"\n      [attr.data-val]=\"getPointData(answer)\"\n      [attr.originalval]=\"answer?.value\"\n      #pointConfigPopover=\"ngbPopover\"\n      panelClass=\"nl-point-popover\"\n      [placement]=\"placementBottom\"\n      [ngbPopover]=\"pointConfigPopoverRef\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      tabindex=\"-1\"\n      (click)=\"openPointPopover($event, answer)\"\n      (hidden)=\"closePointPopover()\"\n    >\n    </button>\n  </div>\n\n  <div\n    *ngFor=\"\n      let segment of getAllSegments();\n      trackBy: trackByUuid;\n      let segmentIndex = index\n    \"\n  >\n    <button\n      type=\"button\"\n      [class]=\"getSegmentClasses(segment)\"\n      [ngStyle]=\"getSegmentStyles(segment)\"\n      [attr.data-val]=\"segment?.from + '_' + segment?.to\"\n      #segmentConfigPopover=\"ngbPopover\"\n      [placement]=\"placementBottom\"\n      panelClass=\"nl-connection-popover\"\n      [ngbPopover]=\"segmentConfigPopoverRef\"\n      [tabindex]=\"!getResponseAsPoint() ? 0 : -1\"\n      triggers=\"click\"\n      autoClose=\"outside\"\n      (click)=\"handleSegmentClick($event, segment)\"\n      (hidden)=\"closeSegmentPopover()\"\n    >\n    </button>\n  </div>\n</div>\n\n<ng-template #pointConfigHeaderPopoverRef>\n  <div class=\"d-flex align-items-center\">\n    <strong class=\"flex-grow-1\" translate>Point Options</strong>\n    <button\n      #closeButton\n      aria-label=\"Close\"\n      class=\"btn-close  ms-auto\"\n      type=\"button\"\n      (click)=\"closePointPopover()\"\n      ></button>\n  </div>\n</ng-template>\n<ng-template #pointConfigPopoverRef>\n  <div appFocusTrap>\n    <div class=\"popover-header d-flex align-items-center\">\n      <strong class=\"flex-grow-1\" translate>Point Options</strong>\n      <button\n        #closeButton\n        aria-label=\"Close\"\n        class=\"btn-close  ms-auto\"\n        type=\"button\"\n        (click)=\"closePointPopover()\"\n        ></button>\n    </div>\n    <div class=\"mb-2 popover-content\">\n      <aa-configure-points\n        [selectedAnswer]=\"getSelectedAnswer()\"\n        [allowSnapToTick]=\"allowSnapToTick\"\n        (updatePlot)=\"onUpdatePlot($event)\"\n      ></aa-configure-points>\n    </div>\n\n    <div class=\"popover-footer\">\n      <button\n        class=\"btn btn-outline-primary me-1\"\n        (click)=\"initializeMathModal()\"\n      >\n        {{ getButtonLabel() }}\n      </button>\n      <button\n        class=\"btn btn-outline-primary\"\n        (click)=\"removePoint()\"\n      >\n        Clear Point\n      </button>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #segmentConfigHeaderPopoverRef>\n  <div class=\"d-flex align-items-center\">\n    <strong class=\"flex-grow-1\" translate\n      >{{ getPopoverGenre() }} Options</strong\n    >\n    <button\n      #closeButton\n      aria-label=\"Close\"\n      class=\"btn-close\"\n      type=\"button\"\n      (click)=\"closeSegmentPopover()\"\n    ></button>\n  </div>\n</ng-template>\n<ng-template #segmentConfigPopoverRef>\n  <div appFocusTrap>\n    <div class=\"popover-header d-flex align-items-center\">\n      <strong class=\"flex-grow-1\" translate\n        >{{ getPopoverGenre() }} Options</strong\n      >\n      <button\n        #closeButton\n        aria-label=\"Close\"\n        class=\"btn-close\"\n        type=\"button\"\n        (click)=\"closeSegmentPopover()\"\n      ></button>\n    </div>\n    <div class=\"border-0 popover-footer\">\n        <button\n          class=\"btn btn-outline-primary\"\n          (click)=\"removeSegment()\"\n        >\n          Clear {{ getPopoverGenre() }}\n        </button>\n    </div>\n  </div>\n</ng-template>\n\n<air-modal\n  [isOpen]=\"showMathModal\"\n  [titleText]=\"getButtonLabel()\"\n  [confirmButtonText]=\"'Confirm'\"\n  panelClass=\"author-labels\"\n  (cancel)=\"openOrShowMathModal.emit(false)\"\n  (close)=\"openOrShowMathModal.emit(false)\"\n  (confirm)=\"saveLabel()\"\n>\n  <aa-math-editor\n    class=\"math-editor\"\n    #mathEditor\n    [editorId]=\"getSelectedAnswer()?.uuid\"\n    (updateAnswer)=\"onUpdatingAnswer($event)\"\n    [mathml]=\"(getSelectedAnswer()?.labels)[0] || ''\"\n    [mathEditorType]=\"'basic'\"\n    [wirisMathUrl]=\"(airConfig$ | async)?.wirisMathUrl\"\n  ></aa-math-editor>\n</air-modal>\n", styles: ["rect.allowClick:hover,circle.allowClick:hover{cursor:crosshair}rect.skipClick:focus,circle.skipClick:focus{outline:0}line.allowClick:hover{cursor:pointer}rect:focus{outline:5px auto #017d92}.config-wrapper .circle-btn{position:absolute;height:10px;width:10px;top:60px;padding:0;background-color:#366472;border:0;border-radius:5px;border:2px solid #366472;z-index:2}.config-wrapper .circle-btn:focus:before{box-shadow:none!important}.config-wrapper .circle-btn.open-point{background:#fff}.config-wrapper .circle-btn.advanced{top:107px}.config-wrapper .segment-btn{position:absolute;height:0;top:110px;padding:0;background-color:transparent;border:0}.config-wrapper .segment-btn.show-segment{height:4px;border-radius:5px;border:1px solid #366472;background-color:#366472}.config-wrapper .segment-btn.click-segment{height:3px}.config-wrapper .segment-btn:focus{box-shadow:0 0 0 2px #007c91}.config-wrapper .segment-btn:focus:before{box-shadow:none}:host ::ng-deep .custom-label{top:72px;position:absolute}:host ::ng-deep .custom-label .MathJax,:host ::ng-deep .custom-label .MathJax_CHTML{padding:3px;min-width:29px;text-align:center;display:inline-block;border:2px solid #4399b2;border-radius:5px;left:calc(-50% + 3px)}:host ::ng-deep .custom-label .MathJax:before,:host ::ng-deep .custom-label .MathJax:after,:host ::ng-deep .custom-label .MathJax_CHTML:before,:host ::ng-deep .custom-label .MathJax_CHTML:after{display:inline-block;content:\"\";position:absolute;left:calc(50% - 10px);border-style:solid;border-width:10px}:host ::ng-deep .custom-label .MathJax:before,:host ::ng-deep .custom-label .MathJax_CHTML:before{bottom:-20px;border-color:#4399b2 transparent transparent}:host ::ng-deep .custom-label .MathJax:after,:host ::ng-deep .custom-label .MathJax_CHTML:after{bottom:-17px;border-color:white transparent transparent}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i0.ElementRef }]; }, propDecorators: { minTicks: [{
                type: Input
            }], maxTicks: [{
                type: Input
            }], tickInterval: [{
                type: Input
            }], labelInterval: [{
                type: Input
            }], answers: [{
                type: Input
            }], showGraphAs: [{
                type: Input
            }], showResponseModeAs: [{
                type: Input
            }], isAdvancedNumberLine: [{
                type: Input
            }], divisionsBetweenTicks: [{
                type: Input
            }], allowSnapToTick: [{
                type: Input
            }], showMathModal: [{
                type: Input
            }], updateCorrectAnswer: [{
                type: Output
            }], updateAnswerLabel: [{
                type: Output
            }], updateInternalTicks: [{
                type: Output
            }], updateToleranceRange: [{
                type: Output
            }], updateAnswerSegment: [{
                type: Output
            }], updatePlot: [{
                type: Output
            }], openOrShowMathModal: [{
                type: Output
            }], pointConfigPopover: [{
                type: ViewChildren,
                args: ['pointConfigPopover']
            }], segmentConfigPopover: [{
                type: ViewChildren,
                args: ['segmentConfigPopover']
            }], mathEditor: [{
                type: ViewChild,
                args: ['mathEditor']
            }], closeButtons: [{
                type: ViewChildren,
                args: ['closeButton']
            }] } });

class AirNumberLineComponent extends AirBaseComponent {
    constructor(store, sanitizeInputService, validationService, configService, renderer) {
        super(store, getPureAirNumberLine, LoadAirItemAction$6, ResetStoreAction$6);
        this.store = store;
        this.sanitizeInputService = sanitizeInputService;
        this.validationService = validationService;
        this.configService = configService;
        this.renderer = renderer;
        this.isAdvancedNumberLine = false;
        this.airNumberLineSubscription$ = empty().subscribe();
        this.pureAirNumberLine$ = this.store.select(getPureAirNumberLine);
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.errorMessage = '';
        this.allowInlineValidation = false;
        this.authorLabelModal = false;
        this.placementRight = 'right';
        this.showMathModal = false;
        this.airNumberLine$ = store.select(getAirNumberLine);
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.store.dispatch(new AdvancedNumberLineAction(!!this.isAdvancedNumberLine));
            this.airNumberLineSubscription$ = this.airNumberLine$.subscribe((airNumberLine) => {
                this.isToleranceEnabled = !!airNumberLine.tolerance;
                this.toleranceObject = this.isToleranceEnabled
                    ? airNumberLine.tolerance || { numberOfPointsBelow: 0, numberOfPointsAbove: 0 }
                    : { numberOfPointsBelow: 2, numberOfPointsAbove: 2 };
            });
        }, 1000);
    }
    updateInputs() {
        const minValue = this.validationService.getValue(this.minVal);
        const maxValue = this.validationService.getValue(this.maxVal);
        const tickValue = this.validationService.getValue(this.tickVal);
        const labelValue = this.validationService.getValue(this.labelVal);
        if (this.checkValue(minValue)) {
            this.store.dispatch(new UpdateMinValueAction(minValue));
        }
        if (this.checkValue(maxValue)) {
            this.store.dispatch(new UpdateMaxValueAction(maxValue));
        }
        if (this.checkValue(tickValue)) {
            this.store.dispatch(new UpdateTickIntervalAction(tickValue));
            this.updateToleranceRange();
        }
        if (this.checkValue(labelValue)) {
            this.store.dispatch(new UpdateLabelIntervalAction(labelValue));
        }
    }
    checkValue(value) {
        return !value.endsWith('/') && !value.startsWith('/') && !value.endsWith('.') && !value.endsWith('/0');
    }
    onChangeSnap(isSnapAllowed) {
        if (isSnapAllowed) {
            this.deleteToleranceRange();
        }
        this.store.dispatch(new AllowSnapAction(isSnapAllowed));
    }
    onUpdateCorrectAnswer(correctAnswer) {
        this.validationService.resetCorrectAnswerError();
        this.store.dispatch(new UpdateCorrectAnswerAction(correctAnswer));
        this.updateToleranceRange();
    }
    onClearCorrectAnswers() {
        this.store.dispatch(new ClearCorrectAnswerAction());
        const element = this.renderer.selectRootElement('.graph-wrapper rect');
        setTimeout(() => element.focus(), 0);
    }
    sanitizeInput(event, allowMinus, allowDecimal) {
        if (this.isAdvancedNumberLine) {
            return this.sanitizeInputService.sanitizeInput(event, allowMinus, true, true);
        }
        else {
            return this.sanitizeInputService.sanitizeInput(event, allowMinus, allowDecimal, false);
        }
    }
    toFixed(value) {
        return this.validationService.toFixed(value);
    }
    getCorrectAnswerError() {
        return this.allowInlineValidation && this.validationService.getCorrectAnswerErrors();
    }
    getMinError() {
        return this.validationService.getMinError();
    }
    onMinFocusOut() {
        this.validationService.resetMinMaxErrors();
        this.validationService.resetTickRangeError();
        const isValid = this.validationService.validateInputs(this, 'min');
        if (isValid) {
            this.updateInputs();
        }
    }
    getMaxError() {
        return this.validationService.getMaxError();
    }
    onMaxFocusOut() {
        this.validationService.resetMinMaxErrors();
        this.validationService.resetTickRangeError();
        const isValid = this.validationService.validateInputs(this, 'max');
        if (isValid) {
            this.updateInputs();
        }
    }
    getTickIntervalError() {
        return this.validationService.getTickIntervalError();
    }
    onTickIntervalFocusOut() {
        this.validationService.resetTickIntError();
        this.validationService.resetTickRangeError();
        const isValid = this.validationService.validateInputs(this, 'tick');
        if (isValid) {
            this.updateInputs();
        }
    }
    getLabelIntervalError() {
        return this.validationService.getLabelIntervalError();
    }
    onLabelIntervalFocusOut() {
        this.validationService.resetLabelIntError();
        const isValid = this.validationService.validateInputs(this, 'label');
        if (isValid) {
            this.updateInputs();
        }
    }
    getTickRangeError() {
        return this.validationService.getTickRangeError();
    }
    onLabelFormatChange(format) {
        this.store.dispatch(new ShowGraphAsAction(format));
        this.pureAirNumberLine$.pipe(take$1(1)).subscribe((airNumberLine) => {
            const answers = airNumberLine.answers.map((answer) => {
                let updatedValue;
                if (typeof answer === 'object' && answer?.type === responseType.POINT) {
                    if (format === 'fraction') {
                        const fractionValue = fraction(answer.value);
                        const numerator = fractionValue.n;
                        const sign = fractionValue.s < 0 ? '-' : '';
                        const denominator = fractionValue.d !== 1 ? '/' + fractionValue.d : '';
                        updatedValue = (sign + numerator + denominator).toString();
                    }
                    else {
                        const decimalValue = Number(Number(fraction(answer.value))
                            .toFixed(5)
                            .toString());
                        updatedValue = decimalValue;
                    }
                    return {
                        ...answer,
                        value: updatedValue,
                    };
                }
                return answer;
            });
            answers.map((answer) => {
                this.store.dispatch(new UpdateAnswerFormatAction(answer));
            });
        });
    }
    onResponseModeChange(value) {
        this.store.dispatch(new ShowResponseModeAsAction(value));
    }
    onUpdateInternalTicks(payload) {
        this.store.dispatch(new UpdateInternalTickAction(payload));
        this.updateToleranceRange();
    }
    onUpdateAnswerLabel(answerLabelEvent) {
        this.store.dispatch(new UpdateAnswerLabelAction(answerLabelEvent));
    }
    onChangeToleranceCheckbox(isToleranceEnabled) {
        this.isToleranceEnabled = isToleranceEnabled;
        if (isToleranceEnabled) {
            this.toleranceObject = {
                numberOfPointsAbove: 2,
                numberOfPointsBelow: 2,
            };
            this.updateToleranceRange();
        }
        else {
            this.deleteToleranceRange();
        }
    }
    onUpdateToleranceBelowInput(below) {
        this.toleranceObject = {
            ...this.toleranceObject,
            numberOfPointsBelow: +below.value,
        };
        this.updateToleranceRange();
    }
    onUpdateToleranceAboveInput(target) {
        const value = target.value;
        this.toleranceObject = {
            ...this.toleranceObject,
            numberOfPointsAbove: +value,
        };
        this.updateToleranceRange();
    }
    updateToleranceRange() {
        if (this.isAdvancedNumberLine && this.isToleranceEnabled) {
            this.store.dispatch(new UpdateAnswerToleranceAction(this.toleranceObject));
        }
    }
    deleteToleranceRange() {
        this.store.dispatch(new DeleteToleranceAction());
    }
    onUpdatePlot(selectedPlot) {
        this.store.dispatch(new UpdatePlotAction(selectedPlot));
    }
    onUpdateAnswerSegment(answerSegment) {
        this.store.dispatch(new UpdateAnswerSegmentAction(answerSegment));
    }
    onOpenOrShowMathModal(isVisible) {
        this.showMathModal = isVisible;
    }
    ngOnDestroy() {
        this.airNumberLineSubscription$.unsubscribe();
    }
}
AirNumberLineComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirNumberLineComponent, deps: [{ token: i1.Store }, { token: SanitizeInputService }, { token: NumberLineValidationService }, { token: ConfigService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
AirNumberLineComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirNumberLineComponent, selector: "aa-air-number-line", inputs: { isAdvancedNumberLine: "isAdvancedNumberLine" }, viewQueries: [{ propertyName: "minVal", first: true, predicate: ["min"], descendants: true, read: ElementRef }, { propertyName: "maxVal", first: true, predicate: ["max"], descendants: true, read: ElementRef }, { propertyName: "tickVal", first: true, predicate: ["tickInterval"], descendants: true, read: ElementRef }, { propertyName: "labelVal", first: true, predicate: ["labelInterval"], descendants: true, read: ElementRef }], usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<ng-container *ngIf=\"airNumberLine$ | async as numberLine\">\n  <h2 class=\"line-setting\" translate>Line settings *</h2>\n\n  <div translate>\n    Changing the line settings may clear or crop your answers.\n  </div>\n\n  <div *ngIf=\"isAdvancedNumberLine\" translate>\n    Instructions: Enter a slash for the fraction.\n  </div>\n\n  <div class=\"settings_wrapper\" [class.has-error]=\"getTickRangeError()?.length\">\n    <div\n      *ngIf=\"isAdvancedNumberLine\"\n      class=\"label-radio-group\"\n    >\n      <fieldset class=\"hstack\">\n        <legend id=\"req-radio-label\">Label format</legend>\n        <div class=\"form-check me-3\">\n          <input\n            id=\"decimals\" type=\"radio\" class=\"form-check-input\"\n            name=\"showGraphAs\"\n            value=\"decimals\"\n            [checked]=\"numberLine.showGraphAs === 'decimals'\"\n            (change)=\"onLabelFormatChange('decimals')\"\n          />\n          <label class=\"form-check-label\" for=\"decimals\">Decimals</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"fraction\" type=\"radio\" class=\"form-check-input\"\n            name=\"showGraphAs\"\n            value=\"fraction\"\n            [checked]=\"numberLine.showGraphAs === 'fraction'\"\n            (change)=\"onLabelFormatChange('fraction')\"\n          />\n          <label class=\"form-check-label\" for=\"fraction\">Fraction</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <div class=\"row align-items-center\">\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLMin\"\n            translate\n          >\n            Minimum\n          </div>\n\n          <div class=\"col\">\n            <input \n            #min \n            [ngModel]=\"numberLine.min\" \n            type=\"text\" \n            (input)=\"onMinFocusOut()\"\n            (keydown)=\"sanitizeInput($event, true, false)\" \n            aria-labelledby=\"nLMin\"\n            />\n            <div *ngIf=\"getMinError()?.length\" class=\"custom-error\">\n              {{ getMinError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLMax\"\n            translate\n          >\n            Maximum\n          </div>\n\n          <div class=\"col\">\n            <input \n            #max \n            [ngModel]=\"numberLine.max\" \n            type=\"text\" \n            (input)=\"onMaxFocusOut()\"\n            (keydown)=\"sanitizeInput($event, true, false)\" \n            aria-labelledby=\"nLMax\" />\n            <div *ngIf=\"getMaxError()?.length\" class=\"custom-error\">\n              {{ getMaxError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n\n\n\n\n\n    <div class=\"row align-items-center\">\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLTickInterval\"\n            translate\n          >\n            Tick interval\n\n            <button\n              class=\"btn btn-sm\"\n              type=\"button\"\n              placement=\"right\"\n              aria-label=\"How often a tick mark should be displayed (positive integers and decimals up to the hundredth place).\"\n              ngbTooltip=\"How often a tick mark should be displayed (positive integers and decimals up to the hundredth place).\"\n            >\n              <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n            </button>\n          </div>\n\n          <div class=\"col\">\n            <input \n              #tickInterval \n              [ngModel]=\"numberLine.tickInterval\" \n              type=\"text\"\n              (input)=\"onTickIntervalFocusOut()\" \n              (keydown)=\"sanitizeInput($event, false, true)\"\n              aria-labelledby=\"nLTickInterval\"\n            />\n            <div *ngIf=\"getTickIntervalError()?.length\" class=\"custom-error\">\n              {{ getTickIntervalError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLLabelInterval\"\n            translate\n          >\n            Label interval\n\n            <button\n              class=\"btn btn-sm\"\n              type=\"button\"\n              placement=\"right\"\n              aria-label=\"How often a number label should be displayed (positive integers and decimals).\"\n              ngbTooltip=\"How often a number label should be displayed (positive integers and decimals).\"\n            >\n              <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n            </button>\n          </div>\n\n          <div class=\"col\">\n            <input \n              #labelInterval \n              [ngModel]=\"numberLine.labelInterval\" \n              type=\"text\"\n              (input)=\"onLabelIntervalFocusOut()\"\n              (keydown)=\"sanitizeInput($event, false, true)\" \n              aria-labelledby=\"nLLabelInterval\"\n            />\n            <div *ngIf=\"getLabelIntervalError()?.length\" class=\"custom-error\">\n              {{ getLabelIntervalError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n\n    <div\n      class=\"snap-points-radio-group\"\n      *ngIf=\"isAdvancedNumberLine\"\n    >\n      <fieldset>\n        <legend id=\"req-radio-snap-points\" translate>Point Options</legend>\n        <div class=\"form-check me-3\">\n          <input\n            id=\"snapToTickMarks\" type=\"radio\" class=\"form-check-input allow-snap\"\n            name=\"allowSnapToTick\"\n            [checked]=\"numberLine.allowSnapToTick === true\"\n            (change)=\"onChangeSnap(true)\"\n          />\n          <label class=\"form-check-label\" for=\"snapToTickMarks\">Snap to tick marks</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"allowPointBetweenTickMarks\" type=\"radio\" class=\"form-check-input allow-points-between-ticks\"\n            name=\"allowSnapToTick\"\n            [checked]=\"numberLine.allowSnapToTick === false\"\n            (change)=\"onChangeSnap(false)\"\n          />\n          <label class=\"form-check-label\" for=\"allowPointBetweenTickMarks\">Allow points between tick marks</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <div *ngIf=\"!numberLine.allowSnapToTick\" class=\"tolerance-container\">\n      <div class=\"form-check\">\n        <input\n          class=\"form-check-input mt-2 me-2\"\n          id=\"tolerance\"\n          type=\"checkbox\"\n          [checked]=\"isToleranceEnabled\"\n          (change)=\"onChangeToleranceCheckbox($event.target['checked'])\"\n        />\n      </div>\n      <p>\n        <span>\n          Enable answer tolerance of\n        </span>\n        <input\n          type=\"number\"\n          [attr.min]=\"0\"\n          [attr.max]=\"10\"\n          [value]=\"numberLine.tolerance?.numberOfPointsBelow\"\n          [disabled]=\"!isToleranceEnabled\"\n          class=\"tolerance-below-input\"\n          (input)=\"onUpdateToleranceBelowInput($event.target)\"\n          aria-labelledby=\"NlToleranceOfPointsBelow\"\n        />\n        <span>\n          points below and\n        </span>\n        <input\n          type=\"number\"\n          [value]=\"numberLine.tolerance?.numberOfPointsAbove\"\n          [attr.min]=\"0\"\n          [attr.max]=\"10\"\n          [disabled]=\"!isToleranceEnabled\"\n          class=\"tolerance-above-input\"\n          aria-labelledby=\"NlToleranceOfPointsAbove\"\n          (input)=\"onUpdateToleranceAboveInput($event.target)\"\n        />\n       <span>\n        points above\n      </span>\n      </p>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getTickRangeError()\"></aa-inline-error>\n\n  <h2 class=\"answer-setting\" translate>Correct answer *</h2>\n  <div translate>A maximum of 10 points can be plotted.</div>\n  <div\n    class=\"graph-wrapper\"\n    [class.has-error]=\"getCorrectAnswerError()?.length\"\n  >\n    <div\n      class=\"label-radio-group\"\n      *ngIf=\"isAdvancedNumberLine\"\n    >\n      <fieldset class=\"hstack\">\n        <div class=\"form-check me-3\">\n          <input\n            id=\"addPoint\" type=\"radio\" class=\"form-check-input\"\n            name=\"showResponseModeAs\"\n            [checked]=\"numberLine.showResponseModeAs === 'point'\"\n            (change)=\"onResponseModeChange('point')\"\n          />\n          <label class=\"form-check-label\" for=\"addPoint\">Add points</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"addConnection\" type=\"radio\" class=\"form-check-input\"\n            name=\"showResponseModeAs\"\n            [checked]=\"numberLine.showResponseModeAs === 'connection'\"\n            (change)=\"onResponseModeChange('connection')\"\n          />\n          <label class=\"form-check-label\" for=\"addConnection\">Connect points / Add arrows</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <aa-line-graph\n      [minTicks]=\"numberLine.min\"\n      [maxTicks]=\"numberLine.max\"\n      [tickInterval]=\"numberLine.tickInterval\"\n      [labelInterval]=\"numberLine.labelInterval\"\n      [answers]=\"numberLine.answers\"\n      [showGraphAs]=\"numberLine.showGraphAs\"\n      [showResponseModeAs]=\"numberLine.showResponseModeAs\"\n      [allowSnapToTick]=\"numberLine.allowSnapToTick\"\n      [isAdvancedNumberLine]=\"numberLine.isAdvancedNumberLine\"\n      [divisionsBetweenTicks]=\"numberLine.divisionsBetweenTicks\"\n      [showMathModal]=\"showMathModal\"\n      (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n      (updateInternalTicks)=\"onUpdateInternalTicks($event)\"\n      (updatePlot)=\"onUpdatePlot($event)\"\n      (updateAnswerSegment)=\"onUpdateAnswerSegment($event)\"\n      (updateAnswerLabel)=\"onUpdateAnswerLabel($event)\"\n      (openOrShowMathModal)=\"onOpenOrShowMathModal($event)\"\n    >\n    </aa-line-graph>\n\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"onClearCorrectAnswers()\"\n      [disabled]=\"!numberLine.answers?.length\"\n      type=\"button\"\n      translate\n    >\n      Clear All\n    </button>\n  </div>\n\n  <aa-inline-error [errors]=\"getCorrectAnswerError()\"></aa-inline-error>\n</ng-container>\n", styles: [".info-alert{display:block;margin-top:25px}.graph-wrapper{border:1px solid #d2dcde;border-radius:5px;padding:0 25px;position:relative;display:inline-flex;flex-wrap:wrap;align-items:center;margin-top:10px}.graph-wrapper.has-error{border:1px solid #b0181e}.settings_wrapper{border:1px solid #d2dcde;border-radius:5px;margin-top:22px;padding:1rem}.settings_wrapper.has-error{border:1px solid #b0181e}.settings_wrapper input[type=text]{margin-left:-22px;border-radius:5px;height:34px;width:80px;padding:10px;border:1px solid #007c91}.settings_wrapper .settings-unit{align-items:center}.settings_wrapper .settings-unit .settings-unit-label{font-weight:700;display:flex;justify-content:space-between;align-items:center}.settings_wrapper .settings-unit mhe-input{width:300px}.error-message{color:#b0181e}.setting-button{background:transparent;border:none;height:20px;margin-right:20px;padding:0;position:relative;top:21px}.setting-button .dpg-icon-system-alert-info{height:16px;width:16px}.line-setting,.answer-setting{display:inline-block;margin-right:5px}.separator{position:relative;top:-14px}.label-radio-group{display:block;width:100%}.label-radio-group fieldset legend{width:auto;font-weight:700;padding-right:4rem}:host ::ng-deep label.ahe-ui-radio{padding:8px 8px 8px 36px}:host ::ng-deep label.ahe-ui-radio input[type=radio]{margin-left:-36px}.snap-points-radio-group{width:100%}.snap-points-radio-group legend{font-weight:700;padding:30px 0 10px}.snap-points-radio-group .radio-button{display:block}.snap-points-radio-group .form-control{display:inline}.tolerance-container{display:flex;align-items:flex-start}.tolerance-container p{display:flex;align-items:baseline}.tolerance-container p span:not(:first-child){margin:0 10px}.tolerance-container p span:first-child{margin-right:10px}.tolerance-container p mhe-input{margin-bottom:-15px}.graph-wrapper .label-radio-group{padding:10px 0;border-bottom:1px solid #d2dcde}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i6.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: i4$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: LineGraphComponent, selector: "aa-line-graph", inputs: ["minTicks", "maxTicks", "tickInterval", "labelInterval", "answers", "showGraphAs", "showResponseModeAs", "isAdvancedNumberLine", "divisionsBetweenTicks", "allowSnapToTick", "showMathModal"], outputs: ["updateCorrectAnswer", "updateAnswerLabel", "updateInternalTicks", "updateToleranceRange", "updateAnswerSegment", "updatePlot", "openOrShowMathModal"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirNumberLineComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-number-line', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n<ng-container *ngIf=\"airNumberLine$ | async as numberLine\">\n  <h2 class=\"line-setting\" translate>Line settings *</h2>\n\n  <div translate>\n    Changing the line settings may clear or crop your answers.\n  </div>\n\n  <div *ngIf=\"isAdvancedNumberLine\" translate>\n    Instructions: Enter a slash for the fraction.\n  </div>\n\n  <div class=\"settings_wrapper\" [class.has-error]=\"getTickRangeError()?.length\">\n    <div\n      *ngIf=\"isAdvancedNumberLine\"\n      class=\"label-radio-group\"\n    >\n      <fieldset class=\"hstack\">\n        <legend id=\"req-radio-label\">Label format</legend>\n        <div class=\"form-check me-3\">\n          <input\n            id=\"decimals\" type=\"radio\" class=\"form-check-input\"\n            name=\"showGraphAs\"\n            value=\"decimals\"\n            [checked]=\"numberLine.showGraphAs === 'decimals'\"\n            (change)=\"onLabelFormatChange('decimals')\"\n          />\n          <label class=\"form-check-label\" for=\"decimals\">Decimals</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"fraction\" type=\"radio\" class=\"form-check-input\"\n            name=\"showGraphAs\"\n            value=\"fraction\"\n            [checked]=\"numberLine.showGraphAs === 'fraction'\"\n            (change)=\"onLabelFormatChange('fraction')\"\n          />\n          <label class=\"form-check-label\" for=\"fraction\">Fraction</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <div class=\"row align-items-center\">\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLMin\"\n            translate\n          >\n            Minimum\n          </div>\n\n          <div class=\"col\">\n            <input \n            #min \n            [ngModel]=\"numberLine.min\" \n            type=\"text\" \n            (input)=\"onMinFocusOut()\"\n            (keydown)=\"sanitizeInput($event, true, false)\" \n            aria-labelledby=\"nLMin\"\n            />\n            <div *ngIf=\"getMinError()?.length\" class=\"custom-error\">\n              {{ getMinError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLMax\"\n            translate\n          >\n            Maximum\n          </div>\n\n          <div class=\"col\">\n            <input \n            #max \n            [ngModel]=\"numberLine.max\" \n            type=\"text\" \n            (input)=\"onMaxFocusOut()\"\n            (keydown)=\"sanitizeInput($event, true, false)\" \n            aria-labelledby=\"nLMax\" />\n            <div *ngIf=\"getMaxError()?.length\" class=\"custom-error\">\n              {{ getMaxError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n\n\n\n\n\n    <div class=\"row align-items-center\">\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLTickInterval\"\n            translate\n          >\n            Tick interval\n\n            <button\n              class=\"btn btn-sm\"\n              type=\"button\"\n              placement=\"right\"\n              aria-label=\"How often a tick mark should be displayed (positive integers and decimals up to the hundredth place).\"\n              ngbTooltip=\"How often a tick mark should be displayed (positive integers and decimals up to the hundredth place).\"\n            >\n              <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n            </button>\n          </div>\n\n          <div class=\"col\">\n            <input \n              #tickInterval \n              [ngModel]=\"numberLine.tickInterval\" \n              type=\"text\"\n              (input)=\"onTickIntervalFocusOut()\" \n              (keydown)=\"sanitizeInput($event, false, true)\"\n              aria-labelledby=\"nLTickInterval\"\n            />\n            <div *ngIf=\"getTickIntervalError()?.length\" class=\"custom-error\">\n              {{ getTickIntervalError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n      <div class=\"col-sm-6 col-md-5 col-lg-4\">\n        <div class=\"settings-unit row\">\n          <div\n            class=\"settings-unit-label col-form-label col\"\n            id=\"nLLabelInterval\"\n            translate\n          >\n            Label interval\n\n            <button\n              class=\"btn btn-sm\"\n              type=\"button\"\n              placement=\"right\"\n              aria-label=\"How often a number label should be displayed (positive integers and decimals).\"\n              ngbTooltip=\"How often a number label should be displayed (positive integers and decimals).\"\n            >\n              <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n            </button>\n          </div>\n\n          <div class=\"col\">\n            <input \n              #labelInterval \n              [ngModel]=\"numberLine.labelInterval\" \n              type=\"text\"\n              (input)=\"onLabelIntervalFocusOut()\"\n              (keydown)=\"sanitizeInput($event, false, true)\" \n              aria-labelledby=\"nLLabelInterval\"\n            />\n            <div *ngIf=\"getLabelIntervalError()?.length\" class=\"custom-error\">\n              {{ getLabelIntervalError() }}\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n\n    <div\n      class=\"snap-points-radio-group\"\n      *ngIf=\"isAdvancedNumberLine\"\n    >\n      <fieldset>\n        <legend id=\"req-radio-snap-points\" translate>Point Options</legend>\n        <div class=\"form-check me-3\">\n          <input\n            id=\"snapToTickMarks\" type=\"radio\" class=\"form-check-input allow-snap\"\n            name=\"allowSnapToTick\"\n            [checked]=\"numberLine.allowSnapToTick === true\"\n            (change)=\"onChangeSnap(true)\"\n          />\n          <label class=\"form-check-label\" for=\"snapToTickMarks\">Snap to tick marks</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"allowPointBetweenTickMarks\" type=\"radio\" class=\"form-check-input allow-points-between-ticks\"\n            name=\"allowSnapToTick\"\n            [checked]=\"numberLine.allowSnapToTick === false\"\n            (change)=\"onChangeSnap(false)\"\n          />\n          <label class=\"form-check-label\" for=\"allowPointBetweenTickMarks\">Allow points between tick marks</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <div *ngIf=\"!numberLine.allowSnapToTick\" class=\"tolerance-container\">\n      <div class=\"form-check\">\n        <input\n          class=\"form-check-input mt-2 me-2\"\n          id=\"tolerance\"\n          type=\"checkbox\"\n          [checked]=\"isToleranceEnabled\"\n          (change)=\"onChangeToleranceCheckbox($event.target['checked'])\"\n        />\n      </div>\n      <p>\n        <span>\n          Enable answer tolerance of\n        </span>\n        <input\n          type=\"number\"\n          [attr.min]=\"0\"\n          [attr.max]=\"10\"\n          [value]=\"numberLine.tolerance?.numberOfPointsBelow\"\n          [disabled]=\"!isToleranceEnabled\"\n          class=\"tolerance-below-input\"\n          (input)=\"onUpdateToleranceBelowInput($event.target)\"\n          aria-labelledby=\"NlToleranceOfPointsBelow\"\n        />\n        <span>\n          points below and\n        </span>\n        <input\n          type=\"number\"\n          [value]=\"numberLine.tolerance?.numberOfPointsAbove\"\n          [attr.min]=\"0\"\n          [attr.max]=\"10\"\n          [disabled]=\"!isToleranceEnabled\"\n          class=\"tolerance-above-input\"\n          aria-labelledby=\"NlToleranceOfPointsAbove\"\n          (input)=\"onUpdateToleranceAboveInput($event.target)\"\n        />\n       <span>\n        points above\n      </span>\n      </p>\n    </div>\n  </div>\n\n  <aa-inline-error [errors]=\"getTickRangeError()\"></aa-inline-error>\n\n  <h2 class=\"answer-setting\" translate>Correct answer *</h2>\n  <div translate>A maximum of 10 points can be plotted.</div>\n  <div\n    class=\"graph-wrapper\"\n    [class.has-error]=\"getCorrectAnswerError()?.length\"\n  >\n    <div\n      class=\"label-radio-group\"\n      *ngIf=\"isAdvancedNumberLine\"\n    >\n      <fieldset class=\"hstack\">\n        <div class=\"form-check me-3\">\n          <input\n            id=\"addPoint\" type=\"radio\" class=\"form-check-input\"\n            name=\"showResponseModeAs\"\n            [checked]=\"numberLine.showResponseModeAs === 'point'\"\n            (change)=\"onResponseModeChange('point')\"\n          />\n          <label class=\"form-check-label\" for=\"addPoint\">Add points</label>\n        </div>\n        <div class=\"form-check\">\n          <input\n            id=\"addConnection\" type=\"radio\" class=\"form-check-input\"\n            name=\"showResponseModeAs\"\n            [checked]=\"numberLine.showResponseModeAs === 'connection'\"\n            (change)=\"onResponseModeChange('connection')\"\n          />\n          <label class=\"form-check-label\" for=\"addConnection\">Connect points / Add arrows</label>\n        </div>\n      </fieldset>\n    </div>\n\n    <aa-line-graph\n      [minTicks]=\"numberLine.min\"\n      [maxTicks]=\"numberLine.max\"\n      [tickInterval]=\"numberLine.tickInterval\"\n      [labelInterval]=\"numberLine.labelInterval\"\n      [answers]=\"numberLine.answers\"\n      [showGraphAs]=\"numberLine.showGraphAs\"\n      [showResponseModeAs]=\"numberLine.showResponseModeAs\"\n      [allowSnapToTick]=\"numberLine.allowSnapToTick\"\n      [isAdvancedNumberLine]=\"numberLine.isAdvancedNumberLine\"\n      [divisionsBetweenTicks]=\"numberLine.divisionsBetweenTicks\"\n      [showMathModal]=\"showMathModal\"\n      (updateCorrectAnswer)=\"onUpdateCorrectAnswer($event)\"\n      (updateInternalTicks)=\"onUpdateInternalTicks($event)\"\n      (updatePlot)=\"onUpdatePlot($event)\"\n      (updateAnswerSegment)=\"onUpdateAnswerSegment($event)\"\n      (updateAnswerLabel)=\"onUpdateAnswerLabel($event)\"\n      (openOrShowMathModal)=\"onOpenOrShowMathModal($event)\"\n    >\n    </aa-line-graph>\n\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"onClearCorrectAnswers()\"\n      [disabled]=\"!numberLine.answers?.length\"\n      type=\"button\"\n      translate\n    >\n      Clear All\n    </button>\n  </div>\n\n  <aa-inline-error [errors]=\"getCorrectAnswerError()\"></aa-inline-error>\n</ng-container>\n", styles: [".info-alert{display:block;margin-top:25px}.graph-wrapper{border:1px solid #d2dcde;border-radius:5px;padding:0 25px;position:relative;display:inline-flex;flex-wrap:wrap;align-items:center;margin-top:10px}.graph-wrapper.has-error{border:1px solid #b0181e}.settings_wrapper{border:1px solid #d2dcde;border-radius:5px;margin-top:22px;padding:1rem}.settings_wrapper.has-error{border:1px solid #b0181e}.settings_wrapper input[type=text]{margin-left:-22px;border-radius:5px;height:34px;width:80px;padding:10px;border:1px solid #007c91}.settings_wrapper .settings-unit{align-items:center}.settings_wrapper .settings-unit .settings-unit-label{font-weight:700;display:flex;justify-content:space-between;align-items:center}.settings_wrapper .settings-unit mhe-input{width:300px}.error-message{color:#b0181e}.setting-button{background:transparent;border:none;height:20px;margin-right:20px;padding:0;position:relative;top:21px}.setting-button .dpg-icon-system-alert-info{height:16px;width:16px}.line-setting,.answer-setting{display:inline-block;margin-right:5px}.separator{position:relative;top:-14px}.label-radio-group{display:block;width:100%}.label-radio-group fieldset legend{width:auto;font-weight:700;padding-right:4rem}:host ::ng-deep label.ahe-ui-radio{padding:8px 8px 8px 36px}:host ::ng-deep label.ahe-ui-radio input[type=radio]{margin-left:-36px}.snap-points-radio-group{width:100%}.snap-points-radio-group legend{font-weight:700;padding:30px 0 10px}.snap-points-radio-group .radio-button{display:block}.snap-points-radio-group .form-control{display:inline}.tolerance-container{display:flex;align-items:flex-start}.tolerance-container p{display:flex;align-items:baseline}.tolerance-container p span:not(:first-child){margin:0 10px}.tolerance-container p span:first-child{margin-right:10px}.tolerance-container p mhe-input{margin-bottom:-15px}.graph-wrapper .label-radio-group{padding:10px 0;border-bottom:1px solid #d2dcde}.custom-error{color:red;font-size:.875rem;margin-top:.25rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: SanitizeInputService }, { type: NumberLineValidationService }, { type: ConfigService }, { type: i0.Renderer2 }]; }, propDecorators: { isAdvancedNumberLine: [{
                type: Input
            }], minVal: [{
                type: ViewChild,
                args: ['min', { read: ElementRef, static: false }]
            }], maxVal: [{
                type: ViewChild,
                args: ['max', { read: ElementRef, static: false }]
            }], tickVal: [{
                type: ViewChild,
                args: ['tickInterval', { read: ElementRef, static: false }]
            }], labelVal: [{
                type: ViewChild,
                args: ['labelInterval', { read: ElementRef, static: false }]
            }] } });

var ItemTypeName;
(function (ItemTypeName) {
    ItemTypeName["ALGORITHMIC"] = "Algorithmic";
    ItemTypeName["AUDIO_RECORDING"] = "Audio Recording";
    ItemTypeName["BUCKETING"] = "Bucketing";
    ItemTypeName["CHOICE_MATRIX"] = "Choice Matrix";
    ItemTypeName["CLOZE"] = "Cloze";
    ItemTypeName["EQUATION_ENTRY"] = "Equation Entry";
    ItemTypeName["ESSAY"] = "Essay";
    ItemTypeName["EXTERNAL"] = "external";
    ItemTypeName["FILL_IN_THE_BLANK"] = "Fill-in-the-Blank";
    ItemTypeName["GRID_IN"] = "Grid-In";
    ItemTypeName["IMAGE_LABELING"] = "Image Labeling";
    ItemTypeName["MATCHING"] = "Matching";
    ItemTypeName["MULTIPLE_CHOICE"] = "Multiple Choice";
    ItemTypeName["MULTIPLE_CHOICE_MULTI_SELECT"] = "Multiple Choice, Multi-Select";
    ItemTypeName["MULTIPLE_CHOICE_AUDIO"] = "Multiple Choice Audio Only";
    ItemTypeName["NUMBER_LINE"] = "Number Line";
    ItemTypeName["ORDERING"] = "Ordering";
    ItemTypeName["SELECT_TEXT"] = "Select Text";
    ItemTypeName["SHORT_ANSWER"] = "Short Answer";
    ItemTypeName["TRUE_FALSE"] = "True/False";
    ItemTypeName["INSTRUCTION"] = "Instructions";
    ItemTypeName["FILE_UPLOAD"] = "File Upload";
    ItemTypeName["GRAPHING"] = "Graphing";
    ItemTypeName["DRAWING"] = "Drawing";
    ItemTypeName["COORDINATE_GRAPH"] = "Coordinate Grapher";
    ItemTypeName["TABLE_ENTRY"] = "Table Entry";
    ItemTypeName["HOTSPOT"] = "Hotspot";
})(ItemTypeName || (ItemTypeName = {}));

const itemTypes = [
    {
        viewValue: ItemTypeName.MULTIPLE_CHOICE,
        value: ItemType.MULTIPLE_CHOICE,
        bentoValue: 'multiple-choice',
        reducerValue: 'AirMultipleChoice',
    },
    {
        viewValue: ItemTypeName.FILL_IN_THE_BLANK,
        value: ItemType.FILL_IN_THE_BLANK,
        bentoValue: 'fill-in-the-blank',
        reducerValue: 'AirFib',
    },
    {
        viewValue: ItemTypeName.FILE_UPLOAD,
        value: ItemType.FILE_UPLOAD,
        bentoValue: 'file-upload',
        reducerValue: 'AirFileUpload',
    },
    {
        viewValue: ItemTypeName.TRUE_FALSE,
        value: ItemType.TRUE_FALSE,
        bentoValue: 'true-false',
        reducerValue: 'AirTrueFalse',
    },
    {
        viewValue: ItemTypeName.SHORT_ANSWER,
        value: ItemType.SHORT_ANSWER,
        bentoValue: 'short-answer',
        reducerValue: 'AirShortAnswer',
    },
    {
        viewValue: ItemTypeName.ESSAY,
        value: ItemType.ESSAY,
        bentoValue: 'essay',
        reducerValue: 'AirEssay',
    },
    {
        viewValue: ItemTypeName.MULTIPLE_CHOICE_MULTI_SELECT,
        value: ItemType.MULTIPLE_CHOICE_MULTI_SELECT,
        bentoValue: 'multiple-choice-multi-select',
        reducerValue: 'AirMultipleChoice',
    },
    {
        viewValue: ItemTypeName.MATCHING,
        value: ItemType.MATCHING,
        bentoValue: 'matching',
        reducerValue: 'AirMatching',
    },
    {
        viewValue: ItemTypeName.CLOZE,
        value: ItemType.CLOZE,
        bentoValue: 'cloze',
        reducerValue: 'AirCloze',
    },
    {
        viewValue: ItemTypeName.BUCKETING,
        value: ItemType.BUCKETING,
        bentoValue: 'bucketing',
        reducerValue: 'AirBucketing',
    },
    {
        viewValue: ItemTypeName.ORDERING,
        value: ItemType.ORDERING,
        bentoValue: 'ordering',
        reducerValue: 'AirOrdering',
    },
    {
        viewValue: ItemTypeName.EQUATION_ENTRY,
        value: ItemType.EQUATION_ENTRY,
        bentoValue: 'equation-entry',
        reducerValue: 'AirEquationEntry',
    },
    {
        viewValue: ItemTypeName.IMAGE_LABELING,
        value: ItemType.IMAGE_LABELING,
        bentoValue: 'image-labels',
        reducerValue: 'AirImageLabeling',
    },
    {
        viewValue: ItemTypeName.SELECT_TEXT,
        value: ItemType.SELECT_TEXT,
        bentoValue: 'select-text',
        reducerValue: 'AirSelectText',
    },
    {
        viewValue: ItemTypeName.CHOICE_MATRIX,
        value: ItemType.CHOICE_MATRIX,
        bentoValue: 'choice-matrix',
        reducerValue: 'AirChoiceMatrix',
    },
    {
        viewValue: ItemTypeName.GRAPHING,
        value: ItemType.GRAPHING,
        bentoValue: 'graphing',
    },
    {
        viewValue: ItemTypeName.NUMBER_LINE,
        value: ItemType.NUMBER_LINE,
        bentoValue: 'number-line',
        reducerValue: 'AirNumberLine',
    },
    {
        viewValue: ItemTypeName.ALGORITHMIC,
        value: ItemType.ALGORITHMIC,
        bentoValue: 'algorithmic',
        reducerValue: 'AirAlgorithmic',
    },
    {
        viewValue: ItemTypeName.INSTRUCTION,
        value: ItemType.INSTRUCTION,
        bentoValue: 'instruction',
        reducerValue: 'AirInstructionItem',
    },
    {
        viewValue: ItemTypeName.GRID_IN,
        value: ItemType.GRID_IN,
        bentoValue: 'grid_in',
        reducerValue: 'AirGridIn',
    },
    {
        viewValue: ItemTypeName.AUDIO_RECORDING,
        value: ItemType.AUDIO_RECORDING,
        bentoValue: 'audio_recording',
        reducerValue: 'AirAudioRecording',
    },
    {
        viewValue: ItemTypeName.DRAWING,
        value: ItemType.DRAWING,
        bentoValue: 'drawing',
        reducerValue: 'AirDrawing',
    },
    {
        viewValue: ItemTypeName.COORDINATE_GRAPH,
        value: ItemType.COORDINATE_GRAPH,
        bentoValue: 'coordinate_grapher',
        reducerValue: 'CoordinateGrapher',
    },
    {
        viewValue: ItemTypeName.TABLE_ENTRY,
        value: ItemType.TABLE_ENTRY,
        bentoValue: 'table_entry',
        reducerValue: 'AirTableEntry',
    },
    {
        viewValue: ItemTypeName.HOTSPOT,
        value: ItemType.HOTSPOT,
        bentoValue: 'hotspot',
        reducerValue: 'AirHotspot',
    },
    {
        viewValue: ItemTypeName.MULTIPLE_CHOICE_AUDIO,
        value: ItemType.MULTIPLE_CHOICE_AUDIO,
        bentoValue: 'multiple_choice_aduio',
        reducerValue: 'AirMultipleChoiceAudio',
    },
];
const bankTypes = [
    { value: 'bank', viewValue: 'Bank' },
    { value: 'item', viewValue: 'Item' },
    { value: 'assessment', viewValue: 'Assessment' },
    { value: 'passage', viewValue: 'Passage' },
];
const assessmentTypes = [
    { name: 'Quiz', value: 'quiz' },
    { name: 'Exam', value: 'exam' },
    { name: 'Practice', value: 'practice' },
    { name: 'Homework', value: 'homework' },
];
const statusTypes = [
    { name: 'Unpublished', value: 'not_published' },
    { name: 'Published', value: 'published' },
];
const externalTypes = [{ name: 'AHE Algo', value: 'AHEAlgo' }];
const dependentSocringTypes = [
    { viewValue: 'full', value: 'full' },
    { viewValue: 'partial', value: 'partial' },
];
const itemKeys = itemTypes.map((itemType) => itemType.viewValue);
const itemValues = itemTypes.map((itemType) => itemType.value);
const bentoItemValues = itemTypes.map((itemType) => itemType.bentoValue);
const bankKeys = bankTypes.map((bankType) => bankType.viewValue);
const bankValues = bankTypes.map((bankType) => bankType.value);
const statusKeys = statusTypes.map((statusType) => statusType.name);
const statusValues = statusTypes.map((statusType) => statusType.value);
const externalKeys = externalTypes.map((externalType) => externalType.name);
const externalValues = externalTypes.map((externalType) => externalType.value);
const assessmentKeys = assessmentTypes.map((assessmentType) => assessmentType.name);
const assessmentValues = assessmentTypes.map((assessmentType) => assessmentType.value);
const dependentSocringKeys = dependentSocringTypes.map((dependentScoringType) => dependentScoringType.viewValue);
const dependentSocringValues = dependentSocringTypes.map((dependentScoringType) => dependentScoringType.value);

class DrawingValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirDrawing$ = this.store.select(getPureAirDrawing);
    }
    validate() {
        let isValid;
        this.pureAirDrawing$.pipe(take(1)).subscribe((airDrawing) => {
            const promptError = !!this.promptValidationService.validatePrompt(airDrawing.prompt);
            isValid = !promptError;
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
}
DrawingValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DrawingValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
DrawingValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DrawingValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DrawingValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class GridInValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirGridIn$ = this.store.select(getPureAirGridIn);
    }
    validate() {
        let isValid;
        this.pureAirGridIn$.pipe(take(1)).subscribe((airGridIn) => {
            const promptError = !!this.promptValidationService.validatePrompt(airGridIn.prompt);
            const maxBlankError = !!this.promptValidationService.validateMaxBlankCount(airGridIn.tooManyAnswers);
            const minBlankError = !!this.promptValidationService.validateMinBlankCount(airGridIn.prompt);
            isValid = !(promptError || maxBlankError || minBlankError);
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
}
GridInValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GridInValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
GridInValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GridInValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GridInValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class InstructionValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirInstruction$ = this.store.select(getPureAirInstructionItem);
    }
    validate() {
        let isValid;
        this.pureAirInstruction$.pipe(take(1)).subscribe((airInstruction) => {
            isValid = !this.promptValidationService.validatePrompt(airInstruction.prompt);
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
}
InstructionValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InstructionValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
InstructionValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InstructionValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: InstructionValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class CoordinateGraphValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureCoordinateGraph$ = this.store.select(getPureCoordinateGrapher);
    }
    validate() {
        let isValid;
        this.pureCoordinateGraph$.pipe(take(1)).subscribe((airCoordinateGraph) => {
            const promptError = !!this.promptValidationService.validatePrompt(airCoordinateGraph.prompt);
            const answerError = !!this.validateAnswers(airCoordinateGraph.answers);
            isValid = !(promptError || answerError);
        });
        return isValid;
    }
    validateAnswers(answers) {
        this.answerError = '';
        if (!answers || !Object.keys(answers).length || (answers && answers.models && !answers.models.length)) {
            this.answerError = E_CG_CORRECT_ANSWER;
        }
        return this.answerError;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
    getAnswerError() {
        return this.answerError ? [this.answerError] : [];
    }
}
CoordinateGraphValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CoordinateGraphValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
CoordinateGraphValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CoordinateGraphValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CoordinateGraphValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class FileUploadValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirFileUpload$ = this.store.select(getPureAirFileUpload);
    }
    validate() {
        let isValid;
        this.pureAirFileUpload$.pipe(take(1)).subscribe((airFileUpload) => {
            const promptError = !!this.promptValidationService.validatePrompt(airFileUpload.prompt);
            isValid = !promptError;
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
}
FileUploadValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
FileUploadValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class TableEntryValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirTableEntry$ = this.store.select(getPureAirTableEntry);
        this.answerErrors = '';
    }
    validate() {
        let isValid;
        this.pureAirTableEntry$.pipe(take(1)).subscribe((airTableEntry) => {
            const promptError = !!this.promptValidationService.validatePrompt(airTableEntry.prompt);
            const answerError = !!this.validateAnswers(airTableEntry.rows);
            isValid = !(promptError || answerError);
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.answerErrors = '';
    }
    getAnswerErrors() {
        const error = this.answerErrors ? [this.answerErrors] : [];
        return error;
    }
    validateAnswers(rows) {
        const emptyAnswers = ['', 'Header', 'Placeholder Text'];
        const hasAnswersInCells = rows.find((row) => {
            return row.cells.some((cell) => !emptyAnswers.includes(cell.text));
        });
        this.answerErrors = hasAnswersInCells ? '' : E_TE_EMPTY_TABLE;
        return this.answerErrors;
    }
}
TableEntryValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TableEntryValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
TableEntryValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TableEntryValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TableEntryValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

function createStrategyKey(shape1, shape2) {
    return `${shape1}_${shape2}`;
}
function transformPolygonDataToD3Points(data) {
    return [data.x, data.y];
}
function transformRectangleToPolygon(data) {
    const shapeData = data.shapeData;
    const tShapeData = [
        { x: shapeData.x, y: shapeData.y },
        { x: shapeData.x + shapeData.width, y: shapeData.y },
        { x: shapeData.x + shapeData.width, y: shapeData.y + shapeData.length },
        { x: shapeData.x, y: shapeData.y + shapeData.length },
    ];
    return Object.assign({}, data, { type: ShapeTypeEnum.POLYGON, shapeData: tShapeData });
}

function doesEllipseIntersectPolygon(ellipse, polygon) {
    const ellipseData = ellipse.shapeData;
    const polygonData = polygon.shapeData;
    const cx = ellipseData.x;
    const cy = ellipseData.y;
    const rx = ellipseData.radiusX;
    const ry = ellipseData.radiusY;
    for (let i = 0; i < polygonData.length; i++) {
        const p1 = polygonData[i];
        const p2 = polygonData[(i + 1) % polygonData.length];
        const x1 = p1.x;
        const y1 = p1.y;
        const x2 = p2.x;
        const y2 = p2.y;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const A = dx ** 2 / rx ** 2 + dy ** 2 / ry ** 2;
        const B = 2 * ((dx * (x1 - cx)) / rx ** 2 + (dy * (y1 - cy)) / ry ** 2);
        const C = (x1 - cx) ** 2 / rx ** 2 + (y1 - cy) ** 2 / ry ** 2 - 1;
        const discriminant = B * B - 4 * A * C;
        if (discriminant >= 0) {
            // There's a line intersection, check the segment
            const t1 = (-B + Math.sqrt(discriminant)) / (2 * A);
            const t2 = (-B - Math.sqrt(discriminant)) / (2 * A);
            // Check if the intersection points lie within the bounds of the line segment
            if ((0 <= t1 && t1 <= 1) || (0 <= t2 && t2 <= 1)) {
                return true;
            }
        }
    }
    return false;
}
function doesPolygonIntersectPolygon(subject, target) {
    let isIntersecting = false;
    for (let i = 0; i < subject.shapeData.length; i++) {
        for (let j = 0; j < target.shapeData.length; j++) {
            isIntersecting = lineIntersect(subject.shapeData[i], subject.shapeData[(i + 1) % subject.shapeData.length], target.shapeData[j], target.shapeData[(j + 1) % target.shapeData.length]);
            if (isIntersecting) {
                break;
            }
        }
        if (isIntersecting) {
            break;
        }
    }
    return isIntersecting;
}
function lineIntersect(a, b, c, d) {
    const denominator1 = b.x - a.x;
    const denominator2 = d.x - c.x;
    if (denominator1 === 0 && denominator2 === 0) {
        // Both lines are vertical and parallel, so no intersection
        return false;
    }
    let x, y;
    if (denominator1 === 0) {
        // Line 1 is vertical
        const m2 = (d.y - c.y) / denominator2;
        const b2 = c.y - m2 * c.x;
        x = a.x;
        y = m2 * x + b2;
    }
    else if (denominator2 === 0) {
        // Line 2 is vertical
        const m1 = (b.y - a.y) / denominator1;
        const b1 = a.y - m1 * a.x;
        x = c.x;
        y = m1 * x + b1;
    }
    else {
        // General case
        const m1 = (b.y - a.y) / denominator1;
        const b1 = a.y - m1 * a.x;
        const m2 = (d.y - c.y) / denominator2;
        const b2 = c.y - m2 * c.x;
        // Check if lines are parallel
        if (m1 === m2) {
            return false;
        }
        // Calculate intersection point
        x = (b2 - b1) / (m1 - m2);
        y = m1 * x + b1;
    }
    // Check if intersection is within both line segments
    return (Math.min(a.x, b.x) <= x &&
        x <= Math.max(a.x, b.x) &&
        Math.min(a.y, b.y) <= y &&
        y <= Math.max(a.y, b.y) &&
        Math.min(c.x, d.x) <= x &&
        x <= Math.max(c.x, d.x) &&
        Math.min(c.y, d.y) <= y &&
        y <= Math.max(c.y, d.y));
}

class IntersectPolygonPolygon {
    intersects(subject, target) {
        return doesPolygonIntersectPolygon(subject, target);
    }
}
class IntersectPolygonRectangle {
    intersects(subject, target) {
        const tTarget = transformRectangleToPolygon(target);
        return doesPolygonIntersectPolygon(subject, tTarget);
    }
}
class IntersectPolygonEllipse {
    intersects(subject, target) {
        return doesEllipseIntersectPolygon(target, subject);
    }
}

class IntersectEllipseEllipse {
    intersects(subject, target) {
        const subjectData = subject.shapeData;
        const targetData = target.shapeData;
        const dx = targetData.x - subjectData.x;
        const dy = targetData.y - subjectData.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < subjectData.radiusX + targetData.radiusX && distance < subjectData.radiusY + targetData.radiusY;
    }
}
class IntersectEllipsePolygon {
    intersects(subject, target) {
        return doesEllipseIntersectPolygon(subject, target);
    }
}
class IntersectEllipseRectangle {
    intersects(subject, target) {
        const tTarget = transformRectangleToPolygon(target);
        return doesEllipseIntersectPolygon(subject, tTarget);
    }
}

class IntersectRectangleRectangle {
    intersects(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        const tTarget = transformRectangleToPolygon(target);
        return doesPolygonIntersectPolygon(tSubject, tTarget);
    }
}
class IntersectRectanglePolygon {
    intersects(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        return doesPolygonIntersectPolygon(tSubject, target);
    }
}
class IntersectRectangleEllipse {
    intersects(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        return doesEllipseIntersectPolygon(target, tSubject);
    }
}

const strategies$1 = new Map();
strategies$1.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.POLYGON), new IntersectPolygonPolygon());
strategies$1.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.RECTANGLE), new IntersectPolygonRectangle());
strategies$1.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.ELLIPSE), new IntersectPolygonEllipse());
strategies$1.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.POLYGON), new IntersectRectanglePolygon());
strategies$1.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.RECTANGLE), new IntersectRectangleRectangle());
strategies$1.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.ELLIPSE), new IntersectRectangleEllipse());
strategies$1.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.POLYGON), new IntersectEllipsePolygon());
strategies$1.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.RECTANGLE), new IntersectEllipseRectangle());
strategies$1.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.ELLIPSE), new IntersectEllipseEllipse());
function isIntersecting(subject, target) {
    const strategyKey = createStrategyKey(subject.shape, target.shape);
    const strategy = strategies$1.get(strategyKey);
    return strategy ? strategy.intersects(subject, target) : false;
}

function doesEllipseOverlapPolygon(subject, target) {
    const ellipse = subject.shapeData;
    for (const vertex of target.shapeData) {
        if (!isPointInsideEllipse(vertex, ellipse)) {
            return false;
        }
    }
    return true;
}
function doesPolygonOverlapPolygon(subject, target) {
    const subjectPoints = subject.shapeData.map(transformPolygonDataToD3Points);
    const targetPoints = target.shapeData.map(transformPolygonDataToD3Points);
    let isOverlapping = false;
    for (const targetPoint of targetPoints) {
        isOverlapping = d3.polygonContains(subjectPoints, targetPoint);
        if (!isOverlapping) {
            return false;
        }
    }
    return isOverlapping;
}
function doesPolygonOverlapEllipse(subject, target) {
    const polygonVertices = subject.shapeData;
    const ellipse = target.shapeData;
    const ellipseCenter = { x: ellipse.x, y: ellipse.y };
    const isOverlapping = false;
    // Check if the ellipse is inside the polygon
    if (!isPointInsidePolygon(ellipseCenter, polygonVertices)) {
        return false;
    }
    for (const vertex of polygonVertices) {
        if (isPointInsideEllipse(vertex, ellipse)) {
            return false;
        }
    }
    return true;
}
function isPointInsideEllipse(point, ellipse) {
    const dx = point.x - ellipse.x;
    const dy = point.y - ellipse.y;
    const a = ellipse.radiusX;
    const b = ellipse.radiusY;
    const distanceSquared = dx ** 2 / a ** 2 + dy ** 2 / b ** 2;
    return distanceSquared <= 1;
}
function isPointInsidePolygon(point, polygonVertices) {
    // Ray Casting algorithm
    let inside = false;
    for (let i = 0, j = polygonVertices.length - 1; i < polygonVertices.length; j = i++) {
        const xi = polygonVertices[i].x;
        const yi = polygonVertices[i].y;
        const xj = polygonVertices[j].x;
        const yj = polygonVertices[j].y;
        const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
        if (intersect) {
            inside = !inside;
        }
    }
    return inside;
}

class OverlapPolygonPolygon {
    overlaps(subject, target) {
        return doesPolygonOverlapPolygon(subject, target);
    }
}
class OverlapPolygonRectangle {
    overlaps(subject, target) {
        const tTarget = transformRectangleToPolygon(target);
        return doesPolygonOverlapPolygon(subject, tTarget);
    }
}
class OverlapPolygonEllipse {
    overlaps(subject, target) {
        return doesPolygonOverlapEllipse(subject, target);
    }
}

class OverlapEllipseEllipse {
    overlaps(subject, target) {
        const outerEllipse = subject.shapeData;
        const innerEllipse = target.shapeData;
        const dx = outerEllipse.x - innerEllipse.x;
        const dy = outerEllipse.y - innerEllipse.y;
        const distanceBetweenCenters = Math.sqrt(dx * dx + dy * dy);
        return (distanceBetweenCenters + innerEllipse.radiusX <= outerEllipse.radiusX &&
            distanceBetweenCenters + innerEllipse.radiusY <= outerEllipse.radiusY);
    }
}
class OverlapEllipsePolygon {
    overlaps(subject, target) {
        return doesEllipseOverlapPolygon(subject, target);
    }
}
class OverlapEllipseRectangle {
    overlaps(subject, target) {
        const tTarget = transformRectangleToPolygon(target);
        return doesEllipseOverlapPolygon(subject, tTarget);
    }
}

class OverlapRectangleRectangle {
    overlaps(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        const tTarget = transformRectangleToPolygon(target);
        return doesPolygonOverlapPolygon(tSubject, tTarget);
    }
}
class OverlapRectanglePolygon {
    overlaps(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        return doesPolygonOverlapPolygon(tSubject, target);
    }
}
class OverlapRectangleEllipse {
    overlaps(subject, target) {
        const tSubject = transformRectangleToPolygon(subject);
        return doesPolygonOverlapEllipse(tSubject, target);
    }
}

const strategies = new Map();
strategies.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.POLYGON), new OverlapPolygonPolygon());
strategies.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.RECTANGLE), new OverlapPolygonRectangle());
strategies.set(createStrategyKey(ShapeTypeEnum.POLYGON, ShapeTypeEnum.ELLIPSE), new OverlapPolygonEllipse());
strategies.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.POLYGON), new OverlapRectanglePolygon());
strategies.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.RECTANGLE), new OverlapRectangleRectangle());
strategies.set(createStrategyKey(ShapeTypeEnum.RECTANGLE, ShapeTypeEnum.ELLIPSE), new OverlapRectangleEllipse());
strategies.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.POLYGON), new OverlapEllipsePolygon());
strategies.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.RECTANGLE), new OverlapEllipseRectangle());
strategies.set(createStrategyKey(ShapeTypeEnum.ELLIPSE, ShapeTypeEnum.ELLIPSE), new OverlapEllipseEllipse());
function isOverlapping(subject, target) {
    const strategyKey = createStrategyKey(subject.shape, target.shape);
    const strategy = strategies.get(strategyKey);
    return strategy ? strategy.overlaps(subject, target) : false;
}

class HotspotValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirHotspot$ = this.store.select(getPureAirHotspot);
        this.answerErrors = '';
        this.shapeErrors = '';
        this.maxSelectionErrors = '';
        this.imageErrors = '';
    }
    validate() {
        let isValid = false;
        this.pureAirHotspot$.pipe(take(1)).subscribe((airHotspot) => {
            const promptError = !!this.promptValidationService.validatePrompt(airHotspot.prompt);
            const imageError = !!this.validateBackgroundImage(airHotspot.backgroundImage);
            const shapeError = !!this.validateShapes(airHotspot.shapes);
            const intersectingError = !!this.validateIntersectingShapes(airHotspot.shapes);
            const overlapError = this.validateOverlappingShapes(airHotspot.shapes);
            const answerError = !!this.validateAnswers(airHotspot.answers);
            const maxSelectionError = !!this.validateMaxSelection(airHotspot.maxSelections, airHotspot.shapes);
            isValid = !(promptError ||
                imageError ||
                shapeError ||
                answerError ||
                intersectingError ||
                overlapError ||
                maxSelectionError);
        });
        return isValid;
    }
    validateBackgroundImage(image) {
        this.imageErrors = image && image.url === '' ? E_NO_BACKGROUND_IMAGE : '';
        return this.imageErrors;
    }
    validateShapes(shapes) {
        this.shapeErrors = shapes && shapes.length < 2 ? E_MORE_THAN_ONE_SHAPE : '';
        return this.shapeErrors;
    }
    validateOverlappingShapes(shapes) {
        let isShapeOverlapping = false;
        for (let i = 0; i < shapes.length; i++) {
            for (let j = 0; j < shapes.length; j++) {
                if (i === j) {
                    continue;
                }
                isShapeOverlapping = isOverlapping(shapes[i], shapes[j]);
                if (isShapeOverlapping) {
                    this.shapeErrors = E_SHAPE_OVERLAP;
                    return true;
                }
            }
        }
        return false;
    }
    validateIntersectingShapes(shapes) {
        // TODO: Add counter -> through arrays
        let isShapeIntersecting = false;
        for (let i = 0; i < shapes.length - 1 && !isShapeIntersecting; i++) {
            for (let j = i + 1; j < shapes.length && !isShapeIntersecting; j++) {
                isShapeIntersecting = isIntersecting(shapes[i], shapes[j]);
                if (isShapeIntersecting) {
                    break;
                }
            }
            if (isShapeIntersecting) {
                break;
            }
        }
        this.shapeErrors = isShapeIntersecting ? E_SHAPE_INTERSECT : this.shapeErrors;
        return this.shapeErrors;
    }
    validateAnswers(answers) {
        this.answerErrors = answers && !answers.length ? E_NO_CORRECT_ANSWER_SELECTED : '';
        return this.answerErrors;
    }
    validateMaxSelection(selections, shapes) {
        this.maxSelectionErrors = selections > shapes?.length ? E_MAX_SELECTION : '';
        return this.maxSelectionErrors;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
        this.imageErrors = '';
        this.answerErrors = '';
        this.shapeErrors = '';
        this.maxSelectionErrors = '';
    }
    getImagesError() {
        return this.imageErrors ? [this.imageErrors] : [];
    }
    getShapesError() {
        return this.shapeErrors ? [this.shapeErrors] : [];
    }
    getAnswerError() {
        return this.answerErrors ? [this.answerErrors] : [];
    }
    getMaxSelectionError() {
        return this.maxSelectionErrors ? [this.maxSelectionErrors] : [];
    }
}
HotspotValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HotspotValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
HotspotValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HotspotValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: HotspotValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class MultipleChoiceAudioValidationService {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.pureAirMultipleChoiceAudio$ = this.store.select(getPureAirMultipleChoiceAudio);
        this.answerErrors = '';
        this.shapeErrors = '';
        this.maxSelectionErrors = '';
        this.imageErrors = '';
    }
    validate() {
        let isValid = false;
        this.pureAirMultipleChoiceAudio$.pipe(take(1)).subscribe((airMultipleChoiceAudio) => {
            const promptError = !!this.promptValidationService.validatePrompt(airMultipleChoiceAudio.prompt);
            isValid = !promptError;
        });
        return isValid;
    }
    resetErrors() {
        this.promptValidationService.resetErrors();
    }
}
MultipleChoiceAudioValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceAudioValidationService, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
MultipleChoiceAudioValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceAudioValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MultipleChoiceAudioValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; } });

class AirValidationService {
    constructor(audioValidationService, bucketingValidationService, choiceMatrixValidationService, clozeValidationService, drawingValidationService, equationEntryValidationService, essayValidationService, fitbValidationService, gridInValidationService, imageLabelingValidationService, instructionValidationService, matchingValidationService, multipleChoiceValidationService, numberLineValidationService, orderingValidationService, selectTextValidationService, shortAnswerValidationService, trueFalseValidationService, coordinateGraphValidationService, fileUploadValidationService, tableEntryValidationService, hotspotValidationService, multipleChoiceAudioValidationService, store) {
        this.audioValidationService = audioValidationService;
        this.bucketingValidationService = bucketingValidationService;
        this.choiceMatrixValidationService = choiceMatrixValidationService;
        this.clozeValidationService = clozeValidationService;
        this.drawingValidationService = drawingValidationService;
        this.equationEntryValidationService = equationEntryValidationService;
        this.essayValidationService = essayValidationService;
        this.fitbValidationService = fitbValidationService;
        this.gridInValidationService = gridInValidationService;
        this.imageLabelingValidationService = imageLabelingValidationService;
        this.instructionValidationService = instructionValidationService;
        this.matchingValidationService = matchingValidationService;
        this.multipleChoiceValidationService = multipleChoiceValidationService;
        this.numberLineValidationService = numberLineValidationService;
        this.orderingValidationService = orderingValidationService;
        this.selectTextValidationService = selectTextValidationService;
        this.shortAnswerValidationService = shortAnswerValidationService;
        this.trueFalseValidationService = trueFalseValidationService;
        this.coordinateGraphValidationService = coordinateGraphValidationService;
        this.fileUploadValidationService = fileUploadValidationService;
        this.tableEntryValidationService = tableEntryValidationService;
        this.hotspotValidationService = hotspotValidationService;
        this.multipleChoiceAudioValidationService = multipleChoiceAudioValidationService;
        this.store = store;
        this.validatorMap = [];
    }
    getValidatorMap() {
        return [
            { itemType: ItemType.AUDIO_RECORDING, service: this.audioValidationService },
            { itemType: ItemType.BUCKETING, service: this.bucketingValidationService },
            { itemType: ItemType.CHOICE_MATRIX, service: this.choiceMatrixValidationService },
            { itemType: ItemType.CLOZE, service: this.clozeValidationService },
            { itemType: ItemType.DRAWING, service: this.drawingValidationService },
            { itemType: ItemType.EQUATION_ENTRY, service: this.equationEntryValidationService },
            { itemType: ItemType.ESSAY, service: this.essayValidationService },
            { itemType: ItemType.FILL_IN_THE_BLANK, service: this.fitbValidationService },
            { itemType: ItemType.GRID_IN, service: this.gridInValidationService },
            { itemType: ItemType.IMAGE_LABELING, service: this.imageLabelingValidationService },
            { itemType: ItemType.INSTRUCTION, service: this.instructionValidationService },
            { itemType: ItemType.MATCHING, service: this.matchingValidationService },
            { itemType: ItemType.MULTIPLE_CHOICE, service: this.multipleChoiceValidationService },
            { itemType: ItemType.MULTIPLE_CHOICE_MULTI_SELECT, service: this.multipleChoiceValidationService },
            { itemType: ItemType.NUMBER_LINE, service: this.numberLineValidationService },
            { itemType: ItemType.ORDERING, service: this.orderingValidationService },
            { itemType: ItemType.SELECT_TEXT, service: this.selectTextValidationService },
            { itemType: ItemType.SHORT_ANSWER, service: this.shortAnswerValidationService },
            { itemType: ItemType.TRUE_FALSE, service: this.trueFalseValidationService },
            { itemType: ItemType.COORDINATE_GRAPH, service: this.coordinateGraphValidationService },
            { itemType: ItemType.FILE_UPLOAD, service: this.fileUploadValidationService },
            { itemType: ItemType.TABLE_ENTRY, service: this.tableEntryValidationService },
            { itemType: ItemType.HOTSPOT, service: this.hotspotValidationService },
            { itemType: ItemType.MULTIPLE_CHOICE_AUDIO, service: this.multipleChoiceAudioValidationService },
        ];
    }
    validateItem(itemType) {
        const validator = this.getValidatorMap().find((item) => item.itemType === itemType);
        const service = validator?.service;
        return service?.validate.apply(service);
    }
    resetErrors() {
        this.getValidatorMap().map((item) => {
            const service = item.service;
            item.service.resetErrors.apply(service);
        });
    }
}
AirValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirValidationService, deps: [{ token: AudioValidationService }, { token: BucketingValidationService }, { token: ChoiceMatrixValidationService }, { token: ClozeValidationService }, { token: DrawingValidationService }, { token: EquationEntryValidationService }, { token: EssayValidationService }, { token: FitbValidationService }, { token: GridInValidationService }, { token: ImageLabelingValidationService }, { token: InstructionValidationService }, { token: MatchingValidationService }, { token: MultipleChoiceValidationService }, { token: NumberLineValidationService }, { token: OrderingValidationService }, { token: SelectTextValidationService }, { token: ShortAnswerValidationService }, { token: TrueFalseValidationService }, { token: CoordinateGraphValidationService }, { token: FileUploadValidationService }, { token: TableEntryValidationService }, { token: HotspotValidationService }, { token: MultipleChoiceAudioValidationService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
AirValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AudioValidationService }, { type: BucketingValidationService }, { type: ChoiceMatrixValidationService }, { type: ClozeValidationService }, { type: DrawingValidationService }, { type: EquationEntryValidationService }, { type: EssayValidationService }, { type: FitbValidationService }, { type: GridInValidationService }, { type: ImageLabelingValidationService }, { type: InstructionValidationService }, { type: MatchingValidationService }, { type: MultipleChoiceValidationService }, { type: NumberLineValidationService }, { type: OrderingValidationService }, { type: SelectTextValidationService }, { type: ShortAnswerValidationService }, { type: TrueFalseValidationService }, { type: CoordinateGraphValidationService }, { type: FileUploadValidationService }, { type: TableEntryValidationService }, { type: HotspotValidationService }, { type: MultipleChoiceAudioValidationService }, { type: i1.Store }]; } });

class ProgressBarComponent {
    constructor() {
        this.percentage = '0';
        this.visible = true;
    }
    set content(value) {
        switch (value.state) {
            case ContentState.Uploading:
                this.visible = true;
                this.onFileUploadStart();
                break;
            case ContentState.Saved:
                this.percentage = '100';
                this.visible = false;
                break;
            default:
                this.visible = false;
        }
    }
    ngOnInit() {
        this.addProgress();
    }
    onFileUploadStart() {
        this.runSimulation();
    }
    runSimulation(count = 20, minimumInterval = 50, intervalThreshold = 500, simulateEnd = true) {
        this.percentage = '0';
        this.addProgress();
        this.visible = true;
        let delay = minimumInterval;
        const maxPercentage = simulateEnd ? Math.floor(Math.random() * (98 - 94) + 94) + 1 : 100;
        for (let i = 0; i < count; ++i) {
            setTimeout(() => {
                this.addProgress(10, maxPercentage);
            }, delay);
            delay += minimumInterval + Math.floor(Math.random() * intervalThreshold);
        }
        if (simulateEnd) {
            setTimeout(() => {
                this.percentage = maxPercentage.toString();
            }, delay);
        }
    }
    addProgress(maxThreshold = 10, maxPercentage = 100) {
        const currentPercentage = parseInt(this.percentage, 10);
        const amountToCompletion = maxPercentage - currentPercentage;
        const minimumIncrease = amountToCompletion === 1 ? 0 : amountToCompletion < maxThreshold ? 1 : 5;
        const maximumIncrease = amountToCompletion < maxThreshold ? amountToCompletion - 1 : maxThreshold;
        const amountToIncrease = Math.floor(Math.random() * (maximumIncrease - minimumIncrease + 1)) + minimumIncrease;
        this.percentage = (currentPercentage + amountToIncrease).toString();
    }
}
ProgressBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ProgressBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ProgressBarComponent, selector: "aa-progress-bar", inputs: { content: "content" }, viewQueries: [{ propertyName: "progress", first: true, predicate: ["progress"], descendants: true }], ngImport: i0, template: "<div *ngIf=\"visible\" class=\"progress-container\">\n  <div>\n    <span>Status: </span>\n    <span>{{ percentage }}%</span>\n  </div>\n  <div class=\"upload-progress-bar-container\">\n    <span\n      #progress\n      class=\"upload-progress-bar\"\n      [style.width.%]=\"percentage\"\n    ></span>\n  </div>\n</div>\n", styles: [".progress-container{display:flex;width:600px}.progress-container .upload-progress-bar-container{flex:1;height:20px;border:1px solid #c1cfd4;border-radius:20px;margin-left:20px}.progress-container .upload-progress-bar-container .upload-progress-bar{display:inline-block;height:100%;border-radius:20px;background-image:linear-gradient(to right,#60c1d1,#007c91)}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-progress-bar', template: "<div *ngIf=\"visible\" class=\"progress-container\">\n  <div>\n    <span>Status: </span>\n    <span>{{ percentage }}%</span>\n  </div>\n  <div class=\"upload-progress-bar-container\">\n    <span\n      #progress\n      class=\"upload-progress-bar\"\n      [style.width.%]=\"percentage\"\n    ></span>\n  </div>\n</div>\n", styles: [".progress-container{display:flex;width:600px}.progress-container .upload-progress-bar-container{flex:1;height:20px;border:1px solid #c1cfd4;border-radius:20px;margin-left:20px}.progress-container .upload-progress-bar-container .upload-progress-bar{display:inline-block;height:100%;border-radius:20px;background-image:linear-gradient(to right,#60c1d1,#007c91)}\n"] }]
        }], propDecorators: { content: [{
                type: Input
            }], progress: [{
                type: ViewChild,
                args: ['progress']
            }] } });

const FILE_ICONS_CLASS = {
    bok: 'dpg-icon-bok',
    csv: 'dpg-icon-csv',
    doc: 'dpg-icon-doc',
    docx: 'dpg-icon-doc',
    xls: 'dpg-icon-xls',
    xlsm: 'dpg-icon-xls',
    xlsx: 'dpg-icon-xls',
    xltm: 'dpg-icon-xls',
    xltx: 'dpg-icon-xls',
    gsp: 'dpg-icon-gsp',
    html: 'dpg-icon-html',
    bmp: 'dpg-icon-image',
    gif: 'dpg-icon-image',
    jpg: 'dpg-icon-image',
    jpeg: 'dpg-icon-image',
    png: 'dpg-icon-image',
    svg: 'dpg-icon-image',
    tiff: 'dpg-icon-image',
    lti: 'dpg-icon-lti',
    mp3: 'dpg-icon-mp3',
    pdf: 'dpg-icon-pdf',
    ppt: 'dpg-icon-ppt',
    pptx: 'dpg-icon-ppt',
    swf: 'dpg-icon-swfhaxbbq',
    avi: 'dpg-icon-video',
    flv: 'dpg-icon-video',
    mov: 'dpg-icon-video',
    mp4: 'dpg-icon-video',
    mpeg4: 'dpg-icon-video',
    qt: 'dpg-icon-video',
    wmv: 'dpg-icon-video',
    zip: 'dpg-icon-zip',
    default: 'dpg-icon-default-asset',
};
const GOOGLE_DOCS = {
    sourceId: 'google',
    thumbnailUrl: 'https://drive.google.com/thumbnail?authuser=0',
};
const MAX_FILE_COUNT = 9;

function getFileExtension(url) {
    if (url && typeof url === 'string') {
        const ext = url.split('.').pop() || '';
        return ext.toLowerCase();
    }
    else {
        return '';
    }
}
function getAttachmentIconFromUrl(url) {
    return getAttachmentIconFromExtension(getFileExtension(url));
}
function getAttachmentIconFromExtension(extension) {
    return FILE_ICONS_CLASS[extension] !== undefined ? FILE_ICONS_CLASS[extension] : FILE_ICONS_CLASS.default;
}

class FileUploadComponent {
    constructor(element) {
        this.element = element;
        this.url = '';
        this.fileUploadStart = new EventEmitter();
        this.error = new EventEmitter();
        this.contentChange = new EventEmitter();
        this.removeContent = new EventEmitter();
        this.ContentState = ContentState;
        this.showConfirmDeleteUpload = false;
        // 50 MB limitation in Core = 50 * 1024 * 1024
        this.maxFileSize = 52428800;
    }
    set content(value) {
        this._content = value;
    }
    get content() {
        return this._content;
    }
    get fileName() {
        if (this.content.data instanceof File) {
            return this.content.data.name;
        }
        else {
            return this.content.data.split('/').pop();
        }
    }
    fileChange(event) {
        this.files = event.target.files;
        this.saveContent();
    }
    saveContent(file) {
        const content = {
            type: ContentType.File,
            data: file ? file : this.files[0],
            state: ContentState.Uploading,
        };
        if (content.data instanceof File) {
            if (content.data.size >= this.maxFileSize) {
                this.showAlert('FILE_SIZE_ERROR');
                return;
            }
            if (!this.isFileExtensionAllowed(content.data.name)) {
                this.showAlert('EXTENSION_ERROR');
                return;
            }
        }
        else {
            for (let i = 0; i < content.data.length; ++i) {
                if (content.data.item(i).size >= this.maxFileSize) {
                    this.showAlert('FILE_SIZE_ERROR');
                    return;
                }
            }
        }
        this.contentChange.emit(content);
    }
    isFileExtensionAllowed(fileName) {
        let isFileAllowed = false;
        const regex = /(?:\.([^.]+))?$/;
        const extension = regex.exec(fileName);
        if (extension) {
            for (const ext of this.allowedFileExtensions) {
                if (ext === extension[0]) {
                    isFileAllowed = true;
                }
            }
        }
        return isFileAllowed;
    }
    removeFile() {
        this.showConfirmDeleteUpload = false;
        this.removeContent.emit(ContentType.File);
    }
    confirmDeleteUpload() {
        this.showConfirmDeleteUpload = true;
    }
    cancelDeleteUpload() {
        this.showConfirmDeleteUpload = false;
    }
    get fileIconClass() {
        return getAttachmentIconFromUrl(this.fileName);
    }
    selectFile() {
        this.fileInput.nativeElement.click();
    }
    dropped(files) {
        const droppedFile = files[0];
        if (droppedFile.fileEntry.isFile) {
            const fileEntry = droppedFile.fileEntry;
            fileEntry.file((fileToUpload) => {
                this.saveContent(fileToUpload);
            });
        }
    }
    fileOver(_) {
        const elementList = this.element.nativeElement.querySelectorAll('.drag-drop-container');
        elementList.forEach((element) => {
            element.className = 'drag-drop-container hover';
        });
    }
    fileLeave(_) {
        const elementList = this.element.nativeElement.querySelectorAll('.drag-drop-container');
        elementList.forEach((element) => {
            element.className = 'drag-drop-container';
        });
    }
    showAlert(errorMessage) {
        const alert = {
            type: AlertType.Error,
            message: errorMessage,
            id: v4(),
        };
        this.error.emit(alert);
    }
}
FileUploadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
FileUploadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: FileUploadComponent, selector: "aa-file-upload", inputs: { url: "url", allowedFileExtensions: "allowedFileExtensions", content: "content" }, outputs: { fileUploadStart: "fileUploadStart", error: "error", contentChange: "contentChange", removeContent: "removeContent" }, viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true }, { propertyName: "fileLabel", first: true, predicate: ["fileLabel"], descendants: true }, { propertyName: "confirmDelete", first: true, predicate: ["confirmDelete"], descendants: true }], ngImport: i0, template: "<div\n  *ngIf=\"content?.state === ContentState.Create\"\n  class=\"file-upload-container\"\n>\n  <h3>Upload File</h3>\n  <div>\n    <input\n      #fileInput\n      class=\"not-visible\"\n      type=\"file\"\n      (change)=\"fileChange($event)\"\n      placeholder=\"browse\"\n    />\n    <div #fileLabel></div>\n    <p>\n      Drag and drop a file or\n      <a\n        href=\"javascript:void(0)\"\n        class=\"ahe-ui-link browse\"\n        (click)=\"selectFile()\"\n      >\n        browse\n      </a>\n      to upload a file.\n    </p>\n\n  </div>\n\n  <ngx-file-drop\n    dropZoneLabel=\"Drop Zone\"\n    contentClassName=\"drag-drop-container\"\n    dropZoneClassName=\"\"\n    (onFileDrop)=\"dropped($event)\"\n    (onFileOver)=\"fileOver($event)\"\n    (onFileLeave)=\"fileLeave($event)\"\n  >\n  </ngx-file-drop>\n  <div>\n    <p><small>Max file size allowed: {{maxFileSize/1024/1024}} MB</small></p>\n    <p><small>Supported file formats for uploading:  {{allowedFileExtensions?.join(', ')}}</small></p>\n  </div>\n</div>\n\n<div class=\"saved-view\" *ngIf=\"content?.state === ContentState.Saved\">\n  <div class=\"file-container\">\n    <div class=\"file-content\">\n      <div class=\"details\">\n        <div class=\"icon\">\n          <i\n            class=\"file-icon dpg-icon {{ fileIconClass }}\"\n            aria-hidden=\"true\"\n          ></i>\n        </div>\n        <div class=\"file\">{{ fileName }}</div>\n      </div>\n      <div class=\"manage\">\n        <a class=\"ahe-ui-link\" href=\"{{ url }}\" target=\"_blank\">View</a>\n        <button\n          class=\"delete-button\"\n          #confirmDelete\n          aria-label=\"Delete Link\"\n          (click)=\"confirmDeleteUpload()\"\n        >\n          <i class=\"dpg-icon dpg-icon-trash\" aria-hidden=\"true\"></i>\n        </button>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"content?.state === ContentState.Edit\">Edit View</div>\n\n<air-modal\n  class=\"confirm-modal\"\n  [isOpen]=\"showConfirmDeleteUpload\"\n  confirmButtonText=\"Yes delete\"\n  (cancel)=\"cancelDeleteUpload()\"\n  (close)=\"cancelDeleteUpload()\"\n  (confirm)=\"removeFile()\"\n>\n  <p translate>Are you sure you want to delete this file?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".ahe-ui.form-group input.form-control{display:inline-block;width:500px}.file-upload-container{padding:1rem}.footer{padding:20px 0}.saved-view{max-width:700px}.ahe-ui-link{margin-right:5px;height:-moz-fit-content;height:fit-content}.ahe-ui-link.browse{margin-right:0;cursor:pointer}.visible{display:block}.not-visible{visibility:hidden;height:1px}.file-container{background:#eef1f2;padding:10px;border-radius:4px;border:1px solid #d1dbde}.file-container .file-content{display:flex;justify-content:space-between}.file-container .file-content .details{display:flex;align-items:center}.file-container .file-content .details .icon{margin-top:5px}.file-container .file-content .details .file{margin-left:10px;max-width:275px;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}.file-container .file-content .manage{display:flex}.file-container .file-content .manage .delete-button{border:transparent solid 1px;padding-top:6px}.file-container .file-content .manage .delete-button:hover{border-color:#265867;border-radius:5px}:host ::ng-deep .drag-drop-container{height:250px;width:100%;margin:20px 0;border:2px dashed #006a7c;border-radius:4px;background-color:#f0f3f4;text-align:center;padding:112px 0;cursor:pointer}:host ::ng-deep .drag-drop-container.hover{background-color:#d1dbde}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$2.NgxFileDropComponent, selector: "ngx-file-drop", inputs: ["accept", "directory", "multiple", "dropZoneLabel", "dropZoneClassName", "useDragEnter", "contentClassName", "showBrowseBtn", "browseBtnClassName", "browseBtnLabel", "disabled"], outputs: ["onFileDrop", "onFileOver", "onFileLeave"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-file-upload', template: "<div\n  *ngIf=\"content?.state === ContentState.Create\"\n  class=\"file-upload-container\"\n>\n  <h3>Upload File</h3>\n  <div>\n    <input\n      #fileInput\n      class=\"not-visible\"\n      type=\"file\"\n      (change)=\"fileChange($event)\"\n      placeholder=\"browse\"\n    />\n    <div #fileLabel></div>\n    <p>\n      Drag and drop a file or\n      <a\n        href=\"javascript:void(0)\"\n        class=\"ahe-ui-link browse\"\n        (click)=\"selectFile()\"\n      >\n        browse\n      </a>\n      to upload a file.\n    </p>\n\n  </div>\n\n  <ngx-file-drop\n    dropZoneLabel=\"Drop Zone\"\n    contentClassName=\"drag-drop-container\"\n    dropZoneClassName=\"\"\n    (onFileDrop)=\"dropped($event)\"\n    (onFileOver)=\"fileOver($event)\"\n    (onFileLeave)=\"fileLeave($event)\"\n  >\n  </ngx-file-drop>\n  <div>\n    <p><small>Max file size allowed: {{maxFileSize/1024/1024}} MB</small></p>\n    <p><small>Supported file formats for uploading:  {{allowedFileExtensions?.join(', ')}}</small></p>\n  </div>\n</div>\n\n<div class=\"saved-view\" *ngIf=\"content?.state === ContentState.Saved\">\n  <div class=\"file-container\">\n    <div class=\"file-content\">\n      <div class=\"details\">\n        <div class=\"icon\">\n          <i\n            class=\"file-icon dpg-icon {{ fileIconClass }}\"\n            aria-hidden=\"true\"\n          ></i>\n        </div>\n        <div class=\"file\">{{ fileName }}</div>\n      </div>\n      <div class=\"manage\">\n        <a class=\"ahe-ui-link\" href=\"{{ url }}\" target=\"_blank\">View</a>\n        <button\n          class=\"delete-button\"\n          #confirmDelete\n          aria-label=\"Delete Link\"\n          (click)=\"confirmDeleteUpload()\"\n        >\n          <i class=\"dpg-icon dpg-icon-trash\" aria-hidden=\"true\"></i>\n        </button>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"content?.state === ContentState.Edit\">Edit View</div>\n\n<air-modal\n  class=\"confirm-modal\"\n  [isOpen]=\"showConfirmDeleteUpload\"\n  confirmButtonText=\"Yes delete\"\n  (cancel)=\"cancelDeleteUpload()\"\n  (close)=\"cancelDeleteUpload()\"\n  (confirm)=\"removeFile()\"\n>\n  <p translate>Are you sure you want to delete this file?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".ahe-ui.form-group input.form-control{display:inline-block;width:500px}.file-upload-container{padding:1rem}.footer{padding:20px 0}.saved-view{max-width:700px}.ahe-ui-link{margin-right:5px;height:-moz-fit-content;height:fit-content}.ahe-ui-link.browse{margin-right:0;cursor:pointer}.visible{display:block}.not-visible{visibility:hidden;height:1px}.file-container{background:#eef1f2;padding:10px;border-radius:4px;border:1px solid #d1dbde}.file-container .file-content{display:flex;justify-content:space-between}.file-container .file-content .details{display:flex;align-items:center}.file-container .file-content .details .icon{margin-top:5px}.file-container .file-content .details .file{margin-left:10px;max-width:275px;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}.file-container .file-content .manage{display:flex}.file-container .file-content .manage .delete-button{border:transparent solid 1px;padding-top:6px}.file-container .file-content .manage .delete-button:hover{border-color:#265867;border-radius:5px}:host ::ng-deep .drag-drop-container{height:250px;width:100%;margin:20px 0;border:2px dashed #006a7c;border-radius:4px;background-color:#f0f3f4;text-align:center;padding:112px 0;cursor:pointer}:host ::ng-deep .drag-drop-container.hover{background-color:#d1dbde}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { fileInput: [{
                type: ViewChild,
                args: ['fileInput']
            }], fileLabel: [{
                type: ViewChild,
                args: ['fileLabel']
            }], confirmDelete: [{
                type: ViewChild,
                args: ['confirmDelete']
            }], url: [{
                type: Input
            }], allowedFileExtensions: [{
                type: Input
            }], content: [{
                type: Input
            }], fileUploadStart: [{
                type: Output
            }], error: [{
                type: Output
            }], contentChange: [{
                type: Output
            }], removeContent: [{
                type: Output
            }] } });

class TextEntryComponent {
    constructor(el) {
        this.el = el;
        this.contentChange = new EventEmitter();
        this.removeContent = new EventEmitter();
        this.editContent = new EventEmitter();
        this.ContentState = ContentState;
        this.showConfirmDeleteTextEntry = false;
        this.savedData = '';
        // The Angular sanitizer seems to remove the 'controls' attribute
        // from the audio element. Adding back in.
        this.observer = new MutationObserver(() => {
            const audioEl = this.el.nativeElement.querySelector('audio');
            if (audioEl) {
                audioEl.setAttribute('controls', 'controls');
            }
        });
        this.observer.observe(this.el.nativeElement, { childList: true });
    }
    setData(data) {
        this.content.data = data;
    }
    saveContent() {
        if (this.content.data === '') {
            this.delete();
            return;
        }
        this.contentChange.emit({
            ...this.content,
            state: ContentState.Uploading,
        });
    }
    edit(event) {
        event.preventDefault();
        this.editContent.emit(this.content);
        this.savedData = this.content.data;
    }
    cancel() {
        this.content = {
            ...this.content,
            data: this.savedData,
        };
        this.saveContent();
    }
    confirmDeleteTextEntry() {
        this.showConfirmDeleteTextEntry = true;
    }
    cancelDeleteTextEntry() {
        this.showConfirmDeleteTextEntry = false;
    }
    delete() {
        this.showConfirmDeleteTextEntry = false;
        this.removeContent.emit(ContentType.TextEntry);
    }
    ngOnDestroy() {
        this.observer.disconnect();
    }
}
TextEntryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TextEntryComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
TextEntryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: TextEntryComponent, selector: "aa-text-entry", inputs: { content: "content" }, outputs: { contentChange: "contentChange", removeContent: "removeContent", editContent: "editContent" }, ngImport: i0, template: "<div class=\"create-view\" *ngIf=\"content?.state === ContentState.Create\">\n  <div class=\"content\">\n    <div>\n      <h3 translate>Enter Text</h3>\n      <p translate>Enter your written content in the input field.</p>\n    </div>\n    <mhe-editor\n      [toggleRichTextMenuBar]=\"false\"\n      [elementPath]=\"false\"\n      [content]=\"content.data\"\n      [showCharacterLimit]=\"false\"\n      [useA11yTooltip]=\"true\"\n      [showResize]=\"false\"\n      (contentChange)=\"setData($event)\"\n    >\n    </mhe-editor>\n  </div>\n  <div class=\"footer\">\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"cancel()\"\n      data-automation-id=\"cancel-button\"\n    >\n      Cancel\n    </button>\n    <button\n      class=\"btn btn-primary ms-3\"\n      (click)=\"saveContent()\"\n      data-automation-id=\"save-button\"\n    >\n      Continue\n    </button>\n  </div>\n</div>\n\n<div class=\"saved-view\" *ngIf=\"content?.state === ContentState.Saved\">\n  <div class=\"header\">\n    <div class=\"title\">Text Entry</div>\n    <div class=\"toolbar\">\n      <button\n        aria-label=\"Edit Content\"\n        (click)=\"edit($event)\"\n        class=\"edit-button\"\n      >\n        <i class=\"dpg-icon dpg-icon-edit\" aria-hidden=\"true\"></i>\n      </button>\n      <button\n        aria-label=\"Delete Content\"\n        (click)=\"confirmDeleteTextEntry()\"\n        class=\"delete-button\"\n      >\n        <i class=\"dpg-icon dpg-icon-trash\" aria-hidden=\"true\"></i>\n      </button>\n    </div>\n  </div>\n  <div class=\"content\">\n    <div\n      assnMathjax\n      [innerHTML]=\"content.data\"\n      data-automation-id=\"text-entry-content\"\n    ></div>\n  </div>\n</div>\n\n<div class=\"edit-view\" *ngIf=\"content?.state === ContentState.Edit\">\n  <div class=\"header\">\n    <span class=\"title\">Text Entry </span>\n  </div>\n  <div class=\"content\">\n    <h2 translate>Edit Text</h2>\n    <p translate>Edit your written content in the input field.</p>\n    <mhe-editor\n      [toggleRichTextMenuBar]=\"false\"\n      [elementPath]=\"false\"\n      [content]=\"content.data\"\n      [showCharacterLimit]=\"false\"\n      [useA11yTooltip]=\"true\"\n      [showResize]=\"false\"\n      (contentChange)=\"setData($event)\"\n    >\n    </mhe-editor>\n  </div>\n  <div class=\"footer\">\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"cancel()\"\n      data-automation-id=\"cancel-button\"\n    >\n      Cancel\n    </button>\n    <button\n      class=\"btn btn-primary ms-3\"\n      (click)=\"saveContent()\"\n      data-automation-id=\"save-button\"\n    >\n      Continue\n    </button>\n  </div>\n</div>\n\n<air-modal\n  class=\"confirm-modal\"\n  [isOpen]=\"showConfirmDeleteTextEntry\"\n  confirmButtonText=\"Remove\"\n  (cancel)=\"cancelDeleteTextEntry()\"\n  (close)=\"cancelDeleteTextEntry()\"\n  (confirm)=\"delete()\"\n>\n  <p translate>Are you sure you want to delete this content?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".create-view{border:1px solid #d1dbde;max-width:650px;border-radius:5px}.create-view .content{min-height:250px!important}.saved-view,.edit-view{max-width:700px;border:1px solid #d1dbde;border-radius:5px;margin-bottom:10px;min-height:100px}.saved-view .header,.edit-view .header{display:flex;justify-content:space-between;background:#eef1f2;padding:8px;border-top-left-radius:4px;border-top-right-radius:4px;border-bottom:1px solid #d1dbde;height:50px}.saved-view .header .title,.edit-view .header .title{margin:5px 0 0 4px}.saved-view .header .toolbar,.edit-view .header .toolbar{display:flex}.saved-view .header .toolbar .delete-button,.saved-view .header .toolbar .edit-button,.edit-view .header .toolbar .delete-button,.edit-view .header .toolbar .edit-button{border:transparent solid 1px;padding-top:6px}.saved-view .header .toolbar .delete-button:hover,.saved-view .header .toolbar .edit-button:hover,.edit-view .header .toolbar .delete-button:hover,.edit-view .header .toolbar .edit-button:hover{border-color:#265867;border-radius:5px}.edit-view .toolbar{margin-top:6px}.edit-button{margin:0 8px}.delete-button{margin-right:3px}.saved-view .content{min-height:auto;overflow-y:auto}.content{padding:1rem}.footer{padding:20px}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TextEntryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-text-entry', template: "<div class=\"create-view\" *ngIf=\"content?.state === ContentState.Create\">\n  <div class=\"content\">\n    <div>\n      <h3 translate>Enter Text</h3>\n      <p translate>Enter your written content in the input field.</p>\n    </div>\n    <mhe-editor\n      [toggleRichTextMenuBar]=\"false\"\n      [elementPath]=\"false\"\n      [content]=\"content.data\"\n      [showCharacterLimit]=\"false\"\n      [useA11yTooltip]=\"true\"\n      [showResize]=\"false\"\n      (contentChange)=\"setData($event)\"\n    >\n    </mhe-editor>\n  </div>\n  <div class=\"footer\">\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"cancel()\"\n      data-automation-id=\"cancel-button\"\n    >\n      Cancel\n    </button>\n    <button\n      class=\"btn btn-primary ms-3\"\n      (click)=\"saveContent()\"\n      data-automation-id=\"save-button\"\n    >\n      Continue\n    </button>\n  </div>\n</div>\n\n<div class=\"saved-view\" *ngIf=\"content?.state === ContentState.Saved\">\n  <div class=\"header\">\n    <div class=\"title\">Text Entry</div>\n    <div class=\"toolbar\">\n      <button\n        aria-label=\"Edit Content\"\n        (click)=\"edit($event)\"\n        class=\"edit-button\"\n      >\n        <i class=\"dpg-icon dpg-icon-edit\" aria-hidden=\"true\"></i>\n      </button>\n      <button\n        aria-label=\"Delete Content\"\n        (click)=\"confirmDeleteTextEntry()\"\n        class=\"delete-button\"\n      >\n        <i class=\"dpg-icon dpg-icon-trash\" aria-hidden=\"true\"></i>\n      </button>\n    </div>\n  </div>\n  <div class=\"content\">\n    <div\n      assnMathjax\n      [innerHTML]=\"content.data\"\n      data-automation-id=\"text-entry-content\"\n    ></div>\n  </div>\n</div>\n\n<div class=\"edit-view\" *ngIf=\"content?.state === ContentState.Edit\">\n  <div class=\"header\">\n    <span class=\"title\">Text Entry </span>\n  </div>\n  <div class=\"content\">\n    <h2 translate>Edit Text</h2>\n    <p translate>Edit your written content in the input field.</p>\n    <mhe-editor\n      [toggleRichTextMenuBar]=\"false\"\n      [elementPath]=\"false\"\n      [content]=\"content.data\"\n      [showCharacterLimit]=\"false\"\n      [useA11yTooltip]=\"true\"\n      [showResize]=\"false\"\n      (contentChange)=\"setData($event)\"\n    >\n    </mhe-editor>\n  </div>\n  <div class=\"footer\">\n    <button\n      class=\"btn btn-outline-primary\"\n      (click)=\"cancel()\"\n      data-automation-id=\"cancel-button\"\n    >\n      Cancel\n    </button>\n    <button\n      class=\"btn btn-primary ms-3\"\n      (click)=\"saveContent()\"\n      data-automation-id=\"save-button\"\n    >\n      Continue\n    </button>\n  </div>\n</div>\n\n<air-modal\n  class=\"confirm-modal\"\n  [isOpen]=\"showConfirmDeleteTextEntry\"\n  confirmButtonText=\"Remove\"\n  (cancel)=\"cancelDeleteTextEntry()\"\n  (close)=\"cancelDeleteTextEntry()\"\n  (confirm)=\"delete()\"\n>\n  <p translate>Are you sure you want to delete this content?</p>\n  <p translate>This cannot be undone.</p>\n</air-modal>\n", styles: [".create-view{border:1px solid #d1dbde;max-width:650px;border-radius:5px}.create-view .content{min-height:250px!important}.saved-view,.edit-view{max-width:700px;border:1px solid #d1dbde;border-radius:5px;margin-bottom:10px;min-height:100px}.saved-view .header,.edit-view .header{display:flex;justify-content:space-between;background:#eef1f2;padding:8px;border-top-left-radius:4px;border-top-right-radius:4px;border-bottom:1px solid #d1dbde;height:50px}.saved-view .header .title,.edit-view .header .title{margin:5px 0 0 4px}.saved-view .header .toolbar,.edit-view .header .toolbar{display:flex}.saved-view .header .toolbar .delete-button,.saved-view .header .toolbar .edit-button,.edit-view .header .toolbar .delete-button,.edit-view .header .toolbar .edit-button{border:transparent solid 1px;padding-top:6px}.saved-view .header .toolbar .delete-button:hover,.saved-view .header .toolbar .edit-button:hover,.edit-view .header .toolbar .delete-button:hover,.edit-view .header .toolbar .edit-button:hover{border-color:#265867;border-radius:5px}.edit-view .toolbar{margin-top:6px}.edit-button{margin:0 8px}.delete-button{margin-right:3px}.saved-view .content{min-height:auto;overflow-y:auto}.content{padding:1rem}.footer{padding:20px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { content: [{
                type: Input
            }], contentChange: [{
                type: Output
            }], removeContent: [{
                type: Output
            }], editContent: [{
                type: Output
            }] } });

class ContentManagerComponent {
    constructor() {
        this.isGoogleDriveEnabled = false;
        this.isFileUploadEnabled = false;
        this.isLinkEnabled = false;
        this.isTextEntryEnabled = false;
        this.fileUrl = '';
        this.allowedFileExtensions = [];
        this.isRadioBtnRequired = true;
        this.onUploadContent = new EventEmitter();
        this.onRemoveContent = new EventEmitter();
        this.onEditContent = new EventEmitter();
        this.ContentType = ContentType;
        this.alertType = AlertType;
        this.openMessageModal = false;
    }
    set isUploadErrorExist(value) {
        if (value) {
            const alert = {
                type: AlertType.Error,
                message: 'UPLOAD_ERROR',
                id: v4(),
            };
            this.onError(alert);
        }
    }
    onNewContent(content) {
        this.content = content;
        this.errorAlert = null;
        if (content.state === ContentState.Uploading) {
            this.onUploadContent.emit(this.content);
        }
    }
    removeContent(contentType) {
        this.onRemoveContent.emit(contentType);
    }
    toggleContent(value) {
        const contentType = value;
        this.contentType = contentType;
        if (this.content.data === '') {
            this.onRemoveContent.emit(contentType);
        }
        else {
            this.openMessageModal = true;
        }
    }
    onEdit(content) {
        this.content = {
            ...content,
            state: ContentState.Edit,
        };
        this.onEditContent.emit();
    }
    onError(alert) {
        this.removeContent(this.content.type);
        this.errorAlert = alert;
    }
    closeToggleContentTypeModal() {
        this.openMessageModal = false;
        this.toggleBetweenTextAndFileUpload();
    }
    toggleBetweenTextAndFileUpload() {
        const fileUploadRadiobBtn = document
            .querySelector(this.sourceClass)
            ?.querySelector('.file-upload-content');
        const textEntryRadioBtn = document
            .querySelector(this.sourceClass)
            ?.querySelector('.text-input-content');
        if (this.contentType === ContentType.File) {
            textEntryRadioBtn.checked = true;
            fileUploadRadiobBtn.checked = false;
            setTimeout(() => {
                textEntryRadioBtn.focus();
                fileUploadRadiobBtn.blur();
            });
        }
        else {
            fileUploadRadiobBtn.checked = true;
            textEntryRadioBtn.checked = false;
            setTimeout(() => {
                fileUploadRadiobBtn.focus();
                textEntryRadioBtn.blur();
            });
        }
    }
    cancelToggleContenttypeModal() {
        this.toggleBetweenTextAndFileUpload();
        this.closeToggleContentTypeModal();
    }
    onConfirmAction() {
        this.removeContent(this.contentType);
        this.openMessageModal = false;
    }
}
ContentManagerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ContentManagerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ContentManagerComponent, selector: "aa-content-manager", inputs: { isGoogleDriveEnabled: "isGoogleDriveEnabled", isFileUploadEnabled: "isFileUploadEnabled", isLinkEnabled: "isLinkEnabled", isTextEntryEnabled: "isTextEntryEnabled", fileUrl: "fileUrl", allowedFileExtensions: "allowedFileExtensions", isRadioBtnRequired: "isRadioBtnRequired", content: "content", sourceClass: "sourceClass", isUploadErrorExist: "isUploadErrorExist" }, outputs: { onUploadContent: "onUploadContent", onRemoveContent: "onRemoveContent", onEditContent: "onEditContent" }, viewQueries: [{ propertyName: "progressBar", first: true, predicate: ProgressBarComponent, descendants: true }, { propertyName: "linkAccModal", first: true, predicate: ["linkAccModal"], descendants: true }], ngImport: i0, template: "<div class=\"content-container\">\n\n  <h4 *ngIf=\"isRadioBtnRequired\" class=\"content-choose-option-text\">Choose how to add your content</h4>\n  <div class=\"editor-option d-flex align-items-center\">\n    <div\n      *ngIf=\"isTextEntryEnabled && isRadioBtnRequired\"\n      class=\"form-check me-3\"\n    >\n      <label class=\"form-check-label\">\n        <input\n          class=\"form-check-input text-input-content\"\n          data-automation-id=\"text-content-type-radio-button\"\n          type=\"radio\"\n          [checked]=\"content.type === ContentType.TextEntry\"\n          (change)=\"toggleContent(ContentType.TextEntry)\"\n        />\n        <i class=\"dpg-icon dpg-icon-edit\" aria-hidden=\"true\"></i>\n        Enter Text\n      </label>\n    </div>\n    <div\n      *ngIf=\"isFileUploadEnabled\"\n      class=\"form-check\"\n    >\n      <label class=\"form-check-label\">\n        <input\n          class=\"form-check-input file-upload-content\"\n          data-automation-id=\"file-content-type-radio-button\"\n          type=\"radio\"\n          [checked]=\"content.type === ContentType.File\"\n          (change)=\"toggleContent(ContentType.File)\"\n        />\n        <i class=\"dpg-icon dpg-icon-system-upload\" aria-hidden=\"true\"></i>\n        Upload File\n      </label>\n    </div>\n\n  <air-modal [isOpen]=\"openMessageModal\"\n              [titleText]=\"'Continue?'\"\n              [confirmButtonText]=\"'Yes, proceed'\"\n              (cancel)=\"cancelToggleContenttypeModal()\"\n              (close)=\"cancelToggleContenttypeModal()\"\n              (confirm)=\"onConfirmAction()\">\n    <div>This will clear previous content. Do you wish to proceed?</div>\n  </air-modal>\n</div>\n\n<div\n  *ngIf=\"isFileUploadEnabled\"\n  class=\"content-area file-upload-container\"\n  [ngClass]=\"{\n    visible: content.type === ContentType.File,\n    'has-error': errorAlert\n  }\"\n>\n  <aa-file-upload\n    [url]=\"fileUrl\"\n    [content]=\"content\"\n    [allowedFileExtensions]=\"allowedFileExtensions\"\n    (error)=\"onError($event)\"\n    (contentChange)=\"onNewContent($event)\"\n    (removeContent)=\"removeContent($event)\"\n  >\n  </aa-file-upload>\n</div>\n\n<div\n  class=\"content-area\"\n  [ngClass]=\"{\n    visible: content.type === ContentType.TextEntry || !isRadioBtnRequired,\n    'has-error': errorAlert\n  }\"\n>\n  <aa-text-entry\n    [content]=\"content\"\n    (contentChange)=\"onNewContent($event)\"\n    (removeContent)=\"removeContent($event)\"\n    (editContent)=\"onEdit($event)\"\n  >\n  </aa-text-entry>\n</div>\n\n<aa-progress-bar [content]=\"content\"></aa-progress-bar>\n<div *ngIf=\"errorAlert\" class=\"ahe-ui form-group has-feedback has-error\">\n  <span class=\"help-block\">\n    <span *ngIf=\"errorAlert.message === 'FILE_SIZE_ERROR'\">\n      It is not allowed to upload file bigger than 50MB.\n    </span>\n\n    <span *ngIf=\"errorAlert.message === 'EXTENSION_ERROR'\">\n      It is not allowed to upload file with this extension.\n    </span>\n\n    <span *ngIf=\"errorAlert.message === 'UPLOAD_ERROR'\">\n      There was a problem uploading your file. Please try again.\n    </span>\n  </span>\n</div>\n", styles: [".content-container{margin-top:10px}.content-area{display:none;margin-top:20px}.content-area.visible{display:block}.content-area.file-upload-container{margin-bottom:15px;border:1px solid #d1dbde;border-radius:5px;width:600px}.content-area.has-error{margin-bottom:0;border:1px solid #b0181e}.dpg-icon{vertical-align:text-bottom}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "component", type: FileUploadComponent, selector: "aa-file-upload", inputs: ["url", "allowedFileExtensions", "content"], outputs: ["fileUploadStart", "error", "contentChange", "removeContent"] }, { kind: "component", type: ProgressBarComponent, selector: "aa-progress-bar", inputs: ["content"] }, { kind: "component", type: TextEntryComponent, selector: "aa-text-entry", inputs: ["content"], outputs: ["contentChange", "removeContent", "editContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-content-manager', template: "<div class=\"content-container\">\n\n  <h4 *ngIf=\"isRadioBtnRequired\" class=\"content-choose-option-text\">Choose how to add your content</h4>\n  <div class=\"editor-option d-flex align-items-center\">\n    <div\n      *ngIf=\"isTextEntryEnabled && isRadioBtnRequired\"\n      class=\"form-check me-3\"\n    >\n      <label class=\"form-check-label\">\n        <input\n          class=\"form-check-input text-input-content\"\n          data-automation-id=\"text-content-type-radio-button\"\n          type=\"radio\"\n          [checked]=\"content.type === ContentType.TextEntry\"\n          (change)=\"toggleContent(ContentType.TextEntry)\"\n        />\n        <i class=\"dpg-icon dpg-icon-edit\" aria-hidden=\"true\"></i>\n        Enter Text\n      </label>\n    </div>\n    <div\n      *ngIf=\"isFileUploadEnabled\"\n      class=\"form-check\"\n    >\n      <label class=\"form-check-label\">\n        <input\n          class=\"form-check-input file-upload-content\"\n          data-automation-id=\"file-content-type-radio-button\"\n          type=\"radio\"\n          [checked]=\"content.type === ContentType.File\"\n          (change)=\"toggleContent(ContentType.File)\"\n        />\n        <i class=\"dpg-icon dpg-icon-system-upload\" aria-hidden=\"true\"></i>\n        Upload File\n      </label>\n    </div>\n\n  <air-modal [isOpen]=\"openMessageModal\"\n              [titleText]=\"'Continue?'\"\n              [confirmButtonText]=\"'Yes, proceed'\"\n              (cancel)=\"cancelToggleContenttypeModal()\"\n              (close)=\"cancelToggleContenttypeModal()\"\n              (confirm)=\"onConfirmAction()\">\n    <div>This will clear previous content. Do you wish to proceed?</div>\n  </air-modal>\n</div>\n\n<div\n  *ngIf=\"isFileUploadEnabled\"\n  class=\"content-area file-upload-container\"\n  [ngClass]=\"{\n    visible: content.type === ContentType.File,\n    'has-error': errorAlert\n  }\"\n>\n  <aa-file-upload\n    [url]=\"fileUrl\"\n    [content]=\"content\"\n    [allowedFileExtensions]=\"allowedFileExtensions\"\n    (error)=\"onError($event)\"\n    (contentChange)=\"onNewContent($event)\"\n    (removeContent)=\"removeContent($event)\"\n  >\n  </aa-file-upload>\n</div>\n\n<div\n  class=\"content-area\"\n  [ngClass]=\"{\n    visible: content.type === ContentType.TextEntry || !isRadioBtnRequired,\n    'has-error': errorAlert\n  }\"\n>\n  <aa-text-entry\n    [content]=\"content\"\n    (contentChange)=\"onNewContent($event)\"\n    (removeContent)=\"removeContent($event)\"\n    (editContent)=\"onEdit($event)\"\n  >\n  </aa-text-entry>\n</div>\n\n<aa-progress-bar [content]=\"content\"></aa-progress-bar>\n<div *ngIf=\"errorAlert\" class=\"ahe-ui form-group has-feedback has-error\">\n  <span class=\"help-block\">\n    <span *ngIf=\"errorAlert.message === 'FILE_SIZE_ERROR'\">\n      It is not allowed to upload file bigger than 50MB.\n    </span>\n\n    <span *ngIf=\"errorAlert.message === 'EXTENSION_ERROR'\">\n      It is not allowed to upload file with this extension.\n    </span>\n\n    <span *ngIf=\"errorAlert.message === 'UPLOAD_ERROR'\">\n      There was a problem uploading your file. Please try again.\n    </span>\n  </span>\n</div>\n", styles: [".content-container{margin-top:10px}.content-area{display:none;margin-top:20px}.content-area.visible{display:block}.content-area.file-upload-container{margin-bottom:15px;border:1px solid #d1dbde;border-radius:5px;width:600px}.content-area.has-error{margin-bottom:0;border:1px solid #b0181e}.dpg-icon{vertical-align:text-bottom}\n"] }]
        }], propDecorators: { isGoogleDriveEnabled: [{
                type: Input
            }], isFileUploadEnabled: [{
                type: Input
            }], isLinkEnabled: [{
                type: Input
            }], isTextEntryEnabled: [{
                type: Input
            }], fileUrl: [{
                type: Input
            }], allowedFileExtensions: [{
                type: Input
            }], isRadioBtnRequired: [{
                type: Input
            }], content: [{
                type: Input
            }], sourceClass: [{
                type: Input
            }], isUploadErrorExist: [{
                type: Input
            }], onUploadContent: [{
                type: Output
            }], onRemoveContent: [{
                type: Output
            }], onEditContent: [{
                type: Output
            }], progressBar: [{
                type: ViewChild,
                args: [ProgressBarComponent]
            }], linkAccModal: [{
                type: ViewChild,
                args: ['linkAccModal']
            }] } });

class ItemExplanationComponent {
    constructor(fileUploadService) {
        this.fileUploadService = fileUploadService;
        this.updateExplanation = new EventEmitter();
        this.fileName = '';
        this.fileUrl = '';
        this.isUploadError = false;
        this.content = {
            type: ContentType.TextEntry,
            data: '',
            state: ContentState.Create,
        };
    }
    ngOnChanges() {
        if (this.explanation) {
            this.content = {
                type: this.explanation.content_type,
                data: this.explanation.content,
                state: this.explanation.content ? ContentState.Saved : ContentState.Create,
            };
            this.fileUrl = this.explanation.file_url || '';
        }
    }
    showFile() {
        return this.content.type === 'file';
    }
    onToggleExplanationAsFile(checkbox) {
        const explanationData = {
            content: '',
            contentType: checkbox.target.checked ? ContentType.File : ContentType.TextEntry,
            url: '',
        };
        this.updateExplanation.emit(explanationData);
    }
    uploadFile(explanationFile) {
        const explanationData = {
            ...explanationFile,
            contentType: 'file',
        };
        this.updateExplanation.emit(explanationData);
    }
    removeFile() {
        this.updateExplanation.emit({
            content: '',
            contentType: ContentType.File,
            url: '',
        });
    }
    editContent() {
        this.content.state = ContentState.Edit;
    }
    uploadContent(content) {
        if (content.data instanceof File) {
            const formData = new FormData();
            formData.append('file', content.data);
            const body = formData;
            this.fileName = content.data.name;
            this.fileUploadService.uploadFile(body, 'file/documentUpload').subscribe((response) => {
                this.fileUrl = response['url'];
                this.onSuccessfulFileUpload();
                this.finishUpload();
            }, (err) => {
                const treatAsSuccess = err.status === 409;
                if (treatAsSuccess) {
                    this.fileUrl = err.error.url;
                    this.onSuccessfulFileUpload();
                }
                this.finishUpload(!treatAsSuccess);
            });
        }
        else if (typeof content.data === 'string') {
            this.onItemExplanationChange(content.data);
            this.finishUpload();
        }
    }
    finishUpload(isError = false) {
        this.isUploadError = isError;
    }
    onItemExplanationChange(itemExplanation) {
        this.updateExplanation.emit({
            content: itemExplanation,
            contentType: ContentType.TextEntry,
            url: '',
        });
    }
    onSuccessfulFileUpload() {
        this.updateExplanation.emit({
            content: this.fileName,
            url: this.fileUrl,
            contentType: ContentType.File,
        });
    }
    removeContent(contentType) {
        this.updateExplanation.emit({
            content: '',
            contentType: contentType,
            url: '',
        });
    }
}
ItemExplanationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExplanationComponent, deps: [{ token: FileUploadService }], target: i0.ɵɵFactoryTarget.Component });
ItemExplanationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemExplanationComponent, selector: "aa-item-explanation", inputs: { explanation: "explanation" }, outputs: { updateExplanation: "updateExplanation" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"explanation-wrapper\">\n  <h2 translate>Explanation</h2>\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [content]=\"content\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    [sourceClass]=\"'.explanation-wrapper'\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: ["aa-file-upload[hidden]{display:none!important}aa-file-upload{margin-top:7px;display:block}mhe-editor[hidden]{display:none!important}mhe-editor{margin-top:7px;display:block}.toggle-checkbox{margin-right:5px}\n"], dependencies: [{ kind: "component", type: ContentManagerComponent, selector: "aa-content-manager", inputs: ["isGoogleDriveEnabled", "isFileUploadEnabled", "isLinkEnabled", "isTextEntryEnabled", "fileUrl", "allowedFileExtensions", "isRadioBtnRequired", "content", "sourceClass", "isUploadErrorExist"], outputs: ["onUploadContent", "onRemoveContent", "onEditContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExplanationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-explanation', template: "<div class=\"explanation-wrapper\">\n  <h2 translate>Explanation</h2>\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [content]=\"content\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    [sourceClass]=\"'.explanation-wrapper'\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: ["aa-file-upload[hidden]{display:none!important}aa-file-upload{margin-top:7px;display:block}mhe-editor[hidden]{display:none!important}mhe-editor{margin-top:7px;display:block}.toggle-checkbox{margin-right:5px}\n"] }]
        }], ctorParameters: function () { return [{ type: FileUploadService }]; }, propDecorators: { explanation: [{
                type: Input
            }], updateExplanation: [{
                type: Output
            }] } });

class ItemHintComponent {
    constructor(fileUploadService) {
        this.fileUploadService = fileUploadService;
        this.updateHint = new EventEmitter();
        this.fileName = '';
        this.fileUrl = '';
        this.isUploadError = false;
        this.content = {
            type: ContentType.TextEntry,
            data: '',
            state: ContentState.Create,
        };
    }
    ngOnChanges() {
        if (this.hint) {
            this.content = {
                type: this.hint.content_type,
                data: this.hint.content,
                state: this.hint.content ? ContentState.Saved : ContentState.Create,
            };
            this.fileUrl = this.hint.file_url || '';
        }
    }
    showFile() {
        return this.content.type === 'file';
    }
    onToggleHintAsFile(checkbox) {
        const hintData = {
            content: '',
            contentType: checkbox.target.checked ? ContentType.File : ContentType.TextEntry,
            url: '',
        };
        this.updateHint.emit(hintData);
    }
    uploadFile(hintFile) {
        const hintData = {
            ...hintFile,
            contentType: 'file',
        };
        this.updateHint.emit(hintData);
    }
    removeFile() {
        this.updateHint.emit({
            content: '',
            contentType: ContentType.File,
            url: '',
        });
    }
    editContent() {
        this.content.state = ContentState.Edit;
    }
    uploadContent(content) {
        if (content.data instanceof File) {
            const formData = new FormData();
            formData.append('file', content.data);
            const body = formData;
            this.fileName = content.data.name;
            this.fileUploadService.uploadFile(body, 'file/documentUpload').subscribe((response) => {
                this.fileUrl = response['url'];
                this.onSuccessfulFileUpload();
                this.finishUpload();
            }, (err) => {
                const treatAsSuccess = err.status === 409;
                if (treatAsSuccess) {
                    this.fileUrl = err.error.url;
                    this.onSuccessfulFileUpload();
                }
                this.finishUpload(!treatAsSuccess);
            });
        }
        else if (typeof content.data === 'string') {
            this.onItemHintChange(content.data);
            this.finishUpload();
        }
    }
    finishUpload(isError = false) {
        this.isUploadError = isError;
    }
    onItemHintChange(content) {
        this.updateHint.emit({
            content: content,
            contentType: ContentType.TextEntry,
            url: '',
        });
    }
    onSuccessfulFileUpload() {
        this.updateHint.emit({
            content: this.fileName,
            url: this.fileUrl,
            contentType: ContentType.File,
        });
    }
    removeContent(contentType) {
        this.updateHint.emit({
            content: '',
            contentType: contentType,
            url: '',
        });
    }
}
ItemHintComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemHintComponent, deps: [{ token: FileUploadService }], target: i0.ɵɵFactoryTarget.Component });
ItemHintComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemHintComponent, selector: "aa-item-hint", inputs: { hint: "hint" }, outputs: { updateHint: "updateHint" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"hint-wrapper\">\n  <h2 translate>Hint</h2>\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [content]=\"content\"\n    [sourceClass]=\"'.hint-wrapper'\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: ["aa-file-upload[hidden]{display:none!important}aa-file-upload{margin-top:7px;display:block}mhe-editor[hidden]{display:none!important}mhe-editor{margin-top:7px;display:block}.toggle-checkbox{margin-right:5px}\n"], dependencies: [{ kind: "component", type: ContentManagerComponent, selector: "aa-content-manager", inputs: ["isGoogleDriveEnabled", "isFileUploadEnabled", "isLinkEnabled", "isTextEntryEnabled", "fileUrl", "allowedFileExtensions", "isRadioBtnRequired", "content", "sourceClass", "isUploadErrorExist"], outputs: ["onUploadContent", "onRemoveContent", "onEditContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemHintComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-hint', template: "<div class=\"hint-wrapper\">\n  <h2 translate>Hint</h2>\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [content]=\"content\"\n    [sourceClass]=\"'.hint-wrapper'\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: ["aa-file-upload[hidden]{display:none!important}aa-file-upload{margin-top:7px;display:block}mhe-editor[hidden]{display:none!important}mhe-editor{margin-top:7px;display:block}.toggle-checkbox{margin-right:5px}\n"] }]
        }], ctorParameters: function () { return [{ type: FileUploadService }]; }, propDecorators: { hint: [{
                type: Input
            }], updateHint: [{
                type: Output
            }] } });

class ItemExampleResponseComponent {
    constructor(fileUploadService) {
        this.fileUploadService = fileUploadService;
        this.updateExampleResponse = new EventEmitter();
        this.fileName = '';
        this.fileUrl = '';
        this.isUploadError = false;
        this.content = {
            type: ContentType.File,
            data: '',
            state: ContentState.Create,
        };
    }
    ngOnChanges() {
        if (this.exampleResponse) {
            this.content = {
                type: this.exampleResponse.content_type,
                data: this.exampleResponse.content,
                state: this.exampleResponse.content ? ContentState.Saved : ContentState.Create,
            };
            this.fileUrl = this.exampleResponse.file_url || '';
        }
    }
    uploadFile(fileData) {
        const payload = {
            ...fileData,
            contentType: ContentType.File,
        };
        this.updateExampleResponse.emit(payload);
    }
    editContent() {
        this.content.state = ContentState.Edit;
    }
    uploadContent(content) {
        if (content.data instanceof File) {
            const formData = new FormData();
            formData.append('file', content.data);
            const body = formData;
            this.fileName = content.data.name;
            this.fileUploadService.uploadFile(body, 'file/documentUpload').subscribe((response) => {
                this.fileUrl = response['url'];
                this.onSuccessfulFileUpload();
                this.finishUpload(content);
            }, (err) => {
                const treatAsSuccess = err.status === 409;
                if (treatAsSuccess) {
                    this.fileUrl = err.error.url;
                    this.onSuccessfulFileUpload();
                }
                this.finishUpload(content, !treatAsSuccess);
            });
        }
        else if (typeof content.data === 'string') {
            this.onItemExampleResponseChange(content.data);
            this.finishUpload(content);
        }
    }
    finishUpload(content, isError = false) {
        this.isUploadError = isError;
        if (!isError) {
            this.content = {
                ...content,
                state: ContentState.Saved,
            };
        }
    }
    onItemExampleResponseChange(content) {
        this.updateExampleResponse.emit({
            content: content,
            contentType: ContentType.TextEntry,
            url: '',
        });
    }
    onSuccessfulFileUpload() {
        this.updateExampleResponse.emit({
            content: this.fileName,
            url: this.fileUrl,
            contentType: ContentType.File,
        });
    }
    removeContent(contentType) {
        this.updateExampleResponse.emit({
            content: '',
            contentType: contentType,
            url: '',
        });
    }
    removeFile() {
        this.updateExampleResponse.emit({ content: '', contentType: ContentType.File, url: '' });
    }
}
ItemExampleResponseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExampleResponseComponent, deps: [{ token: FileUploadService }], target: i0.ɵɵFactoryTarget.Component });
ItemExampleResponseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemExampleResponseComponent, selector: "aa-item-example-response", inputs: { exampleResponse: "exampleResponse" }, outputs: { updateExampleResponse: "updateExampleResponse" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"example-response-wrapper\">\n  <h2 class=\"example-response-label\" translate>\n    Example Response\n  </h2>\n\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    [content]=\"content\"\n    [sourceClass]=\"'.example-response-wrapper'\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: [".example-response-wrapper{display:flex;flex-direction:column;width:100%;margin:30px 0 20px}\n"], dependencies: [{ kind: "component", type: ContentManagerComponent, selector: "aa-content-manager", inputs: ["isGoogleDriveEnabled", "isFileUploadEnabled", "isLinkEnabled", "isTextEntryEnabled", "fileUrl", "allowedFileExtensions", "isRadioBtnRequired", "content", "sourceClass", "isUploadErrorExist"], outputs: ["onUploadContent", "onRemoveContent", "onEditContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExampleResponseComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-example-response', template: "<div class=\"example-response-wrapper\">\n  <h2 class=\"example-response-label\" translate>\n    Example Response\n  </h2>\n\n  <aa-content-manager\n    [isFileUploadEnabled]=\"true\"\n    [isTextEntryEnabled]=\"true\"\n    [fileUrl]=\"fileUrl\"\n    [isUploadErrorExist]=\"isUploadError\"\n    [allowedFileExtensions]=\"['.ppt', '.pptx', '.doc', '.docx', '.pdf']\"\n    [content]=\"content\"\n    [sourceClass]=\"'.example-response-wrapper'\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", styles: [".example-response-wrapper{display:flex;flex-direction:column;width:100%;margin:30px 0 20px}\n"] }]
        }], ctorParameters: function () { return [{ type: FileUploadService }]; }, propDecorators: { exampleResponse: [{
                type: Input
            }], updateExampleResponse: [{
                type: Output
            }] } });

class ItemExampleComponent {
    constructor() {
        this.updateExample = new EventEmitter();
        this.content = {
            type: ContentType.TextEntry,
            data: '',
            state: ContentState.Create,
        };
        this.example_limit = EXAMPLE_LIMIT;
        this.isUploadError = false;
    }
    ngOnChanges() {
        if (this.example) {
            this.content = {
                type: this.example.content_type,
                data: this.example.content,
                state: this.example.content ? ContentState.Saved : ContentState.Create,
            };
        }
    }
    onItemExampleChange(itemExample) {
        this.updateExample.emit({
            content: itemExample,
            contentType: ContentType.TextEntry,
        });
    }
    editContent() {
        this.content.state = ContentState.Edit;
    }
    removeContent(contentType) {
        this.updateExample.emit({
            content: '',
            contentType: contentType,
        });
    }
    uploadContent(content) {
        if (typeof content.data === 'string') {
            this.onItemExampleChange(content.data);
            this.content = { ...content, state: ContentState.Saved };
        }
    }
}
ItemExampleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExampleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ItemExampleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ItemExampleComponent, selector: "aa-item-example", inputs: { example: "example" }, outputs: { updateExample: "updateExample" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"example-wrapper\">\n  <h2 translate>Example</h2>\n  <aa-content-manager\n    [isRadioBtnRequired]=\"false\"\n    [isFileUploadEnabled]=\"false\"\n    [isTextEntryEnabled]=\"true\"\n    [content]=\"content\"\n    [sourceClass]=\"'.example-wrapper'\"\n    [isUploadErrorExist]=\"isUploadError\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n", dependencies: [{ kind: "component", type: ContentManagerComponent, selector: "aa-content-manager", inputs: ["isGoogleDriveEnabled", "isFileUploadEnabled", "isLinkEnabled", "isTextEntryEnabled", "fileUrl", "allowedFileExtensions", "isRadioBtnRequired", "content", "sourceClass", "isUploadErrorExist"], outputs: ["onUploadContent", "onRemoveContent", "onEditContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ItemExampleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-item-example', template: "<div class=\"example-wrapper\">\n  <h2 translate>Example</h2>\n  <aa-content-manager\n    [isRadioBtnRequired]=\"false\"\n    [isFileUploadEnabled]=\"false\"\n    [isTextEntryEnabled]=\"true\"\n    [content]=\"content\"\n    [sourceClass]=\"'.example-wrapper'\"\n    [isUploadErrorExist]=\"isUploadError\"\n    (onUploadContent)=\"uploadContent($event)\"\n    (onRemoveContent)=\"removeContent($event)\"\n    (onEditContent)=\"editContent()\"\n  ></aa-content-manager>\n</div>\n" }]
        }], propDecorators: { example: [{
                type: Input
            }], updateExample: [{
                type: Output
            }] } });

class AirAuthoringComponent {
    constructor(store, promptValidationService, airValidationService, configService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.airValidationService = airValidationService;
        this.configService = configService;
        this.showExplanation = false;
        this.showExampleResponse = false;
        this.showHint = false;
        this.showExample = false;
        this.viewExplanationButton = false;
        this.aleksConfig = null;
        this.apiConfig = null;
        this.questionTextLimit = QUESTION_TEXT_LIMIT;
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        this.promptError = [];
        this.allowInlineValidation = false;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    ngOnChanges(changes) {
        if (changes['itemType']) {
            this.airValidationService.resetErrors();
        }
    }
    getAirState() {
        const itemType = 'get' + this.getTransformedName();
        return fromAirState[itemType] && fromAirState[itemType].apply(this, []);
    }
    getPureAirState() {
        const itemType = 'getPure' + this.getTransformedName();
        return fromAirState[itemType] && fromAirState[itemType].apply(this, []);
    }
    getTransformedName() {
        const mappedItem = itemTypes.find((item) => this.itemType === item.value);
        return mappedItem && mappedItem.reducerValue;
    }
    get itemTypeForPreview() {
        const airState = this.getAirState();
        if (this.itemType === ItemType.MULTIPLE_CHOICE) {
            return airState.selectMultiple ? ItemType.MULTIPLE_CHOICE_MULTI_SELECT : ItemType.MULTIPLE_CHOICE;
        }
        if (this.itemType === ItemType.ALGORITHMIC) {
            return 'external';
        }
        return this.itemType;
    }
    isHavingKeyupValidations(itemType) {
        return itemType === ItemType.CLOZE || itemType === ItemType.FILL_IN_THE_BLANK || itemType === ItemType.GRID_IN;
    }
    onEditorKeyup(prompt) {
        this.store.dispatch(new UpdatePromptAction({
            content: prompt,
            itemType: this.itemType,
        }));
        if (this.isHavingKeyupValidations(this.itemType)) {
            this.promptValidationService.validateMaxBlankCount(this.getAirState().tooManyAnswers);
        }
    }
    getErrors() {
        const errors = this.allowInlineValidation ? this.promptValidationService.getPromptError() : [];
        return errors;
    }
    onEditorBlur(content) {
        this.promptValidationService.validatePrompt(content);
        if (this.isHavingKeyupValidations(this.itemType)) {
            this.promptValidationService.validateMinBlankCount(content);
        }
    }
    onUpdateExplanation(explanation) {
        this.store.dispatch(new UpdateExplanationAction({
            ...explanation,
            itemType: this.itemType,
        }));
    }
    onUpdateHint(hint) {
        this.store.dispatch(new UpdateHintAction({
            ...hint,
            itemType: this.itemType,
        }));
    }
    onUpdateExampleResponse(exampleResponse) {
        this.store.dispatch(new UpdateExampleResponseAction({
            ...exampleResponse,
            itemType: this.itemType,
        }));
    }
    onUpdateExample(example) {
        this.store.dispatch(new UpdateExampleAction({
            ...example,
            itemType: this.itemType,
        }));
    }
    isAlertEnabled() {
        return this.itemType === ItemType.AUDIO_RECORDING || this.itemType === ItemType.DRAWING;
    }
    hasUnderscore() {
        return this.itemType === ItemType.CLOZE || this.itemType === ItemType.FILL_IN_THE_BLANK;
    }
    hasEditor() {
        return this.itemType === ItemType.ESSAY;
    }
    hasGrid() {
        return this.itemType === ItemType.GRID_IN;
    }
    hasQuestionStem() {
        return (this.itemType !== ItemType.GRAPHING &&
            this.itemType !== ItemType.ALGORITHMIC &&
            this.itemType !== ItemType.MULTIPLE_CHOICE_AUDIO);
    }
    getAlertText() {
        const alertText = this.itemType === ItemType.DRAWING
            ? 'This question type is not accessible.'
            : 'This question type is not supported on Internet Explorer 11 or lower.';
        return alertText;
    }
}
AirAuthoringComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringComponent, deps: [{ token: i1.Store }, { token: PromptValidationService }, { token: AirValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirAuthoringComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirAuthoringComponent, selector: "aa-air-authoring", inputs: { itemType: "itemType", showExplanation: "showExplanation", showExampleResponse: "showExampleResponse", showHint: "showHint", showExample: "showExample", viewExplanationButton: "viewExplanationButton", aleksConfig: "aleksConfig", apiConfig: "apiConfig" }, usesOnChanges: true, ngImport: i0, template: "<div\n  *ngIf=\"isAlertEnabled()\"\n  class=\"alert alert-info\"\n  aria-live=\"assertive\"\n  role=\"alert\">\n  {{ getAlertText() }}\n</div>\n\n<aa-item-preview\n  [aleksConfig]=\"aleksConfig\"\n  [itemType]=\"itemTypeForPreview\"\n  [preserveAirState]=\"itemType === 'algorithmic'\"\n  [air]=\"getPureAirState()\"\n  [showRationales]=\"getAirState()?.showRationales\"\n  [viewExplanationButton]=\"false\"\n></aa-item-preview>\n\n<div *ngIf=\"hasQuestionStem()\" class=\"mb-4\">\n  <h2 translate>Question text *</h2>\n\n  <div class=\"item-instructions\" *ngIf=\"hasUnderscore()\" translate>\n    Use two adjacent underscores to add answer blanks. For instance, \"Jack and\n    __ went up the __.\"\n  </div>\n  <div class=\"item-instructions\" *ngIf=\"hasEditor()\" translate>\n    Use two adjacent underscores to add additional essay response fields beyond\n    the single default essay response field.\n  </div>\n  <div class=\"item-instructions\" *ngIf=\"hasGrid()\" translate>\n    Use two adjacent underscores to add dropdown menus, like \"35 + 17 = __ __\".\n  </div>\n\n  <mhe-editor\n    [maxCharacter]=\"questionTextLimit\"\n    [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n    [content]=\"getAirState()?.prompt\"\n    [errors]=\"getErrors()\"\n    [showResize]=\"false\"\n    (contentChange)=\"onEditorKeyup($event)\"\n    (editorFocusOut)=\"onEditorBlur($event)\"\n  ></mhe-editor>\n</div>\n\n<div class=\"mb-4\">\n  <ng-content></ng-content>\n</div>\n\n<aa-item-example-response\n  *ngIf=\"showExampleResponse\"\n  class=\"d-block mb-4\"\n  [exampleResponse]=\"getAirState()?.resources?.example_response\"\n  (updateExampleResponse)=\"onUpdateExampleResponse($event)\"\n></aa-item-example-response>\n\n<aa-item-hint\n  *ngIf=\"showHint\"\n  class=\"d-block mb-4\"\n  [hint]=\"getAirState()?.resources?.hint\"\n  (updateHint)=\"onUpdateHint($event)\"\n></aa-item-hint>\n\n<aa-item-example\n  *ngIf=\"showExample\"\n  class=\"d-block mb-4\"\n  [example]=\"getAirState()?.resources?.example\"\n  (updateExample)=\"onUpdateExample($event)\"\n></aa-item-example>\n\n<aa-item-explanation\n  *ngIf=\"showExplanation\"\n  class=\"d-block mb-4\"\n  [explanation]=\"getAirState()?.resources?.explanation\"\n  (updateExplanation)=\"onUpdateExplanation($event)\"\n></aa-item-explanation>\n", styles: [".item-instructions{width:calc(100% - 180px)}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: ItemPreviewComponent, selector: "aa-item-preview", inputs: ["air", "itemType", "preserveAirState", "viewMode", "showRationales", "viewExplanationButton", "aleksConfig"] }, { kind: "component", type: ItemExplanationComponent, selector: "aa-item-explanation", inputs: ["explanation"], outputs: ["updateExplanation"] }, { kind: "component", type: ItemHintComponent, selector: "aa-item-hint", inputs: ["hint"], outputs: ["updateHint"] }, { kind: "component", type: ItemExampleResponseComponent, selector: "aa-item-example-response", inputs: ["exampleResponse"], outputs: ["updateExampleResponse"] }, { kind: "component", type: ItemExampleComponent, selector: "aa-item-example", inputs: ["example"], outputs: ["updateExample"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-authoring', template: "<div\n  *ngIf=\"isAlertEnabled()\"\n  class=\"alert alert-info\"\n  aria-live=\"assertive\"\n  role=\"alert\">\n  {{ getAlertText() }}\n</div>\n\n<aa-item-preview\n  [aleksConfig]=\"aleksConfig\"\n  [itemType]=\"itemTypeForPreview\"\n  [preserveAirState]=\"itemType === 'algorithmic'\"\n  [air]=\"getPureAirState()\"\n  [showRationales]=\"getAirState()?.showRationales\"\n  [viewExplanationButton]=\"false\"\n></aa-item-preview>\n\n<div *ngIf=\"hasQuestionStem()\" class=\"mb-4\">\n  <h2 translate>Question text *</h2>\n\n  <div class=\"item-instructions\" *ngIf=\"hasUnderscore()\" translate>\n    Use two adjacent underscores to add answer blanks. For instance, \"Jack and\n    __ went up the __.\"\n  </div>\n  <div class=\"item-instructions\" *ngIf=\"hasEditor()\" translate>\n    Use two adjacent underscores to add additional essay response fields beyond\n    the single default essay response field.\n  </div>\n  <div class=\"item-instructions\" *ngIf=\"hasGrid()\" translate>\n    Use two adjacent underscores to add dropdown menus, like \"35 + 17 = __ __\".\n  </div>\n\n  <mhe-editor\n    [maxCharacter]=\"questionTextLimit\"\n    [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n    [content]=\"getAirState()?.prompt\"\n    [errors]=\"getErrors()\"\n    [showResize]=\"false\"\n    (contentChange)=\"onEditorKeyup($event)\"\n    (editorFocusOut)=\"onEditorBlur($event)\"\n  ></mhe-editor>\n</div>\n\n<div class=\"mb-4\">\n  <ng-content></ng-content>\n</div>\n\n<aa-item-example-response\n  *ngIf=\"showExampleResponse\"\n  class=\"d-block mb-4\"\n  [exampleResponse]=\"getAirState()?.resources?.example_response\"\n  (updateExampleResponse)=\"onUpdateExampleResponse($event)\"\n></aa-item-example-response>\n\n<aa-item-hint\n  *ngIf=\"showHint\"\n  class=\"d-block mb-4\"\n  [hint]=\"getAirState()?.resources?.hint\"\n  (updateHint)=\"onUpdateHint($event)\"\n></aa-item-hint>\n\n<aa-item-example\n  *ngIf=\"showExample\"\n  class=\"d-block mb-4\"\n  [example]=\"getAirState()?.resources?.example\"\n  (updateExample)=\"onUpdateExample($event)\"\n></aa-item-example>\n\n<aa-item-explanation\n  *ngIf=\"showExplanation\"\n  class=\"d-block mb-4\"\n  [explanation]=\"getAirState()?.resources?.explanation\"\n  (updateExplanation)=\"onUpdateExplanation($event)\"\n></aa-item-explanation>\n", styles: [".item-instructions{width:calc(100% - 180px)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }, { type: AirValidationService }, { type: ConfigService }]; }, propDecorators: { itemType: [{
                type: Input
            }], showExplanation: [{
                type: Input
            }], showExampleResponse: [{
                type: Input
            }], showHint: [{
                type: Input
            }], showExample: [{
                type: Input
            }], viewExplanationButton: [{
                type: Input
            }], aleksConfig: [{
                type: Input
            }], apiConfig: [{
                type: Input
            }] } });

class AirExternalAlgoComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirAlgorithmic, LoadAirItemAction$5, ResetStoreAction$5);
        this.store = store;
    }
}
AirExternalAlgoComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirExternalAlgoComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirExternalAlgoComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirExternalAlgoComponent, selector: "aa-air-external-algo", usesInheritance: true, ngImport: i0, template: "" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirExternalAlgoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-external-algo', template: "" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class DrawingToolComponent {
    constructor() {
        this.changeToolConfig = new EventEmitter();
        this.changeMathConfig = new EventEmitter();
    }
    isBasicTools() {
        return this.toolConfiguration === 'basic';
    }
    isBasicMathTools() {
        return this.mathConfiguration === 'basic';
    }
    toolConfigChange(toolConfig) {
        this.changeToolConfig.emit(toolConfig);
    }
    mathConfigChange(mathConfig) {
        this.changeMathConfig.emit(mathConfig);
    }
}
DrawingToolComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DrawingToolComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DrawingToolComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DrawingToolComponent, selector: "aa-drawing-tools", inputs: { toolConfiguration: "toolConfiguration", mathConfiguration: "mathConfiguration" }, outputs: { changeToolConfig: "changeToolConfig", changeMathConfig: "changeMathConfig" }, ngImport: i0, template: "<h2 translate>Drawing Tool</h2>\r\n<div class=\"drawing-tool-options\">\r\n  <div class=\"tools-config\">\r\n    <span translate class=\"tool-header\">Available tools</span>\r\n    <div class=\"drawing-tools\">\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"all-tool-id\"\r\n          [checked]=\"!isBasicTools()\"\r\n          (change)=\"toolConfigChange('all')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"all-tool-id\">\r\n          All\r\n        </label>\r\n      </div>\r\n\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"basic-tool-id\"\r\n          [checked]=\"isBasicTools()\"\r\n          (change)=\"toolConfigChange('basic')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"basic-tool-id\">\r\n          Basic\r\n        </label>\r\n      </div>\r\n      <span class=\"tool-help-text\" translate\r\n        >The rectangle, ellipse, and polygon tools will not be available.</span\r\n      >\r\n    </div>\r\n  </div>\r\n  <div class=\"tools-config\">\r\n    <span translate class=\"tool-header\">Math Editor</span>\r\n    <div class=\"drawing-tools\">\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"all-math-id\"\r\n          [checked]=\"!isBasicMathTools()\"\r\n          (change)=\"mathConfigChange('all')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"all-math-id\">\r\n          All\r\n        </label>\r\n      </div>\r\n\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"basic-math-id\"\r\n          [checked]=\"isBasicMathTools()\"\r\n          (change)=\"mathConfigChange('basic')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"basic-math-id\">\r\n          Basic\r\n        </label>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".drawing-tool-options{border:1px solid #a9a9a9;border-radius:5px}.drawing-tool-options .tools-config{margin:15px 0 0 15px}.drawing-tool-options .tools-available{display:block}.drawing-tool-options .tool-header{font-weight:700;padding:20px 12px 12px}.drawing-tool-options .tool-help-text{display:flex;font-size:14px;font-weight:400}.drawing-tool-options .drawing-tools{padding:10px;margin-left:16px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DrawingToolComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-drawing-tools', template: "<h2 translate>Drawing Tool</h2>\r\n<div class=\"drawing-tool-options\">\r\n  <div class=\"tools-config\">\r\n    <span translate class=\"tool-header\">Available tools</span>\r\n    <div class=\"drawing-tools\">\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"all-tool-id\"\r\n          [checked]=\"!isBasicTools()\"\r\n          (change)=\"toolConfigChange('all')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"all-tool-id\">\r\n          All\r\n        </label>\r\n      </div>\r\n\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"basic-tool-id\"\r\n          [checked]=\"isBasicTools()\"\r\n          (change)=\"toolConfigChange('basic')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"basic-tool-id\">\r\n          Basic\r\n        </label>\r\n      </div>\r\n      <span class=\"tool-help-text\" translate\r\n        >The rectangle, ellipse, and polygon tools will not be available.</span\r\n      >\r\n    </div>\r\n  </div>\r\n  <div class=\"tools-config\">\r\n    <span translate class=\"tool-header\">Math Editor</span>\r\n    <div class=\"drawing-tools\">\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"all-math-id\"\r\n          [checked]=\"!isBasicMathTools()\"\r\n          (change)=\"mathConfigChange('all')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"all-math-id\">\r\n          All\r\n        </label>\r\n      </div>\r\n\r\n      <div class=\"form-check\">\r\n        <input\r\n          class=\"form-check-input\"\r\n          type=\"radio\"\r\n          id=\"basic-math-id\"\r\n          [checked]=\"isBasicMathTools()\"\r\n          (change)=\"mathConfigChange('basic')\"\r\n        />\r\n        <label class=\"form-check-label\" for=\"basic-math-id\">\r\n          Basic\r\n        </label>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".drawing-tool-options{border:1px solid #a9a9a9;border-radius:5px}.drawing-tool-options .tools-config{margin:15px 0 0 15px}.drawing-tool-options .tools-available{display:block}.drawing-tool-options .tool-header{font-weight:700;padding:20px 12px 12px}.drawing-tool-options .tool-help-text{display:flex;font-size:14px;font-weight:400}.drawing-tool-options .drawing-tools{padding:10px;margin-left:16px}\n"] }]
        }], propDecorators: { toolConfiguration: [{
                type: Input
            }], mathConfiguration: [{
                type: Input
            }], changeToolConfig: [{
                type: Output
            }], changeMathConfig: [{
                type: Output
            }] } });

class AirDrawingComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirDrawing, LoadAirItemAction$4, ResetStoreAction$4);
        this.store = store;
        this.airDrawing$ = this.store.select(getAirDrawing);
        this.pureAirDrawing$ = this.store.select(getPureAirDrawing);
        this.fileName = '';
    }
    onChangeToolConfig(toolConfiguration) {
        this.store.dispatch(new UpdateToolConfigAction(toolConfiguration));
    }
    onUpdateBackgroundImage(backgroundImage) {
        this.fileName = backgroundImage.url;
        this.store.dispatch(new UpdateBackgroundImageAction(backgroundImage));
    }
    onDeleteBackgroundImage() {
        this.store.dispatch(new DeleteBackgroundImageAction());
        this.fileName = '';
    }
    onChangeMathConfig(mathConfiguration) {
        this.store.dispatch(new UpdateMathConfigAction(mathConfiguration));
    }
}
AirDrawingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirDrawingComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirDrawingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirDrawingComponent, selector: "aa-air-drawing", usesInheritance: true, ngImport: i0, template: "<div class=\"mb-4\">\r\n  <aa-drawing-tools\r\n    (changeToolConfig)=\"onChangeToolConfig($event)\"\r\n    (changeMathConfig)=\"onChangeMathConfig($event)\"\r\n    [toolConfiguration]=\"(airDrawing$ | async)?.toolConfiguration\"\r\n    [mathConfiguration]=\"(airDrawing$ | async)?.mathConfiguration\"\r\n  ></aa-drawing-tools>\r\n</div>\r\n\r\n<div class=\"mb-4\">\r\n  <h2>Background Image</h2>\r\n  <span *ngIf=\"!(airDrawing$ | async)?.backgroundImage\" translate>\r\n    The canvas will be blank if a background image is not uploaded.\r\n  </span>\r\n  <div class=\"drawing-image-dropzone\">\r\n    <aa-image-upload\r\n      id=\"drawing-image-upload\"\r\n      [ngClass]=\"{ 'img-align': (airDrawing$ | async)?.backgroundImage }\"\r\n      [fileName]=\"(airDrawing$ | async)?.backgroundImage\"\r\n      [showDeleteModal]=\"false\"\r\n      (updateBackgroundImage)=\"onUpdateBackgroundImage($event)\"\r\n      (deleteBackgroundImage)=\"onDeleteBackgroundImage()\"\r\n    ></aa-image-upload>\r\n  </div>\r\n</div>\r\n", styles: [".drawing-image-dropzone{border:1px solid #a9a9a9;display:inline-block;min-height:83px;padding:20px;position:relative;top:8px;width:100%}.drawing-image-dropzone .img-align{display:flex;justify-content:center;min-height:150px}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ImageUploadComponent, selector: "aa-image-upload", inputs: ["fileName", "imageUrl", "showDeleteModal"], outputs: ["updateBackgroundImage", "deleteBackgroundImage", "imageLoad"] }, { kind: "component", type: DrawingToolComponent, selector: "aa-drawing-tools", inputs: ["toolConfiguration", "mathConfiguration"], outputs: ["changeToolConfig", "changeMathConfig"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirDrawingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-drawing', template: "<div class=\"mb-4\">\r\n  <aa-drawing-tools\r\n    (changeToolConfig)=\"onChangeToolConfig($event)\"\r\n    (changeMathConfig)=\"onChangeMathConfig($event)\"\r\n    [toolConfiguration]=\"(airDrawing$ | async)?.toolConfiguration\"\r\n    [mathConfiguration]=\"(airDrawing$ | async)?.mathConfiguration\"\r\n  ></aa-drawing-tools>\r\n</div>\r\n\r\n<div class=\"mb-4\">\r\n  <h2>Background Image</h2>\r\n  <span *ngIf=\"!(airDrawing$ | async)?.backgroundImage\" translate>\r\n    The canvas will be blank if a background image is not uploaded.\r\n  </span>\r\n  <div class=\"drawing-image-dropzone\">\r\n    <aa-image-upload\r\n      id=\"drawing-image-upload\"\r\n      [ngClass]=\"{ 'img-align': (airDrawing$ | async)?.backgroundImage }\"\r\n      [fileName]=\"(airDrawing$ | async)?.backgroundImage\"\r\n      [showDeleteModal]=\"false\"\r\n      (updateBackgroundImage)=\"onUpdateBackgroundImage($event)\"\r\n      (deleteBackgroundImage)=\"onDeleteBackgroundImage()\"\r\n    ></aa-image-upload>\r\n  </div>\r\n</div>\r\n", styles: [".drawing-image-dropzone{border:1px solid #a9a9a9;display:inline-block;min-height:83px;padding:20px;position:relative;top:8px;width:100%}.drawing-image-dropzone .img-align{display:flex;justify-content:center;min-height:150px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class MathModule {
}
MathModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MathModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: MathModule, declarations: [MathDirective], exports: [MathDirective] });
MathModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathModule, providers: [MathService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MathModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [MathDirective],
                    exports: [MathDirective],
                    providers: [MathService],
                }]
        }] });

class AirCoordinateGraphComponent extends AirBaseComponent {
    constructor(store, validationService, configService) {
        super(store, getPureCoordinateGrapher, LoadAirItemAction$3, ResetStoreAction$3);
        this.store = store;
        this.validationService = validationService;
        this.configService = configService;
        this.allowInlineValidation = false;
        this.getCoordinateGrapher$ = this.store.select(getCoordinateGrapher);
        this.getPureCoordinateGrapher$ = this.store.select(getPureCoordinateGrapher);
        this.getPureCoordinateGrapher$.subscribe((coordinateGraphData) => {
            this.graphData = coordinateGraphData;
        });
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    onEventHandling(evt) {
        if (!evt.data || evt.data.method !== 'file') {
            return;
        }
        const authoredData = {
            ...evt.data.payload,
            mode: modeEnum.AUTHOR,
            authorMode: true,
        };
        this.store.dispatch(new SaveItemAction(authoredData));
    }
    getErrors() {
        if (this.allowInlineValidation) {
            return this.validationService.getAnswerError();
        }
        return [];
    }
    ngOnDestroy() {
        window.removeEventListener('message', (evt) => {
            this.onEventHandling(evt);
        });
    }
    onLoad(iframe) {
        this.iframeDoc = iframe.contentWindow;
        window.addEventListener('message', (evt) => {
            this.onEventHandling(evt);
        });
        setTimeout(() => {
            let frameData;
            if (this.graphData) {
                const { answers } = this.graphData;
                frameData = {
                    ...this.graphData.data,
                    gridDocument: {
                        ...this.graphData.gridDocument,
                        models: answers ? answers.models : [],
                        grid: answers ? answers.grid : {},
                    },
                    checkDocument: answers,
                };
            }
            if (this.iframeDoc) {
                this.iframeDoc.postMessage({
                    type: 'data',
                    method: 'restore',
                    data: frameData,
                }, '*');
                this.iframeDoc.postMessage({
                    type: 'etoolkit',
                    method: 'hideButtons',
                    data: true,
                }, '*');
            }
        }, 1000);
    }
}
AirCoordinateGraphComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirCoordinateGraphComponent, deps: [{ token: i1.Store }, { token: CoordinateGraphValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirCoordinateGraphComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirCoordinateGraphComponent, selector: "aa-air-coordinate-graph", usesInheritance: true, ngImport: i0, template: "<div class=\"canvas-graph\">\n  <iframe #iframe\n          src=\"https://cdn.assess.prod.mheducation.com/coordinate_grapher/1.2.9/editor.html\"\n          height=\"853\"\n          width=\"100%\"\n          (load)=\"onLoad(iframe)\"\n          [class.has-error]=\"getErrors().length\"\n  ></iframe>\n  <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n</div>\n", styles: [".canvas-graph{border:1px solid #a9a9a9;padding:17px}.canvas-graph .has-error{border:1px solid #b0181e}\n"], dependencies: [{ kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirCoordinateGraphComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-coordinate-graph', template: "<div class=\"canvas-graph\">\n  <iframe #iframe\n          src=\"https://cdn.assess.prod.mheducation.com/coordinate_grapher/1.2.9/editor.html\"\n          height=\"853\"\n          width=\"100%\"\n          (load)=\"onLoad(iframe)\"\n          [class.has-error]=\"getErrors().length\"\n  ></iframe>\n  <aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n</div>\n", styles: [".canvas-graph{border:1px solid #a9a9a9;padding:17px}.canvas-graph .has-error{border:1px solid #b0181e}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: CoordinateGraphValidationService }, { type: ConfigService }]; } });

class ContentManagerModule {
}
ContentManagerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ContentManagerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerModule, declarations: [ContentManagerComponent, FileUploadComponent, ProgressBarComponent, TextEntryComponent], imports: [CommonModule,
        ButtonModule,
        ModalModule,
        AlertModule,
        NgxFileDropModule,
        RadioButtonModule,
        RichTextEditorModule,
        AirModalModule], exports: [ContentManagerComponent] });
ContentManagerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerModule, imports: [CommonModule,
        ButtonModule,
        ModalModule,
        AlertModule,
        NgxFileDropModule,
        RadioButtonModule,
        RichTextEditorModule,
        AirModalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ContentManagerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ContentManagerComponent, FileUploadComponent, ProgressBarComponent, TextEntryComponent],
                    exports: [ContentManagerComponent],
                    imports: [
                        CommonModule,
                        ButtonModule,
                        ModalModule,
                        AlertModule,
                        NgxFileDropModule,
                        RadioButtonModule,
                        RichTextEditorModule,
                        AirModalModule,
                    ],
                }]
        }] });

class AirTableEntryCellComponent {
    constructor(store, promptValidationService) {
        this.store = store;
        this.promptValidationService = promptValidationService;
        this.notifyIsModalOpen = new EventEmitter();
        this.airConfig$ = this.store.select(getConfigState);
        this.errors = [];
        this.refreshEditors = true;
        this.closeImmediateOnConfirm = true;
        this.blankMML = '<math xmlns=\"http://www.w3.org/1998/Math/MathML\"/>';
        this.EMPTY_ANSWER_FIELD_MESSAGE = 'Answer field should not be left empty';
        this.REQUIRED_CORRECT_ANSWER_MESSAGE = 'At least one choice should be marked as a correct answer';
        this.REQUIRED_AT_LEAST_ONE_ANSWER = 'At least one answer should be entered';
        this.cellType = TableEntryCellType;
        this.confirmButtonText = 'Save';
        this.MHE_EDITOR_CHARACTER_TEXT = MHE_EDITOR_CHARACTER_TEXT;
        this.mathEditorType = 'full';
        this.cellTypeOptions = [
            {
                value: TableEntryCellType.BLANK,
                viewValue: 'Blank',
            },
            {
                value: TableEntryCellType.HEADER,
                viewValue: 'Header',
            },
            {
                value: TableEntryCellType.TEXT,
                viewValue: 'Text',
            },
            {
                value: TableEntryCellType.TEXT_ENTRY,
                viewValue: 'Text Entry',
            },
            {
                value: TableEntryCellType.EQUATION_ENTRY,
                viewValue: 'Equation Entry',
            },
            {
                value: TableEntryCellType.DROPDOWN,
                viewValue: 'Dropdown',
            },
        ];
    }
    toTextInputModel(value) {
        return value;
    }
    toTextEntryModel(value) {
        return value;
    }
    toEquationEntryModel(value) {
        return value;
    }
    toDropDownModel(value) {
        return value;
    }
    toTextHeader(value) {
        return value;
    }
    ngOnInit() {
        this.airTableEntry$ = this.store.select(getAirTableEntry);
        this.rows$ = this.airTableEntry$.pipe(map((tableEntry) => tableEntry.rows));
    }
    ngOnChanges(changes) {
        this.errors = [];
        if (changes.cellData?.firstChange) {
            this.originalCellContent = this.cellData;
        }
        if (changes.displayModal) {
            this.showEditor = this.displayModal;
            if (changes.displayModal.currentValue && !changes.displayModal.previousValue) {
                this.contentBeforeEditing = this.cellData;
                this.refreshEditors = false;
                setTimeout(() => {
                    this.refreshEditors = true;
                });
            }
        }
    }
    populateSelectedDropdownOption(cellType) {
        const selectedCellTypeValue = this.cellTypeOptions?.find((option) => option?.value === cellType);
        return selectedCellTypeValue;
    }
    cancelModal() {
        this.showEditor = false;
        this.notifyIsModalOpen.emit(false);
        this.store.dispatch(new UpdateCellAction({
            cell: this.contentBeforeEditing,
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
        this.errors = [];
    }
    closeModal() {
        this.showEditor = false;
        this.notifyIsModalOpen.emit(false);
        this.store.dispatch(new UpdateCellAction({
            cell: this.originalCellContent,
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
        this.errors = [];
    }
    onDeleteTextEntryAnswer(answerIndex) {
        this.store.dispatch(new DeleteTextEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answerIndex: answerIndex,
        }));
        this.refreshEditors = false;
        setTimeout(() => {
            this.refreshEditors = true;
        });
    }
    onDeleteEquationEntryAnswer(answerIndex) {
        this.store.dispatch(new DeleteEquationEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answerIndex: answerIndex,
        }));
        this.refreshEditors = false;
        setTimeout(() => {
            this.refreshEditors = true;
        });
    }
    onDeleteDropDownAnswer(choiceKey) {
        this.store.dispatch(new DeleteDropDownAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answerIndex: choiceKey,
        }));
    }
    confirmModal() {
        this.validateCellContents();
        if (this.errors.length < 1) {
            this.showEditor = false;
            this.notifyIsModalOpen.emit(false);
            this.store.dispatch(new UpdateCellAction({
                cell: this.cellData,
                rowIndex: this.rowIndex,
                columnIndex: this.columnIndex,
            }));
            this.errors = [];
            this.closeImmediateOnConfirm = false;
        }
        else {
            this.closeImmediateOnConfirm = true;
        }
    }
    addTextEntryAnswer() {
        this.store.dispatch(new AddTextEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
    }
    addEquationEntryAnswer() {
        this.store.dispatch(new AddEquationEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
    }
    trackDropDownKey(index, choice) {
        return choice.key;
    }
    trackIndex(index) {
        return index;
    }
    onAddDropDownChoice() {
        this.store.dispatch(new AddDropDownChoiceAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
    }
    onUpdateDropDownCorrectAnswer(choiceKey) {
        this.store.dispatch(new UpdateDropDownAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answer: choiceKey,
        }));
    }
    onDropdownContentChange(choiceKey, updatedText) {
        this.store.dispatch(new UpdateDropDownAnswerContentAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answerIndex: choiceKey,
            updatedText: updatedText,
        }));
    }
    validateCellContents() {
        this.errors = [];
        if (this.cellData.type === TableEntryCellType.TEXT_ENTRY) {
            if (this.toTextEntryModel(this.cellData).answers.length < 1) {
                this.errors.push(this.REQUIRED_AT_LEAST_ONE_ANSWER);
            }
            this.toTextEntryModel(this.cellData).answers.forEach((item) => {
                if (item.values === '') {
                    this.errors.push(this.EMPTY_ANSWER_FIELD_MESSAGE);
                }
            });
        }
        else if (this.cellData.type === TableEntryCellType.DROPDOWN) {
            this.cellData['choices'].forEach((item) => {
                if (item.value === '') {
                    this.errors.push(this.EMPTY_ANSWER_FIELD_MESSAGE);
                }
            });
            if (this.cellData['answer'] === '') {
                this.errors.push(this.REQUIRED_CORRECT_ANSWER_MESSAGE);
            }
        }
        else if (this.cellData.type === TableEntryCellType.EQUATION_ENTRY) {
            if (this.toEquationEntryModel(this.cellData).answers.length < 1) {
                this.errors.push(this.REQUIRED_AT_LEAST_ONE_ANSWER);
            }
            this.toEquationEntryModel(this.cellData).answers.forEach((item) => {
                if (item.values === '' || item.values === this.blankMML) {
                    this.errors.push(this.EMPTY_ANSWER_FIELD_MESSAGE);
                }
            });
        }
        else if (this.cellData.type === TableEntryCellType.HEADER ||
            this.cellData.type === TableEntryCellType.TEXT) {
            if (this.toTextHeader(this.cellData).text === '') {
                this.errors.push(this.EMPTY_ANSWER_FIELD_MESSAGE);
            }
        }
    }
    onEditorBlur(content) {
        this.promptValidationService.validatePrompt(content);
    }
    onTextEntryContentChange(answerIndex, event) {
        const updatedAnswer = event.target.value;
        this.store.dispatch(new UpdateTextEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answer: updatedAnswer,
            answerIndex,
        }));
    }
    onUpdatingEquationEntryAnswer(answerIndex, updatedAnswer) {
        this.store.dispatch(new UpdateEquationEntryAnswerAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            answer: updatedAnswer,
            answerIndex,
        }));
    }
    onTextInputContentChange(updatedText) {
        this.store.dispatch(new UpdateTextInputContentAction({
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
            text: updatedText,
        }));
    }
    onCellTypeSelected(event) {
        this.store.dispatch(new UpdateCellTypeAction({
            cell: { type: event.value },
            rowIndex: this.rowIndex,
            columnIndex: this.columnIndex,
        }));
    }
    isCorrectAnswer(choice) {
        return choice.key === this.cellData.answer;
    }
}
AirTableEntryCellComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTableEntryCellComponent, deps: [{ token: i1.Store }, { token: PromptValidationService }], target: i0.ɵɵFactoryTarget.Component });
AirTableEntryCellComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirTableEntryCellComponent, selector: "aa-air-table-entry-cell", inputs: { rowIndex: "rowIndex", columnIndex: "columnIndex", cellData: "cellData", displayModal: "displayModal" }, outputs: { notifyIsModalOpen: "notifyIsModalOpen" }, usesOnChanges: true, ngImport: i0, template: "<air-modal\n  [isOpen]=\"displayModal\"\n  [confirmButtonText]=\"confirmButtonText\"\n  [focusCloseOnOpen]=\"false\"\n  [closeImmediateOnConfirm]=\"closeImmediateOnConfirm\"\n  (cancel)=\"cancelModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>\n    <h3 style=\"display: inline-block;padding-right: 5px\">Cell Type: </h3>\n   <select\n      ngbAutofocus\n      class=\"form-select w-auto border-top-left-radius-6 border-bottom-left-radius-6 display-inline\"\n      [ngModel]=\"populateSelectedDropdownOption(cellData?.type)\"\n      (ngModelChange)=\"onCellTypeSelected($event)\">\n      <option\n        *ngFor=\"let cellTypeOption of cellTypeOptions\"\n        [ngValue]=\"cellTypeOption\">\n        {{cellTypeOption.viewValue}}\n      </option>\n    </select>\n  </div>\n  <div\n    *ngIf=\"cellData?.type === cellType.TEXT || cellData?.type === cellType.HEADER\">\n    <h3>Text</h3>\n    <mhe-editor\n      *ngIf=\"showEditor\"\n      [maxCharacter]=\"500\"\n      [showCharacterLimit]=\"false\"\n      [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n      [useRichText]=\"true\"\n      [toggleRichTextMenuBar]=\"false\"\n      [content]=\"toTextInputModel(cellData)?.text\"\n      [showResize]=\"false\"\n      (contentChange)=\"onTextInputContentChange($event)\"\n      [elementPath]=\"false\">\n    </mhe-editor>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n  </div>\n  <div *ngIf=\"cellData?.type === cellType.EQUATION_ENTRY && refreshEditors\">\n    <br/>\n    <h3>Answers*</h3>\n    <div\n      *ngFor=\"let answer of toEquationEntryModel(cellData).answers; let answerIndex = index; trackBy: trackIndex\">\n      <div class=\"container\">\n        <br/>\n        <div class=\"row align-items-start\">\n          <div class=\"col-lg-10\">\n            <aa-math-editor\n              [editorId]=\"'equation' + answerIndex\"\n              (updateAnswer)=\"onUpdatingEquationEntryAnswer(answerIndex, $event)\"\n              [mathml]=\"answer.values\"\n              [mathEditorType]=\"mathEditorType\"\n              [wirisMathUrl]=\"(airConfig$ | async).wirisMathUrl\"\n            ></aa-math-editor>\n          </div>\n          <div class=\"col-auto align-self-end\">\n            <aa-trash-can\n              (delete)=\"onDeleteTextEntryAnswer(answerIndex)\">\n            </aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n    <span *ngIf=\"errors.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary add-answer-btn\" (click)=\"addEquationEntryAnswer()\">\n      <span translate>+ Add Answer</span>\n    </button>\n  </div>\n  <div *ngIf=\"cellData?.type === cellType.TEXT_ENTRY && refreshEditors\"\n    class=\"air-table-text-entry-cell\">\n    <br/>\n    <h3>Answers*</h3>\n    <div\n      *ngFor=\"let answer of toTextEntryModel(cellData)?.answers; let answerIndex = index; trackBy: trackIndex\">\n      <div class=\"container\">\n        <div class=\"row align-items-start\">\n          <div class=\"col-lg-10\">\n            <p class=\"text-align-right margin-bottom-0\">{{answer.values.length}}/500</p>\n            <textarea class=\"width-p-100\"\n              (input)=\"onTextEntryContentChange(answerIndex, $event)\">{{answer.values}}</textarea>\n          </div>\n          <div class=\"col margin-top-24\">\n            <aa-trash-can\n              (delete)=\"onDeleteTextEntryAnswer(answerIndex)\">\n            </aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary add-answer-btn\" (click)=\"addTextEntryAnswer()\">\n      <span translate>+ Add Answer</span>\n    </button>\n  </div>\n\n  <div *ngIf=\"cellData?.type === cellType.DROPDOWN\">\n    <br/>\n    <h3>Answer choices *</h3>\n    <div>\n      <div class=\"answer-choice\"\n           *ngFor=\"let choice of toDropDownModel(cellData)?.choices; let choiceKey = index; trackBy: trackDropDownKey\">\n        <button type=\"button\"\n                class=\"col-xs-1 answer-selector\"\n                (click)=\"onUpdateDropDownCorrectAnswer(choice.key)\">\n              <span class=\"glyphicon glyphicon-ok\"\n                    *ngIf=\"isCorrectAnswer(choice)\"></span>\n          <span class=\"glyphicon glyphicon-remove\"\n                *ngIf=\"!isCorrectAnswer(choice)\"></span>\n        </button>\n        <mhe-editor class=\"col-lg-10 option-editor\"\n                    [maxCharacter]=\"500\"\n                    [content]=\"choice.value\"\n                    [useRichText]=\"false\"\n                    [showResize]=\"false\"\n                    (contentChange)=\"onDropdownContentChange(choiceKey, $event)\">\n        </mhe-editor>\n        <aa-trash-can class=\"col-xs-10 trash-can\"\n                      (delete)=\"onDeleteDropDownAnswer(choiceKey)\">\n        </aa-trash-can>\n      </div>\n    </div>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary\" (click)=\"onAddDropDownChoice()\">\n      + Add choice\n    </button>\n  </div>\n</air-modal>\n", styles: ["::ng-deep .modal-content{width:800px!important}.answer-choice{display:flex}.answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px;float:left;margin-top:28px}.trash-can{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;float:left;margin-top:28px}.has-error{color:#e21f26}.add-answer-btn{margin-top:20px}.display-inline{display:inline-block}.air-table-text-entry-cell .text-align-right{text-align:right}.air-table-text-entry-cell .width-p-100{width:100%}.air-table-text-entry-cell .margin-bottom-0{margin-bottom:0}.air-table-text-entry-cell .margin-top-24{margin-top:24px}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.EditorComponent, selector: "mhe-editor", inputs: ["maxCharacter", "characterText", "content", "useRichText", "noMedia", "imageOnly", "toggleRichTextMenuBar", "tinymceMenubar", "elementPath", "basicMode", "showCharacterLimit", "useA11yTooltip", "errors", "showResize"], outputs: ["contentChange", "pasteError", "escape", "editorFocusOut"] }, { kind: "component", type: MathEditorComponent, selector: "aa-math-editor", inputs: ["editorId", "mathml", "mathEditorType", "answers", "wirisMathUrl", "preset"], outputs: ["updateAnswer", "editorBlur"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i4$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: TrashCanComponent, selector: "aa-trash-can", outputs: ["delete"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTableEntryCellComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-table-entry-cell', template: "<air-modal\n  [isOpen]=\"displayModal\"\n  [confirmButtonText]=\"confirmButtonText\"\n  [focusCloseOnOpen]=\"false\"\n  [closeImmediateOnConfirm]=\"closeImmediateOnConfirm\"\n  (cancel)=\"cancelModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>\n    <h3 style=\"display: inline-block;padding-right: 5px\">Cell Type: </h3>\n   <select\n      ngbAutofocus\n      class=\"form-select w-auto border-top-left-radius-6 border-bottom-left-radius-6 display-inline\"\n      [ngModel]=\"populateSelectedDropdownOption(cellData?.type)\"\n      (ngModelChange)=\"onCellTypeSelected($event)\">\n      <option\n        *ngFor=\"let cellTypeOption of cellTypeOptions\"\n        [ngValue]=\"cellTypeOption\">\n        {{cellTypeOption.viewValue}}\n      </option>\n    </select>\n  </div>\n  <div\n    *ngIf=\"cellData?.type === cellType.TEXT || cellData?.type === cellType.HEADER\">\n    <h3>Text</h3>\n    <mhe-editor\n      *ngIf=\"showEditor\"\n      [maxCharacter]=\"500\"\n      [showCharacterLimit]=\"false\"\n      [characterText]=\"MHE_EDITOR_CHARACTER_TEXT\"\n      [useRichText]=\"true\"\n      [toggleRichTextMenuBar]=\"false\"\n      [content]=\"toTextInputModel(cellData)?.text\"\n      [showResize]=\"false\"\n      (contentChange)=\"onTextInputContentChange($event)\"\n      [elementPath]=\"false\">\n    </mhe-editor>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n  </div>\n  <div *ngIf=\"cellData?.type === cellType.EQUATION_ENTRY && refreshEditors\">\n    <br/>\n    <h3>Answers*</h3>\n    <div\n      *ngFor=\"let answer of toEquationEntryModel(cellData).answers; let answerIndex = index; trackBy: trackIndex\">\n      <div class=\"container\">\n        <br/>\n        <div class=\"row align-items-start\">\n          <div class=\"col-lg-10\">\n            <aa-math-editor\n              [editorId]=\"'equation' + answerIndex\"\n              (updateAnswer)=\"onUpdatingEquationEntryAnswer(answerIndex, $event)\"\n              [mathml]=\"answer.values\"\n              [mathEditorType]=\"mathEditorType\"\n              [wirisMathUrl]=\"(airConfig$ | async).wirisMathUrl\"\n            ></aa-math-editor>\n          </div>\n          <div class=\"col-auto align-self-end\">\n            <aa-trash-can\n              (delete)=\"onDeleteTextEntryAnswer(answerIndex)\">\n            </aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n    <span *ngIf=\"errors.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary add-answer-btn\" (click)=\"addEquationEntryAnswer()\">\n      <span translate>+ Add Answer</span>\n    </button>\n  </div>\n  <div *ngIf=\"cellData?.type === cellType.TEXT_ENTRY && refreshEditors\"\n    class=\"air-table-text-entry-cell\">\n    <br/>\n    <h3>Answers*</h3>\n    <div\n      *ngFor=\"let answer of toTextEntryModel(cellData)?.answers; let answerIndex = index; trackBy: trackIndex\">\n      <div class=\"container\">\n        <div class=\"row align-items-start\">\n          <div class=\"col-lg-10\">\n            <p class=\"text-align-right margin-bottom-0\">{{answer.values.length}}/500</p>\n            <textarea class=\"width-p-100\"\n              (input)=\"onTextEntryContentChange(answerIndex, $event)\">{{answer.values}}</textarea>\n          </div>\n          <div class=\"col margin-top-24\">\n            <aa-trash-can\n              (delete)=\"onDeleteTextEntryAnswer(answerIndex)\">\n            </aa-trash-can>\n          </div>\n        </div>\n      </div>\n    </div>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary add-answer-btn\" (click)=\"addTextEntryAnswer()\">\n      <span translate>+ Add Answer</span>\n    </button>\n  </div>\n\n  <div *ngIf=\"cellData?.type === cellType.DROPDOWN\">\n    <br/>\n    <h3>Answer choices *</h3>\n    <div>\n      <div class=\"answer-choice\"\n           *ngFor=\"let choice of toDropDownModel(cellData)?.choices; let choiceKey = index; trackBy: trackDropDownKey\">\n        <button type=\"button\"\n                class=\"col-xs-1 answer-selector\"\n                (click)=\"onUpdateDropDownCorrectAnswer(choice.key)\">\n              <span class=\"glyphicon glyphicon-ok\"\n                    *ngIf=\"isCorrectAnswer(choice)\"></span>\n          <span class=\"glyphicon glyphicon-remove\"\n                *ngIf=\"!isCorrectAnswer(choice)\"></span>\n        </button>\n        <mhe-editor class=\"col-lg-10 option-editor\"\n                    [maxCharacter]=\"500\"\n                    [content]=\"choice.value\"\n                    [useRichText]=\"false\"\n                    [showResize]=\"false\"\n                    (contentChange)=\"onDropdownContentChange(choiceKey, $event)\">\n        </mhe-editor>\n        <aa-trash-can class=\"col-xs-10 trash-can\"\n                      (delete)=\"onDeleteDropDownAnswer(choiceKey)\">\n        </aa-trash-can>\n      </div>\n    </div>\n    <span *ngIf=\"errors?.length > 0\" class=\"help-block has-error\">\n      <span *ngFor=\"let error of errors\">\n        <ul>\n          <li>{{error}}</li>\n        </ul>\n      </span>\n    </span>\n    <button class=\"btn btn-primary\" (click)=\"onAddDropDownChoice()\">\n      + Add choice\n    </button>\n  </div>\n</air-modal>\n", styles: ["::ng-deep .modal-content{width:800px!important}.answer-choice{display:flex}.answer-selector{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;border:1px solid #a9a9a9;border-radius:2px;float:left;margin-top:28px}.trash-can{height:35px;width:35px;padding:0;margin-right:.5em;background:#fff;float:left;margin-top:28px}.has-error{color:#e21f26}.add-answer-btn{margin-top:20px}.display-inline{display:inline-block}.air-table-text-entry-cell .text-align-right{text-align:right}.air-table-text-entry-cell .width-p-100{width:100%}.air-table-text-entry-cell .margin-bottom-0{margin-bottom:0}.air-table-text-entry-cell .margin-top-24{margin-top:24px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: PromptValidationService }]; }, propDecorators: { rowIndex: [{
                type: Input
            }], columnIndex: [{
                type: Input
            }], cellData: [{
                type: Input
            }], displayModal: [{
                type: Input
            }], notifyIsModalOpen: [{
                type: Output
            }] } });

class AirTableEntryComponent extends AirBaseComponent {
    constructor(store, tableEntryValidationService, configService) {
        super(store, getPureAirTableEntry, LoadAirItemAction$2, ResetStoreAction$2);
        this.store = store;
        this.tableEntryValidationService = tableEntryValidationService;
        this.configService = configService;
        this.allowInlineValidation = false;
        this.allowValidation = false;
        this.columnsLabel = 'Columns';
        this.rowsLabel = 'Rows';
        this.buttonText = 'Apply';
        this.confirmButtonText = 'Ok';
        this.placementRight = 'right';
        this._tableEntryCellType = TableEntryCellType;
        this.airTableEntry$ = store.select(getAirTableEntry);
        this.rows$ = this.airTableEntry$.pipe(map((tableEntry) => tableEntry.rows));
        this.displayModal = false;
        this.tableEntrySubscription = this.airTableEntry$.subscribe((data) => {
            this.rowsToAdd = data.rows.length;
            this.columnsToAdd = data.rows[0].cells.length;
        });
        const config = this.configService.getConfig();
        this.allowValidation = config && config['allowInlineValidation'];
    }
    ngOnDestroy() {
        this.tableEntrySubscription.unsubscribe();
        super.ngOnDestroy();
    }
    detectRowsOrColumnChange() {
        return this.hasCountOfRowsChanged || this.hasCountOfColumnsChanged;
    }
    closeModal() {
        this.showDeleteWarningMessage = false;
        this.hasCountOfRowsChanged = false;
        this.hasCountOfRowsChanged = false;
        // If users click cancel then roll back the changes by getting the latest from the store
        this.airTableEntry$.pipe(take(1)).subscribe((data) => {
            this.rowsToAdd = data.rows.length;
            this.columnsToAdd = data.rows[0].cells.length;
        });
    }
    displayProperCellTag(cell) {
        if (cell.type === TableEntryCellType.TEXT && !cell.text) {
            return 'Static Text';
        }
        else if (cell.type === TableEntryCellType.BLANK) {
            return '';
        }
        else if (cell.type === TableEntryCellType.HEADER && !cell.text) {
            return 'Header';
        }
        return '';
    }
    confirmModal() {
        this.onApplyButtonClick();
    }
    onModalStatusChange(status) {
        this.displayModal = status;
        const isModalClosed = !status;
        if (isModalClosed) {
            this.focusOnSelectedCell();
        }
    }
    // this code is needed so that after the cell was interacted with, the tabbing order would not start from the beginning of the page
    focusOnSelectedCell() {
        const selectedCellId = `cellId_${this.rowIndex.toString()}_${this.columnIndex.toString()}`;
        const cell = this.tableCells.find(({ nativeElement }) => nativeElement.id === selectedCellId);
        setTimeout(() => cell?.nativeElement.focus(), 0);
    }
    trackIndex(index) {
        return index;
    }
    toTextInputModel(value) {
        return value;
    }
    toTextEntryModel(value) {
        return value;
    }
    toEquationEntryModel(value) {
        return value;
    }
    displayModalForCell(rowIndex, columnIndex) {
        this.displayModal = true;
        this.rowIndex = rowIndex;
        this.columnIndex = columnIndex;
    }
    onAddColumns(event) {
        this.columnsToAdd = Number(event);
        this.hasCountOfColumnsChanged = true;
    }
    onAddRows(event) {
        this.rowsToAdd = Number(event);
        this.hasCountOfRowsChanged = true;
    }
    deleteRows(numberOfRows) {
        this.store.dispatch(new DeleteRowsAction({ numberOfRows }));
    }
    addRows(numberOfRows) {
        this.store.dispatch(new UpdateRowsAction({ numberOfRows }));
    }
    deleteColumns(numberOfColumns) {
        this.store.dispatch(new DeleteColumnsAction({ numberOfColumns }));
    }
    addColumns(numberOfColumns) {
        this.store.dispatch(new UpdateColumnsAction({ numberOfColumns }));
    }
    deleteRowsAndColumns(numberOfRows, numberOfColumns) {
        this.store.dispatch(new DeleteRowsAndColumnsAction({ numberOfRows, numberOfColumns }));
    }
    addRowsAndColumns(numberOfRows, numberOfColumns) {
        this.store.dispatch(new UpdateRowsAndColumnsAction({
            numberOfRows,
            numberOfColumns,
        }));
    }
    handleDeleteRowsOperation(rowsToAdd, rowCountFromStore) {
        const numberOfRowToDelete = rowCountFromStore - this.rowsToAdd;
        this.deleteRows(numberOfRowToDelete);
    }
    handleAddRowsOperation(rowsToAdd, rowCountFromStore) {
        const numberOfRowToAdd = rowsToAdd - rowCountFromStore;
        this.addRows(numberOfRowToAdd);
    }
    handleDeleteColumnsOperation(columnsToAdd, columnCountFromStore) {
        const numberOfColumnsToDelete = columnCountFromStore - this.columnsToAdd;
        this.deleteColumns(numberOfColumnsToDelete);
    }
    handleAddColumnsOperation(columnsToAdd, columnCountFromStore) {
        const numberOfColumnsToAdd = columnsToAdd - columnCountFromStore;
        this.addColumns(numberOfColumnsToAdd);
    }
    handleDeleteRowsAndColumnsOperation(columnsToAdd, columnCountFromStore, rowsToAdd, rowCountFromStore) {
        const numOfColumnsToDelete = columnCountFromStore - columnsToAdd;
        const numbOfRowToDelete = rowCountFromStore - rowsToAdd;
        this.deleteRowsAndColumns(numbOfRowToDelete, numOfColumnsToDelete);
    }
    handleAddRowsAndColumnsOperation(columnsToAdd, columnCountFromStore, rowsToAdd, rowCountFromStore) {
        const numberOfColumnsToAdd = this.columnsToAdd - columnCountFromStore;
        const numberOfRowsToAdd = this.rowsToAdd - rowCountFromStore;
        this.addRowsAndColumns(numberOfRowsToAdd, numberOfColumnsToAdd);
    }
    // This is to warn users if they are possibly trying to perform delete operation
    validateChanges() {
        let rowCountFromStore;
        let columnCountFromStore;
        // get the latest rows/columns data from store
        this.airTableEntry$.pipe(take(1)).subscribe((data) => {
            rowCountFromStore = data.rows.length;
            columnCountFromStore = data.rows[0].cells.length;
        });
        if (this.rowsToAdd < rowCountFromStore || this.columnsToAdd < columnCountFromStore) {
            this.showDeleteWarningMessage = true;
        }
        // If it gets here, then must be for adding columns and rows
        if (!this.showDeleteWarningMessage) {
            this.onApplyButtonClick();
        }
    }
    onApplyButtonClick() {
        this.showDeleteWarningMessage = false;
        this.hasCountOfRowsChanged = false;
        this.hasCountOfColumnsChanged = false;
        let rowCountFromStore;
        let columnCountFromStore;
        // get the latest rows/columns data from store
        this.airTableEntry$.pipe(take(1)).subscribe((data) => {
            rowCountFromStore = data.rows.length;
            columnCountFromStore = data.rows[0].cells.length;
        });
        // case when users change rows without changing columns
        if (this.columnsToAdd === columnCountFromStore && this.rowsToAdd !== rowCountFromStore) {
            if (this.rowsToAdd < rowCountFromStore) {
                this.handleDeleteRowsOperation(this.rowsToAdd, rowCountFromStore);
                return;
            }
            this.handleAddRowsOperation(this.rowsToAdd, rowCountFromStore);
            return;
        }
        // case when users change columns without changing rows
        if (this.rowsToAdd === rowCountFromStore && this.columnsToAdd !== columnCountFromStore) {
            if (this.columnsToAdd < columnCountFromStore) {
                this.handleDeleteColumnsOperation(this.columnsToAdd, columnCountFromStore);
                return;
            }
            this.handleAddColumnsOperation(this.columnsToAdd, columnCountFromStore);
            return;
        }
        // case when users change both columns and rows
        if (this.rowsToAdd !== rowCountFromStore && this.columnsToAdd !== columnCountFromStore) {
            if (this.columnsToAdd < columnCountFromStore || this.rowsToAdd < rowCountFromStore) {
                this.handleDeleteRowsAndColumnsOperation(this.columnsToAdd, columnCountFromStore, this.rowsToAdd, rowCountFromStore);
                return;
            }
            this.handleAddRowsAndColumnsOperation(this.columnsToAdd, columnCountFromStore, this.rowsToAdd, rowCountFromStore);
            return;
        }
    }
    getAnswerErrors() {
        return (this.allowValidation && this.tableEntryValidationService.getAnswerErrors()) || [];
    }
}
AirTableEntryComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTableEntryComponent, deps: [{ token: i1.Store }, { token: TableEntryValidationService }, { token: ConfigService }], target: i0.ɵɵFactoryTarget.Component });
AirTableEntryComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirTableEntryComponent, selector: "aa-air-table-entry", viewQueries: [{ propertyName: "tableCells", predicate: ["tableCell"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"row\">\n  <div class=\"col-sm-4\">\n    <h2 class=\"answer-options\"\n        translate>\n      Answers*\n      <button\n        class=\"btn btn-sm\"\n        type=\"button\"\n        placement=\"right\"\n        aria-label=\"Please click on a cell to start editing, or change the rows and/or columns to add cells.\"\n        ngbTooltip=\"Please click on a cell to start editing, or change the rows and/or columns to add cells.\"\n      >\n        <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n      </button>\n    </h2>\n  </div>\n</div>\n<div class=\"container\">\n  <div class=\"row no-gutters\">\n    <div class=\"col-md-1\">\n      <label for=\"columns\">{{ columnsLabel }}</label>\n      <input\n       id=\"columns\"\n       type=\"number\"\n       [value]=\"columnsToAdd\"\n       [attr.min]=\"1\"\n       [attr.step]=\"1\"\n       [attr.max]=\"100\"\n       class=\"form-control\"\n       (input)=\"onAddColumns($event.target.value)\"\n     />\n    </div>\n    <div class=\"col-md-1\">\n      <label for=\"rows\">{{ rowsLabel }}</label>\n      <input\n        id=\"rows\"\n        type=\"number\"\n        [value]=\"rowsToAdd\"\n        [attr.min]=\"1\"\n        [attr.step]=\"1\"\n        [attr.max]=\"100\"\n        class=\"form-control\"\n        (input)=\"onAddRows($event.target.value)\"\n      />\n    </div>\n    <div class=\"col-md-1 button-position\">\n      <button\n        class=\"btn btn-primary preview-modal-btn\"\n        (click)=\"validateChanges()\"\n        [disabled]=\"!detectRowsOrColumnChange()\">\n        {{ buttonText }}\n      </button>\n    </div>\n  </div>\n</div>\n<br />\n<air-modal\n  [isOpen]=\"showDeleteWarningMessage\"\n  [confirmButtonText]=\"confirmButtonText\"\n  (cancel)=\"closeModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>This will delete data from table. This is not a reversible action. Are you sure you want to continue?</div>\n</air-modal>\n<div\n  class=\"wrapper\"\n  [ngClass]=\"{ 'error': getAnswerErrors()?.length }\">\n  <table class=\"table table-bordered main-table air-table-entry\">\n    <thead>\n    </thead>\n    <tbody>\n      <tr *ngFor=\"let row of (airTableEntry$ | async).rows; let rowIndex = index; trackBy: trackIndex\">\n        <ng-container *ngFor=\"let cell of row.cells; let columnIndex = index\">\n          <ng-container *ngIf=\"cell.type === _tableEntryCellType.HEADER\">\n            <th class=\"static-text-header\"\n                (click)=\"displayModalForCell(rowIndex, columnIndex)\"\n                (keyup.enter)=\"displayModalForCell(rowIndex, columnIndex)\"\n                id=\"cellId_{{rowIndex}}_{{columnIndex}}\"\n                tabindex=\"0\"\n                [appMath]=\"toTextInputModel(cell).text\"\n            #tableCell></th>\n          </ng-container>\n          <ng-container *ngIf=\"cell.type !== _tableEntryCellType.HEADER\">\n            <td (click)=\"displayModalForCell(rowIndex, columnIndex)\"\n                (keyup.enter)=\"displayModalForCell(rowIndex, columnIndex)\"\n                id=\"cellId_{{rowIndex}}_{{columnIndex}}\"\n                tabindex=\"0\"\n                #tableCell>\n              <div>\n                <div>{{displayProperCellTag(cell)}}</div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.TEXT\" [appMath]=\"toTextInputModel(cell).text\"\n                    class=\"static-text-header\"></div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.DROPDOWN\">\n                  <select id=\"dropdown\">\n                    <option>Select Choice</option>\n                  </select>\n                </div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.TEXT_ENTRY\" class=\"text-entry-border\">\n                  <ul>\n                  <li\n                    class=\"text-entry-values\"\n                    *ngFor=\"let answer of toTextEntryModel(cell).answers\"\n                    [appMath]=\"answer.values\"\n                  >\n                  </li>\n                  </ul>\n                </div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.EQUATION_ENTRY\" class=\"equation-entry-border\">\n                  <ul>\n                    <li\n                      class=\"equation-entry-values\"\n                      *ngFor=\"let answer of toEquationEntryModel(cell).answers\"\n                      [appMath]=\"answer.values\"\n                    >\n                    </li>\n                  </ul>\n                </div>\n              </div>\n            </td>\n          </ng-container>\n        </ng-container>\n      </tr>\n    </tbody>\n  </table>\n</div>\n\n<aa-inline-error [errors]=\"getAnswerErrors()\"></aa-inline-error>\n\n\n  <aa-air-table-entry-cell (notifyIsModalOpen)=\"onModalStatusChange($event)\"\n                           [cellData]=\"(airTableEntry$ | async)?.rows[rowIndex]?.cells[columnIndex]\"\n                           [rowIndex]=\"rowIndex\"\n                           [columnIndex]=\"columnIndex\"\n                           [displayModal]=\"displayModal\">\n  </aa-air-table-entry-cell>\n\n\n\n", styles: [".dpg-icon-system-alert-info:before,.ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23265867' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.btn-secondary-light .dpg-icon-system-alert-info:before,.btn-secondary-light .ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .ahe-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .ahe-icon-system-alert-info:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.air-table-entry td,.air-table-entry th:hover{cursor:pointer}.air-table-entry .button-position{margin-top:25px}.main-table{display:flex;border:0px}.main-table tbody{width:100%;max-width:100%;overflow:auto}.text-entry-border,.equation-entry-border{border:1px solid black;max-height:80px;overflow-y:auto;padding:2px}:host ::ng-deep .text-entry-border .text-entry-values,:host ::ng-deep .text-entry-border .equation-entry-values,:host ::ng-deep .equation-entry-border .text-entry-values,:host ::ng-deep .equation-entry-border .equation-entry-values{max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;list-style-position:inside}:host ::ng-deep .static-text-header>p{max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.wrapper{display:inline-block}.wrapper.error{border:1px solid red}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i6.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "directive", type: MathDirective, selector: "[appMath]", inputs: ["appMath"] }, { kind: "component", type: i2$1.ModalComponent, selector: "air-modal", inputs: ["isOpen", "focusElementOnClose", "titleText", "cancelButtonText", "confirmButtonText", "disableConfirm", "showFooter", "modalSize", "focusCloseOnOpen", "closeImmediateOnConfirm"], outputs: ["close", "confirm", "cancel"] }, { kind: "directive", type: i4$1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i4$1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "component", type: AirTableEntryCellComponent, selector: "aa-air-table-entry-cell", inputs: ["rowIndex", "columnIndex", "cellData", "displayModal"], outputs: ["notifyIsModalOpen"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirTableEntryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-table-entry', template: "<div class=\"row\">\n  <div class=\"col-sm-4\">\n    <h2 class=\"answer-options\"\n        translate>\n      Answers*\n      <button\n        class=\"btn btn-sm\"\n        type=\"button\"\n        placement=\"right\"\n        aria-label=\"Please click on a cell to start editing, or change the rows and/or columns to add cells.\"\n        ngbTooltip=\"Please click on a cell to start editing, or change the rows and/or columns to add cells.\"\n      >\n        <i class=\"dpg-icon dpg-icon-system-alert-info\" aria-hidden=\"true\"></i>\n      </button>\n    </h2>\n  </div>\n</div>\n<div class=\"container\">\n  <div class=\"row no-gutters\">\n    <div class=\"col-md-1\">\n      <label for=\"columns\">{{ columnsLabel }}</label>\n      <input\n       id=\"columns\"\n       type=\"number\"\n       [value]=\"columnsToAdd\"\n       [attr.min]=\"1\"\n       [attr.step]=\"1\"\n       [attr.max]=\"100\"\n       class=\"form-control\"\n       (input)=\"onAddColumns($event.target.value)\"\n     />\n    </div>\n    <div class=\"col-md-1\">\n      <label for=\"rows\">{{ rowsLabel }}</label>\n      <input\n        id=\"rows\"\n        type=\"number\"\n        [value]=\"rowsToAdd\"\n        [attr.min]=\"1\"\n        [attr.step]=\"1\"\n        [attr.max]=\"100\"\n        class=\"form-control\"\n        (input)=\"onAddRows($event.target.value)\"\n      />\n    </div>\n    <div class=\"col-md-1 button-position\">\n      <button\n        class=\"btn btn-primary preview-modal-btn\"\n        (click)=\"validateChanges()\"\n        [disabled]=\"!detectRowsOrColumnChange()\">\n        {{ buttonText }}\n      </button>\n    </div>\n  </div>\n</div>\n<br />\n<air-modal\n  [isOpen]=\"showDeleteWarningMessage\"\n  [confirmButtonText]=\"confirmButtonText\"\n  (cancel)=\"closeModal()\"\n  (close)=\"closeModal()\"\n  (confirm)=\"confirmModal()\">\n  <div>This will delete data from table. This is not a reversible action. Are you sure you want to continue?</div>\n</air-modal>\n<div\n  class=\"wrapper\"\n  [ngClass]=\"{ 'error': getAnswerErrors()?.length }\">\n  <table class=\"table table-bordered main-table air-table-entry\">\n    <thead>\n    </thead>\n    <tbody>\n      <tr *ngFor=\"let row of (airTableEntry$ | async).rows; let rowIndex = index; trackBy: trackIndex\">\n        <ng-container *ngFor=\"let cell of row.cells; let columnIndex = index\">\n          <ng-container *ngIf=\"cell.type === _tableEntryCellType.HEADER\">\n            <th class=\"static-text-header\"\n                (click)=\"displayModalForCell(rowIndex, columnIndex)\"\n                (keyup.enter)=\"displayModalForCell(rowIndex, columnIndex)\"\n                id=\"cellId_{{rowIndex}}_{{columnIndex}}\"\n                tabindex=\"0\"\n                [appMath]=\"toTextInputModel(cell).text\"\n            #tableCell></th>\n          </ng-container>\n          <ng-container *ngIf=\"cell.type !== _tableEntryCellType.HEADER\">\n            <td (click)=\"displayModalForCell(rowIndex, columnIndex)\"\n                (keyup.enter)=\"displayModalForCell(rowIndex, columnIndex)\"\n                id=\"cellId_{{rowIndex}}_{{columnIndex}}\"\n                tabindex=\"0\"\n                #tableCell>\n              <div>\n                <div>{{displayProperCellTag(cell)}}</div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.TEXT\" [appMath]=\"toTextInputModel(cell).text\"\n                    class=\"static-text-header\"></div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.DROPDOWN\">\n                  <select id=\"dropdown\">\n                    <option>Select Choice</option>\n                  </select>\n                </div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.TEXT_ENTRY\" class=\"text-entry-border\">\n                  <ul>\n                  <li\n                    class=\"text-entry-values\"\n                    *ngFor=\"let answer of toTextEntryModel(cell).answers\"\n                    [appMath]=\"answer.values\"\n                  >\n                  </li>\n                  </ul>\n                </div>\n                <div *ngIf=\"cell.type === _tableEntryCellType.EQUATION_ENTRY\" class=\"equation-entry-border\">\n                  <ul>\n                    <li\n                      class=\"equation-entry-values\"\n                      *ngFor=\"let answer of toEquationEntryModel(cell).answers\"\n                      [appMath]=\"answer.values\"\n                    >\n                    </li>\n                  </ul>\n                </div>\n              </div>\n            </td>\n          </ng-container>\n        </ng-container>\n      </tr>\n    </tbody>\n  </table>\n</div>\n\n<aa-inline-error [errors]=\"getAnswerErrors()\"></aa-inline-error>\n\n\n  <aa-air-table-entry-cell (notifyIsModalOpen)=\"onModalStatusChange($event)\"\n                           [cellData]=\"(airTableEntry$ | async)?.rows[rowIndex]?.cells[columnIndex]\"\n                           [rowIndex]=\"rowIndex\"\n                           [columnIndex]=\"columnIndex\"\n                           [displayModal]=\"displayModal\">\n  </aa-air-table-entry-cell>\n\n\n\n", styles: [".dpg-icon-system-alert-info:before,.ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23265867' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.btn-secondary-light .dpg-icon-system-alert-info:before,.btn-secondary-light .ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .ahe-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .ahe-icon-system-alert-info:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .dpg-icon-system-alert-info:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .ahe-icon-system-alert-info:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-2-6h1v-4h-1v-2h3v6h1v2h-4v-2zm1-10h2v2h-2V6z'/%3E%3C/svg%3E\")}.air-table-entry td,.air-table-entry th:hover{cursor:pointer}.air-table-entry .button-position{margin-top:25px}.main-table{display:flex;border:0px}.main-table tbody{width:100%;max-width:100%;overflow:auto}.text-entry-border,.equation-entry-border{border:1px solid black;max-height:80px;overflow-y:auto;padding:2px}:host ::ng-deep .text-entry-border .text-entry-values,:host ::ng-deep .text-entry-border .equation-entry-values,:host ::ng-deep .equation-entry-border .text-entry-values,:host ::ng-deep .equation-entry-border .equation-entry-values{max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;list-style-position:inside}:host ::ng-deep .static-text-header>p{max-width:200px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.wrapper{display:inline-block}.wrapper.error{border:1px solid red}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: TableEntryValidationService }, { type: ConfigService }]; }, propDecorators: { tableCells: [{
                type: ViewChildren,
                args: ['tableCell']
            }] } });

// This model encapsulates drawing of shapes on an svg
// should support polyline, circle, polygon
class Shape extends EventEmitter$1 {
    constructor(svg, shapeUuid, shapeType, shapeAttributes) {
        super();
        this.dragging = false;
        this.svg = svg;
        this.parentSvgD3 = d3.select(this.svg);
        this.shapeType = shapeType;
        this.shapeAttributes = shapeAttributes;
        this.shapeUuid = shapeUuid;
    }
    draw(parentSvg = null) {
        const parentSvgToUSe = parentSvg ? d3.select(parentSvg) : this.parentSvgD3;
        this.shape = parentSvgToUSe.append(this.shapeType);
        this.shape.attr('id', this.shapeUuid);
        for (const attr of this.shapeAttributes) {
            this.shape.attr(attr.name, attr.value);
        }
    }
    update(attributes) {
        for (const attr of attributes) {
            this.shape.attr(attr.name, attr.value);
        }
    }
    updateShapeAttributes(shapeAttributes, updates) {
        for (const update of updates) {
            const obj = shapeAttributes.find((obj) => obj.name === update.name);
            if (obj) {
                obj.value = update.value;
            }
        }
    }
    remove() {
        d3.select(`[id="${this.shapeUuid}"]`)?.remove();
    }
    // explore this to position element to a group
    addToParentGroup(svg) { }
    addDragBehaviourToCirclesOnPolygon() {
        // TODO: Fix types
        const parentNode = this.shape.nodes()[0].parentNode;
        const poly = d3.select(parentNode).select('polygon');
        let circles = d3.select(parentNode).selectAll('circle');
        let pointStr = '';
        const dragBehaviour = d3Drag
            .drag()
            .on('start', (event) => {
            this.dragEventSourceElementId = event.sourceEvent.srcElement.id;
        })
            .on('end', (event, d) => {
            pointStr = '';
            d3.select(`[id="${this.dragEventSourceElementId}"]`).attr('cx', event.x).attr('cy', event.y);
            d3.select(`[id="${this.shapeUuid}"]`);
            circles = d3.select(parentNode).selectAll('circle');
            circles.each((data, index, nodes) => {
                const circle = nodes[index];
                pointStr = pointStr + ` ${circle.getAttribute('cx')},${circle.getAttribute('cy')}`;
            });
            const attributeUpdates = [{ name: 'points', value: pointStr }];
            this.update(attributeUpdates);
            this.updateShapeAttributes(this.shapeAttributes, attributeUpdates);
            this.emit('dragEnd', { point: pointStr, uuid: this.shapeUuid });
        });
        circles.call(dragBehaviour);
    }
    addDragBehaviourToCirclesOnRectangle() {
        const parentNode = this.shape.nodes()[0].parentNode;
        const circles = d3.select(parentNode).selectAll('circle');
        const dragBehaviour = d3Drag
            .drag()
            .on('start', (event) => {
            this.dragEventSourceElementId = event.sourceEvent.srcElement.id;
            this.parentGroup = event.sourceEvent.srcElement.parentNode;
        })
            .on('end', (event, d) => {
            const draggedElem = d3.select(`[id="${this.dragEventSourceElementId}"]`);
            draggedElem.attr('cx', event.x).attr('cy', event.y);
            let startCoords = {
                x: 0,
                y: 0,
            };
            const parentNode = d3.select(this.parentGroup);
            if (draggedElem.classed('topLeft')) {
                startCoords = {
                    x: Number(parentNode.select('.bottomRight').attr('cx')),
                    y: Number(parentNode.select('.bottomRight').attr('cy')),
                };
            }
            if (draggedElem.classed('bottomRight')) {
                startCoords = {
                    x: Number(parentNode.select('.topLeft').attr('cx')),
                    y: Number(parentNode.select('.topLeft').attr('cy')),
                };
            }
            if (draggedElem.classed('topRight')) {
                startCoords = {
                    x: Number(parentNode.select('.bottomLeft').attr('cx')),
                    y: Number(parentNode.select('.bottomLeft').attr('cy')),
                };
            }
            if (draggedElem.classed('bottomLeft')) {
                startCoords = {
                    x: Number(parentNode.select('.topRight').attr('cx')),
                    y: Number(parentNode.select('.topRight').attr('cy')),
                };
            }
            const rectData = {
                rectWidth: Math.abs(startCoords.x - event.x),
                rectHeight: Math.abs(startCoords.y - event.y),
                rectX: Math.min(startCoords.x, event.x),
                rectY: Math.min(startCoords.y, event.y),
                rectUuid: parentNode.select('rect').node().getAttribute('id'),
            };
            this.emit('rectDragEnd', { rectData, parentNode });
        });
        circles.call(dragBehaviour);
    }
    addDragBehaviourToEllipse() {
        const parentNode = this.shape.nodes()[0].parentNode;
        let ellipses = d3.select(parentNode).selectAll('ellipse');
        ellipses.on('click', (event) => {
            if (!document.querySelector('.ellipse-drawing.active')) {
                d3.select(event.target).on('.drag', null);
                event.preventDefault();
                return;
            }
            const dragBehaviour = d3Drag
                .drag()
                .on('start', (event) => {
                if (!document.querySelector('.ellipse-drawing.active')) {
                    return; // Do not initiate drag if not in ellipse drawing mode
                }
                this.dragEventSourceElementId = event.sourceEvent.srcElement.id;
                this.parentGroup = event.sourceEvent.srcElement.parentNode;
                d3.select(`.ellipse[id="${this.dragEventSourceElementId}"]`).attr('stroke-width', 4);
            })
                .on('drag', (event) => {
                if (!document.querySelector('.ellipse-drawing.active')) {
                    return; // Do not drag if not in ellipse drawing mode
                }
                const draggedElem = d3.select(`.ellipse[id="${this.dragEventSourceElementId}"]`);
                const cx = +draggedElem.attr('cx') + event.dx;
                const cy = +draggedElem.attr('cy') + event.dy;
                draggedElem.attr('cx', cx).attr('cy', cy);
            })
                .on('end', (event) => {
                if (!document.querySelector('.ellipse-drawing.active')) {
                    return; // Do not perform end actions if not in ellipse drawing mode
                }
                const updatedCoords = {
                    x: 0,
                    y: 0,
                    radiusX: 0,
                    radiusY: 0,
                };
                d3.select(`.ellipse[id="${this.dragEventSourceElementId}"]`).attr('cx', event.x).attr('cy', event.y);
                d3.select(`[id="${this.shapeUuid}"]`);
                ellipses = d3.select(parentNode).selectAll('ellipse');
                ellipses.each((data, index, nodes) => {
                    const ellipse = nodes[index];
                    updatedCoords.x = ellipse.getAttribute('cx');
                    updatedCoords.y = ellipse.getAttribute('cy');
                    updatedCoords.radiusX = ellipse.getAttribute('rx');
                    updatedCoords.radiusY = ellipse.getAttribute('ry');
                });
                d3.select(`.ellipse[id="${this.dragEventSourceElementId}"]`).attr('stroke-width', 2);
                const attributeUpdates = [
                    { name: 'cx', value: updatedCoords.x },
                    { name: 'cy', value: updatedCoords.y },
                    { name: 'rx', value: updatedCoords.radiusX },
                    { name: 'ry', value: updatedCoords.radiusY },
                ];
                this.update(attributeUpdates);
                this.updateShapeAttributes(this.shapeAttributes, attributeUpdates);
                this.emit('ellipseDragEnd', { shapeData: updatedCoords, uuid: this.shapeUuid });
            });
            ellipses.call(dragBehaviour);
        });
    }
    getData(shapeAttributes, key) {
        return shapeAttributes.find((obj) => obj.name === key)?.value;
    }
    isPointInsidePolygon(point) {
        const points = this.getData(this.shapeAttributes, 'points');
        const pointsArray = points.split(' ').map((pair) => pair.split(',').map(Number));
        return d3.polygonContains(pointsArray, [point.x, point.y]);
    }
    isPointInsideRectangle(point) {
        const rectX = this.getData(this.shapeAttributes, 'x');
        const rectY = this.getData(this.shapeAttributes, 'y');
        const rectWidth = this.getData(this.shapeAttributes, 'width');
        const rectHeight = this.getData(this.shapeAttributes, 'height');
        return point.x >= rectX && point.x <= rectX + rectWidth && point.y >= rectY && point.y <= rectY + rectHeight;
    }
    isPointInsideEllipse(point) {
        const cx = this.getData(this.shapeAttributes, 'cx');
        const cy = this.getData(this.shapeAttributes, 'cy');
        const rx = this.getData(this.shapeAttributes, 'rx');
        const ry = this.getData(this.shapeAttributes, 'ry');
        const normalizedX = (point.x - cx) / rx;
        const normalizedY = (point.y - cy) / ry;
        return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
    }
    toggleHighLightIfPointInsideShape(point, shapeType) {
        let inside = false;
        if (shapeType === SVGShapeType.POLYGON) {
            inside = this.isPointInsidePolygon(point);
        }
        else if (shapeType === SVGShapeType.RECT) {
            inside = this.isPointInsideRectangle(point);
        }
        else if (shapeType === SVGShapeType.ELLIPSE) {
            inside = this.isPointInsideEllipse(point);
        }
        if (inside) {
            if (this.shape.classed('shape-selected')) {
                this.shape.classed('shape-selected', false);
            }
            else {
                this.shape.classed('shape-selected', true);
            }
            this.emit('answerSelection');
        }
        return inside;
    }
}

class ColorPickerComponent {
    constructor() {
        this.colors = [
            'rgb(0, 0, 0)',
            'rgb(51, 0, 0)',
            'rgb(102, 0, 0)',
            'rgb(153, 0, 0)',
            'rgb(204, 0, 0)',
            'rgb(255, 0, 0)',
            'rgb(255, 51, 51)',
            'rgb(255, 102, 102)',
            'rgb(255, 153, 153)',
            'rgb(255, 204, 204)',
            'rgb(255, 255, 255)',
            'rgb(51, 0, 0)',
            'rgb(92, 0, 0)',
            'rgb(133, 0, 0)',
            'rgb(173, 0, 0)',
            'rgb(214, 0, 0)',
            'rgb(255, 0, 0)',
            'rgb(255, 41, 41)',
            'rgb(255, 82, 82)',
            'rgb(255, 122, 122)',
            'rgb(255, 163, 163)',
            'rgb(255, 204, 204)',
            'rgb(51, 26, 0)',
            'rgb(92, 46, 0)',
            'rgb(133, 66, 0)',
            'rgb(173, 87, 0)',
            'rgb(214, 107, 0)',
            'rgb(255, 128, 0)',
            'rgb(255, 148, 41)',
            'rgb(255, 168, 82)',
            'rgb(255, 189, 122)',
            'rgb(255, 209, 163)',
            'rgb(255, 230, 204)',
            'rgb(51, 51, 0)',
            'rgb(92, 92, 0)',
            'rgb(133, 133, 0)',
            'rgb(173, 173, 0)',
            'rgb(214, 214, 0)',
            'rgb(255, 255, 0)',
            'rgb(255, 255, 41)',
            'rgb(255, 255, 82)',
            'rgb(255, 255, 122)',
            'rgb(255, 255, 163)',
            'rgb(255, 255, 204)',
            'rgb(26, 51, 0)',
            'rgb(46, 92, 0)',
            'rgb(66, 133, 0)',
            'rgb(87, 173, 0)',
            'rgb(107, 214, 0)',
            'rgb(128, 255, 0)',
            'rgb(148, 255, 41)',
            'rgb(168, 255, 82)',
            'rgb(189, 255, 122)',
            'rgb(209, 255, 163)',
            'rgb(230, 255, 204)',
            'rgb(0, 51, 0)',
            'rgb(0, 92, 0)',
            'rgb(0, 133, 0)',
            'rgb(0, 173, 0)',
            'rgb(0, 214, 0)',
            'rgb(0, 255, 0)',
            'rgb(41, 255, 41)',
            'rgb(82, 255, 82)',
            'rgb(122, 255, 122)',
            'rgb(163, 255, 163)',
            'rgb(204, 255, 204)',
            'rgb(0, 51, 26)',
            'rgb(0, 92, 46)',
            'rgb(0, 133, 66)',
            'rgb(0, 173, 87)',
            'rgb(0, 214, 107)',
            'rgb(0, 255, 128)',
            'rgb(41, 255, 148)',
            'rgb(82, 255, 168)',
            'rgb(122, 255, 189)',
            'rgb(163, 255, 209)',
            'rgb(204, 255, 230)',
            'rgb(0, 51, 51)',
            'rgb(0, 92, 92)',
            'rgb(0, 133, 133)',
            'rgb(0, 173, 173)',
            'rgb(0, 214, 214)',
            'rgb(0, 255, 255)',
            'rgb(41, 255, 255)',
            'rgb(82, 255, 255)',
            'rgb(122, 255, 255)',
            'rgb(163, 255, 255)',
            'rgb(204, 255, 255)',
            'rgb(0, 26, 51)',
            'rgb(0, 46, 92)',
            'rgb(0, 66, 133)',
            'rgb(0, 87, 173)',
            'rgb(0, 107, 214)',
            'rgb(0, 128, 255)',
            'rgb(41, 148, 255)',
            'rgb(82, 168, 255)',
            'rgb(122, 189, 255)',
            'rgb(163, 209, 255)',
            'rgb(204, 230, 255)',
            'rgb(0, 0, 51)',
            'rgb(0, 0, 92)',
            'rgb(0, 0, 133)',
            'rgb(0, 0, 173)',
            'rgb(0, 0, 214)',
            'rgb(0, 0, 255)',
            'rgb(41, 41, 255)',
            'rgb(82, 82, 255)',
            'rgb(122, 122, 255)',
            'rgb(163, 163, 255)',
            'rgb(204, 204, 255)',
            'rgb(26, 0, 51)',
            'rgb(46, 0, 92)',
            'rgb(66, 0, 133)',
            'rgb(87, 0, 173)',
            'rgb(107, 0, 214)',
            'rgb(128, 0, 255)',
            'rgb(148, 41, 255)',
            'rgb(168, 82, 255)',
            'rgb(189, 122, 255)',
            'rgb(209, 163, 255)',
            'rgb(230, 204, 255)',
            'rgb(51, 0, 51)',
            'rgb(92, 0, 92)',
            'rgb(133, 0, 133)',
            'rgb(173, 0, 173)',
            'rgb(214, 0, 214)',
            'rgb(255, 0, 255)',
            'rgb(255, 41, 255)',
            'rgb(255, 82, 255)',
            'rgb(255, 122, 255)',
            'rgb(255, 163, 255)',
            'rgb(255, 204, 255)',
            'rgb(51, 0, 26)',
            'rgb(92, 0, 46)',
            'rgb(133, 0, 66)',
            'rgb(173, 0, 87)',
            'rgb(214, 0, 107)',
            'rgb(255, 0, 128)',
            'rgb(255, 41, 148)',
            'rgb(255, 82, 168)',
            'rgb(255, 122, 189)',
            'rgb(255, 163, 209)',
            'rgb(255, 204, 230)',
        ];
        this.selectedColor = this.colors[10];
        this.showColorPanel = false;
        this.colorSelected = new EventEmitter();
        this.panelStatus = new EventEmitter();
    }
    ngOnInit() { }
    selectColor(color) {
        this.selectedColor = color;
        this.colorSelected.emit(color);
        this.setColorPanelVisibility(false);
    }
    colorCellKeydown(event, color) {
        if (this.detectSpaceOrEnter(event)) {
            event.preventDefault();
            this.selectColor(color);
        }
    }
    colorPickerKeydown(event) {
        if (this.detectSpaceOrEnter(event)) {
            event.preventDefault();
            this.setColorPanelVisibility(!this.showColorPanel);
        }
    }
    detectSpaceOrEnter(event) {
        const keyCode = event?.keyCode;
        return keyCode === 13 || keyCode === 32;
    }
    setColorPanelVisibility(visibility) {
        this.showColorPanel = visibility;
        this.panelStatus.emit(visibility);
    }
}
ColorPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ColorPickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ColorPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: ColorPickerComponent, selector: "color-picker", inputs: { selectedColor: "selectedColor", showColorPanel: "showColorPanel" }, outputs: { colorSelected: "colorSelected", panelStatus: "panelStatus" }, ngImport: i0, template: "<div class=\"position-relative d-inline-block\">\n  <div\n    tabindex=\"0\"\n    class=\"d-inline-block position-relative current-color\"\n    attr.style=\"background-color: {{selectedColor}}\"\n    (keydown)=\"colorPickerKeydown($event)\"\n    (keydown.escape)=\"setColorPanelVisibility(false)\"\n    (click)=\"setColorPanelVisibility(!showColorPanel)\"\n  ></div>\n  <div\n    *ngIf=\"showColorPanel\"\n    class=\"position-absolute color-panel\"\n  >\n    <div\n      *ngFor=\"let color of colors; let i = index\"\n      tabindex=\"0\"\n      attr.style=\"background-color: {{color}}\"\n      class=\"color-cell\"\n      (keydown.escape)=\"setColorPanelVisibility(false)\"\n      (keydown)=\"colorCellKeydown($event, color)\"\n      (click)=\"selectColor(color)\"\n    >\n    </div>\n  </div>\n</div>\n", styles: [".current-color{border:1px solid grey;border-radius:5px;height:25px;top:5px;width:25px}.color-panel{z-index:1;border:1px solid #000;width:222px;display:inline-flex;flex-wrap:wrap;.color-cell {width: 20px; height: 20px; cursor: pointer; &:hover {outline: 2px auto #000000;}}}\n"], dependencies: [{ kind: "directive", type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ColorPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'color-picker', template: "<div class=\"position-relative d-inline-block\">\n  <div\n    tabindex=\"0\"\n    class=\"d-inline-block position-relative current-color\"\n    attr.style=\"background-color: {{selectedColor}}\"\n    (keydown)=\"colorPickerKeydown($event)\"\n    (keydown.escape)=\"setColorPanelVisibility(false)\"\n    (click)=\"setColorPanelVisibility(!showColorPanel)\"\n  ></div>\n  <div\n    *ngIf=\"showColorPanel\"\n    class=\"position-absolute color-panel\"\n  >\n    <div\n      *ngFor=\"let color of colors; let i = index\"\n      tabindex=\"0\"\n      attr.style=\"background-color: {{color}}\"\n      class=\"color-cell\"\n      (keydown.escape)=\"setColorPanelVisibility(false)\"\n      (keydown)=\"colorCellKeydown($event, color)\"\n      (click)=\"selectColor(color)\"\n    >\n    </div>\n  </div>\n</div>\n", styles: [".current-color{border:1px solid grey;border-radius:5px;height:25px;top:5px;width:25px}.color-panel{z-index:1;border:1px solid #000;width:222px;display:inline-flex;flex-wrap:wrap;.color-cell {width: 20px; height: 20px; cursor: pointer; &:hover {outline: 2px auto #000000;}}}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { selectedColor: [{
                type: Input
            }], showColorPanel: [{
                type: Input
            }], colorSelected: [{
                type: Output
            }], panelStatus: [{
                type: Output
            }] } });

class AirHotspotComponent extends AirBaseComponent {
    constructor(renderer, cdRef, store, configService, hotspotValidationService, sanitizeInputService) {
        super(store, getPureAirHotspot, LoadAirItemAction$1, ResetStoreAction$1);
        this.renderer = renderer;
        this.cdRef = cdRef;
        this.store = store;
        this.configService = configService;
        this.hotspotValidationService = hotspotValidationService;
        this.sanitizeInputService = sanitizeInputService;
        this.alive$ = new Subject();
        this.airHotspot$ = this.store.select(getAirHotspot);
        this.pureAirHotspot$ = this.store.select(getPureAirHotspot);
        this.shapes = [];
        this.activePointsBeingPlotted = [];
        this.polygonCount = 0;
        this.strokeColor = 'blue';
        this.pureShapes = [];
        this.answers = [];
        this.allowInlineValidation = false;
        this.errors = [];
        this.showColorPanel = false;
        this.startCoords = {
            x: 0,
            y: 0,
            hasClicked: false,
        };
        this.isDragEnded = false;
        this.startEllipse = false;
        this.drawEllipseOnMove = false;
        this.startX = 0;
        this.startY = 0;
        this.radiusX = 0;
        this.radiusY = 0;
        const config = this.configService.getConfig();
        this.allowInlineValidation = config && config['allowInlineValidation'];
    }
    ngAfterViewInit() {
        this.hotspotSubscription = this.airHotspot$.pipe(take$1(2), takeUntil$1(this.alive$)).subscribe((airHotspot) => {
            this.answers = airHotspot?.answers || [];
            if (airHotspot.shapes?.length) {
                this.alive$.next(true);
            }
            airHotspot.shapes?.map((shape) => {
                this.parentElementForShapes = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                if (isOfShapeType(shape, ShapeTypeEnum.POLYGON)) {
                    this.parentElementForShapes.setAttribute('class', 'polygon-group');
                }
                else if (isOfShapeType(shape, ShapeTypeEnum.RECTANGLE)) {
                    this.parentElementForShapes.setAttribute('class', 'rect-group');
                }
                else if (isOfShapeType(shape, ShapeTypeEnum.ELLIPSE)) {
                    this.parentElementForShapes.setAttribute('class', 'ellipse-group');
                }
                // TODO: Ellipses doesn't have it's own shape class set
                this.renderer.appendChild(this.parentSvg.nativeElement, this.parentElementForShapes);
                if (isOfShapeType(shape, ShapeTypeEnum.POLYGON)) {
                    shape.shapeData?.map((point) => {
                        const circleAttributes = [
                            { name: 'cx', value: point.x },
                            { name: 'cy', value: point.y },
                            { name: 'r', value: 7 },
                            { name: 'class', value: 'dragHandlers' },
                        ];
                        const pointOfClick = new Shape(this.parentElementForShapes, generateUuid(circleAttributes), 'circle', circleAttributes);
                        pointOfClick.draw();
                        this.shapes.push(pointOfClick);
                    });
                    this.drawPolygon(shape, this.parentElementForShapes.nativeElement);
                }
                else if (isOfShapeType(shape, ShapeTypeEnum.ELLIPSE)) {
                    this.drawEllipse(shape);
                }
                else if (isOfShapeType(shape, ShapeTypeEnum.RECTANGLE)) {
                    let rectData = {
                        rectWidth: shape.shapeData.width,
                        rectHeight: shape.shapeData.length,
                        rectX: shape.shapeData.x,
                        rectY: shape.shapeData.y,
                    };
                    rectData = {
                        ...rectData,
                        fillColor: shape.fillColor,
                        strokeColor: shape.strokeColor,
                        rectUuid: shape.uuid,
                    };
                    this.drawRect(null, true, rectData);
                    this.rect = null;
                }
            });
        });
    }
    deleteFile($event) {
        this.updateFile($event);
    }
    updateFile($event) {
        this.store.dispatch(new UpdateImageAction($event));
    }
    updateShapeInStore(payload) {
        this.store.dispatch(new UpdateShapesAction(payload));
    }
    getSVGPoint(e) {
        const pt = this.parentSvg.nativeElement.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        // convert screen coordinates to local user coordinates
        return pt.matrixTransform(this.parentSvg.nativeElement.getScreenCTM()?.inverse());
    }
    panelStatus(visibility) {
        this.showColorPanel = visibility;
    }
    toggleDelete() {
        this.isDeleting = !this.isDeleting;
        this.isDrawingPolygon =
            this.isDrawingRectangle =
                this.isDrawingEllipse =
                    this.markAnswerSelection =
                        this.colorPickerSelection =
                            this.showColorPanel =
                                false;
    }
    toggleDrawingPolygon() {
        this.isDrawingPolygon = !this.isDrawingPolygon;
        this.isDrawingRectangle =
            this.isDrawingEllipse =
                this.markAnswerSelection =
                    this.isDeleting =
                        this.colorPickerSelection =
                            this.showColorPanel =
                                false;
    }
    toggleDrawingRectangle() {
        this.isDrawingRectangle = !this.isDrawingRectangle;
        this.isDrawingPolygon =
            this.isDrawingEllipse =
                this.markAnswerSelection =
                    this.isDeleting =
                        this.colorPickerSelection =
                            this.showColorPanel =
                                false;
    }
    toggleDrawingEllipse() {
        this.isDrawingEllipse = !this.isDrawingEllipse;
        this.isDrawingPolygon =
            this.isDrawingRectangle =
                this.markAnswerSelection =
                    this.isDeleting =
                        this.colorPickerSelection =
                            this.showColorPanel =
                                false;
    }
    toggleAnswerSelection() {
        this.markAnswerSelection = !this.markAnswerSelection;
        this.isDeleting =
            this.isDrawingPolygon =
                this.isDrawingEllipse =
                    this.isDrawingRectangle =
                        this.colorPickerSelection =
                            this.showColorPanel =
                                false;
    }
    toggleColorPicker() {
        this.colorPickerSelection = !this.colorPickerSelection;
        this.isDeleting =
            this.isDrawingPolygon =
                this.isDrawingEllipse =
                    this.isDrawingRectangle =
                        this.showColorPanel =
                            this.markAnswerSelection =
                                false;
    }
    changeColor(color) {
        this.strokeColor = color;
    }
    onMouseDown($event) {
        if (this.isDrawingRectangle) {
            const point = this.getSVGPoint($event);
            this.startCoords.x = point.x;
            this.startCoords.y = point.y;
            this.startCoords.hasClicked = true;
        }
        else if (this.isDrawingEllipse) {
            this.onMouseDownForEllipse($event);
        }
    }
    onMouseMove($event) {
        if (this.isDrawingPolygon) {
            const point = this.getSVGPoint($event);
            this.drawPossibleLine(point);
        }
        else if (this.isDrawingRectangle) {
            if (this.rect && !this.isDragEnded) {
                this.rect.remove();
                this.rect = null;
            }
            if (!this.startCoords.hasClicked) {
                return;
            }
            if (this.isDragEnded) {
                this.isDragEnded = false;
            }
            this.drawRect($event);
        }
        else if (this.isDrawingEllipse) {
            this.onMouseMoveForEllipse($event);
        }
    }
    onMouseUp($event) {
        if (this.isDrawingRectangle) {
            this.drawRect($event, true);
            this.startCoords.hasClicked = false;
            this.removeRectangle();
        }
        else if (this.isDrawingEllipse) {
            this.onMouseUpForEllipse($event);
        }
    }
    removeRectangle() {
        setTimeout(() => {
            document.querySelectorAll('.rect-group').forEach((elem) => {
                if (!elem.hasChildNodes()) {
                    elem?.remove();
                }
            });
        }, 100);
    }
    drawRect($event, final = false, dragRectData = null) {
        let rectData;
        if (dragRectData) {
            rectData = dragRectData;
        }
        else {
            const point = this.getSVGPoint($event);
            rectData = {
                rectWidth: Math.abs(this.startCoords.x - point.x),
                rectHeight: Math.abs(this.startCoords.y - point.y),
                rectX: Math.min(this.startCoords.x, point.x),
                rectY: Math.min(this.startCoords.y, point.y),
            };
        }
        const fillColor = rectData.fillColor || this.strokeColor;
        const strokeColor = rectData.strokeColor || this.strokeColor;
        const selectedShapeClass = rectData.rectUuid && this.answers.includes(rectData.rectUuid) ? 'shape-selected' : '';
        const rectAttributes = [
            { name: 'width', value: rectData.rectWidth },
            { name: 'height', value: rectData.rectHeight },
            { name: 'x', value: rectData.rectX },
            { name: 'y', value: rectData.rectY },
            { name: 'fill-opacity', value: '0' },
            { name: 'fill', value: fillColor },
            { name: 'stroke', value: strokeColor },
            { name: 'stroke-width', value: '2' },
            { name: 'class', value: selectedShapeClass },
        ];
        const rectUuid = rectData.rectUuid || generateUuid(rectAttributes);
        this.parentElementForShapes = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        this.parentElementForShapes.setAttribute('class', 'rect-group');
        this.renderer.appendChild(this.parentSvg.nativeElement, this.parentElementForShapes);
        this.rect = new Shape(this.parentElementForShapes, rectUuid, 'rect', rectAttributes);
        this.rect.draw(this.parentElementForShapes);
        if (final) {
            this.updateShapeInStore({
                shapeData: {
                    x: rectData.rectX,
                    y: rectData.rectY,
                    length: rectData.rectHeight,
                    width: rectData.rectWidth,
                },
                fillColor,
                strokeColor,
                uuid: rectUuid,
                shape: ShapeTypeEnum.RECTANGLE,
            });
            this.shapes.push(this.rect);
            this.addDragHandles(rectData);
            this.rect.addListener('rectDragEnd', (params) => {
                this.isDragEnded = true;
                this.removeRectangle();
                params.parentNode?.node()?.remove();
                this.drawRect(null, true, params.rectData);
            });
            this.rect.addListener('answerSelection', () => {
                this.updateAnswers();
            });
        }
    }
    addDragHandles(rectData) {
        const circleAttributes = [
            { name: 'cx', value: rectData.rectX },
            { name: 'cy', value: rectData.rectY },
            { name: 'r', value: 7 },
            { name: 'class', value: 'topLeft' },
        ];
        const pointOfClick = new Shape(this.parentElementForShapes, generateUuid(circleAttributes), 'circle', circleAttributes);
        pointOfClick.draw();
        const circleAttributes2 = [
            { name: 'cx', value: rectData.rectX + rectData.rectWidth },
            { name: 'cy', value: rectData.rectY },
            { name: 'r', value: 7 },
            { name: 'class', value: 'topRight' },
        ];
        const pointOfClick2 = new Shape(this.parentElementForShapes, generateUuid(circleAttributes2), 'circle', circleAttributes2);
        pointOfClick2.draw();
        const circleAttributes3 = [
            { name: 'cx', value: rectData.rectX },
            { name: 'cy', value: rectData.rectY + rectData.rectHeight },
            { name: 'r', value: 7 },
            { name: 'class', value: 'bottomLeft' },
        ];
        const pointOfClick3 = new Shape(this.parentElementForShapes, generateUuid(circleAttributes3), 'circle', circleAttributes3);
        pointOfClick3.draw();
        const circleAttributes4 = [
            { name: 'cx', value: rectData.rectX + rectData.rectWidth },
            { name: 'cy', value: rectData.rectY + rectData.rectHeight },
            { name: 'r', value: 7 },
            { name: 'class', value: 'bottomRight' },
        ];
        const pointOfClick4 = new Shape(this.parentElementForShapes, generateUuid(circleAttributes4), 'circle', circleAttributes4);
        pointOfClick4.draw();
        this.rect.addDragBehaviourToCirclesOnRectangle();
    }
    onSvgClick($event) {
        const point = this.getSVGPoint($event);
        if (this.isDeleting) {
            this.shapes.forEach((shape) => {
                let inside = false;
                if (shape.shapeType === SVGShapeType.POLYGON) {
                    inside = shape.isPointInsidePolygon(point);
                }
                else if (shape.shapeType === SVGShapeType.RECT) {
                    inside = shape.isPointInsideRectangle(point);
                }
                else if (shape.shapeType === SVGShapeType.ELLIPSE) {
                    inside = shape.isPointInsideEllipse(point);
                }
                if (inside) {
                    // Removing g (group) removes shape and circle dots.
                    this.parentSvg.nativeElement.getElementById(shape.shapeUuid)?.parentNode?.remove();
                    this.store.dispatch(new DeleteShapeAction({ uuid: shape.shapeUuid }));
                }
            });
            return;
        }
        if (this.markAnswerSelection) {
            this.shapes.forEach((shape) => {
                shape.toggleHighLightIfPointInsideShape(point, shape.shapeType);
            });
            return;
        }
        if (this.isDrawingPolygon) {
            const circleAttributes = [
                { name: 'cx', value: point.x },
                { name: 'cy', value: point.y },
                { name: 'r', value: 7 },
                { name: 'class', value: 'dragHandlers' },
            ];
            if (this.activePointsBeingPlotted.length === 0) {
                this.parentElementForShapes = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                this.parentElementForShapes.setAttribute('class', 'polygon-group');
                this.renderer.appendChild(this.parentSvg.nativeElement, this.parentElementForShapes);
            }
            const pointOfClick = new Shape(this.parentElementForShapes, generateUuid(circleAttributes), 'circle', circleAttributes);
            this.activePointsBeingPlotted.push(point);
            if (this.activePointsBeingPlotted.length > 1) {
                this.drawLine();
            }
            if (this.activePointsBeingPlotted.length >= 3 && this.isPolygonComplete()) {
                this.drawPolygon({
                    shapeData: this.activePointsBeingPlotted,
                    strokeColor: this.strokeColor,
                    fillColor: this.strokeColor,
                });
                this.resetPossibleLine();
                this.polyline.remove();
            }
            else {
                pointOfClick.draw();
                this.shapes.push(pointOfClick);
                this.cdRef.detectChanges();
            }
        }
        if (this.colorPickerSelection) {
            this.shapes.forEach((shape) => {
                let inside = false;
                if (shape.shapeType === SVGShapeType.POLYGON) {
                    inside = shape.isPointInsidePolygon(point);
                }
                else if (shape.shapeType === SVGShapeType.RECT) {
                    inside = shape.isPointInsideRectangle(point);
                }
                else if (shape.shapeType === SVGShapeType.ELLIPSE) {
                    inside = shape.isPointInsideEllipse(point);
                }
                if (inside) {
                    shape.update([
                        { name: 'fill', value: this.strokeColor },
                        { name: 'stroke', value: this.strokeColor },
                    ]);
                    this.updateShapeInStore({
                        uuid: shape.shapeUuid,
                        fillColor: this.strokeColor,
                        strokeColor: this.strokeColor,
                    });
                }
            });
            return;
        }
    }
    resetPossibleLine() {
        this.possibleLine.nativeElement.removeAttribute('x1');
        this.possibleLine.nativeElement.removeAttribute('y1');
        this.possibleLine.nativeElement.removeAttribute('x2');
        this.possibleLine.nativeElement.removeAttribute('y2');
    }
    drawPossibleLine(endPoint) {
        if (this.activePointsBeingPlotted.length > 0) {
            const lastPoint = this.activePointsBeingPlotted[this.activePointsBeingPlotted.length - 1];
            this.possibleLine.nativeElement.setAttribute('x1', lastPoint.x);
            this.possibleLine.nativeElement.setAttribute('y1', lastPoint.y);
            this.possibleLine.nativeElement.setAttribute('x2', endPoint.x);
            this.possibleLine.nativeElement.setAttribute('y2', endPoint.y);
        }
    }
    drawLine() {
        if (this.polyline) {
            this.polyline.remove();
            this.polyline = null;
        }
        const pointsString = this.activePointsBeingPlotted.map((pt) => `${pt.x},${pt.y}`).join(' ');
        const polyLineAttributes = [
            { name: 'points', value: pointsString },
            { name: 'fill', value: 'none' },
            { name: 'stroke', value: 'red' },
            { name: 'stroke-width', value: '2' },
        ];
        this.polyline = new Shape(this.parentElementForShapes, generateUuid(polyLineAttributes), 'polyline', polyLineAttributes);
        this.polyline.draw();
    }
    drawPolygon(shapeAttributes, parentSvg = null) {
        const pointsString = shapeAttributes.shapeData.map((pt) => `${pt.x},${pt.y}`).join(' ');
        const strokeColor = shapeAttributes.strokeColor || 'blue';
        const fillColor = shapeAttributes.fillColor || 'transparent';
        const selectedShapeClass = shapeAttributes.uuid && this.answers.includes(shapeAttributes.uuid) ? 'shape-selected' : '';
        const polygonAttributes = [
            { name: 'points', value: pointsString },
            { name: 'fill', value: fillColor },
            { name: 'stroke', value: strokeColor },
            { name: 'stroke-width', value: '2' },
            { name: 'fill-opacity', value: '0.3' },
            { name: 'class', value: selectedShapeClass },
        ];
        const polygonUUid = shapeAttributes.uuid || generateUuid(polygonAttributes);
        const polygon = new Shape(this.parentElementForShapes, polygonUUid, 'polygon', polygonAttributes);
        polygon.draw(parentSvg);
        this.updateShapeInStore({
            shapeData: shapeAttributes.shapeData.map((pt) => {
                return {
                    x: pt.x,
                    y: pt.y,
                };
            }),
            fillColor,
            strokeColor,
            uuid: polygonUUid,
        });
        this.activePointsBeingPlotted = [];
        this.shapes.push(polygon);
        polygon.addDragBehaviourToCirclesOnPolygon();
        polygon.addListener('dragEnd', (params) => {
            this.updateShapeInStore({
                shapeData: params.point
                    .split(' ')
                    .filter((val) => val)
                    .map((val) => {
                    const coordinates = val.split(',');
                    return {
                        x: Number(coordinates[0]),
                        y: Number(coordinates[1]),
                    };
                }),
                uuid: params.uuid,
            });
        });
        polygon.addListener('answerSelection', () => {
            this.updateAnswers();
        });
    }
    drawEllipse(shapeAttributes) {
        const strokeColor = shapeAttributes.strokeColor || this.strokeColor;
        const fillColor = shapeAttributes.fillColor || this.strokeColor;
        const center = shapeAttributes.shapeData || { x: 0, y: 0 };
        const radiusX = shapeAttributes.shapeData.radiusX || this.radiusX; // radius along x-axis
        const radiusY = shapeAttributes.shapeData.radiusY || this.radiusY; // radius along y-axis
        const selectedShapeClass = shapeAttributes.uuid && this.answers.includes(shapeAttributes.uuid) ? 'ellipse shape-selected' : 'ellipse';
        const ellipseAttributes = [
            { name: 'cx', value: center.x },
            { name: 'cy', value: center.y },
            { name: 'rx', value: radiusX },
            { name: 'ry', value: radiusY },
            { name: 'fill-opacity', value: '0' },
            { name: 'fill', value: fillColor },
            { name: 'stroke', value: strokeColor },
            { name: 'stroke-width', value: '2' },
            { name: 'cursor', value: 'pointer' },
            { name: 'class', value: selectedShapeClass },
        ];
        const ellipseUUid = shapeAttributes.uuid || generateUuid(ellipseAttributes);
        this.parentElementForShapes = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        this.parentElementForShapes.setAttribute('class', 'ellipse-group');
        this.renderer.appendChild(this.parentSvg.nativeElement, this.parentElementForShapes);
        const ellipse = new Shape(this.parentElementForShapes, ellipseUUid, ShapeTypeEnum.ELLIPSE, ellipseAttributes);
        ellipse.draw(this.parentElementForShapes);
        this.removeDynamicEllipses();
        this.updateShapeInStore({
            shapeData: shapeAttributes.shapeData,
            fillColor: this.strokeColor,
            strokeColor: this.strokeColor,
            uuid: ellipseUUid,
            shape: ShapeTypeEnum.ELLIPSE,
        });
        ellipse.addDragBehaviourToEllipse();
        ellipse.addListener('ellipseDragEnd', (params) => {
            this.updateShapeInStore({
                shapeData: params.shapeData,
                uuid: params.uuid,
                shape: ShapeTypeEnum.ELLIPSE,
            });
        });
        this.activeEllipsePointBeingPlotted = undefined;
        this.shapes.push(ellipse);
        ellipse.addListener('answerSelection', () => {
            this.updateAnswers();
        });
    }
    onMouseDownForEllipse(event) {
        this.startEllipse = true;
        this.startX = event.offsetX;
        this.startY = event.offsetY;
        // Attach mousemove event listener
        document.addEventListener('mousemove', this.onMouseMoveForEllipse);
    }
    onMouseMoveForEllipse(event) {
        if (this.startEllipse) {
            this.drawEllipseOnMove = true; // Set to true when mouse is moved after clicking
            const { radiusX, radiusY } = this.calculateEllipseRadiusXY(event);
            this.radiusX = radiusX;
            this.radiusY = radiusY;
            this.drawDynamicEllipse();
        }
    }
    drawDynamicEllipse() {
        const center = { x: this.startX, y: this.startY } || { x: 0, y: 0 };
        const radiusX = this.radiusX;
        const radiusY = this.radiusY;
        const ellipseAttributes = [
            { name: 'cx', value: center.x },
            { name: 'cy', value: center.y },
            { name: 'rx', value: radiusX },
            { name: 'ry', value: radiusY },
            { name: 'fill', value: 'transparent' },
            { name: 'stroke', value: this.strokeColor },
            { name: 'stroke-width', value: '2' },
            { name: 'cursor', value: 'pointer' },
        ];
        // Remove any existing dynamic-ellipse-group
        this.removeDynamicEllipses();
        this.parentElementForShapes = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        this.parentElementForShapes.setAttribute('class', 'dynamic-ellipse-group');
        this.renderer.appendChild(this.parentSvg.nativeElement, this.parentElementForShapes);
        const ellipse = new Shape(this.parentElementForShapes, 'dynamic-ellipse', ShapeTypeEnum.ELLIPSE, ellipseAttributes);
        ellipse.draw(this.parentElementForShapes);
    }
    removeDynamicEllipses() {
        // Remove the existing dynamic-ellipse-group if it exists
        const dynamicEllipseGroup = this.parentSvg.nativeElement.querySelector('.dynamic-ellipse-group');
        if (dynamicEllipseGroup) {
            dynamicEllipseGroup.remove();
        }
    }
    onMouseUpForEllipse(event) {
        this.startEllipse = false;
        const ellipsePoint = {
            x: this.startX,
            y: this.startY,
            radiusX: this.radiusX,
            radiusY: this.radiusY,
        };
        // drawEllipseOnMove flag is used to avoid ellipse drawing on click
        if (this.drawEllipseOnMove) {
            this.activeEllipsePointBeingPlotted = ellipsePoint;
            this.drawEllipse({
                shapeData: this.activeEllipsePointBeingPlotted,
                strokeColor: this.strokeColor,
                fillColor: this.strokeColor,
            });
        }
        // Reset drawEllipseOnMove flag to false when mouse is released
        this.drawEllipseOnMove = false;
        document.removeEventListener('mousemove', this.onMouseMoveForEllipse);
    }
    calculateEllipseRadiusXY(currentXY) {
        const radiusX = Math.abs(currentXY.offsetX - this.startX); // radius along x-axis
        const radiusY = Math.abs(currentXY.offsetY - this.startY); // radius along y-axis
        return { radiusX, radiusY };
    }
    updateAnswers() {
        this.answers = [...this.parentSvg.nativeElement.querySelectorAll('.shape-selected')].map((a) => a.getAttribute('id'));
        this.store.dispatch(new UpdateAnswersAction({ answers: this.answers }));
    }
    isPolygonComplete() {
        // Check if the last point is close to the first point to complete the polygon
        if (this.activePointsBeingPlotted.length >= 3) {
            const firstPoint = this.activePointsBeingPlotted[0];
            const lastPoint = this.activePointsBeingPlotted[this.activePointsBeingPlotted.length - 1];
            const distance = Math.sqrt(Math.pow(lastPoint.x - firstPoint.x, 2) + Math.pow(lastPoint.y - firstPoint.y, 2));
            if (distance < 10) {
                this.activePointsBeingPlotted[this.activePointsBeingPlotted.length - 1] = this.activePointsBeingPlotted[0];
                return true;
            }
        }
        return false;
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.hotspotSubscription.unsubscribe();
        this.alive$.next(false);
    }
    getImagesError() {
        return this.allowInlineValidation ? this.hotspotValidationService.getImagesError() : [];
    }
    getShapesError() {
        return this.allowInlineValidation ? this.hotspotValidationService.getShapesError() : [];
    }
    getAnswerError() {
        return this.allowInlineValidation ? this.hotspotValidationService.getAnswerError() : [];
    }
    getMaxSelectionError() {
        return this.allowInlineValidation ? this.hotspotValidationService.getMaxSelectionError() : [];
    }
    getErrors() {
        this.errors = [];
        this.errors = this.errors
            .concat(this.getImagesError())
            .concat(this.getShapesError())
            .concat(this.getAnswerError().concat(this.getMaxSelectionError()));
        return this.errors;
    }
    onFocusout(event) {
        this.store.dispatch(new UpdateMaxSelectionAction(event.value));
    }
    sanitizeInput($event) {
        return this.sanitizeInputService.sanitizeInput($event, false, false, false);
    }
}
AirHotspotComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirHotspotComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i1.Store }, { token: ConfigService }, { token: HotspotValidationService }, { token: SanitizeInputService }], target: i0.ɵɵFactoryTarget.Component });
AirHotspotComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirHotspotComponent, selector: "aa-air-hotspot", viewQueries: [{ propertyName: "parentSvg", first: true, predicate: ["parentSvg"], descendants: true, static: true }, { propertyName: "possibleLine", first: true, predicate: ["possibleLine"], descendants: true, static: true }, { propertyName: "polygonGroup", first: true, predicate: ["polygonGroup"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"(airHotspot$ | async)?.backgroundImage?.url\">\n  <button\n    class=\"btn border ms-2 mb-2 px-2 polygon-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingPolygon}\"\n    (click)=\"toggleDrawingPolygon()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-polygon' : !isDrawingPolygon,\n      'dpg-icon-shape-polygon-white': isDrawingPolygon}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2 rect-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingRectangle}\"\n    (click)=\"toggleDrawingRectangle()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-rectangle' : !isDrawingRectangle,\n      'dpg-icon-shape-rectangle-white': isDrawingRectangle}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2 ellipse-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingEllipse}\"\n    (click)=\"toggleDrawingEllipse()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-ellipse' : !isDrawingEllipse,\n      'dpg-icon-shape-ellipse-white': isDrawingEllipse}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : markAnswerSelection}\"\n    (click)=\"toggleAnswerSelection()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-system-alert-success' : !markAnswerSelection,\n      'dpg-icon-system-alert-success-white': markAnswerSelection}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : isDeleting}\"\n    (click)=\"toggleDelete()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-trash' : !isDeleting,\n      'dpg-icon-trash-white': isDeleting}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : colorPickerSelection}\"\n    (click)=\"toggleColorPicker()\"\n  >\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-eyedropper' : !colorPickerSelection,\n      'dpg-icon-eyedropper-white': colorPickerSelection}\"></i>\n  </button>\n\n  <color-picker\n    class=\"ms-2 mb-2\"\n    [showColorPanel]=\"showColorPanel\"\n    (colorSelected)=\"changeColor($event)\"\n    (panelStatus)=\"panelStatus($event)\"\n  ></color-picker>\n\n  <div class=\"max-selections-input\">\n    <span class=\"max-selections-label\" translate>Max selections:</span>\n    <input\n      type=\"number\"\n      class=\"form-control max-selections-input-field\"\n      [value]=\"(airHotspot$ | async)?.maxSelections\"\n      [disabled]=\"(airHotspot$ | async)?.disableMaxSelection\"\n      (change)=\"onFocusout($event.target)\"\n      (keydown)=\"sanitizeInput($event)\"\n    />\n  </div>   \n  <button\n    class=\"btn btn-sm\"\n    type=\"button\"\n    placement=\"top\"\n    aria-label=\"Max Selections\"\n    ngbPopover=\"Max selections update when adding or deleting shapes.\"\n    ngbTooltip=\"Max Selections\"\n    #maxSelectionsTooltip=\"ngbTooltip\"\n    (click)=\"maxSelectionsTooltip.close()\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" fill=\"currentColor\" class=\"bi bi-info-circle\" viewBox=\"0 0 16 16\">\n      <path d=\"M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z\"/>\n      <path d=\"m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z\"/>\n    </svg>\n  </button>\n</div>\n\n<div class=\"container p-0\">\n    <div id=\"svg-container\" [ngClass]=\"{'validation-error': getErrors()?.length}\">\n      <div class=\"max-content\">\n        <img\n          *ngIf=\"(airHotspot$ | async)?.backgroundImage?.url\"\n          [src]=\"(airHotspot$ | async)?.backgroundImage?.url\"\n          [alt]=\"(airHotspot$ | async)?.backgroundImage?.altText\"\n        >\n        <svg #parentSvg\n          class=\"svg-overlay\"\n          (mousemove)=\"onMouseMove($event)\"\n          (mousedown)=\"onMouseDown($event)\"\n          (mouseup)=\"onMouseUp($event)\"\n          (click)=\"onSvgClick($event)\"\n        >\n          <g #polygonGroup></g>\n          <line #possibleLine class=\"possible-line\"/>\n        </svg>\n      </div>\n    </div>\n</div>\n\n<aa-file-upload id=\"upload-hotspot-background-image\"\n                class=\"file-upload\"\n                [fileName]=\"(airHotspot$ | async)?.backgroundImage?.altText\"\n                [url]=\"'media/image'\"\n                [formats]=\"'.jpg, .jpeg, .png'\"\n                [isUploadingImage]=\"false\"\n                (onUploadedFile)=\"updateFile($event)\"\n                (onRemoveFile)=\"deleteFile($event)\"></aa-file-upload>\n\n<br/>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".container{position:relative}.svg-overlay{position:absolute;top:0;left:0;width:100%;height:100%}.highlighted{fill:#00f;fill-opacity:.5}.defaultStyle{fill:transparent}.polygon-label{font-size:30px;fill:#000}.possible-line{stroke:red;stroke-width:2}polygon.shape-selected,ellipse.shape-selected,rect.shape-selected,circle.shape-selected{fill-opacity:.3}polygon:not(.shape-selected),ellipse:not(.shape-selected),rect:not(.shape-selected),circle:not(.shape-selected){fill-opacity:0}polygon.dragHandlers,polygon.topLeft,polygon.topRight,polygon.bottomLeft,polygon.bottomRight,ellipse.dragHandlers,ellipse.topLeft,ellipse.topRight,ellipse.bottomLeft,ellipse.bottomRight,rect.dragHandlers,rect.topLeft,rect.topRight,rect.bottomLeft,rect.bottomRight,circle.dragHandlers,circle.topLeft,circle.topRight,circle.bottomLeft,circle.bottomRight{fill-opacity:1;fill:#00f;r:5}polygon.dragHandlers:hover,polygon.topLeft:hover,polygon.topRight:hover,polygon.bottomLeft:hover,polygon.bottomRight:hover,ellipse.dragHandlers:hover,ellipse.topLeft:hover,ellipse.topRight:hover,ellipse.bottomLeft:hover,ellipse.bottomRight:hover,rect.dragHandlers:hover,rect.topLeft:hover,rect.topRight:hover,rect.bottomLeft:hover,rect.bottomRight:hover,circle.dragHandlers:hover,circle.topLeft:hover,circle.topRight:hover,circle.bottomLeft:hover,circle.bottomRight:hover{r:10;cursor:pointer}polygon.selected,ellipse.selected,rect.selected,circle.selected{fill-opacity:.3;stroke-width:2}#svg-container{display:inline-block;width:100%;overflow:auto}#svg-container .max-content{width:-moz-max-content;width:max-content;position:relative}#svg-container.validation-error{border:1px solid #b0181e}#svg-container img{border:1px solid #dedede;border-radius:5px;margin-bottom:10px}.file-upload{padding-left:11px;display:inline-block}.max-selections-input{display:inline-flex;align-items:center;margin-left:20px}.max-selections-label{margin-right:5px}.max-selections-input-field{width:70px;margin-right:10px}\n"], dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i6.NgbPopover, selector: "[ngbPopover]", inputs: ["animation", "autoClose", "ngbPopover", "popoverTitle", "placement", "triggers", "container", "disablePopover", "popoverClass", "openDelay", "closeDelay"], outputs: ["shown", "hidden"], exportAs: ["ngbPopover"] }, { kind: "directive", type: i6.NgbTooltip, selector: "[ngbTooltip]", inputs: ["animation", "autoClose", "placement", "triggers", "container", "disableTooltip", "tooltipClass", "openDelay", "closeDelay", "ngbTooltip"], outputs: ["shown", "hidden"], exportAs: ["ngbTooltip"] }, { kind: "component", type: FileUploadComponent$1, selector: "aa-file-upload", inputs: ["fileName", "formats", "url", "isUploadingImage", "isReturningFormData", "isQti"], outputs: ["onUploadedFile", "onRemoveFile", "imageLoad"] }, { kind: "component", type: InlineErrorComponent, selector: "aa-inline-error", inputs: ["errors"] }, { kind: "component", type: ColorPickerComponent, selector: "color-picker", inputs: ["selectedColor", "showColorPanel"], outputs: ["colorSelected", "panelStatus"] }, { kind: "pipe", type: i3.AsyncPipe, name: "async" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirHotspotComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-hotspot', encapsulation: ViewEncapsulation.None, template: "<div *ngIf=\"(airHotspot$ | async)?.backgroundImage?.url\">\n  <button\n    class=\"btn border ms-2 mb-2 px-2 polygon-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingPolygon}\"\n    (click)=\"toggleDrawingPolygon()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-polygon' : !isDrawingPolygon,\n      'dpg-icon-shape-polygon-white': isDrawingPolygon}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2 rect-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingRectangle}\"\n    (click)=\"toggleDrawingRectangle()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-rectangle' : !isDrawingRectangle,\n      'dpg-icon-shape-rectangle-white': isDrawingRectangle}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2 ellipse-drawing\"\n    [ngClass]=\"{'active btn-primary ' : isDrawingEllipse}\"\n    (click)=\"toggleDrawingEllipse()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-shape-ellipse' : !isDrawingEllipse,\n      'dpg-icon-shape-ellipse-white': isDrawingEllipse}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : markAnswerSelection}\"\n    (click)=\"toggleAnswerSelection()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-system-alert-success' : !markAnswerSelection,\n      'dpg-icon-system-alert-success-white': markAnswerSelection}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : isDeleting}\"\n    (click)=\"toggleDelete()\">\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-trash' : !isDeleting,\n      'dpg-icon-trash-white': isDeleting}\"></i>\n  </button>\n\n  <button\n    class=\"btn border ms-2 mb-2 px-2\"\n    [ngClass]=\"{'active btn-primary ' : colorPickerSelection}\"\n    (click)=\"toggleColorPicker()\"\n  >\n    <i class=\"dpg-icon\" [ngClass]=\"{\n      'dpg-icon-eyedropper' : !colorPickerSelection,\n      'dpg-icon-eyedropper-white': colorPickerSelection}\"></i>\n  </button>\n\n  <color-picker\n    class=\"ms-2 mb-2\"\n    [showColorPanel]=\"showColorPanel\"\n    (colorSelected)=\"changeColor($event)\"\n    (panelStatus)=\"panelStatus($event)\"\n  ></color-picker>\n\n  <div class=\"max-selections-input\">\n    <span class=\"max-selections-label\" translate>Max selections:</span>\n    <input\n      type=\"number\"\n      class=\"form-control max-selections-input-field\"\n      [value]=\"(airHotspot$ | async)?.maxSelections\"\n      [disabled]=\"(airHotspot$ | async)?.disableMaxSelection\"\n      (change)=\"onFocusout($event.target)\"\n      (keydown)=\"sanitizeInput($event)\"\n    />\n  </div>   \n  <button\n    class=\"btn btn-sm\"\n    type=\"button\"\n    placement=\"top\"\n    aria-label=\"Max Selections\"\n    ngbPopover=\"Max selections update when adding or deleting shapes.\"\n    ngbTooltip=\"Max Selections\"\n    #maxSelectionsTooltip=\"ngbTooltip\"\n    (click)=\"maxSelectionsTooltip.close()\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" fill=\"currentColor\" class=\"bi bi-info-circle\" viewBox=\"0 0 16 16\">\n      <path d=\"M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z\"/>\n      <path d=\"m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z\"/>\n    </svg>\n  </button>\n</div>\n\n<div class=\"container p-0\">\n    <div id=\"svg-container\" [ngClass]=\"{'validation-error': getErrors()?.length}\">\n      <div class=\"max-content\">\n        <img\n          *ngIf=\"(airHotspot$ | async)?.backgroundImage?.url\"\n          [src]=\"(airHotspot$ | async)?.backgroundImage?.url\"\n          [alt]=\"(airHotspot$ | async)?.backgroundImage?.altText\"\n        >\n        <svg #parentSvg\n          class=\"svg-overlay\"\n          (mousemove)=\"onMouseMove($event)\"\n          (mousedown)=\"onMouseDown($event)\"\n          (mouseup)=\"onMouseUp($event)\"\n          (click)=\"onSvgClick($event)\"\n        >\n          <g #polygonGroup></g>\n          <line #possibleLine class=\"possible-line\"/>\n        </svg>\n      </div>\n    </div>\n</div>\n\n<aa-file-upload id=\"upload-hotspot-background-image\"\n                class=\"file-upload\"\n                [fileName]=\"(airHotspot$ | async)?.backgroundImage?.altText\"\n                [url]=\"'media/image'\"\n                [formats]=\"'.jpg, .jpeg, .png'\"\n                [isUploadingImage]=\"false\"\n                (onUploadedFile)=\"updateFile($event)\"\n                (onRemoveFile)=\"deleteFile($event)\"></aa-file-upload>\n\n<br/>\n<aa-inline-error [errors]=\"getErrors()\"></aa-inline-error>\n", styles: [".container{position:relative}.svg-overlay{position:absolute;top:0;left:0;width:100%;height:100%}.highlighted{fill:#00f;fill-opacity:.5}.defaultStyle{fill:transparent}.polygon-label{font-size:30px;fill:#000}.possible-line{stroke:red;stroke-width:2}polygon.shape-selected,ellipse.shape-selected,rect.shape-selected,circle.shape-selected{fill-opacity:.3}polygon:not(.shape-selected),ellipse:not(.shape-selected),rect:not(.shape-selected),circle:not(.shape-selected){fill-opacity:0}polygon.dragHandlers,polygon.topLeft,polygon.topRight,polygon.bottomLeft,polygon.bottomRight,ellipse.dragHandlers,ellipse.topLeft,ellipse.topRight,ellipse.bottomLeft,ellipse.bottomRight,rect.dragHandlers,rect.topLeft,rect.topRight,rect.bottomLeft,rect.bottomRight,circle.dragHandlers,circle.topLeft,circle.topRight,circle.bottomLeft,circle.bottomRight{fill-opacity:1;fill:#00f;r:5}polygon.dragHandlers:hover,polygon.topLeft:hover,polygon.topRight:hover,polygon.bottomLeft:hover,polygon.bottomRight:hover,ellipse.dragHandlers:hover,ellipse.topLeft:hover,ellipse.topRight:hover,ellipse.bottomLeft:hover,ellipse.bottomRight:hover,rect.dragHandlers:hover,rect.topLeft:hover,rect.topRight:hover,rect.bottomLeft:hover,rect.bottomRight:hover,circle.dragHandlers:hover,circle.topLeft:hover,circle.topRight:hover,circle.bottomLeft:hover,circle.bottomRight:hover{r:10;cursor:pointer}polygon.selected,ellipse.selected,rect.selected,circle.selected{fill-opacity:.3;stroke-width:2}#svg-container{display:inline-block;width:100%;overflow:auto}#svg-container .max-content{width:-moz-max-content;width:max-content;position:relative}#svg-container.validation-error{border:1px solid #b0181e}#svg-container img{border:1px solid #dedede;border-radius:5px;margin-bottom:10px}.file-upload{padding-left:11px;display:inline-block}.max-selections-input{display:inline-flex;align-items:center;margin-left:20px}.max-selections-label{margin-right:5px}.max-selections-input-field{width:70px;margin-right:10px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i1.Store }, { type: ConfigService }, { type: HotspotValidationService }, { type: SanitizeInputService }]; }, propDecorators: { parentSvg: [{
                type: ViewChild,
                args: ['parentSvg', { static: true }]
            }], possibleLine: [{
                type: ViewChild,
                args: ['possibleLine', { static: true }]
            }], polygonGroup: [{
                type: ViewChild,
                args: ['polygonGroup', { static: true }]
            }] } });

class AirMultipleChoiceAudioComponent extends AirBaseComponent {
    constructor(store) {
        super(store, getPureAirMultipleChoiceAudio, LoadAirItemAction, ResetStoreAction);
        this.store = store;
    }
}
AirMultipleChoiceAudioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMultipleChoiceAudioComponent, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Component });
AirMultipleChoiceAudioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: AirMultipleChoiceAudioComponent, selector: "aa-air-multiple-choice-audio", usesInheritance: true, ngImport: i0, template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirMultipleChoiceAudioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aa-air-multiple-choice-audio', template: "<ng-content select=\".aa-content-stem-header\"></ng-content>\n\n" }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class AirAuthoringModule {
}
AirAuthoringModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AirAuthoringModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringModule, declarations: [AirBucketingComponent,
        AirChoiceMatrixComponent,
        AirClozeComponent,
        AirEssayComponent,
        AirFibComponent,
        AirFileUploadComponent,
        AirMatchingComponent,
        AirMultipleChoiceComponent,
        AirOrderingComponent,
        AirSelectTextComponent,
        AirShortAnswerComponent,
        AirTrueFalseComponent,
        AirImageLabelingComponent,
        AnswerBooleanComponent,
        AnswerFibComponent,
        ClozeChoicesComponent,
        FibSettingComponent,
        FileUploadComponent$1,
        ItemAllowMediaComponent,
        ItemEditorTypeComponent,
        ItemPreviewComponent,
        ItemResponseTextLimitComponent,
        MatchingChoicesComponent,
        MultipleChoiceAnswersComponent,
        OrderingChoicesComponent,
        TrashCanComponent,
        SelectionUnitComponent,
        SelectionUnitsComponent,
        SelectTextEditComponent,
        SelectionUnitTypesComponent,
        BucketingChoicesComponent,
        ChoiceMatrixOptionsComponent,
        ChoiceMatrixRowsColsComponent,
        ImageLabelsComponent,
        ImageDistractorsComponent,
        AddUpdateBucketAndChoiceComponent,
        AirEquationEntryComponent,
        ImageUploadComponent,
        EquationEntryAnswersComponent,
        AirInstructionItemComponent,
        AirGridInComponent,
        GridInChoicesComponent,
        AirAudioRecordingComponent,
        AirNumberLineComponent,
        LineGraphComponent,
        AirAuthoringComponent,
        ItemExplanationComponent,
        ItemHintComponent,
        ItemExampleResponseComponent,
        InlineErrorComponent,
        ItemExampleComponent,
        AirExternalAlgoComponent,
        ItemExampleComponent,
        ViewShortcutsComponent,
        AirDrawingComponent,
        DrawingToolComponent,
        ConfigurePointsComponent,
        AirCoordinateGraphComponent,
        AirTableEntryComponent,
        AirTableEntryCellComponent,
        ChoiceMatrixRowsColsCellComponent,
        AirHotspotComponent,
        ColorPickerComponent,
        AirMultipleChoiceAudioComponent,
        FocusTrapDirective], imports: [AirRenderingModule,
        CommonModule,
        NgxSharedModule,
        NgbModule, i1$1.TranslateModule, RichTextEditorModule,
        MathEditorModule, i1.StoreFeatureModule, i1.StoreFeatureModule, AngularDraggableModule,
        PipesModule,
        MathModule,
        ContentManagerModule,
        AirModalModule,
        FormsModule,
        ReactiveFormsModule,
        LhDropMenuModule], exports: [AirBucketingComponent,
        AirChoiceMatrixComponent,
        AirClozeComponent,
        AirEssayComponent,
        AirFibComponent,
        AirFileUploadComponent,
        AirMatchingComponent,
        AirMultipleChoiceComponent,
        AirOrderingComponent,
        AirSelectTextComponent,
        AirShortAnswerComponent,
        AirTrueFalseComponent,
        AirImageLabelingComponent,
        TrashCanComponent,
        AirEquationEntryComponent,
        AirInstructionItemComponent,
        AirGridInComponent,
        AirAudioRecordingComponent,
        AirNumberLineComponent,
        ItemPreviewComponent,
        AirAuthoringComponent,
        AirExternalAlgoComponent,
        AirDrawingComponent,
        DrawingToolComponent,
        AirCoordinateGraphComponent,
        AirTableEntryComponent,
        AirHotspotComponent,
        AirMultipleChoiceAudioComponent] });
AirAuthoringModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringModule, providers: [
        airAuthoringReducersProvider,
        airConfigReducersProvider,
        TextUtilityService,
        ImageLabelDndService,
        HtmlToPlaintextPipe,
        HtmlToPlaintextWithImagePipe,
        SanitizeInputService,
        FileUploadService,
    ], imports: [AirRenderingModule,
        CommonModule,
        NgxSharedModule,
        NgbModule,
        TranslateModule.forRoot({}),
        RichTextEditorModule,
        MathEditorModule,
        StoreModule.forFeature('airAuthoring', airAuthoringReducersToken),
        StoreModule.forFeature('airConfig', airConfigReducersToken),
        AngularDraggableModule,
        PipesModule,
        MathModule,
        ContentManagerModule,
        AirModalModule,
        FormsModule,
        ReactiveFormsModule,
        LhDropMenuModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AirAuthoringModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AirBucketingComponent,
                        AirChoiceMatrixComponent,
                        AirClozeComponent,
                        AirEssayComponent,
                        AirFibComponent,
                        AirFileUploadComponent,
                        AirMatchingComponent,
                        AirMultipleChoiceComponent,
                        AirOrderingComponent,
                        AirSelectTextComponent,
                        AirShortAnswerComponent,
                        AirTrueFalseComponent,
                        AirImageLabelingComponent,
                        AnswerBooleanComponent,
                        AnswerFibComponent,
                        ClozeChoicesComponent,
                        FibSettingComponent,
                        FileUploadComponent$1,
                        ItemAllowMediaComponent,
                        ItemEditorTypeComponent,
                        ItemPreviewComponent,
                        ItemResponseTextLimitComponent,
                        MatchingChoicesComponent,
                        MultipleChoiceAnswersComponent,
                        OrderingChoicesComponent,
                        TrashCanComponent,
                        SelectionUnitComponent,
                        SelectionUnitsComponent,
                        SelectTextEditComponent,
                        SelectionUnitTypesComponent,
                        BucketingChoicesComponent,
                        ChoiceMatrixOptionsComponent,
                        ChoiceMatrixRowsColsComponent,
                        ImageLabelsComponent,
                        ImageDistractorsComponent,
                        AddUpdateBucketAndChoiceComponent,
                        AirEquationEntryComponent,
                        ImageUploadComponent,
                        EquationEntryAnswersComponent,
                        AirInstructionItemComponent,
                        AirGridInComponent,
                        GridInChoicesComponent,
                        AirAudioRecordingComponent,
                        AirNumberLineComponent,
                        LineGraphComponent,
                        AirAuthoringComponent,
                        ItemExplanationComponent,
                        ItemHintComponent,
                        ItemExampleResponseComponent,
                        InlineErrorComponent,
                        ItemExampleComponent,
                        AirExternalAlgoComponent,
                        ItemExampleComponent,
                        ViewShortcutsComponent,
                        AirDrawingComponent,
                        DrawingToolComponent,
                        ConfigurePointsComponent,
                        AirCoordinateGraphComponent,
                        AirTableEntryComponent,
                        AirTableEntryCellComponent,
                        ChoiceMatrixRowsColsCellComponent,
                        AirHotspotComponent,
                        ColorPickerComponent,
                        AirMultipleChoiceAudioComponent,
                        FocusTrapDirective,
                    ],
                    imports: [
                        AirRenderingModule,
                        CommonModule,
                        NgxSharedModule,
                        NgbModule,
                        TranslateModule.forRoot({}),
                        RichTextEditorModule,
                        MathEditorModule,
                        StoreModule.forFeature('airAuthoring', airAuthoringReducersToken),
                        StoreModule.forFeature('airConfig', airConfigReducersToken),
                        AngularDraggableModule,
                        PipesModule,
                        MathModule,
                        ContentManagerModule,
                        AirModalModule,
                        FormsModule,
                        ReactiveFormsModule,
                        LhDropMenuModule,
                    ],
                    providers: [
                        airAuthoringReducersProvider,
                        airConfigReducersProvider,
                        TextUtilityService,
                        ImageLabelDndService,
                        HtmlToPlaintextPipe,
                        HtmlToPlaintextWithImagePipe,
                        SanitizeInputService,
                        FileUploadService,
                    ],
                    exports: [
                        AirBucketingComponent,
                        AirChoiceMatrixComponent,
                        AirClozeComponent,
                        AirEssayComponent,
                        AirFibComponent,
                        AirFileUploadComponent,
                        AirMatchingComponent,
                        AirMultipleChoiceComponent,
                        AirOrderingComponent,
                        AirSelectTextComponent,
                        AirShortAnswerComponent,
                        AirTrueFalseComponent,
                        AirImageLabelingComponent,
                        TrashCanComponent,
                        AirEquationEntryComponent,
                        AirInstructionItemComponent,
                        AirGridInComponent,
                        AirAudioRecordingComponent,
                        AirNumberLineComponent,
                        ItemPreviewComponent,
                        AirAuthoringComponent,
                        AirExternalAlgoComponent,
                        AirDrawingComponent,
                        DrawingToolComponent,
                        AirCoordinateGraphComponent,
                        AirTableEntryComponent,
                        AirHotspotComponent,
                        AirMultipleChoiceAudioComponent,
                    ],
                }]
        }] });

const LOAD_AIR_TINYMCE_CONFIG = '[tinymce] Load AIR TinyMCE Config';
class LoadAirTinyMCEConfigAction {
    constructor(payload) {
        this.payload = payload;
        this.type = LOAD_AIR_TINYMCE_CONFIG;
    }
}

var airTinymceConfig_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    LOAD_AIR_TINYMCE_CONFIG: LOAD_AIR_TINYMCE_CONFIG,
    LoadAirTinyMCEConfigAction: LoadAirTinyMCEConfigAction
});

const initialState = {
    wirisUrl: null,
    mmsUrl: null,
    tinymceSkinsUrl: null,
    tinymceExternalPlugins: {
        tiny_mce_wiris: null,
    },
    hideA11yChecker: false,
};
function AirTinyMCEConfigReducer(state = initialState, action) {
    switch (action.type) {
        case LOAD_AIR_TINYMCE_CONFIG:
            return { ...action.payload };
        default:
            return state;
    }
}
function getAirTinyMCEConfigReducers() {
    return AirTinyMCEConfigReducer;
}
const airTinyMCEConfigReducersToken = new InjectionToken('AIR TinyMCE Config Module reducers');
Object.assign(airTinyMCEConfigReducersToken, AirTinyMCEConfigReducer);
const airTinyMCEConfigReducersProvider = {
    provide: airTinyMCEConfigReducersToken,
    useFactory: getAirTinyMCEConfigReducers,
};

/**
 * Generated bundle index. Do not edit.
 */

export { AirAudioRecordingComponent, AirAuthoringComponent, AirAuthoringModule, AirBucketingComponent, AirChoiceMatrixComponent, AirClozeComponent, AirCoordinateGraphComponent, AirDrawingComponent, AirEquationEntryComponent, AirEssayComponent, AirExternalAlgoComponent, AirFibComponent, AirFileUploadComponent, AirGridInComponent, AirHotspotComponent, AirImageLabelingComponent, AirInstructionItemComponent, AirMatchingComponent, AirMultipleChoiceAudioComponent, AirMultipleChoiceComponent, AirNumberLineComponent, AirOrderingComponent, AirSelectTextComponent, AirShortAnswerComponent, AirTableEntryComponent, AirTinyMCEConfigReducer, AirTrueFalseComponent, AirValidationService, ChoiceMatrixCellType, ChoiceMatrixTableProps, ConfigService, DrawingToolComponent, ItemPreviewComponent, ItemType, ItemTypeName, MathDirective, MathModule, SVGShapeType, ShapeTypeEnum, TableEntryCellType, TrashCanComponent, airAlgorithmic_actions as airAlgorithmicActions, airAudioRecording_actions as airAudioRecordingActions, airAuthoringReducersProvider, airAuthoringReducersToken, airBucketing_actions as airBucketingActions, airChoiceMatrix_actions as airChoiceMatrixActions, airCloze_actions as airClozeActions, airConfig_actions as airConfigActions, airCoordinateGraph_actions as airCoordinateGrapherActions, airDrawing_actions as airDrawingActions, airEquationEntry_actions as airEquationEntryActions, airEssay_actions as airEssayActions, airFib_actions as airFibActions, airFileUpload_actions as airFileUploadActions, airGridIn_actions as airGridInActions, airHotspot_actions as airHotspotActions, airImageLabeling_actions as airImageLabelingActions, airInstructionItem_actions as airInstructionItemActions, airMatching_actions as airMatchingActions, airMultipleChoice_actions as airMultipleChoiceActions, airMultipleChoiceAudio_actions as airMultipleChoiceAudioActions, airNumberLine_actions as airNumberLineActions, airOrdering_actions as airOrderingActions, airSelectText_actions as airSelectTextActions, airShortAnswer_actions as airShortAnswerActions, airTableEntry_actions as airTableEntryActions, airTinymceConfig_actions as airTinyMCEConfigActions, airTrueFalse_actions as airTrueFalseActions, airAlgorithmic_reducer as fromAirAlgorithmic, airBucketing_reducer as fromAirBucketing, airChoiceMatrix_reducer as fromAirChoiceMatrix, airCloze_reducer as fromAirCloze, airCoordinateGraph_reducer as fromAirCoordinategrapher, airDrawing_reducer as fromAirDrawing, airEquationEntry_reducer as fromAirEquationEntry, airEssay_reducer as fromAirEssay, airFib_reducer as fromAirFib, airFileUpload_reducer as fromAirFileUpload, airGridIn_reducer as fromAirGridIn, airHotspot_reducer as fromAirHotspot, airImageLabeling_reducer as fromAirImageLabeling, airInstructionItem_reducer as fromAirInstructionItem, airMatching_reducer as fromAirMatching, airMultipleChoice_reducer as fromAirMultipleChoice, airMultipleChoiceAudio_reducer as fromAirMultipleChoiceAudio, airNumberLine_reducer as fromAirNumberLine, airOrdering_reducer as fromAirOrdering, airSelectText_reducer as fromAirSelectText, airShortAnswer_reducer as fromAirShortAnswer, fromAirState, airTableEntry_reducer as fromAirTableEntry, airTrueFalse_reducer as fromAirTrueFalse, airAudioRecording_reducer as fromAudioRecording, graphType, isOfShapeType, modeEnum, nlPointType, reducers, responseType, selectors as sharedStoreSelectors };
